import { reportBelongsToSite, groupLegacySiteReports, reportText, siteForReport } from '../lib/reportSiteIdentity';
import { savedWorkspaceUiState } from "../lib/savedWorkspaceUiState";
import { workspaceReadSignal, resetWorkspaceReadSession } from '../lib/workspaceReadSession';
import type { CseReportReviewBasis, CseReportReviewAck } from '../types/cseReportReview';
import { ensureCanvasNodeIncarnations, newCanvasNodeIncarnation } from "../lib/sim/canvas/canvasNodeIncarnation";
import {
  acceptInUseCanvasAssumptions,
  ensureCanvasAssumptionBridge,
  cloneEoGraphConfigForRepair,
  cloneEoTransientConfigForRepair,
  defaultEoCanvasExecutionConfig,
  defaultEoStudySpec,
  defaultEoTransientConfig,
  deriveEoGraphV2ConnectionComposition,
  normalizeEoCanvasExecutionConfig,
  normalizeEoStudyDefinition,
  normalizeEoTransientConfig,
  parseEoCanvasExecutionConfig,
  parseEoCanvasExecutionConfigOrDefault,
  retireInvalidCanvasResultEdges,
} from './canvasAssumptionBridge';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

/** Maximum reports kept in memory. Oldest reports are dropped when this limit is exceeded. */
const MAX_REPORTS = 50;

/**
 * D9 — how long a financial re-stamp waits before persisting, so a user typing
 * through several assumption fields produces ONE write per report instead of
 * one per keystroke-committed field. The in-memory update is not debounced.
 */
const RESTAMP_PERSIST_DEBOUNCE_MS = 2_000;
const restampPersistTimers = new Map<string, ReturnType<typeof setTimeout>>();
interface InFlightCreateSiteEntry {
  initiatingUid: string | null;
  initiatingCompanyId: string | null;
  initiatingScopeGen: number;
  resolvedCompanyScope: string;
  promise: Promise<string>;
}
const inFlightCreateSiteRequests = new Map<string, InFlightCreateSiteEntry>();
const inFlightDocCreateLocks = new Map<string, Promise<unknown>>();
const completedDraftCreateByRequestId = new Map<string, string>();
let _createSiteScopeGeneration = 0;

export function serializeDraftBoundaryIdentity(siteInput: {
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteBoundaries?: Array<{ id?: string; label?: string; polygon: Array<{ lat: number; lng: number }> }>;
  studyArea?: {
    name?: string;
    revision?: number;
    included?: Array<{ kind?: string; id?: string; geometryHash?: string }>;
  };
}): string {
  const parcels =
    Array.isArray(siteInput.siteBoundaries) && siteInput.siteBoundaries.length > 0
      ? siteInput.siteBoundaries
          .map((p) =>
            (Array.isArray(p.polygon) ? p.polygon : [])
              .map((pt) => `${Number(pt.lat).toFixed(6)},${Number(pt.lng).toFixed(6)}`)
              .join(';'),
          )
          .join('|')
      : Array.isArray(siteInput.siteBoundary) && siteInput.siteBoundary.length > 0
        ? siteInput.siteBoundary
            .map((pt) => `${Number(pt.lat).toFixed(6)},${Number(pt.lng).toFixed(6)}`)
            .join(';')
        : '';
  const studyIncluded =
    Array.isArray(siteInput.studyArea?.included) && siteInput.studyArea!.included.length > 0
      ? siteInput.studyArea!.included
          .map((item) => `${item.kind ?? ''}:${item.id ?? ''}:${item.geometryHash ?? ''}`)
          .join(',')
      : '';
  return `${parcels}#${studyIncluded}`;
}

export function buildDraftCreationInputFingerprint(siteInput: {
  name: string;
  address?: string;
  location?: { lat: number; lng: number };
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteBoundaries?: Array<{ id?: string; label?: string; polygon: Array<{ lat: number; lng: number }> }>;
  studyArea?: import('../types/siteStudyArea').SiteStudyArea;
}): string {
  const normalizedName = (siteInput.name ?? '').trim().toLowerCase();
  const normalizedAddress = (siteInput.address ?? '').trim().toLowerCase();
  const latKey =
    typeof siteInput.location?.lat === 'number' && Number.isFinite(siteInput.location.lat)
      ? siteInput.location.lat.toFixed(6)
      : 'na';
  const lngKey =
    typeof siteInput.location?.lng === 'number' && Number.isFinite(siteInput.location.lng)
      ? siteInput.location.lng.toFixed(6)
      : 'na';
  const boundaryKey = serializeDraftBoundaryIdentity(siteInput);
  return `${normalizedName}::${normalizedAddress}::${latKey},${lngKey}::${boundaryKey}`;
}

export function buildDraftCreationReceipt(params: {
  uid?: string;
  companyScope?: string;
  requestId?: string | null;
  siteInput: {
    name: string;
    address?: string;
    location?: { lat: number; lng: number };
    siteBoundary?: Array<{ lat: number; lng: number }>;
    siteBoundaries?: Array<{ id?: string; label?: string; polygon: Array<{ lat: number; lng: number }> }>;
    studyArea?: import('../types/siteStudyArea').SiteStudyArea;
  };
}): import('../types/site').DraftSiteCreationReceipt {
  const { uid, companyScope, requestId, siteInput } = params;
  const boundaryIdentity = serializeDraftBoundaryIdentity(siteInput);
  const fingerprint = buildDraftCreationInputFingerprint(siteInput);
  const hasValidLocation =
    typeof siteInput.location?.lat === 'number' &&
    Number.isFinite(siteInput.location.lat) &&
    typeof siteInput.location?.lng === 'number' &&
    Number.isFinite(siteInput.location.lng);
  return {
    ...(uid ? { uid } : {}),
    ...(companyScope ? { companyScope } : {}),
    ...(requestId ? { requestId } : {}),
    name: (siteInput.name ?? '').trim(),
    address: (siteInput.address ?? '').trim(),
    ...(hasValidLocation
      ? { location: { lat: siteInput.location!.lat, lng: siteInput.location!.lng } }
      : {}),
    boundaryIdentity,
    fingerprint,
  };
}

export function doesDraftCreationInputConflict(
  existing: Partial<import('../types/site').SiteEntity>,
  incoming: {
    name: string;
    address?: string;
    location?: { lat: number; lng: number };
    siteBoundary?: Array<{ lat: number; lng: number }>;
    siteBoundaries?: Array<{ id?: string; label?: string; polygon: Array<{ lat: number; lng: number }> }>;
    studyArea?: import('../types/siteStudyArea').SiteStudyArea;
  },
): boolean {
  const receipt = existing.draftCreationReceipt;
  const existingName = ((receipt ? receipt.name : existing.name) ?? '').trim().toLowerCase();
  const incomingName = (incoming.name ?? '').trim().toLowerCase();
  if (existingName !== incomingName) return true;

  const existingAddress = ((receipt ? receipt.address : existing.address) ?? '').trim().toLowerCase();
  const incomingAddress = (incoming.address ?? '').trim().toLowerCase();
  if (existingAddress !== incomingAddress) return true;

  const existingLocation = receipt ? receipt.location : existing.location;
  const existingLat = existingLocation?.lat;
  const existingLng = existingLocation?.lng;
  const incomingLat = incoming.location?.lat;
  const incomingLng = incoming.location?.lng;
  if (
    typeof existingLat === 'number' &&
    typeof existingLng === 'number' &&
    typeof incomingLat === 'number' &&
    typeof incomingLng === 'number'
  ) {
    if (Math.abs(existingLat - incomingLat) > 1e-6 || Math.abs(existingLng - incomingLng) > 1e-6) {
      return true;
    }
  } else if (receipt) {
    const existingHasLoc = typeof existingLat === 'number' && typeof existingLng === 'number';
    const incomingHasLoc = typeof incomingLat === 'number' && typeof incomingLng === 'number';
    if (existingHasLoc !== incomingHasLoc) {
      return true;
    }
  }

  const existingBoundary = receipt
    ? (receipt.boundaryIdentity ?? '')
    : serializeDraftBoundaryIdentity(existing);
  const incomingBoundary = serializeDraftBoundaryIdentity(incoming);
  if (existingBoundary !== incomingBoundary) return true;

  if (receipt?.fingerprint) {
    const incomingFingerprint = buildDraftCreationInputFingerprint(incoming);
    if (receipt.fingerprint !== incomingFingerprint) return true;
  }

  return false;
}

async function acquireCrossTabStorageLease(
  lockKey: string,
  ttlMs = 2_000,
  maxWaitMs = 2_500,
): Promise<() => void> {
  if (typeof window === 'undefined') return () => undefined;
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  if (!storage || typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function') {
    return () => undefined;
  }
  const storageKey = `datacrate:createSiteLease:${lockKey}`;
  const token = `${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
  const deadline = Date.now() + maxWaitMs;
  let acquired = false;

  while (Date.now() <= deadline) {
    try {
      const now = Date.now();
      const raw = storage.getItem(storageKey);
      let heldByOther = false;
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as { token?: string; expiresAt?: number } | null;
          if (
            parsed &&
            typeof parsed.expiresAt === 'number' &&
            parsed.expiresAt > now &&
            parsed.token !== token
          ) {
            heldByOther = true;
          }
        } catch {
          heldByOther = false;
        }
      }
      if (!heldByOther) {
        storage.setItem(storageKey, JSON.stringify({ token, expiresAt: now + ttlMs }));
        const verifyRaw = storage.getItem(storageKey);
        const verifyParsed = verifyRaw
          ? (JSON.parse(verifyRaw) as { token?: string } | null)
          : null;
        if (verifyParsed?.token === token) {
          acquired = true;
          break;
        }
      }
    } catch {
      // Storage unavailable or quota exceeded: fall back to server-side transaction invariant.
      return () => undefined;
    }
    await new Promise((resolve) => setTimeout(resolve, 15));
  }

  const heartbeatIntervalMs = Math.max(40, Math.floor(ttlMs / 3));
  const heartbeat = acquired
    ? setInterval(() => {
        try {
          const currentRaw = storage?.getItem(storageKey);
          if (!currentRaw) return;
          const current = JSON.parse(currentRaw) as { token?: string } | null;
          if (current?.token === token) {
            storage?.setItem(
              storageKey,
              JSON.stringify({ token, expiresAt: Date.now() + ttlMs }),
            );
          }
        } catch {
          /* ignore storage renewal errors */
        }
      }, heartbeatIntervalMs)
    : null;

  return () => {
    if (heartbeat !== null) {
      clearInterval(heartbeat);
    }
    if (!acquired) return;
    try {
      const currentRaw = storage?.getItem(storageKey);
      if (!currentRaw) return;
      const current = JSON.parse(currentRaw) as { token?: string } | null;
      if (current?.token === token) {
        storage?.removeItem(storageKey);
      }
    } catch {
      /* ignore storage cleanup errors */
    }
  };
}

async function withAtomicSiteCreateLock<T>(lockKey: string, fn: () => Promise<T>): Promise<T> {
  const runInProcessSerial = async (): Promise<T> => {
    const previous = inFlightDocCreateLocks.get(lockKey) ?? Promise.resolve();
    let releaseCurrent!: () => void;
    const currentGate = new Promise<void>((resolve) => {
      releaseCurrent = resolve;
    });
    const chained = previous.catch(() => undefined).then(() => currentGate);
    inFlightDocCreateLocks.set(lockKey, chained);
    await previous.catch(() => undefined);
    const releaseStorage = await acquireCrossTabStorageLease(lockKey);
    try {
      return await fn();
    } finally {
      releaseStorage();
      releaseCurrent();
      if (inFlightDocCreateLocks.get(lockKey) === chained) {
        inFlightDocCreateLocks.delete(lockKey);
      }
    }
  };

  if (
    typeof navigator !== 'undefined' &&
    navigator.locks &&
    typeof navigator.locks.request === 'function'
  ) {
    try {
      return await navigator.locks.request(
        `datacrate:createSite:${lockKey}`,
        { mode: 'exclusive' },
        () => runInProcessSerial(),
      );
    } catch (lockErr) {
      const msg = lockErr instanceof Error ? lockErr.message : String(lockErr ?? '');
      // Only fall back to in-process + Firestore transaction if Web Locks API itself is
      // unsupported/disabled by the environment; propagate any error thrown by `fn()`.
      if (!/SecurityError|NotSupportedError|locks is not/i.test(msg)) {
        throw lockErr;
      }
    }
  }
  return runInProcessSerial();
}

function deterministicDraftSiteId(uid: string, companyScope: string, requestId: string): string {
  const input = `${uid}::${companyScope}::${requestId}`;
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 ^= ch;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= ch ^ ((i & 0xff) + 1);
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return `site-draft-${h1.toString(36).padStart(7, '0')}-${h2.toString(36).padStart(7, '0')}`;
}
const LOCAL_COMPOSITE_BYPASS_SITE_MARKERS = ['holly ridge', 'southwest crossing'] as const;
import {
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  addEdge,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
} from 'reactflow';
import { SimulationState, CustomComponentDef, Site, SimEvent } from '../simulation/types';
import { buildEventPreset } from '../simulation/ns/networks/coolingScenarios';
import { SiteRagState, SiteRagReport } from '../types/siteRag';
import { SiteRagService } from '../services/siteRagService';
import { DEMO_SITES } from '../services/demoData';
import { runSimulationStep, runSimulationStepViaWorker } from '../simulation/stepSimulation';
import { filterSolveInput } from '../lib/sim/solveFilter';
import { normalizeIgnoredNodeIds } from '../lib/sim/solveMembership';
import type { EoStudySpec } from '../lib/sim/eo/study/eoStudySpec';
import type { EoGraphConfigV1 } from '../lib/sim/eo/eoGraphConfig';
import type { EoTransientConfig } from '../lib/sim/eo/eoTransientConfig';
import type { EoCanvasExecutionConfigV1 } from '../lib/sim/eo/eoCanvasExecutionConfig';
import {
  EO_CANVAS_REAL_PROPERTY_PRESSURE_SERIES_EXECUTION_PROFILE,
  EO_CANVAS_FIXED_SPLIT_RECOMBINE_EXECUTION_PROFILE,
  EO_GRAPH_V3_GENERAL_REAL_WATER_HEATER_V6_SOLVER_PROFILE,
  EO_GRAPH_V3_SHAFT_COUPLED_RANKINE_SOLVER_PROFILE,
  EO_GRAPH_V3_SHAFT_COUPLED_DRY_COOLED_RANKINE_ACC_SOLVER_PROFILE,
  EO_CANVAS_SHAFT_COUPLED_RANKINE_PUMP_LOAD_HANDLE,
  EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_PUMP_HANDLE,
  EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_TURBINE_HANDLE,
  EO_CANVAS_SHAFT_COUPLED_RANKINE_TURBINE_SOURCE_HANDLE,
} from '../lib/sim/eo/profileIds';
import {
  isSteadyStateConverged,
  steadyStateReachedMessage,
  steadyStateNotReachedMessage,
  STEADY_STATE_MAX_STEPS,
} from '../simulation/steadyState';
import { DEFAULT_THEME } from '../types/reportTheme';
import type { ReportTemplate } from '../types/reportTemplate';
import { normalizeTemplateHeadingOverrides } from '../lib/reportTemplateNormalization';
import { normalizeSiteEntity } from '../lib/siteReportLink';
import { resolveOrgStamp } from '../lib/siteCompany';
import { syncBrowserUrlForActiveSite } from '../lib/siteNavigation';
import { withTimeout } from '../lib/withTimeout';
import {
  applyHydratedSiteLayersToCurrentSites,
  mergeSitesIntoStore,
  type HydratedSiteLayersPatch,
} from '../lib/site/mergeSitesIntoStore';
import {
  isSiteLayerSetWriteReady,
  isVerifiedCompleteSiteLayerMirror,
} from '../lib/siteLayers/canonicalLayerResolution';
import { sameArrayElements } from '../lib/structuralEqual';
import { dashboardScopePredicate } from '../lib/portfolio/dashboardSiteScope';
// Single source of truth for /api/storage/* + /api/probe-key auth — see
// src/lib/authedFetch.ts. The bare fetch() calls scattered across this
// file were silently 401-ing every storage write.
import { authedFetch } from '../lib/authedFetch';
import { siteDocumentMetadataPatchSchema, siteDocumentMetadataAckSchema, siteDocumentMetadataBasisHash } from '../lib/siteDocumentMetadata';
import { bomSourceDocumentLinkRequestSchema, bomSourceDocumentLinkAckSchema, bomSourceDocumentLinkPlanHash,
  type BomSourceDocumentChange, type BomSourceDocumentLinkRequest, type BomSourceDocumentLinkAck } from '../lib/bomSourceDocumentLink';
import { estimatedReportGenerationDurationMs } from '../lib/reportGeneration/generationEta';
import { withAbortableDeadline } from '../lib/withAbortableDeadline';
import { isLocalMockMode } from '../lib/localDev/isLocalMockMode';
import { isReportHydratedForGcsSave, serializeReportForGcsSave } from '../lib/reportGcsSerialization';
import {
  applyHydratedReportBody,
  compactReportMetadataRow,
  isFullReaderReadyReportBody,
  isImmutableRevisionReport,
  isPortfolioSlimReportBody,
  isRenderableReportBody,
  markReportMetadataOnly,
  pickAuthoritativeReportMetadata,
  mergeReportBodyWithAuthoritativeMetadata,
  overlayLegacySanityDecisionsIfPlausible,
  slimPortfolioReportBody,
} from '../lib/reportBodyHydration';
import {
  mapWithConcurrency,
  REPORT_BACKGROUND_BODY_HYDRATION_CONCURRENCY,
  REPORT_LIST_BODY_HYDRATION_CONCURRENCY,
  REPORT_BODY_READ_TIMEOUT_MS,
  reportMatchesSiteScope,
  retainOpenedReportBody,
  reuseHydratedReportBody,
  reuseUnchangedMetadataRow,
  selectEagerReportBodyIds,
  selectEagerReportBodyIdsForSites,
  siteReportBodiesSettled,
  type ReportSiteScope,
} from '../lib/reportListHydration';
import { parseSiteAppPath } from '../lib/siteNavigation';
import { stripTemplateLinkedLayoutSnapshots } from '../lib/stripTemplateLinkedLayoutSnapshots';
import { migrateSanityFindings } from '../lib/sanityResolutionMigrate';
import {
  buildSanityDecisionEntry,
  overlaySanityDecisionMap,
  REPORT_USER_STATE_SCHEMA_VERSION,
  reportUserStateDocPath,
  sanityFindingIdentity,
} from '../lib/reportUserState';

/**
 * Hydrate a report body and, for immutable-v2 reports, overlay the reader's
 * sanity decisions from the owner-namespace user-state object. The rules
 * forbid repointing `storagePath` on a revision-backed report, so
 * `updateRagReport` persists Apply-on-rerun / Ignore there — and until this
 * overlay nothing read it back (live 2026-08-18: decisions vanished on reload
 * and the rerun applied none of them). Best-effort: any failure yields the
 * plain hydrated body.
 */
async function hydrateReportBodyWithUserState(
  full: unknown,
  metadata: Record<string, unknown>,
  fallbackUid: string,
  signal?: AbortSignal,
): Promise<SiteRagReport | null> {
  const merged = mergeReportBodyWithAuthoritativeMetadata(full, metadata);
  if (!merged || !isImmutableRevisionReport(metadata)) return merged;
  // Layer 1 — legacy owner-namespace body object (decisions made before the
  // user-state doc existed; positional, guarded). Applied FIRST so that
  // Layer 2 — the first-class user-state doc (identity-keyed) — wins.
  let withState = await overlayLegacySanityDecisionsIfPlausible(
    merged,
    metadata,
    fallbackUid,
    async (userStatePath) => {
      const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(userStatePath)}`, { signal });
      return res.ok ? await res.json() as unknown : null;
    },
  );
  try {
    const { db } = await import('../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const ownerUid = [metadata.reportOwnerUid, metadata._ownerUid, metadata.requesterUid, fallbackUid]
      .find((v): v is string => typeof v === 'string' && v.length > 0);
    const id = typeof metadata.id === 'string' ? metadata.id : null;
    if (ownerUid && id) {
      const snap = await getDoc(doc(db, reportUserStateDocPath(ownerUid, id)));
      if (snap.exists()) withState = overlaySanityDecisionMap(withState, snap.data());
    }
  } catch { /* best-effort — permission or offline ⇒ plain hydration */ }
  return withState;
}

// Share exact owner/metadata-bound reads between eager loading and the open reader.
const inFlightReportBodyReads = new Map<string, Promise<SiteRagReport | null>>();
function loadReportBodyWithUserState(
  metadata: Record<string, unknown>,
  uid: string,
  companyId?: string | null,
): Promise<SiteRagReport | null> {
  if (typeof metadata.storagePath !== 'string') return Promise.resolve(null);
  const storagePath = metadata.storagePath;
  const key = JSON.stringify([companyId ?? '', uid, pickAuthoritativeReportMetadata(metadata)]);
  const existing = inFlightReportBodyReads.get(key);
  if (existing) return existing;
  const pending = withAbortableDeadline(async signal => {
    const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(storagePath)}`, { signal });
    if (!res.ok) return null;
    return hydrateReportBodyWithUserState(await res.json(), metadata, uid, signal);
  }, REPORT_BODY_READ_TIMEOUT_MS, 'Report body read').finally(() => {
    if (inFlightReportBodyReads.get(key) === pending) inFlightReportBodyReads.delete(key);
  });
  inFlightReportBodyReads.set(key, pending);
  return pending;
}

// ─── Site-scoped report body residency ──────────────────────────────────────
//
// `fetchRagReports` lists every row the user can see, reads the open site's
// current-head bodies first (that is what gates the first screen), then warms
// the remaining heads behind the gate; `ensureSiteReportBodies` reads one
// site's heads sooner when a surface needs them. All three share the
// row → body read path above and the bookkeeping below.

/**
 * Firestore rows from the latest list pass, by report id. A re-listed mutable
 * legacy row is compared against the row its resident body was read under, so
 * an unchanged `updatedAt` can skip the re-read.
 */
const lastReportMetadataById = new Map<string, Record<string, unknown>>();
const inFlightSiteReportBodyHydrations = new Map<string, Promise<void>>();

function rememberListedReportMetadata(rows: readonly Record<string, unknown>[]): void {
  lastReportMetadataById.clear();
  for (const row of rows) {
    if (typeof row.id === 'string') lastReportMetadataById.set(row.id, row);
  }
}

/** The site whose page is (about to be) on screen: the store first, else the URL the app booted on. */
function resolveOpenSiteId(state: Pick<AppState, 'siteRag'>): string | null {
  if (state.siteRag.activeSiteId) return state.siteRag.activeSiteId;
  if (typeof window === 'undefined' || !window.location) return null;
  return parseSiteAppPath(window.location.pathname).siteId;
}

function siteScopeFor(
  sites: readonly import('../types/site').SiteEntity[],
  siteId: string,
): ReportSiteScope {
  const site = sites.find((candidate) => candidate.id === siteId);
  return { id: siteId, name: site?.name ?? null, reportIds: site?.reportIds ?? null };
}

/** The open site plus every site whose heads are already resident this session. */
function residentReportSiteScopes(
  state: Pick<AppState, 'siteRag'>,
  openSiteId: string | null,
): ReportSiteScope[] {
  const ids = new Set<string>();
  if (openSiteId) ids.add(openSiteId);
  // Before the first pass completes the list may be a restored copy from an
  // earlier session, which says nothing about what is resident now.
  if (state.siteRag.isReportsLoaded) {
    for (const id of state.siteRag.hydratedReportSiteIds ?? []) ids.add(id);
  }
  return [...ids].map((id) => siteScopeFor(state.siteRag.sites, id));
}

/**
 * Record that these sites' current heads are resident (or that their read
 * settled). Residency doubles as the site page's "its heads have been read"
 * signal, so a site is only ever marked by the pass that read its heads —
 * never on behalf of another site.
 */
function markResidentReportSites(
  siteRag: AppState['siteRag'],
  siteIds: readonly string[],
): Pick<AppState['siteRag'], 'hydratedReportSiteIds'> {
  const current = siteRag.hydratedReportSiteIds ?? [];
  const next = [...current];
  for (const id of siteIds) {
    if (!next.includes(id)) next.push(id);
  }
  return { hydratedReportSiteIds: next.length === current.length ? current : next };
}

/** Distinct stamped site ids across the listed rows. */
function listedReportSiteIds(rows: readonly Record<string, unknown>[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    if (typeof row.siteId === 'string' && row.siteId) ids.add(row.siteId);
  }
  return [...ids];
}

/**
 * Read-time normalisation every resident body goes through once per identity.
 * Each step returns its input when it has nothing to change, so a body reused
 * across list passes keeps its object identity and downstream memos stay warm.
 */
function prepareLoadedReportBody(loaded: SiteRagReport): {
  report: SiteRagReport;
  persistSanityMigration: boolean;
  retirementMarker: NonNullable<SiteRagReport['requiresRegeneration']> | null;
} {
  // COVER FIX — at LOAD time templates are not yet resolved, so DON'T strip
  // the report-level cover here (`hasLiveTemplate=false` only snapshots
  // `coverLayoutAtGen`). The actual strip is gated on a resolved live template
  // at render time (SiteRagReportView). Skipped when it would be a no-op.
  const layout = loaded as SiteRagReport & { coverLayout?: unknown; coverLayoutAtGen?: unknown };
  const stripped = loaded.templateId?.trim() && layout.coverLayoutAtGen == null && layout.coverLayout != null
    ? stripTemplateLinkedLayoutSnapshots(loaded, false)
    : loaded;
  const marked = markRetiredSimulationReferences(stripped);
  // The read-time marker is also persisted for owned reports so every later
  // surface sees the same regeneration requirement. Company-shared reports
  // remain read-only in this session.
  const retirementMarker = marked !== stripped && !marked.isCompanyShared && marked.requiresRegeneration
    ? marked.requiresRegeneration
    : null;
  // Apply-on-Rerun pack (D2) — collapse legacy three-state sanity resolutions
  // (approved / changed / discarded) onto the two-state model on first load.
  // Idempotent: reports already carrying `sanityResolutionsMigrated` skip the
  // walk; reports whose findings actually moved get the migrated array + flag
  // persisted back to GCS/Firestore (best-effort, off the loading gate). The
  // flag flips in memory either way so a no-op report isn't re-walked.
  if (marked.sanityResolutionsMigrated) {
    return { report: marked, persistSanityMigration: false, retirementMarker };
  }
  const { findings, changed } = migrateSanityFindings(marked.sanityFindings);
  return {
    report: { ...marked, sanityFindings: findings, sanityResolutionsMigrated: true },
    persistSanityMigration: changed && !marked.isCompanyShared,
    retirementMarker,
  };
}

/**
 * Persist read-time migrations outside the loading gate. `updateRagReport`
 * reads the current store state, which already has the migrated array, so
 * passing the flag is enough. Every write is best-effort and stops at an
 * auth or company-scope boundary.
 */
async function persistReportMigrations(
  get: () => AppState,
  scopeIsCurrent: () => boolean,
  sanityMigrationIds: readonly string[],
  retirementMarkers: ReadonlyMap<string, NonNullable<SiteRagReport['requiresRegeneration']>>,
): Promise<void> {
  for (const id of sanityMigrationIds) {
    if (!scopeIsCurrent()) return;
    try {
      await get().updateRagReport(id, { sanityResolutionsMigrated: true });
    } catch { /* best-effort — GCS has the report either way */ }
  }
  for (const [id, requiresRegeneration] of retirementMarkers) {
    if (!scopeIsCurrent()) return;
    try {
      await get().updateRagReport(id, { requiresRegeneration });
    } catch { /* best-effort — the in-memory report remains safely marked */ }
  }
}

/** Collects the read-time migrations of one hydration pass and persists them off the gate. */
function createReportMigrationBatch(get: () => AppState, scopeIsCurrent: () => boolean): {
  collect: (prepared: ReturnType<typeof prepareLoadedReportBody>) => SiteRagReport;
  flush: () => void;
} {
  const sanityMigrationIds: string[] = [];
  const retirementMarkers = new Map<string, NonNullable<SiteRagReport['requiresRegeneration']>>();
  return {
    collect(prepared) {
      if (prepared.persistSanityMigration) sanityMigrationIds.push(prepared.report.id);
      if (prepared.retirementMarker) retirementMarkers.set(prepared.report.id, prepared.retirementMarker);
      return prepared.report;
    },
    flush() {
      if (sanityMigrationIds.length === 0 && retirementMarkers.size === 0) return;
      // Not awaited: the persist loop used to hold `isReportsLoaded`.
      void persistReportMigrations(get, scopeIsCurrent, sanityMigrationIds, retirementMarkers);
    },
  };
}

function publishHydratedReportBody(
  set: (update: (state: AppState) => Partial<AppState>) => void,
  metadata: Record<string, unknown>,
  hydrated: SiteRagReport,
): boolean {
  let applied = false;
  set((state) => ({
    siteRag: {
      ...state.siteRag,
      reports: state.siteRag.reports.map((current) => {
        if (current.id !== metadata.id) return current;
        const next = applyHydratedReportBody(current, metadata, hydrated, {
          activeReportId: state.siteRag.activeReportId,
        });
        if (next !== current) applied = true;
        return next;
      }),
    },
  }));
  return applied;
}

/** One pass over the rows for a whole batch of delayed bodies, so subscribers move once. */
function publishHydratedReportBodies(
  rows: SiteRagReport[],
  bodies: ReadonlyMap<
    string,
    { metadata: Record<string, unknown>; report: SiteRagReport }
  >,
  activeReportId?: string | null,
): SiteRagReport[] {
  if (bodies.size === 0) return rows;
  return rows.map((row) => {
    const entry = bodies.get(row.id);
    if (!entry) return row;
    return applyHydratedReportBody(row, entry.metadata, entry.report, { activeReportId });
  });
}

import { toReportPickRow } from '../lib/reportVersionGroups';
import { resolveSectionOrder } from '../lib/reportOrdering';
import { eligibleDocIdsForSed } from '../lib/evidenceDocEligibility';
import { resolvePreferredTemplate, resolveReportTemplateStamp } from '../lib/templateSelection';
import { resolveEffectiveLogo, resolveUploadedLogo } from '../lib/resolveEffectiveLogo';
import { createChatSlice, type ChatSlice } from './chatSlice';
import { createRecorderSlice, type RecorderSlice } from '../lib/sim/graphing/recorderSlice';
import { captureCanvasGeoProjection } from '../lib/sim/canvas/geo/canvasGeoProjection';
import { computePipeGeometry } from '../lib/sim/canvas/geo/computePipeGeometry';
import { haversineMeters } from '../services/siteLayersBlock';
import { markRetiredSimulationReferences } from '../lib/reportGeneration/legacySimulationRetirement';
import { formatEnqueueRejection } from '../lib/reportGeneration/enqueueRejectionMessage';
import {
  SENTIMENT_EVIDENCE_LEAVE_WARNING,
  SENTIMENT_EVIDENCE_MAX_AFTER_SCAN_RETRIES,
  SENTIMENT_EVIDENCE_MAX_AUTO_DISPATCHES,
  SENTIMENT_EVIDENCE_RETRY_DELAY_MS,
  SENTIMENT_EVIDENCE_WAITING_NOTICE,
  SENTIMENT_EVIDENCE_WAIT_STAGE,
  classifySentimentSiteEvidence,
  nextSentimentWaitStep,
  parseSentimentEvidenceNotReadyResponse,
  publicSentimentEvidenceDisposition,
  sentimentEvidenceHoldMessage,
  type PublicSentimentEvidenceDisposition,
  type SentimentEvidenceExhaustion,
  type SentimentEvidenceNotReadyReason,
} from '../lib/sentiment/sentimentEvidenceReadiness';
import {
  isRepairProtectedTab,
  repairDispositionReason,
  type SimulationRepairDisposition,
} from '../lib/sim/canvas/repairDisposition';
import type {
  GeneratedCanvasCommitResult,
  GeneratedCanvasRetirements,
  SiteLayerCanvasLinkPatch,
} from '../lib/sim/canvas/generatedCanvasCommit';
import {
  simulationRevisionMutationBlocker,
  type SimulationRevisionBinding,
} from '../lib/sim/projects/simulationRevisionState';
import {
  parseEoKpiUncertaintyDefinitionSetV1,
  type EoKpiUncertaintyDefinitionSetV1,
} from '../lib/sim/eo/eoKpiUncertaintyDefinitionSet';
import { kmzConversionSourceFingerprint } from '../lib/sim/canvas/kmzSourceFingerprint';
import { isManagedGenerationImpactLayer } from '../lib/siteLayers/managedGenerationImpactLayers';
import { siteLayersRevision } from '../lib/siteLayers/siteLayerRevision';
import { assertSiteLayerParentBudget } from '../lib/siteLayers/siteLayerCompatibilityMirror';
import { setLayerCanvasLink } from '../lib/siteLayerEditor';
import { resolveOpenQuestionIdentity } from '../lib/findingClaim';

function readLocalCompositeBypassFlag(): boolean {
  let explicit: string | undefined;
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env) {
      const raw = (import.meta as { env: Record<string, unknown> }).env.VITE_LOCAL_COMPOSITE_BYPASS;
      if (typeof raw === 'string') explicit = raw;
    }
  } catch {
    /* import.meta may be unavailable in test/node contexts */
  }
  if (!explicit && typeof process !== 'undefined' && process.env) {
    explicit = process.env.VITE_LOCAL_COMPOSITE_BYPASS;
  }
  // Default ON for localhost-only testing; explicit "false" disables.
  return explicit?.trim().toLowerCase() !== 'false';
}

function isLocalhostRuntime(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
}

function isLocalCompositeBypassSite(siteName: string | undefined): boolean {
  if (!siteName) return false;
  const normalized = siteName.trim().toLowerCase();
  return LOCAL_COMPOSITE_BYPASS_SITE_MARKERS.some((marker) => normalized.includes(marker));
}

function canBypassCompanyForLocalComposite(siteName: string | undefined): boolean {
  return isLocalhostRuntime() && readLocalCompositeBypassFlag() && isLocalCompositeBypassSite(siteName);
}

export type AppNode = Node;

/**
 * #116 — merge a solver result (which only contains the SOLVED subset of nodes,
 * because ignored components are pruned from the solve input) back into the full
 * canvas node array. Solved nodes are replaced by id; any node the solver never
 * saw (ignored) is left exactly as-is so it stays on the canvas un-stepped.
 * Order is preserved (canvas order, not solver order). When the solver saw every
 * node (the no-ignore default), this returns `result` semantics — every node is
 * replaced — so the existing step path is unchanged.
 */
function mergeSolvedNodes(canvasNodes: AppNode[], solvedNodes: Node[]): AppNode[] {
  if (solvedNodes.length === canvasNodes.length) {
    // Fast path: nothing was pruned (no ignores), keep the solver's array.
    return solvedNodes as AppNode[];
  }
  const byId = new Map(solvedNodes.map((n) => [n.id, n]));
  return canvasNodes.map((n) => (byId.get(n.id) as AppNode | undefined) ?? n);
}

/**
 * S4 / S9 — resolve the lightweight simulation `Site` (the editor's `site`
 * shape) from a first-class `SiteEntity`.
 *
 * The editor `site` only carries id/name/location/zoom; EO run persistence and
 * the `linkedSiteEntity` lookup both key off `site.id`, so the id MUST be the
 * SiteEntity's REAL id (never a synthetic id), which lets both resolve back to
 * the full SiteEntity in `siteRag.sites`.
 *
 * Reused by the in-canvas "Link a Site" tool (S4) and by the legacy-model fork
 * compatibility action when an imported model has no site (S9). `zoom`
 * defaults to the editor's established site-link view level of 15.
 */
export function siteFromEntity(
  entity: Pick<import('../types/site').SiteEntity, 'id' | 'name' | 'location'>,
): Site {
  return {
    id: entity.id,
    name: entity.name,
    location: entity.location,
    zoom: 15,
  };
}

/** Options for opening a tab. */
export interface AddTabOptions {
  /**
   * Keep the loaded content exactly as given. Used when opening an immutable
   * project revision (head or recovery draft): it must reopen byte-exact, so
   * "in use => accepted" is settled only when the user edits it.
   */
  preserveLoadedContent?: boolean;
}

export interface SimulationTab {
  id: string;
  name: string;
  nodes: AppNode[];
  edges: Edge[];
  simState: SimulationState;
  site: Site | null;
  cloudId?: string;
  version?: number;
  autoSave?: boolean;
  lastSaved?: string;
  /**
   * Additive pointer to a tenant-owned model and its immutable server head.
   * Presence routes autosave to the caller's recovery draft; it must never be
   * mirrored into the mutable `users/{uid}/simulations` compatibility row.
   */
  revisionBinding?: SimulationRevisionBinding;
  /** Versioned EO boundary/closure inputs. This, not boundary-node display metadata, is submitted. */
  eoGraphConfig?: EoGraphConfigV1;
  /**
   * Sole persisted authority for interpreting this existing Sim Canvas.
   * Optional only for legacy/test object compatibility; every tab created or
   * loaded through the store receives the explicit graph-v2 default.
   */
  eoCanvasExecutionConfig?: EoCanvasExecutionConfigV1;
  /**
   * Canonical EO solve exclusions. Optional only for legacy/test fixtures;
   * every tab created or normalized by the store receives an explicit array.
   */
  ignoredNodeIds?: string[];
  /** Model-owned exploratory study definition. Study results are stored separately as run history. */
  eoStudySpec?: EoStudySpec;
  /** Versioned, tab-owned transient RunSpec and per-stream initial conditions. */
  eoTransientConfig?: EoTransientConfig;
  /** Exact revision-owned per-KPI uncertainty definitions; totals remain server-computed. */
  eoKpiUncertaintyDefinitionSet?: EoKpiUncertaintyDefinitionSetV1;
  /**
   * Exact normalized solver-input identity last confirmed written: graph-v2
   * solve-body signature or explicit canvas graph-v3 adapter request hash.
   * Null/absent means the selected profile is not yet runnable/saved exactly.
   */
  savedEoInputSignature?: string | null;
  /**
   * Legacy serialized source marker. The historic field name is retained so
   * old workspaces still open safely; a marked tab is read-only until the user
   * creates an EO repair copy. New EO-ready templates do not set this field.
   */
  sourceBoP?: string;
  /**
   * Typed live-editor protection for newly generated ambiguous repair drafts.
   * Absence means the tab is editable unless the legacy `sourceBoP` fallback is
   * present. The persisted `repairMode` field reconstructs protection on load.
   */
  repairDisposition?: SimulationRepairDisposition;
  /**
   * S8 — persisted React Flow viewport (pan + zoom) so a save→reload returns the
   * user to the exact framing. OPTIONAL for back-compat: tabs/cloud sims saved
   * before this shipped omit it, and the editor falls back to `fitView`.
   */
  flowViewport?: { x: number; y: number; zoom: number };
}

/** Store-authoritative mutation blocker for protected sources and project roles/conflicts. */
export function canvasTabMutationBlocker(
  tab: Pick<SimulationTab, 'repairDisposition' | 'sourceBoP' | 'revisionBinding'>
    | null
    | undefined,
): string | null {
  return repairDispositionReason(tab)
    ?? simulationRevisionMutationBlocker(tab?.revisionBinding);
}

/**
 * Store-authoritative read-only check for the live canvas. The historic name
 * is retained for compatibility, but project viewer/reviewer/conflict tabs are
 * now protected by the same non-bypassable mutation gate.
 */
export function isActiveCanvasRepairProtected(
  state: Pick<AppState, 'tabs' | 'activeTabId'>,
): boolean {
  return canvasTabMutationBlocker(
    state.tabs.find((tab) => tab.id === state.activeTabId),
  ) !== null;
}

/** Same engineering content: the same reference or the same canonical JSON. */
function sameEngineeringContent(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * True when `live` changes the engineering parameters of `stored`: a node added
 * or removed, or a node whose `data.params` differs by value (parameter edit,
 * paste, template, undo/redo). View and result rewrites (selection, measured
 * dimensions, drag, solved-value decorations) leave `data.params` unchanged.
 */
function nodeParamsChanged(
  stored: readonly AppNode[],
  live: readonly AppNode[],
): boolean {
  if (stored === live) return false;
  if (stored.length !== live.length) return true;
  const storedById = new Map(stored.map((node) => [node.id, node]));
  return live.some((node) => {
    const before = storedById.get(node.id);
    if (!before) return true;
    const a: unknown = before.data?.params;
    const b: unknown = node.data?.params;
    return a !== b && !sameEngineeringContent(a, b);
  });
}

/**
 * True when `live` changes the drawn connections of `stored`: an edge added or
 * removed, or an edge whose endpoints or handles differ. Selection, animation
 * and solved-value decorations on an edge are not topology changes.
 */
function edgeTopologyChanged(
  stored: readonly Edge[],
  live: readonly Edge[],
): boolean {
  if (stored === live) return false;
  if (stored.length !== live.length) return true;
  const storedById = new Map(stored.map((edge) => [edge.id, edge]));
  return live.some((edge) => {
    const before = storedById.get(edge.id);
    return !before
      || before.source !== edge.source
      || before.target !== edge.target
      || (before.sourceHandle ?? null) !== (edge.sourceHandle ?? null)
      || (before.targetHandle ?? null) !== (edge.targetHandle ?? null);
  });
}

/**
 * The acceptance-carrying fields of `patch` whose content differs from the
 * tab's current value. A tab bound to a project revision is settled
 * ("in use => accepted") only where the user actually edits it: unchanged
 * write-backs (panel reconciliation, normalization) keep the immutable
 * revision byte-exact.
 */
function changedAcceptanceFields(
  tab: SimulationTab,
  patch: Partial<SimulationTab>,
): Partial<SimulationTab> {
  const changed: Partial<SimulationTab> = {};
  const has = (key: keyof SimulationTab): boolean =>
    Object.prototype.hasOwnProperty.call(patch, key);
  if (has('nodes') && patch.nodes && nodeParamsChanged(tab.nodes, patch.nodes)) {
    changed.nodes = patch.nodes;
  }
  if (has('eoGraphConfig') && !sameEngineeringContent(tab.eoGraphConfig, patch.eoGraphConfig)) {
    changed.eoGraphConfig = patch.eoGraphConfig;
  }
  if (has('eoTransientConfig') && !sameEngineeringContent(tab.eoTransientConfig, patch.eoTransientConfig)) {
    changed.eoTransientConfig = patch.eoTransientConfig;
  }
  if (has('eoStudySpec') && !sameEngineeringContent(tab.eoStudySpec, patch.eoStudySpec)) {
    changed.eoStudySpec = patch.eoStudySpec;
  }
  if (
    has('eoCanvasExecutionConfig') &&
    !sameEngineeringContent(tab.eoCanvasExecutionConfig, patch.eoCanvasExecutionConfig)
  ) {
    changed.eoCanvasExecutionConfig = patch.eoCanvasExecutionConfig;
  }
  return changed;
}

/**
 * The fields to settle for a patch: the patch itself plus the tab's execution
 * config (merged), so the profile in use keeps a complete model-limitation
 * record whenever the drawn topology or the config is written. Identity is
 * preserved when nothing changes, so an unchanged config is not rewritten.
 */
function withExecutionConfigInUse(
  fields: Partial<SimulationTab>,
  merged: Pick<SimulationTab, 'eoCanvasExecutionConfig'>,
): Partial<SimulationTab> {
  if (
    merged.eoCanvasExecutionConfig === undefined
    || Object.prototype.hasOwnProperty.call(fields, 'eoCanvasExecutionConfig')
  ) {
    return fields;
  }
  return { ...fields, eoCanvasExecutionConfig: merged.eoCanvasExecutionConfig };
}

/**
 * Selection is ephemeral view state and is safe on an immutable/read-only
 * revision. A replacement is selection-only only when topology, ordering,
 * geometry, data, and every other own property retain the same identities.
 */
function isSelectionOnlyElementReplacement<
  T extends { id: string; selected?: boolean },
>(current: readonly T[], replacement: readonly T[]): boolean {
  if (current.length !== replacement.length) return false;
  return current.every((before, index) => {
    const after = replacement[index];
    if (!after || before.id !== after.id) return false;
    const beforeKeys = Object.keys(before).filter((key) => key !== 'selected');
    const afterKeys = Object.keys(after).filter((key) => key !== 'selected');
    if (
      beforeKeys.length !== afterKeys.length
      || beforeKeys.some((key, keyIndex) => key !== afterKeys[keyIndex])
    ) {
      return false;
    }
    return beforeKeys.every(
      (key) =>
        (before as unknown as Record<string, unknown>)[key]
        === (after as unknown as Record<string, unknown>)[key],
    );
  });
}

const PROTECTED_SOURCE_INITIALIZATION_KEYS = new Set<keyof SimulationTab>([
  'eoGraphConfig',
  'eoCanvasExecutionConfig',
  'savedEoInputSignature',
  'ignoredNodeIds',
  'eoStudySpec',
  'eoTransientConfig',
]);
const PROTECTED_GENERATED_CLOUD_KEYS = new Set<keyof SimulationTab>([
  'cloudId',
  'version',
  'autoSave',
]);
const PROTECTION_ESTABLISHMENT_KEYS = new Set<keyof SimulationTab>([
  'sourceBoP',
  'repairDisposition',
  'autoSave',
]);

/**
 * Legacy file loading establishes `sourceBoP` in `loadCycle` before App.tsx
 * attaches the already-normalized EO boundary payload. Permit that one-time,
 * additive initialization only; subsequent config edits remain read-only.
 */
function isProtectedSourceInitializationPatch(
  tab: SimulationTab,
  data: Partial<SimulationTab>,
): boolean {
  const keys = Object.keys(data) as Array<keyof SimulationTab>;
  return tab.repairDisposition === undefined
    && typeof tab.sourceBoP === 'string'
    && tab.sourceBoP.trim().length > 0
    && tab.eoGraphConfig === undefined
    && data.eoGraphConfig !== undefined
    && keys.length > 0
    && keys.every((key) => PROTECTED_SOURCE_INITIALIZATION_KEYS.has(key));
}

/** One-time binding of a newly persisted generated repair source. */
function isProtectedGeneratedCloudInitializationPatch(
  tab: SimulationTab,
  data: Partial<SimulationTab>,
): boolean {
  const keys = Object.keys(data) as Array<keyof SimulationTab>;
  return tab.repairDisposition?.kind === 'protected-repair'
    && tab.cloudId === undefined
    && typeof data.cloudId === 'string'
    && data.cloudId.trim().length > 0
    && data.autoSave !== true
    && keys.length > 0
    && keys.every((key) => PROTECTED_GENERATED_CLOUD_KEYS.has(key));
}

function isStopOnlySimulationPatch(data: Partial<SimulationState>): boolean {
  const keys = Object.keys(data) as Array<keyof SimulationState>;
  return keys.length === 1 && keys[0] === 'isRunning' && data.isRunning === false;
}

function isProtectionEstablishmentPatch(data: Partial<SimulationTab>): boolean {
  const keys = Object.keys(data) as Array<keyof SimulationTab>;
  return keys.length > 0
    && keys.every((key) => PROTECTION_ESTABLISHMENT_KEYS.has(key));
}

/**
 * SIM-MGMT — uniform result for the destructive / mutating saved-cloud-sim
 * management actions (`deleteCloudSim`, `renameCloudSim`, `createSimBackup`).
 * `ok` is the single source of truth; `error` is a human-readable reason when
 * `ok === false`. Actions NEVER throw — callers branch on `ok`.
 */
export interface CloudSimActionResult {
  ok: boolean;
  error?: string;
}

/**
 * SIM-MGMT — the persisted payload shape for one saved cloud simulation AND
 * for each point-in-time backup snapshot. Mirrors the AutoSaver / App.tsx
 * `handleCloudQuickSave` doc shape EXACTLY (nodes/edges/simState/site/siteRag
 * are JSON-stringified) so a backup restore can reuse the same parse path
 * `loadCloudSim` uses. All thermodynamic/canvas fields are optional so legacy
 * sims saved before a given field shipped still load (fall back to defaults).
 */
export interface CloudSimPayload {
  name?: string;
  /** JSON.stringify(AppNode[]) */
  nodes?: string | null;
  /** JSON.stringify(Edge[]) */
  edges?: string | null;
  /** JSON.stringify(SimulationState) */
  simState?: string | null;
  /** JSON.stringify(Site) — the canvas "view-state" anchor; nullable. */
  site?: string | null;
  /** JSON.stringify(light siteRag UI view-state) — ADDITIVE, nullable. */
  siteRag?: string | null;
  /** JSON.stringify(EoGraphConfigV1); absent on legacy canvases, which migrate fail-closed. */
  eoGraphConfig?: string | null;
  /** JSON.stringify(EoCanvasExecutionConfigV1); absent legacy rows remain graph-v2. */
  eoCanvasExecutionConfig?: string | null;
  /** Graph-v2 solve-body signature or explicit canvas graph-v3 normalized input hash. */
  eoInputSignature?: string | null;
  /** JSON.stringify(EoSolveMembershipV1); absent legacy rows solve every node. */
  solveMembership?: string | null;
  /** JSON.stringify(EoStudyDefinitionV1); absent legacy rows receive the safe default. */
  eoStudyDefinition?: string | null;
  /** JSON.stringify(EoTransientConfigV1); absent/invalid legacy rows load unreviewed. */
  eoTransientConfig?: string | null;
  /** Historic serialized marker for a preserved read-only legacy canvas. */
  sourceBoP?: string | null;
  /** Explicit fail-closed marker emitted by generated migration drafts. */
  repairMode?: boolean;
  /** Generator capability used to distinguish normal required-input review from repair. */
  eoCapability?: string;
  /** Saved generator contract marker; boundary review remains authoritative. */
  eoContractVersion?: number;
  version?: number;
}

/** SIM-MGMT — one backup listing row (newest-first when returned from the store). */
export interface SimBackupRow {
  /** Monotonic snapshot version (doc id in the `backups` subcollection). */
  version: number;
  /** ISO-8601 capture time. */
  savedAt: string;
}

/**
 * Sim Phase 3b — a single point-in-time snapshot of the editable canvas.
 * Deep-cloned at capture so later in-place mutations of the live nodes/edges
 * cannot retro-edit the stack (node.data is mutated by the fluid-propagation
 * and NS-step paths). Only the canvas geometry/topology is captured — simState
 * (solver output) is intentionally excluded so undo restores TOPOLOGY/PARAMS,
 * not transient solver metrics.
 */
export interface CanvasSnapshot {
  nodes: AppNode[];
  edges: Edge[];
}

/** Bounded undo/redo stacks for the canvas. */
export interface CanvasHistory {
  /** LIFO stack of pre-mutation snapshots (most recent last). */
  past: CanvasSnapshot[];
  /** Redo stack (most recently undone last). */
  future: CanvasSnapshot[];
}

export type AppState = {
  nodes: AppNode[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;
  setNodes: (nodes: AppNode[]) => void;
  setEdges: (edges: Edge[]) => void;
  addNode: (node: AppNode) => void;
  removeNode: (id: string) => void;

  // ── Geo correlation (Feature #91 / #95) ──────────────────────────────
  /**
   * Recompute lat/lng for every node with `data.geoEnabled === true` from
   * its canvas position via the snapshot projection (anchored on
   * `site.location` + `site.zoom`). Manual (geo OFF) nodes are never
   * touched. One snapshot is taken around the batch for undo.
   */
  recomputeGeoForNodes: () => void;
  /**
   * Stamp `elevation` (m ASL) + `elevationSource:'auto'` on geo-enabled
   * nodes from a pre-fetched id→metres map. Only geo-enabled nodes are
   * written; misses leave the node untouched. One snapshot for undo.
   */
  applyGeoElevations: (elevations: Map<string, number>) => void;
  /**
   * Feature #95 commit path: add-or-update generated canvas nodes/edges by
   * `data.sourceSiteLayerId` (no duplicates on re-import), and explicitly
   * retire generated artifacts that disappeared from the source. One snapshot
   * for undo around the batch. Returns the exact committed node ids plus a
   * compare-and-compensate rollback for SiteLayer persistence failures.
   */
  commitGeneratedNodes: (
    nodes: AppNode[],
    edges: Edge[],
    retirements?: GeneratedCanvasRetirements,
  ) => GeneratedCanvasCommitResult;

  // ── Sim Phase 3b — canvas undo/redo history ──────────────────────────
  /**
   * Bounded undo/redo history of the simulation canvas ({nodes, edges}).
   * Lives at store top level (NOT inside the persisted SimulationTab payload —
   * see `syncActiveTab`, which only writes nodes/edges/simState/site) so the
   * history stack never bloats cloud saves. `past` is the LIFO stack of
   * pre-mutation snapshots; `future` is the redo stack. Both are capped at
   * `HISTORY_CAP` (oldest entry dropped on overflow).
   */
  canvasHistory: CanvasHistory;
  /**
   * Push a deep snapshot of the current {nodes, edges} onto `past` and clear
   * `future`. Call at the START of a DISCRETE mutation (add/remove node,
   * connect/delete edge, param-commit) — NOT on transient drag/position ticks.
   */
  snapshotForUndo: (coalesceKey?: string) => void;
  /** Restore the most recent pre-mutation snapshot; current state → `future`. */
  undo: () => void;
  /** Re-apply the most recently undone snapshot; current state → `past`. */
  redo: () => void;
  /** Selector: true iff there is at least one snapshot to undo. */
  canUndo: () => boolean;
  /** Selector: true iff there is at least one snapshot to redo. */
  canRedo: () => boolean;

  // View State
  viewMode: 'simulation' | 'siterag';
  setViewMode: (mode: 'simulation' | 'siterag') => void;
  compactView: boolean;
  setCompactView: (compact: boolean) => void;
  /**
   * #102/#106 — per-canvas-panel visibility for the editor's floating overlays.
   * All default to `true` (every panel visible). The "View" popup in
   * SimulationEditor toggles these, and each panel's render is gated on its flag
   * ({showMinimap && <MiniMap/>}, etc.).
   *
   * #115 — these are now PERSISTED on the simulation's `EditorViewState` (via
   * AutoSaver/buildViewState) and restored on load (restoreEditorViewState), so
   * a user's panel layout survives reload / exit-reopen.
   */
  showMinimap: boolean;
  setShowMinimap: (show: boolean) => void;
  showControls: boolean;
  setShowControls: (show: boolean) => void;
  showSolverStatus: boolean;
  setShowSolverStatus: (show: boolean) => void;
  showInspector: boolean;
  setShowInspector: (show: boolean) => void;
  /**
   * #115 — onboarding "guide" overlay visibility. Was a local useState in
   * SimulationEditor (lost on reload); now store-backed and persisted on
   * EditorViewState alongside the four panel flags. Default visible.
   */
  showGuide: boolean;
  setShowGuide: (show: boolean) => void;
  /** #102/#106 — reset all four canvas panels back to visible. */
  restoreAllPanels: () => void;

  // ── #116 — Layers/Cycles panel: per-component visibility + solve membership ──
  // Both are tracked as id SETS held as arrays (serialisable React Flow ids).
  // Visibility remains session-scoped presentation state. Solve membership is
  // physics input and is mirrored to the active tab/cloud model. Membership is
  // an EXCEPTION list, so legacy models with no field still solve every node.
  /** Node ids visually hidden on the canvas (still on `nodes`, not deleted). */
  hiddenNodeIds: string[];
  /** Node ids excluded from the solve payload (still on `nodes`). */
  ignoredNodeIds: string[];
  /** Toggle a single component's canvas visibility. `hidden=true` ⇒ hide. */
  setNodeHidden: (id: string, hidden: boolean) => void;
  /** Toggle a single component's solve membership. `ignored=true` ⇒ exclude. */
  setNodeIgnored: (id: string, ignored: boolean) => void;
  /** Hide/show every node whose `data.cycleId` matches (ungrouped ⇒ `null`). */
  setCycleHidden: (cycleId: string | null, hidden: boolean) => void;
  /** Ignore/solve every node whose `data.cycleId` matches (ungrouped ⇒ `null`). */
  setCycleIgnored: (cycleId: string | null, ignored: boolean) => void;
  /**
   * S8 — live React Flow viewport (pan + zoom) for the active canvas, captured
   * on move-end and persisted onto the active tab via `syncActiveTab`. `null`
   * means "no saved framing yet" → the editor uses `fitView`.
   */
  flowViewport: { x: number; y: number; zoom: number } | null;
  setFlowViewport: (viewport: { x: number; y: number; zoom: number } | null) => void;
  /** Active color overlay variable (null = off) */
  colorOverlay: string | null;
  setColorOverlay: (variable: string | null) => void;
  /**
   * F1 — simulation canvas backdrop. 'grid' = the default dot grid; 'map' /
   * 'satellite' render a DECORATIVE Google Maps backdrop (via the editor's own
   * APIProvider) centred on the active site for spatial context. The backdrop
   * is non-interactive and NOT geo-anchored to node positions (see
   * MapBackgroundLayer) — it is context, not a coordinate substrate.
   */
  canvasBackgroundMode: 'grid' | 'map' | 'satellite';
  setCanvasBackgroundMode: (mode: 'grid' | 'map' | 'satellite') => void;
  /**
   * F1 — canvas scale: how many real-world metres one React Flow unit
   * represents. Drives the scale bar + proportional component glyph sizing.
   */
  canvasMetersPerFlowUnit: number;
  setCanvasMetersPerFlowUnit: (metersPerUnit: number) => void;
  site: Site | null;
  setSite: (site: Site | null) => void;

  // SiteRAG State
  siteRag: SiteRagState;
  userApiKeys: import('../types/siteRag').UserApiKeys;
  setUserApiKeys: (keys: import('../types/siteRag').UserApiKeys) => void;
  // Workflow Prompt 13 — generateSiteRagReport shim removed. Every
  // caller in the app now uses generateTypedReport directly; legacy
  // positional-args entrypoint had zero remaining call sites.
  /**
   * Generate a report. SC2-2 added the optional `siteConfigOverrides` arg
   * which carries the per-report overrides set in the wizard / Rerun
   * panel. The store stamps the resolved (override → site → empty)
   * values onto `report.*AtGen` BEFORE the LLM call so the prompt
   * builders read frozen snapshots, never the live site doc.
   *
   * The legacy positional `typedPreferences` + `siteContextOverride`
   * args are preserved for back-compat — existing callers (rerun fast
   * path, the legacy NewReportWizard) still work without touching
   * `siteConfigOverrides`.
   */
  generateTypedReport: (
    siteId: string,
    reportTypeId: import('../config/reportTypes').ReportTypeId,
    typedPreferences?: import('../types/siteRag').TypedPreferencesByType,
    mode?: 'new' | 'update' | 'replace' | 'add-another',
    parentReportId?: string,
    companyArchetypeOverride?: import('../types/company').CompanyArchetype,
    documentSelection?: { documentRefs: NonNullable<import('../types/siteRag').SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' },
    siteContextOverride?: import('../types/siteContext').SiteContext,
    siteConfigOverrides?: {
      projectIntentOverride?: import('../types/siteRag').ProjectIntent;
      typedPreferencesOverride?: import('../types/siteRag').TypedPreferencesByType;
      siteContextOverride?: import('../types/siteContext').SiteContext;
      /** User queued Generate while evidence collection was in flight. */
      queuedEvidenceWait?: boolean;
      /** R-71 — one-time provider coverage dispatch waivers for this run. */
      providerCoverageDispatchWaivers?: { providerId: string }[];
      /** R-3 — waive citation density gate for this dispatch. */
      allowCitationDensityOverride?: boolean;
      /** R-3 — one citation-focus regeneration attempt for this dispatch. */
      citationDensityRegenerateFocus?: boolean;
      /**
       * Compatibility field for generation tier. Omitted, investment-grade,
       * and stale legacy `draft` values all resolve to the full-quality
       * investment-grade path before dispatch.
       */
      generationTier?: import('../config/generationTier').GenerationTier;
      sectionColumnLayouts?: Record<string, 1 | 2 | 3>;
      expectedTestFitAuthorityRef?: import('../types/generationImpact').TestFitAuthorityRef;
      /** Public Sentiment rerun: re-read the external sources instead of reusing a recent scan. */
      refreshPublicSentimentScan?: true;
    },
    runRequestId?: string,
  ) => Promise<void>;
  /** Enqueue a reload-safe async report generation job (feature-flagged). */
  enqueueReportGenerationJob: (
    siteId: string,
    reportTypeId: import('../config/reportTypes').ReportTypeId,
    typedPreferences?: import('../types/siteRag').TypedPreferencesByType,
    mode?: 'new' | 'update' | 'replace' | 'add-another',
    parentReportId?: string,
    companyArchetypeOverride?: import('../types/company').CompanyArchetype,
    documentSelection?: { documentRefs: NonNullable<import('../types/siteRag').SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' },
    siteContextOverride?: import('../types/siteContext').SiteContext,
    siteConfigOverrides?: {
      projectIntentOverride?: import('../types/siteRag').ProjectIntent;
      typedPreferencesOverride?: import('../types/siteRag').TypedPreferencesByType;
      siteContextOverride?: import('../types/siteContext').SiteContext;
      queuedEvidenceWait?: boolean;
      providerCoverageDispatchWaivers?: { providerId: string }[];
      generationTier?: import('../config/generationTier').GenerationTier;
      sectionColumnLayouts?: Record<string, 1 | 2 | 3>;
      expectedTestFitAuthorityRef?: import('../types/generationImpact').TestFitAuthorityRef;
      /** Public Sentiment rerun: re-read the external sources instead of reusing a recent scan. */
      refreshPublicSentimentScan?: true;
    },
    clientRunId?: string,
  ) => Promise<string | undefined>;
  /**
   * Cancel an in-flight generation run by its activeGenerations key. Calls the
   * server cancel endpoint (rules make the job doc client-read-only) for runs
   * that reached the server (have a jobId); always clears the local run so the
   * progress card disappears immediately.
   */
  cancelReportGenerationJob: (runId: string) => Promise<void>;
  /**
   * Dismiss a Public Sentiment site-evidence hold (a run that was not
   * dispatched because the evidence needs a rebuild). Local only.
   */
  dismissSentimentEvidenceHold: (holdId: string) => void;
  /**
   * "Run again" for a Public Sentiment hold whose automatic wait/retries ran
   * out: re-sends the held run's original request (same run id). No-op when
   * the hold is gone or has no recorded request (e.g. after a page reload).
   */
  retrySentimentEvidenceHold: (holdId: string) => Promise<string | undefined>;
  /**
   * Read-only: whether a Public Sentiment request that resolved without a job
   * is `waiting` for a site-evidence build or `held` (see
   * `publicSentimentEvidenceDisposition`). Lets callers that toast "started"
   * after `generateTypedReport` resolves skip it for a run that has not started.
   */
  publicSentimentEvidenceDisposition: (target: {
    siteId: string;
    reportTypeId: string | undefined;
    runId?: string;
  }) => PublicSentimentEvidenceDisposition;
  /** Subscribe to in-flight reportGenerationJobs and hydrate activeGenerations. */
  subscribeReportGenerationJobs: (uid: string) => void;
  unsubscribeReportGenerationJobs: () => void;
  /**
   * RED-ERROR-CLEAR FIX (#8) — clear the GLOBAL `siteRag.error` banner. Called
   * on every regenerate/retry entry point so a stale "Generation Failed" banner
   * never outlives a fresh attempt, regardless of which pipeline (sync vs async)
   * runs. Also backs the in-banner Dismiss button.
   */
  clearGenerationError: () => void;
  saveEditCorrections: (reportId: string, original: SiteRagReport, edited: SiteRagReport) => void;
  setActiveRagReport: (id: string | null) => void;
  /**
   * Re-fetch ONE report's semantic body from GCS and swap it into state.
   *
   * `fetchRagReports` hydrates every body up-front, but its failure path is
   * silent (`catch {}` → `markReportMetadataOnly`), so a single transient GCS
   * error leaves that report permanently `_metadataOnly` with no retry
   * anywhere. Resolves `true` when the report is renderable afterwards.
   */
  hydrateReportBody: (reportId: string) => Promise<boolean>;
  /**
   * Read the current-head bodies of one site now (another site's page, a
   * composite recompute). `fetchRagReports` reads the open site's heads first
   * and warms the rest behind the loading gate; a surface that needs a site
   * before the warm-up reaches it asks here. Idempotent and de-duplicated per
   * site (and with the warm-up's in-flight reads); resolves once every head
   * read for that site has settled, success or not, and records the site in
   * `siteRag.hydratedReportSiteIds`.
   */
  ensureSiteReportBodies: (siteId: string) => Promise<void>;
  deleteRagReport: (id: string, mode?: 'report-only' | 'with-evidence' | 'with-everything') => Promise<{ sedRemoved: boolean; docsRemoved: number; bytesFreed: number }>;
  updateRagReport: (
    id: string,
    updates: Partial<SiteRagReport>,
    options?: { strictPersist?: boolean },
  ) => Promise<void>;
  /**
   * Record the reader's decision on a sanity finding (Apply-on-rerun / Ignore /
   * un-resolve) in the report's first-class USER-STATE doc
   * (`users/{owner}/reports/{id}/userState/current`, identity-keyed) and
   * mirror it into the in-memory report. Replaces writing `sanityFindings`
   * through `updateRagReport`, which on immutable-v2 reports landed in a
   * legacy body object nothing read back (2026-08-18).
   */
  resolveSanityFinding: (
    reportId: string,
    findingIndex: number,
    next: { resolution?: 'apply' | 'ignore'; userNote?: string; applyDirective?: unknown },
  ) => Promise<void>;
  /**
   * Apply one or more source-supported audit corrections immediately through
   * the authenticated server endpoint (`POST /api/reports/:reportId/audit-corrections/apply`),
   * minting a new immutable report revision without a full AI rerun and updating
   * the in-memory report record on success.
   */
  applySourceSupportedCorrectionToReport: (
    reportId: string,
    corrections: import('../lib/reportGeneration/applySourceSupportedAuditCorrection').SourceSupportedAuditCorrectionInput[],
    options?: {
      reportOwnerUid?: string;
      expectedRevisionId?: string;
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      operationId?: string;
    },
  ) => Promise<{
    applied: boolean;
    idempotent?: boolean;
    conflict?: boolean;
    code?: string;
    report?: SiteRagReport;
    newRevisionId?: string;
    previousRevisionId?: string;
    clearedHoldFindings?: string[];
    remainingHoldFindings?: string[];
    receipt?: import('../lib/reportGeneration/applySourceSupportedAuditCorrection').SourceSupportedAuditCorrectionReceiptV1;
    error?: string;
    reason?: string;
  }>;
  /** Record an acknowledged review separately; finalized report and current SED stay unchanged. */
  saveCseConflictResolution: (
    reportId: string,
    resolutions: Record<string, string>,
    review: { basis: CseReportReviewBasis; operationId: string; signal?: AbortSignal },
  ) => Promise<CseReportReviewAck>;
  /**
   * Re-run only the Stage 2 narrative generation for an existing report and
   * merge the result. Used by the "Refresh narrative" button on degraded
   * reports — the SED + scoring already exist; only the AI call is replayed.
   * Resolves to true on success (degradedMode flag cleared), false otherwise.
   */
  regenerateNarrativeOnly: (reportId: string) => Promise<boolean>;
  /**
   * MEL-MR-2 — admin-only single-section regen. Fires the same
   * strengthened-prompt Gemini call as the gen-tail MR-1 retry, but
   * for a single user-clicked section after the report has already
   * shipped. Used by `MetaLeakChip` on `SiteRagReportView`.
   *
   * Resolves `true` when the section was regenerated + persisted,
   * `false` on any failure path (Gemini empty / threw, report not
   * found, persistence failed). Never throws — the calling chip
   * shows a toast on failure but the parent report view stays mounted.
   *
   * Admin gating lives on the chip (`isAdmin` prop); the store action
   * itself does not re-check the claim because the auditLog emit is
   * the single audit trail that matters and it carries the actor's
   * UID.
   */
  triggerSectionRegen: (reportId: string, sectionId: string) => Promise<boolean>;
  /**
   * RN-1 (Report TOC-Body regen, wave-16) — regenerate a single
   * body section of an existing report and merge the result. Thin
   * wrapper over `regenerateNarrativeOnly`: today's narrative regen
   * is full-report-scoped, but the per-section affordance exists so
   * the UI can plumb a "↻ Regenerate" button on every empty
   * placeholder TOC entry. Returns true on success.
   *
   * The `sectionId` is preserved in the regenerationReason audit
   * trail so the report's edit-history surface can identify which
   * placeholder click triggered the call. Distinct from
   * `triggerSectionRegen` (MEL-MR-2) — that surface is the meta-leak
   * fix-up entrypoint; this one is the empty-placeholder fill
   * entrypoint.
   */
  regenerateSection: (reportId: string, sectionId: string) => Promise<boolean>;
  /**
   * RN-2 (Auto-fill from provider, wave-16) — apply a single
   * provider-derived auto-fill value to an empty section's body.
   * The chip surfaces ONLY when the coverage matrix already knows
   * of a non-missing fill source for the relevant requirement;
   * clicking the chip routes here so the value is committed to the
   * report. Conservative — never auto-applies; called only on
   * explicit user click.
   *
   * Returns true on success. No-op + false when the report can't be
   * found OR the provider has no cached value (defensive — the chip
   * shouldn't be rendered in that case, but we never write garbage).
   */
  applyAutoFillToSection: (reportId: string, sectionId: string, providerId: string) => Promise<boolean>;
  /**
   * RN-3 (NWX deep prefetch, wave-16) — eagerly warm the local
   * report cache for a report the user is about to click into.
   * Wired on dashboard tile hover so the report renders instantly
   * on click. Idempotent — repeated hovers on the same id collapse
   * into a single in-flight load via the cache map. If the user
   * navigates away before the prefetch lands the result is dropped
   * silently (no toast, no error). Always resolves.
   */
  prefetchReport: (reportId: string) => Promise<void>;
  fetchRagReports: () => Promise<void>;
  repairReportData: () => Promise<{ repaired: number; failed: number }>;
  toggleComparisonSite: (id: string) => void;
  clearComparisonSites: () => void;
  toggleDemoMode: () => void;

  // Site CRUD
  fetchSites: () => Promise<void>;
  /**
   * X-USER-VIEW — load a SINGLE owner-owned site on demand for the report
   * viewer. When an admin opens a company / sub-company report, the report
   * carries `siteId` + the transient `_ownerUid`, but the company-shared site
   * sweep (`collectionGroup('sites')`) can degrade to self-only — the sites read
   * rule's get()-based `isInCompanyTree` is NOT query-safe for a LIST — leaving
   * the owning site unresolved → blank map + missing SED in the viewer. A DIRECT
   * `getDoc(users/{ownerUid}/sites/{siteId})` is authorised by the SAME per-doc
   * rule (a single get() has no list-query limitation), so it succeeds where the
   * sweep silently failed. Merges the site into the store so
   * `reportSiteFromStore` resolves it. No-op when already loaded or ids missing.
   */
  ensureReportOwnerSiteLoaded: (siteId: string, ownerUid: string) => Promise<void>;
  /**
   * LIVE-SITES — real-time `onSnapshot` subscription that keeps company-scoped
   * site state (incl. `pinnedReportIds`) propagating live to every user with
   * access. Mirrors `fetchSites`'s EXACT RBAC queries (own `users/{uid}/sites`
   * + the company-shared `collectionGroup('sites')` subtree sweep) so it can
   * never widen access. Idempotent (re-subscribing tears the prior listeners
   * down first); call `unsubscribeSites` on logout / scope change / teardown.
   */
  subscribeSites: (uid: string) => void;
  /** Tear down the live sites listener(s). */
  unsubscribeSites: () => void;
  createSite: (
    site: Omit<import('../types/site').SiteEntity, 'id' | 'createdAt' | 'updatedAt' | 'reportIds' | 'simulationIds' | 'documentIds'> & {
      draftOnly?: boolean;
      draftRequestId?: string;
      abortSignal?: AbortSignal;
      onCommitStart?: () => void;
    },
  ) => Promise<string>;
  updateSite: (id: string, updates: Partial<import('../types/site').SiteEntity>) => Promise<void>;

  // ═══ Site edit-lock (#82) ═══
  /**
   * Guard for every site-mutating entry point. Throws a `SiteLockedError`
   * when the site is locked (`locked === true`) AND the caller is neither the
   * holder (`uid === lockedByUid`) nor a platform admin nor a company admin.
   *
   * Legacy-safe: a site with no `locked` field (or `locked === false`) is
   * always writable; an unknown siteId is treated as writable (the downstream
   * write performs its own existence checks). Resolves the current uid +
   * company-admin role synchronously from the store; the platform-admin claim
   * is checked from a force-refreshed ID token (`requireAdminClaim`), so this
   * is async. Called at the TOP of `updateSite`, `deleteSite`,
   * `updateRagReport`, `saveSEDAmendment`, `saveGeneratedSimToSite`, and the
   * document service's `saveDocument` / `deleteDocument`.
   */
  assertSiteWritable: (siteId: string) => Promise<void>;
  /**
   * Lock the site for the current user (client-side via `updateSite`, the
   * holder writing their own path). No-op if already locked by someone else
   * (the rules reject a non-holder write anyway). Stamps `lockedByUid`,
   * `lockedByName`, `lockedByEmail`, `lockedAt`.
   */
  lockSite: (siteId: string) => Promise<void>;
  /**
   * Release the lock the current user holds (client-side via `updateSite`).
   * Clears every lock field with Firestore `deleteField()` so the persisted
   * doc reads as legacy-unlocked. No-op if the caller is not the holder.
   */
  unlockSite: (siteId: string) => Promise<void>;
  /**
   * Ask the current holder for edit access (SERVER-side — the server writes
   * the cross-uid `edit_access_requests/{id}` record + notifies/emails the
   * holder). No-op if the site is unlocked or the caller already holds it.
   * Returns the created request id, or null if no request was warranted.
   */
  requestEditAccess: (siteId: string, message?: string) => Promise<string | null>;
  /**
   * Approve an open edit-access request (SERVER-side). TRANSFERS the lock to
   * the requester (the site stays single-holder) and notifies/emails them.
   * Only the current holder may approve.
   */
  approveEditRequest: (requestId: string) => Promise<void>;
  /** Deny an open edit-access request (SERVER-side). Notifies/emails the requester. */
  denyEditRequest: (requestId: string) => Promise<void>;
  /**
   * Force-unlock a site as a platform or company admin (SERVER-side). Clears
   * the lock + notifies/emails the displaced holder. Throws if the caller is
   * neither a platform admin nor a company admin.
   */
  adminForceUnlock: (siteId: string) => Promise<void>;

  /** MP Phase 3 — multi-parcel boundary mutations with debounced evidence refresh. */
  addParcel: (
    siteId: string,
    polygon: Array<{ lat: number; lng: number }>,
    meta?: Partial<Pick<import('../types/siteBoundary').SiteBoundaryParcel, 'label' | 'intendedUse' | 'ownership' | 'notes'>>,
  ) => Promise<void>;
  removeParcel: (siteId: string, parcelId: string) => Promise<void>;
  updateParcelPolygon: (siteId: string, parcelId: string, polygon: Array<{ lat: number; lng: number }>) => Promise<void>;
  updateParcelMetadata: (
    siteId: string,
    parcelId: string,
    meta: Partial<Pick<import('../types/siteBoundary').SiteBoundaryParcel, 'label' | 'intendedUse' | 'ownership' | 'notes' | 'parcelId'>>,
  ) => Promise<void>;
  mergeParcels: (siteId: string, sourceIds: string[], targetId: string) => Promise<void>;
  splitParcel: (siteId: string, parcelId: string, polygons: Array<Array<{ lat: number; lng: number }>>) => Promise<void>;
  /**
   * SED rebuild telemetry — optimistic local progress patch that persists
   * to Firestore best-effort WITHOUT rolling back local state on failure.
   * Used by sedBootstrap's logProgress so Firestore write-queue exhaustion
   * does not erase lastProgressAt and falsely trigger the soft-stall banner.
   */
  patchSedRebuildProgressBestEffort: (
    siteId: string,
    progress: NonNullable<import('../types/site').SiteEntity['sedRebuildProgress']>,
    opts?: { persist?: boolean },
  ) => Promise<void>;
  /** One-shot backfill: stamps sedId on legacy sites from their most recent report's SED. Idempotent. */
  migrateLegacySiteSeds: () => Promise<void>;
  /**
   * Documents_On_Site Prompt 1 — one-shot at app load: copy each legacy
   * report's inline `documents[]` onto its owning site (dedup by id) and
   * stamp `documentRefs[]` on the report. Idempotent. Reports keep
   * `documents` for back-compat read until Prompt 2.
   */
  migrateLegacyReportDocuments: () => Promise<void>;
  /** Promote financial docs that landed only on report.documents (legacy
   *  Financial Model upload bug) onto the owning site.documents and
   *  clear the report's array. One-shot, runs after auth load. */
  migrateFinancialDocsToSite: () => Promise<void>;
  /** Retry bootstrap for a site whose SED is in `failed` state. Stamps
   *  sedStatus='bootstrapping' immediately so the UI flips, clears any
   *  prior sedFailureReason, then dispatches sedBootstrap.
   *  Single entry point shared by SedStatusPill and the failed-SED
   *  preflight banner so both surfaces stay consistent. */
  retrySEDBootstrap: (siteId: string, opts?: { forceRefresh?: boolean }) => Promise<void>;
  /**
   * SED_Stale_Data_Rebuild SD2 — full SED rebuild for a site that
   * already has an SED. Clears the SED pointer + force-fails the status
   * so `sedBootstrap`'s idempotency guard (`sedBootstrap.ts:300`) won't
   * short-circuit, then re-enters via `retrySEDBootstrap` which handles
   * the UI status flip + audit. Use after a major upstream data-quality
   * fix (PDF extraction, provider coordinates, doc classification) so
   * the SED reflects the corrected sources. Existing reports keep their
   * snapshotted SED version — only the site's canonical SED is rebuilt.
   */
  rebuildSiteSed: (
    siteId: string,
    opts?: import('../lib/sed/sedRebuildOrchestrator').SedRebuildOpts,
  ) => Promise<void>;
  /**
   * SDL-2 — destructively clear the SED state for a site. Used when a
   * user wants to abandon a stale provider-bootstrap SED (no docs
   * uploaded, no report generated, wrong-site upload, etc.) rather
   * than rebuilding it again. Differs from `rebuildSiteSed`:
   *
   * - Rebuild: keeps `site.sedId`, force-fails the status, then
   *   re-enters bootstrap so a fresh SED replaces the old one.
   * - Delete: nulls `site.sedId` + `site.sedStatus` + clears
   *   `evidenceReadiness`, tombstones the prior SED under
   *   `users/{uid}/deletedSeds/{sedId}` for audit, best-effort
   *   Storage cleanup. No bootstrap re-entry — the user explicitly
   *   chose to leave the site without an SED.
   *
   * Reports pinned to a prior SED keep their `sedSnapshot` and
   * continue rendering. User documents are NOT deleted. Site
   * project intent, context, boundary, and location are NOT touched.
   *
   * Idempotent — calling on a site whose `sedId` is already undefined
   * is a no-op (no Firestore write happens).
   */
  deleteSiteSed: (siteId: string) => Promise<void>;
  /**
   * RSF-3 — Force-abort an in-flight SED rebuild that's exceeded the
   * hard ceiling (`STUCK_BACKSTOP_ELAPSED_MS`, 30 min). Writes
   * `sedStatus='failed'` immediately + a `sedFailure` breadcrumb
   * stamping the abort cause. Composes with RSF-2's wall-clock:
   *   - RSF-2 catches the doom-loop on FUTURE runs (15-min ceiling).
   *   - RSF-3 lets users recover from an already-stuck run NOW,
   *     without waiting 15 min for RSF-2 to fire (e.g. if the
   *     watchdog itself failed to land due to a hot-reload race).
   *
   * Idempotent — calling on a site whose status is already 'failed'
   * is a no-op (the breadcrumb update would be a write-of-no-change).
   */
  forceAbortSedRebuild: (siteId: string) => Promise<void>;
  /** Section-scoped SED refresh — runs the providers backing the
   *  given SedSectionIds via topUpSiteSed's onlyProviders filter.
   *  Drives the "Refresh evidence" popover on SiteLandingPage so
   *  users can re-fetch e.g. just air quality without paying for a
   *  full deep-provider sweep. Bumps sedVersion, recomputes
   *  evidenceReadiness, surfaces progress through progressStage. */
  /** Outcome of a scoped refresh. Returned rather than thrown so the existing
   *  fire-and-forget callers are unaffected, while a caller that DID promise the
   *  user an action ("Re-run collection") can report what actually happened.
   *  `superseded` — the top-up stopped without saving because the live site
   *  pointer moved, the tab's copy was stale, or a build owns the evidence.
   *  `opts.abortSignal` cancels provider work; a run whose upload started
   *  still finishes its pointer commit. */
  refreshSedSections: (
    siteId: string,
    sectionIds: import('../types/siteEvidence').SedSectionId[],
    opts?: { abortSignal?: AbortSignal },
  ) => Promise<{ ok: boolean; persisted: boolean; unchanged?: boolean; superseded?: boolean; upgraded: string[]; reason?: string }>;
  /**
   * LLW-FOLLOWUP-SECTION-REFRESH-BUTTON — per-section "Refresh Live
   * Literature" action wired from the SED Variable Browser. Loads
   * the site's current SED, fires the externalLiteratureOrchestrator
   * via `refreshExternalLiteratureForSingleSite`, and persists the
   * updated `sed.externalLiterature` slot. Bypasses the 14d TTL gate
   * (user explicitly asked) but respects the 60s LLW-4 soft rate-
   * limit and audits via `external-literature.refreshed` with
   * `source: 'manual-section'`. Returns the outcome string the
   * caller toasts. Fail-soft: any error returns
   * `'orchestrator-failed'` with the reason logged.
   */
  refreshLiveLiteratureForSection: (siteId: string) => Promise<
    import('../services/scheduledRebuild').SchedulerOutcome['outcome']
  >;
  /**
   * Documents_On_Site Prompt 10 — recompute and persist
   * `SiteEntity.evidenceReadiness`. Called from the four trigger
   * sites: site doc updates, SED status changes, project intent
   * edits, and report-registry refreshes. Fire-and-forget: failures
   * log but never block the originating action.
   */
  recomputeEvidenceReadiness: (siteId: string) => Promise<void>;
  /**
   * Fix_React_Error_185_Prompt — true while AuthGate is replaying the
   * one-shot migrations after fetch (`migrateLegacySiteSeds`,
   * `migrateLegacyReportDocuments`). When set, `updateSite`'s readiness
   * trigger is suppressed so the migration's burst of writes doesn't
   * fan out into N synchronous recomputes that flood React's update
   * budget. The deferred batch runs via `runDeferredReadinessRecompute`
   * once the bracket exits.
   */
  isInitialMigration: boolean;
  setInitialMigration: (v: boolean) => void;
  /** Run readiness recompute for every site, throttled at 200ms/site. */
  runDeferredReadinessRecompute: () => Promise<void>;
  /**
   * SED_Stale_Data_Rebuild SD1 — backfill `sedComputedAt` on legacy
   * sites. Walks every site with `sedId && !sedComputedAt` and stamps
   * `sedComputedAt` to the site's `updatedAt` value (best-effort — the
   * actual SED build time is unknown for pre-SD1 data). Idempotent: a
   * site that already carries `sedComputedAt`, or that has no SED, is a
   * no-op. Called from AuthGate alongside the other deferred passes so
   * staleness detection (sedStaleness.isStaleSed + the bulk-rebuild
   * action) works consistently against historical sites.
   */
  backfillSedComputedAt: () => Promise<void>;
  /**
   * Dashboard_Zero_Report_Filter_Fix ZR2 — heal stale `site.reportIds`.
   * Walks every site, discovers reports linked via `report.siteId` or a
   * case-insensitive `siteName` match (the triple-join's 2nd + 3rd legs;
   * the 1st leg is `reportIds` itself), and appends any ids the array is
   * missing — idempotently, with one `updateSite` write per healed site.
   * Called once on session start from AuthGate's post-fetch migration
   * bracket so historical drift fixes itself on the next reload, and
   * (cheaply, as a near no-op) every reload after that.
   */
  reconcileSiteReportIds: () => Promise<void>;
  /**
   * Evidence_Setup_Auto_And_Modes ES1 — content-classify every site's
   * yet-unclassified documents (docs with no `autoTaggedAt` stamp), then
   * force a readiness recompute for that site so the dashboard chip + Docs
   * % update on the same page load. Walks one site every ~250ms so a 20-site
   * portfolio takes ~5s and never saturates React's update budget. Idempotent
   * — the per-doc skip-guard inside `classifyAndMergeDocTags` (and our own
   * `autoTaggedAt` filter) makes a warm reload a near no-op. Called from
   * AuthGate alongside `runDeferredReadinessRecompute`. Replaces the manual
   * "Backfill document tags" AdminPanel click on the user-facing path (the
   * button stays for ops re-classification after model-prompt tweaks).
   */
  runDeferredDocClassifierBackfill: () => Promise<void>;
  /**
   * Evidence_Setup_Auto_And_Modes ES1 — classify the un-tagged documents on
   * one site (reuses AC2's `classifyAndMergeDocTags` helper from
   * `docClassifier`), persist the augmented array via `updateSiteDocuments`,
   * then force `recomputeEvidenceReadiness` so the chip updates immediately.
   * No-op when the site has no docs or every doc already carries
   * `autoTaggedAt`. Also exposed for the AdminPanel manual override.
   */
  backfillDocTagsForSite: (siteId: string) => Promise<void>;
  /** Replace the entire siteLayers set on a site. Persists as id-keyed subcollection docs. */
  updateSiteLayers: (
    id: string,
    layers:
      | import('../types/site').SiteLayer[]
      | ((
          current: readonly import('../types/site').SiteLayer[],
        ) => import('../types/site').SiteLayer[]),
    /** Revision captured when a long-lived editor draft was initialized. */
    expectedRevision?: number,
  ) => Promise<void>;
  /** Adopt the complete canonical layer set returned by a generation publish. */
  adoptPublishedGenerationLayers: (
    id: string,
    layers: import('../types/site').SiteLayer[],
    revision: number,
  ) => void;
  /**
   * Atomically patch KMZ canvas linkage fields against the latest authoritative
   * owner-site document. Fails when a source fingerprint drifted or vanished.
   */
  updateSiteLayerCanvasLinks: (
    id: string,
    patches: readonly SiteLayerCanvasLinkPatch[],
  ) => Promise<void>;
  /**
   * Fetch regional ArcGIS / PeeringDB layers within a radius and merge into
   * `site.siteLayers` via content-hash dedup.
   */
  fetchAndMergeRegionalLayers: (
    siteId: string,
    opts: { catalogEntryIds: string[]; customUrls?: string[]; radiusMiles: number; refdataOnly?: boolean },
  ) => Promise<{ added: number; skipped: number; warnings: string[] }>;
  /**
   * Site_Context SC2 — write the freeform site-context prose for a site,
   * run the claim extractor (Gemini Flash, SC3) and the deterministic
   * cross-checker (SC4), then persist the resulting SiteContext on the
   * site doc. Audit-log entry emitted on every write.
   *
   * Snapshots are taken at report-generation time
   * (`generateTypedReport` writes `report.siteContextSnapshot`) so prior
   * reports stay reproducible even after later edits here.
   *
   * Best-effort: extractor failures fall through to raw-text-only
   * persistence; the cross-checker is pure and never throws on missing
   * SED fields.
   */
  updateSiteContext: (siteId: string, text: string) => Promise<void>;
  /**
   * CLP-2 — re-run `extractClaims` against the current
   * `site.siteContext.text` without requiring the user to edit the
   * prose first. Persists the resulting status (success / quota-error
   * / etc.) onto the site doc.
   *
   * Used by:
   *   - The `[ Re-extract claims ]` button below the textarea, so
   *     users can manually retry after a Gemini quota refresh or the
   *     VTX migration deploys.
   *   - The chip's inline "Retry" affordance on `*-error` statuses.
   *
   * No-op when `siteContext.text` is empty or the site has no
   * `siteContext`. Concurrent invocations are de-duped through the
   * `extractorStatus === 'extracting'` guard set on the optimistic
   * update.
   */
  retryExtractClaims: (siteId: string) => Promise<void>;
  /**
   * Operator answers a report question. SiteContext is canonical; the report
   * and SED receive durable projections for reloads and future generations.
   */
  answerOpenQuestion: (input: {
    siteId: string;
    openQuestion: import('../lib/findingClaim').FindingClaim;
    answer: import('../lib/findingClaim').OpenQuestionAnswerValue;
    details?: string;
    reportId: string;
    sectionId?: string;
  }) => Promise<import('../lib/findingClaim').AnswerOpenQuestionResult>;
  /**
   * SCE-3 — soft-flag a SiteContext claim as a typed extractor failure.
   *
   * The claim STAYS in `siteContext.claims[]` for audit; downstream
   * read-side surfaces (diff tab, cross-checker, prompt block) filter
   * out `state === 'mis-extracted'` rows so the claim no longer
   * participates in the SED reconciliation. The store re-runs
   * `crossCheckClaims` after the flag lands so the conflict list
   * reflects the new disposition.
   *
   * Concurrent invocations are safe — the optimistic update only
   * mutates the claim's `state`/`misExtractedAt`/`misExtractedBy`
   * fields; concurrent writes converge.
   *
   * No-op when the claim id is not found or the site has no
   * `siteContext`.
   */
  markClaimMisExtracted: (siteId: string, claimId: string) => Promise<void>;
  /**
   * Record a human decision about one document-extracted field.
   *
   * Accept records that someone checked the value against the document.
   * Correct replaces it. Reject DELETES it — an extraction a human has called
   * wrong must not survive, because everything downstream reads a present
   * value as evidence. Pass `decision: null` to undo, restoring whatever the
   * decision removed or replaced.
   */
  reviewExtractedField: (
    siteId: string,
    sectionId: string,
    fieldKey: string,
    decision: import('../lib/coverage/extractedFieldReview').FieldReviewDecision | null,
    correctedValue?: import('../types/siteEvidence').StructuredEvidenceValue,
  ) => Promise<{ ok: boolean; reason?: string }>;
  /**
   * Remove a deleted document's machine contributions from the SED —
   * unreviewed extracted fields it stamped, sole-source section payloads it
   * filled (downgraded honestly to `unavailable`), and every dangling
   * linkage. Human decisions (accepted/corrected reviews) survive. Called
   * from the delete dialog when the user opts to remove the data with the
   * document; a no-op scrub skips the save entirely.
   */
  scrubDocumentFromSed: (
    siteId: string,
    docId: string,
  ) => Promise<{ ok: boolean; removedFieldCount?: number; downgradedSections?: string[]; reason?: string }>;
  /**
   * UFR-A — "Adopt SED" action from the SED-vs-Context diff tab.
   *
   * Writes the selected row's SED value onto the matching Site Context
   * claim, appends `syncHistory` with `resolution: 'adopt-sed'`, persists
   * to the site doc, and emits a `sed_context_sync` audit event.
   *
   * Idempotent: if the claim already equals `sedValue`, this is a no-op.
   */
  adoptSedValueIntoContext: (
    siteId: string,
    identity: string,
    sedValue: import('../types/siteContext').ExtractedClaim['claimedValue'] | null,
  ) => Promise<void>;
  /**
   * UFR-A — "Adopt Context" action from the SED-vs-Context diff tab.
   *
   * Drafts a single-path amendment against the row's `sedPath` using the
   * provided `contextValue`, delegates the SED write-through to
   * `saveSEDAmendment`, then appends claim `syncHistory` with
   * `resolution: 'adopt-context'` and persists Site Context.
   *
   * Idempotent: if the SED already equals `contextValue`, this is a no-op.
   */
  adoptContextValueIntoSed: (
    siteId: string,
    identity: string,
    contextValue: import('../types/siteContext').ExtractedClaim['claimedValue'] | null,
    claimId?: string,
  ) => Promise<void>;
  /**
   * Site_Configuration_Consolidation SC2-1 — write the site's canonical
   * `projectIntent` (stamped on `site.projectIntent`) and update the
   * `siteConfigUpdatedAt` / `siteConfigUpdatedBy` provenance.
   *
   * IC10 — the legacy `defaultProjectIntent` mirror has been retired IC10-SENTINEL-OK
   * (data migrated via `scripts/migrateDropDefaultProjectIntent.ts`).
   * Only `site.projectIntent` is written now.
   */
  updateSiteProjectIntent: (siteId: string, intent: import('../types/siteRag').ProjectIntent, options?: {
    expectedDecisionHash?: string;
    phasePlanApplication?: import('../types/projectIntentUpdate').PhasePlanApplication;
  }) => Promise<void>;
  /**
   * R-65/R-71 — append audit override with rationale (one-time dispatch waiver).
   * A caller-supplied `id` opts into UPSERT semantics: consumers key resolve
   * decisions deterministically (`validate:{findingId}` / `recon:{findingId}`),
   * so re-deciding the same finding must replace the prior entry, not stack.
   */
  recordAuditOverride: (
    siteId: string,
    override: import('../types/site').CreateSiteAuditOverride & { id?: string },
    options?: {
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      operationId?: string;
    },
  ) => Promise<import('../types/site').SiteAuditOverride>;
  /** Append a retraction override (Undo for validate/reconcile/dismiss decisions) preserving prior history. */
  removeAuditOverride: (
    siteId: string,
    overrideId: string,
    options?: {
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      operationId?: string;
      rationale?: string;
    },
  ) => Promise<void>;
  /** R-71 — re-fetch a single provider's SED section(s). */
  retryProviderFetch: (siteId: string, providerId: string) => Promise<void>;
  /**
   * Site_Configuration_Consolidation SC2-1 — write a per-report-type
   * preference default onto the site. Other report-type entries on
   * `typedPreferencesDefaults` are preserved; only the named slot is
   * replaced. Stamps `siteConfigUpdatedAt` / `siteConfigUpdatedBy`.
   *
   * Zero data migration: callers should never read this field through
   * `site.typedPreferencesDefaults` directly — go through
   * `resolveSiteConfig()` so the override → site → empty waterfall is
   * applied consistently.
   */
  updateSiteTypedPreferencesDefault: <T extends import('../config/reportTypes').ReportTypeId>(
    siteId: string,
    reportType: T,
    preferences: NonNullable<import('../types/siteRag').TypedPreferencesByType[T]>,
  ) => Promise<void>;
  /** Save or update a Custom Brief template on the site. */
  saveSiteCustomBrief: (
    siteId: string,
    preferences: import('../types/siteRag').CustomBriefPreferences,
    existingId?: string,
  ) => Promise<string>;
  /** Remove a saved Custom Brief template from the site. */
  deleteSiteCustomBrief: (siteId: string, briefId: string) => Promise<void>;
  /**
   * Financial Assumptions (FA1) — write the site's canonical
   * `financialAssumptions` in one shot. Stamps `updatedAt` /
   * `updatedBy` on the assumptions payload. Used by the post-upload
   * commit path (FA5 review modal) and the "save baseline" path (FA4).
   */
  updateSiteFinancialAssumptions: (
    siteId: string,
    assumptions: import('../types/financialAssumptions').FinancialAssumptions,
  ) => Promise<void>;
  /**
   * FA1 — patch a single field on the site's `financialAssumptions`.
   * Lazily seeds an empty `FinancialAssumptions` when the site has
   * none yet. The provided `provenance` is stamped on
   * `fieldProvenance[field]`; `updatedAt`/`updatedBy` are stamped on
   * the assumptions payload.
   *
   * Used by the FinancialAssumptionsForm's per-field save path so a
   * user typing into one cell doesn't have to re-submit the whole
   * set.
   */
  /**
   * Per-field campus patch. PHASE-AWARE: rollup-derived fields (capacity,
   * CAPEX, $/kW, capacity factor, in-service date) are written onto the single
   * phase, because `phases[]` is authoritative on write and a core-only write
   * is discarded by the next save. On a MULTI-phase model such a field cannot
   * be attributed to a phase, so the write is REFUSED with `{ ok: false }` —
   * callers must surface that rather than reporting success.
   */
  /**
   * D9 — recompute the DETERMINISTIC financial stamp on every Financial report
   * of a site, because the model underneath them changed. No LLM, no narrative
   * rewrite: `report.financialAnalysis`, `overallScore` and the deterministic
   * conclusion fields follow the current assumptions, while the written
   * analysis and `financialAssumptionsAtGen` stay as generated so the staleness
   * banner keeps flagging the prose.
   *
   * Called by every financial-assumptions writer. The IN-MEMORY update lands
   * immediately (the tab is truthful on the next render); the PERSIST is
   * debounced, so a user typing through a dozen fields writes once rather than
   * a dozen times. Never throws — an FA save must not fail because a report
   * could not be re-stamped.
   */
  restampFinancialReportsForSite: (siteId: string) => Promise<{ restamped: number }>;
  updateSiteFinancialAssumptionsField: <
    K extends keyof import('../types/financialAssumptions').FinancialAssumptionsCore,
  >(
    siteId: string,
    field: K,
    value: import('../types/financialAssumptions').FinancialAssumptionsCore[K],
    provenance: import('../types/financialAssumptions').FieldProvenance,
  ) => Promise<{ ok: true } | { ok: false; error: string } | void>;
  /**
   * Supply_Chain_BoM_Wire_Up SC4-1 — seed (or re-seed)
   * `site.supplyChainPlan.bom` from the resolved Project Intent +
   * Financial Assumptions. PP-aware: when `intent.phases` is non-empty,
   * delegates to `generateBomFromPhases`; otherwise to
   * `generateBomFromContext`. Stamps `lastSeededAt` / `lastSeededBy` /
   * `seedSource` on the plan and emits a `scm.bom_seeded` audit event.
   *
   * Resolves with `{ ok: true, itemCount }` on success or
   * `{ ok: false, error }` when Project Intent is missing / the site
   * cannot be found / persistence fails. Never throws — the calling UI
   * branches on the result.
   *
   * NOTE: the canonical supply-chain plan is site-level (see
   * `SiteEntity.supplyChainPlan`), so this action takes a `siteId`. The
   * pack spec uses `reportId` as a shorthand because reports inherit
   * the plan via the SC2-style resolver waterfall; in this codebase the
   * write target is the site.
   */
  generateBomFromIntent: (args: {
    siteId: string;
    /** Optional override — when omitted, resolves from `site.projectIntent`. */
    intent?: import('../types/siteRag').ProjectIntent;
    /** Optional override — when omitted, resolves from `site.financialAssumptions`. */
    financial?: import('../types/financialAssumptions').FinancialAssumptions;
    /** Whether the seed was kicked off automatically on mount (SC4-2) or
     *  triggered by an explicit user click. Recorded on the audit
     *  metadata so the Activity tab can separate drift-driven from
     *  user-driven seeds. Defaults to `'manual'`. */
    trigger?: 'manual' | 'auto-seed';
  }) => Promise<{ ok: true; itemCount: number } | { ok: false; error: string }>;
  updateSupplyChainPlan: (
    siteId: string,
    updater: (plan: import('../types/supplyChain').SupplyChainPlan) => import('../types/supplyChain').SupplyChainPlan,
  ) => Promise<{ ok: true; plan: import('../types/supplyChain').SupplyChainPlan } | { ok: false; error: string }>;
  /** Atomically bind a current site document to a BoM entry or manual quote. */
  linkBomSourceDocument: (siteId: string, documentId: string, change: BomSourceDocumentChange) => Promise<void>;
  applyBomFromUpload: (args: {
    siteId: string;
    items: import('../types/supplyChain').BomItem[];
    mode: 'create' | 'merge';
    documentId?: string;
  }) => Promise<{ ok: true; itemCount: number } | { ok: false; error: string }>;
  addSupplierFromDirectory: (args: {
    siteId: string;
    supplierId: string;
    companyId?: string | null;
  }) => Promise<{ ok: true } | { ok: false; error: string }>;
  updateBomItemStatus: (
    siteId: string,
    itemId: string,
    toStatus: import('../types/supplyChain').BomItemStatus,
    opts: { revert: boolean; reason?: string },
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  attachDeliveryReceipt: (args: {
    siteId: string;
    itemId: string;
    document: Pick<import('../types/siteRag').SiteDocument, 'id' | 'fileName' | 'mimeType' | 'uploadedAt'>;
    notes?: string;
    lat?: number;
    lng?: number;
  }) => Promise<{ ok: true } | { ok: false; error: string }>;
  applyRfqPipelineResult: (
    siteId: string,
    result: import('../services/supplyChain/processRfqResponse').ProcessRfqResponseResult,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  awardOrRejectQuote: (
    siteId: string,
    quoteId: string,
    action: 'award' | 'reject',
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  /*
   * F1 / decision Q5 — `syncCapexFromSupplyChainPlan` WAS HERE AND IS RETIRED.
   *
   * It wrote the BoM CAPEX rollup at CAMPUS level. `phases[]` is
   * authoritative, so on any multi-phase model it could not attribute the
   * figure and refused — while both Sync buttons in the supply-chain UI,
   * including the one on the PER-PHASE rollup strip, called it. The
   * phase-aware `pullFinancialFromSources` below is now the single BoM → FA
   * route, and it carries the `procurementCommercials` snapshot this action
   * used to own. Two writers for one job is how they drift apart.
   */
  /**
   * Layout → BOM live-link (test-fit yield program) — derive site-works
   * civil-construction quantities (fencing, pads, roads, clearing, route
   * corridors, laydown) from the PROMOTED generation scenario's
   * deterministic parcel test fit and merge them into
   * `site.supplyChainPlan.bom`. Quantities are screening-grade and unpriced
   * (`unitCostUsd: null`); pricing flows through quotes and reaches CAPEX
   * via `syncCapexFromSupplyChainPlan`. Re-running updates quantities in
   * place while preserving pricing/status/supplier edits on matched lines.
   */
  syncSiteWorksBomFromLayout: (
    siteId: string,
  ) => Promise<
    | { ok: true; added: number; updated: number; removed: number }
    | { ok: false; error: string }
  >;
  /**
   * Financial auto-populate (R-58) — pull native per-phase financial
   * values from the site's REAL, present sources only:
   *   - BOM line items   → per-phase `capexTotalUsd` / `capexPerKwUsd`
   *     (sum of `BomItem.totalCostUsd`, grouped by phaseId).
   *   - project-intent   → per-phase `capacityMW` / `inServiceDate`.
   *
   * INVESTMENT-GRADE HARD RULE: never fabricate or guess a value. A field
   * with no real source is left null/blank and reported in
   * `skippedUnsourced`. Every auto-filled field is stamped
   * `FieldProvenance { source:'derived', confidence:'medium', cellReference,
   * extractedAt }` with cellReference `'supplyChainPlan.bom'` (BOM rollup)
   * or `'projectIntent.phases[i]'` (intent). A user `'manual'` field is
   * NEVER clobbered — only blanks are filled, and only previously-`'derived'`
   * fields are refreshed (per-phase provenance is persisted on
   * `financialAssumptions.phaseFieldProvenance`).
   *
   * Bumps `modelVersion` to 2 (native phases) when phases were produced,
   * stamps `updatedAt`/`updatedBy`, persists via the FA write path, and
   * emits a `site.financial_assumptions_update` audit breadcrumb.
   *
   * Never throws — the calling UI branches on the result.
   */
  pullFinancialFromSources: (
    siteId: string,
  ) => Promise<
    | {
      ok: true;
      phaseCount: number;
      filledFieldCount: number;
      skippedUnsourcedCount: number;
      /** Campus + per-phase financial fields filled by the CBE third source. */
      contextFilledCount: number;
      /** Same-field divergences the CBE surfaced (not silently picked). */
      contextConflictCount: number;
      /** Range context values left unfilled (no scalar midpoint). */
      contextRangeHintCount: number;
      /**
       * A4 — priced BoM cost that did NOT reach the per-phase rollup (USD).
       * Non-zero means the financial model is SHORT by this much: lines whose
       * `phaseId` matches no phase, or that sit in a phase with no capacity,
       * drop out of the rollup. The caller must not report an unqualified
       * success over a leak.
       */
      capexUnreconciledUsd: number;
      /** A4 — BoM lines whose `phaseId` matches no phase. */
      capexOrphanedLineCount: number;
    }
    | { ok: false; error: string }
  >;
  /**
   * Process and persist a site-level document. Drives the SiteLandingPage
   * Documents tab so users can upload supporting material BEFORE the first
   * report exists. Pipeline: extract text → Gemini summarise → tag → save full
   * doc to GCS + metadata to Firestore (`users/{uid}/documents/{docId}`),
   * then push the SiteDocument onto `site.documents` and updateSite.
   * Resolves to the new SiteDocument so the caller can scroll to it.
   */
  addSiteDocument: (
    siteId: string,
    file: File,
    opts?: {
      folder?: string;
      linkedBomItemIds?: string[];
      appendTags?: string[];
      intendedRequirementIds?: string[];
      intentSource?:
        | 'coverage-matrix-upload-button'
        | 'coverage-matrix-replace-button'
        | 'free-drop'
        | 'bulk-import'
        | 'rerun-attach';
      intentSetBy?: string;
    },
  ) => Promise<import('../types/siteRag').SiteDocument>;
  /**
   * SOFT-delete a doc on this site. Pre-DDL-FOLLOWUP this was a hard
   * delete that filtered the doc out of `site.documents` (and every
   * `report.documents` on the site) plus deleted the user-level index
   * row + a separate 30-day re-ingest tombstone in
   * `users/{uid}/deletedDocuments/{docId}` (DLB-3). The hard-delete
   * path was a data-loss footgun: a misclick was unrecoverable.
   *
   * The new contract stamps `deletedAt` (ISO) + `deletedBy` (uid) on
   * the SiteDocument record in place across the same cross-array
   * sweep, hides the doc from the default Documents-tab read path
   * (`isLiveDoc` in `src/lib/documentSoftDelete.ts`), and DOES NOT
   * delete the user-level index row or the GCS bytes. Restore is
   * `restoreSiteDocument` below. After `SOFT_DELETE_TTL_DAYS` (7) the
   * doc is eligible for the purge cron, which hard-deletes it (and
   * writes the DLB-3 re-ingest tombstone at that point).
   *
   * The DLB-3 separate re-ingest tombstone collection is still written
   * here because the user's intent ("don't accidentally re-ingest the
   * canonical-name doc that I just deleted") survives the soft / hard
   * boundary — and the upload path's tombstone check already keys on
   * that collection, not on the in-place flag.
   */
  removeSiteDocument: (siteId: string, docId: string) => Promise<void>;
  /**
   * DDL-FOLLOWUP-RESTORE — restore a soft-deleted SiteDocument. Nulls
   * `deletedAt` / `deletedBy` on the doc record in place across the
   * DDL-1 cross-array sweep (site.documents + every report.documents
   * on the site) so the SiteLandingPage union dedupe can't reapply a
   * stale tombstone from a report copy on the next render. Best-effort
   * persistence + per-report failure isolation, mirroring the
   * `removeSiteDocument` shape.
   *
   * No-op (silent return) when the doc isn't soft-deleted (defensive
   * — admins can click Restore on a row that races with another tab's
   * restore without erroring) or when the doc doesn't exist on the
   * site (the action is keyed on docId; legacy ghost rows resolve
   * cleanly).
   */
  restoreSiteDocument: (siteId: string, docId: string) => Promise<void>;
  /**
   * DLB-3 — bulk variant of `removeSiteDocument`. One Firestore batch
   * for tombstones + index deletes + site patch, plus best-effort GCS
   * cleanup per doc and one updateRagReport per affected report.
   * Reuses the same cross-array sweep shape (site.documents +
   * documentIds + every report.documents + documentRefs +
   * siteContext.claims) so the SiteLandingPage union dedupe can't
   * resurrect any of the deleted docs. Caller-supplied reason string
   * is recorded on each tombstone for the admin Ghost Docs panel.
   * Auto-chunks above 200 docs per call to stay under Firestore's
   * 500-write batch limit.
   */
  bulkRemoveSiteDocuments: (
    siteId: string,
    docIds: string[],
    reason?: 'noise' | 'older-revision' | 'bulk-manual',
  ) => Promise<void>;
  /** Replace the entire `site.documents` array — used by DocumentLibrary edits (rename, folder move, priority toggle). */
  updateSiteDocuments: (siteId: string, documents: import('../types/siteRag').SiteDocument[], expectedDocumentsBasis?: string) => Promise<void>;
  /**
   * The site's SED pointer and build state read from the SERVER (never the
   * local cache) at the owner path. `null` when the store does not hold the
   * site or the doc is gone. Throws without a signed-in user or on read error.
   */
  readLiveSedHead: (siteId: string) => Promise<import('../lib/sed/sedPointerCas').LiveSedHead | null>;
  /**
   * SED pointer compare-and-set. In one transaction: refuse unless the live
   * pointer equals `expected`; refuse with `requireIdle` while a build holds
   * the site; otherwise write `patch`, plus `statusPatch` only when no build
   * holds the site. Keeps `updateSite`'s owner path, `lastEditedBy`,
   * transient/document-body stripping and readiness recompute.
   */
  commitSedPointerIfUnchanged: (
    siteId: string,
    expected: import('../lib/sed/sedPointerCas').SedPointer,
    patch: Partial<import('../types/site').SiteEntity>,
    opts?: import('../lib/sed/sedPointerCas').SedPointerCommitOptions<Partial<import('../types/site').SiteEntity>>,
  ) => Promise<import('../lib/sed/sedPointerCas').SedPointerCommitResult>;
  /**
   * DDL-3 — cross-array per-doc patch. Applies the same partial update to
   * `site.documents[i]` AND every `report.documents[i]` on the site so the
   * SiteLandingPage union dedupe can't re-introduce the pre-edit copy on
   * the next render. Same divergence shape as the DDL-1 delete fix, but
   * for rename / folder move / priority toggle / includeInReport flip.
   * Passing `null` to clear a field is supported by the caller using
   * `Partial<SiteDocument>` with `undefined`.
   */
  patchSiteDocument: (
    siteId: string,
    docId: string,
    patch: Partial<import('../types/siteRag').SiteDocument>,
  ) => Promise<void>;
  /**
   * DMF-1 — move a doc to a different folder. Thin wrapper around
   * `patchSiteDocument` that writes `{ folder, movedAt }` so the
   * DDL-3 cross-array parity (site.documents + every report.documents)
   * applies verbatim. Stamping `movedAt` lets downstream surfaces
   * (audit log, "recently moved" filters) distinguish folder changes
   * from rename / priority / includeInSed edits. The folder string
   * is stored as-is — empty string clears the folder override and
   * lets the auto-categorizer pick the bucket again.
   */
  moveSiteDocument: (
    siteId: string,
    docId: string,
    newFolder: string,
  ) => Promise<void>;
  /**
   * DF-1 — folder bulk-rename with cross-array atomicity. Pre-DF-1 the
   * folder-rename inline handler in `DocumentLibrary.tsx` mapped over
   * the UNION (`freshDocs()`) and pushed the patched array through
   * `commit()` — but `commit()` only writes ONE side (the host's
   * `onUpdateDocuments` callback persists `site.documents`; the cross-
   * report copies are untouched). On the next render, the
   * SiteLandingPage union dedupe (site → latestReport → siteReports,
   * last-wins) restores `folder: <oldName>` from a stale
   * `report.documents` copy. Same X-array divergence shape as the
   * DDL-1 delete bug — folder renames need to land on `site.documents`
   * AND every `report.documents` on the site in one set-state-builder.
   *
   * Match semantics mirror the inline handler's filter — a doc is
   * "in" the folder if EITHER `d.folder === oldName` (explicit
   * override) OR `categorize(d) === oldName` (auto-bucket).
   * `documentRefs` is walked for completeness even though no folder
   * field lives on refs — the walk asserts the invariant and is a
   * no-op for current ref shape.
   *
   * Persistence: single Firestore `writeBatch` (site update + N
   * report updates) — if any path of the batch fails, all writes roll
   * back, mirroring DDL's `bulkRemoveSiteDocuments` shape. Empty /
   * unchanged folders short-circuit before any writes.
   */
  renameFolder: (
    siteId: string,
    oldName: string,
    newName: string,
  ) => Promise<void>;
  /**
   * DDL-5 — single-doc / batch append for `site.documents`. Pre-DDL the
   * upload handler wrote `[...union, ...newDocs]` back through
   * `updateSiteDocuments` — every upload re-pushed the entire union
   * (including report-derived copies) into `site.documents`, causing the
   * monotonic-growth + un-deletable doc class. This action appends without
   * re-reading the union.
   */
  appendSiteDocuments: (
    siteId: string,
    newDocs: import('../types/siteRag').SiteDocument[],
  ) => Promise<void>;
  /**
   * DVF-3 — single-doc re-extraction. Downloads the raw bytes from
   * `doc.rawStoragePath`, re-runs the type-appropriate extractor
   * (`extractPdfTextFromBytes` for PDFs, `extractTextFromFile` for
   * DOCX/XLSX/PPTX/image), and writes the new `textContent` plus a
   * `pdfTextReprocessedAt` / `officeReprocessedAt` stamp back to the
   * doc via `patchSiteDocument` (so both `site.documents[i]` and every
   * `report.documents[i]` on the site stay in sync). User-facing
   * recovery action surfaced by the DocViewerDialog when a doc opens
   * with an empty `textContent` — re-extraction is what unblocks the
   * "AI text" tab + "View extracted text" link. The doc is located by
   * scanning every site's documents array (doc IDs are user-unique).
   * Throws on missing doc / raw bytes so the caller can surface a
   * toast. Returns the new textContent for the caller to display.
   */
  reExtractDocument: (docId: string) => Promise<string>;
  /**
   * Evidence_Setup_Auto_And_Modes ES3 — set the site's `evidenceMode`
   * (and stamp `evidenceModeSetAt`). Persists via `updateSite` and forces
   * a `recomputeEvidenceReadiness` so the dashboard chip + Data-readiness
   * surfaces update on the same interaction (an `'ai-only'` site treats
   * 0% doc coverage as intentional; `'docs-included'` does not). Idempotent
   * — re-setting the same mode is a cheap no-op apart from the timestamp.
   */
  setSiteEvidenceMode: (siteId: string, mode: NonNullable<import('../types/site').SiteEntity['evidenceMode']>) => Promise<void>;
  /**
   * Evidence_Setup_Auto_And_Modes ES3 — doc-upload completion hook. Reads
   * the site's current `evidenceMode` + doc count and, when the rule says so
   * (see `nextEvidenceModeAfterDocChange` in `services/knowledge/documentUpload`),
   * flips `'awaiting'` → `'docs-included'` on the first successful upload.
   * No-op for `'ai-only'` / `'docs-included'` sites. Called from every
   * doc-attach path (`addSiteDocument`, `updateSiteDocuments`).
   */
  maybeFlipEvidenceModeOnDocUpload: (siteId: string) => Promise<void>;
  deleteSite: (id: string, mode?: import('../services/cascadeDelete').DeleteSiteMode) => Promise<import('../services/cascadeDelete').DeleteSiteResult>;
  setActiveSite: (id: string | null) => void;
  /** R-81 — per-site user audit cache + run actions. */
  auditBySite: Record<string, import('../types/userAudit').SiteAuditCacheEntry>;
  runAuditForSite: (siteId: string, opts?: { force?: boolean }) => Promise<import('../types/userAudit').UserAuditResult | null>;
  clearAuditForSite: (siteId: string) => void;
  loadAuditForSite: (siteId: string) => Promise<void>;
  /** R-80 — cross-report recommendation graph cache + rebuild. */
  recommendationGraphBySite: Record<string, import('../types/recommendationGraph').SiteRecommendationGraphCacheEntry>;
  rebuildRecommendationGraph: (siteId: string) => Promise<import('../types/recommendationGraph').RecommendationGraph | null>;
  /**
   * R-ROADMAP-INC — incremental roadmap update. Diffs the site's report ids
   * against the persisted graph's `reportIds`, extracts + dedupes only the new
   * reports, appends genuinely-new cards (preserving existing nodes + their
   * user-set status), and persists via the same path as rebuild. No-op (returns
   * the existing graph) when there are no new reports or no prior graph.
   */
  incrementalUpdateRecommendationGraph: (siteId: string) => Promise<import('../types/recommendationGraph').RecommendationGraph | null>;
  updateRecommendationStatus: (
    siteId: string,
    nodeId: string,
    status: import('../types/recommendationGraph').RecommendationStatus,
  ) => Promise<void>;
  loadRecommendationGraphForSite: (siteId: string) => Promise<void>;
  /** L1/L3 — portfolio composite cache + recompute actions. */
  portfolio: {
    composites: Record<string, import('../types/portfolioComposite').SiteComposite>;
    /** Session-only current pointer authority; never persisted as source data. */
    analysisContexts?: Record<string, import('../lib/portfolio/compositeScorer').CompositeAnalysisContext>;
    lastComputedAt: string | null;
    computing: boolean;
    error: string | null;
    bandConfig: import('../lib/portfolio/decisionBands').DecisionBandConfig | null;
    bandConfigLoading: boolean;
    clustering: import('../lib/portfolio/clusterAggregator').PortfolioClustering | null;
    geoClusters: import('../lib/portfolio/clusterTaxonomy').GeoClusterConfig | null;
  };
  computeCompositeForSite: (siteId: string, analysisContext?: import('../lib/portfolio/compositeScorer').CompositeAnalysisContext) => Promise<import('../types/portfolioComposite').SiteComposite>;
  computeCompositeForPortfolio: () => Promise<void>;
  /**
   * L3 — idempotent auto-populate for the dashboard/portfolio views.
   * Loads persisted composites, then computes only those missing (or stale
   * beyond `maxAgeMs`, default 24h) for company-scoped sites. No-ops while a
   * portfolio recompute is already in flight.
   */
  ensurePortfolioComposites: (customerId: string, opts?: { maxAgeMs?: number }) => Promise<void>;
  clearPortfolioComposites: () => void;
  loadPortfolioComposites: (customerId: string) => Promise<void>;
  loadDecisionBandConfig: (customerId: string) => Promise<import('../lib/portfolio/decisionBands').DecisionBandConfig>;
  saveDecisionBandConfig: (
    customerId: string,
    config: import('../lib/portfolio/decisionBands').DecisionBandConfig,
  ) => Promise<void>;
  loadGeoClusterConfig: (customerId: string) => Promise<import('../lib/portfolio/clusterTaxonomy').GeoClusterConfig>;
  saveGeoClusterConfig: (
    customerId: string,
    config: import('../lib/portfolio/clusterTaxonomy').GeoClusterConfig,
  ) => Promise<void>;
  recomputeClustering: (customerId: string) => Promise<void>;
  /**
   * @internal Mark one site's persisted portfolio composite STALE so the lazy
   * `ensurePortfolioComposites` pass recomputes it on next view. Idempotent —
   * stamps `lastComputedAt` to the epoch sentinel (the type-safe "always
   * stale" value, since `lastComputedAt` is a required string and cannot be
   * cleared). No-op when no composite exists for the site or it is already
   * epoch-stamped. Does NOT touch composite scoring — invalidation only.
   */
  _staleStampSiteComposite: (siteId: string) => void;
  /** @internal Migration helper — creates sites from existing reports. */
  _migrateReportsToSites: () => Promise<import('../types/site').SiteEntity[]>;

  // User role & company
  userRole: string;
  companyId: string | null;
  companyRole: import('../types/company').CompanyRole | null;
  companyName: string | null;
  /** Coarse customer classification driving the AI prompt's reader lens.
   *  Loaded from the company doc by loadCompanyData. Undefined for legacy
   *  records / solo users — falls through to neutral framing. */
  companyArchetype: import('../types/company').CompanyArchetype | null;
  /** Optional one-liner clarification on the archetype. */
  companyArchetypeNote: string | null;
  /**
   * Documents_On_Site Prompt 9 — tenant-configured cap on report
   * confidence when a critical checklist doc is missing. Loaded from
   * the company doc; falls through to DEFAULT_MISSING_CRITICAL_CAP
   * when undefined.
   */
  companyMissingCriticalCap: number | null;
  /**
   * SA2 — when true, SED amendments stay `pending` after save and require
   * the existing review workflow to fold into a new revision. When false /
   * null (the default), amendments take effect on the live SED immediately
   * via the direct-apply path in `saveSEDAmendment`.
   */
  companyRequireReviewWorkflow: boolean | null;
  /**
   * R-32 — tenant override for SED bootstrap freshness gate (ms).
   * Loaded from `companies/{id}.settings.sedBootstrapFreshnessThresholdMs`.
   */
  companySedBootstrapFreshnessThresholdMs: number | null;
  companyMembers: import('../types/company').CompanyMember[];
  /**
   * The signed-in user's company SUBTREE — own org + every descendant org —
   * as full `Company` records (own id always present, even when sparse).
   * Populated from the `companies where path array-contains self` query
   * (`fetchSubtreeCompanies`, authorized for ANY user in the tree) by
   * SiteRagEngine's data-scope effect. Shared so descendant-aware surfaces
   * OUTSIDE the engine — notably the Dashboard portfolio scope — can resolve
   * the same visible company set via `collectDescendantCompanyIds` instead of
   * an exact `companyId === customerId` match (which dropped sub-company sites
   * and made the Portfolio Overview read 0). Empty for solo/leaf users.
   */
  subtreeCompanies: import('../types/company').Company[];
  setSubtreeCompanies: (companies: import('../types/company').Company[]) => void;
  featureAccess: import('../types/company').FeatureAccess;
  /** Workflow Prompt 8 — admins can flip this to bypass the preflight gate
   *  on report generation. Persists in localStorage. Has no effect for
   *  non-admin users. */
  adminOverrideEnabled: boolean;
  setAdminOverrideEnabled: (enabled: boolean) => void;
  loadCompanyData: () => Promise<void>;
  canAccessSiteRag: () => boolean;
  canAccessSimulation: () => boolean;

  // ── Brand logo (feat/org-logo) ─────────────────────────────────────
  /** The signed-in user's PERSONAL brand logo (users/{uid}/profile/base.logoUrl).
   *  Set from the user menu; overrides the org logo for that user. */
  userLogoUrl: string | null;
  /** The user's ORG brand logo (companies/{cid}/settings/branding.logoUrl).
   *  Set by a company admin; the org-wide brand for every member. */
  orgLogoUrl: string | null;

  // ── HEARTBEAT-IDENTITY (2026-10-07) ─────────────────────────────────
  /**
   * Latest server liveness beat per report-generation run key (ISO
   * `heartbeatAt`, or `updatedAt` for a job doc written before that field).
   * Kept at the ROOT, outside `siteRag.activeGenerations`, on purpose: the job
   * worker re-stamps it every 30s for the whole run, and only the stall
   * indicators (`useGenerationHeartbeat`) read it. The run objects — and the
   * `siteRag` slice — therefore keep their identity across heartbeat-only
   * ticks, so the site page's memo chain (Path-to-GO build, findings model)
   * and the app shell do not re-render every 30s. Not persisted.
   */
  generationHeartbeats: Record<string, string>;
  /** Load both brand-logo tiers (user + org) into the store. Called from
   *  the auth/company-data init alongside loadCompanyData. Best-effort. */
  loadBrandLogos: () => Promise<void>;
  /** Persist the current user's personal brand logo (or clear with null)
   *  and reflect it in the store immediately. */
  setUserLogo: (logoUrl: string | null) => Promise<void>;
  /** Persist the given org's brand logo (or clear with null). Requires
   *  company-admin / platform-admin. Reflects in the store immediately
   *  when `companyId` matches the caller's own org. */
  setOrgLogo: (companyId: string, logoUrl: string | null) => Promise<void>;
  /** The EFFECTIVE brand logo (user → org → Datacrate default), derived
   *  through the single `resolveEffectiveLogo` helper. Always non-empty. */
  effectiveLogoUrl: () => string;
  /** The effective UPLOADED brand logo (user → org), or null when neither
   *  is set. Fed into `resolveReportBranding`'s `effectiveLogoUrl` so the
   *  cover's `template → theme → effective → default` chain stays intact. */
  uploadedLogoUrl: () => string | null;

  // Report Theme
  reportTheme: import('../types/reportTheme').ReportTheme;
  /** In-memory + localStorage update only. Does NOT persist to Firestore.
   *  Used by the theme editor's draft state. Call commitReportTheme to persist. */
  setReportTheme: (theme: Partial<import('../types/reportTheme').ReportTheme>) => void;
  /** In-memory + localStorage update only. Does NOT persist to Firestore. */
  setReportLogo: (dataUrl: string | null) => void;
  /** Persist a full theme to Firestore (user scope), update store state, set
   *  lastReportThemeSavedAt, and toast on success. Throws + error-toasts on failure. */
  commitReportTheme: (theme: import('../types/reportTheme').ReportTheme) => Promise<void>;
  /** Save a theme as the company default. Defaults to the current store theme;
   *  pass an explicit theme to commit a draft from the editor without first
   *  committing it personally. Requires companyRole 'admin'. */
  saveThemeAsCompanyDefault: (theme?: import('../types/reportTheme').ReportTheme) => Promise<void>;
  /** Timestamp (ms epoch) of the last successful theme save. Drives the SaveIndicator. */
  lastReportThemeSavedAt: number | null;
  /**
   * TSC-3 (2026-05-18) — transient draft channel for the Template Studio.
   *
   * Studio mutations live in local React state (`draftTheme`) and don't
   * reach `reportTheme` until the user clicks Save. The canonical site
   * report viewer reads `reportTheme` only, so theme changes don't
   * propagate live. SleekTemplateStudio.tsx debounce-writes its
   * `draftTheme` into this field on every change; SiteRagEngine.tsx
   * prefers `activeDraftTheme ?? reportTheme` when wrapping the viewer
   * in <ThemeOverrideProvider>. Unmount of Studio clears the channel
   * back to null so the viewer falls back to the persisted theme.
   *
   * Caveat: if the user makes Studio drafts and never saves, then
   * closes Studio, the changes vanish — by design (Studio is
   * draft→preview→optionally commit). Localstorage-back is filed as
   * TSC-FOLLOWUP-UNDO-PERSISTENCE.
   */
  activeDraftTheme: import('../types/reportTheme').ReportTheme | null;
  setActiveDraftTheme: (theme: import('../types/reportTheme').ReportTheme | null) => void;
  /**
   * TCP-7 + TCP-8 (2026-05-19, row 0.99233) — transient draft channel
   * for the Template Studio's section list (page assignment + drag
   * reorder + visibility toggles). Mirrors `activeDraftTheme`'s shape
   * but ships the FULL draft template snapshot so the receiving end
   * sees coherent state (sections + pageLayouts + headingOverrides +
   * customSections all move together). Per-frame propagation: Studio
   * writes synchronously inside `updateDraftTemplate`; subscribers
   * (TemplatePreviewPane via useStore selector) re-render in the same
   * React commit so a drag-drop or page reassignment lands within 1
   * frame.
   *
   * The Studio's existing `TemplateOverrideProvider` continues to
   * carry the in-flight draft to the local preview (no behaviour
   * change there). This channel exists so any OTHER mounted surface
   * (a second Studio tab, a live preview embedded outside Studio, a
   * future puppeteer probe) can subscribe to the same snapshot
   * without going through the override provider's React context.
   *
   * Cleared on Studio unmount + after a successful save (see
   * `updateReportTemplate` / SleekTemplateStudio mount-teardown).
   */
  activeDraftTemplate: import('../types/reportTemplate').ReportTemplate | null;
  setActiveDraftTemplate: (template: import('../types/reportTemplate').ReportTemplate | null) => void;

  /**
   * RTU-4 (2026-05-19, row 0.99235) — which report type the Studio's
   * Sections + Pages tabs are currently configuring. `null` means
   * legacy / flat-list mode (the panes read + write the template's
   * top-level `sections` array, preserving full back-compat with
   * pre-RTU-4 templates). Non-null means the panes route through
   * `template.sectionsByReportType[type]` — selecting a type with no
   * existing entry yields an empty Sections list (cold start), and
   * the first edit materialises the per-type array.
   *
   * Reset to `null` on Studio unmount so a future re-entry starts in
   * flat-list mode. Coupled-by-default with the preview's Sample
   * dropdown via the SleekTemplateStudio shell (preview switch can
   * auto-sync this field; an explicit user pick decouples them).
   */
  studioConfiguringType: import('../config/reportTypes').ReportTypeId | null;
  setStudioConfiguringType: (
    type: import('../config/reportTypes').ReportTypeId | null,
  ) => void;
  /** When true, preview sample changes do not auto-sync configuring type. */
  studioConfiguringTypePinned: boolean;
  setStudioConfiguringTypePinned: (pinned: boolean) => void;

  // Report Templates
  reportTemplates: import('../types/reportTemplate').ReportTemplate[];
  activeTemplateId: string | null;
  addReportTemplate: (template: import('../types/reportTemplate').ReportTemplate) => void;
  updateReportTemplate: (id: string, updates: Partial<import('../types/reportTemplate').ReportTemplate>) => void;
  deleteReportTemplate: (id: string) => void;
  setDefaultTemplate: (id: string) => void;
  setActiveTemplate: (id: string | null) => void;
  /** Write a template into the company-scoped collection. Admin-only via Firestore rules. */
  saveTemplateAsCompany: (template: import('../types/reportTemplate').ReportTemplate) => Promise<void>;
  /**
   * Write a template into a CHOSEN company's collection — the user's own
   * org or any DESCENDANT org (subtree-only). `companies` is the known
   * company universe (own org + descendants) used to validate the target
   * is within the user's subtree via `isInCompanyTree`. Throws on an
   * out-of-tree target before any Firestore write. Admin-only + subtree-
   * only via Firestore rules (defence in depth).
   */
  saveTemplateToCompany: (
    template: import('../types/reportTemplate').ReportTemplate,
    targetCompanyId: string,
    companies: import('../types/company').Company[],
  ) => Promise<void>;
  /** Copy a company-scoped template into the user's personal collection with a fresh id. */
  forkCompanyTemplate: (templateId: string) => Promise<void>;
  /**
   * TSE-2 C12 — list reports that reference a given template (via
   * `report.templateId`). Returns a snapshot of {id, siteName,
   * templateVersionUsed}; callers can show "N reports affected" and
   * let the user pick which to bump.
   */
  findReportsUsingTemplate: (templateId: string) => Array<import('../lib/reportVersionGroups').ReportPickRow>;
  /** Reports not yet linked to `templateId` (missing or different templateId). */
  findReportsEligibleForTemplateLink: (templateId: string) => Array<import('../lib/reportVersionGroups').ReportPickRow>;
  /**
   * TSE-2 C12 — stamp `templateVersionUsed = <newVersion>` on a list
   * of report ids so the viewer picks up the new template shape on
   * next view. No content re-generation — just a snapshot version
   * bump (the live template flows through the existing activeTemplate
   * resolution chain).
   */
  applyTemplateToExistingReports: (
    templateId: string,
    reportIds: string[],
  ) => Promise<{ linked: number; failed: number; failedIds: string[] }>;

  /** Pull the user's report theme + templates from Firestore (with company fallback)
   *  and replace in-memory state. Falls back to localStorage if Firestore is unreachable.
   *  Also runs the one-time localStorage→Firestore migration. */
  hydrateUserSettings: () => Promise<void>;
  /** True once hydrateUserSettings has resolved at least once for this session. */
  _settingsHydrated: boolean;

  // SED Amendment System
  amendmentState: import('../types/sedAmendments').AmendmentState;
  fetchSEDAmendments: (sedId: string) => Promise<void>;
  saveSEDAmendment: (
    amendment: import('../types/sedAmendments').SEDAmendment,
    options?: {
      expectedSedVersion?: number;
      operationId?: string;
    },
  ) => Promise<import('../types/sedAmendments').SaveAmendmentResult>;
  /**
   * Fetches the authoritative site document and SED baseline from Firestore/GCS
   * and updates the store so conflict recovery never relies on stale local state.
   */
  refreshSiteBaseline: (
    siteId: string,
  ) => Promise<{
    site: import('../types/site').SiteEntity | null;
    sed: import('../types/siteEvidence').SiteEvidenceDocument | null;
  }>;
  /**
   * "Accept value as correct" — record a positive, non-mutating review of a
   * SED variable. Writes a `status: 'accepted'` amendment record to the same
   * amendments subcollection (audit trail), WITHOUT applying anything to the
   * SED (no version bump, the row is not marked "Amended"). Idempotent from the
   * UI's side (the browser hides the affordance once a row is accepted).
   */
  acceptSEDVariable: (input: {
    sedId: string;
    sedVersion: number;
    variablePath: string;
    variableLabel: string;
    domain: string;
    value: string | number | boolean | null;
    unit?: string;
  }) => Promise<import('../types/sedAmendments').SaveAmendmentResult>;
  updateAmendmentStatus: (sedId: string, amendmentId: string, status: 'approved' | 'rejected' | 'superseded', reviewComment?: string) => Promise<void>;
  /**
   * AMP-2 — Reapply every `applied` amendment from the OLD sedId onto
   * the freshly-rebuilt SED for `siteId`. Reads the old amendments
   * from Firestore, walks them via `planAmendmentReapply`, writes the
   * updated SED to GCS, mirrors the new amendment records at the new
   * sedId (with `reappliedFromAmendmentId` back-pointers), and writes
   * any conflicts onto the SED's `evidenceConflicts[]` array so the
   * existing UFC resolution UI can surface them. Returns a summary
   * `{reapplied, conflicts, orphans, skipped, errors}` for the caller
   * (subscriber) to toast.
   *
   * Safe to call multiple times — re-walking the same amendments is
   * idempotent (provider-matches-newValue becomes a no-op).
   */
  reapplyAmendmentsAfterRebuild: (siteId: string, oldSedId: string) => Promise<{
    reapplied: number;
    conflicts: number;
    orphans: number;
    skipped: number;
    errors: number;
  }>;
  /**
   * AMP-3 — Write a counter-amendment that restores the original
   * provider value for `variablePath` and supersede all prior `applied`
   * amendments on that path. Surfaces a confirmation-friendly summary
   * `{ok, restoredValue, supersededIds}` for the caller's confirmation
   * dialog + history strip refresh. Returns `{ok: false, error}` when
   * the site/SED/variable can't be located or no applied amendments
   * exist on the path.
   */
  revertSedAmendment: (siteId: string, variablePath: string) => Promise<
    | {
        ok: true;
        applied?: boolean;
        idempotent?: boolean;
        restoredValue: unknown;
        supersededIds: string[];
        revertAmendmentId: string;
      }
    | {
        ok: false;
        error: string;
        code?: string;
        conflict?: boolean;
        operationConfirmed?: boolean;
        refreshRequired?: boolean;
        idempotent?: boolean;
        sedVersion?: number;
      }
  >;
  createSEDRevision: (sedId: string, reportId: string, comments: string) => Promise<string | null>;
  regenerateFromAmendedSED: (reportId: string, sedRevisionId: string) => Promise<void>;

  // Approval Workflow
  approvalWorkflow: import('../types/approvalWorkflow').ApprovalWorkflowState;
  submitForReview: (reportId: string, sedId: string, reportTitle: string) => Promise<void>;
  loadApprovalWorkflow: (reportId: string) => Promise<void>;
  actOnApprovalStage: (reportId: string, action: 'approve' | 'reject' | 'request-changes', comments: string) => Promise<void>;

  // Notifications
  /**
   * #110 — true while a top-bar toast/notification (sonner) is currently
   * visible. Derived from the live sonner DOM by `useToastVisibility`
   * (MutationObserver), so it can never get stuck "on": it always reflects the
   * actual rendered toasts. The centered "DATACRATE" header wordmark hides
   * while this is true and restores when toasts clear.
   */
  toastVisible: boolean;
  setToastVisible: (visible: boolean) => void;
  notificationState: import('../types/notification').NotificationState;
  loadNotifications: () => Promise<void>;
  markNotificationRead: (notifId: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  createNotification: (targetUid: string, notification: import('../types/notification').NewNotificationInput) => Promise<void>;
  notifyAdmins: (
    provider: import('../config/apiProviders').ApiProviderDef,
    context: { gapReason: string },
  ) => Promise<void>;

  // Simulation State
  simState: SimulationState;
  /**
   * RUN-CONSOLIDATION (toolbar-consolidation) — decoupled "open the Simulation
   * Results panel" signal. The single top-toolbar action raises this token after
   * an EO solve; SimulationEditor watches it and opens its local
   * `showAnalysis` panel. A monotonically-increasing counter lets repeat solves
   * re-trigger the effect even when the panel was closed again.
   */
  analysisRequestToken: number;
  requestShowAnalysis: () => void;
  /**
   * Run-request signal so the canvas keyboard shortcut routes through App.tsx's
   * single EO readiness/solve handler instead of mutating local playback state.
   * App.tsx watches runRequestToken → handleRun().
   */
  runRequestToken: number;
  requestRun: () => void;
  toggleSimulation: () => void;
  resetSimulation: () => void;
  clearSimulation: () => void;
  loadCycle: (
    nodes: AppNode[],
    edges: Edge[],
    legacySourceLabel?: string,
    modelName?: string,
  ) => boolean;
  updateSimState: (newState: Partial<SimulationState>) => void;
  // ── R2-B4 transient event-schedule actions (operate on simState.events) ────
  /** Append one scheduled event (auto-assigns an id when absent). */
  addSimEvent: (event: Omit<SimEvent, 'id'> & { id?: string }) => void;
  /** Patch one event by id (id is preserved). No-op for an unknown id. */
  updateSimEvent: (id: string, patch: Partial<SimEvent>) => void;
  /** Remove one event by id. */
  removeSimEvent: (id: string) => void;
  /** Clear all scheduled events. */
  clearSimEvents: () => void;
  /** Load a canned cooling-scenario preset (M5) as the current event set. */
  loadEventPreset: (presetKey: string) => void;
  updateNodeData: (id: string, data: any) => void;
  /**
   * Merge schema-backed component parameters into `node.data.params`.
   * Used by the Inputs tab for schema-registry-driven fields. Keeps the flat
   * `node.data` fields untouched for back-compat with the legacy property
   * panel and the NS solver.
   */
  updateNodeParams: (id: string, params: Record<string, unknown>) => void;
  stepSimulation: (dt: number) => void;
  /**
   * ASYNC transient step routed through the off-main-thread Web Worker solver.
   * Reached ONLY when `simState.useWorkerSolver` is true; the App run loop
   * branches on the flag and calls the synchronous `stepSimulation` otherwise.
   * Worker results are bit-identical to `stepSimulation` and fall back to the
   * main thread on any worker failure/timeout, so this never hangs the UI.
   */
  stepSimulationViaWorker: (dt: number) => Promise<void>;
  /**
   * One tick of the pseudo-transient run-to-convergence loop for STEADY-STATE
   * mode. Performs exactly one `stepSimulation`, then inspects the metrics the
   * NS solver just produced via `isSteadyStateConverged`. When converged it
   * auto-stops the loop (`isRunning = false`) and surfaces a
   * "Steady state reached (N iterations, residual …)" warning. When
   * `STEADY_STATE_MAX_STEPS` is hit without convergence it stops and surfaces a
   * "did not reach steady state in N iterations" warning.
   *
   * Cancellable: if `isRunning` has gone false (e.g. user pressed Stop) the tick
   * is a no-op. This wraps the step + converge-check + auto-stop so App.tsx only
   * has to drive ticks. Does NOT touch the NS solver internals — it reuses
   * `stepSimulation` exactly as transient mode does.
   *
   * @returns `true` if the loop should keep ticking, `false` once it has
   *          stopped (converged or hit the cap, or was already cancelled).
   */
  steadyStateTick: (dt: number) => boolean;

  // Tabs State
  tabs: SimulationTab[];
  activeTabId: string;
  addTab: (name: string, nodes?: AppNode[], edges?: Edge[], simState?: SimulationState, site?: Site | null, cloudId?: string, version?: number, autoSave?: boolean, eoGraphConfig?: EoGraphConfigV1, savedEoInputSignature?: string | null, repairDisposition?: SimulationRepairDisposition, ignoredNodeIds?: readonly string[], eoStudySpec?: EoStudySpec, eoTransientConfig?: EoTransientConfig, eoKpiUncertaintyDefinitionSet?: EoKpiUncertaintyDefinitionSetV1, eoCanvasExecutionConfig?: EoCanvasExecutionConfigV1, options?: AddTabOptions) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTabName: (id: string, name: string) => void;
  updateTab: (id: string, data: Partial<SimulationTab>) => void;
  syncActiveTab: () => void;
  /** Create an editable EO repair copy from a read-only legacy-source tab. */
  createEoRepairCopy: (newName: string) => void;

  // ── SIM-MGMT — saved cloud-sim management ────────────────────────────
  // Operates on the canonical sim doc path used by AutoSaver + App.tsx:
  //   users/{uid}/simulations/{cloudId}
  // Backups live in the subcollection:
  //   users/{uid}/simulations/{cloudId}/backups/{version}  (valid doc path)
  // These are ADDITIVE — the existing save (AutoSaver / handleCloudQuickSave),
  // fetch (fetchCloudSims) and load (loadCloudSim) paths are untouched.
  /**
   * Delete a saved cloud simulation: its `backups` subcollection docs, then the
   * sim doc itself, then a best-effort GCS payload cleanup (errors swallowed).
   * Destructive — the UI MUST confirm before calling this. Deletes ONLY the one
   * targeted sim. Never throws; returns `{ ok, error? }`.
   */
  deleteCloudSim: (cloudId: string) => Promise<CloudSimActionResult>;
  /**
   * Rename a saved cloud simulation. Trims the name and REJECTS empty/whitespace
   * (returns `{ ok: false, error }`) without writing. Updates the doc's `name`
   * field. Never throws; returns `{ ok, error? }`.
   */
  renameCloudSim: (cloudId: string, name: string) => Promise<CloudSimActionResult>;
  /**
   * List the backup snapshots for a saved cloud simulation, newest version
   * first. Returns `[]` when unauthenticated or on any read error (never throws).
   */
  listSimBackups: (cloudId: string) => Promise<SimBackupRow[]>;
  /**
   * Restore a backup snapshot into the LIVE canvas, reusing the same parse path
   * `loadCloudSim` uses (merge siteRag, open a tab, switch to the simulation
   * view). Tolerates missing/legacy fields — falls back to empty canvas /
   * defaults rather than crashing. Never throws; returns `{ ok, error? }`.
   */
  restoreSimBackup: (cloudId: string, version: number) => Promise<CloudSimActionResult>;
  /**
   * Capture a bounded point-in-time backup of a saved cloud simulation into its
   * `backups` subcollection. Retains the newest `SIM_BACKUP_RETAIN` snapshots
   * and prunes the oldest. ADDITIVE helper for the Wire phase (save flow) — the
   * existing save actions are intentionally left intact. Never throws.
   */
  createSimBackup: (cloudId: string, snapshot: CloudSimPayload) => Promise<CloudSimActionResult>;

  // Custom Components
  customComponents: CustomComponentDef[];
  addCustomComponent: (comp: CustomComponentDef) => void;
  updateCustomComponent: (id: string, comp: CustomComponentDef) => void;
  deleteCustomComponent: (id: string) => void;
} & ChatSlice & RecorderSlice;

const initialNodes: AppNode[] = [];

const initialEdges: Edge[] = [];

const initialSimState: SimulationState = {
  time: 0,
  isRunning: false,
  timeStep: 0.1, // 100ms
  autoTimeStep: false,
  solverType: 'navier-stokes',
  timeIntegration: 'implicit',
  relaxationFactor: 0.8,
  alpha: 0.5,
  convergenceTolerance: 0.0001,
  // S3 — default to the authoritative EO steady-state design solve. loadCycle
  // and the legacy-model fork action spread initialSimState, so they inherit
  // this UI default. No solver mathematics are changed here.
  simulationMode: 'steady-state',
  isSteadyState: false,
  // Web Worker NS solver — DEFAULT ON (user-enabled 2026-06-01). The transient
  // run loop routes each NS step through the worker so intense sims don't freeze
  // the UI. SAFETY: step parity is proven bit-identical (≤1e-12) to the
  // main-thread runNSStep — including non-default solver settings (carried
  // per-step) and lossless serialization for BoP + cooling networks — and the
  // worker client transparently FALLS BACK to runNSStep on any failure (no
  // Worker / SSR / crash / timeout), so it can never produce wrong results.
  // Flip to false to force the legacy synchronous path. See src/simulation/ns/worker/.
  useWorkerSolver: true,
  physicsParams: {
    diffusionCoefficient: 0.05,
    convectiveHeatTransfer: 0.5,
    flowInertia: 2.0,
  },
  warnings: [],
  errors: [],
  metrics: {
    totalComputePower: 40,
    totalCoolingPower: 6, // 40 * 0.15
    systemPUE: 1.15,
    thermalEfficiency: 0.8,
    totalPowerGenerated: 0,
    totalPowerConsumed: 0,
    convergence: {
      residual: 0,
      iterations: 0,
      isConverged: false,
    }
  }
};

/** Fresh canonical editor defaults for trust-boundary model hydration. */
export function createInitialSimulationState(): SimulationState {
  return {
    ...initialSimState,
    physicsParams: { ...initialSimState.physicsParams },
    warnings: [],
    errors: [],
    metrics: {
      ...initialSimState.metrics,
      ...(initialSimState.metrics.convergence
        ? { convergence: { ...initialSimState.metrics.convergence } }
        : {}),
    },
  };
}

// ── Pseudo-transient run-loop bookkeeping (steady-state mode) ───────────────
// Module-scoped so they persist across store-action invocations but reset when
// a run stops/cancels. NOT part of SimulationState (kept out of persistence /
// React re-render churn — they are pure loop control, like _nsSolverState).
let _steadyStateStepCount = 0;
let _steadyStatePrevMetrics: SimulationState['metrics'] | null = null;

function safeUUID(): string {
  try { return crypto.randomUUID(); }
  catch { return Date.now().toString(36) + Math.random().toString(36).slice(2); }
}

// ── Sim Phase 3b — canvas undo/redo history ─────────────────────────────────
/**
 * Max snapshots retained per stack. On overflow the OLDEST entry is dropped
 * (we keep the most recent HISTORY_CAP), bounding memory regardless of how
 * many discrete edits a session accumulates.
 */
const HISTORY_CAP = 50;

/**
 * SIM-MGMT — max backup snapshots retained per saved cloud simulation. On
 * overflow the OLDEST snapshot(s) are pruned (we keep the most recent N),
 * bounding the `backups` subcollection regardless of how often the sim is saved.
 */
const SIM_BACKUP_RETAIN = 10;

/**
 * Coalesce window for keyed snapshots. Rapid consecutive edits sharing a
 * coalesce key (e.g. typing into ONE param field, which fires onChange per
 * keystroke) collapse into a SINGLE undo entry: the first keystroke pushes the
 * pre-edit-session snapshot; subsequent same-key edits within the window reuse
 * it. Module-scoped so it survives across action calls (like `_nsSolverState`).
 */
const UNDO_COALESCE_MS = 1000;
let _undoLastCoalesceKey: string | null = null;
let _undoLastSnapshotMs = 0;

/** Empty history — used for initial state and resets. */
const EMPTY_CANVAS_HISTORY: CanvasHistory = { past: [], future: [] };

/**
 * Deep-clone the canvas so the snapshot is fully detached from the live store.
 * node.data is mutated in place by fluid propagation / NS steps, so a shallow
 * copy would let later edits silently corrupt history entries. Falls back to
 * JSON clone if structuredClone is unavailable (older test envs); both produce
 * plain serialisable {nodes, edges} which is exactly what the canvas holds.
 */
function cloneCanvas(nodes: AppNode[], edges: Edge[]): CanvasSnapshot {
  const snap = { nodes, edges };
  try {
    return structuredClone(snap) as CanvasSnapshot;
  } catch {
    return JSON.parse(JSON.stringify(snap)) as CanvasSnapshot;
  }
}

type CanvasEntity = { id: string };

/**
 * Conservative structural comparison for transaction compensation. A false
 * negative only leaves an artifact in place and reports a conflict; it never
 * overwrites a concurrent user edit.
 */
function sameCanvasEntity<T extends CanvasEntity>(
  left: T | undefined,
  right: T | undefined,
): boolean {
  if (left === right) return true;
  if (left === undefined || right === undefined) return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function changedCanvasEntityIds<T extends CanvasEntity>(
  before: readonly T[],
  after: readonly T[],
): Set<string> {
  const beforeById = new Map(before.map((entity) => [entity.id, entity]));
  const afterById = new Map(after.map((entity) => [entity.id, entity]));
  const beforeCounts = new Map<string, number>();
  const afterCounts = new Map<string, number>();
  before.forEach((entity) => {
    beforeCounts.set(entity.id, (beforeCounts.get(entity.id) ?? 0) + 1);
  });
  after.forEach((entity) => {
    afterCounts.set(entity.id, (afterCounts.get(entity.id) ?? 0) + 1);
  });
  const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
  return new Set(
    [...ids].filter((id) => (
      beforeCounts.get(id) !== afterCounts.get(id)
      || !sameCanvasEntity(beforeById.get(id), afterById.get(id))
    )),
  );
}

interface CanvasCompensation<T extends CanvasEntity> {
  entities: T[];
  conflicts: string[];
}

interface CanvasHistoryCompensation {
  history: CanvasHistory;
  conflicts: string[];
}

/**
 * Restore only transaction-owned entities whose current state still matches
 * the post-commit state. Affected entities changed by the user are retained
 * and returned as conflicts. Unrelated concurrent edits are preserved.
 */
function compensateCanvasEntities<T extends CanvasEntity>(
  current: readonly T[],
  before: readonly T[],
  after: readonly T[],
  affectedIds: ReadonlySet<string>,
): CanvasCompensation<T> {
  const currentById = new Map<string, T>();
  const currentCounts = new Map<string, number>();
  for (const entity of current) {
    currentById.set(entity.id, entity);
    currentCounts.set(entity.id, (currentCounts.get(entity.id) ?? 0) + 1);
  }
  const beforeById = new Map(before.map((entity) => [entity.id, entity]));
  const afterById = new Map(after.map((entity) => [entity.id, entity]));
  const safeIds = new Set<string>();
  const conflicts: string[] = [];

  for (const id of affectedIds) {
    const count = currentCounts.get(id) ?? 0;
    if (count > 1) {
      conflicts.push(id);
      continue;
    }
    const currentEntity = currentById.get(id);
    const beforeEntity = beforeById.get(id);
    const afterEntity = afterById.get(id);
    if (
      sameCanvasEntity(currentEntity, afterEntity)
      || sameCanvasEntity(currentEntity, beforeEntity)
    ) {
      safeIds.add(id);
    } else {
      conflicts.push(id);
    }
  }

  // Remove safe post-commit values and reinsert their pre-commit values near
  // the original indices. Unrelated/conflicting entries (including malformed
  // duplicates) remain byte-for-byte and in their current order.
  const entities = current.filter((entity) => !safeIds.has(entity.id));
  before.forEach((beforeEntity, beforeIndex) => {
    if (!safeIds.has(beforeEntity.id)) return;
    entities.splice(Math.min(beforeIndex, entities.length), 0, beforeEntity);
  });

  return { entities, conflicts };
}

/**
 * Remove the transaction's own undo snapshot and compensate every snapshot
 * created after it. Pre-existing history entries are retained by identity.
 * Conflicting post-commit snapshots are dropped (never allowed to resurrect a
 * failed import) and reported so the caller surfaces a partial rollback.
 */
function compensateGeneratedCanvasHistory(
  current: CanvasHistory,
  preexistingSnapshots: ReadonlySet<CanvasSnapshot>,
  transactionSnapshots: ReadonlySet<CanvasSnapshot>,
  beforeCanvas: CanvasSnapshot,
  committedCanvas: CanvasSnapshot,
  affectedNodeIds: ReadonlySet<string>,
  affectedEdgeIds: ReadonlySet<string>,
): CanvasHistoryCompensation {
  const conflicts: string[] = [];
  const compensateStack = (
    stack: readonly CanvasSnapshot[],
    stackName: 'past' | 'future',
  ): CanvasSnapshot[] => {
    const compensated: CanvasSnapshot[] = [];
    stack.forEach((snapshot, index) => {
      if (transactionSnapshots.has(snapshot)) return;
      if (preexistingSnapshots.has(snapshot)) {
        compensated.push(snapshot);
        return;
      }
      const nodes = compensateCanvasEntities(
        snapshot.nodes,
        beforeCanvas.nodes,
        committedCanvas.nodes,
        affectedNodeIds,
      );
      const edges = compensateCanvasEntities(
        snapshot.edges,
        beforeCanvas.edges,
        committedCanvas.edges,
        affectedEdgeIds,
      );
      if (nodes.conflicts.length > 0 || edges.conflicts.length > 0) {
        conflicts.push(
          ...nodes.conflicts.map((id) => `history:${stackName}:${index}:node:${id}`),
          ...edges.conflicts.map((id) => `history:${stackName}:${index}:edge:${id}`),
        );
        return;
      }
      compensated.push({ nodes: nodes.entities, edges: edges.entities });
    });
    return compensated;
  };

  return {
    history: {
      past: compensateStack(current.past, 'past'),
      future: compensateStack(current.future, 'future'),
    },
    conflicts,
  };
}

function emptyGeneratedCanvasCommit(): GeneratedCanvasCommitResult {
  return {
    committedNodeIds: [],
    rollback: () => ({ ok: true, conflicts: [] }),
  };
}

/**
 * True iff the error is a Firestore permission-denied. Used to decide whether
 * a caught error should log at warn (real problem) or be downgraded — the
 * upstream caller is the one who knows whether the user even has the relevant
 * claim, so this helper just identifies the class of error.
 */
function isPermissionDenied(e: unknown): boolean {
  const code = (e as { code?: string })?.code;
  const message = (e as { message?: string })?.message ?? '';
  return code === 'permission-denied' || /missing or insufficient permissions/i.test(message);
}

/**
 * R-150 — resolve the SUBTREE company-id set for a parent-org user: the
 * user's OWN company plus EVERY descendant company. A parent must RECEIVE
 * its sub-orgs' shared data, not just its own.
 *
 * The descendant set is derived from the VERIFIED hierarchy — company docs
 * persist a root→self `path[]` (createCompany / setCompanyParent), so a
 * `companies where path array-contains companyId` query returns exactly
 * { self, descendants }. This is NOT user input: a malicious client cannot
 * inject a sibling/parent id, because each returned company doc is itself
 * gated by the subtree-only `match /companies/{companyId}` rule (the parent
 * can only read docs whose path contains its own companyId).
 *
 * Always includes `companyId` itself (defensive) so the own-org reports are
 * fetched even if the company doc's `path` is malformed/missing. On any
 * failure (permission / offline) it degrades to `[companyId]` so the parent
 * still sees its own org's shared reports — never widens beyond the subtree.
 *
 * Returns a de-duplicated array of company ids.
 */
async function resolveSubtreeCompanyIds(
  db: import('firebase/firestore').Firestore,
  companyId: string,
): Promise<string[]> {
  const ids = new Set<string>([companyId]);
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const snap = await getDocs(
      query(collection(db, 'companies'), where('path', 'array-contains', companyId)),
    );
    for (const d of snap.docs) {
      const id = (d.data() as { id?: unknown }).id;
      if (typeof id === 'string' && id.trim().length > 0) ids.add(id);
      else ids.add(d.id);
    }
  } catch (e) {
    // Degrade to self-only — the own-company `in` query below still runs.
    if (isPermissionDenied(e)) {
      console.debug('[resolveSubtreeCompanyIds] permission denied enumerating subtree — falling back to self-only', e);
    } else {
      console.warn('[resolveSubtreeCompanyIds] subtree enumeration failed — falling back to self-only:', e);
    }
  }
  return [...ids];
}

/** Split an array into chunks of at most `size` (Firestore `in` caps at 30). */
function chunkIds<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Hydrate a site from the canonical owner-scoped `/layers` subcollection.
 *
 * Dense CAD rows are intentionally omitted from the bounded parent-document
 * mirror, so every full site load must resolve layers from the canonical owner
 * path. Read failures retain the marker-aware legacy fallback used throughout
 * the app; a stamped canonical deletion is therefore never resurrected.
 */
async function loadResolvedClientSiteLayersWithStatus(params: {
  db: import('firebase/firestore').Firestore;
  ownerUid?: string;
  siteId: string;
  site: import('../types/site').SiteEntity;
  logContext: string;
}): Promise<{
  layers: import('../types/site').SiteLayer[];
  canonicalComplete: boolean;
  hydrationError?: string;
}> {
  if (isVerifiedCompleteSiteLayerMirror(params.site)) {
    return {
      layers: params.site.siteLayers ?? [],
      canonicalComplete: true,
    };
  }

  let canonicalLayers: import('../types/site').SiteLayer[] = [];
  let canonicalComplete = false;
  let hydrationError: string | undefined;
  if (params.ownerUid) {
    try {
      const { listSiteLayers } = await import('../services/siteLayers/siteLayerPersistence');
      canonicalLayers = await listSiteLayers(
        params.db,
        params.ownerUid,
        params.siteId,
      );
      canonicalComplete = true;
    } catch (e) {
      hydrationError = (e as Error)?.message || 'Failed to load canonical site map layers.';
      console.warn(`${params.logContext} layer load failed:`, hydrationError);
    }
  } else {
    hydrationError = 'Site owner UID unavailable for canonical map layer resolution.';
  }

  const legacyMirror = Array.isArray(params.site.siteLayers)
    ? params.site.siteLayers
    : [];
  const { resolveCanonicalSiteLayers } = await import('../lib/siteLayers/canonicalLayerResolution');
  const resolved = resolveCanonicalSiteLayers(
    canonicalLayers,
    legacyMirror,
    params.site.siteLayersCanonicalVersion,
  );
  return {
    layers: resolved.layers,
    canonicalComplete,
    ...(hydrationError ? { hydrationError } : {}),
  };
}

/**
 * Structural subset of the Firebase `User` we depend on for claim freshness.
 * Kept minimal so the helper is trivially unit-testable with a stub.
 */
interface ClaimRefreshableUser {
  getIdToken(forceRefresh?: boolean): Promise<string>;
  getIdTokenResult(forceRefresh?: boolean): Promise<{ claims: Record<string, unknown> }>;
}

/**
 * BUG #100 — force-refresh a STALE company claim once.
 *
 * The company-shared `collectionGroup('sites')` / `collectionGroup('reports')`
 * queries are gated by Firestore rules on the caller's `companyId` /
 * `companyPath` CUSTOM CLAIM. When that claim is granted server-side AFTER the
 * user's last sign-in (e.g. `repairUserCompanyClaim`, an org assignment, or a
 * setCompanyParent path change), the LIVE ID token still lacks it — so the
 * rules reject with permission-denied even though the store already knows the
 * `companyId` (loaded from the user doc / profile fallback).
 *
 * This forces `getIdToken(true)` exactly ONCE to mint a fresh token carrying
 * the up-to-date claims, then reports whether the relevant claim is now
 * present. Callers MUST gate their retry on a single-shot flag so a token that
 * genuinely never gets the claim (truly not in the company) can't infinite-loop.
 *
 * Returns `true` when the refreshed token carries a `companyId` claim.
 */
async function forceRefreshCompanyClaim(user: ClaimRefreshableUser): Promise<boolean> {
  try {
    // Force a network round-trip: bypasses the cached token so a claim minted
    // after last login is picked up within the session.
    await user.getIdToken(true);
    const refreshed = await user.getIdTokenResult();
    return typeof refreshed.claims.companyId === 'string' && (refreshed.claims.companyId as string).length > 0;
  } catch (e) {
    console.warn('[forceRefreshCompanyClaim] token force-refresh failed:', (e as Error)?.message);
    return false;
  }
}

/**
 * BUG #100 — PROACTIVE staleness check used at the top of the company-shared
 * hop. When the store already knows a `companyId` but the LIVE token's claims
 * lack it, the token predates the claim grant — force a single refresh so the
 * subsequent collectionGroup query is authorized on the FIRST attempt rather
 * than relying on the catch-path retry.
 *
 * No-ops (returns false, issues no network call) when the live token already
 * carries the claim — the common steady-state path.
 */
async function refreshClaimIfStale(user: ClaimRefreshableUser): Promise<boolean> {
  try {
    const current = await user.getIdTokenResult();
    const claimPresent =
      typeof current.claims.companyId === 'string' && (current.claims.companyId as string).length > 0;
    if (claimPresent) return false;
  } catch {
    // If we can't even read the current token, fall through and try a refresh.
  }
  return forceRefreshCompanyClaim(user);
}

/**
 * In-flight fetch registry — module scope so parallel callers
 * (AuthGate's Promise.all + any component-level retry) share the
 * same Promise rather than racing to the same GCS endpoint.
 *
 * Key is a stable identifier ('fetchRagReports' / 'fetchSites'); value
 * is the Promise of the in-flight call. The wrapper clears the entry
 * once the promise settles so the next intentional refresh runs the
 * fetch fresh.
 */
const _inFlightFetches = new Map<string, Promise<void>>();
function dedupeFetch(key: string, run: () => Promise<void>): Promise<void> {
  const existing = _inFlightFetches.get(key);
  if (existing) return existing;
  const p = run().finally(() => {
    if (_inFlightFetches.get(key) === p) _inFlightFetches.delete(key);
  });
  _inFlightFetches.set(key, p);
  return p;
}

/** MP Phase 3 — debounced evidence readiness refresh after parcel edits. */
const _parcelEvidenceRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
function scheduleParcelEvidenceRefresh(
  siteId: string,
  get: () => { recomputeEvidenceReadiness: (siteId: string) => Promise<void> },
): void {
  const prev = _parcelEvidenceRefreshTimers.get(siteId);
  if (prev) clearTimeout(prev);
  _parcelEvidenceRefreshTimers.set(
    siteId,
    setTimeout(() => {
      _parcelEvidenceRefreshTimers.delete(siteId);
      void get().recomputeEvidenceReadiness(siteId);
    }, 800),
  );
}

/**
 * PH-A2.12 — clear the in-flight-fetch map on auth boundary changes.
 *
 * `_inFlightFetches` lives at module scope so it survives sign-out and
 * sign-in. Without an explicit reset, a fetchSites / fetchRagReports call
 * that started under user A and was still pending when A logged out would
 * stay in the map forever (its `.finally` only fires after the promise
 * settles — if the auth swap drops the request, the cleanup never runs).
 * User B's first dashboard load would then return user A's cached
 * Promise instead of issuing a fresh fetch against B's data.
 *
 * Called from AuthGate's onAuthStateChanged handler whenever the uid
 * changes (including null → uid, uid → null, uid-a → uid-b).
 */
export function resetInFlightFetches(): void {
  resetWorkspaceReadSession();
  _inFlightFetches.clear();
  inFlightReportBodyReads.clear();
  inFlightSiteReportBodyHydrations.clear();
  lastReportMetadataById.clear();
  inFlightCreateSiteRequests.clear();
  inFlightDocCreateLocks.clear();
  completedDraftCreateByRequestId.clear();
  _createSiteScopeGeneration += 1;
}

/** Tear down the report-generation jobs listener on auth boundary changes. */
let _reportGenerationJobsUnsub: (() => void) | null = null;
// FROZEN-CARD FIX (2026-06-17) — bounded re-arm state for the running-jobs
// listener (see the onError in subscribeReportGenerationJobs). A transient
// onSnapshot error silently DETACHES the listener; without re-arming, the
// progress card froze on its last frame ("QUEUED… 0%") even as the job ran and
// completed. The timer holds the pending re-subscribe; the attempt counter caps
// the loop on a persistent error and is reset by any healthy snapshot.
let _reportGenerationJobsResubTimer: ReturnType<typeof setTimeout> | null = null;
let _reportGenerationJobsResubAttempts = 0;
const REPORT_GEN_JOBS_MAX_RESUBS = 6;
// AUTH-DENIAL FIX (2026-08-03) — `permission-denied` on this listener is NOT
// transient, so the backoff re-arm above cannot clear it (see
// `reportGenerationJobsAuthRecovery.ts`: every arm of the deployed rule starts
// with `isAuthenticated()`, so an owner-path denial means the stream carried no
// credential). We force ONE ID-token refresh instead, then re-subscribe.
//
// `_reportGenerationJobsSubUid` scopes both budgets to the signed-in user: the
// re-arm path re-enters `subscribeReportGenerationJobs` with the SAME uid and
// must NOT reset them (that would be an unbounded loop), while a genuine
// sign-in as a DIFFERENT user must start fresh. The old code reset the attempt
// counter only on a healthy snapshot, so once a page session burned all six
// attempts the listener stayed dead for the rest of that page — the frozen
// progress card the re-arm was added to prevent.
let _reportGenerationJobsSubUid: string | null = null;
let _reportGenerationJobsAuthRecoveryTried = false;

/**
 * LIVE-SITES — module-scope holders for the real-time sites listener(s).
 *
 * There are TWO listeners, mirroring `fetchSites`'s two queries:
 *   - `_sitesOwnUnsub`     — the user's OWN `users/{uid}/sites` collection.
 *   - `_sitesCompanyUnsubs`— one per 30-id chunk of the company-shared
 *                            `collectionGroup('sites')` subtree sweep.
 *
 * `_sitesSubUid` / `_sitesSubCompanyId` track WHAT the live listeners are
 * currently scoped to, so `subscribeSites` is idempotent: a duplicate call
 * for the same (uid, companyId) is a no-op rather than a leaked double-
 * subscribe; a call for a DIFFERENT scope tears the old listeners down first.
 * These live at module scope (alongside `_reportGenerationJobsUnsub`) so they
 * survive sign-out / sign-in and can be torn down from the auth-boundary
 * reset path even after the store action object is recreated.
 */
let _sitesOwnUnsub: (() => void) | null = null;
let _sitesCompanyUnsubs: Array<() => void> = [];
let _sitesSubUid: string | null = null;
let _sitesSubCompanyId: string | null = null;

/**
 * LIVE-SITES — tear down every live sites listener and clear the scope
 * trackers. Called from `unsubscribeSites`, from `subscribeSites` before a
 * re-subscribe, and from the auth-boundary reset (`resetInFlightFetches`
 * companion below) on logout / uid change.
 */
export function resetSitesSubscription(): void {
  _sitesOwnUnsub?.();
  _sitesOwnUnsub = null;
  for (const u of _sitesCompanyUnsubs) u();
  _sitesCompanyUnsubs = [];
  _sitesSubUid = null;
  _sitesSubCompanyId = null;
}

/**
 * DE-PHANTOM/CANCEL — local cancelled-run tombstone set.
 *
 * cancelReportGenerationJob deletes the local card immediately, but the
 * server-side cancel is best-effort/async and the job doc's flip to
 * 'cancelled' LAGS. While the doc is still 'queued'/'running' the next
 * onSnapshot would unconditionally rebuild the card from the snapshot docs
 * and re-add the just-deleted run ("Cancel does nothing"). This set lets the
 * subscription suppress re-hydration of explicitly-cancelled runKeys until
 * the doc settles terminal (or a server-cancel failure leaves it lingering).
 *
 * Keys are run keys (clientRunId ?? jobId) AND, defensively, the jobId — the
 * caller passes the run key (activeGenerations key) but the snapshot rebuild
 * keys by `job.clientRunId ?? job.id`, so seeding both guarantees a match
 * even for runId-less / legacy jobs.
 *
 * Lifecycle: a key is removed once applyTerminalJob/the one-shot reconcile
 * settles that job (so the tombstone never leaks), and the whole set is
 * cleared on subscription teardown so a fresh login starts clean.
 */
const _cancelledRunKeys = new Set<string>();

/**
 * PUBLIC SENTIMENT EVIDENCE WAIT — watchers for Public Sentiment runs held
 * client-side while a site-evidence build runs (see
 * `src/lib/sentiment/sentimentEvidenceReadiness.ts`). Keyed by run id; the
 * value disposes the store subscription + re-check interval. In-memory only:
 * a closed page simply drops the wait (nothing was sent, nothing failed).
 */
const _sentimentEvidenceWaiters = new Map<string, () => void>();
/** Re-check cadence for time-based conditions (retry delay, lock lease, ceiling). */
const SENTIMENT_EVIDENCE_WAIT_TICK_MS = 10_000;
/**
 * Newest Public Sentiment attempt (run id) per site. A request superseded by a
 * newer attempt for its site is dropped silently if the server later refuses
 * it with a retryable 409, so it can never go back to waiting beside the newer
 * one and dispatch a second scan. Cleared on the auth uid boundary.
 */
const _latestSentimentAttemptBySite = new Map<string, string>();
/**
 * "Run again" for exhausted holds, keyed by hold (= run) id: the held run's
 * original enqueue call. In-memory like the hold itself; removed with the hold
 * (dismiss, a new attempt for the site) and on the auth uid boundary.
 */
const _sentimentEvidenceHoldRetries = new Map<string, { siteId: string; run: () => Promise<string | undefined> }>();

/**
 * The firebase module for the evidence-wait paths (requester capture and the
 * watcher's dispatch), loaded through ONE shared promise so those paths never
 * start concurrent imports of it. A failed load is forgotten so the next
 * attempt retries it.
 */
let _sentimentWaitFirebaseModule: Promise<typeof import('../firebase')> | null = null;
function loadFirebaseForSentimentWait(): Promise<typeof import('../firebase')> {
  if (!_sentimentWaitFirebaseModule) {
    const loading = import('../firebase');
    _sentimentWaitFirebaseModule = loading;
    loading.catch(() => {
      if (_sentimentWaitFirebaseModule === loading) _sentimentWaitFirebaseModule = null;
    });
  }
  return _sentimentWaitFirebaseModule;
}

function forgetSentimentHoldRetriesForSite(siteId: string): void {
  for (const [holdId, entry] of _sentimentEvidenceHoldRetries) {
    if (entry.siteId === siteId) _sentimentEvidenceHoldRetries.delete(holdId);
  }
}

/**
 * A waiting run exists only in this page's memory, so leaving the page loses
 * it. Warn before unload while any run waits (the waiting copy says "while
 * this page stays open"). Registered and removed with the waiter set, so it
 * also covers a wizard closed with "Continue in Background" and pages outside
 * the SiteRAG engine. addEventListener dedupes the same listener reference.
 */
function warnBeforeLeavingSentimentWait(event: BeforeUnloadEvent): string {
  event.preventDefault();
  event.returnValue = SENTIMENT_EVIDENCE_LEAVE_WARNING;
  return SENTIMENT_EVIDENCE_LEAVE_WARNING;
}

function syncSentimentEvidenceLeaveWarning(): void {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  if (_sentimentEvidenceWaiters.size > 0) window.addEventListener('beforeunload', warnBeforeLeavingSentimentWait);
  else window.removeEventListener('beforeunload', warnBeforeLeavingSentimentWait);
}

function disposeSentimentEvidenceWaiter(runId: string): boolean {
  const dispose = _sentimentEvidenceWaiters.get(runId);
  if (!dispose) return false;
  _sentimentEvidenceWaiters.delete(runId);
  dispose();
  syncSentimentEvidenceLeaveWarning();
  return true;
}

/** TEST SEAM — whether a held Public Sentiment run is still being watched. */
export function __hasSentimentEvidenceWaiterForTest(runId: string): boolean {
  return _sentimentEvidenceWaiters.has(runId);
}

/**
 * AUTH UID BOUNDARY (AuthGate, on logout / user swap) — a Public Sentiment
 * wait or hold belongs to the session that requested it. Stop every watcher
 * (so a build finishing later can never POST it under another user's token or
 * after sign-out), forget the newest-attempt and "Run again" records, and
 * remove waiting runs and holds from the store. Nothing was dispatched for
 * them, so there is no job to cancel and no outcome to record.
 * NOT called on a same-uid resubscribe (`resetReportGenerationJobsSubscription`
 * also runs then), which must keep the waits.
 */
export function resetSentimentEvidenceWaits(): void {
  for (const runId of [..._sentimentEvidenceWaiters.keys()]) disposeSentimentEvidenceWaiter(runId);
  _latestSentimentAttemptBySite.clear();
  _sentimentEvidenceHoldRetries.clear();
  useStore.setState((s) => {
    const runs = s.siteRag.activeGenerations ?? {};
    const waiting = Object.values(runs).filter(run => run.waitingForSiteEvidence).map(run => run.id);
    if (waiting.length === 0 && Object.keys(s.siteRag.sentimentEvidenceHolds ?? {}).length === 0) return {};
    const remaining = { ...runs };
    for (const id of waiting) delete remaining[id];
    const latest = Object.values(remaining)
      .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0];
    return {
      siteRag: {
        ...s.siteRag,
        activeGenerations: remaining,
        isLoading: Object.keys(remaining).length > 0,
        progress: normalizeProgressPercent(latest?.progress ?? 0),
        progressStage: latest?.progressStage ?? '',
        sentimentEvidenceHolds: {},
      },
    };
  });
}

export function resetReportGenerationJobsSubscription(): void {
  _reportGenerationJobsUnsub?.();
  _reportGenerationJobsUnsub = null;
  if (_reportGenerationJobsResubTimer) {
    clearTimeout(_reportGenerationJobsResubTimer);
    _reportGenerationJobsResubTimer = null;
  }
  _cancelledRunKeys.clear();
}

/**
 * TEST SEAM — seed the cancelled-run tombstone set directly. Used only by the
 * report-gen subscription unit tests to assert the de-orphan sweep honors a
 * tombstone (the real cancel path seeds this set internally; tests cannot reach
 * it without a live server cancel). No production caller.
 */
export function __seedCancelledRunKeyForTest(runKey: string): void {
  _cancelledRunKeys.add(runKey);
}

/**
 * Deep-strip undefined properties from an object/array tree. Firestore's web
 * SDK (without `ignoreUndefinedProperties`) throws on any undefined field
 * value, so optional fields on SiteEntity / SiteRagReport (siteLayers,
 * description, projectIntent, …) need to be removed — not set to null
 * — before writing.
 *
 * CDS-1 — extracted to `src/lib/firestore/stripUndefined.ts` so non-store
 * call sites (extractionCache, future evidence/claim writers) can use the
 * same scrub without pulling the whole store. Re-exported here so existing
 * store-internal call sites stay unchanged.
 */
export { stripUndefined } from '../lib/firestore/stripUndefined';
import { stripUndefined, applyAuthoritativeUpdates, isFieldValueSentinel } from '../lib/firestore/stripUndefined';
// P1.1 (document-intelligence plan) — document bodies never enter the
// Firestore site record; the per-document GCS JSON and chunk subcollection
// hold them. Every `documents:` write below is projected through this.
import { documentsForSiteRecord, stripDocumentBodiesFromSitePatch } from '../lib/siteDocumentPersistence';

// #82 — `SiteLockedError` lives in lib so the headless app store throws the
// same class; re-exported here for existing store importers.
export { SiteLockedError } from '../lib/siteLockError';
import { SiteLockedError } from '../lib/siteLockError';
// Client SED top-up guard — live-head comparison and pointer compare-and-set.
import {
  SED_TOP_UP_REFUSAL_MESSAGE,
  sameSedPointer,
  sedBuildFieldsFromGetter,
  sedBuildInProgress,
  sedPointerFromGetter,
  sedPointerOf,
} from '../lib/sed/sedPointerCas';

// CDS-3 — gate used by the per-action catch blocks below to skip the
// generic console.warn when the failure is actually a stale-chunk
// preload error (App.tsx's global handler reloads the tab; the store-
// level warn was misleading triage as "Firestore write failed").
import { isStaleChunkError } from '../lib/isStaleChunkError';
import { normalizeProgressPercent } from '../lib/coerceNumber';
import { REPORT_GENERATION_STALE_HEARTBEAT_MS } from '../types/reportGenerationJob';
import {
  siteOwnerUid,
  siteOwnerSitesPath,
  stripTransientSiteAnnotations,
  reportOwnerUid,
  reportOwnerDocPath,
  reportOwnerGcsPath,
  stripTransientReportAnnotations,
} from '../lib/siteOwnerPath';

// #owner-path-sweep — every site-doc WRITE (and shared-site subcollection
// READ) must target `users/{ownerUid}/sites/...`, where ownerUid is the uid
// whose path actually HOLDS the doc (transient `_ownerUid`, stamped by
// fetchSites from the collectionGroup ref; absent on own sites). Writing a
// company-shared, foreign-owned site to the CURRENT user's path targets a
// non-existent doc → the sites update rule errors on `resource.data` (null
// resource) → PERMISSION_DENIED for everyone, including admins (prod
// incident 2026-06-11, fixed for updateSite in ca5eefb0; this sweep extends
// the fix to every other writer). These wrappers resolve the entity from
// store state by id; if the site isn't in state they fall back to the
// current uid, which is byte-identical to the pre-sweep behaviour.
const ownerSitesPath = (state: AppState, siteId: string, currentUid: string): string =>
  siteOwnerSitesPath(state.siteRag.sites.find(s => s.id === siteId), currentUid);
const ownerSiteDocPath = (state: AppState, siteId: string, currentUid: string): string =>
  `${ownerSitesPath(state, siteId, currentUid)}/${siteId}`;

/** Compare the persisted document view; hydration alone is not a competing edit. */
const siteDocumentsBasis = (documents: readonly import('../types/siteRag').SiteDocument[] = []): string =>
  documents.map(siteDocumentMetadataBasisHash).join(',');

// #owner-path-sweep-p2 — same disease in the REPORTS collection. Wrappers
// resolve the report entity from store state by id; if the report isn't in
// state they fall back to the current uid (byte-identical to pre-sweep). The
// `_ownerUid` transient is stamped by `fetchRagReports` from the
// collectionGroup doc-ref path exactly as fetchSites does for sites.
const ownerReportDocPath = (state: AppState, reportId: string, currentUid: string): string =>
  reportOwnerDocPath(state.siteRag.reports.find(r => r.id === reportId), currentUid, reportId);
const ownerReportGcsPath = (state: AppState, reportId: string, currentUid: string): string =>
  reportOwnerGcsPath(state.siteRag.reports.find(r => r.id === reportId), currentUid, reportId);

const _siteLayerWriteTails = new Map<string, Promise<void>>();

// SiteContext mutations are whole-document read/merge/write operations. Keep
// every mutation ordered per site so extraction, conflict resolution and
// operator answers cannot persist stale snapshots over one another.
const _siteContextWriteTails = new Map<string, Promise<void>>();

// Document edits are confirmed in order per owner/site/document. Keep one
// operation ID for an unchanged ambiguous retry; the server owns its receipt.
const _siteDocumentWriteTails = new Map<string, Promise<void>>();
const _siteDocumentMetadataOperationIds = new Map<string, string>();
const _bomSourceDocumentPendingRequests = new Map<string, BomSourceDocumentLinkRequest>();
const _siteDocumentMetadataEditableFields = new Set(['fileName', 'summary', 'tags', 'folder', 'movedAt',
  'priority', 'includeInSed', 'includeInReport', 'importanceScore', 'importanceScoreAt']);

function enqueueSiteDocumentWrite<T>(key: string, write: () => Promise<T>): Promise<T> {
  const previous = _siteDocumentWriteTails.get(key) ?? Promise.resolve();
  const result = previous.catch(() => undefined).then(write);
  const tail = result.then(() => undefined, () => undefined);
  _siteDocumentWriteTails.set(key, tail);
  return result.finally(() => {
    if (_siteDocumentWriteTails.get(key) === tail) _siteDocumentWriteTails.delete(key);
  });
}


function enqueueSiteContextWrite<T>(siteId: string, write: () => Promise<T>): Promise<T> {
  const previous = _siteContextWriteTails.get(siteId) ?? Promise.resolve();
  const result = previous.catch(() => undefined).then(write);
  const tail = result.then(() => undefined, () => undefined);
  _siteContextWriteTails.set(siteId, tail);
  return result.finally(() => {
    if (_siteContextWriteTails.get(siteId) === tail) {
      _siteContextWriteTails.delete(siteId);
    }
  });
}

const OPEN_QUESTION_ENTRY_KEYS = new Set([
  'risks',
  'redFlags',
  'materialRisks',
  'keyRisks',
]);

/** Fail closed for hydrated and metadata-only immutable artifact records. */
function hasImmutableReportIdentityForMutation(report: SiteRagReport): boolean {
  const identity = report as SiteRagReport & {
    reportRevisionId?: unknown;
    artifactState?: unknown;
    deliveryDecision?: unknown;
  };
  if (Object.prototype.hasOwnProperty.call(identity, 'reportRevision')) return true;
  if (typeof identity.reportRevisionId === 'string' && identity.reportRevisionId.trim()) return true;
  if (
    identity.artifactState === 'ready'
    || identity.artifactState === 'staged'
    || identity.artifactState === 'failed'
  ) return true;
  return Boolean(
    identity.deliveryDecision
    && typeof identity.deliveryDecision === 'object'
    && (identity.deliveryDecision as { schemaVersion?: unknown }).schemaVersion === 'delivery-decision-v1'
  );
}

function normalizeSubmittedOpenQuestion(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
}

function customBriefQuestionIdFromSection(sectionId: string): string | null {
  if (sectionId.startsWith('customBriefQ_')) {
    return sectionId.slice('customBriefQ_'.length) || null;
  }
  // Compatibility with the short-lived recovered renderer spelling. New
  // callsites use the canonical customBriefQ_* section id.
  if (sectionId.startsWith('customBriefQuestion-')) {
    return sectionId.slice('customBriefQuestion-'.length) || null;
  }
  return null;
}

/** Resolve the canonical report-owned FindingClaim targeted by an answer. */
function findSubmittedOpenQuestion(
  report: SiteRagReport,
  sectionId: string | undefined,
  question: string,
): import('../lib/findingClaim').FindingClaim | null {
  const normalizedQuestion = normalizeSubmittedOpenQuestion(question);
  if (!normalizedQuestion) return null;

  const normalizedSectionId = sectionId?.trim();
  let section: unknown = report;
  if (normalizedSectionId) {
    const customBriefQuestionId = customBriefQuestionIdFromSection(normalizedSectionId);
    if (customBriefQuestionId) {
      section = report.customBriefAnswers?.find(
        (answer) => answer.questionId === customBriefQuestionId,
      );
    } else {
      section = (report as unknown as Record<string, unknown>)[normalizedSectionId];
    }
  }
  if (section == null) return null;

  const visited = new WeakSet<object>();
  const visit = (
    value: unknown,
    findingCollection = false,
  ): import('../lib/findingClaim').FindingClaim | null => {
    if (typeof value === 'string') {
      return findingCollection && normalizeSubmittedOpenQuestion(value) === normalizedQuestion
        ? { claim: value.trim(), evidenceState: 'unknown', openQuestion: value.trim() }
        : null;
    }
    if (value == null || typeof value !== 'object') return null;
    if (visited.has(value)) return null;
    visited.add(value);

    if (!Array.isArray(value)) {
      const candidate = value as Record<string, unknown>;
      if (
        candidate.evidenceState === 'unknown'
        && typeof candidate.claim === 'string'
      ) {
        const finding = candidate as unknown as import('../lib/findingClaim').FindingClaim;
        const identity = resolveOpenQuestionIdentity(finding);
        if (
          identity.questionAliases.some(
            (alias) => normalizeSubmittedOpenQuestion(alias) === normalizedQuestion,
          )
        ) {
          return finding;
        }
      }
      for (const [key, child] of Object.entries(candidate)) {
        const found = visit(child, findingCollection || OPEN_QUESTION_ENTRY_KEYS.has(key));
        if (found) return found;
      }
      return null;
    }
    for (const child of value) {
      const found = visit(child, findingCollection);
      if (found) return found;
    }
    return null;
  };

  return visit(section);
}

/** Serialize site-layer writes per site so field patches cannot race a batch replacement. */
function enqueueSiteLayerWrite<T>(siteId: string, write: () => Promise<T>): Promise<T> {
  const previous = _siteLayerWriteTails.get(siteId) ?? Promise.resolve();
  const result = previous.catch(() => undefined).then(write);
  const tail = result.then(() => undefined, () => undefined);
  _siteLayerWriteTails.set(siteId, tail);
  return result.finally(() => {
    if (_siteLayerWriteTails.get(siteId) === tail) {
      _siteLayerWriteTails.delete(siteId);
    }
  });
}

const pendingStatusCasOperations = new Map<string, string>();
const pendingSedAmendmentOperations = new Map<string, string>();
const pendingSedClearOperations = new Map<
  string,
  {
    operationId: string;
    ownerUid: string;
    clearIntent: 'delete' | 'rebuild';
    expectedSedId: string;
    expectedSedVersion: number;
    expectedHistoryVersion: number;
    expectedSedStorageGeneration?: string;
  }
>();
const pendingSedReapplyOperations = new Map<
  string,
  {
    operationId: string;
    ownerUid: string;
    sedId: string;
    sourceSedId: string;
    expectedSedVersion: number;
    expectedHistoryVersion: number;
    expectedSedStorageGeneration?: string;
  }
>();
const pendingSedRevertOperations = new Map<
  string,
  {
    operationId: string;
    ownerUid: string;
    siteId: string;
    sedId: string;
    variablePath: string;
    amendmentId: string;
    reason: string;
    expectedSedVersion?: number;
    expectedHistoryVersion?: number;
    expectedSedStorageGeneration?: string;
    requireReviewWorkflow?: true;
    requestBody: Record<string, unknown>;
    lineage: {
      latestNewValue: unknown;
      earliestVariableLabel: string;
      earliestId: string;
      originalProviderValue: unknown;
      supersededIds: string[];
    };
  }
>();

export const useStore = create<AppState>((set, get, api) => ({
  nodes: initialNodes,
  edges: initialEdges,
  canvasHistory: EMPTY_CANVAS_HISTORY,
  viewMode: 'siterag',
  site: null,

  // FIX #92 — no phantom "Simulation 1". Start with ZERO canvas tabs so a fresh
  // editor load shows an empty state (just the "+" to create a sim). A real tab
  // appears only when the user clicks "+" (addTab) or a saved sim is restored
  // (App.tsx localStorage rehydrate / loadSimulation / addTab paths).
  tabs: [],
  activeTabId: '',
  customComponents: [],

  auditBySite: {},
  recommendationGraphBySite: {},
  portfolio: {
    composites: {},
    lastComputedAt: null,
    computing: false,
    error: null,
    bandConfig: null,
    bandConfigLoading: false,
    clustering: null,
    geoClusters: null,
  },

  siteRag: {
    reports: [],
    activeReportId: null,
    comparisonIds: [],
    isLoading: false,
    progress: 0,
    progressStage: '',
    error: null,
    activeGenerations: {},
    generationOutcomes: {},
    sentimentEvidenceHolds: {},
    isDemoMode: false,
    sites: [],
    activeSiteId: null,
    isSitesLoaded: false,
    isReportsLoaded: false,
    isReportMetadataLoaded: false,
    hydratedReportSiteIds: [],
  },
  userRole: 'analyst',
  companyId: null,
  companyRole: null,
  companyName: null,
  companyArchetype: null,
  companyArchetypeNote: null,
  companyMissingCriticalCap: null,
  companyRequireReviewWorkflow: null,
  companySedBootstrapFreshnessThresholdMs: null,
  // Brand logo (feat/org-logo) — loaded by loadBrandLogos() at auth init.
  userLogoUrl: null,
  orgLogoUrl: null,
  // HEARTBEAT-IDENTITY — see the AppState doc; rebuilt by the jobs listener.
  generationHeartbeats: {},
  // Fix_React_Error_185_Prompt — see SiteRagSlice docs above.
  isInitialMigration: false,
  setInitialMigration: (v) => set({ isInitialMigration: v }),
  companyMembers: [],
  subtreeCompanies: [],
  setSubtreeCompanies: (companies) => set({ subtreeCompanies: companies }),
  featureAccess: { canAccessSiteRag: true, canAccessSimulation: true },
  adminOverrideEnabled: (() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('adminOverrideEnabled') === '1'; }
    catch { return false; }
  })(),
  setAdminOverrideEnabled: (enabled) => {
    try { if (typeof localStorage !== 'undefined') localStorage.setItem('adminOverrideEnabled', enabled ? '1' : '0'); }
    catch { /* ignore */ }
    set({ adminOverrideEnabled: enabled });
  },
  loadCompanyData: async () => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    const applyCompanyScope = (nextCompanyId: string | null, nextRole: AppState['companyRole']) => {
      if (auth.currentUser?.uid !== user.uid) return false;
      const changed = get().companyId !== nextCompanyId;
      if (changed) resetInFlightFetches();
      set(state => ({ companyId: nextCompanyId, companyRole: nextRole,
        ...(changed ? { siteRag: { ...state.siteRag, isSitesLoaded: false, isReportsLoaded: false, isReportMetadataLoaded: false } } : {}),
      }));
      return true;
    };

    // Read the token claim BEFORE issuing any company-scoped query — users
    // without a companyId claim are not in a company, and any subsequent
    // companies/{cid} or `where('companyId', ...)` query would correctly fail
    // Firestore rules. Skipping silently here keeps the console clean for
    // solo users (the rules are doing their job; this isn't an error).
    let companyId: string | undefined;
    let companyRoleClaim: string | undefined;
    try {
      const token = await user.getIdTokenResult();
      companyId = token.claims.companyId as string | undefined;
      companyRoleClaim = token.claims.companyRole as string | undefined;
    } catch (e) {
      console.debug('[loadCompanyData] token refresh failed — skipping', e);
      return;
    }
    if (!companyId) {
      // R-111 — claim missing. Try profile-doc fallback so the current
      // session works, and queue a server-side claim repair so the next
      // session works without fallback.
      try {
        const { db } = await import('../firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const profileSnap = await getDoc(doc(db, `users/${user.uid}`));
        const profile = profileSnap.exists() ? profileSnap.data() : null;
        const fallbackCompanyId = profile?.companyId as string | undefined;
        const fallbackRole = profile?.companyRole as string | undefined;
        if (fallbackCompanyId) {
          console.warn(
            '[loadCompanyData] companyId claim missing for uid',
            user.uid,
            '— using profile fallback + queueing server-side repair',
          );
          companyId = fallbackCompanyId;
          companyRoleClaim = fallbackRole;
          // Fire-and-forget repair. User must re-auth to pick up new claim.
          void (async () => {
            try {
              // Routed through the server proxy (`/api/functions/repairUserCompanyClaim`).
              // The org's Domain Restricted Sharing policy blocks direct browser →
              // Cloud Run callable invocation, so the previously region-pinned
              // `httpsCallable(functionsWest1(), ...)` call fails at the Cloud Run
              // edge. The proxy verifies the ID token and delegates with the
              // trusted uid (admin-or-self enforced server-side).
              const { callUserFunction } = await import('../lib/callUserFunction');
              await callUserFunction('repairUserCompanyClaim', { uid: user.uid });
              console.info('[loadCompanyData] claim repair queued for uid', user.uid,
                '— sign out + back in to pick up the new claim');
            } catch (e) {
              console.warn('[loadCompanyData] claim repair call failed:', (e as Error)?.message);
            }
          })();
        } else {
          console.debug('[loadCompanyData] no claim AND no profile companyId — user truly not in a company');
          applyCompanyScope(null, null);
          return;
        }
      } catch (e) {
        console.debug('[loadCompanyData] profile fallback failed:', e);
        return;
      }
    }

    const validRoles: import('../types/company').CompanyRole[] = ['admin', 'approver', 'reviewer', 'analyst', 'viewer'];
    const companyRole = validRoles.includes(companyRoleClaim as any) ? companyRoleClaim as import('../types/company').CompanyRole : 'viewer';
    if (!applyCompanyScope(companyId, companyRole)) return;

    try {
      // Load company info
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const c = companyDoc.data() as Partial<import('../types/company').Company>;
        set({
          companyName: c.name ?? null,
          companyArchetype: c.archetype ?? null,
          companyArchetypeNote: c.archetypeNote ?? null,
          companyMissingCriticalCap: c.settings?.missingCriticalCap ?? null,
          companyRequireReviewWorkflow: c.settings?.requireReviewWorkflow ?? null,
          companySedBootstrapFreshnessThresholdMs:
            c.settings?.sedBootstrapFreshnessThresholdMs ?? null,
        });
      }

      // Load member's feature access
      const memberDoc = await getDoc(doc(db, 'company_members', `${companyId}_${user.uid}`));
      if (memberDoc.exists()) {
        const data = memberDoc.data();
        set({
          featureAccess: {
            canAccessSiteRag: data.features?.canAccessSiteRag ?? false,
            canAccessSimulation: data.features?.canAccessSimulation ?? false,
          },
        });
      }

      // Load all company members (needed for approval workflow assignment)
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const membersSnap = await getDocs(query(collection(db, 'company_members'), where('companyId', '==', companyId)));
        const members = membersSnap.docs.map(d => ({ ...d.data() })) as import('../types/company').CompanyMember[];
        set({ companyMembers: members });
      } catch { /* best-effort — members list is non-critical */ }
    } catch (e) {
      // Permission-denied AT THIS POINT means the claim is set but the rules
      // still rejected — token likely stale (claim was just removed) or rules
      // changed. That's a real problem; warn so it's visible.
      if (isPermissionDenied(e)) {
        console.warn('[loadCompanyData] permission denied despite companyId claim — token may be stale; sign out and back in if this persists.', e);
      } else {
        console.warn('[loadCompanyData] failed:', e);
      }
    }
  },
  canAccessSiteRag: () => get().featureAccess.canAccessSiteRag,
  canAccessSimulation: () => get().featureAccess.canAccessSimulation,

  // ── Brand logo (feat/org-logo) ─────────────────────────────────────
  // (userLogoUrl / orgLogoUrl initial values live with the company fields
  //  above; only the actions + derived selectors are declared here.)
  loadBrandLogos: async () => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    const companyId = get().companyId;
    const svc = await import('../services/brandLogoService');
    // Both tiers in parallel; each fails closed to null on its own.
    const [userLogoUrl, orgLogoUrl] = await Promise.all([
      svc.fetchUserLogoUrl(user.uid),
      companyId ? svc.fetchOrgLogoUrl(companyId) : Promise.resolve(null),
    ]);
    set({ userLogoUrl, orgLogoUrl });
  },
  setUserLogo: async (logoUrl) => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in — cannot save your logo.');
    const svc = await import('../services/brandLogoService');
    await svc.saveUserLogoUrl(user.uid, logoUrl);
    set({ userLogoUrl: logoUrl });
  },
  setOrgLogo: async (companyId, logoUrl) => {
    if (!companyId) throw new Error('No organization — cannot save org logo.');
    const svc = await import('../services/brandLogoService');
    await svc.saveOrgLogoUrl(companyId, logoUrl);
    // Reflect immediately only when it's the caller's OWN org (the value
    // that drives this user's effective logo). An admin editing a foreign
    // org doesn't change their own brand.
    if (companyId === get().companyId) set({ orgLogoUrl: logoUrl });
  },
  effectiveLogoUrl: () => {
    const s = get();
    return resolveEffectiveLogo({ userLogoUrl: s.userLogoUrl, orgLogoUrl: s.orgLogoUrl });
  },
  uploadedLogoUrl: () => {
    const s = get();
    return resolveUploadedLogo({ userLogoUrl: s.userLogoUrl, orgLogoUrl: s.orgLogoUrl });
  },

  reportTheme: (() => {
    try {
      const saved = localStorage.getItem('deepatomic_report_theme');
      if (saved) return { ...DEFAULT_THEME, ...JSON.parse(saved) };
    } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
    return { ...DEFAULT_THEME };
  })(),
  lastReportThemeSavedAt: null,
  // TSC-3 — transient Studio draft channel; null when Studio is closed.
  activeDraftTheme: null,
  setActiveDraftTheme: (theme) => set({ activeDraftTheme: theme }),
  // TPN-1 — transient Studio draft TEMPLATE channel; symmetric with
  // activeDraftTheme. Cleared on Studio unmount + on Save.
  // TCP-7 + TCP-8 (row 0.99233) — same channel carries the Studio's
  // section list (page assignment + drag-reorder + visibility).
  activeDraftTemplate: null,
  setActiveDraftTemplate: (template) => set({ activeDraftTemplate: template }),
  // RTU-4 — Studio's per-type configuring channel. `null` = legacy /
  // flat-list mode (full back-compat). Studio writes the user's
  // dropdown pick here; the Sections + Pages panes read it to route
  // edits into `template.sectionsByReportType[type]`.
  studioConfiguringType: null,
  setStudioConfiguringType: (type) => set({ studioConfiguringType: type }),
  studioConfiguringTypePinned: false,
  setStudioConfiguringTypePinned: (pinned) => set({ studioConfiguringTypePinned: pinned }),
  _settingsHydrated: false,
  userApiKeys: (() => {
    // API keys for NREL, EIA, AirNow are now injected server-side by the proxy.
    // No client-side keys needed. This store only holds user-provided keys
    // for any future providers that require user-specific credentials.
    try {
      const saved = localStorage.getItem('deepatomic_api_keys');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch { /* ignore */ }
    return {};
  })(),
  setUserApiKeys: (keys) => {
    set({ userApiKeys: keys });
    // Persist to localStorage
    try { localStorage.setItem('deepatomic_api_keys', JSON.stringify(keys)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
    // Persist to Firestore for cross-device sync
    (async () => {
      try {
        const { auth, db } = await import('../firebase');
        const user = auth.currentUser;
        if (user) {
          const { doc, setDoc } = await import('firebase/firestore');
          await setDoc(doc(db, `users/${user.uid}/settings`, 'apiKeys'), keys, { merge: true });
        }
      } catch { /* best-effort */ }
    })();
  },

  setViewMode: (mode) => set({ viewMode: mode }),
  // S1 — node glyphs default to the COMPACT view so a freshly opened canvas
  // (preset or blank) reads as a clean schematic; the toolbar toggle flips to
  // the detailed/expanded glyph. (Was `false` / detailed-by-default.)
  compactView: true,
  setCompactView: (compact) => set({ compactView: compact }),
  // #102/#106 — canvas-panel visibility. Default every overlay visible; the
  // "View" popup flips these and the editor gates each panel's render on the
  // matching flag. #115 — now persisted on EditorViewState (AutoSaver) and
  // restored on load (restoreEditorViewState).
  showMinimap: true,
  setShowMinimap: (show) => set({ showMinimap: show }),
  showControls: true,
  setShowControls: (show) => set({ showControls: show }),
  showSolverStatus: true,
  setShowSolverStatus: (show) => set({ showSolverStatus: show }),
  showInspector: true,
  setShowInspector: (show) => set({ showInspector: show }),
  // #115 — onboarding guide overlay; default visible, persisted on view-state.
  showGuide: true,
  setShowGuide: (show) => set({ showGuide: show }),
  restoreAllPanels: () =>
    set({
      showMinimap: true,
      showControls: true,
      showSolverStatus: true,
      showInspector: true,
    }),
  // ── #116 — Layers/Cycles: per-component / per-cycle hide + ignore ──────────
  // Exception-list semantics: a node id absent from `hiddenNodeIds` is visible,
  // absent from `ignoredNodeIds` is solved. So an empty editor (or a legacy sim
  // that never touched the panel) behaves exactly as before.
  hiddenNodeIds: [],
  ignoredNodeIds: [],
  setNodeHidden: (id, hidden) =>
    set((s) => {
      const present = s.hiddenNodeIds.includes(id);
      if (hidden === present) return {} as Partial<typeof s>;
      return {
        hiddenNodeIds: hidden
          ? [...s.hiddenNodeIds, id]
          : s.hiddenNodeIds.filter((x) => x !== id),
      };
    }),
  setNodeIgnored: (id, ignored) =>
    set((s) => {
      if (isActiveCanvasRepairProtected(s)) return {} as Partial<typeof s>;
      const present = s.ignoredNodeIds.includes(id);
      if (ignored === present) return {} as Partial<typeof s>;
      const ignoredNodeIds = normalizeIgnoredNodeIds(
        ignored
          ? [...s.ignoredNodeIds, id]
          : s.ignoredNodeIds.filter((x) => x !== id),
        s.nodes,
      );
      return {
        ignoredNodeIds,
        tabs: s.tabs.map((tab) => tab.id === s.activeTabId
          ? { ...tab, ignoredNodeIds }
          : tab),
      };
    }),
  setCycleHidden: (cycleId, hidden) =>
    set((s) => {
      // Match by data.cycleId; the "Ungrouped" bucket is cycleId === null/undefined.
      const memberIds = s.nodes
        .filter((n) => ((n.data as Record<string, unknown> | undefined)?.cycleId ?? null) === cycleId)
        .map((n) => n.id);
      if (memberIds.length === 0) return {} as Partial<typeof s>;
      const next = new Set(s.hiddenNodeIds);
      for (const id of memberIds) {
        if (hidden) next.add(id);
        else next.delete(id);
      }
      return { hiddenNodeIds: Array.from(next) };
    }),
  setCycleIgnored: (cycleId, ignored) =>
    set((s) => {
      if (isActiveCanvasRepairProtected(s)) return {} as Partial<typeof s>;
      const memberIds = s.nodes
        .filter((n) => ((n.data as Record<string, unknown> | undefined)?.cycleId ?? null) === cycleId)
        .map((n) => n.id);
      if (memberIds.length === 0) return {} as Partial<typeof s>;
      const next = new Set(s.ignoredNodeIds);
      for (const id of memberIds) {
        if (ignored) next.add(id);
        else next.delete(id);
      }
      const ignoredNodeIds = normalizeIgnoredNodeIds(Array.from(next), s.nodes);
      return {
        ignoredNodeIds,
        tabs: s.tabs.map((tab) => tab.id === s.activeTabId
          ? { ...tab, ignoredNodeIds }
          : tab),
      };
    }),
  // S8 — viewport (pan/zoom) is null until the user moves the canvas (or a saved
  // viewport is restored on load). syncActiveTab mirrors it onto the active tab.
  flowViewport: null,
  setFlowViewport: (viewport) => {
    set({ flowViewport: viewport });
    get().syncActiveTab();
  },
  colorOverlay: null,
  setColorOverlay: (variable) => set({ colorOverlay: variable }),
  canvasBackgroundMode: 'grid',
  setCanvasBackgroundMode: (mode) => set({ canvasBackgroundMode: mode }),
  canvasMetersPerFlowUnit: 1,
  setCanvasMetersPerFlowUnit: (metersPerUnit) =>
    set({ canvasMetersPerFlowUnit: metersPerUnit > 0 ? metersPerUnit : 1 }),
  setSite: (site) => {
    if (isActiveCanvasRepairProtected(get())) return;
    set({ site });
    get().syncActiveTab();
  },

  syncActiveTab: () => {
    const state = get();
    // The protected tab is the canonical source. Never mirror live arrays back
    // into it: even a raw top-level drift must be discarded when the user
    // switches away and returns. View-only state remains usable in-session.
    if (isActiveCanvasRepairProtected(state)) return;
    // Single write-side choke point for every editable node mutation (drop,
    // template, paste, inspector edit, undo/redo): a parameter value that is
    // in use is accepted at the source ("in use => accepted", owner direction
    // 5 Oct 2026). Identity-preserving, so settled canvases do not re-render.
    // The execution profile in use has its model limitations accepted for the
    // drawn topology at the same choke point (owner direction 6 Oct 2026).
    // A tab bound to a project revision is settled only once its parameters
    // or connections are actually edited (which makes it a new draft): an
    // unedited head must stay byte-exact through tab switches, viewport moves,
    // result decorations and save checks.
    const activeTab = state.tabs.find((tab) => tab.id === state.activeTabId);
    const edited = activeTab?.revisionBinding === undefined
      || nodeParamsChanged(activeTab.nodes, state.nodes)
      || edgeTopologyChanged(activeTab.edges, state.edges);
    const settled = edited
      ? acceptInUseCanvasAssumptions({
          nodes: state.nodes,
          edges: state.edges,
          ...(activeTab?.eoCanvasExecutionConfig !== undefined
            ? { eoCanvasExecutionConfig: activeTab.eoCanvasExecutionConfig }
            : {}),
        })
      : { nodes: state.nodes, eoCanvasExecutionConfig: activeTab?.eoCanvasExecutionConfig };
    const nodes = settled.nodes;
    const ignoredNodeIds = normalizeIgnoredNodeIds(state.ignoredNodeIds, nodes);
    set({
      ...(nodes !== state.nodes ? { nodes } : {}),
      ignoredNodeIds,
      tabs: state.tabs.map(tab =>
        tab.id === state.activeTabId
          ? {
            ...tab,
            nodes,
            edges: state.edges,
            ...(settled.eoCanvasExecutionConfig !== undefined
              && settled.eoCanvasExecutionConfig !== tab.eoCanvasExecutionConfig
              ? { eoCanvasExecutionConfig: settled.eoCanvasExecutionConfig }
              : {}),
            simState: state.simState,
            site: state.site,
            ignoredNodeIds,
            // S8 — persist the viewport only when one has been captured; leave
            // the tab's existing value untouched (back-compat) when it's null.
            ...(state.flowViewport ? { flowViewport: state.flowViewport } : {}),
          }
          : tab
      )
    });
  },

  createEoRepairCopy: (newName: string) => {
    const state = get();
    const sourceTab = state.tabs.find((tab) => tab.id === state.activeTabId);
    // Deep-copy the canonical tab payload, not the live top-level arrays. A
    // protected tab deliberately refuses syncActiveTab, so even accidental
    // raw live-state drift cannot contaminate the explicit repair copy.
    // The copy is an editable model: every value it carries is in use, so it
    // is accepted at the source ("in use => accepted"); values are unchanged.
    const copiedEdges = JSON.parse(JSON.stringify(sourceTab?.edges ?? state.edges)) as Edge[];
    const copiedInUse = acceptInUseCanvasAssumptions({
      nodes: (JSON.parse(JSON.stringify(sourceTab?.nodes ?? state.nodes)) as AppNode[])
        .map(newCanvasNodeIncarnation),
      edges: copiedEdges,
      eoGraphConfig: cloneEoGraphConfigForRepair(sourceTab?.eoGraphConfig),
      eoCanvasExecutionConfig: normalizeEoCanvasExecutionConfig(
        sourceTab?.eoCanvasExecutionConfig,
      ).config,
      eoStudySpec: normalizeEoStudyDefinition(sourceTab?.eoStudySpec).spec,
      eoTransientConfig: cloneEoTransientConfigForRepair(sourceTab?.eoTransientConfig),
    });
    const copiedNodes = copiedInUse.nodes;
    const copiedSimState = {
      ...JSON.parse(JSON.stringify(sourceTab?.simState ?? state.simState)),
      isRunning: false,
    } as SimulationState;
    const copiedEoGraphConfig = copiedInUse.eoGraphConfig;
    const copiedEoCanvasExecutionConfig = copiedInUse.eoCanvasExecutionConfig;
    const copiedEoStudySpec = copiedInUse.eoStudySpec;
    const copiedEoTransientConfig = copiedInUse.eoTransientConfig;
    const copiedUncertaintyDefinitionSet = sourceTab?.eoKpiUncertaintyDefinitionSet === undefined
      ? undefined
      : parseEoKpiUncertaintyDefinitionSetV1(sourceTab.eoKpiUncertaintyDefinitionSet);
    const copiedIgnoredNodeIds = normalizeIgnoredNodeIds(
      sourceTab?.ignoredNodeIds ?? state.ignoredNodeIds,
      copiedNodes,
    );
    // S9 — an EO repair copy should land linked to a site when one is available.
    // Imported models commonly have site===null, so seed from the active
    // SiteEntity: prefer the active-site pointer, then the site owning the active
    // report. The real SiteEntity id preserves model-scoped EO run history. If
    // nothing is active, the copy remains unlinked and readiness explains it.
    const resolveActiveSite = (): Site | null => {
      if (sourceTab?.site) return JSON.parse(JSON.stringify(sourceTab.site));
      if (!isRepairProtectedTab(sourceTab) && state.site) {
        return JSON.parse(JSON.stringify(state.site));
      }
      const sites = state.siteRag?.sites ?? [];
      const { activeSiteId, activeReportId } = state.siteRag ?? {};
      const entity =
        (activeSiteId ? sites.find((s) => s.id === activeSiteId) : undefined) ??
        (activeReportId
          ? sites.find((s) => s.reportIds?.includes(activeReportId))
          : undefined);
      return entity ? siteFromEntity(entity) : null;
    };
    const forkedSite = resolveActiveSite();
    // Create a new editable tab without the legacy source marker.
    const newId = 'tab-' + safeUUID();
    const newTab: SimulationTab = {
      id: newId,
      name: newName,
      nodes: copiedNodes,
      edges: copiedEdges,
      simState: copiedSimState,
      site: forkedSite,
      eoGraphConfig: copiedEoGraphConfig,
      eoCanvasExecutionConfig: copiedEoCanvasExecutionConfig,
      ignoredNodeIds: copiedIgnoredNodeIds,
      eoStudySpec: copiedEoStudySpec,
      eoTransientConfig: copiedEoTransientConfig,
      ...(copiedUncertaintyDefinitionSet
        ? { eoKpiUncertaintyDefinitionSet: copiedUncertaintyDefinitionSet }
        : {}),
      // A fork is a new source model. It cannot inherit the imported canvas's
      // cloud revision or evidence identity.
      savedEoInputSignature: null,
    };
    set({
      tabs: [...state.tabs, newTab],
      activeTabId: newId,
      nodes: copiedNodes,
      edges: copiedEdges,
      simState: copiedSimState,
      // S9 — make the copy active and mirror the resolved site to the live
      // top-level `site`, so EO persistence/readiness sees it immediately.
      site: forkedSite,
      // Visibility is presentation-only and starts clean. Solve membership is
      // part of the copied model, so the repair copy keeps the exact subject.
      hiddenNodeIds: [],
      ignoredNodeIds: copiedIgnoredNodeIds,
      // Forked into a fresh editable canvas — undo history starts empty.
      canvasHistory: EMPTY_CANVAS_HISTORY,
    });
  },

  addCustomComponent: (comp) => set((state) => ({
    customComponents: [...state.customComponents, comp]
  })),
  updateCustomComponent: (id, comp) => set((state) => ({
    customComponents: state.customComponents.map(c => c.id === id ? comp : c)
  })),
  deleteCustomComponent: (id) => set((state) => ({
    customComponents: state.customComponents.filter(c => c.id !== id)
  })),

  setActiveRagReport: (id) => {
    set((state) => ({
      siteRag: { ...state.siteRag, activeReportId: id }
    }));
    if (id) {
      const target = get().siteRag.reports.find((r) => r.id === id);
      if ((target as { _portfolioSlimBody?: boolean } | undefined)?._portfolioSlimBody === true) {
        void get().hydrateReportBody(id);
      }
    }
  },

  hydrateReportBody: async (reportId) => {
    const sessionSignal = workspaceReadSignal();
    const scopeCompanyId = get().companyId;
    const report = get().siteRag.reports.find((r) => r.id === reportId);
    if (!report) return false;
    if (
      report._metadataOnly !== true
      && (report as { _portfolioSlimBody?: boolean })._portfolioSlimBody !== true
    ) {
      return true;
    }
    const { auth } = await import('../firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) return false;
    const metadata = report as unknown as Record<string, unknown>;
    try {
      const hydrated = await loadReportBodyWithUserState(metadata, uid, scopeCompanyId);
      if (
        !hydrated
        || sessionSignal.aborted
        || auth.currentUser?.uid !== uid
        || get().companyId !== scopeCompanyId
      ) {
        return false;
      }
      return publishHydratedReportBody(set, metadata, hydrated);
    } catch {
      // ReportReaderRecovery exposes Retry; a timeout never fabricates a body.
      return false;
    }
  },

  ensureSiteReportBodies: async (siteId) => {
    const sessionSignal = workspaceReadSignal();
    if (!siteId) return;
    const inFlight = inFlightSiteReportBodyHydrations.get(siteId);
    if (inFlight) return inFlight;
    const run = (async () => {
      // Nothing to scope until the library rows exist. Callers re-run once
      // `isReportMetadataLoaded` flips, and the list pass itself reads the
      // open site's heads.
      if (!get().siteRag.isReportMetadataLoaded) return;
      const { auth } = await import('../firebase');
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const scopeCompanyId = get().companyId;
      const scopeIsCurrent = () => !sessionSignal.aborted && auth.currentUser?.uid === uid && get().companyId === scopeCompanyId;

      const scope = siteScopeFor(get().siteRag.sites, siteId);
      const siteRows = get().siteRag.reports
        .filter((row) => reportMatchesSiteScope(row as unknown as Record<string, unknown>, scope));
      const heads = selectEagerReportBodyIds(siteRows as unknown as Record<string, unknown>[]);
      const pending = siteRows.filter((row) => {
        const storagePath = (row as unknown as Record<string, unknown>).storagePath;
        return row._metadataOnly === true
          && heads.has(row.id)
          && typeof storagePath === 'string'
          && storagePath.length > 0;
      });
      const migrations = createReportMigrationBatch(get, scopeIsCurrent);
      const hydratedById = new Map<
        string,
        { metadata: Record<string, unknown>; report: SiteRagReport }
      >();
      await mapWithConcurrency(pending, REPORT_LIST_BODY_HYDRATION_CONCURRENCY, async (row) => {
        const metadata = row as unknown as Record<string, unknown>;
        try {
          const hydrated = await loadReportBodyWithUserState(metadata, uid);
          if (!hydrated || !scopeIsCurrent()) return;
          const prepared = prepareLoadedReportBody(hydrated);
          const fullReport = migrations.collect(prepared);
          const keepFull =
            row.id === get().siteRag.activeReportId
            || prepared.persistSanityMigration
            || prepared.retirementMarker !== null;
          hydratedById.set(row.id, {
            metadata,
            report: keepFull ? fullReport : slimPortfolioReportBody(fullReport),
          });
        } catch {
          // Fail closed exactly like the list pass: the row stays an explicit
          // metadata-only record and the reader's Retry remains available.
        }
      });

      if (!scopeIsCurrent()) return;
      set((state) => {
        const nextSiteRag = {
          ...state.siteRag,
          reports: publishHydratedReportBodies(
            state.siteRag.reports,
            hydratedById,
            state.siteRag.activeReportId,
          ),
        };
        return {
          siteRag: { ...nextSiteRag, ...markResidentReportSites(nextSiteRag, [siteId]) },
        };
      });
      const activeIdAfterEnsure = get().siteRag.activeReportId;
      if (
        activeIdAfterEnsure
        && (hydratedById.get(activeIdAfterEnsure)?.report as { _portfolioSlimBody?: boolean } | undefined)?._portfolioSlimBody === true
      ) {
        void get().hydrateReportBody(activeIdAfterEnsure);
      }
      migrations.flush();
    })().finally(() => {
      if (inFlightSiteReportBodyHydrations.get(siteId) === run) {
        inFlightSiteReportBodyHydrations.delete(siteId);
      }
    });
    inFlightSiteReportBodyHydrations.set(siteId, run);
    return run;
  },

  repairReportData: async () => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return { repaired: 0, failed: 0 };

    const token = await user.getIdToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    const reports = get().siteRag.reports;
    let repaired = 0;
    let failed = 0;

    // Find reports with missing datasets or location
    const incomplete = reports.filter(r =>
      !r.datasets || !r.location || !r.categories || r.categories.length === 0
    );

    if (incomplete.length === 0) return { repaired: 0, failed: 0 };

    // Try to load full data from GCS for each incomplete report
    const updatedReports = [...reports];
    for (const report of incomplete) {
      // Company-shared reports owned by another org keep their content under the
      // OWNER's uid — use the report's own storagePath rather than reconstructing
      // with the viewer's uid (which 404s cross-org → "insufficient data"). The
      // server authorizes the cross-org read for company-shared reports.
      const storagePath = (report as { storagePath?: string }).storagePath
        ?? `users/${user.uid}/reports/${report.id}.json`;
      try {
        const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(storagePath)}`);
        if (res.ok) {
          const fullReport = await hydrateReportBodyWithUserState(
            await res.json(),
            report as unknown as Record<string, unknown>,
            user.uid,
          );
          const idx = updatedReports.findIndex(r => r.id === report.id);
          if (idx >= 0 && fullReport) {
            updatedReports[idx] = fullReport;
            repaired++;
          } else {
            failed++;
          }
        } else { failed++; }
      } catch { failed++; }
    }

    if (repaired > 0) {
      set((state) => ({ siteRag: { ...state.siteRag, reports: updatedReports } }));
    }

    return { repaired, failed };
  },

  toggleComparisonSite: (id) => set((state) => {
    const current = state.siteRag.comparisonIds || [];
    const next = current.includes(id)
      ? current.filter(i => i !== id)
      : [...current, id].slice(-4); // Max 4
    return { siteRag: { ...state.siteRag, comparisonIds: next } };
  }),

  clearComparisonSites: () => set((state) => ({
    siteRag: { ...state.siteRag, comparisonIds: [] }
  })),

  toggleDemoMode: () => set((state) => {
    const isDemoMode = !state.siteRag.isDemoMode;
    if (isDemoMode) {
      // Preserve real reports by prepending demo sites
      const realReports = state.siteRag.reports.filter(r => !r.id.startsWith('demo-'));
      return {
        siteRag: {
          ...state.siteRag,
          isDemoMode,
          reports: [...DEMO_SITES, ...realReports],
          activeReportId: DEMO_SITES[0].id,
          comparisonIds: DEMO_SITES.map(s => s.id),
        },
      };
    } else {
      // Remove demo sites, restore real reports
      const realReports = state.siteRag.reports.filter(r => !r.id.startsWith('demo-'));
      return {
        siteRag: {
          ...state.siteRag,
          isDemoMode,
          reports: realReports,
          activeReportId: realReports.length > 0 ? realReports[0].id : null,
          comparisonIds: [],
        },
      };
    }
  }),

  // ═══ Report Templates ═══
  reportTemplates: (() => {
    try {
      const saved = localStorage.getItem('deepatomic_report_templates');
      if (saved) {
        const parsed = JSON.parse(saved) as Array<Partial<ReportTemplate> & { id: string }>;
        // Backfill scope on legacy entries (pre-Firestore migration) so the in-memory
        // shape always matches the current type. Migration to Firestore happens during hydrate.
        return parsed.map(t => normalizeTemplateHeadingOverrides({ ...t, scope: t.scope ?? 'user' } as ReportTemplate));
      }
    } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
    return [];
  })(),
  activeTemplateId: null,

  addReportTemplate: (template) => {
    // Default new templates to user scope unless explicitly marked company.
    const t: ReportTemplate = normalizeTemplateHeadingOverrides({ ...template, scope: template.scope ?? 'user' });
    set((state) => {
      const templates = [...state.reportTemplates, t];
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTemplates: templates };
    });
    // Write-through to Firestore (best-effort)
    (async () => {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (!user) return;
        const ownerId = t.scope === 'company' ? (get().companyId ?? '') : user.uid;
        if (!ownerId) return;
        const svc = await import('../services/reportTemplateService');
        await svc.saveReportTemplate(ownerId, t, t.scope);
      } catch (e) {
        // CDS-3 — the global vite:preloadError handler (App.tsx) is
        // already reloading the tab; a generic console.warn here just
        // adds noise and mislabels a stale-chunk failure as a Firestore
        // write failure.
        if (isStaleChunkError(e)) return;
        console.warn('addReportTemplate Firestore write failed:', e);
        // A real (non-stale-chunk) Firestore write failure was previously
        // silent — the template lived only in localStorage and the user got
        // no signal. Surface a toast so they know to retry the sync.
        void import('sonner').then(({ toast }) =>
          toast.error('Template save failed — kept locally, retry'),
        );
      }
    })();
  },
  updateReportTemplate: (id, updates) => {
    // STAR-DEFAULT-PERSIST FIX (#7) — `setDefaultTemplate` is the SINGLE source
    // of truth for `isDefault`. A general template Save must never author this
    // flag: the Studio's draft can carry a STALE `isDefault` (it's seeded from
    // a snapshot taken before the user starred the open template via the kebab),
    // and spreading that stale value here would clobber the just-persisted
    // default back to false. Strip it from `updates` so an edit-then-Save never
    // regresses the default; every caller is protected, not just the Studio.
    const { isDefault: _ignoredIsDefault, ...safeUpdates } = updates;
    let updated: ReportTemplate | null = null;
    set((state) => {
      const templates = state.reportTemplates.map(t => {
        if (t.id !== id) return t;
        // TSE-2 C12 — bump `version` on every persist. Legacy
        // templates (no version field) treat undefined as 1; the next
        // save lands them on 2 so the diff is always at least 1-step
        // ahead. The Apply-to-existing-reports flow uses this to
        // detect drift between a report's stamped
        // templateVersionUsed and the live template.
        // If the caller supplied a version (e.g. testing fixtures),
        // honour it; otherwise auto-bump.
        const baseVersion = t.version ?? 1;
        const nextVersion = typeof safeUpdates.version === 'number' ? safeUpdates.version : baseVersion + 1;
        const next = normalizeTemplateHeadingOverrides({
          ...t,
          ...safeUpdates,
          // Preserve the live default flag — never let a Save touch it.
          isDefault: t.isDefault,
          version: nextVersion,
          updatedAt: new Date().toISOString(),
        } as ReportTemplate);
        updated = next;
        return next;
      });
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      // TPN-1 — clear the Studio's transient draft channel on Save so
      // the next render falls through to the freshly-persisted template
      // with no visual flicker (mirrors commitReportTheme's
      // activeDraftTheme: null clear).
      return { reportTemplates: templates, activeDraftTemplate: null };
    });
    if (!updated) {
      // A COMPANY-scoped id that isn't in the store must NOT be synthesized into
      // an insert: addReportTemplate routes company writes to the CALLER's own
      // company (get().companyId), not the descendant company the template may
      // belong to — so the insert would land in the wrong collection silently.
      // Bail (the template likely lives in a descendant company list that this
      // store deliberately doesn't fold in).
      if ((updates as Partial<ReportTemplate>).scope === 'company') {
        console.warn(
          'updateReportTemplate: company-scoped id not in store; refusing to synthesize an insert (would mis-route the company write).',
          id,
        );
        return;
      }
      // Defense-in-depth — the id wasn't in reportTemplates, so the .map
      // above left it untouched and `updated` is null. Previously this was a
      // silent early-return no-op (the update was dropped entirely). Treat a
      // missing USER-scoped id as an INSERT instead: build the template from
      // `updates` and route it through addReportTemplate so it lands in local
      // state and write-through to Firestore (with the same toast-on-failure).
      const inserted = normalizeTemplateHeadingOverrides({
        ...(updates as Partial<ReportTemplate>),
        id,
        version: typeof updates.version === 'number' ? updates.version : 1,
        updatedAt: new Date().toISOString(),
      } as ReportTemplate);
      get().addReportTemplate(inserted);
      return;
    }
    const target = updated as ReportTemplate;
    (async () => {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (!user) return;
        const ownerId = target.scope === 'company' ? (get().companyId ?? '') : user.uid;
        if (!ownerId) return;
        const svc = await import('../services/reportTemplateService');
        await svc.saveReportTemplate(ownerId, target, target.scope);
      } catch (e) {
        if (isStaleChunkError(e)) return;  // CDS-3 — global handler reloads
        console.warn('updateReportTemplate Firestore write failed:', e);
        // Match addReportTemplate / commitReportTheme — a real write failure
        // must be visible, not silently swallowed.
        void import('sonner').then(({ toast }) =>
          toast.error('Template save failed — kept locally, retry'),
        );
      }
    })();
  },
  deleteReportTemplate: (id) => {
    const target = get().reportTemplates.find(t => t.id === id);
    set((state) => {
      const templates = state.reportTemplates.filter(t => t.id !== id);
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTemplates: templates, activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId };
    });
    if (!target) return;
    (async () => {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (!user) return;
        const ownerId = target.scope === 'company' ? (get().companyId ?? '') : user.uid;
        if (!ownerId) return;
        const svc = await import('../services/reportTemplateService');
        await svc.deleteReportTemplateDoc(ownerId, id, target.scope);
      } catch (e) {
        if (isStaleChunkError(e)) return;  // CDS-3 — global handler reloads
        console.warn('deleteReportTemplate Firestore write failed:', e);
        // Surface the failure — the row already vanished locally, so without a
        // toast the user sees a "deleted but came back on reload" mystery.
        void import('sonner').then(({ toast }) =>
          toast.error('Template delete failed — it may reappear on reload'),
        );
      }
    })();
  },
  setDefaultTemplate: (id) => {
    const previous = get().reportTemplates;
    const target = previous.find(t => t.id === id);
    if (!target) return;

    // Setting a COMPANY template as the default writes the shared company doc —
    // an admin-only action. A non-admin would flip local state that the Firestore
    // rule then rejects, leaving local/server diverged until the next refetch.
    // Guard here too (the Studio UI also disables the star for company templates).
    if (target.scope === 'company' && get().companyRole !== 'admin') return;

    const updatedSet: ReportTemplate[] = previous.map(t => {
      if (t.id === id) return { ...t, isDefault: true };
      if (target.scope === 'company') {
        // When setting a company default, clear ALL defaults (both company and user)
        // so the user's personal override is removed and they inherit the new company default.
        return { ...t, isDefault: false };
      }
      // When setting a user default, clear only user defaults. Leave company defaults intact.
      if (target.scope === 'user' && t.scope === 'user') {
        return { ...t, isDefault: false };
      }
      return t;
    });

    set(() => {
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(updatedSet)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTemplates: updatedSet };
    });

    // Persist both user and company template changes
    const changed = updatedSet.filter((t, i) => t.isDefault !== previous[i].isDefault);
    if (changed.length === 0) return;

    (async () => {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (!user) return;
        const companyId = get().companyId;
        const svc = await import('../services/reportTemplateService');

        for (const t of changed) {
          if (t.scope === 'company' && companyId) {
            await svc.saveReportTemplate(companyId, t, 'company');
          } else if (t.scope === 'user') {
            await svc.saveReportTemplate(user.uid, t, 'user');
          }
        }
      } catch (e) {
        if (isStaleChunkError(e)) return;  // CDS-3 — global handler reloads
        console.warn('setDefaultTemplate Firestore write failed:', e);
        void import('sonner').then(({ toast }) =>
          toast.error('Default not saved — kept locally, retry'),
        );
      }
    })();
  },
  setActiveTemplate: (id) => set({ activeTemplateId: id }),

  saveTemplateAsCompany: async (template) => {
    const cid = get().companyId;
    if (!cid) throw new Error('No company context — cannot save company template');
    const svc = await import('../services/reportTemplateService');
    const companyTemplate: ReportTemplate = normalizeTemplateHeadingOverrides({
      ...template,
      id: `tmpl-co-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      scope: 'company',
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    await svc.saveReportTemplate(cid, companyTemplate, 'company');
    set((state) => {
      const templates = [...state.reportTemplates, companyTemplate];
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTemplates: templates };
    });
  },

  saveTemplateToCompany: async (template, targetCompanyId, companies) => {
    const ownCid = get().companyId;
    if (!ownCid) throw new Error('No company context — cannot save company template');
    const target = typeof targetCompanyId === 'string' ? targetCompanyId.trim() : '';
    if (!target) throw new Error('No target organisation selected');
    // Subtree-only client guard: the target must be the user's own org
    // or a descendant. Rejects parents/siblings before any write; the
    // Firestore rule re-checks the same invariant authoritatively.
    const { isInCompanyTree } = await import('../lib/companyHierarchy');
    if (!isInCompanyTree(companies, ownCid, target)) {
      throw new Error(`Target organisation ${target} is not within your organisation subtree`);
    }
    const svc = await import('../services/reportTemplateService');
    const companyTemplate: ReportTemplate = normalizeTemplateHeadingOverrides({
      ...template,
      id: `tmpl-co-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      scope: 'company',
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    await svc.saveReportTemplate(target, companyTemplate, 'company');
    // Only fold the new template into the in-memory list when it lands
    // in the user's OWN company collection — that's the only company
    // scope `fetchReportTemplates` reloads (descendant collections aren't
    // part of the studio's fetched union, so showing them would be a
    // ghost row that vanishes on reload).
    if (target === ownCid) {
      set((state) => {
        const templates = [...state.reportTemplates, companyTemplate];
        try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
        return { reportTemplates: templates };
      });
    }
  },

  forkCompanyTemplate: async (templateId) => {
    const source = get().reportTemplates.find(t => t.id === templateId && t.scope === 'company');
    if (!source) throw new Error('Company template not found: ' + templateId);
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const forked: ReportTemplate = normalizeTemplateHeadingOverrides({
      ...source,
      id: `tmpl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: `${source.name} (Personal Copy)`,
      scope: 'user',
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    const svc = await import('../services/reportTemplateService');
    await svc.saveReportTemplate(user.uid, forked, 'user');
    set((state) => {
      const templates = [...state.reportTemplates, forked];
      try { localStorage.setItem('deepatomic_report_templates', JSON.stringify(templates)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTemplates: templates, activeTemplateId: forked.id };
    });
  },

  // TSE-2 C12 — Apply-to-existing-reports.
  findReportsUsingTemplate: (templateId) => {
    const reports = get().siteRag.reports;
    return reports
      .filter(r => (r as { templateId?: string }).templateId === templateId)
      .map(toReportPickRow);
  },

  findReportsEligibleForTemplateLink: (templateId) => {
    const reports = get().siteRag.reports;
    return reports
      .filter(r => (r as { templateId?: string }).templateId !== templateId)
      .map(toReportPickRow);
  },

  applyTemplateToExistingReports: async (templateId, reportIds) => {
    const tmpl = get().reportTemplates.find(t => t.id === templateId);
    if (!tmpl) return { linked: 0, failed: 0, failedIds: [] };
    const targetVersion = tmpl.version ?? 1;
    const idSet = new Set(reportIds);
    set((state) => {
      const reports = state.siteRag.reports.map(r => {
        if (!idSet.has(r.id)) return r;
        const next = {
          ...r,
          templateId,
          templateVersionUsed: targetVersion,
        } as typeof r;
        // COVER FIX — preserve the gen-time cover layout as a durable
        // snapshot so an unresolved-template render falls back to it
        // instead of the Hero default (never lose the assigned cover).
        const prevCover = (r as { coverLayout?: unknown }).coverLayout;
        if ((next as { coverLayoutAtGen?: unknown }).coverLayoutAtGen == null && prevCover != null) {
          (next as { coverLayoutAtGen?: unknown }).coverLayoutAtGen = prevCover;
        }
        delete (next as { sectionVisibility?: unknown }).sectionVisibility;
        delete (next as { sectionColumnLayouts?: unknown }).sectionColumnLayouts;
        delete (next as { coverLayout?: unknown }).coverLayout;
        return next;
      });
      return { siteRag: { ...state.siteRag, reports } };
    });

    let linked = 0;
    const failedIds: string[] = [];
    for (const rid of reportIds) {
      try {
        // Time-box each step: prefetch's storage fetch and updateRagReport's
        // assertSiteWritable can STALL (never settle) — e.g. a metadata-only
        // report whose storage load hangs, or a writable-check on a
        // company-shared site — which left the Link dialog stuck on "Linking…"
        // forever (the surrounding try/catch only catches throws, not hangs).
        // A timeout converts a stall into a caught failure so the dialog always
        // completes; the report is reported as failed and the user can retry.
        await withTimeout(get().prefetchReport(rid), 15_000, `link:prefetch:${rid}`);
        const row = get().siteRag.reports.find(r => r.id === rid);
        if (!row || !isReportHydratedForGcsSave(row)) {
          console.warn(`applyTemplateToExistingReports: skip persist for ${rid} — not hydrated`);
          failedIds.push(rid);
          continue;
        }
        await withTimeout(get().updateRagReport(rid, {
          templateId,
          templateVersionUsed: targetVersion,
        }), 25_000, `link:update:${rid}`);
        linked += 1;
      } catch (e) {
        console.warn(`applyTemplateToExistingReports: write failed for ${rid}:`, e);
        failedIds.push(rid);
      }
    }
    return { linked, failed: failedIds.length, failedIds };
  },

  // ═══ Report Theme ═══
  // setReportTheme / setReportLogo are PREVIEW-ONLY — they update in-memory
  // state and the localStorage cache but do NOT persist to Firestore. Use
  // commitReportTheme to actually save. The theme editor uses a local draft
  // state for editing and only calls commitReportTheme on Save click.
  setReportTheme: (updates) => {
    set((state) => {
      const next = { ...state.reportTheme, ...updates };
      try { localStorage.setItem('deepatomic_report_theme', JSON.stringify(next)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTheme: next };
    });
  },
  setReportLogo: (dataUrl) => {
    set((state) => {
      const next = { ...state.reportTheme, logoUrl: dataUrl };
      try { localStorage.setItem('deepatomic_report_theme', JSON.stringify(next)); } catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
      return { reportTheme: next };
    });
  },
  commitReportTheme: async (theme) => {
    // TSC-3 — clear the transient draft channel atomically with the
    // persist so the canonical viewer's `activeDraftTheme ?? reportTheme`
    // resolver rehydrates from the new `reportTheme` (same value, no
    // visual flicker). R2 in the pack risk register.
    set({ reportTheme: theme, activeDraftTheme: null });
    try { localStorage.setItem('deepatomic_report_theme', JSON.stringify(theme)); }
    catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      const { toast } = await import('sonner');
      toast.warning('Theme saved locally — sign in to sync');
      return;
    }
    const svc = await import('../services/reportThemeService');
    try {
      await svc.saveReportTheme(user.uid, theme, 'user');
      set({ lastReportThemeSavedAt: Date.now() });
      const { toast } = await import('sonner');
      toast.success('Theme saved');
    } catch (e) {
      console.warn('commitReportTheme: Firestore write failed', e);
      const { toast } = await import('sonner');
      toast.error('Theme save failed — changes kept locally');
      throw e;
    }
  },
  saveThemeAsCompanyDefault: async (theme) => {
    const cid = get().companyId;
    if (!cid) throw new Error('No company context — cannot save company default theme');
    const svc = await import('../services/reportThemeService');
    await svc.saveReportTheme(cid, theme ?? get().reportTheme, 'company');
  },

  hydrateUserSettings: async () => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    const companyId = get().companyId;
    const themeSvc = await import('../services/reportThemeService');
    const tmplSvc = await import('../services/reportTemplateService');

    let theme: import('../types/reportTheme').ReportTheme;
    let templates: ReportTemplate[];
    try {
      [theme, templates] = await Promise.all([
        themeSvc.fetchReportTheme(user.uid, companyId),
        tmplSvc.fetchReportTemplates(user.uid, companyId),
      ]);
    } catch (e) {
      // Offline / Firestore unreachable — fall back to localStorage cache.
      console.warn('hydrateUserSettings: Firestore failed, using localStorage cache', e);
      theme = themeSvc.readCachedReportTheme() ?? { ...DEFAULT_THEME };
      templates = tmplSvc.readCachedReportTemplates() ?? [];
    }

    const normalizedTemplates = templates.map(normalizeTemplateHeadingOverrides);
    const currentActiveTemplateId = get().activeTemplateId;
    const nextActiveTemplate = resolvePreferredTemplate(normalizedTemplates, {
      activeTemplateId: currentActiveTemplateId,
    });
    set({
      reportTheme: theme,
      reportTemplates: normalizedTemplates,
      activeTemplateId: nextActiveTemplate?.id ?? null,
      _settingsHydrated: true,
    });
    themeSvc.writeCachedReportTheme(theme);
    tmplSvc.writeCachedReportTemplates(normalizedTemplates);

    // One-time migration of legacy localStorage data (no-ops if already migrated
    // or if user already has Firestore data).
    const { migrateLocalSettingsIfNeeded } = await import('../services/settingsMigration');
    await migrateLocalSettingsIfNeeded(user.uid);

    // One-time migration of legacy data: URL logos to Firebase Storage. Idempotent.
    try {
      const outcome = await themeSvc.migrateDataUrlLogoIfNeeded(user.uid, theme.logoUrl);
      if (outcome.migrated) {
        const updatedTheme = { ...theme, logoUrl: outcome.newUrl };
        set({ reportTheme: updatedTheme });
        themeSvc.writeCachedReportTheme(updatedTheme);
        // Persist the new (or cleared) URL back to Firestore so other devices pick it up.
        try { await themeSvc.saveReportTheme(user.uid, updatedTheme, 'user'); }
        catch (e) { console.warn('Failed to persist migrated logo URL:', e); }
      }
    } catch (e) {
      console.warn('Logo migration step failed:', e);
    }
  },

  // ═══ SED Amendment System ═══
  amendmentState: {
    activeSedId: null,
    amendments: [],
    revisions: [],
    signOffs: [],
    isLoadingAmendments: false,
    isRegenerating: false,
    amendmentError: null,
  },

  fetchSEDAmendments: async (sedId) => {
    set((state) => ({ amendmentState: { ...state.amendmentState, isLoadingAmendments: true, activeSedId: sedId } }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        set((state) => ({ amendmentState: { ...state.amendmentState, isLoadingAmendments: false } }));
        return;
      }
      const { validateAmendmentList, validateRevisionList } = await import('../types/sedAmendmentsValidator');
      const owningSite = get().siteRag.sites.find(
        (s) =>
          s.sedId === sedId ||
          s.lastKnownAmendmentsSourceSedId === sedId ||
          (Array.isArray(s.sedAmendmentHistory) && s.sedAmendmentHistory.some((a) => a?.sedId === sedId)),
      );
      let amendments: import('../types/sedAmendments').SEDAmendment[] = [];
      let historyVersion: number | undefined;
      if (owningSite) {
        const ownerUid = siteOwnerUid(owningSite, user.uid);
        const { authedFetch } = await import('../lib/authedFetch');
        const url = `/api/sites/${encodeURIComponent(owningSite.id)}/sed-amendments?ownerUid=${encodeURIComponent(ownerUid)}&sedId=${encodeURIComponent(sedId)}`;
        const res = await authedFetch(url);
        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          throw new Error(`Failed to fetch SED amendments (HTTP ${res.status})${errText ? `: ${errText.slice(0, 160)}` : ''}`);
        }
        const body = (await res.json()) as Record<string, unknown>;
        if (
          !body ||
          body.ok !== true ||
          body.ownerUid !== ownerUid ||
          body.siteId !== owningSite.id ||
          body.sedId !== sedId ||
          !Array.isArray(body.amendments) ||
          typeof body.historyVersion !== 'number'
        ) {
          throw new Error('Malformed SED amendments response from server.');
        }
        amendments = validateAmendmentList(body.amendments, sedId);
        historyVersion = body.historyVersion;
      } else {
        const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
        const amendSnap = await getDocs(query(collection(db, `users/${user.uid}/evidence/${sedId}/amendments`), orderBy('createdAt', 'desc')));
        const rawAmendments = amendSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        amendments = validateAmendmentList(rawAmendments, sedId);
      }

      let revisions: import('../types/sedAmendments').SEDRevision[] = [];
      try {
        const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
        const revOwnerUid = owningSite ? siteOwnerUid(owningSite, user.uid) : user.uid;
        const revSnap = await getDocs(query(collection(db, `users/${revOwnerUid}/evidence/${sedId}/revisions`), orderBy('version', 'desc')));
        const rawRevisions = revSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        revisions = validateRevisionList(rawRevisions, sedId);
      } catch {
        revisions = [];
      }

      set((state) => ({
        amendmentState: {
          ...state.amendmentState,
          amendments,
          revisions,
          isLoadingAmendments: false,
          amendmentError: null,
        },
        ...(owningSite
          ? {
              siteRag: {
                ...state.siteRag,
                sites: state.siteRag.sites.map((st) =>
                  st.id === owningSite.id
                    ? {
                        ...st,
                        sedAmendmentHistory: amendments,
                        ...(typeof historyVersion === 'number'
                          ? { sedAmendmentHistoryVersion: historyVersion }
                          : {}),
                      }
                    : st,
                ),
              },
            }
          : {}),
      }));
    } catch (e) {
      set((state) => ({ amendmentState: { ...state.amendmentState, isLoadingAmendments: false, amendmentError: (e as Error).message } }));
    }
  },

  refreshSiteBaseline: async (siteId) => {
    const existingSite = get().siteRag.sites.find((st) => st.id === siteId);
    if (!siteId || !siteId.trim()) {
      throw new Error('Cannot refresh site baseline: siteId is required.');
    }

    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Cannot refresh site baseline: user is not authenticated.');
    }
    const initiatingRefreshUid = user.uid;
    const docOwnerUid = existingSite
      ? siteOwnerUid(existingSite, initiatingRefreshUid)
      : initiatingRefreshUid;

    const getRefreshBaselineSignature = (
      candidate: import('../types/site').SiteEntity | null | undefined,
    ): string => {
      if (!candidate) return 'none';
      return JSON.stringify([
        candidate.sedId ?? null,
        typeof candidate.sedVersion === 'number' ? candidate.sedVersion : null,
        typeof candidate.sedStoragePath === 'string' && candidate.sedStoragePath.trim()
          ? candidate.sedStoragePath.trim()
          : null,
        typeof candidate.sedStorageGeneration === 'string' &&
        candidate.sedStorageGeneration.trim()
          ? candidate.sedStorageGeneration.trim()
          : null,
        typeof candidate.sedAmendmentHistoryVersion === 'number'
          ? candidate.sedAmendmentHistoryVersion
          : null,
        typeof candidate.auditOverridesVersion === 'number'
          ? candidate.auditOverridesVersion
          : null,
        candidate.sedStatus ?? null,
        candidate.sedBootstrapLockStatus ?? null,
      ]);
    };

    const initialBaselineSig = getRefreshBaselineSignature(existingSite);

    const assertRefreshBaselineUnchanged = () => {
      if (!auth.currentUser || auth.currentUser.uid !== initiatingRefreshUid) {
        throw new Error(
          'The signed-in account or site owner changed during site baseline refresh.',
        );
      }
      const liveSite = get().siteRag.sites.find((st) => st.id === siteId);
      if (liveSite && siteOwnerUid(liveSite, initiatingRefreshUid) !== docOwnerUid) {
        throw new Error(
          'The signed-in account or site owner changed during site baseline refresh.',
        );
      }
      if (
        liveSite?.sedStatus === 'bootstrapping' ||
        liveSite?.sedBootstrapLockStatus === 'locked' ||
        getRefreshBaselineSignature(liveSite) !== initialBaselineSig
      ) {
        const err = new Error(
          'Local site baseline changed during refresh; retry site baseline refresh.',
        ) as Error & { code?: string; retryRequired?: boolean };
        err.code = 'SITE_BASELINE_CHANGED_DURING_REFRESH';
        err.retryRequired = true;
        throw err;
      }
      return liveSite ?? null;
    };

    const firestoreModule = await import('firebase/firestore');
    const getServerDoc =
      typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer === 'function'
        ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc }).getDocFromServer
        : null;
    if (!getServerDoc) {
      throw new Error('Authoritative server-only Firestore read (getDocFromServer) is unavailable.');
    }
    const { computeAuditOverridesVersion } = await import('../lib/audit/truthAttestations');
    const snap = await getServerDoc(
      firestoreModule.doc(db, `users/${docOwnerUid}/sites`, siteId),
    );
    assertRefreshBaselineUnchanged();

    if (!snap || typeof snap.exists !== 'function' || !snap.exists()) {
      throw new Error(`Authoritative site record "${siteId}" could not be loaded from Firestore.`);
    }
    if (
      'metadata' in snap &&
      snap.metadata &&
      typeof (snap.metadata as { fromCache?: unknown }).fromCache === 'boolean' &&
      (snap.metadata as { fromCache: boolean }).fromCache === true
    ) {
      throw new Error(`Authoritative site record "${siteId}" resolved from local cache instead of server.`);
    }

    const rawFresh = snap.data() as Partial<import('../types/site').SiteEntity> & Record<string, unknown>;
    const authoritativeOverrides = Array.isArray(rawFresh.auditOverrides)
      ? [...rawFresh.auditOverrides]
      : [];
    const authoritativeVersion = computeAuditOverridesVersion({
      auditOverrides: authoritativeOverrides,
      auditOverridesVersion: rawFresh.auditOverridesVersion,
    });
    const authoritativeHistory = Array.isArray(rawFresh.sedAmendmentHistory)
      ? [...rawFresh.sedAmendmentHistory]
      : [];
    const freshSedId =
      typeof rawFresh.sedId === 'string' && rawFresh.sedId.trim()
        ? rawFresh.sedId.trim()
        : undefined;
    const baseWithoutStaleSedFields: Record<string, unknown> = { ...(existingSite ?? {}) };
    if (!freshSedId) {
      delete baseWithoutStaleSedFields.sedId;
      delete baseWithoutStaleSedFields.sedVersion;
      delete baseWithoutStaleSedFields.sedStoragePath;
      delete baseWithoutStaleSedFields.sedStorageGeneration;
      delete baseWithoutStaleSedFields.sedContentHash;
      delete baseWithoutStaleSedFields.sedContentHashVersion;
      delete baseWithoutStaleSedFields.sedSectionContentHashes;
      delete baseWithoutStaleSedFields.sedSectionContentHashesV3;
      delete baseWithoutStaleSedFields.sedCollectedAt;
      delete baseWithoutStaleSedFields.sedComputedAt;
      for (const k of Object.keys(baseWithoutStaleSedFields)) {
        if (k.startsWith('sedSectionHash_')) {
          delete baseWithoutStaleSedFields[k];
        }
      }
    }

    const refreshedSite = normalizeSiteEntity({
      ...baseWithoutStaleSedFields,
      ...rawFresh,
      id: siteId,
      sedId: freshSedId,
      sedVersion:
        freshSedId && typeof rawFresh.sedVersion === 'number' ? rawFresh.sedVersion : undefined,
      sedStoragePath:
        freshSedId &&
        typeof rawFresh.sedStoragePath === 'string' &&
        rawFresh.sedStoragePath.trim()
          ? rawFresh.sedStoragePath.trim()
          : undefined,
      sedStorageGeneration:
        freshSedId &&
        typeof rawFresh.sedStorageGeneration === 'string' &&
        rawFresh.sedStorageGeneration.trim()
          ? rawFresh.sedStorageGeneration.trim()
          : undefined,
      auditOverrides: authoritativeOverrides,
      auditOverridesVersion: authoritativeVersion,
      sedAmendmentHistory: authoritativeHistory,
      ...(existingSite?._sourceScope ? { _sourceScope: existingSite._sourceScope } : {}),
      ...(existingSite?._ownerUid ? { _ownerUid: existingSite._ownerUid } : {}),
    } as import('../types/site').SiteEntity);

    let loadedSed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
    const sedLoaderMod = await import('../services/sedLoader');
    const { loadSedById } = sedLoaderMod;
    const invalidateCacheFn =
      'invalidateCachedSed' in sedLoaderMod &&
      typeof sedLoaderMod.invalidateCachedSed === 'function'
        ? sedLoaderMod.invalidateCachedSed
        : null;

    if (refreshedSite.sedId) {
      assertRefreshBaselineUnchanged();
      if (invalidateCacheFn) {
        invalidateCacheFn(refreshedSite.sedId);
        if (existingSite?.sedId && existingSite.sedId !== refreshedSite.sedId) {
          invalidateCacheFn(existingSite.sedId);
        }
      }
      loadedSed = await loadSedById(
        refreshedSite.sedId,
        refreshedSite.sedStoragePath,
        refreshedSite.sedVersion,
      );
      try {
        assertRefreshBaselineUnchanged();
      } catch (err) {
        if (invalidateCacheFn) {
          invalidateCacheFn(refreshedSite.sedId);
        }
        throw err;
      }
      if (
        !loadedSed ||
        loadedSed.id !== refreshedSite.sedId ||
        (typeof refreshedSite.sedVersion === 'number' &&
          loadedSed.version !== refreshedSite.sedVersion)
      ) {
        throw new Error(
          `Authoritative SED "${refreshedSite.sedId}" could not be loaded or did not match expected version.`,
        );
      }
    } else if (existingSite?.sedId && invalidateCacheFn) {
      assertRefreshBaselineUnchanged();
      invalidateCacheFn(existingSite.sedId);
    }

    assertRefreshBaselineUnchanged();
    const clearedSedId = !refreshedSite.sedId ? existingSite?.sedId : undefined;

    set((curr) => {
      if (!auth.currentUser || auth.currentUser.uid !== initiatingRefreshUid) {
        return curr;
      }
      const liveSite = curr.siteRag.sites.find((st) => st.id === siteId);
      if (liveSite && siteOwnerUid(liveSite, initiatingRefreshUid) !== docOwnerUid) {
        return curr;
      }
      if (
        liveSite?.sedStatus === 'bootstrapping' ||
        liveSite?.sedBootstrapLockStatus === 'locked' ||
        getRefreshBaselineSignature(liveSite) !== initialBaselineSig
      ) {
        return curr;
      }
      return {
        siteRag: {
          ...curr.siteRag,
          sites: curr.siteRag.sites.some((st) => st.id === siteId)
            ? curr.siteRag.sites.map((st) => (st.id === siteId ? refreshedSite : st))
            : [...curr.siteRag.sites, refreshedSite],
        },
        ...(refreshedSite.sedId
          ? {
              amendmentState: {
                ...curr.amendmentState,
                amendments: [
                  ...authoritativeHistory,
                  ...curr.amendmentState.amendments.filter(
                    (a) =>
                      a.sedId !== refreshedSite.sedId &&
                      (!existingSite?.sedId || a.sedId !== existingSite.sedId),
                  ),
                ],
              },
            }
          : clearedSedId
            ? {
                amendmentState: {
                  ...curr.amendmentState,
                  amendments: [
                    ...authoritativeHistory,
                    ...curr.amendmentState.amendments.filter((a) => a.sedId !== clearedSedId),
                  ],
                },
              }
            : {}),
      };
    });

    return { site: refreshedSite, sed: loadedSed };
  },

  saveSEDAmendment: async (amendment, options) => {
    const initialOwningSite = get().siteRag.sites.find(s => s.sedId === amendment.sedId);
    if (initialOwningSite) {
      await get().assertSiteWritable(initialOwningSite.id);
    }
    const { matchesSedAmendmentOperationPayload } = await import('../lib/audit/truthAttestations');
    const explicitOperationId = (options?.operationId ?? amendment.operationId)?.trim() || undefined;
    const expectedSedVersion =
      typeof options?.expectedSedVersion === 'number'
        ? options.expectedSedVersion
        : typeof initialOwningSite?.sedVersion === 'number'
          ? initialOwningSite.sedVersion
          : amendment.sedVersion;

    const findMatchingOperationInList = (
      list: readonly import('../types/sedAmendments').SEDAmendment[] | undefined,
    ):
      | { kind: 'applied'; entry: import('../types/sedAmendments').SEDAmendment }
      | { kind: 'pending'; entry: import('../types/sedAmendments').SEDAmendment }
      | { kind: 'mismatch'; entry: import('../types/sedAmendments').SEDAmendment }
      | null => {
      if (!explicitOperationId || !Array.isArray(list)) return null;
      const found = list.find((a) => a.operationId === explicitOperationId);
      if (!found) return null;
      if (!matchesSedAmendmentOperationPayload(found, amendment)) {
        return { kind: 'mismatch', entry: found };
      }
      if (found.status === 'applied' || found.status === 'superseded') {
        return { kind: 'applied', entry: found };
      }
      return { kind: 'pending', entry: found };
    };

    const mismatchErrorResult = (opId?: string) => ({
      ok: false as const,
      error: `Operation ID "${opId ?? explicitOperationId ?? ''}" was already committed with a different amendment payload; start a new save operation.`,
      code: 'SED_OPERATION_ID_MISMATCH' as const,
      conflict: true,
    });

    if (explicitOperationId) {
      const localCheck =
        findMatchingOperationInList(initialOwningSite?.sedAmendmentHistory) ??
        findMatchingOperationInList(get().amendmentState.amendments);
      if (localCheck?.kind === 'mismatch') return mismatchErrorResult(explicitOperationId);
      if (localCheck?.kind === 'pending' && get().companyRequireReviewWorkflow) {
        return {
          ok: true,
          applied: false,
          idempotent: true,
          sedVersion: localCheck.entry.sedVersion,
          amendment: localCheck.entry,
        };
      }
    }

    if (isLocalMockMode()) {
      set((state) => ({
        amendmentState: {
          ...state.amendmentState,
          amendments: [amendment, ...state.amendmentState.amendments],
        },
      }));
      return { ok: true, applied: false, amendment };
    }

    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        return { ok: false, error: 'Sign in again to record an amendment.' };
      }
      const owningSite = get().siteRag.sites.find(s => s.sedId === amendment.sedId);
      if (!owningSite) {
        return {
          ok: false,
          error: `No site owns SED ${amendment.sedId}.`,
        };
      }

      const rawUser = user as unknown as Record<string, unknown>;
      const { isInCompanyTree } = await import('../lib/companyHierarchy');
      let isPlatformAdmin = rawUser.role === 'admin';
      if (!isPlatformAdmin) {
        try {
          const { requireAdminClaim } = await import('../lib/requireAdminClaim');
          isPlatformAdmin = Boolean(await requireAdminClaim());
        } catch {
          isPlatformAdmin = false;
        }
      }
      if (
        owningSite._ownerUid &&
        owningSite._ownerUid !== user.uid &&
        !isPlatformAdmin
      ) {
        const callerCid = get().companyId;
        const canWriteShared =
          typeof owningSite.companyId === 'string' &&
          owningSite.companyId.trim().length > 0 &&
          (callerCid
            ? isInCompanyTree(get().subtreeCompanies ?? [], callerCid, owningSite.companyId)
            : true);
        if (!canWriteShared) {
          return {
            ok: false,
            error: 'Insufficient permissions to modify foreign-owned site evidence.',
            code: 'FORBIDDEN_SITE_WRITE',
          };
        }
      }

      const ownerUid = siteOwnerUid(owningSite, user.uid);
      const { getCachedSedStorageGeneration, primeCommittedSedFromServer } = await import('../services/sedLoader');
      const {
        computeSedAmendmentDraftSignature,
        readPendingSedSaveEnvelope,
        writePendingSedSaveEnvelope,
        clearPendingSedSaveEnvelope,
      } = await import('../services/sedAmendments');
      const { authedFetch } = await import('../lib/authedFetch');

      const draftSignature = computeSedAmendmentDraftSignature({
        variablePath: amendment.variablePath,
        newValue: amendment.newValue,
        reason: amendment.reason,
        sourceType: amendment.source?.type ?? '',
        sourceLabel: amendment.source?.label ?? '',
        sourceUrl: amendment.source?.url,
        issueType: amendment.issueType,
      });
      const envelopeScope = {
        actorUid: user.uid,
        ownerUid,
        siteId: owningSite.id,
        sedId: amendment.sedId,
        variablePath: amendment.variablePath,
      };
      const existingByOperationId = explicitOperationId
        ? readPendingSedSaveEnvelope(envelopeScope, { operationId: explicitOperationId })
        : null;

      if (
        existingByOperationId &&
        (existingByOperationId.draftSignature !== draftSignature ||
          !matchesSedAmendmentOperationPayload(existingByOperationId.amendment, amendment))
      ) {
        return mismatchErrorResult(explicitOperationId);
      }

      const existingByDraft = !explicitOperationId
        ? readPendingSedSaveEnvelope(envelopeScope, { draftSignature })
        : null;

      let activeEnvelope =
        existingByOperationId ??
        (existingByDraft && existingByDraft.amendmentId === amendment.id
          ? existingByDraft
          : null);
      if (!activeEnvelope) {
        const expectedHistoryVersion =
          typeof owningSite.sedAmendmentHistoryVersion === 'number'
            ? owningSite.sedAmendmentHistoryVersion
            : undefined;
        const expectedSedStorageGeneration =
          getCachedSedStorageGeneration(amendment.sedId, owningSite.sedStoragePath) ??
          (typeof owningSite.sedStorageGeneration === 'string' && owningSite.sedStorageGeneration.trim()
            ? owningSite.sedStorageGeneration.trim()
            : undefined);
        const mintedOperationId =
          explicitOperationId ??
          `sed-amend-save-${owningSite.id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
            /[^A-Za-z0-9._:-]/g,
            '-',
          );
        const requireReviewWorkflowFlag =
          get().companyRequireReviewWorkflow === true ? true : undefined;
        const amendmentWithOp: import('../types/sedAmendments').SEDAmendment = {
          ...amendment,
          operationId: mintedOperationId,
        };
        const requestBody = {
          action: 'save' as const,
          operationId: mintedOperationId,
          ownerUid,
          sedId: amendment.sedId,
          ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
          ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
          ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          ...(requireReviewWorkflowFlag ? { requireReviewWorkflow: true } : {}),
          amendment: amendmentWithOp,
        };
        activeEnvelope = {
          actorUid: user.uid,
          ownerUid,
          siteId: owningSite.id,
          sedId: amendment.sedId,
          variablePath: amendment.variablePath,
          draftSignature,
          operationId: mintedOperationId,
          amendmentId: amendment.id,
          ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
          ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
          ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          ...(requireReviewWorkflowFlag ? { requireReviewWorkflow: true } : {}),
          amendment: amendmentWithOp,
          requestBody,
          createdAt: amendment.createdAt || new Date().toISOString(),
        };
        writePendingSedSaveEnvelope(activeEnvelope);
      }

      const operationId = activeEnvelope.operationId;
      const clearPendingOpIfTerminal = (status?: number) => {
        if (
          status === undefined ||
          (status >= 400 && status < 500 && status !== 408 && status !== 429)
        ) {
          clearPendingSedSaveEnvelope(envelopeScope, operationId);
        }
      };

      const res = await authedFetch(
        `/api/sites/${encodeURIComponent(owningSite.id)}/sed-amendments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(activeEnvelope.requestBody),
        },
      );

      let body: Record<string, unknown> | null = null;
      try {
        const rawText = typeof res.text === 'function' ? await res.text() : '';
        body = rawText
          ? (JSON.parse(rawText) as Record<string, unknown>)
          : typeof res.json === 'function'
            ? ((await res.json()) as Record<string, unknown>)
            : null;
      } catch {
        body = null;
      }

      if (!res.ok) {
        clearPendingOpIfTerminal(res.status);
        const errCode = typeof body?.code === 'string' ? body.code : undefined;
        const errMsg =
          typeof body?.error === 'string' && body.error.trim()
            ? body.error.trim()
            : `Failed to save amendment (HTTP ${res.status})`;
        if (errCode === 'OPERATION_ID_CONFLICT') {
          return mismatchErrorResult(operationId);
        }
        if (res.status === 409 || res.status === 412) {
          return {
            ok: false,
            error: errMsg,
            code: (errCode ?? 'SED_VERSION_CONFLICT') as 'SED_VERSION_CONFLICT',
            conflict: true,
          };
        }
        return {
          ok: false,
          error: errMsg,
          ...(errCode ? { code: errCode as 'FORBIDDEN_SITE_WRITE' } : {}),
        };
      }

      if (
        !body ||
        body.ok !== true ||
        body.action !== 'save' ||
        body.operationId !== operationId ||
        body.ownerUid !== ownerUid ||
        body.siteId !== owningSite.id ||
        body.sedId !== amendment.sedId ||
        typeof body.applied !== 'boolean' ||
        !body.amendment ||
        typeof body.amendment !== 'object' ||
        !Array.isArray(body.amendments) ||
        typeof body.historyVersion !== 'number' ||
        typeof body.sedVersion !== 'number' ||
        typeof body.sedStoragePath !== 'string' ||
        !body.sedStoragePath.trim() ||
        typeof body.sedStorageGeneration !== 'string' ||
        !body.sedStorageGeneration.trim()
      ) {
        return {
          ok: false,
          error: 'Malformed or mismatched SED amendment response from server.',
        };
      }

      const committedStoragePath = body.sedStoragePath.trim();
      const committedGeneration = body.sedStorageGeneration.trim();
      const committedVersion = body.sedVersion;
      const committedHistoryVersion = body.historyVersion;
      const committedAmendment = body.amendment as import('../types/sedAmendments').SEDAmendment;
      const effectiveAmendments = body.amendments as import('../types/sedAmendments').SEDAmendment[];
      const isIdempotentReplay = body.idempotentReplay === true;
      const returnedSed =
        body.sed && typeof body.sed === 'object'
          ? (body.sed as import('../types/siteEvidence').SiteEvidenceDocument)
          : null;

      if (body.applied) {
        if (
          !returnedSed ||
          returnedSed.id !== amendment.sedId ||
          returnedSed.version !== committedVersion ||
          typeof body.sedContentHash !== 'string' ||
          !body.sedContentHash.trim()
        ) {
          return {
            ok: false,
            error: 'Malformed applied SED amendment response: missing authoritative SED payload or content hash.',
          };
        }
      }

      clearPendingOpIfTerminal();

      const sectionHashes: Record<string, string> = {};
      for (const [k, v] of Object.entries(body)) {
        if (k.startsWith('sedSectionHash_') && typeof v === 'string') {
          sectionHashes[k] = v;
        }
      }

      const initiatingSaveUid = user.uid;
      const inspectSaveLocalState = () => {
        const actorMatches = auth.currentUser?.uid === initiatingSaveUid;
        const localSite = get().siteRag.sites.find((s) => s.id === owningSite.id) ?? null;
        const ownerMatches = Boolean(
          localSite && siteOwnerUid(localSite, initiatingSaveUid) === ownerUid,
        );
        const sameSed = Boolean(
          actorMatches && ownerMatches && localSite && localSite.sedId === amendment.sedId,
        );
        const ptrVer =
          sameSed && typeof localSite?.sedVersion === 'number' ? localSite.sedVersion : -1;
        const histVer =
          sameSed && typeof localSite?.sedAmendmentHistoryVersion === 'number'
            ? localSite.sedAmendmentHistoryVersion
            : -1;
        return {
          actorMatches,
          ownerMatches,
          localSite,
          sameSed,
          ptrVer,
          histVer,
          hasNewerPointer:
            !sameSed ||
            ptrVer > committedVersion ||
            localSite?.sedStatus === 'bootstrapping' ||
            localSite?.sedBootstrapLockStatus === 'locked',
          hasNewerHistory: !sameSed || histVer > committedHistoryVersion,
        };
      };

      const preSaveLocal = inspectSaveLocalState();
      if (!preSaveLocal.actorMatches || !preSaveLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment operation.',
        };
      }
      const needsAuthoritativeSaveRead =
        isIdempotentReplay || preSaveLocal.hasNewerPointer || preSaveLocal.hasNewerHistory;

      let authoritativeServerSite: Record<string, unknown> | null = null;
      if (needsAuthoritativeSaveRead || !body.applied) {
        try {
          const firestoreModule = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const siteDocRef = firestoreModule.doc(db, `users/${ownerUid}/sites`, owningSite.id);
          const getServerDoc =
            typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer ===
            'function'
              ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc })
                  .getDocFromServer
              : firestoreModule.getDoc;
          if (typeof getServerDoc === 'function') {
            const snap = await getServerDoc(siteDocRef);
            if (snap && typeof snap.exists === 'function' && snap.exists()) {
              authoritativeServerSite = (snap.data() ?? null) as Record<string, unknown> | null;
            }
          }
        } catch {
          authoritativeServerSite = null;
        }
      }

      const afterDocSaveLocal = inspectSaveLocalState();
      if (!afterDocSaveLocal.actorMatches || !afterDocSaveLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment operation.',
        };
      }

      if (needsAuthoritativeSaveRead && !authoritativeServerSite) {
        return {
          ok: false,
          error:
            'Amendment operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
          code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
          operationConfirmed: true,
          refreshRequired: true,
          ...(isIdempotentReplay ? { idempotent: true } : {}),
          sedVersion: committedVersion,
          amendment: committedAmendment,
        };
      }

      const serverSameSed = authoritativeServerSite
        ? authoritativeServerSite.sedId === amendment.sedId
        : !needsAuthoritativeSaveRead;
      const serverPointerVersion =
        serverSameSed && typeof authoritativeServerSite?.sedVersion === 'number'
          ? (authoritativeServerSite.sedVersion as number)
          : -1;

      let loadedAuthoritativeHeadSed: import('../types/siteEvidence').SiteEvidenceDocument | null =
        null;
      if (
        (!serverSameSed ||
          !afterDocSaveLocal.sameSed ||
          Math.max(serverPointerVersion, afterDocSaveLocal.ptrVer) > committedVersion) &&
        serverSameSed &&
        serverPointerVersion > committedVersion &&
        typeof authoritativeServerSite?.sedStoragePath === 'string' &&
        authoritativeServerSite.sedStoragePath.trim()
      ) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          loadedAuthoritativeHeadSed = await loadSedById(
            amendment.sedId,
            authoritativeServerSite.sedStoragePath.trim(),
            serverPointerVersion,
          );
        } catch {
          loadedAuthoritativeHeadSed = null;
        }
      }

      const beforeCacheSaveLocal = inspectSaveLocalState();
      if (!beforeCacheSaveLocal.actorMatches || !beforeCacheSaveLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment operation.',
        };
      }
      const currentLocalSite = beforeCacheSaveLocal.localSite ?? owningSite;
      const localSameSed = beforeCacheSaveLocal.sameSed;
      const localPointerVersion = beforeCacheSaveLocal.ptrVer;
      const maxExistingPointerVersion = Math.max(serverPointerVersion, localPointerVersion);
      const pointerOutdated =
        !serverSameSed ||
        !localSameSed ||
        maxExistingPointerVersion > committedVersion ||
        beforeCacheSaveLocal.hasNewerPointer ||
        authoritativeServerSite?.sedStatus === 'bootstrapping' ||
        authoritativeServerSite?.sedBootstrapLockStatus === 'locked';

      if (isIdempotentReplay) {
        const hasAuthoritativeHistory =
          Boolean(authoritativeServerSite) &&
          authoritativeServerSite?.sedId === amendment.sedId &&
          typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number' &&
          (authoritativeServerSite.sedAmendmentHistoryVersion as number) >=
            committedHistoryVersion &&
          Array.isArray(authoritativeServerSite?.sedAmendmentHistory);
        const hasAuthoritativePointer = !body.applied
          ? Boolean(authoritativeServerSite) &&
            authoritativeServerSite?.sedId === amendment.sedId
          : Boolean(authoritativeServerSite) &&
            authoritativeServerSite?.sedId === amendment.sedId &&
            typeof authoritativeServerSite?.sedVersion === 'number' &&
            (authoritativeServerSite.sedVersion as number) >= committedVersion &&
            typeof authoritativeServerSite?.sedStoragePath === 'string' &&
            authoritativeServerSite.sedStoragePath.trim().length > 0 &&
            ((authoritativeServerSite.sedVersion as number) === committedVersion ||
              loadedAuthoritativeHeadSed !== null);

        if (!hasAuthoritativeHistory || !hasAuthoritativePointer) {
          return {
            ok: false,
            error:
              'Amendment operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
            code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
            operationConfirmed: true,
            refreshRequired: true,
            idempotent: true,
            sedVersion: committedVersion,
            amendment: committedAmendment,
          };
        }
      }

      if (body.applied && returnedSed && !pointerOutdated) {
        primeCommittedSedFromServer({
          sed: returnedSed,
          sedId: amendment.sedId,
          storagePath: committedStoragePath,
          version: committedVersion,
          generation: committedGeneration,
          preserveStoragePaths: currentLocalSite.sedStoragePath
            ? [currentLocalSite.sedStoragePath]
            : [],
        });
      }

      const serverHistoryVersion =
        serverSameSed &&
        typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number'
          ? (authoritativeServerSite.sedAmendmentHistoryVersion as number)
          : -1;
      const serverAmendments =
        serverSameSed && Array.isArray(authoritativeServerSite?.sedAmendmentHistory)
          ? (authoritativeServerSite.sedAmendmentHistory as import('../types/sedAmendments').SEDAmendment[])
          : null;
      const localHistoryVersion =
        localSameSed && typeof currentLocalSite.sedAmendmentHistoryVersion === 'number'
          ? currentLocalSite.sedAmendmentHistoryVersion
          : -1;
      const localAmendments =
        localSameSed && Array.isArray(currentLocalSite.sedAmendmentHistory)
          ? currentLocalSite.sedAmendmentHistory
          : null;

      let reconciledHistoryVersion = committedHistoryVersion;
      let reconciledAmendments = effectiveAmendments;
      if (
        serverHistoryVersion > committedHistoryVersion &&
        serverHistoryVersion >= localHistoryVersion &&
        serverAmendments
      ) {
        reconciledHistoryVersion = serverHistoryVersion;
        reconciledAmendments = serverAmendments;
      } else if (localHistoryVersion > committedHistoryVersion && localAmendments) {
        reconciledHistoryVersion = localHistoryVersion;
        reconciledAmendments = localAmendments;
      } else if (
        isIdempotentReplay &&
        serverHistoryVersion === committedHistoryVersion &&
        serverAmendments
      ) {
        reconciledAmendments = serverAmendments;
      }

      const serverSectionHashes: Record<string, string> = {};
      if (authoritativeServerSite) {
        for (const [k, v] of Object.entries(authoritativeServerSite)) {
          if (k.startsWith('sedSectionHash_') && typeof v === 'string') {
            serverSectionHashes[k] = v;
          }
        }
      }

      set((curr) => ({
        siteRag: {
          ...curr.siteRag,
          sites: curr.siteRag.sites.map((st) => {
            if (st.id !== owningSite.id) return st;

            const stSameSed = !st.sedId || st.sedId === amendment.sedId;
            const stPointerVersion =
              stSameSed && typeof st.sedVersion === 'number' ? st.sedVersion : -1;
            const stHistoryVersion =
              stSameSed && typeof st.sedAmendmentHistoryVersion === 'number'
                ? st.sedAmendmentHistoryVersion
                : -1;

            const nextHistoryVersion =
              stHistoryVersion > reconciledHistoryVersion && Array.isArray(st.sedAmendmentHistory)
                ? stHistoryVersion
                : reconciledHistoryVersion;
            const nextAmendments =
              stHistoryVersion > reconciledHistoryVersion && Array.isArray(st.sedAmendmentHistory)
                ? st.sedAmendmentHistory
                : reconciledAmendments;

            let pointerPatch: Record<string, unknown> = {};
            if (!stSameSed || !serverSameSed) {
              pointerPatch = {};
            } else if (stPointerVersion > committedVersion && stPointerVersion >= serverPointerVersion) {
              pointerPatch = {};
            } else if (
              serverPointerVersion > committedVersion &&
              authoritativeServerSite &&
              typeof authoritativeServerSite.sedStoragePath === 'string'
            ) {
              pointerPatch = {
                sedVersion: serverPointerVersion,
                sedStoragePath: authoritativeServerSite.sedStoragePath,
                ...(typeof authoritativeServerSite.sedStorageGeneration === 'string'
                  ? { sedStorageGeneration: authoritativeServerSite.sedStorageGeneration }
                  : {}),
                ...(typeof authoritativeServerSite.sedContentHash === 'string'
                  ? { sedContentHash: authoritativeServerSite.sedContentHash }
                  : {}),
                ...(typeof authoritativeServerSite.sedContentHashVersion === 'number'
                  ? { sedContentHashVersion: authoritativeServerSite.sedContentHashVersion }
                  : {}),
                ...(typeof authoritativeServerSite.sedCollectedAt === 'string'
                  ? {
                      sedCollectedAt: authoritativeServerSite.sedCollectedAt,
                      sedComputedAt: authoritativeServerSite.sedCollectedAt,
                    }
                  : {}),
                ...serverSectionHashes,
              };
            } else if (body.applied && returnedSed) {
              pointerPatch = {
                sedVersion: committedVersion,
                sedStoragePath: committedStoragePath,
                sedStorageGeneration: committedGeneration,
                sedContentHash: body.sedContentHash as string,
                sedContentHashVersion:
                  typeof body.sedContentHashVersion === 'number'
                    ? body.sedContentHashVersion
                    : committedVersion,
                sedCollectedAt: returnedSed.collectedAt,
                sedComputedAt: returnedSed.collectedAt,
                ...sectionHashes,
                updatedAt: committedAmendment.appliedAt ?? new Date().toISOString(),
              };
            } else if (stPointerVersion < committedVersion) {
              pointerPatch = {
                sedVersion: committedVersion,
                sedStoragePath: committedStoragePath,
                sedStorageGeneration: committedGeneration,
              };
            }

            return {
              ...st,
              ...pointerPatch,
              ...(stSameSed && serverSameSed
                ? {
                    sedAmendmentHistory: nextAmendments,
                    sedAmendmentHistoryVersion: nextHistoryVersion,
                    ...(nextAmendments.some((a) => a.status === 'applied')
                      ? { lastKnownAmendmentsSourceSedId: amendment.sedId }
                      : {}),
                  }
                : {}),
            };
          }),
        },
        amendmentState: {
          ...curr.amendmentState,
          amendments: [
            ...(() => {
              const st = curr.siteRag.sites.find((s) => s.id === owningSite.id);
              const stSameSed = !st?.sedId || st.sedId === amendment.sedId;
              const stHistoryVersion =
                stSameSed && typeof st?.sedAmendmentHistoryVersion === 'number'
                  ? st.sedAmendmentHistoryVersion
                  : -1;
              return stHistoryVersion > reconciledHistoryVersion &&
                Array.isArray(st?.sedAmendmentHistory)
                ? st.sedAmendmentHistory
                : reconciledAmendments;
            })(),
            ...curr.amendmentState.amendments.filter((a) => a.sedId !== amendment.sedId),
          ],
        },
      }));

      if (body.applied && !isIdempotentReplay) {
        if (!pointerOutdated) {
          get()._staleStampSiteComposite(owningSite.id);
        }
        try {
          const { emitAuditEvent } = await import('../lib/auditLogClient');
          void emitAuditEvent({
            action: 'sed.amendment_applied',
            target: { type: 'site', id: owningSite.id, label: owningSite.name },
            before: { value: committedAmendment.oldValue, sedVersion: expectedSedVersion },
            after: { value: committedAmendment.newValue, sedVersion: committedVersion },
            metadata: {
              sedId: amendment.sedId,
              amendmentId: committedAmendment.id,
              operationId,
              supersedesAmendmentId: committedAmendment.supersedesAmendmentId,
              variablePath: committedAmendment.variablePath,
              variableLabel: committedAmendment.variableLabel,
              domain: committedAmendment.domain,
              reason: committedAmendment.reason,
              sourceType: committedAmendment.source.type,
              sourceLabel: committedAmendment.source.label,
              sourceUrl: committedAmendment.source.url,
              unit: committedAmendment.unit,
              appliedAt: committedAmendment.appliedAt,
            },
          });
        } catch (auditErr) {
          console.debug('[saveSEDAmendment] audit emit failed:', (auditErr as Error)?.message);
        }
      }

      const finalSiteAfter = get().siteRag.sites.find((s) => s.id === owningSite.id);
      const finalResolvedSedVersion =
        finalSiteAfter?.sedId === amendment.sedId && typeof finalSiteAfter.sedVersion === 'number'
          ? finalSiteAfter.sedVersion
          : Math.max(committedVersion, serverPointerVersion, localPointerVersion);
      const finalResolvedAmendment =
        (finalSiteAfter?.sedAmendmentHistory ?? reconciledAmendments).find(
          (a) => a.id === committedAmendment.id,
        ) ?? committedAmendment;

      return {
        ok: true,
        applied: body.applied,
        ...(isIdempotentReplay ? { idempotent: true } : {}),
        sedVersion: finalResolvedSedVersion,
        amendment: finalResolvedAmendment,
      };
    } catch (e) {
      if (e instanceof SiteLockedError) throw e;
      const err = (e as Error)?.message || 'Unknown error';
      console.error('Failed to save amendment:', e);
      return { ok: false, error: err };
    }
  },

  acceptSEDVariable: async (input) => {
    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return { ok: false, error: 'Not signed in' };

      const owningSite = get().siteRag.sites.find(s => s.sedId === input.sedId);
      if (!owningSite) return { ok: false, error: 'Site or SED not found' };
      await get().assertSiteWritable(owningSite.id);

      const ownerUid = siteOwnerUid(owningSite, user.uid);
      const { getCachedSedStorageGeneration } = await import('../services/sedLoader');
      const { authedFetch } = await import('../lib/authedFetch');

      const expectedSedVersion =
        typeof owningSite.sedVersion === 'number' ? owningSite.sedVersion : input.sedVersion;
      const expectedHistoryVersion =
        typeof owningSite.sedAmendmentHistoryVersion === 'number'
          ? owningSite.sedAmendmentHistoryVersion
          : undefined;
      const expectedSedStorageGeneration =
        getCachedSedStorageGeneration(input.sedId, owningSite.sedStoragePath) ??
        (typeof owningSite.sedStorageGeneration === 'string' && owningSite.sedStorageGeneration.trim()
          ? owningSite.sedStorageGeneration.trim()
          : undefined);

      const envelopeKey = JSON.stringify([
        user.uid,
        ownerUid,
        owningSite.id,
        input.sedId,
        'accept',
        input.variablePath,
        input.value ?? null,
      ]);
      const operationId =
        pendingSedAmendmentOperations.get(envelopeKey) ??
        `sed-amend-accept-${owningSite.id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
          /[^A-Za-z0-9._:-]/g,
          '-',
        );
      pendingSedAmendmentOperations.set(envelopeKey, operationId);

      const res = await authedFetch(
        `/api/sites/${encodeURIComponent(owningSite.id)}/sed-amendments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'accept',
            operationId,
            ownerUid,
            sedId: input.sedId,
            variablePath: input.variablePath,
            variableLabel: input.variableLabel,
            domain: input.domain,
            currentValue: input.value,
            unit: input.unit,
            ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
            ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
            ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          }),
        },
      );

      const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
      if (!res.ok) {
        if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
          pendingSedAmendmentOperations.delete(envelopeKey);
        }
        return {
          ok: false,
          error:
            typeof body?.error === 'string' && body.error.trim()
              ? body.error.trim()
              : `Failed to accept SED variable (HTTP ${res.status})`,
        };
      }
      if (
        !body ||
        body.ok !== true ||
        body.action !== 'accept' ||
        body.operationId !== operationId ||
        body.ownerUid !== ownerUid ||
        body.siteId !== owningSite.id ||
        body.sedId !== input.sedId ||
        !Array.isArray(body.amendments) ||
        typeof body.historyVersion !== 'number'
      ) {
        return { ok: false, error: 'Malformed accept response from server.' };
      }
      pendingSedAmendmentOperations.delete(envelopeKey);
      const effectiveAmendments = body.amendments as import('../types/sedAmendments').SEDAmendment[];
      const committedHistoryVersion = body.historyVersion;

      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map((st) =>
            st.id === owningSite.id
              ? {
                  ...st,
                  sedAmendmentHistory: effectiveAmendments,
                  sedAmendmentHistoryVersion: committedHistoryVersion,
                }
              : st,
          ),
        },
        amendmentState: {
          ...state.amendmentState,
          amendments: [
            ...effectiveAmendments,
            ...state.amendmentState.amendments.filter((a) => a.sedId !== input.sedId),
          ],
        },
      }));
      return { ok: true, applied: false };
    } catch (e) {
      const err = (e as Error)?.message || 'Unknown error';
      console.error('Failed to accept SED variable:', e);
      return { ok: false, error: err };
    }
  },

  updateAmendmentStatus: async (sedId, amendmentId, status, reviewComment) => {
    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const owningSite = get().siteRag.sites.find(s => s.sedId === sedId);
      if (!owningSite) {
        throw new Error(`No site owns SED ${sedId}.`);
      }
      if (status === 'superseded') {
        throw new Error('Supersession is server-managed on save/revert.');
      }

      const ownerUid = siteOwnerUid(owningSite, user.uid);
      const { getCachedSedStorageGeneration, primeCommittedSedFromServer } = await import('../services/sedLoader');
      const { authedFetch } = await import('../lib/authedFetch');

      const expectedSedVersion =
        typeof owningSite.sedVersion === 'number' ? owningSite.sedVersion : undefined;
      const expectedHistoryVersion =
        typeof owningSite.sedAmendmentHistoryVersion === 'number'
          ? owningSite.sedAmendmentHistoryVersion
          : undefined;
      const expectedSedStorageGeneration =
        getCachedSedStorageGeneration(sedId, owningSite.sedStoragePath) ??
        (typeof owningSite.sedStorageGeneration === 'string' && owningSite.sedStorageGeneration.trim()
          ? owningSite.sedStorageGeneration.trim()
          : undefined);

      const envelopeKey = JSON.stringify([
        user.uid,
        ownerUid,
        owningSite.id,
        sedId,
        'update-status',
        amendmentId,
        status,
        reviewComment ?? '',
      ]);
      const operationId =
        pendingSedAmendmentOperations.get(envelopeKey) ??
        `sed-amend-status-${owningSite.id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
          /[^A-Za-z0-9._:-]/g,
          '-',
        );
      pendingSedAmendmentOperations.set(envelopeKey, operationId);

      const res = await authedFetch(
        `/api/sites/${encodeURIComponent(owningSite.id)}/sed-amendments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update-status',
            operationId,
            ownerUid,
            sedId,
            amendmentId,
            status,
            ...(reviewComment !== undefined ? { reviewComment } : {}),
            ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
            ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
            ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          }),
        },
      );

      const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
      if (!res.ok) {
        if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
          pendingSedAmendmentOperations.delete(envelopeKey);
        }
        throw new Error(
          typeof body?.error === 'string' && body.error.trim()
            ? body.error.trim()
            : `Failed to update amendment status (HTTP ${res.status})`,
        );
      }
      if (
        !body ||
        body.ok !== true ||
        body.action !== 'update-status' ||
        body.operationId !== operationId ||
        body.ownerUid !== ownerUid ||
        body.siteId !== owningSite.id ||
        body.sedId !== sedId ||
        typeof body.applied !== 'boolean' ||
        !Array.isArray(body.amendments) ||
        typeof body.historyVersion !== 'number' ||
        typeof body.sedVersion !== 'number' ||
        typeof body.sedStoragePath !== 'string' ||
        typeof body.sedStorageGeneration !== 'string'
      ) {
        throw new Error('Malformed update-status response from server.');
      }

      pendingSedAmendmentOperations.delete(envelopeKey);
      const effectiveAmendments = body.amendments as import('../types/sedAmendments').SEDAmendment[];
      const committedVersion = body.sedVersion;
      const committedStoragePath = body.sedStoragePath.trim();
      const committedGeneration = body.sedStorageGeneration.trim();
      const committedHistoryVersion = body.historyVersion;
      const returnedSed =
        body.sed && typeof body.sed === 'object'
          ? (body.sed as import('../types/siteEvidence').SiteEvidenceDocument)
          : null;

      if (body.applied && returnedSed) {
        primeCommittedSedFromServer({
          sed: returnedSed,
          sedId,
          storagePath: committedStoragePath,
          version: committedVersion,
          generation: committedGeneration,
          preserveStoragePaths: owningSite.sedStoragePath ? [owningSite.sedStoragePath] : [],
        });
      }

      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map((st) =>
            st.id === owningSite.id
              ? {
                  ...st,
                  sedVersion: committedVersion,
                  sedStoragePath: committedStoragePath,
                  sedStorageGeneration: committedGeneration,
                  sedAmendmentHistory: effectiveAmendments,
                  sedAmendmentHistoryVersion: committedHistoryVersion,
                }
              : st,
          ),
        },
        amendmentState: {
          ...state.amendmentState,
          amendments: [
            ...effectiveAmendments,
            ...state.amendmentState.amendments.filter((a) => a.sedId !== sedId),
          ],
        },
      }));
    } catch (e) {
      console.error('Failed to update amendment status:', e);
      throw e;
    }
  },

  reapplyAmendmentsAfterRebuild: async (siteId: string, oldSedId: string) => {
    const summary = { reapplied: 0, conflicts: 0, orphans: 0, skipped: 0, errors: 0 };
    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return summary;
      const initiatingUid = user.uid;
      const site = get().siteRag.sites.find((s) => s.id === siteId);
      if (!site?.sedId || !site.sedStoragePath) return summary;
      if (site.sedId === oldSedId) return summary;

      await get().assertSiteWritable(siteId);
      const ownerUid = siteOwnerUid(site, initiatingUid);
      const {
        getCachedSedStorageGeneration,
        invalidateCachedSed,
        primeCommittedSedFromServer,
      } = await import('../services/sedLoader');
      const { authedFetch } = await import('../lib/authedFetch');

      const currentExpectedSedVersion =
        typeof site.sedVersion === 'number' && Number.isFinite(site.sedVersion)
          ? site.sedVersion
          : 1;
      const currentExpectedHistoryVersion =
        typeof site.sedAmendmentHistoryVersion === 'number' &&
        Number.isFinite(site.sedAmendmentHistoryVersion)
          ? site.sedAmendmentHistoryVersion
          : 0;
      const currentExpectedStorageGeneration =
        getCachedSedStorageGeneration(site.sedId, site.sedStoragePath) ??
        (typeof site.sedStorageGeneration === 'string' && site.sedStorageGeneration.trim()
          ? site.sedStorageGeneration.trim()
          : undefined);

      const envelopeKey = JSON.stringify([
        initiatingUid,
        ownerUid,
        siteId,
        site.sedId,
        oldSedId,
      ]);
      const existingEnvelope = pendingSedReapplyOperations.get(envelopeKey);
      const envelope =
        existingEnvelope ??
        {
          operationId: `sed-amend-reapply-${siteId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
            /[^A-Za-z0-9._:-]/g,
            '-',
          ),
          ownerUid,
          sedId: site.sedId,
          sourceSedId: oldSedId,
          expectedSedVersion: currentExpectedSedVersion,
          expectedHistoryVersion: currentExpectedHistoryVersion,
          ...(currentExpectedStorageGeneration
            ? { expectedSedStorageGeneration: currentExpectedStorageGeneration }
            : {}),
        };
      pendingSedReapplyOperations.set(envelopeKey, envelope);

      const clearPendingReapplyIfTerminal = (status?: number) => {
        if (
          status === undefined ||
          (status >= 400 && status < 500 && status !== 408 && status !== 429)
        ) {
          pendingSedReapplyOperations.delete(envelopeKey);
        }
      };

      const res = await authedFetch(
        `/api/sites/${encodeURIComponent(siteId)}/sed-amendments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'reapply-after-rebuild',
            operationId: envelope.operationId,
            ownerUid: envelope.ownerUid,
            sedId: envelope.sedId,
            sourceSedId: envelope.sourceSedId,
            expectedSedVersion: envelope.expectedSedVersion,
            expectedHistoryVersion: envelope.expectedHistoryVersion,
            ...(envelope.expectedSedStorageGeneration
              ? { expectedSedStorageGeneration: envelope.expectedSedStorageGeneration }
              : {}),
          }),
        },
      );

      let body: Record<string, unknown> | null = null;
      try {
        const rawText = typeof res.text === 'function' ? await res.text() : '';
        body = rawText
          ? (JSON.parse(rawText) as Record<string, unknown>)
          : typeof res.json === 'function'
            ? ((await res.json()) as Record<string, unknown>)
            : null;
      } catch {
        body = null;
      }

      if (!res.ok) {
        clearPendingReapplyIfTerminal(res.status);
        console.error(
          '[reapplyAmendmentsAfterRebuild] server reapply failed:',
          res.status,
          typeof body?.error === 'string' ? body.error : undefined,
        );
        summary.errors += 1;
        return summary;
      }

      if (
        !body ||
        body.ok !== true ||
        body.action !== 'reapply-after-rebuild' ||
        body.operationId !== envelope.operationId ||
        body.ownerUid !== envelope.ownerUid ||
        body.siteId !== siteId ||
        body.sedId !== envelope.sedId ||
        typeof body.applied !== 'boolean' ||
        !Array.isArray(body.amendments) ||
        typeof body.historyVersion !== 'number' ||
        !Number.isFinite(body.historyVersion) ||
        typeof body.sedVersion !== 'number' ||
        !Number.isFinite(body.sedVersion) ||
        typeof body.sedStoragePath !== 'string' ||
        !body.sedStoragePath.trim() ||
        typeof body.sedStorageGeneration !== 'string' ||
        !body.sedStorageGeneration.trim() ||
        !body.reapplySummary ||
        typeof body.reapplySummary !== 'object'
      ) {
        console.error('[reapplyAmendmentsAfterRebuild] malformed or mismatched server ACK');
        summary.errors += 1;
        return summary;
      }

      const committedSedId = body.sedId;
      const committedVersion = body.sedVersion;
      const committedPath = body.sedStoragePath.trim();
      const committedGen = body.sedStorageGeneration.trim();
      const committedHistoryVersion = body.historyVersion;
      const effectiveAmendments = body.amendments as import('../types/sedAmendments').SEDAmendment[];
      const isIdempotentReplay = body.idempotentReplay === true;
      const returnedSed =
        body.sed && typeof body.sed === 'object'
          ? (body.sed as import('../types/siteEvidence').SiteEvidenceDocument)
          : null;

      if (
        body.applied &&
        (!returnedSed ||
          returnedSed.id !== committedSedId ||
          returnedSed.version !== committedVersion ||
          typeof body.sedContentHash !== 'string' ||
          !body.sedContentHash.trim())
      ) {
        console.error(
          '[reapplyAmendmentsAfterRebuild] malformed applied server ACK: missing authoritative SED or content hash',
        );
        summary.errors += 1;
        return summary;
      }

      clearPendingReapplyIfTerminal();

      const srvSummary = body.reapplySummary as {
        reapplied?: unknown;
        conflicts?: unknown;
        orphaned?: unknown;
        skipped?: unknown;
        errors?: unknown;
      };
      summary.reapplied = typeof srvSummary.reapplied === 'number' ? srvSummary.reapplied : 0;
      summary.conflicts = typeof srvSummary.conflicts === 'number' ? srvSummary.conflicts : 0;
      summary.orphans = typeof srvSummary.orphaned === 'number' ? srvSummary.orphaned : 0;
      summary.skipped = typeof srvSummary.skipped === 'number' ? srvSummary.skipped : 0;
      summary.errors = typeof srvSummary.errors === 'number' ? srvSummary.errors : 0;

      const inspectLocalSiteForReapply = () => {
        const actorMatches = auth.currentUser?.uid === initiatingUid;
        const localSite = get().siteRag.sites.find((s) => s.id === siteId) ?? null;
        const ownerMatches = Boolean(
          localSite && siteOwnerUid(localSite, initiatingUid) === ownerUid,
        );
        const sameSed = Boolean(
          actorMatches && ownerMatches && localSite && localSite.sedId === committedSedId,
        );
        const localPtrVer =
          sameSed && typeof localSite?.sedVersion === 'number' ? localSite.sedVersion : -1;
        const localHistVer =
          sameSed && typeof localSite?.sedAmendmentHistoryVersion === 'number'
            ? localSite.sedAmendmentHistoryVersion
            : -1;
        const localAmends =
          sameSed && Array.isArray(localSite?.sedAmendmentHistory)
            ? localSite.sedAmendmentHistory
            : null;
        const pointerMoved = Boolean(
          !actorMatches ||
            !ownerMatches ||
            !sameSed ||
            localPtrVer > committedVersion ||
            (localPtrVer === committedVersion &&
              typeof localSite?.sedStorageGeneration === 'string' &&
              localSite.sedStorageGeneration.trim().length > 0 &&
              localSite.sedStorageGeneration.trim() !== committedGen) ||
            localSite?.sedStatus === 'bootstrapping' ||
            localSite?.sedBootstrapLockStatus === 'locked',
        );
        const historyMoved = Boolean(
          !actorMatches || !ownerMatches || !sameSed || localHistVer > committedHistoryVersion,
        );
        return {
          actorMatches,
          ownerMatches,
          localSite,
          sameSed,
          localPtrVer,
          localHistVer,
          localAmends,
          pointerMoved,
          historyMoved,
        };
      };

      const preReadLocal = inspectLocalSiteForReapply();
      if (!preReadLocal.actorMatches || !preReadLocal.ownerMatches) {
        return summary;
      }

      const needsAuthoritativeHeadRead =
        isIdempotentReplay || preReadLocal.pointerMoved || preReadLocal.historyMoved;
      let authoritativeServerSite: Record<string, unknown> | null = null;
      if (needsAuthoritativeHeadRead) {
        try {
          const firestoreModule = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const siteDocRef = firestoreModule.doc(db, `users/${ownerUid}/sites`, siteId);
          const getServerDoc =
            typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer ===
            'function'
              ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc })
                  .getDocFromServer
              : firestoreModule.getDoc;
          if (typeof getServerDoc === 'function') {
            const snap = await getServerDoc(siteDocRef);
            if (snap && typeof snap.exists === 'function' && snap.exists()) {
              authoritativeServerSite = (snap.data() ?? null) as Record<string, unknown> | null;
            }
          }
        } catch {
          authoritativeServerSite = null;
        }
      }

      const postReadLocal = inspectLocalSiteForReapply();
      if (!postReadLocal.actorMatches || !postReadLocal.ownerMatches) {
        return summary;
      }

      if (needsAuthoritativeHeadRead && !authoritativeServerSite) {
        summary.errors += 1;
        Object.assign(summary, {
          code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
          operationConfirmed: true,
          refreshRequired: true,
          ...(isIdempotentReplay ? { idempotent: true } : {}),
        });
        return summary;
      }

      const serverSameSed = needsAuthoritativeHeadRead
        ? Boolean(authoritativeServerSite && authoritativeServerSite.sedId === committedSedId)
        : true;
      const serverPointerVersion =
        serverSameSed && typeof authoritativeServerSite?.sedVersion === 'number'
          ? (authoritativeServerSite.sedVersion as number)
          : -1;

      let loadedAuthoritativeHeadSed: import('../types/siteEvidence').SiteEvidenceDocument | null =
        null;
      if (
        needsAuthoritativeHeadRead &&
        serverSameSed &&
        serverPointerVersion > committedVersion &&
        typeof authoritativeServerSite?.sedStoragePath === 'string' &&
        authoritativeServerSite.sedStoragePath.trim()
      ) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          loadedAuthoritativeHeadSed = await loadSedById(
            committedSedId,
            authoritativeServerSite.sedStoragePath.trim(),
            serverPointerVersion,
          );
        } catch {
          loadedAuthoritativeHeadSed = null;
        }
      }

      const beforeCacheLocal = inspectLocalSiteForReapply();
      if (!beforeCacheLocal.actorMatches || !beforeCacheLocal.ownerMatches) {
        return summary;
      }

      if (isIdempotentReplay && serverSameSed) {
        const hasAuthoritativeHistory =
          Boolean(authoritativeServerSite) &&
          authoritativeServerSite?.sedId === committedSedId &&
          typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number' &&
          (authoritativeServerSite.sedAmendmentHistoryVersion as number) >=
            committedHistoryVersion &&
          Array.isArray(authoritativeServerSite?.sedAmendmentHistory);
        const hasAuthoritativePointer =
          Boolean(authoritativeServerSite) &&
          authoritativeServerSite?.sedId === committedSedId &&
          typeof authoritativeServerSite?.sedVersion === 'number' &&
          (authoritativeServerSite.sedVersion as number) >= committedVersion &&
          typeof authoritativeServerSite?.sedStoragePath === 'string' &&
          authoritativeServerSite.sedStoragePath.trim().length > 0 &&
          ((authoritativeServerSite.sedVersion as number) === committedVersion ||
            loadedAuthoritativeHeadSed !== null);
        if (!hasAuthoritativeHistory || !hasAuthoritativePointer) {
          summary.errors += 1;
          Object.assign(summary, {
            code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
            operationConfirmed: true,
            refreshRequired: true,
            idempotent: true,
          });
          return summary;
        }
      }

      const localSameSed = beforeCacheLocal.sameSed;
      const localPointerVersion = beforeCacheLocal.localPtrVer;
      const pointerOutdated =
        beforeCacheLocal.pointerMoved ||
        !localSameSed ||
        !serverSameSed ||
        serverPointerVersion > committedVersion ||
        (serverPointerVersion === committedVersion &&
          typeof authoritativeServerSite?.sedStorageGeneration === 'string' &&
          authoritativeServerSite.sedStorageGeneration.trim().length > 0 &&
          authoritativeServerSite.sedStorageGeneration.trim() !== committedGen) ||
        localPointerVersion > committedVersion ||
        authoritativeServerSite?.sedStatus === 'bootstrapping' ||
        authoritativeServerSite?.sedBootstrapLockStatus === 'locked';

      if (body.applied && returnedSed && !pointerOutdated) {
        primeCommittedSedFromServer({
          sed: returnedSed,
          sedId: committedSedId,
          storagePath: committedPath,
          version: committedVersion,
          generation: committedGen,
          preserveStoragePaths: beforeCacheLocal.localSite?.sedStoragePath
            ? [beforeCacheLocal.localSite.sedStoragePath]
            : [],
        });
      } else if (!pointerOutdated) {
        invalidateCachedSed(committedSedId);
      }

      const serverHistoryVersion =
        serverSameSed &&
        typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number'
          ? (authoritativeServerSite.sedAmendmentHistoryVersion as number)
          : -1;
      const serverAmendments =
        serverSameSed && Array.isArray(authoritativeServerSite?.sedAmendmentHistory)
          ? (authoritativeServerSite.sedAmendmentHistory as import('../types/sedAmendments').SEDAmendment[])
          : null;
      const localHistoryVersion = beforeCacheLocal.localHistVer;
      const localAmendments = beforeCacheLocal.localAmends;

      let reconciledHistoryVersion = committedHistoryVersion;
      let reconciledAmendments = effectiveAmendments;
      if (
        serverHistoryVersion > committedHistoryVersion &&
        serverHistoryVersion >= localHistoryVersion &&
        serverAmendments
      ) {
        reconciledHistoryVersion = serverHistoryVersion;
        reconciledAmendments = serverAmendments;
      } else if (localHistoryVersion > committedHistoryVersion && localAmendments) {
        reconciledHistoryVersion = localHistoryVersion;
        reconciledAmendments = localAmendments;
      } else if (
        isIdempotentReplay &&
        serverHistoryVersion === committedHistoryVersion &&
        serverAmendments
      ) {
        reconciledAmendments = serverAmendments;
      }

      set((state) => {
        if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
          return state;
        }
        const liveSite = state.siteRag.sites.find((s) => s.id === siteId);
        if (!liveSite || siteOwnerUid(liveSite, initiatingUid) !== ownerUid) {
          return state;
        }
        const liveSameSed =
          liveSite.sedId === committedSedId &&
          serverSameSed &&
          liveSite.sedStatus !== 'bootstrapping' &&
          liveSite.sedBootstrapLockStatus !== 'locked' &&
          authoritativeServerSite?.sedStatus !== 'bootstrapping' &&
          authoritativeServerSite?.sedBootstrapLockStatus !== 'locked';
        if (!liveSameSed) {
          return state;
        }
        const liveHistVer =
          typeof liveSite.sedAmendmentHistoryVersion === 'number'
            ? liveSite.sedAmendmentHistoryVersion
            : -1;
        const liveAmendments =
          liveHistVer > reconciledHistoryVersion && Array.isArray(liveSite.sedAmendmentHistory)
            ? liveSite.sedAmendmentHistory
            : reconciledAmendments;

        return {
          amendmentState: {
            ...state.amendmentState,
            amendments: liveAmendments,
          },
          siteRag: {
            ...state.siteRag,
            sites: state.siteRag.sites.map((st) => {
              if (st.id !== siteId) return st;
              if (siteOwnerUid(st, initiatingUid) !== ownerUid) return st;
              const stSameSed = st.sedId === committedSedId;
              if (
                !stSameSed ||
                !serverSameSed ||
                st.sedStatus === 'bootstrapping' ||
                st.sedBootstrapLockStatus === 'locked' ||
                authoritativeServerSite?.sedStatus === 'bootstrapping' ||
                authoritativeServerSite?.sedBootstrapLockStatus === 'locked'
              ) {
                return st;
              }
            const stPointerVersion =
              typeof st.sedVersion === 'number' ? st.sedVersion : -1;
            const stHistoryVersion =
              typeof st.sedAmendmentHistoryVersion === 'number'
                ? st.sedAmendmentHistoryVersion
                : -1;
            const nextHistoryVersion =
              stHistoryVersion > reconciledHistoryVersion && Array.isArray(st.sedAmendmentHistory)
                ? stHistoryVersion
                : reconciledHistoryVersion;
            const nextAmendments =
              stHistoryVersion > reconciledHistoryVersion && Array.isArray(st.sedAmendmentHistory)
                ? st.sedAmendmentHistory
                : reconciledAmendments;
            const hasApplied = nextAmendments.some((a) => a.status === 'applied');

            const stEqualVerGenMismatch =
              stPointerVersion === committedVersion &&
              typeof st.sedStorageGeneration === 'string' &&
              st.sedStorageGeneration.trim().length > 0 &&
              st.sedStorageGeneration.trim() !== committedGen;
            const serverEqualVerGenMismatch =
              serverPointerVersion === committedVersion &&
              typeof authoritativeServerSite?.sedStorageGeneration === 'string' &&
              authoritativeServerSite.sedStorageGeneration.trim().length > 0 &&
              authoritativeServerSite.sedStorageGeneration.trim() !== committedGen;

            let pointerPatch: Record<string, unknown> = {};
            if (
              (stPointerVersion > committedVersion && stPointerVersion >= serverPointerVersion) ||
              (stEqualVerGenMismatch &&
                stPointerVersion >= serverPointerVersion &&
                !serverEqualVerGenMismatch)
            ) {
              pointerPatch = {};
            } else if (
              (serverPointerVersion > committedVersion || serverEqualVerGenMismatch) &&
              authoritativeServerSite &&
              typeof authoritativeServerSite.sedStoragePath === 'string' &&
              authoritativeServerSite.sedStoragePath.trim()
            ) {
              pointerPatch = {
                sedVersion: serverPointerVersion,
                sedStoragePath: authoritativeServerSite.sedStoragePath.trim(),
                ...(typeof authoritativeServerSite.sedStorageGeneration === 'string' &&
                authoritativeServerSite.sedStorageGeneration.trim()
                  ? { sedStorageGeneration: authoritativeServerSite.sedStorageGeneration.trim() }
                  : {}),
                ...(typeof authoritativeServerSite.sedContentHash === 'string'
                  ? { sedContentHash: authoritativeServerSite.sedContentHash }
                  : {}),
                ...(typeof authoritativeServerSite.sedContentHashVersion === 'number'
                  ? { sedContentHashVersion: authoritativeServerSite.sedContentHashVersion }
                  : {}),
                ...(authoritativeServerSite.sedSectionContentHashes
                  ? { sedSectionContentHashes: authoritativeServerSite.sedSectionContentHashes }
                  : {}),
                ...(authoritativeServerSite.sedSectionContentHashesV3
                  ? { sedSectionContentHashesV3: authoritativeServerSite.sedSectionContentHashesV3 }
                  : {}),
                ...(typeof authoritativeServerSite.sedCollectedAt === 'string'
                  ? {
                      sedCollectedAt: authoritativeServerSite.sedCollectedAt,
                      sedComputedAt: authoritativeServerSite.sedCollectedAt,
                    }
                  : {}),
              };
            } else if (pointerOutdated) {
              pointerPatch = {};
            } else {
              pointerPatch = {
                sedVersion: committedVersion,
                sedStoragePath: committedPath,
                sedStorageGeneration: committedGen,
                ...(typeof body.sedContentHash === 'string'
                  ? {
                      sedContentHash: body.sedContentHash,
                      sedContentHashVersion:
                        typeof body.sedContentHashVersion === 'number'
                          ? body.sedContentHashVersion
                          : committedVersion,
                    }
                  : {}),
                ...(body.sedSectionContentHashes
                  ? { sedSectionContentHashes: body.sedSectionContentHashes }
                  : {}),
                ...(body.sedSectionContentHashesV3
                  ? { sedSectionContentHashesV3: body.sedSectionContentHashesV3 }
                  : {}),
                ...(returnedSed?.collectedAt
                  ? {
                      sedCollectedAt: returnedSed.collectedAt,
                      sedComputedAt: returnedSed.collectedAt,
                    }
                  : {}),
              };
            }

              return {
                ...st,
                ...pointerPatch,
                sedAmendmentHistory: nextAmendments,
                sedAmendmentHistoryVersion: nextHistoryVersion,
                ...(hasApplied ? { lastKnownAmendmentsSourceSedId: committedSedId } : {}),
              };
            }),
          },
        };
      });

      if (
        !isIdempotentReplay &&
        (summary.reapplied > 0 ||
          summary.conflicts > 0 ||
          summary.orphans > 0 ||
          summary.skipped > 0)
      ) {
        try {
          const { emitAuditEvent } = await import('../lib/auditLogClient');
          void emitAuditEvent({
            action: 'sed.amendments_reapplied',
            target: { type: 'site', id: siteId, label: site.name },
            metadata: {
              oldSedId,
              newSedId: committedSedId,
              ...summary,
            },
          });
        } catch (auditErr) {
          console.debug('[reapplyAmendmentsAfterRebuild] audit emit failed:', (auditErr as Error)?.message);
        }
      }
    } catch (e) {
      console.error('[reapplyAmendmentsAfterRebuild] unexpected error:', e);
      summary.errors += 1;
    }
    return summary;
  },

  revertSedAmendment: async (siteId: string, variablePath: string) => {
    try {
      const initialSite = get().siteRag.sites.find((s) => s.id === siteId);
      if (!initialSite?.sedId) return { ok: false, error: 'Site or SED not found' };
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return { ok: false, error: 'Not signed in' };
      const initiatingUid = user.uid;
      const initialOwnerUid = siteOwnerUid(initialSite, initiatingUid);

      await get().assertSiteWritable(initialSite.id);

      if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment revert operation.',
        };
      }
      const state = get();
      const site = state.siteRag.sites.find((s) => s.id === siteId);
      if (!site?.sedId || siteOwnerUid(site, initiatingUid) !== initialOwnerUid) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment revert operation.',
        };
      }

      const sedId = site.sedId;
      const ownerUid = initialOwnerUid;
      const { getCachedSedStorageGeneration, primeCommittedSedFromServer } = await import(
        '../services/sedLoader'
      );
      const { authedFetch } = await import('../lib/authedFetch');

      const envelopeKey = JSON.stringify([
        initiatingUid,
        ownerUid,
        site.id,
        sedId,
        'revert',
        variablePath,
      ]);

      let activeEnvelope = pendingSedRevertOperations.get(envelopeKey);
      if (!activeEnvelope) {
        const statusRank = (
          s: import('../types/sedAmendments').SEDAmendment['status'] | undefined,
        ): number => {
          if (s === 'reverted') return 4;
          if (s === 'superseded') return 3;
          if (s === 'rejected') return 2;
          if (s === 'applied') return 1;
          return 0;
        };
        const mergedForRevertById = new Map<string, import('../types/sedAmendments').SEDAmendment>();
        for (const a of [
          ...(site.sedAmendmentHistory ?? []),
          ...(state.amendmentState.amendments ?? []),
        ]) {
          if (!a || !a.id) continue;
          const prev = mergedForRevertById.get(a.id);
          if (!prev) {
            mergedForRevertById.set(a.id, a);
          } else {
            const winningStatus =
              statusRank(a.status) >= statusRank(prev.status) ? a.status : prev.status;
            mergedForRevertById.set(a.id, {
              ...prev,
              ...a,
              status: winningStatus,
              ...(a.supersedesAmendmentId ?? prev.supersedesAmendmentId
                ? { supersedesAmendmentId: a.supersedesAmendmentId ?? prev.supersedesAmendmentId }
                : {}),
              ...(a.revertedFromAmendmentId ?? prev.revertedFromAmendmentId
                ? {
                    revertedFromAmendmentId:
                      a.revertedFromAmendmentId ?? prev.revertedFromAmendmentId,
                  }
                : {}),
            });
          }
        }

        const { resolveRevertLineageOnSed } = await import('../services/sedAmendments');
        const lineage = resolveRevertLineageOnSed(
          [...mergedForRevertById.values()],
          sedId,
          variablePath,
        );
        if (!lineage.ok) {
          return { ok: false, error: lineage.error };
        }

        const expectedSedVersion =
          typeof site.sedVersion === 'number' ? site.sedVersion : undefined;
        const expectedHistoryVersion =
          typeof site.sedAmendmentHistoryVersion === 'number'
            ? site.sedAmendmentHistoryVersion
            : undefined;
        const expectedSedStorageGeneration =
          getCachedSedStorageGeneration(sedId, site.sedStoragePath) ??
          (typeof site.sedStorageGeneration === 'string' && site.sedStorageGeneration.trim()
            ? site.sedStorageGeneration.trim()
            : undefined);
        const requireReviewWorkflowFlag =
          get().companyRequireReviewWorkflow === true ? true : undefined;
        const mintedOperationId = `sed-amend-revert-${site.id}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}`.replace(/[^A-Za-z0-9._:-]/g, '-');
        const reason = 'Revert via UI — restore original provider value.';
        const requestBody: Record<string, unknown> = {
          action: 'revert',
          operationId: mintedOperationId,
          ownerUid,
          sedId,
          amendmentId: lineage.latest.id,
          reason,
          ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
          ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
          ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          ...(requireReviewWorkflowFlag ? { requireReviewWorkflow: true } : {}),
        };

        activeEnvelope = {
          operationId: mintedOperationId,
          ownerUid,
          siteId: site.id,
          sedId,
          variablePath,
          amendmentId: lineage.latest.id,
          reason,
          ...(typeof expectedSedVersion === 'number' ? { expectedSedVersion } : {}),
          ...(typeof expectedHistoryVersion === 'number' ? { expectedHistoryVersion } : {}),
          ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
          ...(requireReviewWorkflowFlag ? { requireReviewWorkflow: true } : {}),
          requestBody,
          lineage: {
            latestNewValue: lineage.latest.newValue,
            earliestVariableLabel: lineage.earliest.variableLabel,
            earliestId: lineage.earliest.id,
            originalProviderValue: lineage.originalProviderValue,
            supersededIds: [...lineage.supersededIds],
          },
        };
        pendingSedRevertOperations.set(envelopeKey, activeEnvelope);
      }

      const operationId = activeEnvelope.operationId;
      const clearPendingOpIfTerminal = (status?: number) => {
        if (
          status === undefined ||
          (status >= 400 && status < 500 && status !== 408 && status !== 429)
        ) {
          pendingSedRevertOperations.delete(envelopeKey);
        }
      };

      const res = await authedFetch(
        `/api/sites/${encodeURIComponent(site.id)}/sed-amendments`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(activeEnvelope.requestBody),
        },
      );

      let body: Record<string, unknown> | null = null;
      try {
        const rawText = typeof res.text === 'function' ? await res.text() : '';
        body = rawText
          ? (JSON.parse(rawText) as Record<string, unknown>)
          : typeof res.json === 'function'
            ? ((await res.json()) as Record<string, unknown>)
            : null;
      } catch {
        body = null;
      }

      if (!res.ok) {
        clearPendingOpIfTerminal(res.status);
        const errCode = typeof body?.code === 'string' ? body.code : undefined;
        const errMsg =
          typeof body?.error === 'string' && body.error.trim()
            ? body.error.trim()
            : `Failed to revert amendment (HTTP ${res.status})`;
        if (res.status === 409 || res.status === 412) {
          return {
            ok: false,
            error: errMsg,
            ...(errCode ? { code: errCode } : {}),
            conflict: true,
          };
        }
        return {
          ok: false,
          error: errMsg,
          ...(errCode ? { code: errCode } : {}),
        };
      }

      if (
        !body ||
        body.ok !== true ||
        body.action !== 'revert' ||
        body.operationId !== operationId ||
        body.ownerUid !== ownerUid ||
        body.siteId !== site.id ||
        body.sedId !== sedId ||
        typeof body.applied !== 'boolean' ||
        !body.amendment ||
        typeof body.amendment !== 'object' ||
        !Array.isArray(body.amendments) ||
        typeof body.historyVersion !== 'number' ||
        typeof body.sedVersion !== 'number' ||
        typeof body.sedStoragePath !== 'string' ||
        !body.sedStoragePath.trim() ||
        typeof body.sedStorageGeneration !== 'string' ||
        !body.sedStorageGeneration.trim()
      ) {
        return {
          ok: false,
          error: 'Malformed or mismatched SED revert response from server.',
        };
      }

      const committedStoragePath = body.sedStoragePath.trim();
      const committedGeneration = body.sedStorageGeneration.trim();
      const committedVersion = body.sedVersion;
      const committedHistoryVersion = body.historyVersion;
      const committedRevert = body.amendment as import('../types/sedAmendments').SEDAmendment;
      const effectiveAmendments = body.amendments as import('../types/sedAmendments').SEDAmendment[];
      const isIdempotentReplay = body.idempotentReplay === true;
      const returnedSed =
        body.sed && typeof body.sed === 'object'
          ? (body.sed as import('../types/siteEvidence').SiteEvidenceDocument)
          : null;

      if (body.applied) {
        if (
          !returnedSed ||
          returnedSed.id !== sedId ||
          returnedSed.version !== committedVersion ||
          typeof body.sedContentHash !== 'string' ||
          !body.sedContentHash.trim()
        ) {
          return {
            ok: false,
            error: 'Malformed applied SED revert response: missing authoritative SED payload or content hash.',
          };
        }
      }

      clearPendingOpIfTerminal();

      const sectionHashes: Record<string, string> = {};
      for (const [k, v] of Object.entries(body)) {
        if (k.startsWith('sedSectionHash_') && typeof v === 'string') {
          sectionHashes[k] = v;
        }
      }

      const inspectRevertLocalState = () => {
        const actorMatches = auth.currentUser?.uid === initiatingUid;
        const localSite = get().siteRag.sites.find((s) => s.id === site.id) ?? null;
        const ownerMatches = Boolean(
          localSite && siteOwnerUid(localSite, initiatingUid) === ownerUid,
        );
        const sameSed = Boolean(
          actorMatches && ownerMatches && localSite && localSite.sedId === sedId,
        );
        const ptrVer =
          sameSed && typeof localSite?.sedVersion === 'number' ? localSite.sedVersion : -1;
        const histVer =
          sameSed && typeof localSite?.sedAmendmentHistoryVersion === 'number'
            ? localSite.sedAmendmentHistoryVersion
            : -1;
        const equalVerGenMismatch = Boolean(
          sameSed &&
            ptrVer === committedVersion &&
            typeof localSite?.sedStorageGeneration === 'string' &&
            localSite.sedStorageGeneration.trim().length > 0 &&
            localSite.sedStorageGeneration.trim() !== committedGeneration,
        );
        return {
          actorMatches,
          ownerMatches,
          localSite,
          sameSed,
          ptrVer,
          histVer,
          equalVerGenMismatch,
          hasNewerPointer:
            !sameSed ||
            ptrVer > committedVersion ||
            equalVerGenMismatch ||
            localSite?.sedStatus === 'bootstrapping' ||
            localSite?.sedBootstrapLockStatus === 'locked',
          hasNewerHistory: !sameSed || histVer > committedHistoryVersion,
        };
      };

      const preRevertLocal = inspectRevertLocalState();
      if (!preRevertLocal.actorMatches || !preRevertLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment revert operation.',
        };
      }
      const needsAuthoritativeRevertRead =
        isIdempotentReplay || preRevertLocal.hasNewerPointer || preRevertLocal.hasNewerHistory;

      let authoritativeServerSite: Record<string, unknown> | null = null;
      if (needsAuthoritativeRevertRead || !body.applied) {
        try {
          const firestoreModule = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const siteDocRef = firestoreModule.doc(db, `users/${ownerUid}/sites`, site.id);
          const getServerDoc =
            typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer ===
            'function'
              ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc })
                  .getDocFromServer
              : firestoreModule.getDoc;
          if (typeof getServerDoc === 'function') {
            const snap = await getServerDoc(siteDocRef);
            if (snap && typeof snap.exists === 'function' && snap.exists()) {
              authoritativeServerSite = (snap.data() ?? null) as Record<string, unknown> | null;
            }
          }
        } catch {
          authoritativeServerSite = null;
        }
      }

      const afterDocRevertLocal = inspectRevertLocalState();
      if (!afterDocRevertLocal.actorMatches || !afterDocRevertLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment revert operation.',
        };
      }

      if (needsAuthoritativeRevertRead && !authoritativeServerSite) {
        return {
          ok: false,
          error:
            'Amendment revert operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
          code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
          operationConfirmed: true,
          refreshRequired: true,
          ...(isIdempotentReplay ? { idempotent: true } : {}),
          sedVersion: committedVersion,
        };
      }

      const serverSameSed = authoritativeServerSite
        ? authoritativeServerSite.sedId === sedId
        : !needsAuthoritativeRevertRead;
      const serverPointerVersion =
        serverSameSed && typeof authoritativeServerSite?.sedVersion === 'number'
          ? (authoritativeServerSite.sedVersion as number)
          : -1;
      const serverEqualVerGenMismatch = Boolean(
        serverSameSed &&
          serverPointerVersion === committedVersion &&
          typeof authoritativeServerSite?.sedStorageGeneration === 'string' &&
          authoritativeServerSite.sedStorageGeneration.trim().length > 0 &&
          authoritativeServerSite.sedStorageGeneration.trim() !== committedGeneration,
      );

      let loadedAuthoritativeHeadSed: import('../types/siteEvidence').SiteEvidenceDocument | null =
        null;
      if (
        (!serverSameSed ||
          !afterDocRevertLocal.sameSed ||
          Math.max(serverPointerVersion, afterDocRevertLocal.ptrVer) > committedVersion ||
          serverEqualVerGenMismatch ||
          afterDocRevertLocal.equalVerGenMismatch) &&
        serverSameSed &&
        (serverPointerVersion > committedVersion || serverEqualVerGenMismatch) &&
        typeof authoritativeServerSite?.sedStoragePath === 'string' &&
        authoritativeServerSite.sedStoragePath.trim()
      ) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          loadedAuthoritativeHeadSed = await loadSedById(
            sedId,
            authoritativeServerSite.sedStoragePath.trim(),
            serverPointerVersion,
          );
        } catch {
          loadedAuthoritativeHeadSed = null;
        }
      }

      const beforeCacheRevertLocal = inspectRevertLocalState();
      if (!beforeCacheRevertLocal.actorMatches || !beforeCacheRevertLocal.ownerMatches) {
        return {
          ok: false,
          error: 'The signed-in account or site owner changed during the amendment revert operation.',
        };
      }
      const currentLocalSite = beforeCacheRevertLocal.localSite ?? site;
      const localSameSed = beforeCacheRevertLocal.sameSed;
      const localPointerVersion = beforeCacheRevertLocal.ptrVer;
      const maxExistingPointerVersion = Math.max(serverPointerVersion, localPointerVersion);
      const pointerOutdated =
        !serverSameSed ||
        !localSameSed ||
        maxExistingPointerVersion > committedVersion ||
        serverEqualVerGenMismatch ||
        beforeCacheRevertLocal.hasNewerPointer ||
        authoritativeServerSite?.sedStatus === 'bootstrapping' ||
        authoritativeServerSite?.sedBootstrapLockStatus === 'locked';

      if (isIdempotentReplay) {
        const hasAuthoritativeHistory =
          Boolean(authoritativeServerSite) &&
          authoritativeServerSite?.sedId === sedId &&
          typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number' &&
          (authoritativeServerSite.sedAmendmentHistoryVersion as number) >=
            committedHistoryVersion &&
          Array.isArray(authoritativeServerSite?.sedAmendmentHistory);
        const hasAuthoritativePointer = !body.applied
          ? Boolean(authoritativeServerSite) &&
            authoritativeServerSite?.sedId === sedId
          : Boolean(authoritativeServerSite) &&
            authoritativeServerSite?.sedId === sedId &&
            typeof authoritativeServerSite?.sedVersion === 'number' &&
            (authoritativeServerSite.sedVersion as number) >= committedVersion &&
            typeof authoritativeServerSite?.sedStoragePath === 'string' &&
            authoritativeServerSite.sedStoragePath.trim().length > 0 &&
            ((authoritativeServerSite.sedVersion as number) === committedVersion &&
            !serverEqualVerGenMismatch
              ? true
              : loadedAuthoritativeHeadSed !== null);

        if (!hasAuthoritativeHistory || !hasAuthoritativePointer) {
          return {
            ok: false,
            error:
              'Amendment revert operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
            code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
            operationConfirmed: true,
            refreshRequired: true,
            idempotent: true,
            sedVersion: committedVersion,
          };
        }
      }

      if (body.applied && returnedSed && !pointerOutdated) {
        primeCommittedSedFromServer({
          sed: returnedSed,
          sedId,
          storagePath: committedStoragePath,
          version: committedVersion,
          generation: committedGeneration,
          preserveStoragePaths: currentLocalSite.sedStoragePath
            ? [currentLocalSite.sedStoragePath]
            : [],
        });
      }

      const serverHistoryVersion =
        serverSameSed &&
        typeof authoritativeServerSite?.sedAmendmentHistoryVersion === 'number'
          ? (authoritativeServerSite.sedAmendmentHistoryVersion as number)
          : -1;
      const serverAmendments =
        serverSameSed && Array.isArray(authoritativeServerSite?.sedAmendmentHistory)
          ? (authoritativeServerSite.sedAmendmentHistory as import('../types/sedAmendments').SEDAmendment[])
          : null;
      const localHistoryVersion =
        localSameSed && typeof currentLocalSite.sedAmendmentHistoryVersion === 'number'
          ? currentLocalSite.sedAmendmentHistoryVersion
          : -1;
      const localAmendments =
        localSameSed && Array.isArray(currentLocalSite.sedAmendmentHistory)
          ? currentLocalSite.sedAmendmentHistory
          : null;

      let reconciledHistoryVersion = committedHistoryVersion;
      let reconciledAmendments = effectiveAmendments;
      if (
        serverHistoryVersion > committedHistoryVersion &&
        serverHistoryVersion >= localHistoryVersion &&
        serverAmendments
      ) {
        reconciledHistoryVersion = serverHistoryVersion;
        reconciledAmendments = serverAmendments;
      } else if (localHistoryVersion > committedHistoryVersion && localAmendments) {
        reconciledHistoryVersion = localHistoryVersion;
        reconciledAmendments = localAmendments;
      } else if (
        isIdempotentReplay &&
        serverHistoryVersion === committedHistoryVersion &&
        serverAmendments
      ) {
        reconciledAmendments = serverAmendments;
      }

      const serverSectionHashes: Record<string, string> = {};
      if (authoritativeServerSite) {
        for (const [k, v] of Object.entries(authoritativeServerSite)) {
          if (k.startsWith('sedSectionHash_') && typeof v === 'string') {
            serverSectionHashes[k] = v;
          }
        }
      }

      set((stateNow) => {
        if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
          return stateNow;
        }
        const liveSite = stateNow.siteRag.sites.find((s) => s.id === site.id);
        if (!liveSite || siteOwnerUid(liveSite, initiatingUid) !== ownerUid) {
          return stateNow;
        }
        const liveSameSed =
          (!liveSite.sedId || liveSite.sedId === sedId) &&
          serverSameSed &&
          liveSite.sedStatus !== 'bootstrapping' &&
          liveSite.sedBootstrapLockStatus !== 'locked' &&
          authoritativeServerSite?.sedStatus !== 'bootstrapping' &&
          authoritativeServerSite?.sedBootstrapLockStatus !== 'locked';
        const liveHistoryVersion =
          liveSameSed && typeof liveSite.sedAmendmentHistoryVersion === 'number'
            ? liveSite.sedAmendmentHistoryVersion
            : -1;
        const nextTopLevelAmendments = !liveSameSed
          ? stateNow.amendmentState.amendments
          : [
              ...(liveHistoryVersion > reconciledHistoryVersion &&
              Array.isArray(liveSite.sedAmendmentHistory)
                ? liveSite.sedAmendmentHistory
                : reconciledAmendments),
              ...stateNow.amendmentState.amendments.filter((a) => a.sedId !== sedId),
            ];

        return {
          siteRag: {
            ...stateNow.siteRag,
            sites: stateNow.siteRag.sites.map((st) => {
              if (st.id !== site.id) return st;
              if (siteOwnerUid(st, initiatingUid) !== ownerUid) return st;

              const stSameSed = !st.sedId || st.sedId === sedId;
              const stBusy =
                st.sedStatus === 'bootstrapping' ||
                st.sedBootstrapLockStatus === 'locked' ||
                authoritativeServerSite?.sedStatus === 'bootstrapping' ||
                authoritativeServerSite?.sedBootstrapLockStatus === 'locked';
              if (!stSameSed || !serverSameSed || stBusy) {
                return st;
              }
              const stPointerVersion =
                typeof st.sedVersion === 'number' ? st.sedVersion : -1;
              const stHistoryVersion =
                typeof st.sedAmendmentHistoryVersion === 'number'
                  ? st.sedAmendmentHistoryVersion
                  : -1;

              const nextHistoryVersion =
                stHistoryVersion > reconciledHistoryVersion &&
                Array.isArray(st.sedAmendmentHistory)
                  ? stHistoryVersion
                  : reconciledHistoryVersion;
              const nextAmendments =
                stHistoryVersion > reconciledHistoryVersion &&
                Array.isArray(st.sedAmendmentHistory)
                  ? st.sedAmendmentHistory
                  : reconciledAmendments;

              const stEqualVerGenMismatch =
                stPointerVersion === committedVersion &&
                typeof st.sedStorageGeneration === 'string' &&
                st.sedStorageGeneration.trim().length > 0 &&
                st.sedStorageGeneration.trim() !== committedGeneration;

              let pointerPatch: Record<string, unknown> = {};
              if (
                (stPointerVersion > committedVersion &&
                  stPointerVersion >= serverPointerVersion) ||
                (stEqualVerGenMismatch &&
                  stPointerVersion >= serverPointerVersion &&
                  !serverEqualVerGenMismatch)
              ) {
                pointerPatch = {};
              } else if (
                (serverPointerVersion > committedVersion || serverEqualVerGenMismatch) &&
                authoritativeServerSite &&
                typeof authoritativeServerSite.sedStoragePath === 'string' &&
                authoritativeServerSite.sedStoragePath.trim()
              ) {
                pointerPatch = {
                  sedVersion: serverPointerVersion,
                  sedStoragePath: authoritativeServerSite.sedStoragePath.trim(),
                  ...(typeof authoritativeServerSite.sedStorageGeneration === 'string' &&
                  authoritativeServerSite.sedStorageGeneration.trim()
                    ? {
                        sedStorageGeneration:
                          authoritativeServerSite.sedStorageGeneration.trim(),
                      }
                    : {}),
                  ...(typeof authoritativeServerSite.sedContentHash === 'string'
                    ? { sedContentHash: authoritativeServerSite.sedContentHash }
                    : {}),
                  ...(typeof authoritativeServerSite.sedContentHashVersion === 'number'
                    ? {
                        sedContentHashVersion:
                          authoritativeServerSite.sedContentHashVersion,
                      }
                    : {}),
                  ...(typeof authoritativeServerSite.sedCollectedAt === 'string'
                    ? {
                        sedCollectedAt: authoritativeServerSite.sedCollectedAt,
                        sedComputedAt: authoritativeServerSite.sedCollectedAt,
                      }
                    : {}),
                  ...serverSectionHashes,
                };
              } else if (pointerOutdated) {
                pointerPatch = {};
              } else if (body.applied && returnedSed) {
                pointerPatch = {
                  sedVersion: committedVersion,
                  sedStoragePath: committedStoragePath,
                  sedStorageGeneration: committedGeneration,
                  sedContentHash: body.sedContentHash as string,
                  sedContentHashVersion:
                    typeof body.sedContentHashVersion === 'number'
                      ? body.sedContentHashVersion
                      : committedVersion,
                  sedCollectedAt: returnedSed.collectedAt,
                  sedComputedAt: returnedSed.collectedAt,
                  ...sectionHashes,
                  updatedAt: committedRevert.appliedAt ?? new Date().toISOString(),
                };
              } else if (stPointerVersion < committedVersion) {
                pointerPatch = {
                  sedVersion: committedVersion,
                  sedStoragePath: committedStoragePath,
                  sedStorageGeneration: committedGeneration,
                };
              }

              return {
                ...st,
                ...pointerPatch,
                sedAmendmentHistory: nextAmendments,
                sedAmendmentHistoryVersion: nextHistoryVersion,
                ...(nextAmendments.some((a) => a.status === 'applied')
                  ? { lastKnownAmendmentsSourceSedId: sedId }
                  : {}),
              };
            }),
          },
          amendmentState: {
            ...stateNow.amendmentState,
            amendments: nextTopLevelAmendments,
          },
        };
      });

      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'sed.amendment_reverted',
          target: { type: 'site', id: siteId, label: site.name },
          before: { value: activeEnvelope.lineage.latestNewValue },
          after: { value: activeEnvelope.lineage.originalProviderValue },
          metadata: {
            sedId,
            variablePath,
            variableLabel: activeEnvelope.lineage.earliestVariableLabel,
            revertAmendmentId: committedRevert.id,
            supersededIds: body.applied ? activeEnvelope.lineage.supersededIds : [],
            originalAmendmentId: activeEnvelope.lineage.earliestId,
          },
        });
      } catch (auditErr) {
        console.debug('[revertSedAmendment] audit emit failed:', (auditErr as Error)?.message);
      }

      return {
        ok: true,
        applied: body.applied,
        ...(isIdempotentReplay ? { idempotent: true } : {}),
        restoredValue: committedRevert.newValue ?? activeEnvelope.lineage.originalProviderValue,
        supersededIds: body.applied ? activeEnvelope.lineage.supersededIds : [],
        revertAmendmentId: committedRevert.id,
      };
    } catch (e) {
      if (e instanceof SiteLockedError) throw e;
      const err = (e as Error)?.message || 'Unknown error';
      console.error('Failed to revert amendment:', e);
      return { ok: false, error: err };
    }
  },

  createSEDRevision: async (_sedId, _reportId, _comments) => {
    const err =
      'Direct client SED revision creation is deprecated and disabled; use the server-owned /api/sites/:siteId/sed-amendments workflow.';
    console.error('[createSEDRevision]', err);
    set((state) => ({
      amendmentState: {
        ...state.amendmentState,
        amendmentError: err,
      },
    }));
    throw new Error(err);
  },

  regenerateFromAmendedSED: async (reportId, sedRevisionId) => {
    set((state) => ({ amendmentState: { ...state.amendmentState, isRegenerating: true } }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        set((state) => ({ amendmentState: { ...state.amendmentState, isRegenerating: false } }));
        return;
      }

      // Find the revision and original report
      const revision = get().amendmentState.revisions.find(r => r.id === sedRevisionId);
      if (!revision) throw new Error('Revision not found');
      const originalReport = get().siteRag.reports.find(r => r.id === reportId);
      if (!originalReport) throw new Error('Original report not found');

      if (originalReport.gridLedStandbyPlanningAtGen) throw new Error('Planning report revisions require normal owner-scoped server generation against current sources; the amended-SED browser path cannot certify this basis.');
      const amendedSed = revision.snapshot;
      const { SiteRagService: SRS } = await import('../services/siteRagService');

      // LK pack — resolve siteLayers from the underlying site entity so
      // KMZ-imported overlays reach the legacy report path too. Sites
      // pre-KMZ-import have no siteLayers and fall through silently.
      const reportSite = originalReport.siteId
        ? get().siteRag.sites.find(s => s.id === originalReport.siteId)
        : undefined;

      if (reportSite?.generationAssessmentSelection?.kind === 'grid-led-standby-planning') throw new Error('Selected planning report revisions require the normal owner-scoped server generation workflow.');

      // On-site Test Fit may never use the legacy amended-SED generator: that
      // path accepts mutable live layers and has no pre-prompt authority
      // contract. Only an amendment already promoted to the site's canonical
      // SED identity can rerun, and it goes through the shared typed pipeline
      // with the exact expected authority tuple.
      if (originalReport.reportType === 'test-fit' && reportSite) {
        const { evaluatePersistedTestFitWorkflow } = await import('../lib/generationImpact/testFitWorkflow');
        const amendedTestFitReadiness = evaluatePersistedTestFitWorkflow(
          reportSite,
          amendedSed as unknown as import('../types/siteEvidence').SiteEvidenceDocument,
        );
        if (amendedTestFitReadiness.generationImpactRequired) {
          if (!amendedTestFitReadiness.ready || !amendedTestFitReadiness.authority) {
            throw new Error(
              `Test Fit amendment cannot run until its SED is rebuilt — ${amendedTestFitReadiness.title}: ${amendedTestFitReadiness.message}`,
            );
          }
          const { getRerunDefaults } = await import('../lib/rerunDefaults');
          const defaults = getRerunDefaults(originalReport);
          const documentSelection = defaults.documentRefs?.length
            ? {
                documentRefs: defaults.documentRefs,
                documentSelectionMode: 'mixed' as const,
              }
            : undefined;
          await get().generateTypedReport(
            reportSite.id,
            'test-fit',
            defaults.typedPreferences,
            'update',
            originalReport.id,
            defaults.archetypeOverride,
            documentSelection,
            undefined,
            { expectedTestFitAuthorityRef: amendedTestFitReadiness.authority },
          );
          set((state) => ({
            amendmentState: { ...state.amendmentState, isRegenerating: false },
          }));
          return;
        }
      }

      // PH-A2.11 — resolve documents from site.documents filtered by
      // report.documentRefs (post-IC source of truth) instead of the
      // deprecated inline report.documents. Legacy reports without
      // documentRefs fall through to the inline list for back-compat
      // so regenerations against pre-docRef reports still ship the
      // same evidence set as the original generation.
      let resolvedDocs: import('../types/siteRag').SiteDocument[] | undefined;
      if (originalReport.documentRefs?.length && reportSite) {
        const byId = new Map((reportSite.documents ?? []).map(d => [d.id, d]));
        resolvedDocs = originalReport.documentRefs
          .filter(r => r.userIncluded)
          .map(r => byId.get(r.docId))
          .filter((d): d is import('../types/siteRag').SiteDocument => Boolean(d));
      } else {
        resolvedDocs = originalReport.documents;
      }

      // Run the FULL pipeline on amended evidence — recalculates scores, Go/No-Go, AI analysis
      const amendedReport = await SRS.getInstance().generateReport(
        originalReport.siteName,
        originalReport.location,
        originalReport.address,
        'standard',
        originalReport.siteBoundary,
        originalReport.siteArea,
        (amendedSed as Record<string, any>).amendments?.map((a: any) => ({ field: a.field, originalValue: String(a.originalValue), editedValue: String(a.correctedValue) })),
        get().userApiKeys,
        undefined, // use all providers
        (amendedSed as Record<string, any>).projectIntent,
        originalReport.sitePerimeter,
        originalReport.siteCentroid,
        originalReport.siteElevationRange,
        resolvedDocs,
        undefined, // onProgress
        undefined, // typedPreferences
        undefined, // regenerationReason
        reportSite?.siteLayers, // LK pack — KMZ-imported overlays
        // SOC — channel the site-overview inputs into the regenerated
        // SED so this legacy regeneration path produces a SOC-complete
        // SED (matching the new bootstrap path).
        reportSite?.siteContext,
        reportSite?.financialAssumptions,
      );

      const newReport = {
        ...amendedReport,
        id: 'report-' + Date.now(),
        regeneratedFrom: reportId,
        sedRevisionVersion: revision.version,
        amendmentCount: revision.amendmentCount,
      } as import('../types/siteRag').SiteRagReport;

      const { resolvePromotedGenerationScenario } = await import('../lib/generationImpact/promotedScenario');
      const amendedGenerationScenario = resolvePromotedGenerationScenario(
        reportSite?.projectIntent,
        reportSite?.promotedGenerationScenarioSnapshot,
        (amendedSed as unknown as import('../types/siteEvidence').SiteEvidenceDocument).promotedGenerationScenarioSnapshot,
        reportSite?.generationAssessmentSelection,
        reportSite?.generationProjectInputBinding,
      );
      const { stampGenerationImpactOntoReport } = await import('../lib/generationImpact/reportProjection');
      const { requireGenerationImpactTestFitAuthority } = await import('../lib/generationImpact/testFitProjection');
      const {
        assessGenerationImpactTestFitApplicability,
        generationImpactTestFitScopeFromSite,
      } = await import('../lib/generationImpact/testFitApplicability');
      const amendedApplicability = reportSite && originalReport.reportType === 'test-fit'
        ? assessGenerationImpactTestFitApplicability(
            generationImpactTestFitScopeFromSite(reportSite),
          )
        : null;
      const amendedAuthority =
        reportSite
        && originalReport.reportType === 'test-fit'
        && amendedApplicability?.required
          ? requireGenerationImpactTestFitAuthority(
              reportSite,
              amendedGenerationScenario,
              amendedSed as unknown as import('../types/siteEvidence').SiteEvidenceDocument,
            )
          : null;
      const amendedTestFitWithoutGenerationImpact =
        amendedApplicability && !amendedApplicability.required
          ? (amendedApplicability.status === 'grid-only'
              ? 'grid-only' as const
              : 'undecided' as const)
          : undefined;
      stampGenerationImpactOntoReport(
        newReport,
        (originalReport.reportType ?? 'feasibility') as import('../config/reportTypes').ReportTypeId,
        originalReport.reportType === 'test-fit' && amendedTestFitWithoutGenerationImpact
          ? null
          : amendedAuthority?.scenarioSnapshot ?? amendedGenerationScenario,
        {
          site: reportSite,
          sed: amendedSed as unknown as import('../types/siteEvidence').SiteEvidenceDocument,
          authority: amendedAuthority,
          ...(amendedTestFitWithoutGenerationImpact
            ? { testFitWithoutGenerationImpact: amendedTestFitWithoutGenerationImpact }
            : {}),
        },
      );

      // Save to GCS + Firestore metadata (same pattern as generateSiteRagReport)
      const { doc: firestoreDoc, setDoc } = await import('firebase/firestore');
      const cleanNew = JSON.parse(JSON.stringify(newReport, (_, v) => v === undefined ? null : v));
      const regenStoragePath = `users/${user.uid}/reports/${newReport.id}.json`;
      // #18 — org-visibility stamp so a REGENERATED report stays visible to the
      // site's company tree (Clayco/Azimuth + parent Oakland), not just the
      // owner. Mirrors the persist path; anchors on the site's companyId with the
      // user's own companyId as fallback. Without this the regen path reintroduced
      // the owner-only gap the server writer fix closes.
      const regenOrgStamp = (await import('../lib/siteCompany')).resolveOrgStamp(reportSite?.companyId, get().companyId);
      try {
        const gcsRes = await authedFetch('/api/storage/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: regenStoragePath, data: cleanNew }) });
        if (gcsRes.ok) {
          await setDoc(firestoreDoc(db, `users/${user.uid}/reports`, newReport.id), {
            id: newReport.id, siteName: newReport.siteName, address: newReport.address,
            overallScore: newReport.overallScore, overallStatus: newReport.overallStatus,
            createdAt: newReport.createdAt, storagePath: regenStoragePath,
            regeneratedFrom: reportId, sedRevisionVersion: revision.version,
            ...(newReport.generationImpactProjection ? {
              generationImpactScenarioId: newReport.generationImpactProjection.source.scenarioId,
              generationImpactInputHash: newReport.generationImpactProjection.source.inputHash,
              generationImpactResultHash: newReport.generationImpactProjection.source.resultHash,
              ...(newReport.generationImpactProjection.source.layoutPublicationId
                ? { generationImpactLayoutPublicationId: newReport.generationImpactProjection.source.layoutPublicationId }
                : {}),
              ...(newReport.generationImpactProjection.source.layoutVersion != null
                ? { generationImpactLayoutVersion: newReport.generationImpactProjection.source.layoutVersion }
                : {}),
              ...(newReport.generationImpactProjection.source.layoutHash
                ? { generationImpactLayoutHash: newReport.generationImpactProjection.source.layoutHash }
                : {}),
              ...(newReport.generationImpactProjection.source.sedAuthorityContractVersion != null
                ? { generationImpactSedAuthorityContractVersion: newReport.generationImpactProjection.source.sedAuthorityContractVersion }
                : {}),
              ...(newReport.generationImpactProjection.source.parcelBoundarySchemaVersion != null
                ? { generationImpactParcelBoundarySchemaVersion: newReport.generationImpactProjection.source.parcelBoundarySchemaVersion }
                : {}),
              ...(newReport.generationImpactProjection.source.parcelBoundarySource
                ? { generationImpactParcelBoundarySource: newReport.generationImpactProjection.source.parcelBoundarySource }
                : {}),
              ...(newReport.generationImpactProjection.source.parcelBoundaryHash
                ? { generationImpactParcelBoundaryHash: newReport.generationImpactProjection.source.parcelBoundaryHash }
                : {}),
              ...(newReport.generationImpactProjection.source.parcelBoundaryAreaAcres != null
                ? { generationImpactParcelBoundaryAreaAcres: newReport.generationImpactProjection.source.parcelBoundaryAreaAcres }
                : {}),
            } : {}),
            ...regenOrgStamp,
          });
        } else {
          await setDoc(firestoreDoc(db, `users/${user.uid}/reports`, newReport.id), cleanNew);
        }
      } catch {
        await setDoc(firestoreDoc(db, `users/${user.uid}/reports`, newReport.id), cleanNew);
      }

      set((state) => ({
        siteRag: {
          ...state.siteRag,
          reports: [newReport as any, ...state.siteRag.reports].slice(0, MAX_REPORTS),
          activeReportId: newReport.id,
        },
        amendmentState: { ...state.amendmentState, isRegenerating: false },
      }));

      // Dashboard_Zero_Report_Filter_Fix ZR2 — keep site.reportIds in
      // sync for the regenerated report too. Idempotent; no-op when the
      // report carries no siteId or the array already lists it.
      const regenSiteId = (newReport as { siteId?: string }).siteId;
      if (regenSiteId) {
        // COMPOSITE STALE-ON-REPORT-WRITE — a regenerated report can shift the
        // site's overview verdict / decision band; invalidate this site's
        // cached composite so the lazy ensurePortfolioComposites pass refreshes
        // it on next view. Invalidation only (no scoring change); idempotent.
        get()._staleStampSiteComposite(regenSiteId);
        const parentSite = get().siteRag.sites.find(s => s.id === regenSiteId);
        if (parentSite && !(parentSite.reportIds ?? []).includes(newReport.id)) {
          try {
            await get().updateSite(regenSiteId, { reportIds: [...(parentSite.reportIds ?? []), newReport.id] });
          } catch (e) {
            console.warn('[regenerateReport] reportIds sync failed:', (e as Error)?.message);
          }
        }
      }
    } catch (e) {
      set((state) => ({ amendmentState: { ...state.amendmentState, isRegenerating: false, amendmentError: (e as Error).message } }));
    }
  },

  // ═══ Approval Workflow ═══
  approvalWorkflow: {
    activeWorkflow: null,
    isSubmitting: false,
    isLoading: false,
    isActing: false,
    workflowError: null,
  },

  submitForReview: async (reportId, sedId, reportTitle) => {
    set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, isSubmitting: true, workflowError: null } }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const { companyId, companyRole, companyMembers } = get();
      if (!companyId) throw new Error('Not in a company');

      // Find reviewers and approvers in the company
      const reviewers = companyMembers.filter(m => ['reviewer', 'approver', 'admin'].includes(m.companyRole));
      const approvers = companyMembers.filter(m => ['approver', 'admin'].includes(m.companyRole));

      if (reviewers.length === 0) throw new Error('No reviewers available in your company');

      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const now = new Date().toISOString();
      const workflow: import('../types/approvalWorkflow').ApprovalWorkflow = {
        id: reportId,
        companyId,
        reportId,
        sedId,
        reportTitle,
        currentStageIndex: 0,
        stages: [
          {
            stage: 'reviewer',
            requiredRole: 'reviewer',
            status: 'pending',
            assignedTo: reviewers.map(m => m.uid),
            assignedNames: reviewers.map(m => m.displayName || m.email),
          },
          {
            stage: 'approver',
            requiredRole: 'approver',
            status: 'pending',
            assignedTo: approvers.length > 0 ? approvers.map(m => m.uid) : reviewers.map(m => m.uid),
            assignedNames: approvers.length > 0 ? approvers.map(m => m.displayName || m.email) : reviewers.map(m => m.displayName || m.email),
          },
        ],
        status: 'in-review',
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(doc(db, 'approval_workflows', reportId), workflow);
      set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, activeWorkflow: workflow, isSubmitting: false } }));

      // Notify all reviewers in the first stage (best-effort, don't block on failure)
      const { createNotification: notify } = get();
      const firstStage = workflow.stages[0];
      const notifPromises = firstStage.assignedTo
        .filter(uid => uid !== user.uid)
        .map(uid => notify(uid, {
          type: 'approval-requested',
          title: 'Review Requested',
          message: `${user.displayName || user.email} submitted "${reportTitle}" for your review.`,
          reportId,
          reportTitle,
          fromUid: user.uid,
          fromName: user.displayName || user.email || 'Unknown',
        }));
      await Promise.allSettled(notifPromises);
    } catch (e) {
      set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, isSubmitting: false, workflowError: (e as Error).message } }));
      throw e;
    }
  },

  loadApprovalWorkflow: async (reportId) => {
    set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, isLoading: true } }));
    try {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'approval_workflows', reportId));
      if (snap.exists()) {
        set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, activeWorkflow: snap.data() as import('../types/approvalWorkflow').ApprovalWorkflow, isLoading: false } }));
      } else {
        set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, activeWorkflow: null, isLoading: false } }));
      }
    } catch (e) {
      // Permission-denied is the expected path for users without a companyId
      // claim (the workflow doc is company-scoped). Log at debug so the
      // console stays quiet for solo users; warn on anything else so real
      // bugs don't go unnoticed.
      if (isPermissionDenied(e) && !get().companyId) {
        console.debug('[loadApprovalWorkflow] permission denied (no companyId claim) — expected for solo users');
      } else if (!isPermissionDenied(e)) {
        console.warn('[loadApprovalWorkflow] failed:', e);
      }
      set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, activeWorkflow: null, isLoading: false } }));
    }
  },

  actOnApprovalStage: async (reportId, action, comments) => {
    set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, isActing: true, workflowError: null } }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const { doc, getDoc, updateDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'approval_workflows', reportId));
      if (!snap.exists()) throw new Error('Workflow not found');
      const workflow = snap.data() as import('../types/approvalWorkflow').ApprovalWorkflow;

      // Idempotency: prevent acting on already-completed workflows
      if (workflow.status !== 'in-review') throw new Error(`Workflow is ${workflow.status}, not in review`);

      const stageIdx = workflow.currentStageIndex;
      const stage = workflow.stages[stageIdx];
      if (!stage) throw new Error('No active stage');
      if (stage.status !== 'pending') throw new Error('Stage already completed');

      // Check user is assigned to this stage
      if (!stage.assignedTo.includes(user.uid)) {
        // Also allow global admin
        const { companyRole } = get();
        if (companyRole !== 'admin') throw new Error('You are not assigned to this approval stage');
      }

      const now = new Date().toISOString();
      const updatedStages = [...workflow.stages];
      updatedStages[stageIdx] = {
        ...stage,
        status: action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'changes-requested',
        completedBy: user.uid,
        completedByName: user.displayName || user.email || 'Unknown',
        completedAt: now,
        comments,
      };

      let newStatus: 'in-review' | 'approved' | 'rejected' | 'changes-requested' = workflow.status;
      let newStageIndex = stageIdx;
      let finalApprovedAt: string | undefined;
      let finalApprovedBy: string | undefined;
      let finalApprovedByName: string | undefined;

      if (action === 'approve') {
        // Move to next stage or finalize
        if (stageIdx < workflow.stages.length - 1) {
          newStageIndex = stageIdx + 1;
          newStatus = 'in-review';
        } else {
          newStatus = 'approved';
          finalApprovedAt = now;
          finalApprovedBy = user.uid;
          finalApprovedByName = user.displayName || user.email || 'Unknown';
        }
      } else if (action === 'reject') {
        newStatus = 'rejected';
      } else {
        newStatus = 'changes-requested';
      }

      const updates: Record<string, any> = {
        stages: updatedStages,
        currentStageIndex: newStageIndex,
        status: newStatus,
        updatedAt: now,
      };
      if (finalApprovedAt) {
        updates.finalApprovedAt = finalApprovedAt;
        updates.finalApprovedBy = finalApprovedBy;
        updates.finalApprovedByName = finalApprovedByName;
      }

      await updateDoc(doc(db, 'approval_workflows', reportId), updates);

      const updatedWorkflow: import('../types/approvalWorkflow').ApprovalWorkflow = {
        ...workflow,
        stages: updatedStages,
        currentStageIndex: newStageIndex,
        status: newStatus,
        updatedAt: now,
        finalApprovedAt,
        finalApprovedBy,
        finalApprovedByName,
      };
      set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, activeWorkflow: updatedWorkflow, isActing: false } }));

      // Dispatch notifications (best-effort, don't block on failure)
      const { createNotification: notify2 } = get();
      const actorName = user.displayName || user.email || 'Unknown';
      const reportTitle = workflow.reportTitle;
      const nPromises: Promise<void>[] = [];

      if (action === 'approve' && newStatus === 'approved') {
        nPromises.push(notify2(workflow.createdBy, {
          type: 'approval-final',
          title: 'Report Approved',
          message: `Your report "${reportTitle}" has received final approval from ${actorName}.`,
          reportId, reportTitle,
          fromUid: user.uid, fromName: actorName,
        }));
      } else if (action === 'approve' && newStageIndex > stageIdx) {
        nPromises.push(notify2(workflow.createdBy, {
          type: 'approval-approved',
          title: 'Stage Approved',
          message: `${actorName} approved the ${stage.stage} stage of "${reportTitle}". Moving to ${updatedStages[newStageIndex].stage}.`,
          reportId, reportTitle,
          fromUid: user.uid, fromName: actorName,
        }));
        const nextStage = updatedStages[newStageIndex];
        for (const uid of nextStage.assignedTo) {
          if (uid !== user.uid) {
            nPromises.push(notify2(uid, {
              type: 'approval-requested',
              title: 'Review Requested',
              message: `"${reportTitle}" is ready for your ${nextStage.stage} review.`,
              reportId, reportTitle,
              fromUid: user.uid, fromName: actorName,
            }));
          }
        }
      } else if (action === 'reject') {
        nPromises.push(notify2(workflow.createdBy, {
          type: 'approval-rejected',
          title: 'Report Rejected',
          message: `${actorName} rejected "${reportTitle}" at the ${stage.stage} stage.${comments ? ` Comment: ${comments}` : ''}`,
          reportId, reportTitle,
          fromUid: user.uid, fromName: actorName,
        }));
      } else if (action === 'request-changes') {
        nPromises.push(notify2(workflow.createdBy, {
          type: 'approval-changes',
          title: 'Changes Requested',
          message: `${actorName} requested changes on "${reportTitle}".${comments ? ` Comment: ${comments}` : ''}`,
          reportId, reportTitle,
          fromUid: user.uid, fromName: actorName,
        }));
      }
      await Promise.allSettled(nPromises);
    } catch (e) {
      set((state) => ({ approvalWorkflow: { ...state.approvalWorkflow, isActing: false, workflowError: (e as Error).message } }));
      throw e;
    }
  },

  // ═══ Notifications ═══
  // #110 — toast visibility (driven by useToastVisibility off the sonner DOM).
  toastVisible: false,
  setToastVisible: (visible) => set((state) => (state.toastVisible === visible ? state : { toastVisible: visible })),
  notificationState: {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
  },

  loadNotifications: async () => {
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { collection, getDocs } = await import('firebase/firestore');
      // Simple query without orderBy (avoids index requirement)
      const snap = await getDocs(collection(db, `users/${user.uid}/notifications`));
      const notifications = snap.docs
        .map(d => ({ id: d.id, ...d.data() }) as import('../types/notification').AppNotification)
        .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
        .slice(0, 50);
      set({
        notificationState: {
          notifications,
          unreadCount: notifications.filter(n => !n.read).length,
          isLoading: false,
        },
      });
    } catch {
      // Silently fail — notifications are non-critical
      set((state) => ({ notificationState: { ...state.notificationState, isLoading: false } }));
    }
  },

  markNotificationRead: async (notifId) => {
    const prev = get().notificationState;
    const wasUnread = prev.notifications.find(n => n.id === notifId)?.read === false;
    // Optimistic update
    set((state) => ({
      notificationState: {
        ...state.notificationState,
        notifications: state.notificationState.notifications.map(n =>
          n.id === notifId ? { ...n, read: true } : n
        ),
        unreadCount: wasUnread ? Math.max(0, state.notificationState.unreadCount - 1) : state.notificationState.unreadCount,
      },
    }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, `users/${user.uid}/notifications`, notifId), { read: true });
    } catch (e) {
      console.warn('Failed to mark notification read:', e);
      // Rollback
      set({ notificationState: prev });
    }
  },

  markAllNotificationsRead: async () => {
    const prev = get().notificationState;
    const unread = prev.notifications.filter(n => !n.read);
    if (unread.length === 0) return;

    // Optimistic update
    set((state) => ({
      notificationState: {
        ...state.notificationState,
        notifications: state.notificationState.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
      },
    }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, updateDoc } = await import('firebase/firestore');
      const results = await Promise.allSettled(unread.map(n =>
        updateDoc(doc(db, `users/${user.uid}/notifications`, n.id), { read: true })
      ));
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) console.warn(`Failed to mark ${failed}/${unread.length} notifications as read`);
    } catch (e) {
      console.warn('Failed to mark all notifications read:', e);
      // Rollback
      set({ notificationState: prev });
    }
  },

  createNotification: async (targetUid, notification) => {
    // PH-A2.6 — Firestore rules require an authenticated caller for any
    // write under users/{uid}/notifications/. Without a current user the
    // write silently 403s inside the catch, and the caller (e.g.
    // submitForReview, approval-decision paths) gets no feedback. Guard
    // with the same warn-and-return pattern used by other write paths so
    // dropped notifications surface in the console.
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      console.warn('[notification] dropped — no auth');
      return;
    }
    try {
      const { collection, addDoc } = await import('firebase/firestore');
      await addDoc(collection(db, `users/${targetUid}/notifications`), {
        ...notification,
        read: false,
        createdAt: new Date().toISOString(),
      });
    } catch (e) {
      console.warn('Failed to create notification:', e);
    }
  },

  notifyAdmins: async (provider, context) => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const siteRag = get().siteRag;
    const { authedFetch } = await import('../lib/authedFetch');
    const res = await authedFetch('/api/key-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerId: provider.id,
        providerLabel: provider.displayName,
        envVarName: provider.envVarName,
        context: {
          gapReason: context.gapReason,
          reportId: siteRag.activeReportId,
          siteId: siteRag.activeSiteId,
        },
      }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Key request failed (${res.status})`);
    }
  },

  // ═══ Site CRUD ═══
  // X-USER-VIEW — direct single-site fetch for the report viewer (see the
  // interface doc). Bypasses the collectionGroup company sweep entirely.
  ensureReportOwnerSiteLoaded: async (siteId: string, ownerUid: string): Promise<void> => {
    if (!siteId || !ownerUid) return;
    // NB: NO "already loaded" short-circuit. A cross-user (foreign-owned) site
    // has NO live onSnapshot listener in this session — it arrives once via the
    // company sweep / a prior fetch and then goes stale (e.g. the owner rebuilt
    // the SED to v4 'complete' but this session still holds the old 'failed'
    // snapshot). So we ALWAYS re-fetch fresh on report open and OVERWRITE the
    // store copy, so the viewer reflects the owner's current state.
    try {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, `users/${ownerUid}/sites/${siteId}`));
      if (!snap.exists()) {
        console.debug('[ensureReportOwnerSiteLoaded] owner site not found', siteId, ownerUid);
        return;
      }
      const data = normalizeSiteEntity(snap.data() as import('../types/site').SiteEntity);
      // Layers live in a subcollection (the array is a legacy fallback) — load
      // them under the OWNER's path so the map overlay + boundary resolve.
      const resolvedLayers = await loadResolvedClientSiteLayersWithStatus({
        db,
        ownerUid,
        siteId,
        site: data,
        logContext: '[ensureReportOwnerSiteLoaded]',
      });
      const ownCompanyId = get().companyId;
      const site = {
        ...data,
        id: data.id ?? siteId,
        siteLayers: resolvedLayers.layers,
        _siteLayersCanonicalComplete: resolvedLayers.canonicalComplete,
        _siteLayersHydrationPending: false,
        ...(resolvedLayers.hydrationError
          ? { _siteLayersHydrationError: resolvedLayers.hydrationError }
          : {}),
        _ownerUid: ownerUid,
        _sourceScope: (ownCompanyId && data.companyId === ownCompanyId ? 'company' : 'subCompany') as 'company' | 'subCompany',
      } as import('../types/site').SiteEntity;
      // REPLACE-BY-ID (not mergeSitesIntoStore) — that helper returns ONLY the
      // `incoming` set, so passing a single site would DROP every other site
      // from the store. Keep all other sites; swap the stale copy (or append
      // when absent) for the freshly-fetched, authoritative owner doc.
      set((state) => {
        const others = state.siteRag.sites.filter((s) => s.id !== site.id);
        return {
          siteRag: { ...state.siteRag, sites: [site, ...others] },
        };
      });
    } catch (e) {
      console.warn('[ensureReportOwnerSiteLoaded] failed for', siteId, (e as Error)?.message);
    }
  },

  // Wrapped in dedupeFetch so parallel callers (AuthGate + any
  // component-level retry path) share the same in-flight Promise.
  // Cleared once the fetch settles so the next intentional refresh
  // runs through GCS.
  fetchSites: () => dedupeFetch('fetchSites', async () => {
    const sessionSignal = workspaceReadSignal();
    const readStartedWith = get().siteRag.sites;
    let scopeIsCurrent = () => !sessionSignal.aborted;
    // Load sites independently of reports so this is safe to call in parallel
    // with fetchRagReports. The Firestore site doc is the source of truth —
    // createSite/updateSite keep `thumbnailStatus`, `latestReportScore`,
    // `latestReportType` (XS3), and `latestReportId` cached on it, so we
    // don't need reports loaded to render the dashboard sidebar.
    //
    // The legacy derive-from-reports path stays as a fallback for users whose
    // sites pre-date the site-centric architecture (createSite was added later).
    // It only kicks in when Firestore returns zero sites AND reports happen to
    // be loaded at the moment we run.
    //
    // `isSitesLoaded` flips to true in a `finally` so a Firestore hiccup
    // doesn't leave the UI stuck on "Loading sites…" forever.
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        console.debug('[fetchSites] no signed-in user; skipping');
        return;
      }

      const { collection, getDocs } = await import('firebase/firestore');
      const scopeCompanyId = get().companyId;
      scopeIsCurrent = () => !sessionSignal.aborted
        && auth.currentUser?.uid === user.uid && get().companyId === scopeCompanyId;
      if (!scopeIsCurrent()) return;
      const sitesPath = `users/${user.uid}/sites`;
      const companyId = scopeCompanyId;
      type RawCompanySiteRow = {
        id: string;
        ownerUid?: string;
        raw: import('../types/site').SiteEntity;
      };
      const extractLayerResolutionMeta = (
        site: import('../types/site').SiteEntity,
      ): import('../types/site').SiteEntity => ({
        id: site.id,
        siteLayers: site.siteLayers,
        siteLayersCanonicalVersion: site.siteLayersCanonicalVersion,
        siteLayersMirrorComplete: site.siteLayersMirrorComplete,
        siteLayerCount: site.siteLayerCount,
        siteLayersRevision: site.siteLayersRevision,
        generationImpactLayoutHead: site.generationImpactLayoutHead,
        _siteLayersCanonicalComplete: site._siteLayersCanonicalComplete,
      } as import('../types/site').SiteEntity);

      const companyRawDocsPromise: Promise<RawCompanySiteRow[]> = !companyId
        ? Promise.resolve([])
        : (async () => {
            const { collectionGroup, query, where } = await import('firebase/firestore');
            const queryChunksOnce = async (): Promise<RawCompanySiteRow[]> => {
              const subtreeCompanyIds = await resolveSubtreeCompanyIds(db, companyId);
              const chunks = chunkIds(subtreeCompanyIds, 30);
              const perChunk = await Promise.all(
                chunks.map(async (chunk) => {
                  const compQ = query(
                    collectionGroup(db, 'sites'),
                    where('isCompanyShared', '==', true),
                    where('companyId', 'in', chunk),
                  );
                  const compSnap = await getDocs(compQ);
                  return compSnap.docs.map((d) => ({
                    id: d.id,
                    ownerUid: (d as { ref?: { parent?: { parent?: { id?: string } | null } } })
                      .ref?.parent?.parent?.id,
                    raw: d.data() as import('../types/site').SiteEntity,
                  }));
                }),
              );
              return perChunk.flat();
            };

            await refreshClaimIfStale(user);
            let claimRefreshed = false;
            try {
              return await queryChunksOnce();
            } catch (e) {
              if (isPermissionDenied(e) && !claimRefreshed) {
                console.warn('[fetchSites] permission denied on company-shared sites query despite companyId in store — token may be stale; forcing one refresh and retrying.', e);
                claimRefreshed = true;
                const hasClaim = await forceRefreshCompanyClaim(user);
                if (hasClaim) {
                  try {
                    return await queryChunksOnce();
                  } catch (retryErr) {
                    if (isPermissionDenied(retryErr)) {
                      console.warn('[fetchSites] still permission denied after token refresh — claim genuinely absent; serving own sites only.', retryErr);
                    } else {
                      console.warn('[fetchSites] retry of company-shared sites query failed:', (retryErr as Error)?.message);
                    }
                  }
                } else {
                  console.warn('[fetchSites] refreshed token still lacks companyId claim — serving own sites only.');
                }
              } else if (isPermissionDenied(e)) {
                console.warn('[fetchSites] permission denied on company-shared sites query despite companyId in store — token may be stale.', e);
              } else {
                console.warn('[fetchSites] failed to load company-shared sites:', (e as Error)?.message);
              }
              return [];
            }
          })();

      const ownRawRows = await (async () => {
        const snap = await getDocs(collection(db, sitesPath));
        console.debug(`[fetchSites] read ${snap.docs.length} doc(s) from ${sitesPath}`,
          snap.docs.length === 0 ? '(empty — falls back to legacy derive-from-reports)' : snap.docs.map(d => d.id));
        return snap.docs.map((d) => ({
          id: d.id,
          raw: d.data() as import('../types/site').SiteEntity,
        }));
      })();
      const { siteBoundariesNeedReconcile } = await import('../lib/reconcileSiteBoundaryParcels');
      const ownSitesDeferredLayerHydration = new Map<string, import('../types/site').SiteEntity>();
      // Always stamp the Firestore doc id — legacy writes occasionally
      // omitted `id` from the payload; without this merge, URL hydration
      // and site-scoped saves target the wrong doc after reload.
      const persistedSitesRaw: import('../types/site').SiteEntity[] = await mapWithConcurrency(
        ownRawRows,
        4,
        async (d) => {
          // Normalize the raw doc FIRST so legacy sites (predating reportIds /
          // simulationIds / documentIds) carry [] instead of undefined — these
          // fields are typed required but the type lies about legacy runtime
          // presence, and admins iterate these arrays across the whole subtree.
          const data = normalizeSiteEntity(d.raw);
          const siteId = data.id ?? d.id;
          const dup = (data as { duplicateOf?: unknown }).duplicateOf;
          if (typeof dup === 'string' && dup.length > 0) {
            return {
              ...data,
              id: siteId,
            };
          }
          if (
            ((Array.isArray(data.siteBoundaries) && data.siteBoundaries.length > 0) || data.siteBoundary != null)
            && siteBoundariesNeedReconcile(data)
          ) {
            const resolved = await loadResolvedClientSiteLayersWithStatus({
              db,
              ownerUid: user.uid,
              siteId,
              site: data,
              logContext: `[fetchSites] site ${siteId}`,
            });
            return {
              ...data,
              id: siteId,
              siteLayers: resolved.layers,
              _siteLayersCanonicalComplete: resolved.canonicalComplete,
              _siteLayersHydrationPending: false,
              ...(resolved.hydrationError
                ? { _siteLayersHydrationError: resolved.hydrationError }
                : {}),
            };
          }
          const verifiedMirror = isVerifiedCompleteSiteLayerMirror(data);
          if (!verifiedMirror) {
            ownSitesDeferredLayerHydration.set(siteId, extractLayerResolutionMeta(data));
          }
          return {
            ...data,
            id: siteId,
            siteLayers: Array.isArray(data.siteLayers) ? data.siteLayers : [],
            _siteLayersCanonicalComplete: verifiedMirror,
            _siteLayersHydrationPending: !verifiedMirror,
          };
        },
      );
      ownRawRows.length = 0;

      // DUP-1 (Redhead reconciliation, 2026-07-02) — a site doc tombstoned as
      // a duplicate (`duplicateOf` = the canonical doc path under another uid)
      // must not surface in the owner's own list: the canonical copy reaches
      // this user via the company sweep, and rendering both produced the
      // dual-write divergence (edits landing on whichever copy a session
      // resolved). Server-side generation skips tombstoned copies the same
      // way (loadGenerationContext DUP-1).
      const persistedSitesFiltered = persistedSitesRaw.filter((s) => {
        const dup = (s as { duplicateOf?: unknown }).duplicateOf;
        if (typeof dup === 'string' && dup.length > 0) {
          console.debug(`[fetchSites] skipping tombstoned duplicate site ${s.id} (canonical: ${dup})`);
          return false;
        }
        return true;
      });

      // MP11 — lazy single-parcel → multi-parcel migration on read, plus the
      // stale-aggregate / infrastructure-parcel RECONCILE. Sites persisted
      // before MP shipped only have siteBoundary + siteArea / sitePerimeter /
      // siteCentroid; sites persisted before the union aggregate shipped carry
      // a version-1 (summed) aggregate. Both are lifted to the canonical shape
      // here — `planSiteBoundaryRead` holds the decision table.
      //
      // The aggregate (polygon union + pairwise disjoint scan) is the expensive
      // part: it runs in a Web Worker via `computeAggregatesOffMainThread`
      // (main-thread fallback yields between sites), so a 60-parcel site can no
      // longer freeze the dashboard. And the migrated shape is WRITTEN BACK
      // once — `boundaryWriteBacks` is flushed right after the store update
      // below — so the next load plans no work at all. (Until now every load
      // re-ran the migration in memory and never persisted it: on prod that
      // was 22 s of blocked main thread per dashboard visit.)
      //
      // Audit event `site.boundary_migrated_to_multi_parcel` is recorded ONCE
      // per site, gated by the boolean flag on SiteEntity — which the
      // write-back now persists, so the gate finally holds across loads.
      let persistedSites = persistedSitesFiltered;
      const boundaryWriteBacks: import('../services/siteBoundary/siteBoundaryWriteBack').SiteBoundaryWriteBackItem[] = [];
      try {
        const { planSiteBoundaryRead, planNeedsAggregate, buildSiteBoundaryWriteBackPatch } =
          await import('../lib/siteBoundary/siteBoundaryReadMigration');
        const { computeAggregatesOffMainThread } = await import('../lib/siteBoundary/aggregateWorkerClient');
        if (!scopeIsCurrent()) return;
        const plans = persistedSitesFiltered.map((s) => planSiteBoundaryRead(s));
        const work = plans.filter(planNeedsAggregate);
        const aggregates = work.length > 0
          ? await computeAggregatesOffMainThread(
              work.map((plan) => plan.parcels),
              {
                signal: sessionSignal,
                onFallback: (reason) => console.debug(
                  '[fetchSites] boundary aggregate worker unavailable — computing on the main thread:',
                  reason,
                ),
              },
            )
          : [];
        if (!scopeIsCurrent()) return;
        const auditPromises: Promise<void>[] = [];
        let nextAggregate = 0;
        persistedSites = plans.map((plan) => {
          if (!planNeedsAggregate(plan)) return plan.site;
          const aggregate = aggregates[nextAggregate++]!;
          const migrated = plan.apply(aggregate);
          if (plan.kind === 'lift' && !plan.site.boundary_migrated_to_multi_parcel) {
            auditPromises.push(
              (async () => {
                try {
                  const { emitAuditEvent } = await import('../lib/auditLogClient');
                  await emitAuditEvent({
                    action: 'site.boundary_migrated_to_multi_parcel',
                    target: { type: 'site', id: migrated.id, label: migrated.name },
                    metadata: { parcelCount: plan.parcels.length, totalArea: aggregate.totalArea },
                  });
                } catch { /* audit is best-effort */ }
              })(),
            );
          }
          const patch = buildSiteBoundaryWriteBackPatch(plan, migrated);
          if (patch) boundaryWriteBacks.push({
            site: plan.site, patch,
            ...(plan.addedLayers.length > 0 ? { migratedLayers: migrated.siteLayers } : {}),
          });
          return migrated;
        });
        // Don't await — the audit fire-and-forget is fine.
        void Promise.all(auditPromises);
      } catch (e) {
        // Defensive — a migration error must not block site loading.
        if (!scopeIsCurrent()) return;
        console.warn('[fetchSites] MP11 lazy migration failed:', (e as Error)?.message);
      }

      // DR1 — lazy legacy `description` → SiteContext `general/brief`
      // claim migration on read. Mirrors the MP11 pattern above:
      // idempotent, no-clobber, audit-emitting (once per site). The
      // migrator returns null when no work is needed. Persistence
      // happens on the next write that touches the site doc — the
      // loader doesn't write back here to keep fetchSites read-only.
      try {
        const { migrateLegacyDescriptionToSiteContext } = await import('../services/siteContext/legacyDescriptionMigration');
        const drAuditPromises: Promise<void>[] = [];
        persistedSites = persistedSites.map((s) => {
          const result = migrateLegacyDescriptionToSiteContext(s, s.siteContext);
          if (!result) return s;
          drAuditPromises.push(
            (async () => {
              try {
                const { emitAuditEvent } = await import('../lib/auditLogClient');
                await emitAuditEvent({
                  action: 'site.description_migrated_to_site_context',
                  target: { type: 'site', id: s.id, label: s.name },
                  metadata: {
                    claimId: result.addedClaim.id,
                    migratedChars: result.migratedChars,
                  },
                });
              } catch { /* audit is best-effort */ }
            })(),
          );
          return {
            ...s,
            siteContext: result.nextContext,
          };
        });
        void Promise.all(drAuditPromises);
      } catch (e) {
        console.warn('[fetchSites] DR1 lazy migration failed:', (e as Error)?.message);
      }

      // SCE-4 — lazy validation of legacy claims against the SCE-1
      // typed-field schema. Stamps `qualityWarnings: ['sce-*']` on
      // claims that would fail today's gate; does NOT delete or
      // mutate values. Idempotent. Persistence happens on the next
      // write that touches the site doc (fetchSites stays read-only).
      try {
        const { migrateSiteContextClaims } = await import('../services/siteContext/sceLazyMigration');
        persistedSites = persistedSites.map((s) => {
          const r = migrateSiteContextClaims(s.siteContext);
          if (!r) return s;
          return { ...s, siteContext: r.nextContext };
        });
      } catch (e) {
        console.warn('[fetchSites] SCE-4 lazy migration failed:', (e as Error)?.message);
      }

      try {
        const { applyAuthoredStructuredFieldsToSed } = await import('../lib/coverage/authoredSedOverlay');
        persistedSites = persistedSites.map((s) => {
          const sedCandidate = ('sed' in s ? (s as { sed?: unknown }).sed : undefined);
          if (!sedCandidate || !s.siteContext?.claims?.length) return s;
          const sed = JSON.parse(JSON.stringify(sedCandidate));
          applyAuthoredStructuredFieldsToSed(sed, s.siteContext.claims);
          return { ...s, sed };
        });
      } catch (e) {
        console.warn('[fetchSites] authored SED overlay failed:', (e as Error)?.message);
      }

      try {
        const { dedupSiteLayers } = await import('../lib/siteLayers/dedupSiteLayers');
        persistedSites = persistedSites.map((site) => {
          const { layers } = dedupSiteLayers({ siteId: site.id, layers: site.siteLayers });
          if (layers === site.siteLayers) return site;
          return { ...site, siteLayers: layers };
        });
      } catch (e) {
        console.warn('[fetchSites] siteLayers dedup pass failed:', (e as Error)?.message);
      }

      // #79 — tag every OWN site 'mine'. Own sites always win dedupe and
      // stay 'mine' even though they ALSO match the company-shared
      // collectionGroup query below (createSite stamps isCompanyShared:true
      // whenever a companyId resolves).
      const ownSites: import('../types/site').SiteEntity[] = persistedSites.map((s) => ({
        ...s,
        _sourceScope: 'mine' as const,
      }));

      // #79 — SECOND HOP: load own + DESCENDANT company-shared sites via a
      // subtree-scoped collectionGroup('sites') query, mirroring the proven
      // fetchRagReports company-shared sweep.
      //
      // SECURITY: the candidate company ids come ONLY from
      // `resolveSubtreeCompanyIds` (own company + every descendant, derived
      // from the VERIFIED `companies where path array-contains self` query),
      // never from user input and never an ancestor/sibling/other tenant. The
      // per-doc Firestore rule (`match /{path=**}/sites/{siteId}` with
      // `isInCompanyTree(resource.data.companyId)`) independently re-verifies
      // subtree membership, so a tampered id list cannot widen access. On
      // permission-denied the resolver degrades to `[companyId]` (self-only).
      //
      // Company sites are READ-ONLY: they skip the lazy migrations / audit
      // passes above (those ran on the user's OWN raw sites only) — we
      // normalize them in-memory for display, never write back.
      const candidates: Array<{
        data: import('../types/site').SiteEntity;
        siteId: string;
        ownerUid?: string;
        scope: 'company' | 'subCompany';
      }> = [];
      if (!companyId) {
        console.debug('[fetchSites] no companyId — skipping company-shared collectionGroup query (own-only)');
      } else {
        const companyDocs = await companyRawDocsPromise;
        // Own sites win dedupe — exclude them from the company set so an
        // own site (which is ALSO isCompanyShared:true) is never re-tagged
        // 'company'/'subCompany'. Dedupe by site id against the own-uid set.
        const seen = new Set<string>(ownSites.map((s) => s.id));
        for (const d of companyDocs) {
          const ownerUid = d.ownerUid;
          if (ownerUid === user.uid) continue;
          // Normalize legacy reportIds/simulationIds/documentIds to [] —
          // this is the admin cross-org path: it surfaces sites from other
          // tenants that may predate these required-typed fields, and the
          // sites tab maps over reportIds directly (AdminPanel.tsx).
          const data = normalizeSiteEntity(d.raw);
          const siteId = data.id ?? d.id;
          // DUP-1 — collectionGroup result order is not canonical. A
          // tombstoned duplicate can carry the same logical id as the
          // canonical owner doc; skip it BEFORE `seen` so it cannot claim
          // the id, wrong owner path, or incomplete layer mirror.
          const duplicateOf = (data as { duplicateOf?: unknown }).duplicateOf;
          if (typeof duplicateOf === 'string' && duplicateOf.trim().length > 0) {
            console.debug(`[fetchSites] skipping company-query tombstone ${siteId} (canonical: ${duplicateOf})`);
            continue;
          }
          if (seen.has(siteId)) continue; // own site or cross-chunk dup
          seen.add(siteId);
          // 'company' when the owning org IS the caller's own company;
          // 'subCompany' when it's a descendant org in the subtree.
          const scope: 'company' | 'subCompany' =
            data.companyId === companyId ? 'company' : 'subCompany';
          candidates.push({
            data,
            siteId,
            ownerUid,
            scope,
          });
        }
        companyDocs.length = 0;
      }

      const initialCompanySites: import('../types/site').SiteEntity[] = candidates.map(
        (candidate) => {
          const { data, siteId, ownerUid, scope } = candidate;
          const verifiedMirror = isVerifiedCompleteSiteLayerMirror(data);
          candidate.data = extractLayerResolutionMeta(data);
          return {
            ...data,
            id: siteId,
            siteLayers: Array.isArray(data.siteLayers) ? data.siteLayers : [],
            _siteLayersCanonicalComplete: verifiedMirror,
            _siteLayersHydrationPending: !verifiedMirror,
            _sourceScope: scope,
            ...(ownerUid ? { _ownerUid: ownerUid } : {}),
          } as import('../types/site').SiteEntity;
        },
      );

      if (!scopeIsCurrent()) return;
      if (ownSites.length > 0 || initialCompanySites.length > 0) {
        // Freshest-wins merge for user-edited site config. fetchSites can
        // re-run mid-session (dedupeFetch retry, tab refocus); a failed
        // Firestore write rolls local state back but a subsequent fetch
        // must not clobber a newer in-memory projectIntent / prefs /
        // siteContext when siteConfigUpdatedAt says local is ahead.
        //
        // #79 — own sites are listed FIRST so they win any positional
        // tie-break; company sites are already deduped against own ids.
        // Publish parent site docs + `isSitesLoaded: true` immediately so the
        // portfolio dashboard and UnifiedLoader never block on thousands of
        // subcollection `/layers` reads.
        const initialAllSites = [...ownSites, ...initialCompanySites];
        let phase1Sites: import('../types/site').SiteEntity[] = [];
        set((state) => {
          const sites = mergeSitesIntoStore({ current: state.siteRag.sites, incoming: initialAllSites, readStartedWith });
          phase1Sites = sites;
          if (sameArrayElements(sites, state.siteRag.sites) && state.siteRag.isSitesLoaded) return state;
          return { siteRag: { ...state.siteRag, sites, isSitesLoaded: true } };
        });
        persistedSitesRaw.length = 0;
        initialAllSites.length = 0;
        // Persist the boundary shape migrated above, ONCE (own sites only —
        // the migrations run on own sites only). Fire-and-forget AFTER the
        // store update so the dashboard never waits on a Firestore ack; the
        // ledger inside guarantees a single attempt per site per session.
        if (boundaryWriteBacks.length > 0) {
          void (async () => {
            try {
              const { persistSiteBoundaryWriteBacks } = await import('../services/siteBoundary/siteBoundaryWriteBack');
              const outcome = await persistSiteBoundaryWriteBacks({
                db,
                uid: user.uid,
                items: boundaryWriteBacks,
                logContext: '[fetchSites]',
                isCurrent: scopeIsCurrent,
              });
              console.debug(
                `[fetchSites] boundary write-back: ${outcome.written.length} written, `
                + `${outcome.skipped.length} skipped, ${outcome.failed.length} failed`,
              );
            } catch (e) {
              console.warn('[fetchSites] boundary write-back failed:', (e as Error)?.message);
            }
          })();
        }

        // Phase 2 — hydrate canonical `/layers` subcollections for own sites
        // (that did not already hydrate for boundary reconciliation) and
        // company-shared sites with bounded concurrency, then patch ONLY the
        // layer fields onto the CURRENT site records in the store so concurrent
        // updates to pins, locks, reportIds, or config are never overwritten.
        const phase1ById = new Map<string, import('../types/site').SiteEntity>(
          phase1Sites.map((s) => [s.id, s]),
        );
        const layerPatchesById = new Map<string, HydratedSiteLayersPatch>();
        let dedupFn: ((args: { siteId: string; layers: import('../types/site').SiteLayer[] | undefined }) => { layers: import('../types/site').SiteLayer[] | undefined }) | null = null;
        try {
          const mod = await import('../lib/siteLayers/dedupSiteLayers');
          dedupFn = mod.dedupSiteLayers;
        } catch { /* optional dedup */ }

        await mapWithConcurrency(
          ownSites,
          1,
          async (site) => {
            const rawData = ownSitesDeferredLayerHydration.get(site.id);
            if (!rawData) return;
            const phase1Site = phase1ById.get(site.id);
            if (phase1Site && phase1Site._siteLayersCanonicalComplete === true && !phase1Site._siteLayersHydrationPending) {
              return;
            }
            const resolved = await loadResolvedClientSiteLayersWithStatus({
              db,
              ownerUid: user.uid,
              siteId: site.id,
              site: rawData,
              logContext: `[fetchSites] site ${site.id}`,
            });
            const finalLayers = (dedupFn
              ? dedupFn({ siteId: site.id, layers: resolved.layers }).layers
              : resolved.layers) ?? [];
            layerPatchesById.set(site.id, {
              siteId: site.id,
              expectedRevision: rawData.siteLayersRevision,
              phase1Layers: phase1Site?.siteLayers ?? site.siteLayers,
              layers: finalLayers,
              canonicalComplete: resolved.canonicalComplete,
              hydrationError: resolved.hydrationError,
            });
          },
        );

        await mapWithConcurrency(
          candidates,
          1,
          async ({ data, siteId, ownerUid }, idx) => {
            const initialSite = initialCompanySites[idx]!;
            const phase1Site = phase1ById.get(siteId);
            if (phase1Site && phase1Site._siteLayersCanonicalComplete === true && !phase1Site._siteLayersHydrationPending) {
              return;
            }
            const resolved = await loadResolvedClientSiteLayersWithStatus({
              db,
              ownerUid,
              siteId,
              site: data,
              logContext: `[fetchSites] shared site ${siteId}`,
            });
            const finalLayers = (dedupFn
              ? dedupFn({ siteId, layers: resolved.layers }).layers
              : resolved.layers) ?? [];
            layerPatchesById.set(siteId, {
              siteId,
              expectedRevision: data.siteLayersRevision,
              phase1Layers: phase1Site?.siteLayers ?? initialSite.siteLayers,
              layers: finalLayers,
              canonicalComplete: resolved.canonicalComplete,
              hydrationError: resolved.hydrationError,
            });
          },
        );

        if (!scopeIsCurrent()) return;
        if (layerPatchesById.size > 0) {
          set((state) => {
            const sites = applyHydratedSiteLayersToCurrentSites(state.siteRag.sites, layerPatchesById);
            if (sameArrayElements(sites, state.siteRag.sites)) return state;
            return { siteRag: { ...state.siteRag, sites } };
          });
        }
        return;
      }

      // Legacy fallback: derive sites from reports in memory. Only runs for
      // users whose reports never went through createSite. If reports aren't
      // loaded yet the user sees `sites=[]` and the empty-state copy — fine,
      // the empty state is the truth in the modern path too.
      const reports = get().siteRag.reports;
      if (reports.length === 0) return;

      const groups = groupLegacySiteReports(reports);
      const derived: import('../types/site').SiteEntity[] = [];
      for (const [, groupReports] of groups) {
        const sorted = [...groupReports].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        const latest = sorted.find(report => reportText(report.siteName))!;
        const siteId = latest.siteId || `site-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        derived.push({
          id: siteId,
          name: reportText(latest.siteName), address: reportText(latest.address), location: latest.location,
          siteBoundary: latest.siteBoundary, siteArea: latest.siteArea,
          sitePerimeter: latest.sitePerimeter, siteCentroid: latest.siteCentroid,
          siteElevationRange: latest.siteElevationRange,
          reportIds: sorted.map(r => r.id),
          simulationIds: [],
          documentIds: [],
          createdAt: sorted[sorted.length - 1].createdAt, updatedAt: new Date().toISOString(),
          thumbnailStatus: latest.overallStatus, latestReportScore: latest.overallScore, latestReportId: latest.id,
          // XS3 — cache the latest report's TYPE so the dashboard site card
          // can render "{shortLabel} verdict · {score}/100" without a
          // round-trip lookup. Defaults to 'feasibility' on legacy reports.
          latestReportType: latest.reportType ?? 'feasibility',
        });
      }
      if (derived.length > 0) set((state) => ({ siteRag: { ...state.siteRag, sites: derived } }));
    } catch (e) {
      console.warn('[fetchSites] failed:', (e as Error)?.message);
    } finally {
      if (scopeIsCurrent()) set((state) => ({ siteRag: { ...state.siteRag, isSitesLoaded: true } }));
    }
  }),

  // Legacy migration function — kept for type compatibility but no longer writes to Firestore
  _migrateReportsToSites: async (): Promise<import('../types/site').SiteEntity[]> => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) { console.warn('[Migration] No user'); return []; }
    const { doc, setDoc } = await import('firebase/firestore');
    const reports = get().siteRag.reports;
    // Migration starting with ${reports.length} reports, user: ${user.uid}`);

    // Explicit site IDs stay separate; only unscoped legacy rows use names.
    const groups = groupLegacySiteReports(reports);

    const sites: import('../types/site').SiteEntity[] = [];
    const now = new Date().toISOString();

    for (const [, groupReports] of groups) {
      const sorted = [...groupReports].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const latest = sorted.find(report => reportText(report.siteName))!;
      const oldest = sorted[sorted.length - 1];
      const siteId = latest.siteId || `site-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const site: import('../types/site').SiteEntity = {
        id: siteId,
        name: reportText(latest.siteName),
        address: reportText(latest.address),
        location: latest.location,
        siteBoundary: latest.siteBoundary,
        siteArea: latest.siteArea,
        sitePerimeter: latest.sitePerimeter,
        siteCentroid: latest.siteCentroid,
        siteElevationRange: latest.siteElevationRange,
        reportIds: sorted.map(r => r.id),
        simulationIds: [],
        documentIds: [],
        createdAt: oldest.createdAt,
        updatedAt: now,
        thumbnailStatus: latest.overallStatus,
        latestReportScore: latest.overallScore,
        latestReportId: latest.id,
        // XS3 — same cache as the live fetchSites path.
        latestReportType: latest.reportType ?? 'feasibility',
        isCompanyShared: latest.isCompanyShared,
        companyId: latest.companyId,
      };
      sites.push(site);

      // Clean undefined values for Firestore (rejects undefined)
      const cleanSite = JSON.parse(JSON.stringify(site));
      const { withSemaphore } = await import('../lib/firestore/writeLimiter');

      // Save site to Firestore
      try {
        await withSemaphore(() => setDoc(doc(db, `users/${user.uid}/sites`, siteId), cleanSite));
        console.log(`[Sites] Created site "${site.name}" (${siteId}) with ${sorted.length} reports`);
      } catch (e) {
        // Firestore save failed — site exists in memory only (will retry next load)
      }

      // Backfill siteId on reports (non-blocking)
      const { updateDoc } = await import('firebase/firestore');
      for (const r of sorted) {
        r.siteId = siteId;
        try { await withSemaphore(() => updateDoc(doc(db, `users/${user.uid}/reports`, r.id), { siteId })); } catch { /* best-effort */ }
      }
    }
    // Update reports in store with siteIds
    set((state) => ({ siteRag: { ...state.siteRag, reports: [...state.siteRag.reports] } }));
    // Migrated ${reports.length} reports into ${sites.length} sites`);
    return sites;
  },

  createSite: async (siteData) => {
    const createAbortError = () => {
      const err = new Error('Site creation cancelled');
      err.name = 'AbortError';
      return err;
    };
    if (siteData.abortSignal?.aborted) {
      throw createAbortError();
    }

    // FIX #85 (root cause) — never persist a phantom site. A failed/partial
    // create previously left a doc with name===undefined (stripUndefined drops
    // the key) and/or no createdAt, which crashed delete and could not be
    // name-confirmed. Require a non-empty, trimmed name BEFORE any Firestore
    // write; refuse the write outright when it's missing. `name` is typed as
    // `string`, but the wizard can hand us '' / '   ' / undefined on a partial
    // submit, so guard at runtime regardless of the type. We persist the
    // trimmed name so the confirm-to-delete target matches what the UI shows.
    const trimmedName = typeof siteData.name === 'string' ? siteData.name.trim() : '';
    if (!trimmedName) {
      throw new Error('createSite: a non-empty site name is required — refusing to persist a nameless (phantom) site.');
    }

    const requestId = typeof siteData.draftRequestId === 'string' && siteData.draftRequestId.trim().length > 0
      ? siteData.draftRequestId.trim()
      : null;
    const isDraftOnly = siteData.draftOnly !== false;
    const locLat = typeof siteData.location?.lat === 'number' && Number.isFinite(siteData.location.lat)
      ? siteData.location.lat.toFixed(6)
      : 'na';
    const locLng = typeof siteData.location?.lng === 'number' && Number.isFinite(siteData.location.lng)
      ? siteData.location.lng.toFixed(6)
      : 'na';

    const initiatingCompanyId = get().companyId ?? null;
    const initiatingScopeGen = _createSiteScopeGeneration;
    const orgStamp = resolveOrgStamp(siteData.companyId, initiatingCompanyId);
    const resolvedCompanyScope = orgStamp.companyId ?? initiatingCompanyId ?? 'personal';
    const normalizedAddressKey = typeof siteData.address === 'string' ? siteData.address.trim().toLowerCase() : '';
    const boundaryIdentityKey = serializeDraftBoundaryIdentity(siteData);
    const effectiveRequestId =
      requestId ??
      (isDraftOnly
        ? `draft:${trimmedName.toLowerCase()}:${normalizedAddressKey}:${locLat}:${locLng}:${boundaryIdentityKey}:${resolvedCompanyScope}:${siteData.evidenceMode ?? ''}`
        : null);
    const baseSlotKey = `${initiatingScopeGen}:${resolvedCompanyScope}:${effectiveRequestId ?? ''}:${trimmedName.toLowerCase()}:${normalizedAddressKey}:${locLat}:${locLng}:${boundaryIdentityKey}:${siteData.evidenceMode ?? ''}`;

    const existingEntry = inFlightCreateSiteRequests.get(baseSlotKey);
    if (existingEntry) {
      const { auth } = await import('../firebase');
      const callerUid = auth.currentUser?.uid ?? null;
      if (
        callerUid &&
        existingEntry.initiatingUid === callerUid &&
        existingEntry.initiatingCompanyId === (get().companyId ?? null) &&
        existingEntry.initiatingScopeGen === _createSiteScopeGeneration
      ) {
        return existingEntry.promise;
      }
    }

    const entry: InFlightCreateSiteEntry = {
      initiatingUid: null,
      initiatingCompanyId,
      initiatingScopeGen,
      resolvedCompanyScope,
      promise: Promise.resolve(''),
    };

    const runCreate = (async () => {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const initiatingUid = user.uid;
      entry.initiatingUid = initiatingUid;

      if (siteData.abortSignal?.aborted) {
        throw createAbortError();
      }

      const isScopeStillCurrent = () =>
        Boolean(initiatingUid) &&
        auth.currentUser?.uid === initiatingUid &&
        (get().companyId ?? null) === initiatingCompanyId;

      const commitSiteToCurrentWorkspace = (targetSite: import('../types/site').SiteEntity) => {
        if (!isScopeStillCurrent()) {
          throw new Error('createSite: auth or company scope changed before site creation completed.');
        }
        let appliedToCurrentScope = false;
        set((state) => {
          if (
            auth.currentUser?.uid !== initiatingUid ||
            (state.companyId ?? null) !== initiatingCompanyId
          ) {
            return state;
          }
          appliedToCurrentScope = true;
          return {
            siteRag: {
              ...state.siteRag,
              sites: [
                targetSite,
                ...state.siteRag.sites.filter((s) => s.id !== targetSite.id),
              ],
            },
          };
        });
        if (!appliedToCurrentScope || !isScopeStillCurrent()) {
          throw new Error('createSite: auth or company scope changed before site creation completed.');
        }
      };

      const scopeKeyPrefix = `${initiatingUid}:${resolvedCompanyScope}:${initiatingScopeGen}`;
      const siteId = effectiveRequestId
        ? deterministicDraftSiteId(initiatingUid, resolvedCompanyScope, effectiveRequestId)
        : `site-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      if (effectiveRequestId) {
        const existingInStore = get().siteRag.sites.find(
          (s) =>
            s.id === siteId ||
            (requestId !== null &&
              s.draftRequestId === requestId &&
              (s.createdBy?.uid ?? initiatingUid) === initiatingUid &&
              (s.companyId ?? 'personal') === resolvedCompanyScope),
        );
        if (existingInStore) {
          if (!isScopeStillCurrent()) {
            throw new Error('createSite: auth or company scope changed before site creation completed.');
          }
          if (doesDraftCreationInputConflict(existingInStore, siteData)) {
            throw new Error(
              'createSite: existing site record for this draftRequestId has conflicting creation inputs; refusing to overwrite existing document.',
            );
          }
          if (requestId) {
            completedDraftCreateByRequestId.set(`${scopeKeyPrefix}:${requestId}`, existingInStore.id);
          }
          return existingInStore.id;
        }
        if (requestId) {
          const completedSiteId = completedDraftCreateByRequestId.get(`${scopeKeyPrefix}:${requestId}`);
          const completedSite = completedSiteId
            ? get().siteRag.sites.find((s) => s.id === completedSiteId)
            : undefined;
          if (completedSiteId && completedSite) {
            if (!isScopeStillCurrent()) {
              throw new Error('createSite: auth or company scope changed before site creation completed.');
            }
            if (doesDraftCreationInputConflict(completedSite, siteData)) {
              throw new Error(
                'createSite: existing site record for this draftRequestId has conflicting creation inputs; refusing to overwrite existing document.',
              );
            }
            return completedSiteId;
          }
        }
      }

      const firestoreMod = await import('firebase/firestore');
      const { doc } = firestoreMod;
      if (siteData.abortSignal?.aborted) {
        throw createAbortError();
      }
      if (!isScopeStillCurrent()) {
        throw new Error('createSite: auth or company scope changed before site creation could commit.');
      }

      const now = new Date().toISOString();
      // DAE4 — 'docs-included' is the default for fresh sites (William
      // 2026-07-22): uploaded documents are weighted into evidence without
      // the "Store document for later?" interstitial. 'ai-only' (Datacrate
      // Engine only) remains a valid explicit opt-out via the
      // EvidenceModeControl — it just requires a deliberate click now, so
      // the store-for-later dialog only ever fires on a mode the user
      // actually chose.
      const evidenceMode = siteData.evidenceMode ?? 'docs-included';
      const evidenceModeSetAt = siteData.evidenceModeSetAt ?? now;
      const hasExplicitProjectIntentKey = Object.prototype.hasOwnProperty.call(siteData, 'projectIntent');
      const resolvedProjectIntent = hasExplicitProjectIntentKey
        ? siteData.projectIntent
        : (await import('../lib/intent/undecidedProjectIntent')).undecidedProjectIntent();

      if (siteData.abortSignal?.aborted) {
        throw createAbortError();
      }
      if (!isScopeStillCurrent()) {
        throw new Error('createSite: auth or company scope changed before site creation could commit.');
      }

      const {
        draftOnly: _draftOnly,
        draftRequestId: _draftRequestId,
        abortSignal: _abortSignal,
        onCommitStart,
        ...persistedSiteData
      } = siteData;
      void _draftOnly;
      void _draftRequestId;
      void _abortSignal;

      const draftCreationReceipt = buildDraftCreationReceipt({
        uid: initiatingUid,
        companyScope: resolvedCompanyScope,
        requestId: effectiveRequestId,
        siteInput: {
          ...siteData,
          name: trimmedName,
        },
      });

      const site: import('../types/site').SiteEntity = {
        ...persistedSiteData,
        ...orgStamp,
        ...(hasExplicitProjectIntentKey
          ? { projectIntent: resolvedProjectIntent }
          : { projectIntent: resolvedProjectIntent }),
        // FIX #85 — persist the validated/trimmed name (not the raw spread value)
        // so the stored doc always has a real, non-empty name and the
        // confirm-to-delete target matches exactly.
        name: trimmedName,
        id: siteId,
        reportIds: [],
        simulationIds: [],
        documentIds: [],
        // createdAt is always stamped here — a phantom doc could previously
        // land with createdAt===undefined (stripUndefined drops it) which broke
        // the delete dialog's `new Date(site.createdAt)` render.
        createdAt: now,
        updatedAt: now,
        createdBy: siteData.createdBy ?? { uid: user.uid, email: user.email ?? undefined },
        reportAudienceDefault: siteData.reportAudienceDefault ?? 'internal',
        evidenceMode,
        evidenceModeSetAt,
        // #82 — sites are created UNLOCKED. Persisting an explicit `locked:false`
        // (rather than omitting it) keeps the Firestore doc + rules diff simple
        // and avoids the "legacy field-less" branch for freshly-created sites.
        locked: siteData.locked ?? false,
        draftOnly: isDraftOnly,
        ...(requestId ? { draftRequestId: requestId } : {}),
        draftCreationReceipt,
        // Draft-only site creation: never stamp `sedStatus: 'bootstrapping'` or
        // auto-start background research, regional layer seeding, economic
        // screening, simulations, or reports on site creation. Starting research
        // is an explicit separate user action. If a programmatic caller explicitly
        // sets `draftOnly: false`, preserve the legacy immediate-bootstrap contract.
        ...(isDraftOnly
          ? (siteData.sedStatus !== undefined ? { sedStatus: siteData.sedStatus } : {})
          : {
              sedStatus:
                siteData.sedStatus ??
                (siteData.studyArea?.included.length === 0 ? 'partial' : 'bootstrapping'),
            }),
      };
      // #79 — `_sourceScope` is a TRANSIENT in-memory annotation set only by
      // fetchSites during the merge. It must never be persisted. `stripUndefined`
      // already drops it when undefined; delete defensively in case `siteData`
      // carried it (a site object round-tripped through the UI). The optimistic
      // local insert below intentionally keeps it absent so the freshly-created
      // own site reads as 'mine' (legacy/untagged ⇒ 'mine' per the filter model).
      delete (site as Partial<import('../types/site').SiteEntity>)._sourceScope;
      // #sync-permission-fix — `_ownerUid` is equally transient; never persist.
      delete (site as Partial<import('../types/site').SiteEntity>)._ownerUid;
      // Audit history is server-owned; never persist on client site creation/cloning.
      delete (site as Partial<import('../types/site').SiteEntity>).auditOverrides;
      delete (site as Partial<import('../types/site').SiteEntity>).auditOverridesVersion;

      const persistedPayload = stripUndefined(stripDocumentBodiesFromSitePatch(site));
      const siteDocRef = doc(db, `users/${initiatingUid}/sites`, siteId);

      const executeCreateOrRead = async (
        readExisting: () => Promise<{ exists?: () => boolean; data?: () => unknown } | undefined>,
        writeNew: () => Promise<void> | void,
      ): Promise<import('../types/site').SiteEntity> => {
        if (siteData.abortSignal?.aborted) {
          throw createAbortError();
        }
        if (!isScopeStillCurrent()) {
          throw new Error('createSite: auth or company scope changed before site creation could commit.');
        }

        const existingSnap = await readExisting();
        if (siteData.abortSignal?.aborted) {
          throw createAbortError();
        }
        if (!isScopeStillCurrent()) {
          throw new Error('createSite: auth or company scope changed before site creation completed.');
        }
        if (!existingSnap || typeof existingSnap.exists !== 'function') {
          throw new Error(
            'createSite: failed to verify whether target site record already exists; refusing to overwrite.',
          );
        }

        if (existingSnap.exists()) {
          const existingRaw = existingSnap.data?.() as Partial<import('../types/site').SiteEntity> | undefined;
          if (!existingRaw || typeof existingRaw.name !== 'string' || !existingRaw.name.trim()) {
            throw new Error(
              'createSite: existing site record could not be validated; refusing to overwrite existing document.',
            );
          }
          if (
            existingRaw.companyId !== undefined &&
            (existingRaw.companyId ?? 'personal') !== resolvedCompanyScope
          ) {
            throw new Error(
              'createSite: existing site record belongs to a different company scope; refusing to overwrite.',
            );
          }
          if (doesDraftCreationInputConflict(existingRaw, siteData)) {
            throw new Error(
              'createSite: existing site record for this draftRequestId has conflicting creation inputs; refusing to overwrite existing document.',
            );
          }
          return normalizeSiteEntity({
            ...existingRaw,
            id: existingRaw.id ?? siteId,
          } as import('../types/site').SiteEntity);
        }

        if (siteData.abortSignal?.aborted) {
          throw createAbortError();
        }
        if (!isScopeStillCurrent()) {
          throw new Error('createSite: auth or company scope changed before site creation could commit.');
        }

        // Irreversible commit boundary: once transactional set begins, the write
        // is in flight to Firestore and cannot be rolled back by a client AbortSignal.
        onCommitStart?.();
        await writeNew();
        return site;
      };

      let optionalRunTransaction: typeof firestoreMod.runTransaction | undefined;
      try {
        if ('runTransaction' in firestoreMod && typeof firestoreMod.runTransaction === 'function') {
          optionalRunTransaction = firestoreMod.runTransaction;
        }
      } catch {
        optionalRunTransaction = undefined;
      }

      if (typeof optionalRunTransaction !== 'function') {
        throw new Error(
          'createSite: atomic Firestore transaction capability (runTransaction with tx.get and tx.set) is required for site creation.',
        );
      }

      const resolvedSite = await withAtomicSiteCreateLock(`${initiatingUid}:${siteId}`, async () => {
        return await optionalRunTransaction(db, async (tx) => {
          if (typeof tx?.get !== 'function' || typeof tx?.set !== 'function') {
            throw new Error(
              'createSite: atomic Firestore transaction capability (runTransaction with tx.get and tx.set) is required for site creation.',
            );
          }
          return executeCreateOrRead(
            () => tx.get(siteDocRef),
            () => {
              tx.set(siteDocRef, persistedPayload);
            },
          );
        });
      });

      commitSiteToCurrentWorkspace(resolvedSite);
      if (requestId) {
        completedDraftCreateByRequestId.set(`${scopeKeyPrefix}:${requestId}`, resolvedSite.id);
      }

      if (!isDraftOnly) {
        const hasDocsAtCreate = (siteData.documents?.length ?? 0) > 0;
        if (!hasDocsAtCreate && resolvedSite.studyArea?.included.length !== 0) {
          void import('../lib/sed/sedBootstrapCoordinator').then(({ bootstrapSedCoordinated }) =>
            bootstrapSedCoordinated(resolvedSite.id).catch((e) =>
              console.warn('[SED-BOOTSTRAP] dispatch failed:', (e as Error)?.message),
            ),
          );
        }
        void (async () => {
          try {
            const { runAutoSeedCoreLayers } = await import('../services/siteLayers/autoSeedCoreLayers');
            const addedTotal = await runAutoSeedCoreLayers({
              site: resolvedSite,
              siteId: resolvedSite.id,
              fetchAndMergeRegionalLayers: (opts) => get().fetchAndMergeRegionalLayers(resolvedSite.id, opts),
              persistSeedStamp: (stamp) => get().updateSite(resolvedSite.id, { autoLayerSeed: stamp }),
            });
            if (addedTotal != null && addedTotal > 0) {
              console.info(`[autoSeedCoreLayers] seeded ${addedTotal} layer(s) at creation for ${resolvedSite.id}`);
            }
          } catch (e) {
            console.warn('[autoSeedCoreLayers] creation seed skipped:', (e as Error)?.message);
          }
        })();
      }

      return resolvedSite.id;
    })();

    entry.promise = runCreate;
    inFlightCreateSiteRequests.set(baseSlotKey, entry);
    try {
      return await runCreate;
    } finally {
      if (inFlightCreateSiteRequests.get(baseSlotKey) === entry) {
        inFlightCreateSiteRequests.delete(baseSlotKey);
      }
    }
  },

  updateSite: async (id, updates) => {
    // Fix_React_Error_185_Prompt step 5 — opt-in update tracing.
    // Toggle in a prod console: `window.__DEEPATOMIC_TRACE_UPDATESITE__ = true`,
    // reload, watch the console. Off by default. Confirms updateSite
    // fires fewer than 5 times per second after the migration bracket.
    if (typeof window !== 'undefined' && (window as unknown as { __DEEPATOMIC_TRACE_UPDATESITE__?: boolean }).__DEEPATOMIC_TRACE_UPDATESITE__) {
      const stackLines = new Error().stack?.split('\n').slice(2, 6).join(' | ') ?? '';
      console.log('[updateSite]', id, Object.keys(updates).join(','), stackLines);
    }
    // #82 — lock gate. Throws SiteLockedError when the site is locked and the
    // caller is neither holder, platform admin, nor company admin. The holder's
    // own lockSite/unlockSite writes pass (uid === lockedByUid, or the site is
    // not yet locked for lockSite).
    await get().assertSiteWritable(id);
    const priorSite = get().siteRag.sites.find(s => s.id === id);
    if (!priorSite) return;
    if (updates.supplyChainPlan) {
      const { validateMaterialScopeLimitations } = await import('../lib/supplyChainMaterialScope');
      validateMaterialScopeLimitations(
        updates.supplyChainPlan.materialScopeLimitations,
        new Set((priorSite.documents ?? []).map(document => document.id)),
      );
    }
    const rollbackSite = { ...priorSite };
    const now = new Date().toISOString();

    // #81(b) — stamp the editor's identity on every write. Resolved BEFORE
    // the optimistic set so the same value lands in both the local state and
    // the Firestore payload. The display name is captured at write time via
    // `resolveMemberName` (companyMembers ?? email ?? undefined) so the
    // "last edited by {name}" tag renders without a member lookup on read.
    // `at` is an ISO string (matches updatedAt / siteConfigUpdatedAt).
    //
    // Falls back to undefined when no user is signed in — the write below
    // will throw on the missing user anyway, but the optimistic state must
    // still be self-consistent.
    let lastEditedBy: import('../types/site').SiteEntity['lastEditedBy'];
    {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) {
        const { resolveMemberName } = await import('../lib/siteCompany');
        const name = resolveMemberName(u.uid, get().companyMembers, u.email ?? undefined)
          ?? u.displayName ?? u.email ?? undefined;
        lastEditedBy = { uid: u.uid, name, at: now };
      }
    }

    // Sanitise FieldValue sentinels (e.g. deleteField()) out of the OPTIMISTIC
    // state — they belong only in the Firestore write payload. A sentinel
    // normalises to `null` here so it can never leak into render (task #65:
    // a deleteField() sentinel in `customerLogoUrl` would break the <img>).
    const optimisticUpdates: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(updates)) {
      optimisticUpdates[k] = isFieldValueSentinel(v) ? null : v;
    }
    // #79 — `_sourceScope` is transient; never let a caller's `updates`
    // persist or override the in-memory annotation through updateSite.
    delete (optimisticUpdates as Record<string, unknown>)._sourceScope;
    // #sync-permission-fix — same for `_ownerUid`.
    delete (optimisticUpdates as Record<string, unknown>)._ownerUid;
    // `draftCreationReceipt` is immutable once stamped by `createSite`.
    delete (optimisticUpdates as Record<string, unknown>).draftCreationReceipt;
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.map(s => s.id === id
          ? {
              ...s,
              ...optimisticUpdates,
              ...(s.draftCreationReceipt ? { draftCreationReceipt: s.draftCreationReceipt } : {}),
              ...(lastEditedBy ? { lastEditedBy } : {}),
              updatedAt: now,
            }
          : s),
      },
    }));
    // LOCAL DEV MOCK MODE: writes are local-only (no signed-in user, no live
    // Firestore). The optimistic update above stands; skip persistence so we
    // don't hit the offline/suspended backend, throw 'Not signed in', and roll
    // back. Gate is statically false in prod builds. See src/lib/localDev/.
    if (isLocalMockMode()) return;
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not signed in');
      }
      const { doc, updateDoc } = await import('firebase/firestore');
      // Strip undefined so callers passing optional fields explicitly as
      // `undefined` (e.g. ProjectProfileCard clearing description) don't
      // crash the write. Firestore's `deleteField()` sentinel is the right
      // tool to actually remove a previously-written field.
      //
      // #79/#81(b): omit the transient `_sourceScope` from the write and
      // stamp `lastEditedBy` so the persisted doc records the editor.
      // #sync-permission-fix: `_ownerUid` is equally transient — omit it.
      // (#owner-path-sweep: shared strip helper, same semantics.)
      const persistableUpdates = stripDocumentBodiesFromSitePatch(stripTransientSiteAnnotations(updates));
      delete (persistableUpdates as Record<string, unknown>).auditOverrides;
      delete (persistableUpdates as Record<string, unknown>).auditOverridesVersion;
      delete (persistableUpdates as Record<string, unknown>).draftCreationReceipt;
      // #sync-permission-fix — write to the path that actually HOLDS the doc.
      // Own sites: `users/{currentUid}/sites/{id}` (unchanged). Company-shared
      // foreign-owned sites carry `_ownerUid` (stamped by fetchSites from the
      // collectionGroup ref); writing those to the CURRENT user's path hits a
      // non-existent doc, the sites update rule errors on `resource.data`
      // (null resource) and Firestore surfaces PERMISSION_DENIED for every
      // caller — including admins and the in-app site owner. The path-specific
      // rule (firestore.rules `match /users/{userId}/sites/{siteId}`) already
      // authorizes company-tree members on the OWNER's path, so this is purely
      // a write-shape fix — no rule change. (#owner-path-sweep: shared helper.)
      const docOwnerUid = siteOwnerUid(priorSite, user.uid);
      await updateDoc(
        doc(db, `users/${docOwnerUid}/sites`, id),
        stripUndefined({ ...persistableUpdates, ...(lastEditedBy ? { lastEditedBy } : {}), updatedAt: now }),
      );
      // Refresh local state from canonical Firestore doc to prevent stale
      // sibling-field staleness from leaking into future updateSite calls.
      //
      // Task #65 — "customer logo returns after Remove": `getDoc` can resolve
      // from the local cache, and there is a real window where that cached
      // snapshot has NOT yet reflected the just-issued write (e.g. a field
      // cleared to `null` / `deleteField()`). Blindly replacing local state
      // with `{ ...freshData, id }` then RESURRECTS the field the caller just
      // cleared. The caller's own `updates` are authoritative for the keys
      // they explicitly set — the refresh only exists to pick up SIBLING
      // fields that changed server-side. So re-apply `updates` on top of the
      // fresh doc, normalising any FieldValue sentinel (deleteField/null) to
      // an absent field so a sentinel can never leak into the rendered state
      // (e.g. an <img src>).
      try {
        const { getDoc } = await import('firebase/firestore');
        // #sync-permission-fix — refresh from the SAME owner path we wrote.
        const fresh = await getDoc(doc(db, `users/${docOwnerUid}/sites`, id));
        if (fresh.exists()) {
          const freshData = normalizeSiteEntity(fresh.data() as import('../types/site').SiteEntity);
          const authoritative = applyAuthoritativeUpdates(freshData, updates);
          set((state) => ({
            siteRag: {
              ...state.siteRag,
              // #sync-permission-fix — the persisted doc never carries the
              // transient `_sourceScope` / `_ownerUid` annotations, so re-stamp
              // them from the prior in-memory site or the NEXT updateSite on a
              // shared site would fall back to the wrong (current-uid) path.
              sites: state.siteRag.sites.map(s => s.id === id
                ? {
                  ...authoritative,
                  id,
                  ...(s.draftCreationReceipt
                    ? { draftCreationReceipt: s.draftCreationReceipt }
                    : priorSite.draftCreationReceipt
                      ? { draftCreationReceipt: priorSite.draftCreationReceipt }
                      : {}),
                  ...(s._sourceScope ? { _sourceScope: s._sourceScope } : {}),
                  ...(s._ownerUid ? { _ownerUid: s._ownerUid } : {}),
                }
                : s),
            },
          }));
        }
      } catch (e) {
        console.warn('[updateSite] refresh after write failed:', e);
      }
    } catch (e) {
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map(s => s.id === id ? rollbackSite : s),
        },
      }));
      if (!isStaleChunkError(e)) {
        console.warn('Failed to update site:', e);
      }
      throw e;
    }
    // Documents_On_Site Prompt 10 — when readiness-relevant fields
    // change, recompute. Skip when the update IS the readiness write
    // itself to avoid recursion. SED version / status / project intent
    // / boundary all influence readiness.
    //
    // Fix_React_Error_185_Prompt — also skip while AuthGate is in the
    // middle of replaying initial migrations. The migration writes a
    // burst of sites in one tick; firing recompute synchronously for
    // each tipped React over the update-depth budget. The deferred
    // batch (runDeferredReadinessRecompute) sweeps after the bracket
    // exits with a 200ms-per-site throttle.
    if (!('evidenceReadiness' in updates) && !get().isInitialMigration) {
      // DR3 — `description` removed from the trigger list (no longer
      // contributes to the readiness score). `siteContext` added since
      // SC claim count now drives the site-profile component.
      const triggers: (keyof typeof updates)[] = [
        'sedStatus', 'sedVersion', 'sedCompleteness', 'sedId',
        'projectIntent', 'siteBoundary', 'siteBoundaries', 'studyArea', 'siteContext',
      ];
      if (triggers.some(k => k in updates)) {
        void get().recomputeEvidenceReadiness(id);
      }
    }
    // Editing project intent changes phased capacity + power mix, which the
    // portfolio composite snapshots (composite.capacityMW + decision bands).
    // Mark this site's composite STALE so the next portfolio/dashboard recompute
    // refreshes it — otherwise the composite-derived capacity/bands could lag up
    // to the 24h staleness window. Cheap (clears a timestamp); the lazy
    // ensurePortfolioComposites pass does the actual recompute on next view.
    if ('projectIntent' in updates || 'studyArea' in updates || 'siteBoundaries' in updates) {
      get()._staleStampSiteComposite(id);
    }
    if (updates.studyArea?.included.length && !priorSite.sedId && !(updates.draftOnly ?? priorSite.draftOnly)) {
      void import('../lib/sed/sedBootstrapCoordinator').then(({ bootstrapSedCoordinated }) => bootstrapSedCoordinated(id, { forceRefresh: true }))
        .catch(error => console.warn('[study-area] Initial evidence collection failed:', error));
    }
  },

  // ═══ Site edit-lock (#82) ═══
  assertSiteWritable: async (siteId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    // Unknown site OR legacy/unlocked site ⇒ writable. `locked === undefined`
    // (legacy doc with no field) and `locked === false` both fall through.
    if (!site || site.locked !== true) return;

    const { auth } = await import('../firebase');
    const uid = auth.currentUser?.uid;
    // The holder may always write their own site (this is also how
    // lockSite/unlockSite pass the gate).
    if (uid && site.lockedByUid === uid) return;

    // Company admin override: companyRole 'admin' AND the locked site belongs
    // to the caller's company tree (the in-store companyId reflects verified
    // tree membership; mirrors the rules' isCompanyAdminOf carve-out).
    const { companyRole, companyId } = get();
    if (
      companyRole === 'admin' &&
      typeof site.companyId === 'string' &&
      site.companyId.length > 0 &&
      site.companyId === companyId
    ) {
      return;
    }

    // Platform admin override — verified from a force-refreshed ID token so a
    // revoked admin can't keep editing a locked site.
    try {
      const { requireAdminClaim } = await import('../lib/requireAdminClaim');
      if (await requireAdminClaim()) return;
    } catch (e) {
      console.warn('[assertSiteWritable] admin claim check failed:', (e as Error)?.message);
    }

    throw new SiteLockedError({
      siteId,
      lockedByUid: site.lockedByUid,
      lockedByName: site.lockedByName,
    });
  },

  lockSite: async (siteId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) throw new Error('Site not found');
    // Already locked by someone else — the holder must release or an admin must
    // force-unlock first. (The rules also reject a non-holder lock write.)
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    if (site.locked === true && site.lockedByUid && site.lockedByUid !== user.uid) {
      throw new SiteLockedError({
        siteId,
        lockedByUid: site.lockedByUid,
        lockedByName: site.lockedByName,
      });
    }
    // Already held by the caller — idempotent.
    if (site.locked === true && site.lockedByUid === user.uid) return;

    const { resolveMemberName } = await import('../lib/siteCompany');
    const name = resolveMemberName(user.uid, get().companyMembers, user.email ?? undefined)
      ?? user.displayName ?? user.email ?? undefined;
    // Client-side write through updateSite — the holder writes their OWN path.
    // updateSite's guard passes because the site is not yet locked by anyone.
    await get().updateSite(siteId, {
      locked: true,
      lockedByUid: user.uid,
      lockedByName: name,
      lockedByEmail: user.email ?? undefined,
      lockedAt: new Date().toISOString(),
    });
  },

  unlockSite: async (siteId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) throw new Error('Site not found');
    // Already unlocked — idempotent no-op (no write).
    if (site.locked !== true) return;
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    // Only the holder releases via this client path. Non-holders (admins) use
    // adminForceUnlock (server-side), which the rules permit cross-uid.
    if (site.lockedByUid !== user.uid) {
      throw new SiteLockedError({
        siteId,
        lockedByUid: site.lockedByUid,
        lockedByName: site.lockedByName,
      });
    }
    // Clear EVERY lock field with deleteField() — NOT undefined (stripUndefined
    // drops undefined from the write payload without removing the persisted
    // field, which would leave the doc locked).
    const { deleteField } = await import('firebase/firestore');
    await get().updateSite(siteId, {
      locked: deleteField() as unknown as boolean,
      lockedByUid: deleteField() as unknown as string,
      lockedByName: deleteField() as unknown as string,
      lockedByEmail: deleteField() as unknown as string,
      lockedAt: deleteField() as unknown as string,
    });
  },

  requestEditAccess: async (siteId, message) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) throw new Error('Site not found');
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    // Nothing to request: site is unlocked, or the caller already holds it.
    if (site.locked !== true || !site.lockedByUid) return null;
    if (site.lockedByUid === user.uid) return null;

    // SERVER-side — the server writes the cross-uid edit_access_requests record
    // (admin SDK) and notifies + emails the current holder.
    const res = await authedFetch('/api/edit-access-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, message: message ?? undefined }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Edit-access request failed (${res.status})`);
    }
    const body = (await res.json().catch(() => ({}))) as { requestId?: string };
    return body.requestId ?? null;
  },

  approveEditRequest: async (requestId) => {
    // SERVER-side — TRANSFERS the lock to the requester (single holder) and
    // notifies + emails them. Only the current holder may approve (enforced
    // server-side against the request's currentHolderUid).
    const res = await authedFetch(`/api/edit-access-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Approve edit-access failed (${res.status})`);
    }
    // Refresh sites so the transferred lock holder is reflected locally.
    await get().fetchSites();
  },

  denyEditRequest: async (requestId) => {
    // SERVER-side — marks the request 'denied' and notifies + emails the
    // requester. Only the current holder may deny.
    const res = await authedFetch(`/api/edit-access-requests/${encodeURIComponent(requestId)}/deny`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Deny edit-access failed (${res.status})`);
    }
  },

  adminForceUnlock: async (siteId) => {
    // SERVER-side — clears the lock (admin SDK cross-uid write on the owner's
    // path) and notifies + emails the displaced holder. The server re-verifies
    // the caller is a platform admin or company admin; we surface a clear error
    // if not.
    const res = await authedFetch(`/api/edit-access-requests/${encodeURIComponent(siteId)}/force-unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Force-unlock failed (${res.status})`);
    }
    // Refresh sites so the cleared lock is reflected locally.
    await get().fetchSites();
  },

  addParcel: async (siteId, polygon, meta) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { addParcelToSite } = await import('../lib/siteParcelStoreActions');
    const updates = addParcelToSite(site, polygon, meta);
    await get().updateSite(siteId, updates);
    const { logAuditEvent } = await import('../lib/telemetry/auditTelemetry');
    logAuditEvent({ type: 'parcel-added', siteId, payload: { parcelCount: updates.siteBoundaries?.length } });
    scheduleParcelEvidenceRefresh(siteId, get);
  },

  removeParcel: async (siteId, parcelId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { removeParcelFromSite } = await import('../lib/siteParcelStoreActions');
    const updates = removeParcelFromSite(site, parcelId);
    if (!updates) return;
    await get().updateSite(siteId, updates);
    scheduleParcelEvidenceRefresh(siteId, get);
  },

  updateParcelPolygon: async (siteId, parcelId, polygon) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { updateParcelPolygonOnSite } = await import('../lib/siteParcelStoreActions');
    await get().updateSite(siteId, updateParcelPolygonOnSite(site, parcelId, polygon));
    scheduleParcelEvidenceRefresh(siteId, get);
  },

  updateParcelMetadata: async (siteId, parcelId, meta) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { updateParcelMetadataOnSite } = await import('../lib/siteParcelStoreActions');
    await get().updateSite(siteId, updateParcelMetadataOnSite(site, parcelId, meta));
  },

  mergeParcels: async (siteId, sourceIds, targetId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { mergeParcelsOnSite } = await import('../lib/siteParcelStoreActions');
    const updates = mergeParcelsOnSite(site, sourceIds, targetId);
    if (!updates) return;
    await get().updateSite(siteId, updates);
    const { logAuditEvent } = await import('../lib/telemetry/auditTelemetry');
    logAuditEvent({ type: 'parcel-merged', siteId, payload: { sourceIds, targetId } });
    scheduleParcelEvidenceRefresh(siteId, get);
  },

  splitParcel: async (siteId, parcelId, polygons) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { splitParcelOnSite } = await import('../lib/siteParcelStoreActions');
    const updates = splitParcelOnSite(site, parcelId, polygons);
    if (!updates) return;
    await get().updateSite(siteId, updates);
    const { logAuditEvent } = await import('../lib/telemetry/auditTelemetry');
    logAuditEvent({ type: 'parcel-split', siteId, payload: { parcelId, splitCount: polygons.length } });
    scheduleParcelEvidenceRefresh(siteId, get);
  },

  patchSedRebuildProgressBestEffort: async (siteId, progress, opts) => {
    const priorSite = get().siteRag.sites.find(s => s.id === siteId);
    if (!priorSite) return;
    const now = new Date().toISOString();
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.map(s =>
          s.id === siteId
            ? { ...s, sedRebuildProgress: progress, updatedAt: now }
            : s,
        ),
      },
    }));
    if (opts?.persist === false) return;
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, updateDoc } = await import('firebase/firestore');
      // #owner-path-sweep — write to the path that actually holds the doc.
      await updateDoc(
        doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
        stripUndefined({ sedRebuildProgress: progress, updatedAt: now }),
      );
    } catch (e) {
      if (!isStaleChunkError(e)) {
        console.warn(
          `[patchSedRebuildProgressBestEffort] Firestore write failed for ${siteId}:`,
          (e as Error)?.message,
        );
      }
    }
  },

  migrateLegacySiteSeds: async () => {
    // One-shot at app load: copy each legacy site's most-recent-report sedId
    // onto the site doc so SED-aware UI doesn't trip on missing pointers.
    // Idempotent — sites already carrying `sedId` are skipped by the helper.
    const state = get();
    if (!state.siteRag.isSitesLoaded || !state.siteRag.isReportsLoaded) return;
    const { findLegacySiteSedUpdates } = await import('../lib/siteSedMigration');
    const updates = findLegacySiteSedUpdates(state.siteRag.sites, state.siteRag.reports);
    if (updates.length === 0) return;
    console.log(`[SED-MIGRATION] backfilling sedId on ${updates.length} legacy site(s)`);
    for (const { siteId, updates: patch } of updates) {
      try {
        await get().updateSite(siteId, patch);
      } catch (e) {
        console.warn(`[SED-MIGRATION] failed for site ${siteId}:`, (e as Error)?.message);
      }
    }
  },

  retrySEDBootstrap: async (siteId: string, opts?: { forceRefresh?: boolean }) => {
    // Single entry point so SedStatusPill (SiteLandingPage header) and
    // the failed-SED preflight banner (ReportPreferencesStep) share
    // identical retry semantics. Stamp 'bootstrapping' immediately so
    // the pill / banner flip without waiting on the bootstrap's first
    // write; clear sedFailureReason so a stale error doesn't shadow
    // the new attempt. Bootstrap dispatch is fire-and-forget — the
    // pipeline writes status/completeness as it lands.
    try {
      const priorSite = get().siteRag.sites.find(s => s.id === siteId);
      const priorStatus = priorSite?.sedStatus;
      const nowIso = new Date().toISOString();
      await get().updateSite(siteId, {
        draftOnly: false,
        sedStatus: 'bootstrapping',
        sedFailureReason: undefined,
        // RSF-2 — clear the structured failure breadcrumb so a stale
        // wall-clock timeout record doesn't shadow the new attempt.
        // Mirrors the sedFailureReason clear above; the two live in
        // lock-step (`markSedFailed` writes both, retry clears both).
        sedFailure: undefined,
        // SA5 — stamp the start time so the stuck-bootstrap watchdog
        // and AuthGate backstop can detect bootstraps that have been
        // running too long without making progress.
        sedStatusStartedAt: nowIso,
        // SA4 — seed an empty progress doc so the banner has values
        // to render the moment it mounts.
        sedRebuildProgress: {
          done: 0,
          total: 4, // matches the four milestone stages emitted by sedBootstrap
          startedAt: nowIso,
          lastProgressAt: nowIso,
          stage: 'queued',
        },
      } as Partial<import('../types/site').SiteEntity>);
      const { bootstrapSedCoordinated } = await import('../lib/sed/sedBootstrapCoordinator');
      // Intentional: ADDITIVE top-up / first-build retry, not a full rebuild.
      // R-28 guard scope: rebuildSedWithGuard only (see docs/runbooks/sed-rebuild-safely.md).
      void bootstrapSedCoordinated(siteId, { forceRefresh: opts?.forceRefresh })
        .then(async (result) => {
          if (result.source !== 'skipped-fresh') return;
          // R-32 / phantom-banner reconcile — the coordinator skipped because the
          // SED is already fresh, so NO server job ran. The optimistic
          // 'bootstrapping' stamp above would otherwise leave a phantom "rebuild
          // still working" banner over an already-complete SED (the live
          // 2026-06-26 symptom). Restore the AUTHORITATIVE live status the
          // coordinator reported (`liveSedStatus` — 'complete' / 'partial'),
          // falling back to the prior status, then 'partial' as a last resort.
          const revertStatus =
            (result.liveSedStatus && result.liveSedStatus !== 'bootstrapping'
              ? result.liveSedStatus
              : undefined)
            ?? (priorStatus && priorStatus !== 'bootstrapping' ? priorStatus : undefined)
            ?? 'partial';
          await get().updateSite(siteId, {
            sedStatus: revertStatus,
            sedRebuildProgress: undefined,
            sedStatusStartedAt: undefined,
          } as Partial<import('../types/site').SiteEntity>);
        })
        .catch((e) => {
          const reason = (e as Error)?.message ?? 'unknown error';
          console.warn(`[retrySEDBootstrap] bootstrap observation failed for site ${siteId}:`, reason);
          // A lost enqueue response, snapshot error or client timeout does not
          // prove the server job failed. Keep its status, progress and lease
          // authoritative; surface the observation error only in this tab.
          set((s) => ({
            siteRag: {
              ...s.siteRag,
              error: `SED refresh could not be confirmed: ${reason}. Reload evidence to check the server job before retrying.`,
            },
          }));
        });
    } catch (e) {
      throw new Error(`Failed to retry: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  },

  rebuildSiteSed: async (siteId: string, rebuildOpts = {}) => {
    // SED_Stale_Data_Rebuild SD2 — clear the SED pointer + force-fail so
    // the bootstrap's idempotency guard (sedBootstrap.ts:300,
    // `site.sedId && site.sedStatus && site.sedStatus !== 'failed'`)
    // won't short-circuit. Then re-enter via the existing retry path,
    // which stamps 'bootstrapping' immediately and dispatches the
    // bootstrap fire-and-forget.
    //
    // SA4 — instant click feedback. A long-lived loading toast covers
    // the bootstrap window (~2–5 min); success/failure toasts fire
    // from the AuthGate subscriber when sedStatus flips.
    //
    // AMP-2 — capture the old sedId BEFORE we clear the site pointer
    // so the subscriber below can run `reapplyAmendmentsAfterRebuild`
    // against the freshly-rebuilt SED. The store subscription fires
    // when sedStatus transitions from 'bootstrapping' → 'complete' or
    // 'partial', AND the sedId has changed (new SED materialised);
    // it's a one-shot listener that unsubscribes after firing.
    //
    // AMP-FOLLOWUP-LAST-KNOWN-AMENDMENTS-SOURCE — when the previous
    // rebuild FAILED, it left `site.sedId === undefined` behind (cleared
    // by this same action at the `updateSite(... sedId: undefined ...)`
    // call below). A naive `site.sedId` read at that point would return
    // undefined and the reapply subscriber would be skipped (`if (oldSedId
    // && ...)` guard) — losing every amendment on the next successful
    // rebuild. Fall back to `lastKnownAmendmentsSourceSedId`, which is
    // pinned every time an amendment is successfully applied OR mirrored
    // onto a new sedId, so the pointer survives a failed rebuild in the
    // middle of a sequence.
    const siteAtRebuildStart = get().siteRag.sites.find(s => s.id === siteId);
    const oldSedId = siteAtRebuildStart?.sedId
      ?? siteAtRebuildStart?.lastKnownAmendmentsSourceSedId;
    const { toast } = await import('sonner');
    // C9a — the rebuild is fire-and-forget; the success/error verdict toast
    // re-uses this same id when sedStatus flips. The kickoff toast must NOT
    // be a 5-minute non-dismissible blocker. Make it short + dismissible.
    // Dismiss only hides the toast (toast.dismiss) — it does NOT cancel the
    // background build.
    toast.loading('SED rebuild started — running in the background', {
      id: `sed-rebuild-${siteId}`,
      duration: 6000,
      dismissible: true,
      action: {
        label: 'Dismiss',
        onClick: () => toast.dismiss(`sed-rebuild-${siteId}`),
      },
    });
    // RC4 — suppress the staleness toast while the rebuild is in flight.
    // Settings changed BY the rebuild process (sedId cleared, sedStatus
    // flipped, etc.) shouldn't fire a "now stale" toast — that's a
    // tautology. The flag is cleared in `finally` so a failure during
    // the bootstrap doesn't leave it stuck on.
    const { setSedStalenessSuppressed } = await import('../lib/sedStaleness');
    setSedStalenessSuppressed(siteId, true);

    // AMP-2 — install the one-shot reapply trigger BEFORE kicking off
    // the bootstrap so we don't race the bootstrap writing the new
    // sedId. Skipping the subscription when `useStore` is mocked
    // without `.subscribe` (some test envs) keeps the rebuild path
    // usable for unit tests that don't exercise AMP-2.
    const storeMaybeSubscribable = useStore as unknown as { subscribe?: (cb: (s: AppState) => void) => () => void };
    if (oldSedId && typeof storeMaybeSubscribable.subscribe === 'function') {
      const subscribe = storeMaybeSubscribable.subscribe.bind(storeMaybeSubscribable);
      let unsubscribed = false;
      const startedAt = Date.now();
      const MAX_WAIT_MS = 6 * 60 * 1000;  // hard ceiling — drop subscription rather than leak
      const unsubscribe = subscribe((s: AppState) => {
        if (unsubscribed) return;
        // Safety valve: if the bootstrap is still in flight after
        // MAX_WAIT_MS, drop the subscription. The next manual
        // rebuild can retry from scratch; we don't want a stuck
        // listener keeping the store ref alive.
        if (Date.now() - startedAt > MAX_WAIT_MS) {
          unsubscribed = true;
          unsubscribe();
          return;
        }
        const siteNow = s.siteRag.sites.find(x => x.id === siteId);
        if (!siteNow) return;
        const newSedId = siteNow.sedId;
        const status = siteNow.sedStatus;
        // Trigger conditions:
        //   - bootstrap settled into a terminal-success state
        //     (`complete` or `partial`),
        //   - sedId has changed (a fresh SED was actually written;
        //     a rebuild that aborted before producing a new SED
        //     would leave sedId === undefined).
        if (
          (status === 'complete' || status === 'partial')
          && newSedId
          && newSedId !== oldSedId
        ) {
          unsubscribed = true;
          unsubscribe();
          void (async () => {
            try {
              const summary = await get().reapplyAmendmentsAfterRebuild(siteId, oldSedId);
              if (summary.reapplied > 0 || summary.conflicts > 0) {
                const total = summary.reapplied + summary.conflicts;
                if (summary.conflicts > 0) {
                  toast.warning(
                    `${summary.reapplied} of ${total} amendments re-applied. ${summary.conflicts} need review — provider data shifted.`,
                    { duration: 12_000 },
                  );
                } else {
                  toast.success(
                    `${summary.reapplied} user amendment${summary.reapplied === 1 ? '' : 's'} preserved across rebuild.`,
                  );
                }
              }
            } catch (e) {
              console.warn('[rebuildSiteSed] reapply trigger failed:', (e as Error)?.message);
            }
          })();
        }
      });
    }

    // CAF-5 — install a one-shot reconcile listener that fires
    // `cafService.writeSiteCoverage` after the bootstrap settles into
    // a terminal-success state. This runs INDEPENDENTLY of the AMP-2
    // amendment-reapply subscriber above (which only fires when an
    // oldSedId existed), so first-run sites also get a coverage
    // envelope. Fire-and-forget — failures inside `writeSiteCoverage`
    // never break the rebuild.
    if (typeof storeMaybeSubscribable.subscribe === 'function') {
      const subscribe = storeMaybeSubscribable.subscribe.bind(storeMaybeSubscribable);
      let cafUnsubscribed = false;
      const cafStartedAt = Date.now();
      const CAF_MAX_WAIT_MS = 6 * 60 * 1000;
      const cafUnsubscribe = subscribe((s: AppState) => {
        if (cafUnsubscribed) return;
        if (Date.now() - cafStartedAt > CAF_MAX_WAIT_MS) {
          cafUnsubscribed = true;
          cafUnsubscribe();
          return;
        }
        const siteNow = s.siteRag.sites.find(x => x.id === siteId);
        if (!siteNow) return;
        if (siteNow.sedStatus !== 'complete' && siteNow.sedStatus !== 'partial') return;
        cafUnsubscribed = true;
        cafUnsubscribe();
        void (async () => {
          try {
            const { writeSiteCoverage } = await import('../services/cafService');
            const coverage = await writeSiteCoverage(siteId);
            if (Object.keys(coverage).length > 0) {
              set((s) => ({
                siteRag: {
                  ...s.siteRag,
                  sites: s.siteRag.sites.map(x =>
                    x.id === siteId ? { ...x, coverage } : x,
                  ),
                },
              }));
            }
          } catch (e) {
            console.warn('[rebuildSiteSed] CAF coverage writer failed:', (e as Error)?.message);
          }
        })();
      });
    }

    try {
      const { SED_CONFIG } = await import('../config/sedConfig');
      const siteForDocs = get().siteRag.sites.find(s => s.id === siteId);
      const includeDocIds = siteForDocs ? eligibleDocIdsForSed(siteForDocs) : [];

      if (SED_CONFIG.DIFF_GUARD_ENABLED) {
        const { rebuildSedWithGuard } = await import('../lib/sed/sedRebuildOrchestrator');
        const { SedRebuildBlockedError } = await import('../types/sedRebuild');
        try {
          const result = await rebuildSedWithGuard(
            siteId,
            { includeDocIds, force: rebuildOpts.force },
            { trigger: 'manual-rebuild' },
          );
          if (result.warning) {
            toast.warning(result.warning, { duration: 12_000 });
          }
        } catch (e) {
          if (e instanceof SedRebuildBlockedError) {
            toast.error(
              `SED rebuild rolled back — ${e.diff.verifiedSectionsLost.length} section(s) would have regressed`,
              { id: `sed-rebuild-${siteId}`, duration: 15_000 },
            );
            throw e;
          }
          throw e;
        }
      } else {
        const siteBeforeFallback = get().siteRag.sites.find((s) => s.id === siteId);
        let shouldStartRebuildBootstrap = true;
        if (siteBeforeFallback?.sedId) {
          await get().assertSiteWritable(siteId);
          const { auth } = await import('../firebase');
          const user = auth.currentUser;
          if (!user) throw new Error('Not signed in');
          const initiatingUid = user.uid;
          const ownerUid = siteOwnerUid(siteBeforeFallback, initiatingUid);
          const { getCachedSedStorageGeneration, invalidateCachedSed } = await import(
            '../services/sedLoader'
          );
          const { authedFetch } = await import('../lib/authedFetch');
          const expectedSedId = siteBeforeFallback.sedId;
          const expectedSedVersion =
            typeof siteBeforeFallback.sedVersion === 'number' &&
            Number.isFinite(siteBeforeFallback.sedVersion)
              ? siteBeforeFallback.sedVersion
              : 1;
          const expectedHistoryVersion =
            typeof siteBeforeFallback.sedAmendmentHistoryVersion === 'number' &&
            Number.isFinite(siteBeforeFallback.sedAmendmentHistoryVersion)
              ? siteBeforeFallback.sedAmendmentHistoryVersion
              : 0;
          const expectedSedStorageGeneration =
            getCachedSedStorageGeneration(expectedSedId, siteBeforeFallback.sedStoragePath) ??
            (typeof siteBeforeFallback.sedStorageGeneration === 'string' &&
            siteBeforeFallback.sedStorageGeneration.trim()
              ? siteBeforeFallback.sedStorageGeneration.trim()
              : undefined);
          const clearKey = JSON.stringify([
            initiatingUid,
            ownerUid,
            siteId,
            'rebuild',
            expectedSedId,
            expectedSedVersion,
            expectedHistoryVersion,
            expectedSedStorageGeneration ?? null,
          ]);
          const clearEnvelope =
            pendingSedClearOperations.get(clearKey) ??
            {
              operationId: `sed-clear-rebuild-${siteId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
                /[^A-Za-z0-9._:-]/g,
                '-',
              ),
              ownerUid,
              clearIntent: 'rebuild' as const,
              expectedSedId,
              expectedSedVersion,
              expectedHistoryVersion,
              ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
            };
          pendingSedClearOperations.set(clearKey, clearEnvelope);
          const clearPendingRebuildOpIfTerminal = (status?: number) => {
            if (
              status === undefined ||
              (status >= 400 && status < 500 && status !== 408 && status !== 429)
            ) {
              pendingSedClearOperations.delete(clearKey);
            }
          };
          const clearRes = await authedFetch(
            `/api/sites/${encodeURIComponent(siteId)}/sed-clear`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(clearEnvelope),
            },
          );
          if (!clearRes.ok) {
            clearPendingRebuildOpIfTerminal(clearRes.status);
            const errBody = await clearRes.json().catch(() => ({}));
            throw new Error(
              (errBody as { error?: string })?.error ||
                `SED pointer clear failed (HTTP ${clearRes.status})`,
            );
          }
          const clearAck = (await clearRes.json()) as {
            ok?: boolean;
            action?: string;
            clearIntent?: string;
            operationId?: string;
            ownerUid?: string;
            siteId?: string;
            clearedSedId?: string | null;
            clearedSedVersion?: number;
            clearedSedStorageGeneration?: string | null;
            historyVersion?: number;
            sedStatus?: string | null;
            sedCompleteness?: number | null;
            updatedAt?: string;
            idempotentReplay?: boolean;
          };
          if (
            !clearAck ||
            clearAck.ok !== true ||
            clearAck.action !== 'clear-pointer' ||
            clearAck.clearIntent !== 'rebuild' ||
            clearAck.operationId !== clearEnvelope.operationId ||
            clearAck.ownerUid !== ownerUid ||
            clearAck.siteId !== siteId ||
            clearAck.clearedSedId !== expectedSedId ||
            clearAck.clearedSedVersion !== expectedSedVersion ||
            (expectedSedStorageGeneration !== undefined &&
              clearAck.clearedSedStorageGeneration !== expectedSedStorageGeneration) ||
            clearAck.historyVersion !== expectedHistoryVersion ||
            clearAck.sedStatus !== 'failed' ||
            clearAck.sedCompleteness !== 0 ||
            typeof clearAck.updatedAt !== 'string' ||
            !clearAck.updatedAt.trim()
          ) {
            throw new Error('Malformed or mismatched SED clear acknowledgement.');
          }
          clearPendingRebuildOpIfTerminal();
          const inspectLocalSiteForRebuildClear = () => {
            const actorMatches = auth.currentUser?.uid === initiatingUid;
            const localSite = get().siteRag.sites.find((s) => s.id === siteId) ?? null;
            const ownerMatches = Boolean(
              localSite && siteOwnerUid(localSite, initiatingUid) === ownerUid,
            );
            const movedPastClear = Boolean(
              !actorMatches ||
                !ownerMatches ||
                !localSite ||
                (localSite.sedId && localSite.sedId !== expectedSedId) ||
                (localSite.sedId === expectedSedId &&
                  typeof localSite.sedVersion === 'number' &&
                  localSite.sedVersion > expectedSedVersion) ||
                (localSite.sedId === expectedSedId &&
                  expectedSedStorageGeneration !== undefined &&
                  typeof localSite.sedStorageGeneration === 'string' &&
                  localSite.sedStorageGeneration !== expectedSedStorageGeneration) ||
                (typeof localSite.sedAmendmentHistoryVersion === 'number' &&
                  localSite.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
                localSite.sedStatus === 'bootstrapping' ||
                localSite.sedBootstrapLockStatus === 'locked',
            );
            return { actorMatches, ownerMatches, localSite, movedPastClear };
          };

          const preReadLocal = inspectLocalSiteForRebuildClear();
          if (!preReadLocal.actorMatches || !preReadLocal.ownerMatches) {
            const actorErr = new Error(
              'The signed-in account or site owner changed during the SED clear operation.',
            );
            toast.error(actorErr.message, { id: `sed-rebuild-${siteId}` });
            throw actorErr;
          }

          const isIdempotentReplay = clearAck.idempotentReplay === true;
          const needsAuthoritativeHeadRead = isIdempotentReplay || preReadLocal.movedPastClear;
          let authoritativeServerSite: Record<string, unknown> | null = null;
          if (needsAuthoritativeHeadRead) {
            try {
              const firestoreModule = await import('firebase/firestore');
              const { db } = await import('../firebase');
              const siteDocRef = firestoreModule.doc(db, `users/${ownerUid}/sites`, siteId);
              const getServerDoc =
                typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer ===
                'function'
                  ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc })
                      .getDocFromServer
                  : firestoreModule.getDoc;
              if (typeof getServerDoc === 'function') {
                const snap = await getServerDoc(siteDocRef);
                if (snap && typeof snap.exists === 'function' && snap.exists()) {
                  authoritativeServerSite = (snap.data() ?? null) as Record<string, unknown> | null;
                }
              }
            } catch {
              authoritativeServerSite = null;
            }
          }

          const postReadLocal = inspectLocalSiteForRebuildClear();
          if (!postReadLocal.actorMatches || !postReadLocal.ownerMatches) {
            const actorErr = new Error(
              'The signed-in account or site owner changed during the SED clear operation.',
            );
            toast.error(actorErr.message, { id: `sed-rebuild-${siteId}` });
            throw actorErr;
          }

          if (needsAuthoritativeHeadRead && !authoritativeServerSite) {
            shouldStartRebuildBootstrap = false;
            const refreshErr = Object.assign(
              new Error(
                'SED clear operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
              ),
              {
                code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
                operationConfirmed: true,
                refreshRequired: true,
                ...(isIdempotentReplay ? { idempotent: true } : {}),
              },
            );
            toast.error(refreshErr.message, { id: `sed-rebuild-${siteId}` });
            throw refreshErr;
          }

          const serverMovedPastClear = Boolean(
            authoritativeServerSite &&
              ((typeof authoritativeServerSite.sedId === 'string' &&
                authoritativeServerSite.sedId.trim().length > 0 &&
                authoritativeServerSite.sedId !== expectedSedId) ||
                (authoritativeServerSite.sedId === expectedSedId &&
                  typeof authoritativeServerSite.sedVersion === 'number' &&
                  authoritativeServerSite.sedVersion > expectedSedVersion) ||
                (authoritativeServerSite.sedId === expectedSedId &&
                  expectedSedStorageGeneration !== undefined &&
                  typeof authoritativeServerSite.sedStorageGeneration === 'string' &&
                  authoritativeServerSite.sedStorageGeneration !== expectedSedStorageGeneration) ||
                (typeof authoritativeServerSite.sedAmendmentHistoryVersion === 'number' &&
                  authoritativeServerSite.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
                authoritativeServerSite.sedStatus === 'bootstrapping' ||
                authoritativeServerSite.sedBootstrapLockStatus === 'locked' ||
                authoritativeServerSite.sedStatus === 'complete' ||
                authoritativeServerSite.sedStatus === 'partial'),
          );
          const serverMatchesClearedRebuildBaseline = Boolean(
            authoritativeServerSite &&
              !authoritativeServerSite.sedId &&
              (authoritativeServerSite.sedVersion === undefined ||
                authoritativeServerSite.sedVersion === null) &&
              !authoritativeServerSite.sedStoragePath &&
              !authoritativeServerSite.sedStorageGeneration &&
              (authoritativeServerSite.sedAmendmentHistoryVersion ?? 0) ===
                clearAck.historyVersion &&
              authoritativeServerSite.sedStatus === 'failed' &&
              authoritativeServerSite.sedCompleteness === 0 &&
              authoritativeServerSite.sedBootstrapLockStatus !== 'locked',
          );

          if (postReadLocal.movedPastClear || serverMovedPastClear) {
            shouldStartRebuildBootstrap = false;
            const supersededErr = Object.assign(
              new Error(
                'SED rebuild clear was superseded by newer site evidence or an active build.',
              ),
              {
                code: 'SED_CLEAR_SUPERSEDED',
                operationConfirmed: true,
                superseded: true,
                ...(isIdempotentReplay ? { idempotent: true } : {}),
              },
            );
            toast.error(supersededErr.message, { id: `sed-rebuild-${siteId}` });
            throw supersededErr;
          }

          if (needsAuthoritativeHeadRead && !serverMatchesClearedRebuildBaseline) {
            shouldStartRebuildBootstrap = false;
            const refreshErr = Object.assign(
              new Error(
                'SED clear operation was confirmed on server, but authoritative current site evidence did not match the cleared rebuild baseline. Refresh site evidence before continuing.',
              ),
              {
                code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
                operationConfirmed: true,
                refreshRequired: true,
                ...(isIdempotentReplay ? { idempotent: true } : {}),
              },
            );
            toast.error(refreshErr.message, { id: `sed-rebuild-${siteId}` });
            throw refreshErr;
          }

          const beforeCacheLocal = inspectLocalSiteForRebuildClear();
          if (
            !beforeCacheLocal.actorMatches ||
            !beforeCacheLocal.ownerMatches ||
            beforeCacheLocal.movedPastClear
          ) {
            shouldStartRebuildBootstrap = false;
            const supersededErr = Object.assign(
              new Error(
                'SED rebuild clear was superseded by newer site evidence or an active build.',
              ),
              {
                code: 'SED_CLEAR_SUPERSEDED',
                operationConfirmed: true,
                superseded: true,
                ...(isIdempotentReplay ? { idempotent: true } : {}),
              },
            );
            toast.error(supersededErr.message, { id: `sed-rebuild-${siteId}` });
            throw supersededErr;
          }

          invalidateCachedSed(expectedSedId);
          shouldStartRebuildBootstrap = true;

          const ackUpdatedAt = clearAck.updatedAt.trim();
          let stateReconciledForRebuild = false;
          set((s) => {
            if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
              return s;
            }
            return {
              siteRag: {
                ...s.siteRag,
                sites: s.siteRag.sites.map((x) => {
                  if (x.id !== siteId) return x;
                  if (siteOwnerUid(x, initiatingUid) !== ownerUid) return x;
                  const stMovedPastClear =
                    (x.sedId && x.sedId !== expectedSedId) ||
                    (x.sedId === expectedSedId &&
                      typeof x.sedVersion === 'number' &&
                      x.sedVersion > expectedSedVersion) ||
                    (x.sedId === expectedSedId &&
                      expectedSedStorageGeneration !== undefined &&
                      typeof x.sedStorageGeneration === 'string' &&
                      x.sedStorageGeneration !== expectedSedStorageGeneration) ||
                    (typeof x.sedAmendmentHistoryVersion === 'number' &&
                      x.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
                    x.sedStatus === 'bootstrapping' ||
                    x.sedBootstrapLockStatus === 'locked';
                  if (stMovedPastClear || serverMovedPastClear) {
                    return x;
                  }
                  stateReconciledForRebuild = true;
                  return {
                    ...x,
                    sedId: undefined,
                    sedVersion: undefined,
                    sedStoragePath: undefined,
                    sedStorageGeneration: undefined,
                    sedContentHash: undefined,
                    sedContentHashVersion: undefined,
                    sedSectionContentHashes: undefined,
                    sedSectionContentHashesV3: undefined,
                    sedReportMaterialHashes: undefined,
                    sedStatus: 'failed',
                    sedCompleteness: 0,
                    sedCollectedAt: undefined,
                    sedComputedAt: undefined,
                    sedGapCount: undefined,
                    sedConflictCount: undefined,
                    sedHighSeverityGapCount: undefined,
                    sedSectionStatuses: undefined,
                    sedKeyFindings: undefined,
                    sedFailureReason: undefined,
                    sedRebuildProgress: undefined,
                    sedAmendmentHistoryVersion: clearAck.historyVersion,
                    updatedAt: ackUpdatedAt,
                  };
                }),
              },
            };
          });
          if (!stateReconciledForRebuild) {
            shouldStartRebuildBootstrap = false;
            const supersededErr = Object.assign(
              new Error(
                'SED rebuild clear was superseded by newer site evidence or an active build.',
              ),
              {
                code: 'SED_CLEAR_SUPERSEDED',
                operationConfirmed: true,
                superseded: true,
                ...(isIdempotentReplay ? { idempotent: true } : {}),
              },
            );
            toast.error(supersededErr.message, { id: `sed-rebuild-${siteId}` });
            throw supersededErr;
          }
        } else {
          await get().updateSite(siteId, {
            sedStatus: 'failed',
            sedFailureReason: undefined,
            sedCompleteness: 0,
            sedComputedAt: undefined,
            sedRebuildProgress: undefined,
          } as Partial<import('../types/site').SiteEntity>);
        }
        // Intentional: DIFF_GUARD_ENABLED=false escape hatch — bypasses R-28 guard by design.
        // R-28 guard scope: rebuildSedWithGuard only (see docs/runbooks/sed-rebuild-safely.md).
        if (shouldStartRebuildBootstrap) {
          await get().retrySEDBootstrap(siteId, { forceRefresh: true });
        }
      }
      // W22-7 — emit one `sed.rebuild` audit event per user-initiated
      // rebuild so the admin RECENT ACTIVITY panel records the trigger.
      // Mirrors AdminPanel's bulk-rebuild emit shape (action + target.type
      // 'site' + label = site name); metadata carries the prior sedId so
      // the activity row can show "Rebuild started; prior SED <id>" even
      // before the new bootstrap settles. Soft-fail: emission errors
      // never block the rebuild path.
      try {
        const siteAfter = get().siteRag.sites.find(s => s.id === siteId);
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'sed.rebuild',
          target: { type: 'site', id: siteId, label: siteAfter?.name },
          metadata: {
            prevSedId: oldSedId,
            trigger: rebuildOpts.force ? 'forced-full' : 'manual',
          },
        });
      } catch (e) {
        console.debug('[rebuildSiteSed] audit emit soft-failed:', (e as Error)?.message);
      }
    } finally {
      setSedStalenessSuppressed(siteId, false);
    }
  },

  deleteSiteSed: async (siteId: string) => {
    // SDL-2 — destructive SED clear via server-owned `/api/sites/:siteId/sed-clear`
    // with full baseline preconditions (`expectedSedId`, `expectedSedVersion`,
    // `expectedHistoryVersion`, `expectedSedStorageGeneration`), server-derived
    // `clearIntent: 'delete'`, and retained operation/replay identity.
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const sedId = site.sedId;
    if (!sedId) return; // idempotent — nothing to delete.

    await get().assertSiteWritable(siteId);

    const { setSedStalenessSuppressed } = await import('../lib/sedStaleness');
    setSedStalenessSuppressed(siteId, true);

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const initiatingUid = user.uid;
      const ownerUid = siteOwnerUid(site, initiatingUid);
      const { getCachedSedStorageGeneration, invalidateCachedSed } = await import(
        '../services/sedLoader'
      );
      const { authedFetch } = await import('../lib/authedFetch');

      const expectedSedVersion =
        typeof site.sedVersion === 'number' && Number.isFinite(site.sedVersion)
          ? site.sedVersion
          : 1;
      const expectedHistoryVersion =
        typeof site.sedAmendmentHistoryVersion === 'number' &&
        Number.isFinite(site.sedAmendmentHistoryVersion)
          ? site.sedAmendmentHistoryVersion
          : 0;
      const expectedSedStorageGeneration =
        getCachedSedStorageGeneration(sedId, site.sedStoragePath) ??
        (typeof site.sedStorageGeneration === 'string' && site.sedStorageGeneration.trim()
          ? site.sedStorageGeneration.trim()
          : undefined);

      const clearKey = JSON.stringify([
        initiatingUid,
        ownerUid,
        siteId,
        'delete',
        sedId,
        expectedSedVersion,
        expectedHistoryVersion,
        expectedSedStorageGeneration ?? null,
      ]);
      const clearEnvelope =
        pendingSedClearOperations.get(clearKey) ??
        {
          operationId: `sed-clear-delete-${siteId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
            /[^A-Za-z0-9._:-]/g,
            '-',
          ),
          ownerUid,
          clearIntent: 'delete' as const,
          expectedSedId: sedId,
          expectedSedVersion,
          expectedHistoryVersion,
          ...(expectedSedStorageGeneration ? { expectedSedStorageGeneration } : {}),
        };
      pendingSedClearOperations.set(clearKey, clearEnvelope);
      const clearPendingDeleteOpIfTerminal = (status?: number) => {
        if (
          status === undefined ||
          (status >= 400 && status < 500 && status !== 408 && status !== 429)
        ) {
          pendingSedClearOperations.delete(clearKey);
        }
      };

      const clearRes = await authedFetch(
        `/api/sites/${encodeURIComponent(siteId)}/sed-clear`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clearEnvelope),
        },
      );
      if (!clearRes.ok) {
        clearPendingDeleteOpIfTerminal(clearRes.status);
        const errBody = await clearRes.json().catch(() => ({}));
        throw new Error(
          (errBody as { error?: string })?.error ||
            `SED pointer clear failed (HTTP ${clearRes.status})`,
        );
      }
      const clearAck = (await clearRes.json()) as {
        ok?: boolean;
        action?: string;
        clearIntent?: string;
        operationId?: string;
        ownerUid?: string;
        siteId?: string;
        clearedSedId?: string | null;
        clearedSedVersion?: number;
        clearedSedStorageGeneration?: string | null;
        historyVersion?: number;
        sedStatus?: string | null;
        sedCompleteness?: number | null;
        updatedAt?: string;
        idempotentReplay?: boolean;
      };
      if (
        !clearAck ||
        clearAck.ok !== true ||
        clearAck.action !== 'clear-pointer' ||
        clearAck.clearIntent !== 'delete' ||
        clearAck.operationId !== clearEnvelope.operationId ||
        clearAck.ownerUid !== ownerUid ||
        clearAck.siteId !== siteId ||
        clearAck.clearedSedId !== sedId ||
        clearAck.clearedSedVersion !== expectedSedVersion ||
        (expectedSedStorageGeneration !== undefined &&
          clearAck.clearedSedStorageGeneration !== expectedSedStorageGeneration) ||
        clearAck.historyVersion !== expectedHistoryVersion ||
        clearAck.sedStatus !== null ||
        clearAck.sedCompleteness !== null ||
        typeof clearAck.updatedAt !== 'string' ||
        !clearAck.updatedAt.trim()
      ) {
        throw new Error('Malformed or mismatched SED clear acknowledgement.');
      }
      clearPendingDeleteOpIfTerminal();
      const inspectLocalSiteForDeleteClear = () => {
        const actorMatches = auth.currentUser?.uid === initiatingUid;
        const localSite = get().siteRag.sites.find((s) => s.id === siteId) ?? null;
        const ownerMatches = Boolean(
          localSite && siteOwnerUid(localSite, initiatingUid) === ownerUid,
        );
        const movedPastClear = Boolean(
          !actorMatches ||
            !ownerMatches ||
            !localSite ||
            (localSite.sedId && localSite.sedId !== sedId) ||
            (localSite.sedId === sedId &&
              typeof localSite.sedVersion === 'number' &&
              localSite.sedVersion > expectedSedVersion) ||
            (localSite.sedId === sedId &&
              expectedSedStorageGeneration !== undefined &&
              typeof localSite.sedStorageGeneration === 'string' &&
              localSite.sedStorageGeneration !== expectedSedStorageGeneration) ||
            (typeof localSite.sedAmendmentHistoryVersion === 'number' &&
              localSite.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
            localSite.sedStatus === 'bootstrapping' ||
            localSite.sedBootstrapLockStatus === 'locked',
        );
        return { actorMatches, ownerMatches, localSite, movedPastClear };
      };

      const preReadLocal = inspectLocalSiteForDeleteClear();
      if (!preReadLocal.actorMatches || !preReadLocal.ownerMatches) {
        throw new Error(
          'The signed-in account or site owner changed during the SED delete operation.',
        );
      }

      const isIdempotentReplay = clearAck.idempotentReplay === true;
      const needsAuthoritativeHeadRead = isIdempotentReplay || preReadLocal.movedPastClear;
      let authoritativeServerSite: Record<string, unknown> | null = null;
      if (needsAuthoritativeHeadRead) {
        try {
          const firestoreModule = await import('firebase/firestore');
          const siteDocRef = firestoreModule.doc(db, `users/${ownerUid}/sites`, siteId);
          const getServerDoc =
            typeof (firestoreModule as { getDocFromServer?: unknown }).getDocFromServer ===
            'function'
              ? (firestoreModule as { getDocFromServer: typeof firestoreModule.getDoc })
                  .getDocFromServer
              : firestoreModule.getDoc;
          if (typeof getServerDoc === 'function') {
            const snap = await getServerDoc(siteDocRef);
            if (snap && typeof snap.exists === 'function' && snap.exists()) {
              authoritativeServerSite = (snap.data() ?? null) as Record<string, unknown> | null;
            }
          }
        } catch {
          authoritativeServerSite = null;
        }
      }

      const postReadLocal = inspectLocalSiteForDeleteClear();
      if (!postReadLocal.actorMatches || !postReadLocal.ownerMatches) {
        throw new Error(
          'The signed-in account or site owner changed during the SED delete operation.',
        );
      }

      if (needsAuthoritativeHeadRead && !authoritativeServerSite) {
        throw Object.assign(
          new Error(
            'SED delete operation was confirmed on server, but authoritative current site evidence could not be refreshed. Refresh site evidence before continuing.',
          ),
          {
            code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
            operationConfirmed: true,
            refreshRequired: true,
            ...(isIdempotentReplay ? { idempotent: true } : {}),
          },
        );
      }

      const serverMovedPastClear = Boolean(
        authoritativeServerSite &&
          ((typeof authoritativeServerSite.sedId === 'string' &&
            authoritativeServerSite.sedId.trim().length > 0 &&
            authoritativeServerSite.sedId !== sedId) ||
            (authoritativeServerSite.sedId === sedId &&
              typeof authoritativeServerSite.sedVersion === 'number' &&
              authoritativeServerSite.sedVersion > expectedSedVersion) ||
            (authoritativeServerSite.sedId === sedId &&
              expectedSedStorageGeneration !== undefined &&
              typeof authoritativeServerSite.sedStorageGeneration === 'string' &&
              authoritativeServerSite.sedStorageGeneration !== expectedSedStorageGeneration) ||
            (typeof authoritativeServerSite.sedAmendmentHistoryVersion === 'number' &&
              authoritativeServerSite.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
            authoritativeServerSite.sedStatus === 'bootstrapping' ||
            authoritativeServerSite.sedBootstrapLockStatus === 'locked' ||
            authoritativeServerSite.sedStatus === 'complete' ||
            authoritativeServerSite.sedStatus === 'partial'),
      );
      const serverMatchesClearedDeleteBaseline = Boolean(
        authoritativeServerSite &&
          !authoritativeServerSite.sedId &&
          (authoritativeServerSite.sedVersion === undefined ||
            authoritativeServerSite.sedVersion === null) &&
          !authoritativeServerSite.sedStoragePath &&
          !authoritativeServerSite.sedStorageGeneration &&
          (authoritativeServerSite.sedAmendmentHistoryVersion ?? 0) === clearAck.historyVersion &&
          (authoritativeServerSite.sedStatus === undefined ||
            authoritativeServerSite.sedStatus === null) &&
          (authoritativeServerSite.sedCompleteness === undefined ||
            authoritativeServerSite.sedCompleteness === null) &&
          authoritativeServerSite.sedBootstrapLockStatus !== 'locked',
      );

      if (postReadLocal.movedPastClear || serverMovedPastClear) {
        throw Object.assign(
          new Error(
            'SED delete clear was superseded by newer site evidence or an active build.',
          ),
          {
            code: 'SED_CLEAR_SUPERSEDED',
            operationConfirmed: true,
            superseded: true,
            ...(isIdempotentReplay ? { idempotent: true } : {}),
          },
        );
      }

      if (needsAuthoritativeHeadRead && !serverMatchesClearedDeleteBaseline) {
        throw Object.assign(
          new Error(
            'SED delete operation was confirmed on server, but authoritative current site evidence did not match the cleared delete baseline. Refresh site evidence before continuing.',
          ),
          {
            code: 'SED_AUTHORITATIVE_REFRESH_REQUIRED',
            operationConfirmed: true,
            refreshRequired: true,
            ...(isIdempotentReplay ? { idempotent: true } : {}),
          },
        );
      }

      const beforeCacheLocal = inspectLocalSiteForDeleteClear();
      if (
        !beforeCacheLocal.actorMatches ||
        !beforeCacheLocal.ownerMatches ||
        beforeCacheLocal.movedPastClear
      ) {
        throw Object.assign(
          new Error(
            'SED delete clear was superseded by newer site evidence or an active build.',
          ),
          {
            code: 'SED_CLEAR_SUPERSEDED',
            operationConfirmed: true,
            superseded: true,
            ...(isIdempotentReplay ? { idempotent: true } : {}),
          },
        );
      }

      invalidateCachedSed(sedId);

      const ackUpdatedAt = clearAck.updatedAt.trim();
      let stateReconciledForDelete = false;

      // Reconcile in-memory state from the authoritative server ACK, preserving any newer head/history/build
      set(s => {
        if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
          return s;
        }
        return {
          siteRag: {
            ...s.siteRag,
            sites: s.siteRag.sites.map(x => {
              if (x.id !== siteId) return x;
              if (siteOwnerUid(x, initiatingUid) !== ownerUid) return x;
              const stMovedPastClear =
                (x.sedId && x.sedId !== sedId) ||
                (x.sedId === sedId &&
                  typeof x.sedVersion === 'number' &&
                  x.sedVersion > expectedSedVersion) ||
                (x.sedId === sedId &&
                  expectedSedStorageGeneration !== undefined &&
                  typeof x.sedStorageGeneration === 'string' &&
                  x.sedStorageGeneration !== expectedSedStorageGeneration) ||
                (typeof x.sedAmendmentHistoryVersion === 'number' &&
                  x.sedAmendmentHistoryVersion > expectedHistoryVersion) ||
                x.sedStatus === 'bootstrapping' ||
                x.sedBootstrapLockStatus === 'locked';
              if (stMovedPastClear || serverMovedPastClear) {
                return x;
              }
              stateReconciledForDelete = true;
              return {
                ...x,
                sedId: undefined,
                sedVersion: undefined,
                sedStoragePath: undefined,
                sedStorageGeneration: undefined,
                sedContentHash: undefined,
                sedContentHashVersion: undefined,
                sedSectionContentHashes: undefined,
                sedSectionContentHashesV3: undefined,
                sedReportMaterialHashes: undefined,
                sedStatus: undefined,
                sedCompleteness: undefined,
                sedCollectedAt: undefined,
                sedComputedAt: undefined,
                sedGapCount: undefined,
                sedConflictCount: undefined,
                sedHighSeverityGapCount: undefined,
                sedSectionStatuses: undefined,
                sedKeyFindings: undefined,
                sedFailureReason: undefined,
                sedRebuildProgress: undefined,
                evidenceReadiness: undefined,
                sedAmendmentHistoryVersion: clearAck.historyVersion,
                updatedAt: ackUpdatedAt,
              };
            }),
          },
        };
      });
      if (!stateReconciledForDelete) {
        throw Object.assign(
          new Error(
            'SED delete clear was superseded by newer site evidence or an active build.',
          ),
          {
            code: 'SED_CLEAR_SUPERSEDED',
            operationConfirmed: true,
            superseded: true,
            ...(isIdempotentReplay ? { idempotent: true } : {}),
          },
        );
      }

      // Tombstone + legacy /seds subdoc cleanup (no direct client site authority patch)
      const { doc, writeBatch, serverTimestamp } = await import('firebase/firestore');
      const batch = writeBatch(db);
      batch.set(doc(db, `users/${initiatingUid}/deletedSeds`, sedId), {
        sedId,
        siteId,
        deletedAt: serverTimestamp(),
        reason: 'manual-delete',
        actorUid: initiatingUid,
      });
      batch.delete(doc(db, `users/${ownerUid}/sites/${siteId}/seds`, sedId));
      await batch.commit();

      // Best-effort legacy `/seds/{sedId}.json` cleanup (never touches immutable revision paths)
      try {
        await authedFetch('/api/storage/delete', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: `users/${ownerUid}/sites/${siteId}/seds/${sedId}.json` }),
        });
      } catch (e) {
        console.debug('[deleteSiteSed] storage cleanup soft-failed:', (e as Error)?.message);
      }

      if (!isIdempotentReplay) {
        try {
          const { emitAuditEvent } = await import('../lib/auditLogClient');
          await emitAuditEvent({
            action: 'sed.delete',
            target: { type: 'site', id: siteId, label: site.name },
            metadata: { sedId, reason: 'manual-delete', actorUid: initiatingUid },
          });
        } catch (e) {
          console.debug('[deleteSiteSed] audit emit soft-failed:', (e as Error)?.message);
        }
      }
    } catch (e) {
      console.warn('[deleteSiteSed] Firestore cascade failed:', (e as Error)?.message);
      throw e;
    } finally {
      setSedStalenessSuppressed(siteId, false);
    }
  },

  forceAbortSedRebuild: async (siteId: string) => {
    // RSF-3 — Synchronously park the site as 'failed' so the
    // SedRebuildBanner.handleRetry path can re-enter via
    // `rebuildSiteSed` without racing the previous run.
    //
    // We can't actually KILL the in-flight Promise (JavaScript has
    // no cooperative cancellation for the doc-aware Gemini awaits the
    // body is parked in). What we CAN do is mark the site doc as
    // failed so:
    //   1. The bootstrap's idempotency guard (sedBootstrap.ts:599
    //      `site.sedId && site.sedStatus && site.sedStatus !== 'failed'`)
    //      won't short-circuit the retry.
    //   2. When the stale run eventually returns (or hits RSF-2's
    //      wall-clock), its `markSedFailed` write lands on top of the
    //      new attempt's 'bootstrapping' flip — last-write-wins, but
    //      the retry already kicked off so the user sees progress.
    //   3. The store's `setSedStalenessSuppressed` flag prevents the
    //      retry's own field flips from firing a stale-toast loop.
    //
    // The breadcrumb shape mirrors `markSedFailed` (RSF-2) so callers
    // / dashboards can treat both code paths uniformly.
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    // Idempotent — never re-stamp an already-failed site.
    if (site.sedStatus === 'failed') return;
    const startedMs = site.sedStatusStartedAt
      ? Date.parse(site.sedStatusStartedAt)
      : NaN;
    const elapsedMs = Number.isFinite(startedMs)
      ? Math.max(0, Date.now() - startedMs)
      : 0;
    const stage = site.sedRebuildProgress?.stage ?? 'unknown';
    try {
      await get().updateSite(siteId, {
        sedStatus: 'failed',
        sedFailureReason: 'Force-aborted: previous rebuild exceeded the 30-min hard ceiling.',
        sedFailure: {
          reason: 'force-aborted by user retry after stuck-bootstrap threshold',
          elapsedMs,
          stage,
          at: new Date().toISOString(),
        },
      } as Partial<import('../types/site').SiteEntity>);
    } catch (e) {
      console.warn(`[forceAbortSedRebuild] failed for site ${siteId}:`, (e as Error)?.message);
    }
  },

  refreshSedSections: async (siteId, sectionIds, opts) => {
    // Section-scoped refresh of the site SED. Maps the requested
    // section ids → provider ids via SED_SECTION_PROVIDERS, then
    // delegates to topUpSiteSed with onlyProviders. Sections whose
    // provider list is empty (parcel, regulatory, adamsDocuments,
    // siteProfile) are silently dropped — they aren't user-refreshable.
    if (sectionIds.length === 0) return { ok: false, persisted: false, upgraded: [], reason: 'No sections selected.' };
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return { ok: false, persisted: false, upgraded: [], reason: 'Site not loaded.' };
    const { computeSedSectionStatuses, resolveRefreshProviders } = await import('../lib/sedStaleness');
    // Use the same statuses the popover used so the provider mapping
    // matches what the user saw. SED is unavailable here (we'd need a
    // fetch) but the resolution only reads the static provider/producer
    // maps regardless of the SED contents.
    const statuses = computeSedSectionStatuses(null);
    // Registry providers PLUS the intent-stamped/derived producers
    // (technology, cooling, power, firmCapacity, lcoe), which have no
    // `fetchAllSiteData` provider id but are still regenerable. That mapping
    // used to live here as three inline pushes, so every surface that OFFERS
    // a refresh disagreed with this action about what is refreshable — and
    // offered refreshes it then rejected. One shared resolution now.
    const providers = resolveRefreshProviders(statuses, sectionIds);

    if (providers.length === 0) {
      console.warn('[refreshSedSections] no provider-backed sections in selection', sectionIds);
      return {
        ok: false,
        persisted: false,
        upgraded: [],
        reason: 'That section has no data provider, so there is nothing to re-run.',
      };
    }
    set((s) => ({
      siteRag: {
        ...s.siteRag, isLoading: true, progress: 5,
        progressStage: `Refreshing ${sectionIds.length} evidence section${sectionIds.length === 1 ? '' : 's'}…`,
        error: null,
      },
    }));
    const startedAt = Date.now();
    const sedVersionFrom = site.sedVersion ?? 0;
    let outcome: { ok: boolean; persisted: boolean; unchanged?: boolean; superseded?: boolean; upgraded: string[]; reason?: string } = {
      ok: false, persisted: false, upgraded: [],
    };
    try {
      const { topUpSiteSed } = await import('../services/sedBootstrap');
      // Intentional: ADDITIVE top-up, not a full rebuild.
      // R-28 guard scope: rebuildSedWithGuard only (see docs/runbooks/sed-rebuild-safely.md).
      const result = await topUpSiteSed(siteId, { onlyProviders: providers, abortSignal: opts?.abortSignal });
      const sedVersionTo = result?.sedVersion ?? sedVersionFrom;
      // `upgraded` is the provider's own report of which sections changed IN
      // MEMORY. It is not proof the change was saved: observed 2026-07-27 on a
      // site whose SED write 403s (owner-scoped storage path), where the run
      // reported upgraded=['publicSentiment'] and the section was still absent
      // after a reload. A caller that trusts `upgraded` alone shows a success
      // toast the very next render contradicts.
      //
      // `result.outcome` is the writer's OWN report of what happened to the
      // bytes — 'saved' / 'save-failed' / 'no-change'. The old version-delta
      // heuristic ("version advanced ⇒ persisted") conflated a failed save
      // with a run that had nothing new to persist, and misread idempotent
      // same-version rewrites as failures (observed live 2026-07-27: a
      // "could not be saved" toast over bytes that HAD landed). A null
      // result remains a hard failure (preconditions or main-path save
      // failure — the site carries sedFailureReason for the latter).
      if (result == null) {
        outcome = {
          ok: false,
          persisted: false,
          upgraded: [],
          reason: get().siteRag.sites.find(s => s.id === siteId)?.sedFailureReason
            ?? 'The refresh could not run — see the site status for the cause.',
        };
      } else if (result.outcome === 'save-failed') {
        outcome = {
          ok: false,
          persisted: false,
          upgraded: [...result.upgraded],
          reason: result.saveFailReason
            ?? 'The data was collected but could NOT be saved, so it will be gone on reload.',
        };
      } else if (result.outcome === 'sync-unresolved') {
        outcome = {
          ok: false,
          persisted: true,
          upgraded: [...result.upgraded],
          reason: result.saveFailReason
            ?? 'Evidence was published on the server, but reloading the committed document failed.',
        };
      } else if (result.outcome === 'no-change') {
        outcome = {
          ok: true,
          persisted: false,
          unchanged: true,
          upgraded: [],
        };
      } else if (result.outcome === 'superseded') {
        // The top-up stopped before changing anything: the live site pointer
        // moved, this tab's copy of the site was stale, or a build owns the
        // evidence. Not a success, and nothing to record as checked.
        outcome = {
          ok: false,
          persisted: false,
          superseded: true,
          upgraded: [],
          reason: SED_TOP_UP_REFUSAL_MESSAGE[result.supersededReason ?? 'pointer-moved'],
        };
        // A company-shared (foreign-owned) site has no live listener in this
        // session, so a stale copy would refuse every refresh. Re-read it from
        // its owner; own sites heal through their listener.
        const supersededSite = get().siteRag.sites.find(s => s.id === siteId);
        const ownerUid = supersededSite?._ownerUid;
        if (ownerUid) {
          const { auth } = await import('../firebase');
          if (ownerUid !== auth.currentUser?.uid) await get().ensureReportOwnerSiteLoaded(siteId, ownerUid);
        }
      } else {
        outcome = { ok: true, persisted: true, upgraded: [...result.upgraded] };
      }

      // A refresh that overtook an operator's amendment must not report a
      // plain green "refresh complete". `topUpSiteSed` deliberately keeps the
      // NEWER evidence rather than re-asserting a stale human decision over
      // it, so the decision has to surface somewhere a person will see it.
      // Persist it as an evidence conflict — the same shape and the same
      // helper `reapplyAmendmentsAfterRebuild` uses after a rebuild — so the
      // existing conflict-resolution UI picks it up instead of the divergence
      // living only in a console warning.
      if (result?.graftAmendmentConflicts?.length) {
        try {
          const [{ persistSedConflicts }, { loadSedById }] = await Promise.all([
            import('../services/evidenceConflict'),
            import('../services/sedLoader'),
          ]);
          const refreshedSite = get().siteRag.sites.find(s => s.id === siteId);
          const sedForConflicts = refreshedSite?.sedId
            ? await loadSedById(refreshedSite.sedId, refreshedSite.sedStoragePath, refreshedSite.sedVersion)
            : null;
          if (sedForConflicts) {
            const { auth } = await import('../firebase');
            const conflictUid = auth.currentUser?.uid;
            const existingConflicts = sedForConflicts.evidenceConflicts ?? [];
            // #sed-owner-path — WITHOUT the canonical pointer this falls back
            // to the caller-uid key, which forks a company-shared site's SED.
            if (conflictUid) await persistSedConflicts(conflictUid, siteId, sedForConflicts, [
              ...existingConflicts,
              ...result.graftAmendmentConflicts.map(c => ({
                sectionId: c.sectionId,
                field: c.field,
                providerValue: c.providerValueNow,
                providerSource: 'refresh',
                docValue: c.userValue,
                docId: '<user-amendment>',
                docName: 'User amendment superseded by refresh',
                severity: 'medium' as const,
                explanation:
                  `The refreshed source value for ${c.field} is neither the value your amendment `
                  + `replaced nor the value you set. Your amendment set it to ${String(c.userValue)}; `
                  + `the source now reports ${String(c.providerValueNow)}. The newer source value was kept.`,
                suggestedResolution: 'manual-review' as const,
                authoritative: 'manual-review' as const,
                authoredValue: c.userValue,
              })),
            ], refreshedSite?.sedStoragePath);
          }
        } catch (conflictErr) {
          console.warn('[refreshSedSections] amendment-conflict persist failed:', (conflictErr as Error)?.message);
        }
      }
      // Nag-loop fix — a COMPLETED run (saved or nothing-new) counts as
      // having LOOKED at the requested sections. The currency planner
      // compares input-change stamps against max(collected, checked), so
      // without this a refresh that found nothing new re-demanded itself
      // forever. Only the sections the run could actually cover are
      // stamped (provider-less requests were silently dropped above).
      if (result != null && (result.outcome === 'saved' || result.outcome === 'no-change')) {
        try {
          const { USER_REFRESHABLE_SECTIONS } = await import('../lib/sedStaleness');
          const nowIso = new Date().toISOString();
          const prior = get().siteRag.sites.find(s => s.id === siteId)?.sedSectionCheckedAt ?? {};
          const stamped: Record<string, string> = { ...prior };
          for (const sid of sectionIds) {
            if (USER_REFRESHABLE_SECTIONS.has(sid)) stamped[sid] = nowIso;
          }
          await get().updateSite(siteId, { sedSectionCheckedAt: stamped });
        } catch (e) {
          console.warn('[refreshSedSections] checkedAt stamp failed:', (e as Error)?.message);
        }
      }
      // Recompute readiness so the dashboard chip + landing page pill
      // reflect the new evidence base immediately.
      try { await get().recomputeEvidenceReadiness(siteId); } catch { /* best-effort */ }
      // RD-1 (E) — one-time auto-seed of the core refdata map-layer set
      // (grid / flood / wetlands / protected areas) after the site's first
      // successful evidence run. refdataOnly ⇒ never touches live ArcGIS;
      // unseeded datasets skip silently. Fire-and-forget: layer seeding
      // must never delay or fail the refresh outcome.
      if (result != null && (result.outcome === 'saved' || result.outcome === 'no-change')) {
        void (async () => {
          try {
            const { runAutoSeedCoreLayers } = await import('../services/siteLayers/autoSeedCoreLayers');
            const addedTotal = await runAutoSeedCoreLayers({
              site: get().siteRag.sites.find(s => s.id === siteId),
              siteId,
              fetchAndMergeRegionalLayers: opts => get().fetchAndMergeRegionalLayers(siteId, opts),
              persistSeedStamp: stamp => get().updateSite(siteId, { autoLayerSeed: stamp }),
            });
            if (addedTotal != null && addedTotal > 0) {
              console.info(`[autoSeedCoreLayers] added ${addedTotal} layer(s) for site ${siteId}`);
            }
          } catch (e) {
            console.warn('[autoSeedCoreLayers] skipped:', (e as Error)?.message);
          }
        })();
      }
      // Telemetry — soft-fail emit. Lets us measure how often users
      // pull the explicit refresh lever vs the implicit topUp inside
      // generateTypedReport.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'sed-section-refresh',
          target: { type: 'site', id: siteId, label: site.name },
          metadata: {
            sectionIds,
            providers,
            sedVersionFrom,
            sedVersionTo,
            durationMs: Date.now() - startedAt,
            upgraded: result?.upgraded ?? [],
            outcome: result?.outcome ?? null,
            ...(result?.supersededReason ? { supersededReason: result.supersededReason } : {}),
          },
        });
      } catch { /* audit is best-effort */ }
    } catch (e) {
      console.warn('[refreshSedSections] failed:', (e as Error)?.message);
      outcome = {
        ok: false,
        persisted: false,
        upgraded: [],
        reason: (e as Error)?.message || 'The refresh failed for an unknown reason.',
      };
    } finally {
      set((s) => ({
        siteRag: {
          ...s.siteRag, isLoading: false, progress: 100, progressStage: 'Complete',
        },
      }));
    }
    return outcome;
  },

  refreshLiveLiteratureForSection: async (siteId) => {
    // LLW-FOLLOWUP-SECTION-REFRESH-BUTTON — one-click per-site Live
    // Literature refresh wired from the SED Variable Browser. Bypasses
    // the 14d TTL gate (user explicitly asked) but respects the LLW-4
    // 60s soft rate-limit and audits with `source: 'manual-section'`.
    //
    // Why this lives next to refreshSedSections but doesn't reuse it:
    // refreshSedSections routes through topUpSiteSed which (a) honours
    // the TTL gate, (b) audits with source: 'refresh' / 'bootstrap',
    // and (c) doesn't expose a force-refresh switch. CL-2 needs all
    // three different defaults, so we delegate directly to
    // `refreshExternalLiteratureForSingleSite` and persist via the
    // standalone sedLoader.saveSedById helper.
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site || !site.sedId) {
      console.warn('[refreshLiveLiteratureForSection] site has no SED', siteId);
      return 'load-failed';
    }
    set((s) => ({
      siteRag: {
        ...s.siteRag, isLoading: true, progress: 5,
        progressStage: 'Refreshing live literature…',
        error: null,
      },
    }));
    try {
      const { loadSedById, saveSedById } = await import('../services/sedLoader');
      const loadedSed = await loadSedById(site.sedId, site.sedStoragePath);
      if (!loadedSed) {
        console.warn('[refreshLiveLiteratureForSection] could not load SED for site', siteId);
        return 'load-failed';
      }
      const sed = structuredClone(loadedSed);
      const { refreshExternalLiteratureForSingleSite } = await import(
        '../services/scheduledRebuild'
      );
      const outcome = await refreshExternalLiteratureForSingleSite({
        site,
        sed,
        persistSed: async (_site, mutated) => {
          if (!await saveSedById(mutated, site.sedStoragePath)) {
            throw new Error('The live literature update could not be confirmed as saved. Reload evidence before retrying.');
          }
        },
      });
      // Refreshed admits a confirmed save; skipped uses the existing evidence.
      if (outcome.outcome === 'refreshed' || outcome.outcome === 'fresh-skipped') {
        try { await get().recomputeEvidenceReadiness(siteId); } catch { /* best-effort */ }
      }
      return outcome.outcome;
    } catch (e) {
      console.warn('[refreshLiveLiteratureForSection] threw:', (e as Error)?.message);
      return 'orchestrator-failed';
    } finally {
      set((s) => ({
        siteRag: {
          ...s.siteRag, isLoading: false, progress: 100, progressStage: 'Complete',
        },
      }));
    }
  },

  migrateFinancialDocsToSite: async () => {
    // One-shot at app load: financial docs uploaded via the Financial
    // Model tab BEFORE the writer-bug fix landed only on report.documents
    // (deprecated). Promote any such stragglers onto site.documents
    // (folder='Financial' if untagged) and clear the report's array.
    // Reports whose docs are already on the site are left untouched.
    const state = get();
    if (!state.siteRag.isSitesLoaded || !state.siteRag.isReportsLoaded) return;
    const { planFinancialDocsMigration } = await import('./migrations/migrateFinancialDocsToSite');
    const plan = planFinancialDocsMigration(state.siteRag.sites, state.siteRag.reports);
    if (plan.sitePatches.size === 0 && plan.clearedReportIds.length === 0) return;
    console.log(`[FIN-DOCS-MIGRATION] promoting docs on ${plan.sitePatches.size} site(s), clearing ${plan.clearedReportIds.length} report(s)`);

    for (const [siteId, documents] of plan.sitePatches) {
      try {
        const documentIds = [...new Set(documents.map(d => d.id))];
        await get().updateSite(siteId, { documents, documentIds });
      } catch (e) {
        console.warn(`[FIN-DOCS-MIGRATION] site ${siteId} failed:`, (e as Error)?.message);
      }
    }
    for (const reportId of plan.clearedReportIds) {
      try {
        await get().updateRagReport(reportId, { documents: [] });
      } catch (e) {
        console.warn(`[FIN-DOCS-MIGRATION] report ${reportId} clear failed:`, (e as Error)?.message);
      }
    }
  },

  recomputeEvidenceReadiness: async (siteId) => {
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    // #non-owner-write-guard — a CROSS-ORG viewer opens a company-shared,
    // foreign-owned site via the company tree. Post #owner-path-sweep the
    // persist WOULD now succeed (updateSite writes to the owner's path, which
    // the rules authorize for company-tree members on unlocked sites), so
    // this is no longer a permission workaround — it is a deliberate
    // write-amplification policy: the recompute is purely a cache-warming
    // write for the dashboard chip, and AuthGate fires it for every site on
    // every reload. Letting every company viewer re-persist every shared
    // site's readiness would multiply Firestore writes (and lock-denial
    // noise on locked sites) for zero freshness gain. Owner stays the single
    // writer. Legacy / untagged sites (no `_sourceScope`) were loaded on the
    // own-path hop and count as 'mine' — same predicate the SiteSourceFilter
    // uses. Read-only viewers keep the persisted readiness exactly as the
    // owner last computed it.
    const isOwnSite = (site._sourceScope ?? 'mine') === 'mine';
    if (!isOwnSite) return;
    // Resolve the applicable report types — every type the site has
    // produced, falling back to feasibility for fresh sites. Computed
    // up-front because the cheap summary/action recompute needs it even
    // when the (expensive) readiness recompute is skipped by the cadence
    // guard below.
    const siteReports = state.siteRag.reports.filter(report => reportBelongsToSite(site, report));
    const types = [...new Set(siteReports.map(r => r.reportType ?? 'feasibility'))] as import('../config/reportTypes').ReportTypeId[];

    // Skip the GCS-fetch path when the persisted readiness was computed
    // within the last hour. AuthGate fires this for every site on every
    // reload — without this guard, a portfolio of 20+ sites kicks off
    // 20+ GCS fetches every refresh and floods state writes that amplify
    // any downstream re-render pathologies.
    //
    // The site summary (DC3) and next-action (DC4) are cheap (pure, no
    // I/O) and depend on the report set rather than the SED, so when the
    // cadence guard fires we still refresh those — but only if they're
    // stale or missing. "Stale" = the cached summary's `sourceReportId`
    // is no longer the site's most-actionable report (a new report
    // landed) OR the action's label/target changed.
    const persistedAt = site.evidenceReadiness?.computedAt;
    const readinessFresh = (() => {
      if (!persistedAt) return false;
      const persistedMs = new Date(persistedAt).getTime();
      if (!Number.isFinite(persistedMs)) return false;
      const ageMs = Date.now() - persistedMs;
      if (Number.isFinite(ageMs) && ageMs < 60 * 60 * 1000) return true;
      const sedMs = site.sedComputedAt ? new Date(site.sedComputedAt).getTime() : Number.NaN;
      const configMs = site.siteConfigUpdatedAt ? new Date(site.siteConfigUpdatedAt).getTime() : Number.NaN;
      const updatedMs = site.updatedAt ? new Date(site.updatedAt).getTime() : Number.NaN;
      return (!Number.isFinite(sedMs) || persistedMs >= sedMs)
        && (!Number.isFinite(configMs) || persistedMs >= configMs)
        && (!Number.isFinite(updatedMs) || persistedMs >= updatedMs);
    })();

    // ITX-1 — include `targetReportId` so a freshness diff catches the
    // case where the label is identical but the action's resolution
    // target changed (e.g. a new rerun that ALSO holds unresolved
    // findings flips a single-report site into a multi-report rollup,
    // both with the same count but different routing semantics).
    const actionShape = (a: import('../types/site').SiteEntity['nextSuggestedAction']) =>
      a ? `${a.label}|${a.action}|${a.reportType ?? ''}|${a.targetReportId ?? ''}` : '';

    try {
      const { buildSiteSummary, buildNextSuggestedAction, mostActionableReport } = await import('../lib/siteCardSummary');
      const nextSummary = buildSiteSummary(site, siteReports);
      const nextAction = buildNextSuggestedAction(site, siteReports);

      if (readinessFresh) {
        // Cheap-path: only the summary / next-action may need a refresh.
        const prevSummary = site.siteSummary;
        const expectedSource = mostActionableReport(site, siteReports)?.id;
        const summaryStale = !prevSummary
          || prevSummary.text !== nextSummary.text
          || prevSummary.sourceReportId !== expectedSource;
        const actionStale = actionShape(site.nextSuggestedAction) !== actionShape(nextAction);
        if (!summaryStale && !actionStale) return;
        await get().updateSite(siteId, nextAction
          ? { siteSummary: nextSummary, nextSuggestedAction: nextAction }
          : { siteSummary: nextSummary });
        return;
      }

      const { computeEvidenceReadiness } = await import('../lib/evidenceReadiness');
      // Load the SED if available — best-effort. Failures fall through
      // to provider-coverage = 0 which is honest for a not-yet-bootstrapped site.
      let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
      if (site.sedId) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          sed = await loadSedById(site.sedId, site.sedStoragePath);
        } catch { /* sed unavailable — score reflects that */ }
      }
      const readiness = computeEvidenceReadiness(site, sed, types.length > 0 ? types : undefined);
      // Skip the persist when nothing changed materially — avoids a
      // Firestore write loop when the trigger fires multiple times for
      // the same edit (e.g. doc rename + library refresh).
      const prev = site.evidenceReadiness;
      const prevSummary = site.siteSummary;
      const readinessUnchanged = Boolean(prev) && prev!.score === readiness.score && prev!.tier === readiness.tier
        && prev!.providerCoverage === readiness.providerCoverage
        && prev!.criticalGaps.length === readiness.criticalGaps.length;
      const summaryUnchanged = Boolean(prevSummary) && prevSummary!.text === nextSummary.text
        && (prevSummary!.sourceReportId ?? null) === (nextSummary.sourceReportId ?? null);
      const actionUnchanged = actionShape(site.nextSuggestedAction) === actionShape(nextAction);
      if (readinessUnchanged && summaryUnchanged && actionUnchanged) return;
      await get().updateSite(siteId, nextAction
        ? { evidenceReadiness: readiness, siteSummary: nextSummary, nextSuggestedAction: nextAction }
        : { evidenceReadiness: readiness, siteSummary: nextSummary });
    } catch (e) {
      console.warn(`[evidenceReadiness] recompute failed for site ${siteId}:`, (e as Error)?.message);
    }
  },

  runDeferredReadinessRecompute: async () => {
    // Throttle to 1 site every 200ms. 20 sites take 4s and never
    // saturate React's update-depth budget. The 1-hour skip-guard
    // inside recomputeEvidenceReadiness keeps this a no-op for sites
    // whose persisted readiness is already fresh, so the cost on a
    // warm reload is dominated by the in-flight setTimeout, not GCS.
    const sites = get().siteRag.sites;
    for (const s of sites) {
      try {
        await get().recomputeEvidenceReadiness(s.id);
      } catch (e) {
        console.warn(`[evidenceReadiness] deferred recompute failed for site ${s.id}:`, (e as Error)?.message);
      }
      await new Promise(r => setTimeout(r, 200));
    }
  },

  backfillSedComputedAt: async () => {
    // SED_Stale_Data_Rebuild SD1 — stamp `sedComputedAt` on every legacy
    // site that has an SED but no build timestamp. Uses the site's
    // `updatedAt` as a best-effort approximation — the actual SED build
    // time isn't recorded for pre-SD1 data. One `updateSite` write per
    // legacy site; a fully-stamped portfolio is a zero-write no-op.
    const sites = get().siteRag.sites;
    const pending = sites.filter(s => s.sedId && !s.sedComputedAt);
    if (pending.length === 0) return;
    console.log(`[SED-COMPUTED-AT] backfilling sedComputedAt on ${pending.length} legacy site(s)`);
    for (const s of pending) {
      try {
        await get().updateSite(s.id, { sedComputedAt: s.updatedAt ?? new Date().toISOString() });
      } catch (e) {
        console.warn(`[SED-COMPUTED-AT] backfill failed for site ${s.id}:`, (e as Error)?.message);
      }
    }
  },

  reconcileSiteReportIds: async () => {
    // Dashboard_Zero_Report_Filter_Fix ZR2 — heal stale site.reportIds.
    // For each site, discover the reports that link to it by siteId or
    // by a normalised siteName match, then append the ids the on-site
    // array is missing. One updateSite write per site that actually
    // drifted; a site whose reportIds is already complete is a no-op.
    const { sites, reports } = get().siteRag;
    for (const site of sites) {
      const linked = new Set(site.reportIds ?? []);
      const missing: string[] = [];
      for (const r of reports) {
        if (linked.has(r.id)) continue;
        if (reportBelongsToSite(site, r)) {
          linked.add(r.id);
          missing.push(r.id);
        }
      }
      if (missing.length > 0) {
        try {
          await get().updateSite(site.id, { reportIds: [...(site.reportIds ?? []), ...missing] });
        } catch (e) {
          console.warn(`[reconcileSiteReportIds] failed for site ${site.id}:`, (e as Error)?.message);
        }
      }
    }
  },

  runDeferredDocClassifierBackfill: async () => {
    // Evidence_Setup_Auto_And_Modes ES1 — walk every site, classify the
    // un-tagged docs, force a readiness recompute. Throttle to 1 site every
    // 250ms so a 20-site portfolio takes ~5s and never saturates React's
    // update budget. `backfillDocTagsForSite` short-circuits when a site has
    // no docs or every doc already carries `autoTaggedAt`, so a warm reload
    // is dominated by the in-flight setTimeout, not the classifier.
    const sites = get().siteRag.sites;
    for (const s of sites) {
      try {
        await get().backfillDocTagsForSite(s.id);
      } catch (e) {
        console.warn(`[docClassifier] deferred backfill failed for site ${s.id}:`, (e as Error)?.message);
      }
      await new Promise(r => setTimeout(r, 250));
    }
  },

  backfillDocTagsForSite: async (siteId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const isOwnSite = (site._sourceScope ?? 'mine') === 'mine';
    if (!isOwnSite) return;
    // Idempotency: only docs without an `autoTaggedAt` stamp need work.
    // (The per-doc skip-guard inside `classifyAndMergeDocTags` is a second
    //  line of defence, but filtering up-front means a fully-tagged site
    //  costs zero imports / zero classifier calls.)
    const docs = site.documents ?? [];
    const pending = docs.filter(d => !d.autoTaggedAt);
    if (pending.length === 0) return;
    let changed = false;
    let nextDocs = docs;
    try {
      const { classifyAndMergeDocTags } = await import('../services/docClassifier');
      // Run sequentially — `classifyAndMergeDocTags` may hit the Gemini-flash
      // path and we don't want a burst of N concurrent API calls per site.
      const updated = await Promise.all(docs.map(async (doc) => {
        if (doc.autoTaggedAt) return doc;
        try {
          const tagged = await classifyAndMergeDocTags(doc);
          if (tagged.autoTaggedAt) { changed = true; return tagged; }
          return doc; // no usable extracted text — left untagged, recoverable later
        } catch (e) {
          console.warn(`[docClassifier] backfill failed for doc ${doc.id} on site ${siteId}:`, (e as Error)?.message);
          return doc;
        }
      }));
      nextDocs = updated;
    } catch (e) {
      console.warn(`[docClassifier] backfill import failed for site ${siteId}:`, (e as Error)?.message);
      return;
    }
    if (changed) {
      // `updateSiteDocuments` mirrors the array locally + to Firestore and
      // already fires `recomputeEvidenceReadiness` — but we call it again
      // below to be explicit + resilient to that side-effect changing.
      try { await get().updateSiteDocuments(siteId, nextDocs); }
      catch (e) { console.warn(`[docClassifier] persist failed for site ${siteId}:`, (e as Error)?.message); }
    }
    // Force the readiness recompute even when nothing changed materially —
    // a freshly-tagged doc may have flipped a checklist tier, and the
    // recompute's own 1-hour cadence guard keeps this cheap when it's a
    // no-op. The dashboard chip then updates on the same page load.
    try { await get().recomputeEvidenceReadiness(siteId); }
    catch (e) { console.warn(`[evidenceReadiness] post-backfill recompute failed for site ${siteId}:`, (e as Error)?.message); }
  },

  migrateLegacyReportDocuments: async () => {
    // One-shot at app load (Documents_On_Site Prompt 1): for each report
    // that still carries inline `documents[]` and has no `documentRefs`,
    // promote those docs onto the owning site's documents array (dedup by
    // id) and stamp `documentRefs[]` on the report with placeholder
    // relevance=0.5/userIncluded=true (Prompt 2 fills in real classifier
    // scores). Reports keep their inline `documents` for now so the
    // back-compat read path in `getReportDocuments` keeps working.
    const state = get();
    if (!state.siteRag.isSitesLoaded || !state.siteRag.isReportsLoaded) return;
    const sites = state.siteRag.sites;
    const reports = state.siteRag.reports;

    // Group migrations by site so we touch each site doc once.
    const sitePatches = new Map<string, import('../types/siteRag').SiteDocument[]>();
    const reportPatches: { reportId: string; documentRefs: NonNullable<SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'manual' | 'mixed' }[] = [];

    for (const r of reports) {
      if (
        r._metadataOnly === true
        || isPortfolioSlimReportBody(r)
        || isImmutableRevisionReport(r as unknown as Record<string, unknown>)
        || r.isCompanyShared === true
      ) {
        continue;
      }
      const docs = r.documents ?? [];
      if (docs.length === 0) continue;
      if (r.documentRefs && r.documentRefs.length > 0) continue;  // already migrated

      const site = siteForReport(sites, r);
      if (!site || (site._sourceScope ?? 'mine') !== 'mine') continue;

      // Merge into site.documents (dedup by id; existing wins so user-set
      // priority/folder flags aren't clobbered).
      const existing = sitePatches.get(site.id) ?? [...(site.documents ?? [])];
      const existingIds = new Set(existing.map(d => d.id));
      for (const d of docs) {
        if (!existingIds.has(d.id)) {
          existing.push(d);
          existingIds.add(d.id);
        }
      }
      sitePatches.set(site.id, existing);

      // Build placeholder refs — Prompt 2 reclassifies on next report run.
      const documentRefs = docs.map(d => ({
        docId: d.id,
        relevance: 0.5,
        userIncluded: true,
        includedAt: r.createdAt,
      }));
      reportPatches.push({ reportId: r.id, documentRefs, documentSelectionMode: 'auto' as const });
    }

    if (sitePatches.size === 0 && reportPatches.length === 0) return;
    console.log(`[DOC-MIGRATION] promoting docs on ${sitePatches.size} site(s), stamping refs on ${reportPatches.length} report(s)`);

    for (const [siteId, documents] of sitePatches) {
      try {
        const documentIds = [...new Set(documents.map(d => d.id))];
        await get().updateSite(siteId, { documents, documentIds });
      } catch (e) {
        console.warn(`[DOC-MIGRATION] site ${siteId} failed:`, (e as Error)?.message);
      }
    }
    for (const { reportId, documentRefs, documentSelectionMode } of reportPatches) {
      try {
        await get().updateRagReport(reportId, { documentRefs, documentSelectionMode });
      } catch (e) {
        console.warn(`[DOC-MIGRATION] report ${reportId} failed:`, (e as Error)?.message);
      }
    }
  },

  updateSiteLayerCanvasLinks: (id, patches) => enqueueSiteLayerWrite(id, async () => {
    if (patches.length === 0) return;
    const patchIds = new Set<string>();
    for (const patch of patches) {
      if (
        !patch.siteLayerId.trim()
        || !patch.canvasNodeId.trim()
        || !patch.expectedSourceFingerprint
        || patchIds.has(patch.siteLayerId)
      ) {
        throw new Error('KMZ SiteLayer linkage patches must be unique and complete.');
      }
      patchIds.add(patch.siteLayerId);
    }

    await get().assertSiteWritable(id);
    const site = get().siteRag.sites.find((candidate) => candidate.id === id);
    if (!site) throw new Error('KMZ SiteLayer linkage failed: site not found.');
    const localLayers = site.siteLayers;
    if (!Array.isArray(localLayers)) {
      throw new Error('KMZ SiteLayer linkage failed: authoritative layer mirror is unavailable.');
    }
    for (const patch of patches) {
      const layer = localLayers.find((candidate) => candidate.id === patch.siteLayerId);
      if (
        !layer
        || kmzConversionSourceFingerprint(layer) !== patch.expectedSourceFingerprint
      ) {
        throw new Error(
          `KMZ SiteLayer linkage cancelled because source layer ${patch.siteLayerId} changed or disappeared.`,
        );
      }
      if (layer.source === 'scenario-derived' || isManagedGenerationImpactLayer(layer)) {
        throw new Error('Generation & Impact layout layers are server-managed; publish the layout instead of linking them to Canvas.');
      }
    }

    if (isLocalMockMode()) {
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map((candidate) => {
            if (candidate.id !== id) return candidate;
            let nextLayers = candidate.siteLayers ?? [];
            for (const patch of patches) {
              nextLayers = setLayerCanvasLink(
                nextLayers,
                patch.siteLayerId,
                patch.canvasNodeId,
              );
            }
            return { ...candidate, siteLayers: nextLayers };
          }),
        },
      }));
      return;
    }

    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in — sign in and try linking layers again.');
    const ownerUid = siteOwnerUid(site, user.uid);
    const ownerPath = `users/${ownerUid}/sites/${id}`;
    const { doc, runTransaction } = await import('firebase/firestore');
    const now = new Date().toISOString();

    const committedRevision = await runTransaction(db, async (transaction) => {
      const siteRef = doc(db, ownerPath);
      const siteSnapshot = await transaction.get(siteRef);
      if (!siteSnapshot.exists()) {
        throw new Error('KMZ SiteLayer linkage failed: authoritative site document is unavailable.');
      }
      const siteData = siteSnapshot.data() as Record<string, unknown> & {
        siteLayers?: unknown;
        siteLayersRevision?: unknown;
        siteLayersCanonicalVersion?: unknown;
        siteLayersMirrorComplete?: unknown;
        generationImpactLayoutHead?: unknown;
      };
      if (!Array.isArray(siteData.siteLayers)) {
        throw new Error('KMZ SiteLayer linkage failed: authoritative layer mirror is unavailable.');
      }
      const authoritativeLayers = siteData.siteLayers as import('../types/site').SiteLayer[];
      const layerRefs = patches.map((patch) => (
        doc(db, `${ownerPath}/layers/${patch.siteLayerId}`)
      ));
      const layerSnapshots = await Promise.all(
        layerRefs.map((layerRef) => transaction.get(layerRef)),
      );

      const authoritativeDocs = new Map<string, import('../types/site').SiteLayer>();
      patches.forEach((patch, index) => {
        const mirrorMatches = authoritativeLayers.filter(
          (layer) => layer.id === patch.siteLayerId,
        );
        const mirrorLayer = mirrorMatches[0];
        const layerSnapshot = layerSnapshots[index];
        const canUseCanonicalOnly = siteData.siteLayersCanonicalVersion === 1
          && siteData.siteLayersMirrorComplete === false;
        if (mirrorMatches.length > 1 || (!mirrorLayer && !canUseCanonicalOnly) || !layerSnapshot.exists()) {
          throw new Error(
            `KMZ SiteLayer linkage cancelled because source layer ${patch.siteLayerId} disappeared.`,
          );
        }
        const layerDoc = {
          ...(layerSnapshot.data() as import('../types/site').SiteLayer),
          id: patch.siteLayerId,
        };
        if (layerDoc.source === 'scenario-derived' || isManagedGenerationImpactLayer(layerDoc)) {
          throw new Error('Generation & Impact layout layers are server-managed; publish the layout instead of linking them to Canvas.');
        }
        if (
          (mirrorLayer && kmzConversionSourceFingerprint(mirrorLayer) !== patch.expectedSourceFingerprint)
          || kmzConversionSourceFingerprint(layerDoc) !== patch.expectedSourceFingerprint
        ) {
          throw new Error(
            `KMZ SiteLayer linkage cancelled because source layer ${patch.siteLayerId} changed.`,
          );
        }
        authoritativeDocs.set(patch.siteLayerId, layerDoc);
      });

      let nextMirror = authoritativeLayers;
      const nextCanonicalDocs = new Map<string, import('../types/site').SiteLayer>();
      for (const patch of patches) {
        // A partial mirror never grows to include omitted geometry. The
        // canonical row is the authoritative linkage destination.
        if (nextMirror.some(layer => layer.id === patch.siteLayerId)) {
          nextMirror = setLayerCanvasLink(nextMirror, patch.siteLayerId, patch.canvasNodeId)
            .map(layer => layer.id === patch.siteLayerId ? { ...layer, updatedAt: now } : layer);
        }
        const layerDoc = authoritativeDocs.get(patch.siteLayerId)!;
        const nextLayerDoc = setLayerCanvasLink(
          [layerDoc],
          patch.siteLayerId,
          patch.canvasNodeId,
        )[0];
        nextCanonicalDocs.set(patch.siteLayerId, { ...nextLayerDoc, updatedAt: now });
      }
      const revision = siteLayersRevision(siteData.siteLayersRevision) + 1;
      const parentPatch = {
        ...(!siteData.generationImpactLayoutHead ? { siteLayers: nextMirror } : {}),
        siteLayersRevision: revision,
        updatedAt: now,
      };
      assertSiteLayerParentBudget(siteData, parentPatch);
      for (const patch of patches) {
        transaction.set(
          layerRefs[patches.indexOf(patch)],
          stripUndefined(nextCanonicalDocs.get(patch.siteLayerId)!),
          { merge: true },
        );
      }
      transaction.set(
        siteRef,
        parentPatch,
        { merge: true },
      );
      return revision;
    });

    // Merge only the linkage fields into the latest local state. Source
    // geometry/provider metadata never comes from the stale conversion array.
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.map((candidate) => {
          if (candidate.id !== id) return candidate;
          if (siteLayersRevision(candidate.siteLayersRevision) > committedRevision) return candidate;
          let nextLayers = candidate.siteLayers ?? [];
          for (const patch of patches) {
            nextLayers = setLayerCanvasLink(
              nextLayers,
              patch.siteLayerId,
              patch.canvasNodeId,
            ).map((layer) => (
              layer.id === patch.siteLayerId ? { ...layer, updatedAt: now } : layer
            ));
          }
          return { ...candidate, siteLayers: nextLayers, siteLayersRevision: committedRevision, updatedAt: now };
        }),
      },
    }));
  }),

  adoptPublishedGenerationLayers: (id, layers, revision) => {
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.map((site) => {
          if (site.id !== id) return site;
          // Ignore a delayed response after this client has already adopted a
          // newer canonical revision. For the same/newer revision, replace the
          // set wholesale: `layers` is the server transaction's full canonical
          // snapshot, not merely the scenario rows it just stamped.
          if ((site.siteLayersRevision ?? 0) > revision) return site;
          return {
            ...site,
            siteLayers: [...layers],
            siteLayersCanonicalVersion: 1,
            siteLayersRevision: revision,
            siteLayerCount: layers.length,
            siteLayersMirrorComplete: true,
            _siteLayersCanonicalComplete: true,
            _siteLayersHydrationPending: false,
            _siteLayersHydrationError: undefined,
          };
        }),
      },
    }));
  },

  updateSiteLayers: (id, layersOrUpdater, draftExpectedRevision) => enqueueSiteLayerWrite(id, async () => {
    // Persist layers as subcollection docs keyed by layer id so edits are
    // idempotent upserts and deletes do not rely on array rewrites.
    const now = new Date().toISOString();
    const existingSite = get().siteRag.sites.find((s) => s.id === id);
    if (!isSiteLayerSetWriteReady(existingSite)) {
      const reason = existingSite?._siteLayersHydrationError
        ? `Cannot save map layers because canonical layer loading failed (${existingSite._siteLayersHydrationError}). Reload the site layers and try again.`
        : 'Cannot save map layers while canonical site layers are still loading or incomplete. Wait for layer hydration to finish and try again.';
      throw new Error(reason);
    }
    const prevLayers = existingSite?.siteLayers ?? [];
    const layers = typeof layersOrUpdater === 'function'
      ? layersOrUpdater(prevLayers)
      : layersOrUpdater;
    const prevUpdatedAt = existingSite?.updatedAt;
    const { dirtySectionsForLandGateReviewChanges } = await import('../lib/siteLayers/landGatePromotion');
    const prevById = new Map(prevLayers.map((layer) => [layer.id, layer]));
    const nextLayers = layers.map((layer) => {
      const previous = prevById.get(layer.id);
      const { linkedCanvasNodeId: _staleIncomingLink, ...withoutIncomingLink } = layer;
      return {
        ...withoutIncomingLink,
        // `linkedCanvasNodeId` is transaction-owned. A full-array editor save
        // may neither erase nor replace the current value, even when its stale
        // draft carries a different non-empty link. Unlinking requires an
        // explicit field transaction.
        ...(previous?.linkedCanvasNodeId
          ? { linkedCanvasNodeId: previous.linkedCanvasNodeId }
          : {}),
        id: layer.id?.trim() || crypto.randomUUID(),
        createdAt: previous?.createdAt ?? layer.createdAt ?? now,
        updatedAt: now,
      };
    });
    const promotionDirty = dirtySectionsForLandGateReviewChanges(prevLayers, nextLayers);
    const nextDirtySections = [...new Set([
      ...(existingSite?.siteEvidenceDirtySections ?? []),
      ...promotionDirty,
    ])].sort();
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.map(s => s.id === id ? {
          ...s,
          siteLayers: nextLayers,
          updatedAt: now,
          ...(promotionDirty.length > 0
            ? { siteEvidenceDirtySections: nextDirtySections, siteEvidenceDirtyAt: now }
            : {}),
        } : s),
      },
    }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not signed in — sign in and try saving layers again.');
      }
      const { saveSiteLayersBatch } = await import('../services/siteLayers/siteLayerPersistence');
      const persisted = await saveSiteLayersBatch({
        db,
        uid: siteOwnerUid(existingSite, user.uid),
        siteId: id,
        nextLayers,
        prevLayers,
        expectedRevision: draftExpectedRevision ?? existingSite?.siteLayersRevision ?? 0,
        previousDirtySections: existingSite?.siteEvidenceDirtySections,
        hasGenerationImpactLayoutHead: existingSite?.generationImpactLayoutHead != null,
      });
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map((s) => (
            s.id === id
              ? {
                ...s,
                siteLayers: persisted.layers,
                siteLayersCanonicalVersion: 1,
                siteLayersRevision: persisted.revision,
                _siteLayersCanonicalComplete: true,
                _siteLayersHydrationPending: false,
                _siteLayersHydrationError: undefined,
                updatedAt: now,
                ...(promotionDirty.length > 0
                  ? { siteEvidenceDirtySections: nextDirtySections, siteEvidenceDirtyAt: now }
                  : {}),
              }
              : s
          )),
        },
      }));
    } catch (e) {
      console.warn('Failed to update site layers:', e);
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          sites: state.siteRag.sites.map((s) => (
            s.id === id
              ? {
                ...s,
                siteLayers: prevLayers,
                updatedAt: prevUpdatedAt ?? s.updatedAt,
                siteEvidenceDirtySections: existingSite?.siteEvidenceDirtySections,
                siteEvidenceDirtyAt: existingSite?.siteEvidenceDirtyAt,
              }
              : s
          )),
        },
      }));
      throw e;
    }
  }),

  fetchAndMergeRegionalLayers: async (siteId, opts) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) {
      return { added: 0, skipped: 0, warnings: ['Site not found'] };
    }

    const { fetchRegionalLayers } = await import('../services/arcgis/fetchRegionalLayers');
    const { mergeImportedLayers } = await import('../services/kmzParser');

    const { layers: incoming, warnings } = await fetchRegionalLayers({
      site: {
        location: site.location,
        siteBoundary: site.siteBoundary,
        siteCentroid: site.siteCentroid,
      },
      catalogEntryIds: opts.catalogEntryIds,
      customUrls: opts.customUrls,
      radiusMiles: opts.radiusMiles,
      refdataOnly: opts.refdataOnly,
    });

    const existing = site.siteLayers ?? [];
    const { merged, added, skipped } = mergeImportedLayers(existing, incoming);
    await get().updateSiteLayers(siteId, merged);

    return { added: added.length, skipped: skipped.length, warnings };
  },

  updateSiteContext: (siteId, text) => enqueueSiteContextWrite(siteId, async () => {
    // Site_Context SC2 — persist user-declared ground truth and (best-effort)
    // re-run the claim extractor + cross-checker so the wizard surfaces
    // conflicts inline before the next report run.
    //
    // Pipeline:
    //   1. Snapshot the prior SiteContext (for audit log diff).
    //   2. Cap input at SITE_CONTEXT_MAX_CHARS (8000) — runaway pastes are
    //      a non-starter for Firestore + LLM cost.
    //   3. extractClaims (Gemini Flash) → ExtractedClaim[]. Errors fall
    //      back to an empty array; we still persist the raw prose so the
    //      LLM gets the user's words even when extraction fails.
    //   4. crossCheckClaims (deterministic) → ClaimConflict[]. Pure; safe
    //      on a null SED.
    //   5. Persist optimistically, then Firestore.
    //   6. Audit-log entry (soft-fail).
    //
    // Snapshot-at-gen-time wiring lives in `generateTypedReport`: every
    // report freezes `site.siteContext` onto `report.siteContextSnapshot`
    // so concurrent edits here don't race the rerun.
    const {
      SITE_CONTEXT_MAX_CHARS,
      emptySiteContext,
    } = await import('../types/siteContext');
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;

    const cleaned = (text ?? '').slice(0, SITE_CONTEXT_MAX_CHARS);
    const prior = site.siteContext ?? emptySiteContext();
    const priorStatus = prior.extractorStatus ?? 'idle';
    const priorFailed = priorStatus !== 'success'
      && priorStatus !== 'idle'
      && priorStatus !== 'extracting';
    // Every unchanged, non-failed context is a true no-op, including legacy
    // records with no extractor status (normalized to idle) and an in-flight
    // extraction. Rewriting either would advance both timestamps, change the
    // SED document-input identity despite identical evidence, and immediately
    // stale the freshly rebuilt SED. Explicit failure states still retry.
    if (cleaned === prior.text && !priorFailed) return;
    const { mergeExtractedClaimsPreservingOperatorAttestations } = await import(
      '../services/siteContext/operatorAttestationWriter'
    );
    const preservedOperatorClaims = mergeExtractedClaimsPreservingOperatorAttestations(
      prior.claims,
      [],
    );
    const now = new Date().toISOString();

    // Resolve author for audit / multi-user companies.
    let updatedBy: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) updatedBy = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous — fine, just drop the field */ }

    // Create-site → Overview gap fix: persist the raw text immediately so
    // the Overview tab reflects what the user just entered even while the
    // extractor/cross-check pass is still running in the background.
    const shouldExtract = cleaned !== prior.text || priorFailed;
    const textChanged = cleaned !== prior.text;
    const optimisticStatus: import('../types/siteContext').SiteContext['extractorStatus'] =
      shouldExtract
        ? (cleaned.length > 0 ? 'extracting' : 'success')
        : (cleaned.length === 0 ? 'success' : priorStatus);
    const optimistic: import('../types/siteContext').SiteContext = {
      ...prior,
      text: cleaned,
      updatedAt: now,
      updatedBy,
      // If the user edited text, clear stale claim/conflict projections
      // until the fresh extraction lands.
      claims: textChanged ? preservedOperatorClaims : prior.claims,
      conflicts: textChanged ? [] : prior.conflicts,
      acknowledgedConflictIds: textChanged ? [] : prior.acknowledgedConflictIds,
      extractorStatus: optimisticStatus,
      extractorError: shouldExtract ? undefined : prior.extractorError,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: optimistic, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: optimistic, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist optimistic site context:', e);
    }

    // Preserve prior claims/conflicts if text is unchanged — extraction
    // is expensive and deterministic on identical input. Otherwise
    // re-extract.
    //
    // CLP-2 — also re-run extraction when the text is unchanged BUT the
    // prior run failed (quota / auth / timeout / parse / unknown). Pre-
    // CLP, a user who hit a Gemini 429 once was stranded — every save
    // skipped extraction because `cleaned === prior.text`, leaving the
    // chip permanently stuck on "0 claims parsed". Post-CLP, a save
    // after a deploy that lifted the quota cap (e.g. VTX rev 00489-6c6)
    // automatically retries.
    let claims = prior.claims;
    let extractorVersion = prior.extractorVersion;
    let extractorStatus: import('../types/siteContext').SiteContext['extractorStatus'] = prior.extractorStatus ?? 'idle';
    let extractorError: string | undefined = prior.extractorError;
    let extractedAt: string | undefined = prior.extractedAt;
    // EX-5 — hoist the SED load BEFORE extraction so the extractor's
    // cross-field rules (substation / IX / reactor distance-ratio +
    // regulatory.state locus) actually fire. Pre-EX-5 the SED was loaded
    // only AFTER extraction for the cross-checker, so those rules
    // silently no-op'd (they early-return when ctx.sed is null). Load
    // once here and reuse for BOTH the extractor and the cross-checker.
    let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
    const latestReportForSed = state.siteRag.reports
      .filter(r => r.siteId === site.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    if (latestReportForSed?.sedId) {
      try {
        const { loadSedById } = await import('../services/sedLoader');
        sed = await loadSedById(latestReportForSed.sedId, undefined);
      } catch { /* SED load is best-effort; the extractor + cross-check tolerate null */ }
    }
    let siteStateCode: string | null = null;
    if (sed) {
      try {
        const { inferSiteStateFromSed } = await import('../lib/inferSiteState');
        siteStateCode = inferSiteStateFromSed(sed);
      } catch { /* state inference is best-effort */ }
    }

    if (shouldExtract && cleaned.length > 0) {
      try {
        // EX-4 — use extractClaimsWithRetry (the SX-1 auto-re-extract
        // orchestrator) instead of the raw extractClaims; identical
        // contract, re-extracts once when the first pass produced
        // schema-rejected rows. EX-5 — pass sed + siteStateCode so the
        // cross-field rules fire.
        const { extractClaimsWithRetry } = await import('../services/siteContext/extractClaims');
        const res = await extractClaimsWithRetry(cleaned, { siteName: site.name, sed, siteStateCode });
        claims = mergeExtractedClaimsPreservingOperatorAttestations(prior.claims, res.claims);
        extractorVersion = res.extractorVersion;
        extractorStatus = res.status;
        extractorError = res.errorMessage;
        extractedAt = new Date(res.attemptedAt).toISOString();
      } catch (e) {
        // Defensive: the extractor's own contract is best-effort and
        // shouldn't throw, but if the dynamic import itself fails we
        // still want to persist the prose and stamp the status.
        const msg = (e as Error)?.message ?? String(e);
        console.warn('[site-context] extraction failed, persisting raw text only:', msg);
        claims = preservedOperatorClaims;
        extractorVersion = 'extractor-error';
        extractorStatus = 'unknown-error';
        extractorError = msg;
        extractedAt = new Date().toISOString();
      }
    } else if (cleaned.length === 0) {
      // Empty prose ⇒ no claims, status 'success' (nothing to do).
      claims = preservedOperatorClaims;
      extractorStatus = 'success';
      extractorError = undefined;
    }

    // Re-run the cross-checker against the SED loaded above — pure,
    // never throws on missing fields. The wizard surface re-runs this
    // against fresh SED data when the user opens the panel (see
    // ReportPreferencesStep / FeasibilityRerunPanel).
    let conflicts: import('../types/siteContext').ClaimConflict[] = [];
    try {
      const { crossCheckClaims } = await import('../services/siteContext/crossCheckClaims');
      conflicts = crossCheckClaims(claims, sed);
    } catch (e) {
      console.warn('[site-context] cross-check failed:', (e as Error)?.message);
      conflicts = [];
    }

    // Drop acknowledgements for conflicts that no longer exist (claim
    // edited away). New conflicts surface fresh; only stable ids stay
    // acknowledged.
    const liveIds = new Set(conflicts.map(c => c.id));
    const acknowledgedConflictIds = prior.acknowledgedConflictIds.filter(id => liveIds.has(id));

    const next: import('../types/siteContext').SiteContext = {
      text: cleaned,
      updatedAt: now,
      updatedBy,
      claims,
      conflicts,
      acknowledgedConflictIds,
      extractorVersion,
      extractorStatus,
      extractorError,
      extractedAt,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: next, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: next, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist site context:', e);
    }

    // CBE caller 4 — context→intent auto-fill. Gap-fills undecided
    // ProjectIntent fields from the freshly-merged claims (USER WINS via
    // fieldSource; declared states protected — see contextIntentFill.ts).
    // Best-effort: a failure never blocks the context save.
    try {
      const { pullContextIntentFromClaims } = await import('../lib/contextIntentFill');
      const liveSite = get().siteRag.sites.find(x => x.id === siteId);
      const pulled = pullContextIntentFromClaims(liveSite?.projectIntent, next.claims);
      if (pulled.filledFields.length > 0) {
        await get().updateSiteProjectIntent(siteId, pulled.projectIntent);
      } else {
        get()._staleStampSiteComposite(siteId);
        if (!get().isInitialMigration) void get().recomputeEvidenceReadiness(siteId);
      }
    } catch (e) {
      console.warn('[cbe-intent] context→intent auto-fill failed:', (e as Error)?.message);
      get()._staleStampSiteComposite(siteId);
      if (!get().isInitialMigration) void get().recomputeEvidenceReadiness(siteId);
    }

    // SC9 — audit-log entry. Soft-fail; never blocks the action.
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'site.context_update',
        target: { type: 'site', id: siteId, label: site.name },
        before: {
          textLength: prior.text.length,
          claimCount: prior.claims.length,
          conflictCount: prior.conflicts.length,
        },
        after: {
          textLength: next.text.length,
          claimCount: next.claims.length,
          conflictCount: next.conflicts.length,
        },
        metadata: { extractorVersion, extractorStatus },
      });
    } catch { /* audit emit is best-effort */ }
  }),

  retryExtractClaims: (siteId) => enqueueSiteContextWrite(siteId, async () => {
    // CLP-2 — manual retry path for the SiteContext extractor. Re-runs
    // `extractClaims` against the current persisted text without
    // requiring the user to edit it (the standard `updateSiteContext`
    // skips extraction when `cleaned === prior.text` and the prior
    // run succeeded, which strands users after a Gemini 429).
    //
    // Concurrency: the optimistic update flips `extractorStatus` to
    // `'extracting'` first so a duplicate click is a no-op (the UI
    // disables the button on that state). The cross-check + persist
    // path mirrors `updateSiteContext` so the resulting SiteContext is
    // wire-identical to the one that would be written on a normal save.
    const { emptySiteContext } = await import('../types/siteContext');
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const prior = site.siteContext ?? emptySiteContext();
    const { mergeExtractedClaimsPreservingOperatorAttestations } = await import(
      '../services/siteContext/operatorAttestationWriter'
    );
    // No-op on empty prose — nothing to extract from. We still flip the
    // status to 'success' so the chip clears any prior error state.
    if (!prior.text || prior.text.trim().length === 0) {
      if (prior.extractorStatus && prior.extractorStatus !== 'success' && prior.extractorStatus !== 'idle') {
        // Lift any stale error chip so the user doesn't see "Quota
        // exhausted" against an empty textarea.
        const cleared: import('../types/siteContext').SiteContext = {
          ...prior,
          extractorStatus: 'success',
          extractorError: undefined,
        };
        set((s) => ({
          siteRag: {
            ...s.siteRag,
            sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: cleared } : x),
          },
        }));
      }
      return;
    }
    // Concurrency guard — a second call while the first is in flight
    // is dropped. The button disables on this state, but defensive
    // coverage in the store keeps invariants tight regardless of UI.
    if (prior.extractorStatus === 'extracting') return;

    const optimistic: import('../types/siteContext').SiteContext = {
      ...prior,
      extractorStatus: 'extracting',
      extractorError: undefined,
    };
    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: optimistic } : x),
      },
    }));

    let claims = prior.claims;
    let extractorVersion = prior.extractorVersion;
    let extractorStatus: import('../types/siteContext').SiteContext['extractorStatus'] = 'unknown-error';
    let extractorError: string | undefined;
    let extractedAt: string | undefined;
    // EX-5 — hoist the SED load BEFORE extraction (same rationale as
    // updateSiteContext) and reuse it for the cross-checker, so the
    // extractor's cross-field rules fire on this manual retry.
    let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
    const latestReportForSed = get().siteRag.reports
      .filter(r => r.siteId === site.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    if (latestReportForSed?.sedId) {
      try {
        const { loadSedById } = await import('../services/sedLoader');
        sed = await loadSedById(latestReportForSed.sedId, undefined);
      } catch { /* SED load is best-effort */ }
    }
    let siteStateCode: string | null = null;
    if (sed) {
      try {
        const { inferSiteStateFromSed } = await import('../lib/inferSiteState');
        siteStateCode = inferSiteStateFromSed(sed);
      } catch { /* state inference is best-effort */ }
    }
    try {
      // EX-4 — extractClaimsWithRetry (drop-in for extractClaims).
      // EX-5 — pass sed + siteStateCode so cross-field rules fire.
      const { extractClaimsWithRetry } = await import('../services/siteContext/extractClaims');
      const res = await extractClaimsWithRetry(prior.text, { siteName: site.name, sed, siteStateCode });
      claims = mergeExtractedClaimsPreservingOperatorAttestations(prior.claims, res.claims);
      extractorVersion = res.extractorVersion;
      extractorStatus = res.status;
      extractorError = res.errorMessage;
      extractedAt = new Date(res.attemptedAt).toISOString();
    } catch (e) {
      const msg = (e as Error)?.message ?? String(e);
      console.warn('[site-context] retryExtractClaims failed:', msg);
      extractorStatus = 'unknown-error';
      extractorError = msg;
      extractedAt = new Date().toISOString();
    }

    // Re-run the cross-checker against the SED loaded above — pure,
    // never throws.
    let conflicts: import('../types/siteContext').ClaimConflict[] = prior.conflicts;
    try {
      const { crossCheckClaims } = await import('../services/siteContext/crossCheckClaims');
      conflicts = crossCheckClaims(claims, sed);
    } catch { /* cross-check is best-effort; keep prior conflicts */ }

    const liveIds = new Set(conflicts.map(c => c.id));
    const acknowledgedConflictIds = prior.acknowledgedConflictIds.filter(id => liveIds.has(id));

    const now = new Date().toISOString();
    const next: import('../types/siteContext').SiteContext = {
      ...prior,
      claims,
      conflicts,
      acknowledgedConflictIds,
      extractorVersion,
      extractorStatus,
      extractorError,
      extractedAt,
      updatedAt: now,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: next, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: next, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist retried site context:', e);
    }

    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'site.context_retry_extract',
        target: { type: 'site', id: siteId, label: site.name },
        before: { extractorStatus: prior.extractorStatus ?? 'idle', claimCount: prior.claims.length },
        after: { extractorStatus, claimCount: claims.length },
        metadata: { extractorVersion },
      });
    } catch { /* best-effort */ }
  }),

  answerOpenQuestion: async (input) => {
    const { siteId, openQuestion, answer, details, reportId, sectionId } = input;
    const normalizedReportId = reportId.trim();
    if (!normalizedReportId) throw new Error('Report id is required');
    return enqueueSiteContextWrite(siteId, async () => {
      // Read every merge base inside the queue. Reading before awaiting the
      // prior answer would recreate the stale-snapshot lost-update bug this
      // queue is intended to prevent.
      const state = get();
      const site = state.siteRag.sites.find(s => s.id === siteId);
      if (!site) throw new Error('Site not found');
      const report = state.siteRag.reports.find(r => r.id === normalizedReportId);
      if (!report) throw new Error('Report not found');
      if (report.siteId !== siteId) {
        throw new Error('Report does not belong to this site');
      }
      if (hasImmutableReportIdentityForMutation(report)) {
        throw new Error('Published report revisions are read-only');
      }

      const submittedIdentity = resolveOpenQuestionIdentity(openQuestion);
      const submittedQuestion = submittedIdentity.question;
      if (!submittedQuestion) throw new Error('Open question text is required');
      if (openQuestion.evidenceState !== 'unknown') {
        throw new Error('Only open questions can be answered');
      }
      const reportOpenQuestion = findSubmittedOpenQuestion(report, sectionId, submittedQuestion);
      if (!reportOpenQuestion) {
        throw new Error('Open question was not found in this report section');
      }
      const reportQuestionIdentity = resolveOpenQuestionIdentity(reportOpenQuestion);
      const question = reportQuestionIdentity.question;
      if (!question) throw new Error('Open question text is required');
      if (answer !== 'yes' && answer !== 'no' && answer !== 'details') {
        throw new Error('Invalid open-question answer');
      }

      const now = new Date().toISOString();
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) throw new Error('Not signed in');
      if (
        siteOwnerUid(site, user.uid) !== user.uid
        || reportOwnerUid(report, user.uid) !== user.uid
      ) {
        throw new Error('Only the report and site owner can answer open questions');
      }
      await get().assertSiteWritable(siteId);
      const author = { uid: user.uid, email: user.email ?? undefined };

      const {
        OPEN_QUESTION_DETAILS_MAX_CHARS,
        buildOpenQuestionQuestionKey,
        buildOpenQuestionQuestionKeys,
        buildOpenQuestionSubjectKey,
        canonicalOpenQuestionSectionId,
        findOpenQuestionResponse,
        upsertOpenQuestionResponse,
      } = await import('../lib/findingClaim');
      const normalizedSectionId = canonicalOpenQuestionSectionId(sectionId);
      const identity = {
        reportId: normalizedReportId,
        sectionId: normalizedSectionId,
        question,
        questionAliases: [
          ...reportQuestionIdentity.questionAliases,
          ...submittedIdentity.questionAliases,
        ],
      };
      const questionKey = buildOpenQuestionQuestionKey(identity);
      const attestationSubjectKey = buildOpenQuestionSubjectKey({ question });
      const equivalentQuestionKeys = buildOpenQuestionQuestionKeys(identity);
      const existingResponse = findOpenQuestionResponse(report.openQuestionResponses, identity);
      const submittedDetails = details === undefined ? undefined : details.trim();
      const normalizedDetails = submittedDetails ?? existingResponse?.details?.trim() ?? '';
      if (normalizedDetails.length > OPEN_QUESTION_DETAILS_MAX_CHARS) {
        throw new Error(
          `Details must be ${OPEN_QUESTION_DETAILS_MAX_CHARS.toLocaleString()} characters or fewer`,
        );
      }
      if (answer === 'details' && normalizedDetails.length === 0) {
        throw new Error('Details are required for this response');
      }

      // Details are additive notes, not a third mutually-exclusive
      // disposition. Preserve an existing Yes/No when notes are edited, and
      // preserve existing notes when the operator later chooses Yes or No.
      const resolvedAnswer: import('../lib/findingClaim').OpenQuestionAnswerValue =
        answer === 'details'
          && (existingResponse?.answer === 'yes' || existingResponse?.answer === 'no')
          ? existingResponse.answer
          : answer;
      const context = reportQuestionIdentity.context;
      const response: import('../lib/findingClaim').OpenQuestionResponse = {
        id: existingResponse?.id ?? `open-question-response-${questionKey}`,
        questionKey,
        question,
        ...(context && context !== question ? { context } : {}),
        answer: resolvedAnswer,
        ...(normalizedDetails ? { details: normalizedDetails } : {}),
        reportId: normalizedReportId,
        ...(normalizedSectionId ? { sectionId: normalizedSectionId } : {}),
        answeredAt: now,
        answeredBy: author,
        attestationClaimId: `open-question-attestation-${attestationSubjectKey}`,
        sedSyncStatus: 'pending',
      };

      const {
        buildOperatorAttestationClaim,
        applyOperatorAttestationToContext,
        isOperatorAttestationForQuestions,
        upsertOperatorOpenQuestionWorkflow,
      } = await import('../services/siteContext/operatorAttestationWriter');

      const attestationClaim = buildOperatorAttestationClaim({
        openQuestion: reportOpenQuestion,
        answer: resolvedAnswer,
        details: normalizedDetails || undefined,
        author,
        claimId: response.attestationClaimId,
        nowIso: now,
      });
      const priorAttestationClaims = site.siteContext?.claims.filter((claim) => (
        claim.id === response.attestationClaimId
        || isOperatorAttestationForQuestions(claim, identity.questionAliases)
      )) ?? [];
      const nextContext = applyOperatorAttestationToContext(
        site.siteContext,
        attestationClaim,
        identity.questionAliases,
      );

      // Canonical write: updateSite resolves owner paths, rolls back optimistic
      // state on failure and rethrows. Do not attempt projections if this fails.
      await get().updateSite(siteId, { siteContext: nextContext, updatedAt: now });
      const result: import('../lib/findingClaim').AnswerOpenQuestionResult = {
        response,
        siteSaved: true,
        reportSaved: false,
        sedSaved: false,
      };

      const nextResponses = upsertOpenQuestionResponse(
        report.openQuestionResponses,
        response,
        equivalentQuestionKeys,
      );
      try {
        await get().updateRagReport(
          normalizedReportId,
          {
            openQuestionResponses: nextResponses,
            minorVersion: (report.minorVersion ?? 0) + 1,
            updatedAt: now,
          },
          { strictPersist: true },
        );
        result.reportSaved = true;
      } catch (e) {
        // The report is the durable response ledger. If its strict save fails,
        // remove only this answer's claim from the live context (or restore the
        // prior version on a retry) so the UI cannot imply the answer persisted.
        const latestSite = get().siteRag.sites.find((candidate) => candidate.id === siteId);
        const rollbackBase = latestSite?.siteContext ?? nextContext;
        const rollbackClaims = rollbackBase.claims.filter((claim) => (
          claim.id !== response.attestationClaimId
          && !isOperatorAttestationForQuestions(claim, identity.questionAliases)
        ));
        rollbackClaims.push(...priorAttestationClaims);
        const rollbackContext: import('../types/siteContext').SiteContext = {
          ...rollbackBase,
          claims: rollbackClaims,
          updatedAt: new Date().toISOString(),
          updatedBy: site.siteContext?.updatedBy,
        };
        try {
          await get().updateSite(siteId, { siteContext: rollbackContext, updatedAt: rollbackContext.updatedAt });
        } catch (rollbackError) {
          console.warn(
            '[answerOpenQuestion] report persist and attestation rollback failed:',
            (rollbackError as Error)?.message,
          );
        }
        throw new Error(`Report response could not be saved: ${(e as Error)?.message ?? String(e)}`);
      }

      // Mirror the canonical live context into the current SED and retain an
      // attributable expert-judgment siting note. Do not advance collectedAt:
      // operator annotation is not a provider-data freshness event.
      const sedId = site.sedId ?? report.sedId;
      if (sedId) {
        try {
          const { loadSedById, saveSedById } = await import('../services/sedLoader');
          const sed = await loadSedById(sedId, site.sedStoragePath, site.sedVersion);
          if (sed) {
            const workflows = upsertOperatorOpenQuestionWorkflow(
              sed.workflows,
              response,
              report.version,
            );
            const nextSed: import('../types/siteEvidence').SiteEvidenceDocument = {
              ...sed,
              siteContext: nextContext,
              workflows,
              version: (sed.version ?? 1) + 1,
            };
            const storageSaved = await saveSedById(nextSed, site.sedStoragePath);
            if (storageSaved) {
              result.sedSaved = true;
            }
          }
        } catch (e) {
          console.warn('[answerOpenQuestion] SED persist failed:', (e as Error)?.message);
          result.sedSaved = false;
        }
      }

      if (result.sedSaved) {
        const syncedResponse: import('../lib/findingClaim').OpenQuestionResponse = {
          ...response,
          sedSyncStatus: 'synced',
        };
        const latestReport = get().siteRag.reports.find(
          (candidate) => candidate.id === normalizedReportId,
        );
        const syncedResponses = upsertOpenQuestionResponse(
          latestReport?.openQuestionResponses,
          syncedResponse,
          equivalentQuestionKeys,
        );
        try {
          await get().updateRagReport(
            normalizedReportId,
            { openQuestionResponses: syncedResponses, updatedAt: new Date().toISOString() },
            { strictPersist: true },
          );
          result.response = syncedResponse;
        } catch (e) {
          // The first strict report save remains authoritative and explicitly
          // pending, so a later retry safely reconciles this already-saved SED.
          console.warn('[answerOpenQuestion] SED sync status persist failed:', (e as Error)?.message);
        }
      }

      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'site.context_authored_claim',
          target: { type: 'site', id: siteId, label: site.name },
          before: { claimCount: site.siteContext?.claims?.length ?? 0 },
          after: { claimCount: nextContext.claims.length },
          metadata: {
            answer: resolvedAnswer,
            openQuestion: question,
            questionKey,
            reportId: normalizedReportId,
            sectionId: normalizedSectionId,
            attestationClaimId: attestationClaim.id,
            reportSaved: result.reportSaved,
            sedSaved: result.sedSaved,
          },
        });
      } catch { /* best-effort */ }

      return result;
    });
  },

  reviewExtractedField: async (siteId, sectionId, fieldKey, decision, correctedValue) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site?.sedId) return { ok: false, reason: 'This site has no evidence document yet.' };
    try {
      const { loadSedById, saveSedById } = await import('../services/sedLoader');
      // Version-pinned load: a version-less lookup can serve a stale cached
      // SED, and this action SAVES after mutating — a stale read here would
      // regress the evidence to the cached copy (observed 2026-07-27 as an
      // "empty" extracted-fields panel over a v24 SED with 11 stamps).
      const loadedSed = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
      if (!loadedSed) return { ok: false, reason: 'The evidence document could not be loaded.' };
      // The loader returns its cached object. Keep saved evidence untouched
      // until persistence succeeds and the loader commits the new cache entry.
      const sed = structuredClone(loadedSed);

      const slotMap = sed as unknown as Record<string, {
        provenanceDetail?: {
          autoFillFields?: Record<string, import('../types/siteEvidence').StructuredEvidenceValue>;
          fieldReviews?: Record<string, import('../lib/coverage/extractedFieldReview').FieldReview>;
        };
      } | undefined>;
      const slot = slotMap[sectionId];
      if (!slot) return { ok: false, reason: 'That section is not present on this evidence document.' };

      const { applyFieldReview, undoFieldReview } = await import('../lib/coverage/extractedFieldReview');
      const { auth } = await import('../firebase');
      const who = auth.currentUser?.email ?? auth.currentUser?.uid ?? 'unknown';
      const next = decision === null
        ? undoFieldReview(slot.provenanceDetail, fieldKey)
        : applyFieldReview(slot.provenanceDetail, fieldKey, decision, who, correctedValue);

      slot.provenanceDetail = {
        ...(slot.provenanceDetail ?? { mergeReason: 'field-review' }),
        autoFillFields: next.autoFillFields,
        fieldReviews: next.fieldReviews,
      } as typeof slot.provenanceDetail;

      const saved = await saveSedById(sed, site.sedStoragePath);
      if (!saved) return { ok: false, reason: 'The field review could not be confirmed as saved. Reload evidence before retrying.' };
      // Readiness reads the slot, so a reject that removed a value must be
      // reflected immediately — otherwise the gap keeps reading as filled by
      // data the user just deleted.
      try { await get().recomputeEvidenceReadiness(siteId); } catch { /* best-effort */ }
      return { ok: true };
    } catch (e) {
      console.warn('[reviewExtractedField] failed:', (e as Error)?.message);
      return { ok: false, reason: (e as Error)?.message || 'The review could not be saved.' };
    }
  },
  scrubDocumentFromSed: async (siteId, docId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site?.sedId) return { ok: false, reason: 'This site has no evidence document yet.' };
    try {
      const { loadSedById, saveSedById } = await import('../services/sedLoader');
      // Version-pinned for the same load-then-SAVE staleness hazard as
      // reviewExtractedField above.
      const loadedSed = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
      if (!loadedSed) return { ok: false, reason: 'The evidence document could not be loaded.' };
      const sed = structuredClone(loadedSed);

      const { scrubDocFromSed } = await import('../lib/coverage/docSedContributions');
      const result = scrubDocFromSed(sed, docId);
      if (!result.changed) {
        // Nothing to remove — don't burn a version bump on a no-op.
        return { ok: true, removedFieldCount: 0, downgradedSections: [] };
      }

      if (!await saveSedById(sed, site.sedStoragePath)) {
        return { ok: false, reason: 'The evidence cleanup could not be confirmed as saved. Reload evidence before retrying.' };
      }
      // Readiness + coverage read the slots this scrub just emptied; a
      // downgraded section must reopen its gap immediately, not on the
      // next CAF run.
      try { await get().recomputeEvidenceReadiness(siteId); } catch { /* best-effort */ }
      try {
        const { writeSiteCoverage } = await import('../services/cafService');
        await writeSiteCoverage(siteId);
      } catch { /* best-effort — coverage refresh on next CAF run */ }
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'document.sed_scrubbed',
          target: { type: 'document', id: docId },
          metadata: {
            siteId,
            removedFieldCount: result.removedFieldCount,
            downgradedSections: result.downgradedSections,
            keptHumanFieldCount: result.keptHumanFieldCount,
          },
        });
      } catch { /* audit is best-effort */ }
      return {
        ok: true,
        removedFieldCount: result.removedFieldCount,
        downgradedSections: result.downgradedSections,
      };
    } catch (e) {
      console.warn('[scrubDocumentFromSed] failed:', (e as Error)?.message);
      return { ok: false, reason: (e as Error)?.message || 'The evidence cleanup failed.' };
    }
  },
  markClaimMisExtracted: (siteId, claimId) => enqueueSiteContextWrite(siteId, async () => {
    // SCE-3 — soft-flag the claim as a typed extractor failure. The row
    // stays in `siteContext.claims[]` for audit + undo, but every
    // read-side surface (`buildSedContextDiffRows`, `crossCheckClaims`,
    // `buildSiteContextBlock`) filters `state === 'mis-extracted'`.
    //
    // Side effects:
    //   1. Optimistic store update.
    //   2. Re-run `crossCheckClaims` so the conflict list drops the row.
    //   3. Persist to Firestore.
    //   4. Audit-log entry — feeds the admin "Extractor rejections" rollup.
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site?.siteContext) return;
    const prior = site.siteContext;
    const idx = prior.claims.findIndex(c => c.id === claimId);
    if (idx < 0) return;
    const targetClaim = prior.claims[idx];
    if (!targetClaim) return;
    if (targetClaim.state === 'mis-extracted') return; // idempotent

    const now = new Date().toISOString();
    let actor: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) actor = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous — fine */ }

    const nextClaims = prior.claims.slice();
    nextClaims[idx] = {
      ...targetClaim,
      state: 'mis-extracted',
      misExtractedAt: now,
      misExtractedBy: actor,
    };

    // Re-run the cross-checker against the current SED so conflicts drop
    // the row immediately. Best-effort — failures keep prior conflicts.
    let conflicts = prior.conflicts;
    try {
      const { crossCheckClaims } = await import('../services/siteContext/crossCheckClaims');
      const latestReport = state.siteRag.reports
        .filter(r => r.siteId === site.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
      if (latestReport?.sedId) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          sed = await loadSedById(latestReport.sedId, undefined);
        } catch { /* SED load is best-effort */ }
      }
      conflicts = crossCheckClaims(nextClaims, sed);
    } catch (e) {
      console.warn('[site-context] markClaimMisExtracted cross-check failed:', (e as Error)?.message);
    }

    const liveIds = new Set(conflicts.map(c => c.id));
    const acknowledgedConflictIds = prior.acknowledgedConflictIds.filter(id => liveIds.has(id));

    const next: import('../types/siteContext').SiteContext = {
      ...prior,
      claims: nextClaims,
      conflicts,
      acknowledgedConflictIds,
      updatedAt: now,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: next, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: next, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist mis-extracted claim flag:', e);
    }

    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'site.claim_mis_extracted',
        target: { type: 'site', id: siteId, label: site.name },
        before: {
          claimId,
          sedField: targetClaim.sedField,
          category: targetClaim.category,
          claimedValue: typeof targetClaim.claimedValue === 'string'
            ? targetClaim.claimedValue
            : String(targetClaim.claimedValue ?? ''),
        },
        after: { state: 'mis-extracted' },
        metadata: {
          sourceSpan: targetClaim.sourceSpan.slice(0, 280),
          extractorConfidence: targetClaim.extractorConfidence,
        },
      });
    } catch { /* best-effort */ }
  }),

  adoptSedValueIntoContext: (siteId, identity, sedValue) => enqueueSiteContextWrite(siteId, async () => {
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site?.siteContext) return;
    const prior = site.siteContext;
    const [identityClaimId, sedPath] = identity.split('::');
    if (!identityClaimId || !sedPath || identityClaimId === '__nocontext__') return;

    const idx = prior.claims.findIndex(c => c.id === identityClaimId);
    if (idx < 0) return;
    const targetClaim = prior.claims[idx];
    if (!targetClaim) return;
    const sameValue = (
      (targetClaim.claimedValue === sedValue)
      || (targetClaim.claimedValue == null && sedValue == null)
      || (
        typeof targetClaim.claimedValue === 'number'
        && typeof sedValue === 'number'
        && Number.isNaN(targetClaim.claimedValue)
        && Number.isNaN(sedValue)
      )
    );
    if (sameValue) return;

    const now = new Date().toISOString();
    let actor: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) actor = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous — fine */ }

    const nextClaims = prior.claims.slice();
    nextClaims[idx] = {
      ...targetClaim,
      claimedValue: sedValue ?? undefined,
      lastWrittenAt: now,
      lastWrittenBy: actor,
      syncHistory: [
        ...(targetClaim.syncHistory ?? []),
        {
          sedPath,
          resolution: 'adopt-sed',
          resolvedAt: now,
          resolvedBy: actor,
        },
      ],
    };

    let conflicts = prior.conflicts;
    try {
      const { crossCheckClaims } = await import('../services/siteContext/crossCheckClaims');
      const latestReport = state.siteRag.reports
        .filter(r => r.siteId === site.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
      if (latestReport?.sedId) {
        try {
          const { loadSedById } = await import('../services/sedLoader');
          sed = await loadSedById(latestReport.sedId, undefined);
        } catch { /* SED load is best-effort */ }
      }
      conflicts = crossCheckClaims(nextClaims, sed);
    } catch (e) {
      console.warn('[site-context] adoptSedValueIntoContext cross-check failed:', (e as Error)?.message);
    }

    const liveIds = new Set(conflicts.map(c => c.id));
    const acknowledgedConflictIds = prior.acknowledgedConflictIds.filter(id => liveIds.has(id));
    const next: import('../types/siteContext').SiteContext = {
      ...prior,
      claims: nextClaims,
      conflicts,
      acknowledgedConflictIds,
      updatedAt: now,
      updatedBy: actor,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: next, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: next, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist adopted SED value into site context:', e);
    }

    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'sed_context_sync',
        target: { type: 'site', id: siteId, label: site.name },
        before: { claimValue: targetClaim.claimedValue ?? null },
        after: { claimValue: sedValue ?? null },
        metadata: {
          direction: 'sed→context',
          claimId: targetClaim.id,
          claimCategory: targetClaim.category,
          sedPath,
          resolution: 'adopt-sed',
          source: 'manual-adopt',
        },
      });
    } catch { /* best-effort */ }
  }),

  adoptContextValueIntoSed: (siteId, identity, contextValue, claimId) => enqueueSiteContextWrite(siteId, async () => {
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site?.siteContext || !site.sedId) return;
    const prior = site.siteContext;
    const [identityClaimId, sedPath] = identity.split('::');
    const resolvedClaimId = claimId ?? identityClaimId;
    if (!resolvedClaimId || !sedPath || resolvedClaimId === '__nocontext__') return;
    const idx = prior.claims.findIndex(c => c.id === resolvedClaimId);
    if (idx < 0) return;
    const targetClaim = prior.claims[idx];
    if (!targetClaim) return;

    let actor: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) actor = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous — fine */ }

    const { resolveSedField } = await import('../services/siteContext/crossCheckClaims');
    const { bindingSedPathToVariablePath } = await import('../services/siteContext/sedBindingRegistry');
    const { loadSedById } = await import('../services/sedLoader');
    const sed = await loadSedById(site.sedId, site.sedStoragePath);
    const currentSedValue = sed ? resolveSedField(sed, sedPath) : null;
    const sameValue = (
      (currentSedValue === contextValue)
      || (currentSedValue == null && contextValue == null)
      || (
        typeof currentSedValue === 'number'
        && typeof contextValue === 'number'
        && Number.isNaN(currentSedValue)
        && Number.isNaN(contextValue)
      )
    );
    if (sameValue) return;

    const now = new Date().toISOString();
    const nextClaim: import('../types/siteContext').ExtractedClaim = {
      ...targetClaim,
      claimedValue: contextValue ?? undefined,
      lastWrittenAt: now,
      lastWrittenBy: actor,
      syncHistory: [
        ...(targetClaim.syncHistory ?? []),
        {
          sedPath,
          resolution: 'adopt-context',
          resolvedAt: now,
          resolvedBy: actor,
        },
      ],
    };

    const { syncContextClaimToSed } = await import('../services/siteContext/syncSedContextBindings');
    const syncResult = syncContextClaimToSed({
      claim: nextClaim,
      sed,
      sedId: site.sedId,
      sedVersion: site.sedVersion ?? 1,
      actor: actor ? { uid: actor.uid, email: actor.email ?? '' } : { uid: 'unknown', email: '' },
      suppressAudit: true,
      now: () => now,
    });

    for (const amendment of syncResult.amendments) {
      const result = await get().saveSEDAmendment(amendment);
      if (!result.ok) {
        throw new Error(result.error);
      }
    }
    const nextClaims = prior.claims.slice();
    nextClaims[idx] = nextClaim;
    const next: import('../types/siteContext').SiteContext = {
      ...prior,
      claims: nextClaims,
      updatedAt: now,
      updatedBy: actor,
    };

    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map(x => x.id === siteId ? { ...x, siteContext: next, updatedAt: now } : x),
      },
    }));

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, updateDoc } = await import('firebase/firestore');
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(
          doc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
          stripUndefined({ siteContext: next, updatedAt: now }),
        );
      }
    } catch (e) {
      console.warn('Failed to persist adopted context resolution state:', e);
    }

    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'sed_context_sync',
        target: { type: 'site', id: siteId, label: site.name },
        before: { sedValue: currentSedValue ?? null },
        after: { sedValue: contextValue ?? null },
        metadata: {
          direction: 'context→sed',
          claimId: resolvedClaimId,
          claimCategory: targetClaim.category,
          sedPath,
          resolution: 'adopt-context',
          source: 'manual-adopt',
        },
      });
    } catch { /* best-effort */ }
  }),

  updateSiteProjectIntent: async (siteId, intent, options) => {
    // Site_Configuration_Consolidation SC2-1 — canonical writer for
    // `site.projectIntent`. IC10 retired the legacy alias mirror: IC10-SENTINEL-OK
    // production data was migrated via
    // `scripts/migrateDropDefaultProjectIntent.ts` and every reader now
    // goes through `site.projectIntent` directly (or `resolveSiteConfig`).
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) throw new Error('Site not found');
    const { intentDecisionStateHash } = await import('../lib/intent/undecidedProjectIntent');
    const expectedDecisionHash = intentDecisionStateHash(site);
    if (options?.expectedDecisionHash && options.expectedDecisionHash !== expectedDecisionHash) {
      throw new Error('Project inputs changed while editing. Review the current values before saving.');
    }
    const now = new Date().toISOString();
    let updatedBy: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) updatedBy = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous — fine */ }

    // ─── wf wq07loep2 — the store writes the incoming intent VERBATIM ───
    // This is the CANONICAL writer for `site.projectIntent`. It must write
    // exactly what the caller hands it, with NO field-level merge.
    //
    // Why no store-level merge (the deleted "guard B"):
    // A store-level merge cannot tell "the caller OMITTED a field" from "the
    // user DELIBERATELY CLEARED it" — the store has no per-field edit
    // provenance. Any merge that "fills gaps from the existing intent"
    // therefore silently RESURRECTS values the user just cleared. Concrete
    // user-intended CLEARS the merge reverted:
    //   - switch from an exact MW to a bucket  → `withPowerRequirementBucket`
    //     DELETES `powerRequirementMW`; a merge re-injects the old exact MW.
    //   - switch from an exact date to a timeline bucket → `inServiceDate` is
    //     dropped; a merge re-injects the old date (and its derived timeline).
    //   - disable phasing → `phases` is emptied; a merge re-injects the old
    //     phased rollout.
    // The store cannot distinguish those clears from an accidental omission,
    // so the protection against a THIN write clobbering a RICH intent lives at
    // the CALLER, where edit-provenance exists:
    //   - guard (A) `resolveFreshestSiteConfig` seeds the Rerun wizard from the
    //     RICH live intent (never the thin default) — so the wizard never holds
    //     a thin payload to write back in the first place.
    //   - guard (C) `FeasibilityRerunPanel` skips the apply-to-site write
    //     entirely on an UNTOUCHED rerun (`userHasEditedIntentRef`).
    //   - the SiteConfigurationPanel passes the user's FULL form state
    //     (deliberate clears included), which this writer persists verbatim.
    // `mergeRespectingIncoming` / `projectIntentRichnessScore` stay exported
    // for guard (A) / FeasibilityRerunPanel's richness comparisons, but they
    // MUST NOT gate this write.
    //
    // ─── QA-2 (capacity SSOT) — phased-capacity reconcile-on-save ───────
    // EXCEPTION to "verbatim", and the only one: when the incoming intent is
    // PHASED and its top-level `powerRequirementMW` contradicts its OWN
    // phases' ultimate cumulative milestone, the figure is reconciled to the
    // ultimate before persist (250/500/1000 cumulative ⇒ 1000 MW, never the
    // 1750 MW double-counted sum). This is a NORMALIZATION of the incoming
    // payload against itself — it reads NOTHING from the previously stored
    // intent, so it cannot resurrect a cleared field (the bug the verbatim
    // doctrine exists to prevent). An unset/cleared MW stays unset. Audit
    // breadcrumb emitted below so the correction is diffable.
    let intentToWrite = intent;
    let capacityReconcile: { fromMW: number; toMW: number } | null = null;
    let bucketReconcile: { fromMW: number | null; toBucket: string } | null = null;
    try {
      const { reconcilePowerRequirementWithPhases, reconcilePowerRequirement } = await import('../lib/projectIntent');
      const r = reconcilePowerRequirementWithPhases(intent);
      // Adopt the reconciled intent on `reconciled` ALONE — a RANGE-mode
      // ultimate phase reconciles the coarse BUCKET (toMW stays null; a
      // range never reconciles into a scalar), and gating adoption on the
      // MW pair would silently drop that correction. The MW pair gates only
      // its audit breadcrumb below.
      if (r.reconciled) {
        intentToWrite = r.intent;
        if (r.fromMW != null && r.toMW != null) {
          capacityReconcile = { fromMW: r.fromMW, toMW: r.toMW };
        } else if (r.toBucket != null) {
          bucketReconcile = { fromMW: r.fromMW, toBucket: r.toBucket };
        }
      }
      // V134-BUCKET-REBUILD — bucket reconcile-on-save, SITTING ALONGSIDE the
      // capacity-SSOT (phases) reconcile above. Re-derive the coarse
      // `powerRequirement` bucket from the exact MW so the stored bucket can
      // never drift from `powerRequirementMW` on disk (MW=1750 but bucket left
      // at the legacy 'over-200mw' from a prior bucket-only edit → Coverage
      // Matrix shows 'over-200mw'). This handles the NON-PHASED case the phases
      // reconcile above doesn't reach; for a phased intent it's a no-op because
      // `withPowerRequirementMW` (used by that reconcile) already set the
      // matching bucket. NORMALIZATION of the payload against itself — preserves
      // the wq07loep2 verbatim-write doctrine (never reads prior stored state).
      intentToWrite = reconcilePowerRequirement(intentToWrite);
    } catch { /* best-effort — never block the save */ }
    if (isLocalMockMode()) {
      await get().updateSite(siteId, { projectIntent: intentToWrite, siteConfigUpdatedAt: now, siteConfigUpdatedBy: updatedBy });
    } else {
      if (!updatedBy) throw new Error('Not signed in');
      const ownerUid = site._ownerUid ?? site.createdBy?.uid ?? updatedBy.uid;
      const request = { siteId, ownerUid, expectedDecisionHash, projectIntent: intentToWrite,
        ...(options?.phasePlanApplication ? { phasePlanApplication: options.phasePlanApplication } : {}) };
      const { callUserFunction } = await import('../lib/callUserFunction');
      const { assertProjectIntentUpdateResponse } = await import('../lib/intent/projectIntentUpdate');
      const result = await callUserFunction<import('../types/projectIntentUpdate').UpdateProjectIntentResult>('updateProjectIntent', request);
      assertProjectIntentUpdateResponse(request, result, site, updatedBy.uid);
      const currentSite = get().siteRag.sites.find(candidate => candidate.id === siteId);
      if (!currentSite || (intentDecisionStateHash(currentSite) !== expectedDecisionHash
        && intentDecisionStateHash(currentSite) !== result.decisionHash)) {
        throw new Error('Project inputs changed before the save response arrived. Review the current values.');
      }
      // Adopt only the verified transaction result. A streamed reset or newer
      // edit received while awaiting the response must never be overwritten.
      set(current => ({ siteRag: { ...current.siteRag,
        sites: current.siteRag.sites.map(candidate => candidate.id !== siteId
          || intentDecisionStateHash(candidate) !== expectedDecisionHash ? candidate
          : { ...candidate, projectIntent: result.projectIntent,
            ...(result.generationProjectInputBinding ? { generationProjectInputBinding: result.generationProjectInputBinding } : {}),
            ...(result.updatedAt ? { siteConfigUpdatedAt: result.updatedAt, siteConfigUpdatedBy: updatedBy,
              updatedAt: result.updatedAt, lastDataChangeAt: result.updatedAt } : {}) }),
      } }));
      if (result.changed) {
        get()._staleStampSiteComposite(siteId);
        if (!get().isInitialMigration) void get().recomputeEvidenceReadiness(siteId);
      }
      intentToWrite = result.projectIntent;
    }
    // Soft-fail audit-log breadcrumb so site-level config edits stay
    // diffable from Rerun's "apply to site" path.
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      const { diffProjectIntentAttestations } = await import('../lib/projectIntentAttestations');
      void emitAuditEvent({
        action: 'site.project_intent_update',
        target: { type: 'site', id: siteId, label: site.name },
        before: { hadProjectIntent: Boolean(site.projectIntent) },
        after: { hadProjectIntent: true },
      });
      // QA-2 (capacity SSOT) — audit-friendly note for the reconcile-on-save:
      // one dedicated row recording the contradicting figure that was replaced
      // and the ultimate it was reconciled to, so the correction is reviewable
      // (and distinguishable from a plain user edit) in the audit log.
      if (capacityReconcile) {
        void emitAuditEvent({
          action: 'site.project_intent_update',
          target: { type: 'site', id: siteId, label: site.name },
          before: { powerRequirementMW: capacityReconcile.fromMW }, // SSOT-SENTINEL-OK — audit breadcrumb for the canonical projectIntent field, not a per-prefs shadow
          after: { powerRequirementMW: capacityReconcile.toMW }, // SSOT-SENTINEL-OK — audit breadcrumb for the canonical projectIntent field, not a per-prefs shadow
          metadata: {
            field: 'powerRequirementMW',
            reason: 'capacity-ssot-reconcile',
            detail: `Top-level powerRequirementMW (${capacityReconcile.fromMW} MW) contradicted the phased ultimate cumulative milestone (${capacityReconcile.toMW} MW) — reconciled to the ultimate per the cumulative phase convention (phase capacityMW = total at completion; ultimate = final milestone, never the sum).`,
          },
        });
      }
      // Range-mode sibling of the breadcrumb above: the ultimate phase
      // carries a capacityBucket, so the coarse top-level bucket was synced
      // (and any contradicting exact MW deleted — never replaced by a
      // scalar, because a range never collapses into one).
      if (bucketReconcile) {
        void emitAuditEvent({
          action: 'site.project_intent_update',
          target: { type: 'site', id: siteId, label: site.name },
          before: { powerRequirementMW: bucketReconcile.fromMW }, // SSOT-SENTINEL-OK — audit breadcrumb for the canonical projectIntent field, not a per-prefs shadow
          after: { powerRequirement: bucketReconcile.toBucket },
          metadata: {
            field: 'powerRequirement',
            reason: 'capacity-ssot-reconcile-range',
            detail: `The ultimate phase carries a capacity RANGE (${bucketReconcile.toBucket}) — synced the coarse top-level bucket${bucketReconcile.fromMW != null ? ` and deleted the contradicting exact powerRequirementMW (${bucketReconcile.fromMW} MW)` : ''}. A range never reconciles into a scalar figure.`,
          },
        });
      }
      // R-131-EPISTEMIC-GROUNDING-E — one audit row per changed attestation key.
      // Diff against the intent actually WRITTEN (`intentToWrite`).
      for (const change of diffProjectIntentAttestations(site.projectIntent, intentToWrite)) {
        void emitAuditEvent({
          action: 'site.project_intent_attestation_update',
          target: { type: 'site', id: siteId, label: site.name },
          before: { attestationKey: change.key, state: change.from ?? null },
          after: { attestationKey: change.key, state: change.to ?? null },
          metadata: { field: `attestations.${change.key}` },
        });
      }
    } catch { /* best-effort */ }
  },

  recordAuditOverride: async (siteId, override, options) => {
    await get().assertSiteWritable(siteId);
    const site = get().siteRag.sites.find((s) => s.id === siteId);
    if (!site) {
      throw new Error('Site not found');
    }
    const {
      AuditPersistenceConflictError,
      AuditPersistenceForbiddenError,
      applyAuditOverrideMutation,
      computeAuditOverridesVersion,
      generateAuditOperationId,
      isVerbatimAuditOverrideRecordEqual,
      matchesAuditOverrideOperationPayload,
    } = await import('../lib/audit/truthAttestations');
    const operationId =
      (options?.operationId ?? override.operationId)?.trim() ||
      generateAuditOperationId(`audit-override:${siteId}`);
    const expectedAuditVersion =
      options?.expectedAuditVersion ?? computeAuditOverridesVersion(site);

    if (isLocalMockMode()) {
      const localResult = applyAuditOverrideMutation(
        site,
        { kind: 'append', override },
        {
          operationId,
          expectedAuditVersion,
          expectedAuditFingerprint: options?.expectedAuditFingerprint,
        },
      );
      set((s) => ({
        siteRag: {
          ...s.siteRag,
          sites: s.siteRag.sites.map((item) =>
            item.id === siteId
              ? {
                  ...item,
                  auditOverrides: localResult.auditOverrides,
                  auditOverridesVersion: localResult.auditOverridesVersion,
                }
              : item,
          ),
        },
      }));
      return localResult.entry!;
    }

    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/audit-overrides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        override,
        operationId,
        expectedAuditVersion,
        ...(options?.expectedAuditFingerprint
          ? { expectedAuditFingerprint: options.expectedAuditFingerprint }
          : {}),
        ...(site._ownerUid ? { ownerUid: site._ownerUid } : {}),
      }),
    });
    const payload = (await res.json().catch(() => ({}))) as {
      saved?: boolean;
      idempotent?: boolean;
      operationId?: string;
      actionKind?: string;
      historyRedacted?: boolean;
      siteId?: string;
      ownerUid?: string;
      entry?: import('../types/site').SiteAuditOverride;
      auditOverrides?: import('../types/site').SiteAuditOverride[];
      auditOverridesVersion?: number;
      auditFingerprint?: string;
      writtenAt?: string;
      error?: string;
      code?: string;
      currentVersion?: number;
      currentFingerprint?: string;
      lockedByUid?: string;
      lockedByName?: string;
    };
    if (!res.ok) {
      const msg = payload.error ?? `Audit override save failed (HTTP ${res.status})`;
      if (res.status === 423 || payload.code === 'SITE_LOCKED') {
        throw new SiteLockedError({
          siteId,
          lockedByUid: payload.lockedByUid,
          lockedByName: payload.lockedByName,
        });
      }
      if (
        res.status === 409 ||
        payload.code === 'AUDIT_OVERRIDE_CONFLICT' ||
        payload.code === 'AUDIT_OPERATION_ID_MISMATCH'
      ) {
        throw new AuditPersistenceConflictError(
          payload.code === 'AUDIT_OPERATION_ID_MISMATCH'
            ? 'AUDIT_OPERATION_ID_MISMATCH'
            : 'AUDIT_OVERRIDE_CONFLICT',
          msg,
          payload.currentVersion,
          payload.currentFingerprint,
        );
      }
      if (res.status === 403 || res.status === 404 || payload.code === 'FORBIDDEN_SITE_WRITE') {
        throw new AuditPersistenceForbiddenError(msg);
      }
      throw Object.assign(new Error(msg), { status: res.status, code: payload.code });
    }

    const ackOpId = payload.operationId ?? payload.entry?.operationId;
    const ackAction = payload.actionKind ?? 'append';
    const hasValidEntry =
      Boolean(
        payload.entry &&
          typeof payload.entry.id === 'string' &&
          payload.entry.id.trim().length > 0 &&
          payload.entry.operationId === operationId &&
          matchesAuditOverrideOperationPayload(
            payload.entry,
            override as unknown as Record<string, unknown>,
          ),
      );
    const hasMonotonicVersion =
      typeof payload.auditOverridesVersion === 'number' &&
      Number.isInteger(payload.auditOverridesVersion) &&
      payload.auditOverridesVersion >= (payload.idempotent ? 1 : expectedAuditVersion + 1);
    const hasValidHistory =
      payload.historyRedacted === true
        ? payload.auditOverrides === undefined
        : Array.isArray(payload.auditOverrides) &&
          typeof payload.auditOverridesVersion === 'number' &&
          payload.auditOverridesVersion >= payload.auditOverrides.length &&
          payload.auditOverridesVersion ===
            computeAuditOverridesVersion({
              auditOverrides: payload.auditOverrides,
              auditOverridesVersion: payload.auditOverridesVersion,
            }) &&
          Boolean(
            payload.entry &&
              payload.auditOverrides.some((o) =>
                isVerbatimAuditOverrideRecordEqual(o, payload.entry),
              ),
          );

    const ackValid =
      payload.saved === true &&
      payload.siteId === siteId &&
      ackOpId === operationId &&
      ackAction === 'append' &&
      hasValidEntry &&
      hasMonotonicVersion &&
      hasValidHistory;
    if (!ackValid) {
      throw new Error('Audit override save response was incomplete or mismatched.');
    }

    const committedEntry = payload.entry!;
    const committedVersion = payload.auditOverridesVersion!;
    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map((item) => {
          if (item.id !== siteId) return item;
          const nextOverrides =
            payload.historyRedacted === true
              ? (item.auditOverrides ?? []).some((o) => o.id === committedEntry.id)
                ? [...(item.auditOverrides ?? [])]
                : [...(item.auditOverrides ?? []), committedEntry]
              : payload.auditOverrides!;
          return {
            ...item,
            auditOverrides: nextOverrides,
            auditOverridesVersion: committedVersion,
            ...(payload.writtenAt ? { updatedAt: payload.writtenAt } : {}),
          };
        }),
      },
    }));

    if (!payload.idempotent) {
      try {
        const entry = committedEntry;
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action:
            override.type === 'density'
              ? 'audit.citation_density_override'
              : override.type === 'coverage'
                ? 'audit.provider_coverage_override'
                : override.type === 'validate' ||
                    override.type === 'reconcile' ||
                    override.type === 'retain-unknown'
                  ? 'audit.intent_sed_override'
                  : 'gate' in override && override.gate === 'provider-coverage'
                    ? 'audit.provider_coverage_override'
                    : 'gate' in override && override.gate === 'provider-precedence'
                      ? 'audit.provider_precedence_override'
                      : 'audit.intent_sed_override',
          target: { type: 'site', id: siteId, label: site.name },
          metadata: {
            overrideId: entry.id,
            logicalKey: entry.logicalKey,
            sequence: entry.sequence,
            supersedesOverrideId: entry.supersedesOverrideId,
            operationId: entry.operationId,
            gate: 'gate' in override ? override.gate : undefined,
            type: 'type' in override ? override.type : undefined,
            fields: 'fields' in override ? override.fields : undefined,
            rationale: override.rationale,
            rationaleLength: override.rationale ? override.rationale.length : 0,
            assumptionLabel: 'assumptionLabel' in override ? override.assumptionLabel : undefined,
            sourceType: 'sourceType' in override ? override.sourceType : undefined,
            sourceCitation: 'sourceCitation' in override ? override.sourceCitation : undefined,
            evidenceDocumentId:
              'evidenceDocumentId' in override ? override.evidenceDocumentId : undefined,
            evidenceAttachment:
              'evidenceAttachment' in override ? override.evidenceAttachment : undefined,
            reportTypeId: override.reportTypeId,
          },
        });
      } catch {
        /* best-effort */
      }
    }

    Object.defineProperties(committedEntry, {
      _auditVersion: {
        value: committedVersion,
        enumerable: false,
        configurable: true,
      },
      _auditFingerprint: {
        value: payload.auditFingerprint,
        enumerable: false,
        configurable: true,
      },
    });
    return committedEntry;
  },

  removeAuditOverride: async (siteId, overrideId, options) => {
    await get().assertSiteWritable(siteId);
    const site = get().siteRag.sites.find((s) => s.id === siteId);
    if (!site) {
      throw new Error('Site not found');
    }
    const {
      AuditPersistenceConflictError,
      AuditPersistenceForbiddenError,
      applyAuditOverrideMutation,
      computeAuditOverridesVersion,
      generateAuditOperationId,
      isVerbatimAuditOverrideRecordEqual,
    } = await import('../lib/audit/truthAttestations');
    const operationId =
      options?.operationId?.trim() ||
      generateAuditOperationId(`audit-retract:${siteId}:${overrideId}`);
    const expectedAuditVersion =
      options?.expectedAuditVersion ?? computeAuditOverridesVersion(site);

    if (isLocalMockMode()) {
      const localResult = applyAuditOverrideMutation(
        site,
        { kind: 'retract', overrideId, rationale: options?.rationale },
        {
          operationId,
          expectedAuditVersion,
          expectedAuditFingerprint: options?.expectedAuditFingerprint,
        },
      );
      if (localResult.noop) return;
      set((s) => ({
        siteRag: {
          ...s.siteRag,
          sites: s.siteRag.sites.map((item) =>
            item.id === siteId
              ? {
                  ...item,
                  auditOverrides: localResult.auditOverrides,
                  auditOverridesVersion: localResult.auditOverridesVersion,
                }
              : item,
          ),
        },
      }));
      return;
    }

    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/audit-overrides/${encodeURIComponent(overrideId)}/retract`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(options?.rationale ? { rationale: options.rationale } : {}),
          operationId,
          expectedAuditVersion,
          ...(options?.expectedAuditFingerprint
            ? { expectedAuditFingerprint: options.expectedAuditFingerprint }
            : {}),
          ...(site._ownerUid ? { ownerUid: site._ownerUid } : {}),
        }),
      },
    );
    const payload = (await res.json().catch(() => ({}))) as {
      saved?: boolean;
      noop?: boolean;
      idempotent?: boolean;
      operationId?: string;
      actionKind?: string;
      historyRedacted?: boolean;
      siteId?: string;
      ownerUid?: string;
      entry?: import('../types/site').SiteAuditOverride | null;
      auditOverrides?: import('../types/site').SiteAuditOverride[];
      auditOverridesVersion?: number;
      writtenAt?: string;
      error?: string;
      code?: string;
      currentVersion?: number;
      currentFingerprint?: string;
      lockedByUid?: string;
      lockedByName?: string;
    };
    if (!res.ok) {
      const msg = payload.error ?? `Audit override retraction failed (HTTP ${res.status})`;
      if (res.status === 423 || payload.code === 'SITE_LOCKED') {
        throw new SiteLockedError({
          siteId,
          lockedByUid: payload.lockedByUid,
          lockedByName: payload.lockedByName,
        });
      }
      if (
        res.status === 409 ||
        payload.code === 'AUDIT_OVERRIDE_CONFLICT' ||
        payload.code === 'AUDIT_OPERATION_ID_MISMATCH'
      ) {
        throw new AuditPersistenceConflictError(
          payload.code === 'AUDIT_OPERATION_ID_MISMATCH'
            ? 'AUDIT_OPERATION_ID_MISMATCH'
            : 'AUDIT_OVERRIDE_CONFLICT',
          msg,
          payload.currentVersion,
          payload.currentFingerprint,
        );
      }
      if (res.status === 403 || res.status === 404 || payload.code === 'FORBIDDEN_SITE_WRITE') {
        throw new AuditPersistenceForbiddenError(msg);
      }
      throw Object.assign(new Error(msg), { status: res.status, code: payload.code });
    }

    const ackOpId = payload.operationId ?? payload.entry?.operationId;
    const ackAction = payload.actionKind ?? 'retract';
    const hasMonotonicVersion =
      typeof payload.auditOverridesVersion === 'number' &&
      Number.isInteger(payload.auditOverridesVersion) &&
      payload.auditOverridesVersion >=
        (payload.noop ? expectedAuditVersion : payload.idempotent ? 1 : expectedAuditVersion + 1);
    const requestedTargetId = overrideId.trim();
    const requestedExplicitVersion = /#v\d+$/.test(requestedTargetId);
    const stripVersionSuffix = (id: string) => id.replace(/#v\d+$/, '');
    const ackRetractsId =
      payload.entry &&
      'retractsOverrideId' in payload.entry &&
      typeof payload.entry.retractsOverrideId === 'string'
        ? payload.entry.retractsOverrideId.trim()
        : '';
    const matchesRequestedRetractTarget =
      ackRetractsId.length > 0 &&
      (requestedExplicitVersion
        ? ackRetractsId === requestedTargetId
        : ackRetractsId === requestedTargetId ||
          stripVersionSuffix(ackRetractsId) === stripVersionSuffix(requestedTargetId)) &&
      (payload.historyRedacted === true ||
        !Array.isArray(payload.auditOverrides) ||
        payload.auditOverrides.some((o) => o.id === ackRetractsId));
    const hasValidRetractEntry =
      payload.noop === true ||
      Boolean(
        payload.entry &&
          typeof payload.entry.id === 'string' &&
          payload.entry.id.trim().length > 0 &&
          'type' in payload.entry &&
          payload.entry.type === 'retract' &&
          matchesRequestedRetractTarget &&
          payload.entry.operationId === operationId &&
          (!options?.rationale?.trim() || payload.entry.rationale === options.rationale.trim()),
      );
    const hasValidHistory =
      payload.historyRedacted === true
        ? payload.auditOverrides === undefined
        : Array.isArray(payload.auditOverrides) &&
          typeof payload.auditOverridesVersion === 'number' &&
          payload.auditOverridesVersion >= payload.auditOverrides.length &&
          payload.auditOverridesVersion ===
            computeAuditOverridesVersion({
              auditOverrides: payload.auditOverrides,
              auditOverridesVersion: payload.auditOverridesVersion,
            }) &&
          (payload.noop === true ||
            Boolean(
              payload.entry &&
                payload.auditOverrides.some((o) =>
                  isVerbatimAuditOverrideRecordEqual(o, payload.entry ?? undefined),
                ),
            ));

    const ackValid =
      payload.saved === true &&
      payload.siteId === siteId &&
      (payload.noop === true ? (payload.operationId ?? operationId) === operationId : ackOpId === operationId) &&
      ackAction === 'retract' &&
      hasMonotonicVersion &&
      hasValidRetractEntry &&
      hasValidHistory;
    if (!ackValid) {
      throw new Error('Audit override retraction response was incomplete or mismatched.');
    }

    if (payload.noop) return;
    const committedEntry = payload.entry!;
    const committedVersion = payload.auditOverridesVersion!;
    set((s) => ({
      siteRag: {
        ...s.siteRag,
        sites: s.siteRag.sites.map((item) => {
          if (item.id !== siteId) return item;
          const nextOverrides =
            payload.historyRedacted === true
              ? (item.auditOverrides ?? []).some((o) => o.id === committedEntry.id)
                ? [...(item.auditOverrides ?? [])]
                : [...(item.auditOverrides ?? []), committedEntry]
              : payload.auditOverrides!;
          return {
            ...item,
            auditOverrides: nextOverrides,
            auditOverridesVersion: committedVersion,
            ...(payload.writtenAt ? { updatedAt: payload.writtenAt } : {}),
          };
        }),
      },
    }));
  },

  retryProviderFetch: async (siteId, providerId) => {
    const { PROVIDER_TO_SED_SECTION } = await import('../lib/audit/providerCoverageGate');
    const { normalizeProviderId } = await import('../lib/audit/providerCoverageRegistry');
    const normalized = normalizeProviderId(providerId);
    const sectionId = PROVIDER_TO_SED_SECTION[normalized] ?? PROVIDER_TO_SED_SECTION[providerId];
    if (!sectionId) {
      console.warn('[retryProviderFetch] no SED section for provider', providerId);
      return;
    }
    await get().refreshSedSections(siteId, [sectionId]);
  },

  updateSiteTypedPreferencesDefault: async (siteId, reportType, preferences) => {
    // Site_Configuration_Consolidation SC2-1 — per-report-type write.
    // The site doc's `typedPreferencesDefaults` is a sparse map keyed
    // by ReportTypeId. Other slots are preserved; only the named slot
    // is replaced (the spread merge below ensures this even when the
    // doc has never carried the field — legacy sites get `{}` first).
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const now = new Date().toISOString();
    let updatedBy: { uid: string; email?: string } | undefined;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) updatedBy = { uid: u.uid, email: u.email ?? undefined };
    } catch { /* anonymous */ }
    const nextDefaults: import('../types/siteRag').TypedPreferencesByType = {
      ...(site.typedPreferencesDefaults ?? {}),
      [reportType]: preferences,
    } as import('../types/siteRag').TypedPreferencesByType;
    await get().updateSite(siteId, {
      typedPreferencesDefaults: nextDefaults,
      siteConfigUpdatedAt: now,
      siteConfigUpdatedBy: updatedBy,
    });
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'site.typed_preferences_default_update',
        target: { type: 'site', id: siteId, label: site.name },
        metadata: { reportType },
      });
    } catch { /* best-effort */ }
  },

  saveSiteCustomBrief: async (siteId, preferences, existingId) => {
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return '';
    const now = new Date().toISOString();
    const name = preferences.briefName?.trim() || 'Untitled brief';
    const id = existingId ?? `brief-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const existing = site.savedCustomBriefs ?? [];
    const entry: import('../types/site').SavedCustomBrief = {
      id,
      name,
      createdAt: existing.find(b => b.id === id)?.createdAt ?? now,
      updatedAt: now,
      preferences: {
        ...preferences,
        questions: preferences.questions.map(q => ({ ...q })),
      },
    };
    const next = existing.some(b => b.id === id)
      ? existing.map(b => (b.id === id ? entry : b))
      : [...existing, entry];
    await get().updateSite(siteId, { savedCustomBriefs: next });
    return id;
  },

  deleteSiteCustomBrief: async (siteId, briefId) => {
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site?.savedCustomBriefs?.length) return;
    const next = site.savedCustomBriefs.filter(b => b.id !== briefId);
    await get().updateSite(siteId, { savedCustomBriefs: next });
  },

  restampFinancialReportsForSite: async (siteId) => {
    // D9 — see the interface note. Cheap, deterministic, best-effort.
    try {
      const state = get();
      const site = state.siteRag.sites.find(s => s.id === siteId);
      if (!site?.financialAssumptions) return { restamped: 0 };
      const { restampFinancialReport, canRestampFinancialReport } = await import('../lib/restampFinancialReport');
      const targets = state.siteRag.reports.filter(
        r => r.siteId === siteId && canRestampFinancialReport(r, site),
      );
      if (targets.length === 0) return { restamped: 0 };

      const restamped: SiteRagReport[] = [];
      for (const report of targets) {
        try {
          const result = await restampFinancialReport({ report, site });
          if (result?.changed) restamped.push(result.report);
        } catch (e) {
          console.warn(`restampFinancialReportsForSite: ${report.id} failed`, e);
        }
      }
      if (restamped.length === 0) return { restamped: 0 };

      // Immediate in-memory update — the Financial tab, the cover chip and the
      // dashboard tile all read from here, so they stop quoting the old model
      // on the very next render.
      const byId = new Map(restamped.map(r => [r.id, r]));
      set((prev) => ({
        siteRag: {
          ...prev.siteRag,
          reports: prev.siteRag.reports.map(r => byId.get(r.id) ?? r),
        },
      }));

      // Debounced persist — coalesces a run of per-field edits into one write
      // per report. Reads the LATEST store copy at flush time rather than
      // closing over `restamped`, so a later edit is not overwritten by an
      // earlier timer.
      const pending = restampPersistTimers.get(siteId);
      if (pending) clearTimeout(pending);
      restampPersistTimers.set(siteId, setTimeout(() => {
        restampPersistTimers.delete(siteId);
        void (async () => {
          for (const id of byId.keys()) {
            try {
              const latest = get().siteRag.reports.find(r => r.id === id);
              const latestSite = get().siteRag.sites.find(s => s.id === siteId);
              if (!latest || !canRestampFinancialReport(latest, latestSite)) continue;
              await get().updateRagReport(id, {
                financialAnalysis: latest.financialAnalysis,
                overallScore: latest.overallScore,
                overallStatus: latest.overallStatus,
                conclusion: latest.conclusion,
              });
            } catch (e) {
              console.warn(`restampFinancialReportsForSite: persist failed for ${id}`, e);
            }
          }
        })();
      }, RESTAMP_PERSIST_DEBOUNCE_MS));

      return { restamped: restamped.length };
    } catch (e) {
      console.warn('restampFinancialReportsForSite failed', e);
      return { restamped: 0 };
    }
  },

  updateSiteFinancialAssumptions: async (siteId, assumptions) => {
    // FA1 — full-replace writer. Stamps `updatedAt` + `updatedBy` on
    // the payload so downstream readers can render "Last updated 2 min
    // ago by …". Audit-log breadcrumb is soft-fail.
    //
    // SINGLE WRITE PATH (audit 1.1): this is a WHOLE-RECORD replace — the
    // caller owns `phases[]`, which is authoritative, so there is no campus
    // field to attribute and `applyCampusPatch` has nothing to do here. What it
    // DOES need is the write boundary: `writeFinancialAssumptionsChecked`
    // re-derives the campus rollup AND reconciles the metadata that rollup
    // invalidates (stale campus provenance chips, an expired
    // `procurementCommercials` snapshot).
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const now = new Date().toISOString();
    let updatedBy = assumptions.updatedBy;
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u && (!updatedBy || updatedBy.length === 0)) {
        updatedBy = u.email ?? u.uid;
      }
    } catch { /* anonymous — fine */ }
    const { writeFinancialAssumptionsChecked } = await import('../lib/financialWriteBoundary');
    const { isDataCenterFinance } = await import('../lib/dataCenterFinance');
    let dcContext: import('../lib/financialWriteBoundary').FinancialWriteBoundaryOptions = {};
    if (isDataCenterFinance(assumptions)) {
      const { auth } = await import('../firebase');
      if (!auth.currentUser) throw new Error('Sign in before saving a source-bound financial model.');
      const { hydrateDocumentBodies } = await import('../lib/documentBody');
      dcContext = { ownerUid: siteOwnerUid(site, auth.currentUser.uid), demonstrationScenarioId: site.demonstrationProvenance?.scenarioId,
        documents: await hydrateDocumentBodies(site.documents ?? []) };
    }
    const stamped = writeFinancialAssumptionsChecked(
      siteId,
      {
        ...assumptions,
        updatedAt: now,
        updatedBy: updatedBy ?? 'anonymous',
      },
      now,
      { planUpdatedAt: site.supplyChainPlan?.updatedAt ?? null, ...dcContext },
    );
    await get().updateSite(siteId, { financialAssumptions: stamped });
    // D9 — the model moved, so the reports' deterministic figures move with it.
    void get().restampFinancialReportsForSite(siteId);
    try {
      const { emitAuditEvent } = await import('../lib/auditLogClient');
      void emitAuditEvent({
        action: 'site.financial_assumptions_update',
        target: { type: 'site', id: siteId, label: site.name },
        before: { hadFinancialAssumptions: Boolean(site.financialAssumptions) },
        after: { hadFinancialAssumptions: true, source: stamped.source },
      });
    } catch { /* best-effort */ }
  },

  updateSiteFinancialAssumptionsField: async (siteId, field, value, provenance) => {
    // FA1 — per-field patch. Lazily seeds an empty
    // FinancialAssumptions when the site has none yet so the form's
    // first edit is just one round-trip.
    //
    // PHASE-AWARE (fixed 2026-08-19): `phases[]` is authoritative on write, so a
    // patch to a rollup-derived campus field (capacity / CAPEX / CF / COD)
    // written straight onto the core was reverted by the next save — the
    // "Sync to intent" button turned its warning strip green, stamped `manual`
    // provenance, and the value vanished on the following save. `applyCampusPatch`
    // routes those fields onto the phase; everything else still writes the core.
    // This path also now goes through the FA write boundary so the record keeps
    // its `modelVersion` stamp, a consistent campus rollup, and no provenance
    // chip that outlives the value it describes.
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { applyCampusPatch, emptyFinancialAssumptions } = await import('../types/financialAssumptions');
    const { writeFinancialAssumptionsChecked } = await import('../lib/financialWriteBoundary');
    const now = new Date().toISOString();
    let updatedBy = site.financialAssumptions?.updatedBy ?? 'anonymous';
    try {
      const { auth } = await import('../firebase');
      const u = auth.currentUser;
      if (u) updatedBy = u.email ?? u.uid;
    } catch { /* anonymous */ }
    if (site.financialAssumptions?.businessModelKind === 'data-center-capacity-fee') throw new Error('Edit the annual data-center model through its checked whole-record entry form.');
    const base = site.financialAssumptions ?? emptyFinancialAssumptions(now, updatedBy);
    const { next: patched, unapplied } = applyCampusPatch(base, { [field]: value } as Partial<
      import('../types/financialAssumptions').FinancialAssumptionsCore
    >);
    if (unapplied.length > 0) {
      // Multi-phase model: a single campus figure cannot be attributed to a
      // phase. Refuse loudly rather than writing something the next save drops.
      return {
        ok: false as const,
        error: `"${field}" is derived from this model's ${(base.phases ?? []).length} phases — `
          + 'edit it on the phase you mean rather than at campus level.',
      };
    }
    const next = writeFinancialAssumptionsChecked(
      siteId,
      {
        ...patched,
        fieldProvenance: {
          ...patched.fieldProvenance,
          [field]: provenance,
        },
        updatedAt: now,
        updatedBy,
      },
      now,
      { planUpdatedAt: site.supplyChainPlan?.updatedAt ?? null },
    );
    await get().updateSite(siteId, { financialAssumptions: next });
    void get().restampFinancialReportsForSite(siteId); // D9
    return { ok: true as const };
  },

  generateBomFromIntent: async ({ siteId, intent, financial, trigger }) => {
    // Supply_Chain_BoM_Wire_Up SC4-1 — site-canonical re-seed of
    // `site.supplyChainPlan.bom` from the resolved Project Intent +
    // Financial Assumptions. PP-aware via `intent.phases`. Never throws.
    try {
      const state = get();
      const site = state.siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };

      const resolvedIntent = intent ?? site.projectIntent;
      const resolvedFinancial = financial ?? site.financialAssumptions;
      if (!resolvedIntent) {
        return {
          ok: false as const,
          error: 'Project Intent is required before seeding a BoM',
        };
      }

      // Resolver: prefer the most recent report's recommendations so the
      // seeder's technology-resolution heuristics line up with what the
      // user saw on the report. Falls through to [] when no report exists
      // — the BoM seeder treats an empty recommendations list as "no
      // power-strategy hint" and falls back to financial.technology /
      // intent.nuclearInterest.
      const siteReports = state.siteRag.reports.filter(r => r.siteId === siteId);
      const recommendations = siteReports.length > 0
        ? (siteReports
          .slice()
          .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0]
          ?.recommendations ?? [])
        : [];

      // PP-aware: when `intent.phases` is non-empty AND the
      // bomTemplates module exports `generateBomFromPhases` (PP pack
      // has landed), seed per-phase. Otherwise — and for legacy
      // pre-PP code — fall back to the single-phase generator. The
      // dynamic-import shape lets this worktree (which pre-dates the
      // PP pack) still compile against an older bomTemplates module.
      const bomTemplates = await import('../lib/bomTemplates');
      const intentAny = resolvedIntent as import('../types/siteRag').ProjectIntent & {
        phases?: ReadonlyArray<unknown>;
      };
      const phases = (intentAny.phases && intentAny.phases.length > 0)
        ? intentAny.phases
        : null;

      const seederContext = {
        intent: resolvedIntent,
        financial: resolvedFinancial ?? {},
        recommendations,
      };
      type PhasedSeeder = (ctx: typeof seederContext) => import('../types/supplyChain').BomItem[];
      const phasedSeeder = (bomTemplates as { generateBomFromPhases?: PhasedSeeder })
        .generateBomFromPhases;
      const phasedSeederRan = !!(phases && typeof phasedSeeder === 'function');
      const bom = phasedSeederRan
        ? (phasedSeeder as PhasedSeeder)(seederContext)
        : bomTemplates.generateBomFromContext(seederContext);

      // Resolve actor identity for stamping + audit.
      let actor = 'anonymous';
      try {
        const { auth } = await import('../firebase');
        const u = auth.currentUser;
        if (u) actor = u.email ?? u.uid;
      } catch { /* anonymous — fine */ }

      const now = new Date().toISOString();
      const priorPlan = site.supplyChainPlan;
      const preservedManual = (priorPlan?.bom ?? []).filter(
        item => item.derivedFrom === 'manual' || item.derivedFrom === 'uploaded-bom',
      );
      const generatedIds = new Set(bom.map(item => item.id));
      const mergedBom = [
        ...bom,
        ...preservedManual.filter(item => !generatedIds.has(item.id)),
      ];
      const { suppliersByCategoryFromDirectory, resolveSupplierDirectory } = await import('../lib/resolveSupplierDirectory');
      const directory = await resolveSupplierDirectory(site.companyId ?? get().companyId);
      const bomCategories = new Set(mergedBom.map(item => item.category));
      const suggestedSuppliers = Array.from(bomCategories).flatMap(category =>
        suppliersByCategoryFromDirectory(directory, category),
      );
      const supplierById = new Map<string, import('../types/supplyChain').Supplier>();
      for (const s of suggestedSuppliers) supplierById.set(s.id, s);
      for (const s of priorPlan?.suppliers ?? []) supplierById.set(s.id, s);
      const nextPlan: import('../types/supplyChain').SupplyChainPlan = {
        // Preserve sibling collections + provenance from any prior plan;
        // only the bom + seed-timestamp fields are replaced.
        bom: mergedBom,
        suppliers: Array.from(supplierById.values()),
        quotes: priorPlan?.quotes ?? [],
        ...(priorPlan?.materialScopeLimitations !== undefined ? { materialScopeLimitations: priorPlan.materialScopeLimitations } : {}),
        version: priorPlan?.version ?? 1,
        generatedAt: priorPlan?.generatedAt ?? now,
        generatedBy: priorPlan?.generatedBy ?? 'auto',
        updatedAt: now,
        updatedBy: actor,
        lastSeededAt: now,
        lastSeededBy: actor,
        seedSource: {
          // Deep-clone so subsequent edits to the live intent / financial
          // cannot retroactively mutate the snapshot.
          intentSnapshotAtSeed: JSON.parse(JSON.stringify(resolvedIntent)),
          ...(resolvedFinancial
            ? { financialSnapshotAtSeed: JSON.parse(JSON.stringify(resolvedFinancial)) }
            : {}),
        },
      };

      await get().updateSite(siteId, { supplyChainPlan: nextPlan });

      // Soft-fail audit-log breadcrumb. Mirrors `site.financial_assumptions_update`.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'scm.bom_seeded',
          target: { type: 'site', id: siteId, label: site.name },
          metadata: {
            itemCount: bom.length,
            phased: phasedSeederRan,
            phaseCount: phases?.length ?? 0,
            trigger: trigger ?? 'manual',
          },
        });
      } catch { /* best-effort */ }

      return { ok: true as const, itemCount: mergedBom.length };
    } catch (e) {
      console.error('generateBomFromIntent failed:', e);
      return { ok: false as const, error: (e as Error)?.message ?? 'Unknown error' };
    }
  },

  updateSupplyChainPlan: async (siteId, updater) => {
    try {
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };
      const plan = site.supplyChainPlan;
      if (!plan) return { ok: false as const, error: 'Supply chain plan not initialized' };
      const nextPlanRaw = updater(plan);
      let actor = 'anonymous';
      try {
        const { auth } = await import('../firebase');
        const u = auth.currentUser;
        if (u) actor = u.email ?? u.uid;
      } catch { /* anonymous */ }
      const nextPlan = {
        ...nextPlanRaw,
        updatedAt: new Date().toISOString(),
        updatedBy: actor,
      };
      await get().updateSite(siteId, { supplyChainPlan: nextPlan });
      return { ok: true as const, plan: nextPlan };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed to update supply chain plan' };
    }
  },

  linkBomSourceDocument: async (siteId, documentId, change) => {
    const { auth } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in before linking equipment sources.');
    const initialSites = get().siteRag.sites.filter(site => site.id === siteId);
    if (initialSites.length !== 1) throw new Error('The equipment site is no longer available.');
    const ownerUid = siteOwnerUid(initialSites[0]!, user.uid);
    await enqueueSiteDocumentWrite(`${ownerUid}/${siteId}/bom-sources`, async () => {
      if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before linking.');
      const sites = get().siteRag.sites.filter(site => site.id === siteId);
      const site = sites.length === 1 ? sites[0] : undefined;
      if (!site || siteOwnerUid(site, user.uid) !== ownerUid) throw new Error('The equipment owner changed. Reload before linking.');
      await get().assertSiteWritable(siteId);
      const sources = site.documents?.filter(document => document.id === documentId) ?? [];
      const source = sources.length === 1 ? sources[0] : undefined;
      const plan = site.supplyChainPlan;
      if (!source || !plan) throw new Error('Choose an existing site document and initialized equipment plan.');
      const operationKey = JSON.stringify([user.uid, ownerUid, siteId, documentId, change]);
      let request = _bomSourceDocumentPendingRequests.get(operationKey);
      if (!request) {
        request = bomSourceDocumentLinkRequestSchema.parse({ operationId: crypto.randomUUID(), ownerUid,
          siteId, documentId, expectedDocumentHash: siteDocumentMetadataBasisHash(source),
          expectedPlanHash: bomSourceDocumentLinkPlanHash(plan), change });
        if (_bomSourceDocumentPendingRequests.size >= 64) {
          const oldest = _bomSourceDocumentPendingRequests.keys().next().value;
          if (oldest !== undefined) _bomSourceDocumentPendingRequests.delete(oldest);
        }
        _bomSourceDocumentPendingRequests.set(operationKey, request);
      }
      // Keep the exact request after an ambiguous transport failure. The
      // server receipt verifies both current results before replaying its ACK.
      const response = await authedFetch('/api/site-document-metadata/bom-link', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request),
      });
      if (!response.ok) {
        // A rate limit, expired token, lock or earlier lost ACK is not proof
        // that the operation did not commit. Keep its exact receipt identity.
        const detail = await response.json().catch(() => null) as { error?: unknown } | null;
        throw Object.assign(new Error(typeof detail?.error === 'string' ? detail.error
          : `The equipment source link was not saved (${response.status}).`), { status: response.status });
      }
      const parsed = bomSourceDocumentLinkAckSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error('The equipment source save could not be confirmed. Retry or reload before continuing.');
      const ack = parsed.data as BomSourceDocumentLinkAck;
      if (ack.operationId !== request.operationId || ack.ownerUid !== ownerUid || ack.siteId !== siteId
        || ack.documentId !== documentId || ack.document.id !== documentId
        || ack.beforeDocumentHash !== request.expectedDocumentHash || ack.beforePlanHash !== request.expectedPlanHash
        || ack.afterDocumentHash !== siteDocumentMetadataBasisHash(ack.document)
        || ack.afterPlanHash !== bomSourceDocumentLinkPlanHash(ack.supplyChainPlan)) {
        throw new Error('The equipment source acknowledgment did not match this edit. Reload before continuing.');
      }
      const currentSites = get().siteRag.sites.filter(row => row.id === siteId);
      const currentSite = currentSites.length === 1 ? currentSites[0] : undefined;
      const currentSources = currentSite?.documents?.filter(document => document.id === documentId) ?? [];
      const current = currentSources.length === 1 ? currentSources[0] : undefined;
      const currentPlan = currentSite?.supplyChainPlan;
      if (auth.currentUser?.uid !== user.uid || !currentSite || siteOwnerUid(currentSite, user.uid) !== ownerUid
        || !current || !currentPlan
        || ![request.expectedDocumentHash, ack.afterDocumentHash].includes(siteDocumentMetadataBasisHash(current))
        || ![request.expectedPlanHash, ack.afterPlanHash].includes(bomSourceDocumentLinkPlanHash(currentPlan))) {
        throw new Error('The source or equipment plan changed while saving. Reload to review the saved version.');
      }
      // A source link does not alter the document body. Retain the most
      // complete verified session copy if the listener delivered a preview.
      const bodySource = source.textContent.length > current.textContent.length ? source : current;
      const confirmedDocument = { ...source, ...current, ...ack.document, textContent: bodySource.textContent,
        ...(bodySource.pages ? { pages: bodySource.pages } : {}) };
      set(state => ({ siteRag: { ...state.siteRag,
        sites: state.siteRag.sites.map(row => row.id === siteId ? { ...row, updatedAt: ack.writtenAt,
          supplyChainPlan: ack.supplyChainPlan,
          documents: (row.documents ?? []).map(document => document.id === documentId ? confirmedDocument : document),
        } : row),
      } }));
      // Published report evidence stays frozen; there is no browser document
      // write or second plan save after the server's atomic commit.
      _bomSourceDocumentPendingRequests.delete(operationKey);
    });
  },

  applyBomFromUpload: async ({ siteId, items, mode, documentId }) => {
    try {
      const state = get();
      const site = state.siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };
      if (items.length === 0) return { ok: false as const, error: 'No BoM items to apply' };

      let actor = 'anonymous';
      try {
        const { auth } = await import('../firebase');
        const u = auth.currentUser;
        if (u) actor = u.email ?? u.uid;
      } catch { /* anonymous */ }

      const now = new Date().toISOString();
      const { applyBomUpload } = await import('../services/supplyChain/applyBomUpload');
      const merged = applyBomUpload({
        plan: site.supplyChainPlan,
        items,
        mode,
        actor,
        now,
        documentId,
      });

      // BoM upload updates line items only — project suppliers are added
      // explicitly from the directory, quotes, or manual entry.
      await get().updateSite(siteId, { supplyChainPlan: merged.plan });

      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'scm.bom_uploaded',
          target: { type: 'site', id: siteId, label: site.name },
          metadata: { itemCount: items.length, mode, documentId: documentId ?? null },
        });
      } catch { /* best-effort */ }

      return { ok: true as const, itemCount: items.length };
    } catch (e) {
      console.error('applyBomFromUpload failed:', e);
      return { ok: false as const, error: (e as Error)?.message ?? 'Unknown error' };
    }
  },

  addSupplierFromDirectory: async ({ siteId, supplierId, companyId }) => {
    try {
      const state = get();
      const site = state.siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };

      const { resolveSupplierDirectory, getSupplierFromDirectory } = await import('../lib/resolveSupplierDirectory');
      const directory = await resolveSupplierDirectory(companyId ?? site.companyId ?? state.companyId);
      const supplier = getSupplierFromDirectory(directory, supplierId);
      if (!supplier) return { ok: false as const, error: `Supplier ${supplierId} not found in directory` };

      // Wave B5 (#30) — DECOUPLE shortlisting from BoM existence. Shortlisting a
      // supplier onto a project is qualification/sourcing work that legitimately
      // precedes a populated BoM (you scout vendors before the line items firm
      // up). The old `bom.length === 0` block forced a post-click error and was,
      // in practice, unreachable from the directory UI (which itself sits behind
      // a BoM-exists gate) — so it only ever fired on programmatic/edge calls.
      // We now require only that a supply-chain plan EXISTS (an empty-BoM plan is
      // fine); the genuinely-absent-plan case is still rejected, with the clearer
      // message coming from `updateSupplyChainPlan` ('Supply chain plan not
      // initialized') so callers can prompt the user to initialise the plan.
      const prior = site.supplyChainPlan;
      if (!prior) {
        return { ok: false as const, error: 'Supply chain plan not initialized' };
      }

      const res = await get().updateSupplyChainPlan(siteId, (current) => {
        const exists = current.suppliers.some(s => s.id === supplier.id);
        return {
          ...current,
          suppliers: exists ? current.suppliers : [...current.suppliers, supplier],
        };
      });
      if (!res.ok) return res;
      return { ok: true as const };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed to add supplier' };
    }
  },

  updateBomItemStatus: async (siteId, itemId, toStatus, opts) => {
    try {
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site?.supplyChainPlan) return { ok: false as const, error: 'Supply chain plan not initialized' };
      const item = site.supplyChainPlan.bom.find(b => b.id === itemId);
      if (!item) return { ok: false as const, error: `BoM item ${itemId} not found` };
      const { applyStatusTransition, buildStatusChangeEvent } = await import('../lib/bomItemStatusFlow');
      const { auth } = await import('../firebase');
      const actor = auth.currentUser?.email ?? auth.currentUser?.uid ?? 'anonymous';
      const actorUid = auth.currentUser?.uid ?? 'anonymous';
      const updatedItem = opts.revert
        ? applyStatusTransition(item, toStatus, { revert: true })
        : applyStatusTransition(item, toStatus);
      const nextBom = site.supplyChainPlan.bom.map(b => b.id === itemId ? updatedItem : b);
      const nowIso = new Date().toISOString();
      const nextPlan = {
        ...site.supplyChainPlan,
        bom: nextBom,
        updatedAt: nowIso,
        updatedBy: actor,
      };
      await get().updateSite(siteId, { supplyChainPlan: nextPlan });
      const { event } = buildStatusChangeEvent({
        siteId,
        item,
        toStatus,
        actorUid,
        actorEmail: actor,
        now: nowIso,
        ...(opts.revert ? { revert: true, reason: opts.reason } : {}),
      });
      const { writeBomStatusChange } = await import('../services/supplyChain/bomStatusChangeLog');
      await writeBomStatusChange(event, auth.currentUser?.uid ?? null);
      return { ok: true as const };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed status transition' };
    }
  },

  syncSiteWorksBomFromLayout: async (siteId) => {
    try {
      const sourceSite = get().siteRag.sites.find(s => s.id === siteId);
      if (!sourceSite) return { ok: false as const, error: 'Site not found' };
      const { applySiteStudyArea } = await import('../lib/siteStudyArea');
      const site = applySiteStudyArea(sourceSite);
      const scenario = site.promotedGenerationScenarioSnapshot;
      if (!scenario) {
        return { ok: false as const, error: 'Promote a generation scenario before syncing site-works quantities.' };
      }
      if ((scenario.input.studyAreaSelectionHash ?? null) !== (site.studyAreaScope?.selectionHash ?? null)) return { ok: false as const, error: 'The promoted layout uses a different study area. Review and promote a current scenario before syncing quantities.' };
      const anchor = scenario.input.layoutAnchor ?? site.siteCentroid ?? site.location;
      if (!anchor) return { ok: false as const, error: 'The site has no usable location anchor.' };
      const [
        { buildGenerationParcelTestFit },
        { computeGenerationLayoutTakeoffs },
        { deriveSiteWorksBomItems, mergeSiteWorksBomItems },
        { materializeSiteBoundaries },
      ] = await Promise.all([
        import('../lib/generationImpact/generationLayoutGeometry'),
        import('../lib/generationImpact/generationLayoutTakeoffs'),
        import('../lib/generationImpact/layoutSiteWorksBom'),
        import('../lib/siteBoundaryGeometry'),
      ]);
      const parcels = materializeSiteBoundaries({
        siteBoundaries: site.siteBoundaries,
        siteBoundary: site.siteBoundary,
        siteArea: site.siteArea,
        sitePerimeter: site.sitePerimeter,
        siteCentroid: site.siteCentroid,
      });
      const testFit = buildGenerationParcelTestFit({
        parcels,
        layout: scenario.input.layout,
        fallbackAnchor: anchor,
        fallbackEnvelopeAcres: site.siteBoundaryAggregate?.totalArea ?? site.siteArea,
        generationBlockCount: Math.max(
          1,
          scenario.input.units.reduce((sum, unit) => sum + unit.count, 0),
        ),
        footprint: scenario.result.footprint,
        installedReservations: scenario.input.projectDutyBasis?.installedReservations,
      });
      const takeoffs = computeGenerationLayoutTakeoffs(testFit);
      if (takeoffs.status !== 'available') {
        return {
          ok: false as const,
          error: takeoffs.reason ?? 'The promoted layout does not produce takeoff quantities.',
        };
      }
      const nowIso = new Date().toISOString();
      const derived = deriveSiteWorksBomItems(takeoffs, {
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        derivedAt: nowIso,
      });
      const priorPlan = site.supplyChainPlan;
      const merge = mergeSiteWorksBomItems(priorPlan?.bom ?? [], derived);
      let actor = 'anonymous';
      try {
        const { auth } = await import('../firebase');
        actor = auth.currentUser?.email ?? auth.currentUser?.uid ?? 'anonymous';
      } catch { /* anonymous — fine */ }
      // No seeded plan yet: bootstrap a minimal one so the layout link works
      // on fresh sites. NOTE: a later full BoM seed replaces auto-template
      // lines (these included) — re-run this sync after reseeding.
      const nextPlan: import('../types/supplyChain').SupplyChainPlan = priorPlan
        ? { ...priorPlan, bom: merge.bom, updatedAt: nowIso, updatedBy: actor }
        : {
            bom: merge.bom,
            suppliers: [],
            quotes: [],
            version: 1,
            generatedAt: nowIso,
            generatedBy: 'auto',
            updatedAt: nowIso,
            updatedBy: actor,
          };
      await get().updateSite(siteId, { supplyChainPlan: nextPlan });
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'scm.bom_siteworks_synced',
          target: { type: 'site', id: siteId, label: site.name },
          metadata: {
            scenarioId: scenario.id,
            added: merge.added,
            updated: merge.updated,
            removed: merge.removed,
          },
        });
      } catch { /* best-effort */ }
      return { ok: true as const, added: merge.added, updated: merge.updated, removed: merge.removed };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed to sync site-works from layout' };
    }
  },

  attachDeliveryReceipt: async ({ siteId, itemId, document, notes, lat, lng }) => {
    try {
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site?.supplyChainPlan) return { ok: false as const, error: 'Supply chain plan not initialized' };
      const { auth } = await import('../firebase');
      const actorUid = auth.currentUser?.uid ?? 'anonymous';
      const actorEmail = auth.currentUser?.email ?? actorUid;
      const { recordDeliveryReceipt } = await import('../services/supplyChain/deliveryReceipt');
      const res = recordDeliveryReceipt({
        plan: site.supplyChainPlan,
        bomItemId: itemId,
        document,
        actorUid,
        actorEmail,
        notes,
        lat,
        lng,
        siteId,
        inServiceDate: site.financialAssumptions?.inServiceDate ?? site.projectIntent?.inServiceDate ?? null,
      });
      await get().updateSite(siteId, { supplyChainPlan: res.plan });
      if (res.statusChangeEvent) {
        const { writeBomStatusChange } = await import('../services/supplyChain/bomStatusChangeLog');
        await writeBomStatusChange(res.statusChangeEvent, auth.currentUser?.uid ?? null);
      }
      return { ok: true as const };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed to attach delivery receipt' };
    }
  },

  applyRfqPipelineResult: async (siteId, result) => {
    try {
      if (result.status === 'rejected' || result.status === 'classified-only') return { ok: true as const };
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };
      const nextDocs = (site.documents ?? []).map(d => d.id === result.document.id ? result.document : d);
      await get().updateSite(siteId, {
        supplyChainPlan: result.plan,
        documents: nextDocs,
      });
      return { ok: true as const };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed to apply RFQ pipeline result' };
    }
  },

  awardOrRejectQuote: async (siteId, quoteId, action) => {
    try {
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site?.supplyChainPlan) return { ok: false as const, error: 'Supply chain plan not initialized' };
      const { clearReviewBadge } = await import('../services/supplyChain/processRfqResponse');
      const { auth } = await import('../firebase');
      const plan = site.supplyChainPlan;
      const quote = plan.quotes.find(q => q.id === quoteId);
      if (!quote) return { ok: false as const, error: `Quote ${quoteId} not found` };
      const quoteStatus: import('../types/supplyChain').QuoteStatus =
        action === 'award' ? 'awarded' : 'rejected';
      const nextQuotes = plan.quotes.map(q => q.id === quoteId ? { ...q, status: quoteStatus } : q);
      const nextBom: import('../types/supplyChain').BomItem[] = plan.bom.map(item => {
        if (item.id !== quote.bomItemId) return item;
        if (action !== 'award') return clearReviewBadge(item);
        return {
          ...clearReviewBadge(item),
          status: 'ordered' as import('../types/supplyChain').BomItemStatus,
          awardedSupplierId: quote.supplierId,
          unitCostUsd: quote.unitCostUsd,
          totalCostUsd: item.quantity * quote.unitCostUsd,
          priceSource: 'quote',
          leadTimeSource: 'quote',
          leadTimeMonthsLow: Math.max(0, quote.leadTimeMonths - 1),
          leadTimeMonthsHigh: quote.leadTimeMonths,
          priceProvenance: {
            ...(item.priceProvenance ?? {}),
            quoteId: quote.id,
          },
        };
      });
      const actor = auth.currentUser?.email ?? auth.currentUser?.uid ?? 'anonymous';
      const nextPlan = {
        ...plan,
        bom: nextBom,
        quotes: nextQuotes,
        updatedAt: new Date().toISOString(),
        updatedBy: actor,
      };
      await get().updateSite(siteId, { supplyChainPlan: nextPlan });
      return { ok: true as const };
    } catch (e) {
      return { ok: false as const, error: (e as Error)?.message ?? 'Failed quote decision' };
    }
  },

  pullFinancialFromSources: async (siteId) => {
    // Financial auto-populate (R-58) — fill native per-phase financial
    // values from REAL sources only (BOM rollup + project-intent). The
    // investment-grade HARD RULE is enforced by the pure aggregator
    // `buildFinancialPullFromSources`: unsourced fields stay null and a
    // user 'manual' field is never clobbered (only blanks are filled, and
    // only previously-'derived' fields are refreshed). This ADDS a pull
    // path; the legacy `syncCapexFromSupplyChainPlan` (campus-level) and
    // the extractor paths are untouched. Never throws.
    try {
      const site = get().siteRag.sites.find(s => s.id === siteId);
      if (!site) return { ok: false as const, error: `Site ${siteId} not found` };

      if (site.financialAssumptions?.businessModelKind === 'data-center-capacity-fee') return {
        ok: false as const, error: 'Generation/PPA auto-pull cannot replace annual data-center inputs. Review source-bound capacity-fee inputs in the financial form.',
      };
      const plan = site.supplyChainPlan ?? null;
      const intentPhases = site.projectIntent?.phases ?? null;
      const { contextClaimsEligibleForBinding } = await import('../lib/contextClaimEligibility');
      const contextClaims = contextClaimsEligibleForBinding(site);
      // Need at least ONE real source. BOM / intent phases anchor the
      // per-phase rollup; extracted context (CBE) complements the campus +
      // per-phase financial fields. With NONE of the three there is nothing
      // to attribute — refuse rather than no-op silently.
      const hasPlan = Boolean(plan && plan.bom.length > 0);
      const hasIntentPhases = Boolean(intentPhases && intentPhases.length > 0);
      const hasContextClaims = Boolean(contextClaims && contextClaims.length > 0);
      if (!hasPlan && !hasIntentPhases && !hasContextClaims) {
        return {
          ok: false as const,
          error: 'No real sources to pull from (need a priced BoM, project-intent phases, or tagged site context)',
        };
      }

      const {
        buildFinancialPullFromSources,
        applyIntentOfftakeGapFill,
      } = await import('../lib/syncCapexFromSupplyChain');
      const { writeFinancialAssumptionsChecked } = await import('../lib/financialWriteBoundary');
      const {
        FINANCIAL_MODEL_VERSION_CURRENT,
        emptyFinancialAssumptions,
        writeFinancialAssumptions,
        syncCampusRollupFromPhases,
      } = await import('../types/financialAssumptions');

      const { auth } = await import('../firebase');
      const actor = auth.currentUser?.email ?? auth.currentUser?.uid ?? 'anonymous';
      const now = new Date().toISOString();

      const base = site.financialAssumptions ?? emptyFinancialAssumptions(now, actor);
      const existingPhaseProvenance = base.phaseFieldProvenance ?? {};
      // When the site has NO prior FA, `emptyFinancialAssumptions` seeds a
      // synthetic single-phase placeholder (all-null). If real intent phases
      // exist we must NOT carry that placeholder into the aggregator — it
      // would survive as a spurious empty phase. Pass `[]` so the result is
      // built purely from intent. An existing user-authored FA keeps its
      // phases verbatim (the aggregator fills blanks / appends intent phases).
      // When NEITHER a prior FA NOR intent phases exist but context claims do,
      // seed the synthetic single placeholder phase so the per-phase scaffold
      // survives and the campus-level CBE fields (ppaPrice / itc / …) have an
      // FA to land on. With intent phases present, the placeholder is dropped
      // below (the intent phases replace it).
      const rawExistingPhases = site.financialAssumptions
        ? (base.phases ?? [])
        : (!hasIntentPhases && hasContextClaims ? (base.phases ?? []) : []);
      // Drop synthetic/empty placeholder phases (every per-phase quantity null) when
      // real intent phases are available to replace them — otherwise the all-null
      // placeholder `emptyFinancialAssumptions` seeds survives ALONGSIDE the intent
      // phases as a spurious empty phase.
      const isEmptyPhase = (ph: import('../types/financialAssumptions').FinancialPhase): boolean =>
        ph.capacityMW == null && ph.capexTotalUsd == null && ph.capexPerKwUsd == null &&
        ph.capacityFactorPct == null && ph.inServiceDate == null && ph.constructionMonths == null;
      const existingPhases = hasIntentPhases
        ? rawExistingPhases.filter(ph => !isEmptyPhase(ph))
        : rawExistingPhases;

      const pull = buildFinancialPullFromSources(
        { plan, intentPhases, existing: existingPhases },
        now,
        existingPhaseProvenance,
      );

      if (pull.phases.length === 0) {
        // No phases produced (e.g. empty existing + intent had no phases the
        // aggregator could align). Nothing safe to write.
        return {
          ok: false as const,
          error: 'No phases produced from the available sources',
        };
      }

      // Merge the fresh per-phase provenance over the prior map so manual
      // stamps + untouched derived stamps persist; newly-derived cells are
      // (re)stamped. The aggregator only emits entries for fields it actually
      // filled this run — it does NOT emit for protected manual fields — so a
      // shallow merge preserves the 'manual' guard.
      const mergedPhaseProvenance: Record<
        string,
        import('../types/financialAssumptions').FieldProvenance
      > = {
        ...existingPhaseProvenance,
        ...pull.fieldProvenance,
      };

      // Compose the FA payload: native phases (modelVersion 2), preserving
      // all non-phase fields (metadata, campus core, scenarios, commercials).
      // The write path (`writeFinancialAssumptions`) rolls campus totals from
      // the phases and re-stamps modelVersion 2, so the legacy flat readers
      // stay consistent with the per-phase truth.
      const stamped = writeFinancialAssumptions(siteId, {
        ...base,
        phases: pull.phases,
        modelVersion: FINANCIAL_MODEL_VERSION_CURRENT,
        phaseFieldProvenance: mergedPhaseProvenance,
        updatedAt: now,
        updatedBy: actor,
      });

      // SINGLE-PHASE CAMPUS MIRROR (rewritten 2026-08-19).
      //
      // For a lone phase, campus and phase describe the SAME plant, so a campus
      // quantity the pull could not source per-phase (no matching BoM line) is
      // still the right value for that phase. Mirror it — but ONLY when the
      // campus value carries real provenance, and stamp the phase cell with
      // THAT provenance so the chip never claims a source the value lacks.
      //
      // This used to be an after-the-fact GUARD over `syncSinglePhaseFromCampus`,
      // which back-filled every null phase field from the core indiscriminately;
      // the guard then nulled out the unsourced leaks. That back-fill is gone
      // (it made clearing a phase value impossible — see
      // `migrateFinancialAssumptionsRecord`), so the mirror is now explicit:
      // nothing lands on a phase unless we deliberately put it there.
      const PHASE_QTY_FIELDS: Array<
        'capacityMW' | 'capexTotalUsd' | 'capexPerKwUsd' | 'capacityFactorPct' | 'inServiceDate' | 'constructionMonths'
      > = ['capacityMW', 'capexTotalUsd', 'capexPerKwUsd', 'capacityFactorPct', 'inServiceDate', 'constructionMonths'];
      let enforced = stamped;
      if (enforced.phases && enforced.phases.length === 1) {
        const ph = { ...enforced.phases[0]! };
        const prov: Record<string, import('../types/financialAssumptions').FieldProvenance> = {
          ...(enforced.phaseFieldProvenance ?? {}),
        };
        let mirrored = false;
        for (const f of PHASE_QTY_FIELDS) {
          const key = `${ph.phaseId}:${f}`;
          // Only fill a genuinely empty cell the pull did not stamp itself.
          if (ph[f] != null || prov[key]) continue;
          // `f` is a key of FinancialAssumptionsCore, which FinancialAssumptions
          // extends — index the core shape rather than casting through Record.
          const campusValue = (base as import('../types/financialAssumptions').FinancialAssumptionsCore)[f];
          const campusProv = base.fieldProvenance?.[f];
          // UNSOURCED campus values are NOT mirrored: presenting one as a
          // per-phase figure would fabricate provenance the pull never had.
          if (campusValue == null || !campusProv) continue;
          (ph as Record<string, unknown>)[f] = campusValue;
          prov[key] = { ...campusProv };
          mirrored = true;
        }
        enforced = mirrored
          ? syncCampusRollupFromPhases({ ...enforced, phases: [ph], phaseFieldProvenance: prov })
          : { ...enforced, phases: [ph], phaseFieldProvenance: prov };
      }

      // ── INTENT OFFTAKE PRICE → FA PPA (audit F4 / plan 1.7) ─────────────
      // `ProjectIntent.offtake.pricePerMwhUsd` is DOCUMENTED as feeding
      // `financialAssumptions.ppaPricePerMwhUsd`, but nothing wrote it — so an
      // operator who declared the price still got a null year-one revenue, a
      // null NPV, and a WITHHELD financial verdict. Fill it here, alongside the
      // other intent-sourced fields, as a strict GAP-FILL: a `manual` /
      // baseline / extracted value is never overwritten. Runs BEFORE the CBE
      // pass so a document-extracted price that CONTRADICTS the declaration
      // surfaces as a conflict instead of silently replacing it.
      const offtakeFill = applyIntentOfftakeGapFill(
        enforced,
        site.projectIntent?.offtake ?? null,
        now,
      );
      enforced = offtakeFill.next;
      const intentOfftakeFilledCount = offtakeFill.filled ? 1 : 0;

      // ── THIRD SOURCE — Context Binding Engine (CBE phase 2a) ─────────────
      // After BOM (capex) + Intent (capacity / in-service date) have filled
      // what they OWN, layer the extracted-context fills on top as a pure
      // COMPLEMENT: gap-fill the ~28-32 net-new financial fields the CBE owns
      // (ppaPrice / escalator / term, itcPct, ptcPerMwhUsd, landCost, …), but
      // NEVER overwrite a BOM-owned capex or an Intent-owned capacity / date.
      // The engine's `mergeResolutions` sees the post-BOM+Intent values as
      // `'owner'` / `'manual'` field states, so a populated owner is protected
      // and a same-field DIVERGENCE surfaces as a conflict (not silently
      // picked). RANGE values never midpoint a scalar — they yield a hint.
      let contextFilledCount = 0;
      // A4 — surfaced on the result so the caller can report a CAPEX leak
      // instead of the pull reporting a clean success over a short model.
      let capexUnreconciledUsd = 0;
      let capexOrphanedLineCount = 0;
      let contextConflictCount = 0;
      let contextRangeHintCount = 0;
      try {
        const claims = contextClaims;
        if (claims && claims.length > 0) {
          const { pullContextFinancialFromClaims } = await import('../lib/contextFinancialFill');
          const ctx = pullContextFinancialFromClaims(
            enforced,
            claims,
            enforced.phaseFieldProvenance ?? {},
            now,
          );
          contextFilledCount = ctx.filledFields.length;
          contextConflictCount = ctx.conflicts.length;
          contextRangeHintCount = ctx.rangeHints.length;
          if (contextFilledCount > 0) {
            // Re-roll campus totals so any context-filled per-phase value flows
            // into the legacy flat readers; stamp the actor/timestamp again.
            enforced = syncCampusRollupFromPhases({
              ...ctx.financialAssumptions,
              updatedAt: now,
              updatedBy: actor,
            });
          }
        }
      } catch (err) {
        // Best-effort — context is a complement, never a hard dependency.
        // But never SILENT: a fill failure must be diagnosable (SCAR: the
        // swallowed-cause GCS-save incident).
        console.warn('[cbe] context fill failed', err);
      }

      // CAPEX CONSERVATION (audit A4 / 6.1). `checkCapexConservation` has
      // existed, tested, with NO caller — while the defect it detects is
      // precisely the one this pull can create: a priced BoM line whose
      // `phaseId` matches no phase, or a phase with no capacity, drops out of
      // the per-phase rollup and its cost silently disappears from the model.
      // CLAUDE.md's conservation rule says to flag that, not to let it pass.
      try {
        const { checkCapexConservation } = await import('../lib/syncCapexFromSupplyChain');
        const conservation = checkCapexConservation(plan, enforced.phases ?? []);
        if (!conservation.reconciles) {
          console.warn(
            `[fa-pull] CAPEX does not reconcile: $${Math.round(conservation.unreconciledUsd).toLocaleString()} `
            + `of priced BoM cost is not in the per-phase rollup `
            + `(orphaned $${Math.round(conservation.orphanedCostUsd).toLocaleString()}, `
            + `unpublishable-phase $${Math.round(conservation.unpublishablePhaseCostUsd).toLocaleString()}). `
            + conservation.warnings.join(' '),
          );
        }
        capexUnreconciledUsd = conservation.reconciles ? 0 : conservation.unreconciledUsd;
        capexOrphanedLineCount = conservation.orphanedItems.length;
      } catch (err) {
        console.warn('[fa-pull] CAPEX conservation check failed', err);
      }

      // COMMERCIAL TERMS (audit F1 / decision Q5). The campus sync used to be
      // the only route that refreshed `procurementCommercials`, so retiring it
      // in favour of this per-phase pull would have dropped the deposit /
      // lead-time snapshot the report's procurement sentence reads. It is
      // computed from the SAME plan and stamped with the PLAN's timestamp, so a
      // later plan edit is still detectable as staleness rather than the
      // snapshot being carried forward forever by `{...fa}`.
      try {
        const { computeCommercialTermsSyncFromBom } = await import('../lib/syncCapexFromSupplyChain');
        const commercial = computeCommercialTermsSyncFromBom(plan, enforced.capexTotalUsd);
        if (commercial) {
          enforced = {
            ...enforced,
            procurementCommercials: {
              ...commercial,
              // The BoM the snapshot DESCRIBES, not the moment it was written.
              updatedAt: plan?.updatedAt ?? now,
            },
          };
        }
      } catch (err) {
        // Best-effort, never silent — the CAPEX pull must not fail because a
        // deposit summary could not be derived.
        console.warn('[fa-pull] commercial-terms sync failed', err);
      }

      // FA write boundary (audit 1.1 / 1.2b+c) — one last rollup + provenance
      // reconciliation so no campus chip outlives its value and a superseded
      // `procurementCommercials` snapshot cannot ride along.
      const persisted = writeFinancialAssumptionsChecked(siteId, enforced, now, {
        planUpdatedAt: plan?.updatedAt ?? null,
      });
      await get().updateSite(siteId, { financialAssumptions: persisted });
      void get().restampFinancialReportsForSite(siteId); // D9

      // Audit breadcrumb — reuse the canonical FA-update action with
      // pull-specific metadata so the Activity tab can distinguish an
      // auto-pull from a manual save. Best-effort.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'site.financial_assumptions_update',
          target: { type: 'site', id: siteId, label: site.name },
          before: { hadFinancialAssumptions: Boolean(site.financialAssumptions) },
          after: {
            hadFinancialAssumptions: true,
            source: stamped.source,
            modelVersion: stamped.modelVersion,
          },
          metadata: {
            trigger: 'financial-auto-pull',
            phaseCount: pull.phases.length,
            filledFieldCount: pull.filledFields.length + intentOfftakeFilledCount,
            skippedUnsourcedCount: pull.skippedUnsourced.length,
            filledFromBomRollup: pull.filledFields.filter(f => f.source === 'bom-rollup').length,
            filledFromProjectIntent:
              pull.filledFields.filter(f => f.source === 'project-intent').length
              + intentOfftakeFilledCount,
            // F4 / 1.7 — intent-declared offtake price → FA PPA price.
            filledFromIntentOfftake: intentOfftakeFilledCount,
            // CBE third source (extracted context).
            filledFromContext: contextFilledCount,
            contextConflictCount,
            contextRangeHintCount,
          },
        });
      } catch { /* best-effort */ }

      return {
        ok: true as const,
        phaseCount: pull.phases.length,
        // The intent-declared PPA price is an intent-sourced fill like any
        // other, so it counts toward the "Filled N fields from BOM + intent"
        // summary the form reports back to the user.
        filledFieldCount: pull.filledFields.length + intentOfftakeFilledCount,
        skippedUnsourcedCount: pull.skippedUnsourced.length,
        contextFilledCount,
        contextConflictCount,
        contextRangeHintCount,
        // A4 — priced BoM cost that did NOT reach the per-phase rollup. A
        // non-zero value means the model is SHORT by this much and the pull
        // must not read as an unqualified success.
        capexUnreconciledUsd,
        capexOrphanedLineCount,
      };
    } catch (e) {
      return {
        ok: false as const,
        error: (e as Error)?.message ?? 'Failed to pull financial values from sources',
      };
    }
  },

  updateSiteDocuments: async (siteId, documents, expectedDocumentsBasis) => {
    // Publish locally only after the canonical write succeeds. A failed write
    // must not make the library or its callers announce an unsaved edit.
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in before saving site documents.');
    await get().assertSiteWritable(siteId);
    const before = get().siteRag.sites.find(site => site.id === siteId);
    if (!before) throw new Error('The document site is no longer available.');
    if (before.demonstrationProvenance) throw new Error('Demonstration documents require owner-scoped server intake.');
    const ownerUid = siteOwnerUid(before, user.uid);
    const beforeBasis = siteDocumentsBasis(before.documents);
    if (expectedDocumentsBasis !== undefined && beforeBasis !== expectedDocumentsBasis) {
      throw new Error('The document library changed before saving. Reload before replacing documents.');
    }
    const savedBasis = siteDocumentsBasis(documents);
    const now = new Date().toISOString();
    const { doc, runTransaction } = await import('firebase/firestore');
    if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before saving.');
    const reference = doc(db, siteOwnerSitesPath(before, user.uid), siteId);
    const writtenAt = await runTransaction(db, async transaction => {
      const snapshot = await transaction.get(reference);
      if (!snapshot.exists()) throw new Error('The document site is no longer available.');
      const canonical = snapshot.data() as import('../types/site').SiteEntity;
      if (canonical.demonstrationProvenance) throw new Error('Demonstration documents require owner-scoped server intake.');
      if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before saving.');
      if (siteDocumentsBasis(canonical.documents) !== beforeBasis) {
        throw new Error('The document library changed before saving. Reload before replacing documents.');
      }
      const updatedAt = canonical.updatedAt > now ? canonical.updatedAt : now;
      transaction.update(reference, stripUndefined({ documents: documentsForSiteRecord(documents), updatedAt }));
      return updatedAt;
    });
    const current = get().siteRag.sites.find(site => site.id === siteId);
    if (auth.currentUser?.uid !== user.uid || !current || siteOwnerUid(current, user.uid) !== ownerUid
      || ![beforeBasis, savedBasis].includes(siteDocumentsBasis(current.documents))) {
      throw new Error('The document write completed, but the library changed while saving. Reload to review the saved version.');
    }
    const isLibIndex = (document: import('../types/siteRag').SiteDocument) => (document.tags ?? []).includes('library-index');
    const priorUserDocCount = (before.documents ?? []).filter(document => !isLibIndex(document)).length;
    set(state => ({ siteRag: { ...state.siteRag,
      sites: state.siteRag.sites.map(site => site.id === siteId
        ? { ...site, documents, updatedAt: site.updatedAt > writtenAt ? site.updatedAt : writtenAt } : site),
    } }));
    void get().recomputeEvidenceReadiness(siteId);
    if (documents.filter(document => !isLibIndex(document)).length > priorUserDocCount) {
      void get().maybeFlipEvidenceModeOnDocUpload(siteId);
    }
  },

  readLiveSedHead: async (siteId) => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return null;
    // From the SERVER: the local cache is exactly what was stale in the
    // incident this guards against. Fields are read one by one so the head
    // never passes through a raw `.data() as SiteEntity` cast.
    const { doc, getDocFromServer } = await import('firebase/firestore');
    const snapshot = await getDocFromServer(doc(db, siteOwnerSitesPath(site, user.uid), siteId));
    if (!snapshot.exists()) return null;
    const read = (field: string): unknown => snapshot.get(field);
    return {
      pointer: sedPointerFromGetter(read),
      buildInProgress: sedBuildInProgress(sedBuildFieldsFromGetter(read), Date.now()),
    };
  },

  commitSedPointerIfUnchanged: async (siteId, expected, patch, opts = {}) => {
    // Same gate order as updateSite: the edit-lock first, then the user.
    await get().assertSiteWritable(siteId);
    const { auth } = await import('../firebase');
    const initiatingUser = auth.currentUser;
    if (!initiatingUser) throw new Error('Not signed in');
    const initiatingUid = initiatingUser.uid;
    const before = get().siteRag.sites.find(s => s.id === siteId);
    if (!before) return { committed: false, reason: 'site-missing' };
    const ownerUid = siteOwnerUid(before, initiatingUid);
    const beforeLocalState = {
      updatedAt: before.updatedAt,
      sedStatus: before.sedStatus,
      sedBootstrapLockStatus: before.sedBootstrapLockStatus,
      sedBootstrapLockedAt: before.sedBootstrapLockedAt,
      sedAmendmentHistoryVersion:
        typeof before.sedAmendmentHistoryVersion === 'number'
          ? before.sedAmendmentHistoryVersion
          : 0,
    };
    const now = new Date().toISOString();
    const { resolveMemberName } = await import('../lib/siteCompany');
    const { authedFetch } = await import('../lib/authedFetch');
    if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
      throw new Error('The signed-in account changed during the SED pointer update.');
    }
    let preboundAuthHeader: string | undefined;
    if (typeof (initiatingUser as { getIdToken?: unknown }).getIdToken === 'function') {
      const tok = await (initiatingUser as { getIdToken: () => Promise<string> }).getIdToken();
      if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
        throw new Error('The signed-in account changed during the SED pointer update.');
      }
      if (tok) preboundAuthHeader = `Bearer ${tok}`;
    }
    const lastEditedBy: import('../types/site').SiteEntity['lastEditedBy'] = {
      uid: initiatingUid,
      name: resolveMemberName(initiatingUid, get().companyMembers, initiatingUser.email ?? undefined)
        ?? initiatingUser.displayName ?? initiatingUser.email ?? undefined,
      at: now,
    };
    const sanitizedPatch = stripUndefined(
      stripDocumentBodiesFromSitePatch(stripTransientSiteAnnotations({ ...patch })),
    );
    const sanitizedStatusPatch = opts.statusPatch
      ? stripUndefined(
          stripDocumentBodiesFromSitePatch(
            stripTransientSiteAnnotations({ ...opts.statusPatch }),
          ),
        )
      : undefined;
    const explicitCandidateOperationId =
      typeof opts.candidateOperationId === 'string' && opts.candidateOperationId.trim()
        ? opts.candidateOperationId.trim()
        : undefined;
    const isStatusOnlyCas =
      Object.keys(sanitizedPatch).length === 0 && !explicitCandidateOperationId;
    const statusEnvelopeKey = isStatusOnlyCas
      ? JSON.stringify([
          initiatingUid,
          ownerUid,
          siteId,
          expected,
          sanitizedStatusPatch ?? {},
          Boolean(opts.requireIdle),
        ])
      : null;
    const operationId =
      typeof opts.operationId === 'string' && opts.operationId.trim()
        ? opts.operationId.trim()
        : explicitCandidateOperationId
          ? `pub-${explicitCandidateOperationId}`
          : (statusEnvelopeKey ? pendingStatusCasOperations.get(statusEnvelopeKey) : undefined) ??
            `cas-${siteId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
              /[^A-Za-z0-9._:-]/g,
              '-',
            );
    if (statusEnvelopeKey && !(typeof opts.operationId === 'string' && opts.operationId.trim())) {
      pendingStatusCasOperations.set(statusEnvelopeKey, operationId);
    }
    const clearStatusEnvelopeIfTerminal = (status?: number) => {
      if (!statusEnvelopeKey) return;
      if (status === undefined || (status >= 400 && status < 500)) {
        pendingStatusCasOperations.delete(statusEnvelopeKey);
      }
    };
    if (!auth.currentUser || auth.currentUser.uid !== initiatingUid) {
      if (statusEnvelopeKey) pendingStatusCasOperations.delete(statusEnvelopeKey);
      throw new Error('The signed-in account changed during the SED pointer update.');
    }
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/sed-pointer-cas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(preboundAuthHeader ? { Authorization: preboundAuthHeader } : {}),
      },
      body: JSON.stringify({
        ownerUid,
        expectedActorUid: initiatingUid,
        operationId,
        ...(explicitCandidateOperationId
          ? { candidateOperationId: explicitCandidateOperationId }
          : {}),
        expected,
        patch: sanitizedPatch,
        options: {
          ...(sanitizedStatusPatch ? { statusPatch: sanitizedStatusPatch } : {}),
          ...(opts.requireIdle !== undefined ? { requireIdle: opts.requireIdle } : {}),
        },
      }),
    });
    if (!res.ok) {
      clearStatusEnvelopeIfTerminal(res.status);
      if (res.status === 404) {
        return { committed: false, reason: 'site-missing' };
      }
      const errText = await res.text().catch(() => '');
      if (res.status === 409 && errText.includes('signed-in account changed')) {
        throw new Error('The signed-in account changed during the SED pointer update.');
      }
      throw new Error(
        `SED pointer CAS failed (HTTP ${res.status})${errText ? `: ${errText.slice(0, 200)}` : ''}`,
      );
    }
    let body: Record<string, unknown> | null = null;
    try {
      const rawText = typeof res.text === 'function' ? await res.text() : '';
      body = rawText
        ? (JSON.parse(rawText) as Record<string, unknown>)
        : typeof res.json === 'function'
          ? ((await res.json()) as Record<string, unknown>)
          : null;
    } catch {
      try {
        body =
          typeof res.json === 'function'
            ? ((await res.json()) as Record<string, unknown>)
            : null;
      } catch {
        body = null;
      }
    }
    if (!body || typeof body.committed !== 'boolean') {
      throw new Error('Malformed SED pointer CAS acknowledgement.');
    }
    if (body.committed === false) {
      clearStatusEnvelopeIfTerminal();
      const reason =
        body.reason === 'build-in-progress' || body.reason === 'site-missing'
          ? body.reason
          : 'pointer-moved';
      return { committed: false, reason };
    }
    // Validate common identity fields on committed:true
    if (
      body.operationId !== operationId ||
      body.ownerUid !== ownerUid ||
      body.siteId !== siteId ||
      typeof body.updatedAt !== 'string' ||
      !body.updatedAt.trim() ||
      typeof body.statusWritten !== 'boolean'
    ) {
      throw new Error('Malformed SED pointer CAS acknowledgement: identity mismatch.');
    }
    const expectedCandidateOpId = explicitCandidateOperationId ?? operationId;
    if (!isStatusOnlyCas) {
      if (
        body.candidateOperationId !== expectedCandidateOpId ||
        typeof body.sedId !== 'string' ||
        !body.sedId.trim() ||
        (typeof sanitizedPatch.sedId === 'string' && body.sedId !== sanitizedPatch.sedId) ||
        typeof body.sedVersion !== 'number' ||
        !Number.isFinite(body.sedVersion) ||
        body.sedVersion < 1 ||
        typeof body.sedStoragePath !== 'string' ||
        !body.sedStoragePath.trim() ||
        typeof body.sedStorageGeneration !== 'string' ||
        !/^[1-9][0-9]*$/.test(body.sedStorageGeneration.trim()) ||
        typeof body.sedContentHash !== 'string' ||
        !body.sedContentHash.trim() ||
        typeof body.sedContentHashVersion !== 'number' ||
        body.sedContentHashVersion !== body.sedVersion ||
        typeof body.historyVersion !== 'number' ||
        !Number.isFinite(body.historyVersion) ||
        body.historyVersion < 0 ||
        !Array.isArray(body.sedAmendmentHistory)
      ) {
        throw new Error(
          'Malformed SED candidate publication acknowledgement: missing authoritative pointer fields.',
        );
      }
    } else if (body.candidateOperationId !== undefined) {
      throw new Error('Malformed status-only SED pointer CAS acknowledgement.');
    }
    clearStatusEnvelopeIfTerminal();

    const statusWritten = body.statusWritten === true;
    const serverPointerFields: Record<string, unknown> = {};
    if (!isStatusOnlyCas) {
      serverPointerFields.sedId = body.sedId;
      serverPointerFields.sedVersion = body.sedVersion;
      serverPointerFields.sedStoragePath = body.sedStoragePath;
      serverPointerFields.sedStorageGeneration = body.sedStorageGeneration;
      serverPointerFields.sedContentHash = body.sedContentHash;
      serverPointerFields.sedContentHashVersion = body.sedContentHashVersion;
      if (typeof body.sedCollectedAt === 'string') {
        serverPointerFields.sedCollectedAt = body.sedCollectedAt;
      } else if (typeof sanitizedPatch.sedCollectedAt === 'string') {
        serverPointerFields.sedCollectedAt = sanitizedPatch.sedCollectedAt;
      }
      serverPointerFields.sedAmendmentHistoryVersion = body.historyVersion;
      serverPointerFields.sedAmendmentHistory = body.sedAmendmentHistory;
      if (typeof body.lastKnownAmendmentsSourceSedId === 'string') {
        serverPointerFields.lastKnownAmendmentsSourceSedId = body.lastKnownAmendmentsSourceSedId;
      }
      for (const [k, v] of Object.entries(body)) {
        if (k.startsWith('sedSectionHash_') && typeof v === 'string') {
          serverPointerFields[k] = v;
        }
      }
    }
    const written = stripDocumentBodiesFromSitePatch(
      stripTransientSiteAnnotations({
        ...(statusWritten ? (sanitizedStatusPatch ?? {}) : {}),
        ...serverPointerFields,
      }),
    );
    const updatedAt =
      typeof body.updatedAt === 'string' && body.updatedAt > now ? body.updatedAt : now;
    const local: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(written)) {
      if (value !== undefined) local[key] = isFieldValueSentinel(value) ? null : value;
    }
    const savedPointer = sedPointerOf({ ...expected, ...written });
    let appliedLocally = false;
    set(state => ({
      siteRag: {
        ...state.siteRag,
        // A listener or another operation may have delivered a newer head,
        // build state, or amendment history while this acknowledgement was in
        // flight. Preserve it, including same-pointer updates that lack a
        // newer timestamp.
        sites: state.siteRag.sites.map(s => {
          const currentLocalHistoryVersion =
            typeof s.sedAmendmentHistoryVersion === 'number'
              ? s.sedAmendmentHistoryVersion
              : 0;
          const historyAdvancedLocally =
            currentLocalHistoryVersion > beforeLocalState.sedAmendmentHistoryVersion ||
            (typeof body.historyVersion === 'number' &&
              currentLocalHistoryVersion > body.historyVersion);
          if (
            s.id !== siteId ||
            auth.currentUser?.uid !== initiatingUid ||
            siteOwnerUid(s, initiatingUid) !== ownerUid ||
            s.updatedAt > beforeLocalState.updatedAt ||
            s.sedStatus !== beforeLocalState.sedStatus ||
            s.sedBootstrapLockStatus !== beforeLocalState.sedBootstrapLockStatus ||
            s.sedBootstrapLockedAt !== beforeLocalState.sedBootstrapLockedAt ||
            historyAdvancedLocally ||
            (!sameSedPointer(sedPointerOf(s), expected) &&
              !sameSedPointer(sedPointerOf(s), savedPointer))
          ) {
            return s;
          }
          appliedLocally = true;
          return {
            ...s,
            ...local,
            lastEditedBy,
            updatedAt: s.updatedAt > updatedAt ? s.updatedAt : updatedAt,
          };
        }),
      },
    }));
    // Same readiness triggers as updateSite.
    const readinessTriggers = [
      'sedStatus', 'sedVersion', 'sedCompleteness', 'sedId',
      'projectIntent', 'siteBoundary', 'siteBoundaries', 'studyArea', 'siteContext',
    ];
    if (appliedLocally && !get().isInitialMigration && readinessTriggers.some(key => key in local)) {
      void get().recomputeEvidenceReadiness(siteId);
    }
    return {
      committed: true,
      statusWritten,
      ...(typeof written.sedId === 'string' ? { sedId: written.sedId } : {}),
      ...(typeof written.sedVersion === 'number' ? { sedVersion: written.sedVersion } : {}),
      ...(typeof written.sedStoragePath === 'string' ? { sedStoragePath: written.sedStoragePath } : {}),
      ...(typeof (written as { sedStorageGeneration?: unknown }).sedStorageGeneration === 'string'
        ? { sedStorageGeneration: (written as { sedStorageGeneration: string }).sedStorageGeneration }
        : {}),
      ...(typeof written.sedContentHash === 'string' ? { sedContentHash: written.sedContentHash } : {}),
      ...(typeof written.sedContentHashVersion === 'number'
        ? { sedContentHashVersion: written.sedContentHashVersion }
        : {}),
      ...(typeof written.sedCollectedAt === 'string' ? { sedCollectedAt: written.sedCollectedAt } : {}),
      ...(typeof body.historyVersion === 'number' ? { historyVersion: body.historyVersion } : {}),
      updatedAt,
    };
  },

  setSiteEvidenceMode: async (siteId, mode) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    if (site.evidenceMode === mode && site.evidenceModeSetAt) return; // idempotent no-op
    const priorMode = site.evidenceMode;
    await get().updateSite(siteId, { evidenceMode: mode, evidenceModeSetAt: new Date().toISOString() });
    // ES3 — an 'ai-only' site treats 0% doc coverage as intentional in the
    // readiness score (doc checklist weight excluded); UI surfaces still
    // suppress the "unclassified docs" note separately.
    void get().recomputeEvidenceReadiness(siteId);

    const userDocCount = (site.documents ?? []).filter(
      d => !(d.tags ?? []).includes('library-index'),
    ).length;
    if (userDocCount === 0 && mode !== 'docs-included') return;

    const { toast } = await import('sonner');
    if (mode === 'ai-only') {
      toast.info(
        'User documents are stored but excluded from evidence. Rebuild the SED to remove doc links from an existing SED.',
        {
          duration: 12_000,
          action: {
            label: 'Rebuild SED',
            onClick: () => { void get().rebuildSiteSed(siteId); },
          },
        },
      );
    } else if (mode === 'docs-included' && priorMode !== 'docs-included') {
      toast.info(
        'Documents will now be used in evidence. Rebuild the SED to incorporate them.',
        {
          duration: 12_000,
          action: {
            label: 'Rebuild SED',
            onClick: () => { void get().rebuildSiteSed(siteId); },
          },
        },
      );
    }
  },

  maybeFlipEvidenceModeOnDocUpload: async (siteId) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) return;
    const { nextEvidenceModeAfterDocChange } = await import('../services/knowledge/documentUpload');
    const userDocCount = (site.documents ?? []).filter(d => !(d.tags ?? []).includes('library-index')).length;
    const next = nextEvidenceModeAfterDocChange(site.evidenceMode, userDocCount);
    if (!next || next === site.evidenceMode) return;
    await get().setSiteEvidenceMode(siteId, next);
  },

  addSiteDocument: async (siteId, file, opts) => {
    const siteBefore = get().siteRag.sites.find(s => s.id === siteId);
    // DAE4 — pipeline-skip only on an EXPLICIT 'ai-only' choice (mirrors
    // DocumentLibrary.handleUpload). A legacy undefined-mode site
    // auto-promotes to 'docs-included' on this upload, so its docs must
    // run the evidence pipeline.
    const aiOnlyUpload = siteBefore?.evidenceMode === 'ai-only';
    const { processUploadedFile, saveDocument } = await import('../services/knowledge/documentUpload');
    const baseDoc = await processUploadedFile(file, {
      siteId,
      skipEvidencePipeline: aiOnlyUpload,
      intendedRequirementIds: opts?.intendedRequirementIds,
      intentSource: opts?.intentSource,
      intentSetBy: opts?.intentSetBy,
    });
    const mergedTags = Array.from(
      new Set([...(baseDoc.tags ?? []), ...(opts?.appendTags ?? [])]),
    );
    const mergedLinkedBomItemIds = Array.from(
      new Set([...(baseDoc.linkedBomItemIds ?? []), ...(opts?.linkedBomItemIds ?? [])]),
    );
    const doc = {
      ...baseDoc,
      tags: mergedTags,
      ...(mergedLinkedBomItemIds.length > 0 ? { linkedBomItemIds: mergedLinkedBomItemIds } : {}),
      ...(opts?.folder ? { folder: opts.folder, movedAt: Date.now() } : {}),
    };
    // Best-effort persistence to GCS + the user-level documents collection. If
    // it fails the doc still lands in the site array — `users/{uid}/documents`
    // is a flat index that the report-generation prompt builder reads, so we
    // don't want a network blip to drop the file from the user's UI.
    // A non-fatal raw-file failure (oversized body / upload blip) still
    // persists the doc + its extracted text; log the structured warning so
    // it isn't silently dropped. (UI toasts for this path live at the
    // component callers — the store stays UI-framework-agnostic.)
    // #89 — pass the owning `siteId` so the DOCUMENT write is gated by
    // that site's edit-lock (assertSiteWritable). On a locked site a
    // non-holder upload throws SiteLockedError, which the `.catch` below
    // logs (the action stays UI-framework-agnostic; component callers
    // surface the toast).
    const saveResult = await saveDocument(doc, file, siteId).catch((e) => {
      console.warn('saveDocument failed:', e);
      return undefined;
    });
    if (saveResult?.rawUploadWarning) {
      console.warn('[addSiteDocument] raw-file persistence warning:', saveResult.rawUploadWarning.message);
    }
    const now = new Date().toISOString();
    // PH-A2.7 — capture the new site snapshot inside the set-state-builder
    // so the persistence call uses the exact array we just wrote, not
    // whatever a concurrent setSiteDocuments / removeSiteDocument call
    // could land between our set() and a subsequent getState() read.
    let nextSiteForPersist: import('../types/site').SiteEntity | undefined;
    set((state) => {
      const sites = state.siteRag.sites.map(s => {
        if (s.id !== siteId) return s;
        const nextDocs = [...(s.documents ?? []), doc];
        const nextSite = { ...s, documents: nextDocs, documentIds: [...new Set([...(s.documentIds ?? []), doc.id])], updatedAt: now };
        nextSiteForPersist = nextSite;
        return nextSite;
      });
      return { siteRag: { ...state.siteRag, sites } };
    });
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return doc;
      const { doc: firestoreDoc, updateDoc } = await import('firebase/firestore');
      if (!nextSiteForPersist) return doc;
      // #owner-path-sweep — write to the path that actually holds the doc
      // (nextSiteForPersist is a spread of the in-state site, so it carries
      // the transient `_ownerUid` for shared sites).
      await updateDoc(firestoreDoc(db, siteOwnerSitesPath(nextSiteForPersist, user.uid), siteId), stripUndefined({
        documents: documentsForSiteRecord(nextSiteForPersist.documents ?? []),
        documentIds: nextSiteForPersist.documentIds ?? [],
        updatedAt: now,
      }));
    } catch (e) {
      console.warn('Failed to persist new site document:', e);
    }

    // SCM risk loop — optional RFQ-response ingestion. This augments the
    // strategic BoM risk signal (quote-available review markers) when the
    // uploaded document looks like a vendor RFQ response.
    try {
      const latestSite = get().siteRag.sites.find(s => s.id === siteId);
      const { maybeProcessUploadedFileAsRfqResponse } = await import('../services/knowledge/documentUpload');
      const rfqResult = await maybeProcessUploadedFileAsRfqResponse({
        document: doc,
        supplyChainPlan: latestSite?.supplyChainPlan,
      });
      if (rfqResult.status !== 'skipped' && rfqResult.status !== 'bom-candidate') {
        const applied = await get().applyRfqPipelineResult(siteId, rfqResult);
        if (!applied.ok) {
          console.warn('[SCM12] Failed applying RFQ pipeline result:', applied.error);
        }
      }
    } catch (e) {
      console.warn('[SCM12] RFQ loop failed:', (e as Error)?.message ?? 'unknown error');
    }

    // PL3 — run the SC3 claim extractor over the doc's extracted text
    // and merge into the site's SiteContext with `source:
    // 'document-extracted'` so the panel surfaces "from {fileName}"
    // chips. Mirrors the prose-save path (setSiteContext) but writes
    // through a separate merger that preserves user-typed claims on
    // collision. Best-effort: a Gemini outage / parse failure leaves
    // the doc saved without claim contribution — the user can still
    // manually type claims in the Site Context panel.
    // ES3 — skipped in ai-only mode: docs are stored but inactive.
    if (!aiOnlyUpload && doc.textContent && doc.textContent.length >= 200) {
      try {
        const { extractClaimsWithRetry } = await import('../services/siteContext/extractClaims');
        const { mergeClaimsIntoSiteContext } = await import('../services/siteContext/mergeClaimsIntoSiteContext');
        const { emptySiteContext } = await import('../types/siteContext');
        const site = useStore.getState().siteRag.sites.find(s => s.id === siteId);
        if (site) {
          // EX-5 — PL3 runs at doc-upload time, BEFORE a report/SED may
          // exist for the site. Load the latest report's SED when one is
          // present; otherwise pass null (the extractor's cross-field
          // rules early-return on a null SED, the type/range/unit/enum
          // rules still apply). This is the doc-textContent → claim path.
          let sed: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
          const latestReportForSed = useStore.getState().siteRag.reports
            .filter(r => r.siteId === site.id)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          if (latestReportForSed?.sedId) {
            try {
              const { loadSedById } = await import('../services/sedLoader');
              sed = await loadSedById(latestReportForSed.sedId, undefined);
            } catch { /* SED load is best-effort; pass null */ }
          }
          let siteStateCode: string | null = null;
          if (sed) {
            try {
              const { inferSiteStateFromSed } = await import('../lib/inferSiteState');
              siteStateCode = inferSiteStateFromSed(sed);
            } catch { /* state inference is best-effort */ }
          }
          // EX-4 — extractClaimsWithRetry (drop-in). Widen the destructure
          // to also capture status + rejected (pre-EX-4 PL3 dropped them).
          const { claims, extractorVersion, status, rejected } = await extractClaimsWithRetry(doc.textContent, {
            siteName: site.name,
            sed,
            siteStateCode,
          });
          if (status !== 'success' && (rejected?.length ?? 0) > 0) {
            console.warn(
              `[PL3] doc-extract for ${siteId}: status=${status}, ${rejected?.length ?? 0} schema-rejected claim(s)`,
            );
          }
          if (claims.length > 0) {
            const priorContext = site.siteContext ?? emptySiteContext();
            const merged = mergeClaimsIntoSiteContext(priorContext, claims, {
              sourceDocId: doc.id,
              provenance: 'document-extracted',
            });
            const nextContext = {
              ...priorContext,
              claims: merged.claims,
              extractorVersion,
              updatedAt: now,
            };
            const ts = now;
            set((state) => ({
              siteRag: {
                ...state.siteRag,
                sites: state.siteRag.sites.map(s =>
                  s.id === siteId ? { ...s, siteContext: nextContext, updatedAt: ts } : s,
                ),
              },
            }));
            try {
              const { auth, db } = await import('../firebase');
              const user = auth.currentUser;
              if (user) {
                const { doc: firestoreDoc, updateDoc } = await import('firebase/firestore');
                // #owner-path-sweep — write to the path that actually holds the doc.
                await updateDoc(
                  firestoreDoc(db, ownerSitesPath(get(), siteId, user.uid), siteId),
                  stripUndefined({ siteContext: nextContext, updatedAt: ts }),
                );
              }
            } catch (e) {
              console.warn('[PL3] persist doc-extracted siteContext failed:', (e as Error)?.message);
            }
            // CBE caller 4 — context→intent auto-fill from the doc-extracted
            // claims (gap-fill only, USER WINS — see contextIntentFill.ts).
            // Best-effort: a failure never blocks the upload.
            try {
              const { pullContextIntentFromClaims } = await import('../lib/contextIntentFill');
              const liveSite = get().siteRag.sites.find(x => x.id === siteId);
              const pulled = pullContextIntentFromClaims(liveSite?.projectIntent, nextContext.claims);
              if (pulled.filledFields.length > 0) {
                await get().updateSiteProjectIntent(siteId, pulled.projectIntent);
              }
            } catch (e) {
              console.warn('[cbe-intent] doc-upload intent auto-fill failed:', (e as Error)?.message);
            }
          }
        }
      } catch (e) {
        console.warn('[PL3] doc-extracted claim merge failed:', (e as Error)?.message);
      }
    }
    // (Library-index refresh is not triggered from this action — direct
    //  callers outside DocumentLibrary don't exist, and the test contract
    //  for this action is "verbatim Firestore write, no side effects".
    //  DocumentLibrary fires the refresh from its own commit() path.)
    return doc;
  },

  removeSiteDocument: async (siteId, docId) => {
    const now = new Date().toISOString();
    // PH-A2.7 — same race fix as addSiteDocument. Capture the post-mutation
    // site snapshot inside the set-state-builder rather than re-reading
    // state.sites between the local mutation and the Firestore write.
    //
    // DDL-1 + DDL-FOLLOWUP-RESTORE — cross-array SOFT delete. The Documents
    // tab renders a UNION of
    //   site.documents ∪ latestReport.documents ∪ every report.documents
    // (SiteLandingPage.tsx:679). Pre-DDL-FOLLOWUP this action HARD-filtered
    // the doc out of every array (data-loss footgun: misclick was
    // unrecoverable). The new contract STAMPS `deletedAt` (ISO) + `deletedBy`
    // (uid) on the doc record in place across the same cross-array sweep so
    // the SiteLandingPage union dedupe can't reapply a stale live copy from
    // a report on the next render. The Documents tab filters
    // `isLiveDoc(d)` (lib/documentSoftDelete.ts) so soft-deleted rows are
    // hidden by default; the admin GhostDocsPanel surfaces them with a
    // Restore action that nulls the fields again.
    //
    // Reproducibility invariant unchanged: `siteContextAtGen`,
    // `projectIntentAtGen`, and `typedPreferencesAtGen` are NOT touched —
    // those are historical snapshots.
    //
    // Note: `documentIds` array is LEFT alone (the doc still exists, just
    // tombstoned) so the canonical site → doc edge survives the restore
    // round-trip.
    //
    // The DLB-3 separate re-ingest tombstone in
    // `users/{uid}/deletedDocuments/{docId}` is still written here because
    // the user's intent ("don't accidentally re-upload the same canonical
    // doc") survives the soft / hard boundary. Upload-time checks key on
    // that collection, not on the in-place flag.
    let nextSiteForPersist: import('../types/site').SiteEntity | undefined;
    // DLB-4 — capture the doc's display name BEFORE we mutate. With the
    // soft-delete model the doc still lives in state, but reading the
    // fileName here keeps the tombstone-write payload below cleanly
    // pre-mutation.
    let originalFileName: string | undefined;
    // Track whether the doc was actually found + not-already-soft-deleted.
    // No-op skips persistence / audit emission so retries are idempotent.
    let mutated = false;
    let actorUid: string | undefined;
    try {
      const { auth } = await import('../firebase');
      actorUid = auth.currentUser?.uid;
    } catch {
      actorUid = undefined;
    }
    let reportPatchesForPersist: Array<{
      reportId: string;
      documents: import('../types/siteRag').SiteDocument[];
    }> = [];
    set((state) => {
      const sites = state.siteRag.sites.map(s => {
        if (s.id !== siteId) return s;
        const docs = s.documents ?? [];
        const target = docs.find(d => d.id === docId);
        if (!target) return s;
        // No-op if already soft-deleted — defensive against duplicate-click
        // races so we don't churn the timestamp / actor on every retry.
        if (target.deletedAt) return s;
        originalFileName = target.fileName;
        const nextDocs = docs.map(d =>
          d.id === docId
            ? { ...d, deletedAt: now, deletedBy: actorUid ?? null }
            : d,
        );
        mutated = true;
        const nextSite = {
          ...s,
          documents: nextDocs,
          updatedAt: now,
        };
        nextSiteForPersist = nextSite;
        return nextSite;
      });
      // Mirror the soft-delete stamp across every report on this site that
      // still carries the doc in `documents[]`. `documentRefs[]` is left
      // untouched — references are still valid (the doc isn't gone), they
      // just point at a tombstoned row.
      const patches: typeof reportPatchesForPersist = [];
      const reports = state.siteRag.reports.map(r => {
        if (r.siteId !== siteId) return r;
        const docs = r.documents ?? [];
        const idx = docs.findIndex(d => d.id === docId);
        if (idx < 0) return r;
        const target = docs[idx];
        if (target.deletedAt) return r;
        const nextDocuments = docs.map(d =>
          d.id === docId
            ? { ...d, deletedAt: now, deletedBy: actorUid ?? null }
            : d,
        );
        patches.push({ reportId: r.id, documents: nextDocuments });
        return { ...r, documents: nextDocuments };
      });
      reportPatchesForPersist = patches;
      return { siteRag: { ...state.siteRag, sites, reports } };
    });

    if (!mutated) {
      // Either the doc didn't exist or it was already soft-deleted. Skip
      // persistence so retries are idempotent.
      return;
    }

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, updateDoc, setDoc, serverTimestamp } = await import('firebase/firestore');
      // DLB-3 — re-ingest tombstone write FIRST. Writing the tombstone
      // before the site/report patch means a concurrent `saveDocument`
      // race that interleaves between our patch and the re-ingest path's
      // tombstone check still sees the tombstone and aborts. Best-effort:
      // tombstone failures must NOT block the user-visible soft delete.
      try {
        await setDoc(doc(db, `users/${user.uid}/deletedDocuments`, docId), stripUndefined({
          docId,
          siteId,
          deletedAt: serverTimestamp(),
          reason: 'single-delete',
          actorUid: user.uid,
          originalFileName,
        }));
      } catch (e) {
        console.warn(`[removeSiteDocument] tombstone write failed for ${docId}:`, (e as Error)?.message);
      }
      if (nextSiteForPersist) {
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(doc(db, siteOwnerSitesPath(nextSiteForPersist, user.uid), siteId), stripUndefined({
          documents: documentsForSiteRecord(nextSiteForPersist.documents ?? []),
          updatedAt: now,
        }));
      }
      for (const patch of reportPatchesForPersist) {
        try {
          await get().updateRagReport(patch.reportId, {
            documents: patch.documents,
          });
        } catch (e) {
          console.warn(`[removeSiteDocument] failed to persist report ${patch.reportId}:`, (e as Error)?.message);
        }
      }
      // DDL-FOLLOWUP — DO NOT delete the user-level document index row or
      // the GCS bytes. Soft delete keeps them so restore is viable for the
      // SOFT_DELETE_TTL_DAYS window. The purge cron hard-deletes them
      // after the TTL elapses.
      // W22-7 — emit one `document.soft_deleted` audit event AFTER the
      // persistence writes complete (parity with `restoreSiteDocument`'s
      // emit-after-write discipline). `referencingReportCount` is the N
      // reports the doc was bound to at delete time — the audit row can
      // reconstruct the modal copy without a Firestore re-fetch.
      // Soft-fail: emit errors never block the user-visible soft delete.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'document.soft_deleted',
          target: { type: 'document', id: docId, label: originalFileName },
          metadata: stripUndefined({
            siteId,
            originalFileName,
            referencingReportCount: reportPatchesForPersist.length,
          }),
        });
      } catch (e) {
        console.warn(`[removeSiteDocument] audit emit failed for ${docId}:`, (e as Error)?.message);
      }

      // DOC-DELETE cascade — a removed document must not linger in the SED or
      // be cited by reports. Re-extract the SED from the current (post-delete)
      // doc set so the deleted doc's content is purged; the rebuild flags
      // affected reports stale for re-run (their prose still cites the removed
      // doc until regenerated). Fire-and-forget — the SED rebuild banner shows
      // progress. forceRefresh ensures a real re-extract, not a cache hit.
      void get().retrySEDBootstrap(siteId, { forceRefresh: true }).catch((err) =>
        console.warn(`[removeSiteDocument] SED rebuild after delete failed for ${siteId}:`, (err as Error)?.message),
      );
    } catch (e) {
      console.warn('Failed to soft-delete site document:', e);
    }
  },

  restoreSiteDocument: async (siteId, docId) => {
    const now = new Date().toISOString();
    // DDL-FOLLOWUP-RESTORE — null the soft-delete stamp on the doc across
    // the DDL-1 cross-array sweep so the SiteLandingPage union dedupe
    // can't reapply a stale tombstone copy on the next render. `deletedAt`
    // and `deletedBy` are written as explicit `null` (not undefined) so
    // Firestore sees the "not deleted" state — stripUndefined preserves
    // the null.
    let nextSiteForPersist: import('../types/site').SiteEntity | undefined;
    let mutated = false;
    let originalFileName: string | undefined;
    let softDeleteAgeMs: number | undefined;
    let reportPatchesForPersist: Array<{
      reportId: string;
      documents: import('../types/siteRag').SiteDocument[];
    }> = [];
    set((state) => {
      const sites = state.siteRag.sites.map(s => {
        if (s.id !== siteId) return s;
        const docs = s.documents ?? [];
        const target = docs.find(d => d.id === docId);
        if (!target || !target.deletedAt) return s;
        originalFileName = target.fileName;
        const stampedMs = Date.parse(target.deletedAt);
        if (Number.isFinite(stampedMs)) {
          softDeleteAgeMs = Date.now() - stampedMs;
        }
        const nextDocs = docs.map(d =>
          d.id === docId
            ? { ...d, deletedAt: null, deletedBy: null }
            : d,
        );
        mutated = true;
        const nextSite = {
          ...s,
          documents: nextDocs,
          updatedAt: now,
        };
        nextSiteForPersist = nextSite;
        return nextSite;
      });
      const patches: typeof reportPatchesForPersist = [];
      const reports = state.siteRag.reports.map(r => {
        if (r.siteId !== siteId) return r;
        const docs = r.documents ?? [];
        const idx = docs.findIndex(d => d.id === docId);
        if (idx < 0) return r;
        const target = docs[idx];
        if (!target.deletedAt) return r;
        const nextDocuments = docs.map(d =>
          d.id === docId
            ? { ...d, deletedAt: null, deletedBy: null }
            : d,
        );
        patches.push({ reportId: r.id, documents: nextDocuments });
        return { ...r, documents: nextDocuments };
      });
      reportPatchesForPersist = patches;
      return { siteRag: { ...state.siteRag, sites, reports } };
    });

    if (!mutated) return;

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, updateDoc } = await import('firebase/firestore');
      if (nextSiteForPersist) {
        // #owner-path-sweep — write to the path that actually holds the doc.
        await updateDoc(doc(db, siteOwnerSitesPath(nextSiteForPersist, user.uid), siteId), stripUndefined({
          documents: documentsForSiteRecord(nextSiteForPersist.documents ?? []),
          updatedAt: now,
        }));
      }
      for (const patch of reportPatchesForPersist) {
        try {
          await get().updateRagReport(patch.reportId, {
            documents: patch.documents,
          });
        } catch (e) {
          console.warn(`[restoreSiteDocument] failed to persist report ${patch.reportId}:`, (e as Error)?.message);
        }
      }
      // Emit the restore audit AFTER the writes so a failed persist
      // doesn't lie in the activity log. Soft-fail: emission errors never
      // block the user-visible restore.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'document.restored',
          target: {
            type: 'document',
            id: docId,
            label: originalFileName,
          },
          metadata: stripUndefined({
            siteId,
            originalFileName,
            softDeleteAgeMs: Number.isFinite(softDeleteAgeMs) ? softDeleteAgeMs : undefined,
          }),
        });
      } catch (e) {
        console.warn(`[restoreSiteDocument] audit emit failed for ${docId}:`, (e as Error)?.message);
      }
    } catch (e) {
      console.warn('Failed to restore site document:', e);
    }
  },

  bulkRemoveSiteDocuments: async (siteId, docIds, reason = 'bulk-manual') => {
    // DLB-3 — bulk delete. Same cross-array shape as DDL-1's
    // removeSiteDocument (site.documents + documentIds + every report's
    // documents + documentRefs + siteContext.claims) but in ONE
    // set-state-builder so the SiteLandingPage union dedupe
    // (SiteLandingPage.tsx:679) can never observe a partial state
    // where some reports still reference a half-deleted doc.
    //
    // Persistence: single Firestore writeBatch for the tombstones +
    // index deletes + site patch. Reports + GCS cleanup loop after the
    // batch commits — failures there are isolated per-doc and don't
    // unwind the user-visible delete.
    //
    // Auto-chunks above 200 docs so the batch stays under Firestore's
    // 500-op-per-batch limit (200 docs × 2 ops + 1 site update = 401
    // ops worst case — well under the cap with margin for the
    // tombstone-write-failure-fallback path).
    if (docIds.length === 0) return;
    const BATCH_CAP = 200;
    if (docIds.length > BATCH_CAP) {
      // Recurse in chunks. Each chunk is a fully-atomic batch on its
      // own; the union of chunks is NOT atomic, but the optimistic
      // local strip already covers the visual contract.
      for (let i = 0; i < docIds.length; i += BATCH_CAP) {
        const chunk = docIds.slice(i, i + BATCH_CAP);
        await get().bulkRemoveSiteDocuments(siteId, chunk, reason);
      }
      return;
    }

    const now = new Date().toISOString();
    const docIdSet = new Set(docIds);
    let nextSiteForPersist: import('../types/site').SiteEntity | undefined;
    let siteContextChanged = false;
    // DLB-4 — capture each doc's fileName BEFORE the bulk strip so the
    // tombstones written below can carry an `originalFileName` for the
    // admin Ghost Docs panel. Map keyed by docId; values default to
    // undefined for legacy docs that never carried a fileName.
    const originalFileNames = new Map<string, string | undefined>();
    let reportPatchesForPersist: Array<{
      reportId: string;
      documents: import('../types/siteRag').SiteDocument[];
      documentRefs: NonNullable<import('../types/siteRag').SiteRagReport['documentRefs']>;
    }> = [];
    set((state) => {
      const sites = state.siteRag.sites.map(s => {
        if (s.id !== siteId) return s;
        const priorContext = s.siteContext;
        const nextContext = (() => {
          if (!priorContext || !priorContext.claims?.length) return priorContext;
          const nextClaims = priorContext.claims.filter(c => !docIdSet.has(c.sourceDocId ?? ''));
          if (nextClaims.length === priorContext.claims.length) return priorContext;
          return { ...priorContext, claims: nextClaims, updatedAt: now };
        })();
        siteContextChanged = nextContext !== priorContext;
        // DLB-4 — snapshot fileNames before the filter drops them.
        for (const d of s.documents ?? []) {
          if (docIdSet.has(d.id)) originalFileNames.set(d.id, d.fileName);
        }
        const nextSite = {
          ...s,
          documents: (s.documents ?? []).filter(d => !docIdSet.has(d.id)),
          documentIds: (s.documentIds ?? []).filter(id => !docIdSet.has(id)),
          ...(siteContextChanged && nextContext ? { siteContext: nextContext } : {}),
          updatedAt: now,
        };
        nextSiteForPersist = nextSite;
        return nextSite;
      });
      const patches: typeof reportPatchesForPersist = [];
      const reports = state.siteRag.reports.map(r => {
        if (r.siteId !== siteId) return r;
        const nextDocuments = (r.documents ?? []).filter(d => !docIdSet.has(d.id));
        const nextDocumentRefs = (r.documentRefs ?? []).filter(ref => !docIdSet.has(ref.docId));
        const docsChanged = nextDocuments.length !== (r.documents?.length ?? 0);
        const refsChanged = nextDocumentRefs.length !== (r.documentRefs?.length ?? 0);
        if (!docsChanged && !refsChanged) return r;
        patches.push({
          reportId: r.id,
          documents: nextDocuments,
          documentRefs: nextDocumentRefs,
        });
        return {
          ...r,
          ...(docsChanged ? { documents: nextDocuments } : {}),
          ...(refsChanged ? { documentRefs: nextDocumentRefs } : {}),
        };
      });
      reportPatchesForPersist = patches;
      return { siteRag: { ...state.siteRag, sites, reports } };
    });

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, writeBatch, serverTimestamp } = await import('firebase/firestore');
      const batch = writeBatch(db);

      // 1. Tombstones FIRST. Concurrent re-ingest paths
      //    (saveDocument → checkDocTombstone) see the tombstone and
      //    abort with GhostReingestBlocked. Tombstones are written in
      //    the same batch as the index deletes so a batch-level abort
      //    rolls both back — we never leak a tombstone for a doc that
      //    didn't get its index row deleted. The >200-doc chunking is
      //    handled by the recursion at the top of this action, so docIds
      //    here is already ≤200 (≤401 ops with the site patch).
      for (const id of docIds) {
        batch.set(doc(db, `users/${user.uid}/deletedDocuments`, id), stripUndefined({
          docId: id,
          siteId,
          deletedAt: serverTimestamp(),
          reason,
          actorUid: user.uid,
          // DLB-4 — denormalise so the admin Ghost Docs panel can show
          // a human-readable file name without re-querying a row that
          // by definition no longer exists.
          originalFileName: originalFileNames.get(id),
        }));
        batch.delete(doc(db, `users/${user.uid}/documents`, id));
      }

      // 2. Site patch — single update for the whole bulk operation
      //    (vs N updates if we looped removeSiteDocument). Keeps the
      //    site's `updatedAt` change-stream noise to a single bump.
      if (nextSiteForPersist) {
        const siteContextPatch = siteContextChanged && nextSiteForPersist.siteContext
          ? { siteContext: nextSiteForPersist.siteContext }
          : {};
        // #owner-path-sweep — write to the path that actually holds the doc.
        batch.update(doc(db, siteOwnerSitesPath(nextSiteForPersist, user.uid), siteId), stripUndefined({
          documents: documentsForSiteRecord(nextSiteForPersist.documents ?? []),
          documentIds: nextSiteForPersist.documentIds ?? [],
          updatedAt: now,
          ...siteContextPatch,
        }));
      }

      await batch.commit();

      // 3. Persist each report patch through updateRagReport — the
      //    same path removeSiteDocument uses so the GCS payload +
      //    Firestore metadata stay in sync. Per-report try so one bad
      //    write doesn't strand the rest.
      for (const patch of reportPatchesForPersist) {
        try {
          await get().updateRagReport(patch.reportId, {
            documents: patch.documents,
            documentRefs: patch.documentRefs,
          });
        } catch (e) {
          console.warn(`[bulkRemoveSiteDocuments] failed to persist report ${patch.reportId}:`, (e as Error)?.message);
        }
      }

      // 4. Best-effort GCS bytes cleanup. `deleteDocument` already
      //    handles the storagePath + rawStoragePath sweep and emits
      //    orphan-log entries on failure. We swallow individual
      //    failures here — the tombstones + index deletes are the
      //    contract; GCS bytes are gravy.
      const { deleteDocument } = await import('../services/knowledge/documentUpload');
      await Promise.allSettled(
        // #89 — pass the owning `siteId` so the DOCUMENT delete is gated
        // by that site's edit-lock (assertSiteWritable), consistent with
        // the component delete paths. The per-doc `.catch` keeps the
        // GCS-bytes sweep best-effort (a swallowed failure never strands
        // the tombstones + index deletes that are the actual contract).
        docIds.map(id => deleteDocument(id, siteId).catch(() => undefined)),
      );

      // 5. Audit log emit — ONE event per bulk call (not N events).
      //    Carries the docId list so the activity tab can render the
      //    full deletion list without a re-fetch.
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'document.exclude',
          target: { type: 'site', id: siteId },
          metadata: {
            kind: 'bulk-delete',
            reason,
            count: docIds.length,
            deletedDocIds: docIds,
          },
        });
      } catch (e) {
        console.warn('[bulkRemoveSiteDocuments] audit emit failed:', (e as Error)?.message);
      }

      // DOC-DELETE cascade (bulk) — ONE SED rebuild after the whole batch so
      // the removed docs' content is purged from the SED + reports (not N
      // rebuilds). See removeSiteDocument for the rationale.
      void get().retrySEDBootstrap(siteId, { forceRefresh: true }).catch((err) =>
        console.warn(`[bulkRemoveSiteDocuments] SED rebuild after delete failed for ${siteId}:`, (err as Error)?.message),
      );
    } catch (e) {
      console.warn('Failed to bulk-remove site documents:', e);
    }
  },

  patchSiteDocument: async (siteId, docId, patch) => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in before saving document changes.');
    const initialSite = get().siteRag.sites.find(site => site.id === siteId);
    if (!initialSite) throw new Error('The document site is no longer available.');
    const ownerUid = siteOwnerUid(initialSite, user.uid);
    await enqueueSiteDocumentWrite(`${ownerUid}/${siteId}/${docId}`, async () => {
      if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before saving.');
      const site = get().siteRag.sites.find(row => row.id === siteId);
      if (!site || siteOwnerUid(site, user.uid) !== ownerUid) throw new Error('The document owner changed. Reload before saving.');
      await get().assertSiteWritable(siteId);
      const source = site.documents?.find(document => document.id === docId);
      if (!source) throw new Error('The document is no longer in this site.');
      if (!Object.keys(patch).length) return;
      const expectedDocumentHash = siteDocumentMetadataBasisHash(source);
      // JSON omits undefined. The metadata API uses explicit null to remove
      // optional cached values and confirms the actual cleared keys in its ACK.
      const transported = Object.fromEntries(Object.entries(patch).map(([key, value]) => [key,
        value === undefined && (key === 'importanceScore' || key === 'importanceScoreAt') ? null : value,
      ]));
      const metadataOnly = Object.keys(patch).every(key => _siteDocumentMetadataEditableFields.has(key));
      const metadata = siteDocumentMetadataPatchSchema.safeParse(transported);
      let confirmedDocument: import('../types/siteRag').SiteDocument;
      let confirmedPatch: Partial<import('../types/siteRag').SiteDocument>;
      let writtenAt: string;
      if (metadataOnly) {
        if (!metadata.success) throw new Error(metadata.error.issues[0]?.message ?? 'This document change is invalid.');
        const operationKey = JSON.stringify([user.uid, ownerUid, siteId, docId, expectedDocumentHash,
          Object.entries(metadata.data).sort(([a], [b]) => a.localeCompare(b))]);
        let operationId = _siteDocumentMetadataOperationIds.get(operationKey);
        if (!operationId) {
          operationId = crypto.randomUUID();
          if (_siteDocumentMetadataOperationIds.size >= 64) {
            const oldest = _siteDocumentMetadataOperationIds.keys().next().value;
            if (oldest !== undefined) _siteDocumentMetadataOperationIds.delete(oldest);
          }
          _siteDocumentMetadataOperationIds.set(operationKey, operationId);
        }
        const response = await authedFetch('/api/site-document-metadata', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operationId, ownerUid, siteId, documentId: docId,
            expectedDocumentHash, patch: metadata.data }),
        });
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) _siteDocumentMetadataOperationIds.delete(operationKey);
          const detail = await response.json().catch(() => null) as { error?: unknown; code?: unknown } | null;
          throw Object.assign(new Error(typeof detail?.error === 'string' ? detail.error
            : `Document changes were not saved (${response.status}).`), { status: response.status, code: detail?.code });
        }
        const parsed = siteDocumentMetadataAckSchema.safeParse(await response.json());
        if (!parsed.success) throw new Error('The document save could not be confirmed. Retry or reload before continuing.');
        const ack = parsed.data;
        if (ack.operationId !== operationId || ack.ownerUid !== ownerUid || ack.siteId !== siteId
          || ack.documentId !== docId || ack.document.id !== docId || ack.beforeDocumentHash !== expectedDocumentHash
          || ack.afterDocumentHash !== siteDocumentMetadataBasisHash(ack.document)) {
          throw new Error('The document save acknowledgment did not match this edit. Reload before continuing.');
        }
        confirmedDocument = ack.document;
        writtenAt = ack.writtenAt;
        // Propagate only the confirmed editable fields into mutable legacy
        // copies. Frozen report bodies retain their generation-time evidence.
        confirmedPatch = Object.fromEntries(Object.keys(metadata.data).map(key => [key,
          (ack.document as unknown as Record<string, unknown>)[key],
        ]));
        for (const key of ack.clearedFields) confirmedPatch[key] = undefined;
        _siteDocumentMetadataOperationIds.delete(operationKey);
      } else {
        // Source-specific callers retain their existing writer and rules.
        // Their fields are never admitted to the editable-metadata endpoint.
        const { patchInvalidatesImportance } = await import('../lib/docImportance');
        const effectivePatch = !Object.prototype.hasOwnProperty.call(patch, 'importanceScore') && patchInvalidatesImportance(patch)
          ? { ...patch, importanceScore: undefined, importanceScoreAt: undefined } : patch;
        writtenAt = new Date().toISOString();
        confirmedDocument = { ...source, ...effectivePatch };
        confirmedPatch = effectivePatch;
        const { doc, updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, siteOwnerSitesPath(site, user.uid), siteId), stripUndefined({
          documents: documentsForSiteRecord((site.documents ?? []).map(document => document.id === docId ? confirmedDocument : document)),
          updatedAt: writtenAt,
        }));
      }
      const currentSite = get().siteRag.sites.find(row => row.id === siteId);
      const current = currentSite?.documents?.find(document => document.id === docId);
      const confirmedDocumentHash = siteDocumentMetadataBasisHash(confirmedDocument);
      if (auth.currentUser?.uid !== user.uid || !currentSite || siteOwnerUid(currentSite, user.uid) !== ownerUid || !current
        || (siteDocumentMetadataBasisHash(current) !== expectedDocumentHash
          && siteDocumentMetadataBasisHash(current) !== confirmedDocumentHash)) {
        throw new Error('The document changed while this save was pending. Reload to review the saved version.');
      }
      const reportPatchesForPersist: Array<{ reportId: string; documents: import('../types/siteRag').SiteDocument[] }> = [];
      set((state) => ({ siteRag: { ...state.siteRag,
        sites: state.siteRag.sites.map(row => row.id === siteId ? { ...row, updatedAt: writtenAt,
          documents: (row.documents ?? []).map(document => {
            if (document.id !== docId) return document;
            // Keep hydrated body data omitted from the compact ACK; all
            // canonical fields and explicit cache removals come from the ACK.
            const next = { ...document, ...confirmedDocument };
            if (metadataOnly) {
              // The site-record ACK has a capped preview and empty page text.
              // Metadata never changes the source, so retain its hydrated body.
              next.textContent = document.textContent;
              if (document.pages) next.pages = document.pages;
            }
            for (const key of ['importanceScore', 'importanceScoreAt'] as const) {
              if (Object.prototype.hasOwnProperty.call(confirmedPatch, key) && confirmedPatch[key] === undefined) delete next[key];
            }
            return next;
          }),
        } : row),
        reports: state.siteRag.reports.map(report => {
          if (report.siteId !== siteId || hasImmutableReportIdentityForMutation(report)
            || !(report.documents ?? []).some(document => document.id === docId)) return report;
          const documents = report.documents!.map(document => document.id === docId ? { ...document, ...confirmedPatch } : document);
          reportPatchesForPersist.push({ reportId: report.id, documents });
          return { ...report, documents };
        }),
      } }));
      for (const pending of reportPatchesForPersist) {
        try { await get().updateRagReport(pending.reportId, { documents: pending.documents }); }
        catch (error) { console.warn(`[patchSiteDocument] legacy report ${pending.reportId} sync failed:`, (error as Error)?.message); }
      }
    });
  },

  moveSiteDocument: async (siteId, docId, newFolder) => {
    // DMF-1 — folder move with DDL-3 cross-array parity. Routes through
    // `patchSiteDocument` so the same partial update lands on
    // `site.documents[i]` AND every `report.documents[i]` on this site.
    // Persistence, Firestore writes, and report fan-out are all owned by
    // `patchSiteDocument` — this wrapper exists to give the move flow a
    // dedicated entry point + stamp `movedAt` for audit / "recently
    // moved" surfaces. Empty string is a valid value (clears the folder
    // override; categorize() falls back to the auto bucket).
    await get().patchSiteDocument(siteId, docId, {
      folder: newFolder,
      movedAt: Date.now(),
    });
  },

  renameFolder: async (siteId, oldName, newName) => {
    // DF-1 — folder bulk-rename with cross-array atomicity. Mirrors the
    // DDL-1 / DDL-3 pattern: walk every array a doc copy lives in
    // (site.documents + every report.documents on this site +
    // documentRefs on those reports) inside ONE set-state-builder so
    // the SiteLandingPage union dedupe (last-wins on report copies)
    // cannot restore the pre-rename value on the next render.
    //
    // Match semantics: a doc belongs to the folder when either
    //   (a) `d.folder === oldName`         — explicit user override
    //   (b) `categorize(d) === oldName`    — auto bucket
    // Empty string in `newName` would clear the override but we treat
    // it as a no-op rename (the inline handler in DocumentLibrary only
    // calls us with a non-empty trimmed string, but the guard is
    // worth keeping at the action level).
    //
    // documentRefs has no folder field today; the walk is included for
    // invariant completeness so any future ref-side folder migration
    // automatically lights up this code path.
    //
    // Persistence (mirror DLB-3 writeBatch shape):
    //   1. ONE `writeBatch` for the site doc patch + each touched
    //      report's Firestore metadata stamp. If any of those rejects,
    //      ALL Firestore writes roll back — the in-memory mutation has
    //      already happened but the SiteLandingPage union is consistent
    //      because it's bound to local state, not Firestore directly.
    //   2. Per-report GCS save via `updateRagReport` AFTER the batch
    //      commits. Per-report try so one bad GCS write doesn't strand
    //      the rest.
    if (!oldName || !newName || oldName === newName) return;
    const now = new Date().toISOString();
    let nextSiteForPersist: import('../types/site').SiteEntity | undefined;
    let reportPatchesForPersist: Array<{
      reportId: string;
      documents: import('../types/siteRag').SiteDocument[];
    }> = [];
    // Touched flag: did we mutate anything? If not, short-circuit
    // before Firestore writes.
    let touched = false;
    // Lazy-load the categorizer once (top-level import would create a
    // store ↔ component-lib cycle; the dynamic import here mirrors the
    // pattern used elsewhere in the store for lib helpers).
    const { categorize } = await import('../lib/documentCategory');
    set((state) => {
      const matches = (d: import('../types/siteRag').SiteDocument): boolean =>
        d.folder === oldName || categorize(d) === oldName;
      const sites = state.siteRag.sites.map(s => {
        if (s.id !== siteId) return s;
        const docs = s.documents ?? [];
        const nextDocs = docs.map(d => matches(d) ? { ...d, folder: newName } : d);
        // No-op short-circuit — preserves the original site reference
        // so React's identity check doesn't re-render downstream
        // consumers when the rename was a no-op (e.g. the user picked
        // a name that no doc currently maps to).
        if (nextDocs.every((d, i) => d === docs[i])) return s;
        touched = true;
        const nextSite = { ...s, documents: nextDocs, updatedAt: now };
        nextSiteForPersist = nextSite;
        return nextSite;
      });
      const patches: typeof reportPatchesForPersist = [];
      const reports = state.siteRag.reports.map(r => {
        if (r.siteId !== siteId) return r;
        const docs = r.documents ?? [];
        if (docs.length === 0) return r;
        const nextDocuments = docs.map(d => matches(d) ? { ...d, folder: newName } : d);
        if (nextDocuments.every((d, i) => d === docs[i])) return r;
        touched = true;
        patches.push({ reportId: r.id, documents: nextDocuments });
        return { ...r, documents: nextDocuments };
      });
      reportPatchesForPersist = patches;
      return { siteRag: { ...state.siteRag, sites, reports } };
    });
    if (!touched) return;
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, writeBatch } = await import('firebase/firestore');
      // Single writeBatch covers the site patch + every touched report's
      // Firestore metadata stamp. The full report GCS body is saved
      // via updateRagReport AFTER the batch commits (GCS can't be in
      // the Firestore batch). Failures in the batch roll the whole
      // Firestore side back together; the GCS body is best-effort
      // (same shape as DLB-3).
      const batch = writeBatch(db);
      if (nextSiteForPersist) {
        // #owner-path-sweep — write to the path that actually holds the doc.
        batch.update(doc(db, siteOwnerSitesPath(nextSiteForPersist, user.uid), siteId), stripUndefined({
          documents: documentsForSiteRecord(nextSiteForPersist.documents ?? []),
          updatedAt: now,
        }));
      }
      for (const patch of reportPatchesForPersist) {
        // #owner-path-sweep-p2 — write metadata stamp at the OWNER's path.
        batch.set(
          doc(db, ownerReportDocPath(get(), patch.reportId, user.uid)),
          { id: patch.reportId, updatedAt: now },
          { merge: true },
        );
      }
      await batch.commit();
      // Now persist the FULL report bodies via updateRagReport so the
      // GCS payload tracks the in-memory rename. Per-report try so one
      // bad write doesn't strand the rest.
      for (const patch of reportPatchesForPersist) {
        try {
          await get().updateRagReport(patch.reportId, { documents: patch.documents });
        } catch (e) {
          console.warn(`[renameFolder] failed to persist report ${patch.reportId}:`, (e as Error)?.message);
        }
      }
    } catch (e) {
      console.warn('Failed to rename folder:', e);
    }
  },

  appendSiteDocuments: async (siteId, newDocs) => {
    if (!newDocs.length) return;
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in before attaching site documents.');
    await get().assertSiteWritable(siteId);
    const before = get().siteRag.sites.find(site => site.id === siteId);
    if (!before) throw new Error('The document site is no longer available.');
    if (before.demonstrationProvenance) throw new Error('Demonstration documents require owner-scoped server intake.');
    const existingIds = new Set((before.documents ?? []).map(document => document.id));
    const trulyNew = newDocs.filter(document => {
      if (existingIds.has(document.id)) return false;
      existingIds.add(document.id);
      return true;
    });
    if (!trulyNew.length) return;
    // DDL-5: append only fresh uploads, never report-derived union rows.
    // The full source remains in memory; only the site-record projection is sent.
    const ownerUid = siteOwnerUid(before, user.uid);
    const beforeBasis = siteDocumentsBasis(before.documents);
    const now = new Date().toISOString();
    const { doc, runTransaction } = await import('firebase/firestore');
    if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before attaching.');
    const reference = doc(db, siteOwnerSitesPath(before, user.uid), siteId);
    const saved = await runTransaction(db, async transaction => {
      const snapshot = await transaction.get(reference);
      if (!snapshot.exists()) throw new Error('The document site is no longer available.');
      const canonical = snapshot.data() as import('../types/site').SiteEntity;
      if (canonical.demonstrationProvenance) throw new Error('Demonstration documents require owner-scoped server intake.');
      if (auth.currentUser?.uid !== user.uid) throw new Error('The signed-in account changed. Reload before attaching.');
      const ids = new Set((canonical.documents ?? []).map(document => document.id));
      const additions = trulyNew.filter(document => !ids.has(document.id));
      const documents = documentsForSiteRecord([...(canonical.documents ?? []), ...additions]);
      const documentIds = [...new Set([...(canonical.documentIds ?? []), ...documents.map(document => document.id)])];
      const updatedAt = canonical.updatedAt > now ? canonical.updatedAt : now;
      transaction.update(reference, stripUndefined({ documents, documentIds, updatedAt }));
      return { documents, documentIds, updatedAt };
    });
    // Restore session-only full bodies only when the canonical metadata agrees.
    const sessionDocs = new Map([...(before.documents ?? []), ...trulyNew].map(document => [document.id, document]));
    const documents = saved.documents.map(document => {
      const session = sessionDocs.get(document.id);
      return session && siteDocumentMetadataBasisHash(session) === siteDocumentMetadataBasisHash(document)
        ? session : document;
    });
    const { documentIds } = saved;
    const savedBasis = siteDocumentsBasis(documents);
    const current = get().siteRag.sites.find(site => site.id === siteId);
    // Another confirmed same-tab append can advance the local basis while this
    // transaction retries. Admit only canonical additive rows; competing edits
    // or removals remain intact and require a fresh review instead.
    const canonicalById = new Map(documents.map(document => [document.id, siteDocumentMetadataBasisHash(document)]));
    const currentById = new Map((current?.documents ?? []).map(document => [document.id, siteDocumentMetadataBasisHash(document)]));
    const onlyConfirmedAdditions = (before.documents ?? []).every(document => currentById.get(document.id) === siteDocumentMetadataBasisHash(document))
      && (current?.documents ?? []).every(document => canonicalById.get(document.id) === siteDocumentMetadataBasisHash(document));
    if (auth.currentUser?.uid !== user.uid || !current || siteOwnerUid(current, user.uid) !== ownerUid
      || (![beforeBasis, savedBasis].includes(siteDocumentsBasis(current.documents)) && !onlyConfirmedAdditions)) {
      throw new Error('The document attachment was saved, but the library changed while saving. Reload to review the saved version.');
    }
    set(state => ({ siteRag: { ...state.siteRag,
      sites: state.siteRag.sites.map(site => site.id === siteId
        ? { ...site, documents, documentIds, updatedAt: site.updatedAt > saved.updatedAt ? site.updatedAt : saved.updatedAt } : site),
    } }));
    if (trulyNew.some(document => !(document.tags ?? []).includes('library-index'))) {
      void get().maybeFlipEvidenceModeOnDocUpload(siteId);
    }
    void get().recomputeEvidenceReadiness(siteId);
  },

  reExtractDocument: async (docId) => {
    // DVF-3 — single-doc re-extraction. Mirrors the per-doc branch of the
    // admin batch flows (`PdfTextReprocessBlock`, `OfficeAndImageReprocessBlock`):
    // download bytes via `/api/storage/download-file`, route to the format-
    // appropriate extractor, drop the auto-tag stamp when text changes so the
    // AuthGate auto-backfill reclassifies, then persist via patchSiteDocument
    // (which carries cross-array consistency with reports for free). The
    // doc is located by scanning every site (doc IDs are user-unique).
    const state = get();
    let siteId: string | undefined;
    let doc: import('../types/siteRag').SiteDocument | undefined;
    for (const s of state.siteRag.sites) {
      const found = (s.documents ?? []).find(d => d.id === docId);
      if (found) {
        siteId = s.id;
        doc = found;
        break;
      }
    }
    if (!siteId || !doc) throw new Error(`Document ${docId} not found on any site`);
    if (!doc.rawStoragePath) {
      throw new Error('No stored original — re-upload to enable re-extraction.');
    }

    const res = await authedFetch(
      `/api/storage/download-file?path=${encodeURIComponent(doc.rawStoragePath)}`,
    );
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const buf = await res.arrayBuffer();

    const name = (doc.fileName ?? '').toLowerCase();
    const isPdf = doc.mimeType === 'application/pdf' || name.endsWith('.pdf');
    const reprocessedAt = new Date().toISOString();
    let newText: string;
    const patch: Partial<import('../types/siteRag').SiteDocument> = {};

    if (isPdf) {
      const { extractPdfTextFromBytes } = await import('../services/knowledge/documentUpload');
      newText = await extractPdfTextFromBytes(new Uint8Array(buf), doc.fileName);
      patch.textContent = newText;
      patch.pdfTextReprocessedAt = reprocessedAt;
    } else {
      const { extractTextFromFile } = await import('../services/knowledge/documentUpload');
      const fileObj = new File([buf], doc.fileName, { type: doc.mimeType || 'application/octet-stream' });
      newText = await extractTextFromFile(fileObj);
      patch.textContent = newText;
      patch.officeReprocessedAt = reprocessedAt;
    }

    // If text actually changed, drop the auto-tag stamp so the AuthGate
    // auto-backfill re-classifies the doc against the corrected text.
    // Mirrors the admin batch reprocess behaviour.
    if (newText !== doc.textContent) {
      patch.autoTaggedAt = undefined;
      patch.autoTagModelVersion = undefined;
    }

    await get().patchSiteDocument(siteId, docId, patch);
    return newText;
  },

  deleteSite: async (id, mode = 'site-only') => {
    // #82 — a locked site can only be deleted by its holder / an admin.
    await get().assertSiteWritable(id);
    // Site-delete cascade — mirrors deleteRagReport (Workflow Prompt 7) one
    // level up. Three modes:
    //   - 'site-only'      → remove the site doc; reports become orphaned.
    //   - 'with-reports'   → remove site + every report; leave SEDs and docs in GCS.
    //   - 'with-everything'→ remove site + reports + non-shared SEDs + docs + sims.
    //
    // Order: GCS deletes first (storage), Firestore deletes last (metadata).
    // If any GCS delete fails we ABORT before touching Firestore so we don't
    // strand metadata pointing at vanished files (or vice-versa). The local
    // optimistic state update is rolled back on abort. Errors[] surfaces the
    // partial state to the toast layer.
    const result: import('../services/cascadeDelete').DeleteSiteResult = {
      deletedReports: 0,
      deletedSeds: 0,
      preservedSeds: 0,
      deletedDocuments: 0,
      deletedSimulations: 0,
      storageFreedBytes: 0,
      errors: [],
    };

    const site = get().siteRag.sites.find(s => s.id === id);
    if (!site) {
      result.errors.push('Site not found in store');
      return result;
    }

    // Snapshot reports + sites BEFORE state mutation so we can roll back on
    // GCS failure and so the cascade can read sedId/documents.
    const allReports = get().siteRag.reports;
    const sitesSnapshot = get().siteRag.sites;
    const reportsSnapshot = allReports;
    const { findReportsForSite, findReportsReferencingSed, REPORT_PAYLOAD_ESTIMATE_BYTES, SED_SIZE_ESTIMATE_BYTES } =
      await import('../services/cascadeDelete');
    const siteReports = findReportsForSite(site, allReports);
    const siteReportIdSet = new Set(siteReports.map(r => r.id));

    // Optimistic local state update — site removed immediately; reports removed
    // for cascade modes only (site-only leaves them orphaned in the store).
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        sites: state.siteRag.sites.filter(s => s.id !== id),
        reports: mode === 'site-only'
          ? state.siteRag.reports
          : state.siteRag.reports.filter(r => !siteReportIdSet.has(r.id)),
        activeSiteId: state.siteRag.activeSiteId === id ? null : state.siteRag.activeSiteId,
        activeReportId: mode !== 'site-only' && state.siteRag.activeReportId && siteReportIdSet.has(state.siteRag.activeReportId)
          ? null : state.siteRag.activeReportId,
      },
    }));

    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      result.errors.push('Not authenticated');
      return result;
    }

    const { doc, deleteDoc, collection, getDocs, query, where } = await import('firebase/firestore');
    const callDelete = (path: string) => authedFetch('/api/storage/delete', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
    });

    try {
      if (mode === 'with-reports' || mode === 'with-everything') {
        // ── 1. SEDs (with-everything only). Skip any SED still referenced by
        //       a report outside this site.
        if (mode === 'with-everything') {
          const sedIds = new Set<string>();
          for (const r of siteReports) if (r.sedId) sedIds.add(r.sedId);
          for (const sedId of sedIds) {
            const stillRef = findReportsReferencingSed(sedId, allReports, siteReportIdSet);
            if (stillRef.length > 0) {
              result.preservedSeds++;
              console.warn(`[Delete] SED ${sedId} kept — referenced by ${stillRef.length} report(s) outside this site.`);
              continue;
            }
            try {
              await callDelete(`users/${user.uid}/evidence/${sedId}.json`);
              result.deletedSeds++;
              result.storageFreedBytes += SED_SIZE_ESTIMATE_BYTES;
            } catch (e) {
              result.errors.push(`SED ${sedId}: ${(e as Error).message || 'delete failed'}`);
            }
          }
        }

        // ── 2. Uploaded documents (with-everything only). Dedup by storagePath.
        //       PH-A2.3 — walk `site.documents` filtered by each report's
        //       `documentRefs` (the post-IC source of truth) instead of the
        //       deprecated inline `report.documents`. Inline `documents` is
        //       only consulted as a back-compat fallback for legacy reports
        //       that pre-date the docRef split.
        if (mode === 'with-everything') {
          const seenPaths = new Set<string>();
          const siteDocsById = new Map((site.documents ?? []).map(d => [d.id, d]));
          for (const r of siteReports) {
            const docsForReport: import('../types/siteRag').SiteDocument[] = [];
            if (r.documentRefs?.length) {
              for (const ref of r.documentRefs) {
                if (!ref.userIncluded) continue;
                const sd = siteDocsById.get(ref.docId);
                if (sd) docsForReport.push(sd);
              }
            } else if (r.documents?.length) {
              // Legacy pre-docRef report — fall through to the inline array
              // so a delete cascade on a never-migrated report still cleans
              // up its GCS payloads.
              docsForReport.push(...r.documents);
            }
            for (const d of docsForReport) {
              if (!d.storagePath || seenPaths.has(d.storagePath)) continue;
              seenPaths.add(d.storagePath);
              try {
                await callDelete(d.storagePath);
                result.deletedDocuments++;
                result.storageFreedBytes += d.sizeBytes || 0;
              } catch (e) {
                result.errors.push(`Document ${d.fileName}: ${(e as Error).message || 'delete failed'}`);
              }
            }
          }
        }

        // ── 3. Report payloads (GCS) — both 'with-reports' and 'with-everything'.
        //       #owner-path-sweep-p2 — delete at the OWNER's GCS path. For own
        //       reports the owner falls back to user.uid — byte-identical.
        for (const r of siteReports) {
          try {
            await callDelete(reportOwnerGcsPath(r, user.uid, r.id));
            result.storageFreedBytes += REPORT_PAYLOAD_ESTIMATE_BYTES;
          } catch (e) {
            result.errors.push(`Report payload ${r.id}: ${(e as Error).message || 'delete failed'}`);
          }
        }

        // Abort gate: if any GCS delete in steps 1–3 failed, do NOT proceed
        // to the Firestore phase — that would strand metadata pointing at
        // files we couldn't remove. Roll back optimistic state so the user
        // can retry from a consistent UI.
        if (result.errors.length > 0) {
          set((state) => ({
            siteRag: { ...state.siteRag, sites: sitesSnapshot, reports: reportsSnapshot },
          }));
          return result;
        }

        // ── 4. Simulations — query Firestore for the canonical list (siteId match).
        //       Skip-with-warning if a sim lookup throws (e.g., shared/company sim
        //       outside the user's collection). We prefer that over failing the
        //       whole cascade.
        if (mode === 'with-everything') {
          try {
            const simSnap = await getDocs(query(
              collection(db, `users/${user.uid}/simulations`),
              where('siteId', '==', id),
            ));
            for (const s of simSnap.docs) {
              try {
                await deleteDoc(s.ref);
                result.deletedSimulations++;
              } catch (e) {
                result.errors.push(`Simulation ${s.id}: ${(e as Error).message || 'delete failed'}`);
              }
            }
          } catch (e) {
            result.errors.push(`Simulations lookup: ${(e as Error).message || 'failed'}`);
          }
        }

        // ── 5. Report Firestore docs.
        //       #owner-path-sweep-p2 — delete at the OWNER's path.
        for (const r of siteReports) {
          try {
            await deleteDoc(doc(db, reportOwnerDocPath(r, user.uid, r.id)));
            result.deletedReports++;
          } catch (e) {
            result.errors.push(`Report ${r.id}: ${(e as Error).message || 'delete failed'}`);
          }
        }
      }

      // ── 6. Site doc (always last so a partial failure doesn't strand the
      //       site without its children).
      //       #owner-path-sweep — delete the doc where it actually LIVES.
      //       Pre-sweep, deleting a company-shared, foreign-owned site
      //       deleteDoc'd the CURRENT user's (non-existent) path: a silent
      //       no-op that removed the site from local state while the owner's
      //       doc survived — it reappeared on the next fetchSites. Post-sweep
      //       a platform admin truly deletes it; a non-admin company member
      //       surfaces an honest permission error in result.errors instead of
      //       a fake success. Uses the entity captured BEFORE the optimistic
      //       state removal (the store lookup would miss post-removal).
      await deleteDoc(doc(db, siteOwnerSitesPath(site, user.uid), id));

      // TODO(audit): when audit_log Cloud Function is wired, write a row:
      //   { action: 'site.delete',
      //     target: { type: 'site', id, label: site.name },
      //     metadata: { mode, ...result } }.
      // Client cannot write audit_log directly — Firestore rules block it.
      // Either add a `deleteSite` callable Cloud Function and writeAuditLog
      // there, or trigger on `users/{uid}/sites/{siteId}` deletion.
    } catch (e) {
      result.errors.push((e as Error).message || 'Site delete failed');
      console.warn('Failed to delete site:', e);
    }

    return result;
  },

  setActiveSite: (id) => {
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        activeSiteId: id,
        // Clear active report when switching sites (unless it belongs to this site)
        activeReportId: id ? (
          (state.siteRag.sites.find(s => s.id === id)?.reportIds ?? []).includes(state.siteRag.activeReportId || '')
            ? state.siteRag.activeReportId
            : null
        ) : state.siteRag.activeReportId,
      },
    }));
    if (id) {
      void get().loadAuditForSite(id);
      void get().loadRecommendationGraphForSite(id);
    }
    // W22 / R3 — keep `/sites/{id}` in sync synchronously so the URL is updated
    // before React flushes useUrlSiteHydration on the next render.
    syncBrowserUrlForActiveSite(id);
  },

  loadAuditForSite: async (siteId) => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      // #owner-path-sweep — read from the owner's path. Pre-sweep, a shared
      // site's persisted audit silently read as "no doc" from the viewer's
      // own (empty) path; the rules' read arm already authorizes
      // company-tree members on the owner's subcollection.
      const snap = await getDoc(doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/audit/latest`));
      if (!snap.exists()) return;
      const data = snap.data() as { result?: import('../types/userAudit').UserAuditResult; lastRunAt?: string };
      if (!data.result) return;
      const lastResult = data.result;
      set((state) => ({
        auditBySite: {
          ...state.auditBySite,
          [siteId]: {
            ...state.auditBySite[siteId],
            lastResult,
            lastRunAt: data.lastRunAt ?? lastResult.lastRunAt,
            running: false,
          },
        },
      }));
    } catch {
      /* cache load is best-effort */
    }
  },

  runAuditForSite: async (siteId, opts) => {
    const now = Date.now();
    const entry = get().auditBySite[siteId];
    if (entry?.running) return null;
    // `force` bypasses the debounce for explicit user actions (Validate /
    // Reconcile just persisted an override the counts must reflect); the
    // `running` re-entrancy guard above still applies.
    if (!opts?.force && entry?.lastDebouncedAt) {
      const elapsed = now - new Date(entry.lastDebouncedAt).getTime();
      const { AUDIT_DEBOUNCE_MS } = await import('../lib/audit/runUserAudit');
      if (elapsed < AUDIT_DEBOUNCE_MS) return null;
    }

    set((state) => ({
      auditBySite: {
        ...state.auditBySite,
        [siteId]: {
          ...state.auditBySite[siteId],
          running: true,
          lastDebouncedAt: new Date(now).toISOString(),
        },
      },
    }));

    try {
      const { runUserAudit } = await import('../lib/audit/runUserAudit');
      const result = await runUserAudit(siteId);

      set((state) => ({
        auditBySite: {
          ...state.auditBySite,
          [siteId]: {
            lastResult: result,
            lastRunAt: result.lastRunAt,
            running: false,
            lastDebouncedAt: new Date(now).toISOString(),
          },
        },
      }));

      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, setDoc } = await import('firebase/firestore');
        await setDoc(
          doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/audit/latest`),
          stripUndefined({ result, lastRunAt: result.lastRunAt, updatedAt: result.lastRunAt }),
          { merge: true },
        );
      }

      return result;
    } catch (err) {
      set((state) => ({
        auditBySite: {
          ...state.auditBySite,
          [siteId]: {
            ...state.auditBySite[siteId],
            running: false,
          },
        },
      }));
      throw err;
    }
  },

  clearAuditForSite: (siteId) => {
    set((state) => {
      const next = { ...state.auditBySite };
      delete next[siteId];
      return { auditBySite: next };
    });
  },

  computeCompositeForSite: async (siteId, suppliedAnalysisContext) => {
    const companyId = get().companyId;
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) {
      throw new Error(`Site not found: ${siteId}`);
    }

    const localBypass = !companyId && canBypassCompanyForLocalComposite(site.name);
    if (!companyId && !localBypass) {
      const msg = 'Not in a company — cannot persist portfolio composites';
      set((s) => ({ portfolio: { ...s.portfolio, error: msg } }));
      throw new Error(msg);
    }
    const customerId = companyId ?? `local-dev-${site.id}`;
    if (localBypass) {
      console.info('[computeCompositeForSite] localhost bypass active — computing without company context for', site.name);
    }

    // Composites score from full bodies and fingerprint metadata-only rows
    // differently by design. The list pass reads the open site's bodies first
    // and warms the rest behind the gate, so a recompute can run before THIS
    // site's heads have landed: bring them in first (a no-op once resident)
    // and read the rows synchronously once they are.
    await get().ensureSiteReportBodies(siteId);
    const siteReports = get().siteRag.reports.filter(report => reportBelongsToSite(site, report));

    let evidence: import('../types/siteEvidence').SiteEvidenceDocument | null = null;
    if (site.sedId) {
      try {
        const { loadSedById } = await import('../services/sedLoader');
        // SED-VERSION-LABEL — version-key the load (Redhead RCA, 2026-08-11):
        // without the version arg the cache returned the stale pre-bump doc
        // after a rebuild, baking the OLD SED's data-confidence inputs into a
        // freshly-fingerprinted composite.
        evidence = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
      } catch { /* best-effort */ }
    }

    let bandConfig = get().portfolio.bandConfig;
    if (!bandConfig) {
      if (companyId) {
        bandConfig = await get().loadDecisionBandConfig(companyId);
      } else {
        const { DEFAULT_BAND_CONFIG } = await import('../lib/portfolio/decisionBands');
        bandConfig = DEFAULT_BAND_CONFIG;
      }
    }

    const { loadCompositeAnalysisContext } = await import('../services/compositeAnalysisContext');
    const analysisContext = suppliedAnalysisContext ?? await loadCompositeAnalysisContext(site);
    const { computeSiteComposite } = await import('../lib/portfolio/compositeScorer');
    const composite = computeSiteComposite({
      site,
      customerId,
      reports: siteReports,
      evidence,
      bandConfig,
      analysisContext,
    });

    // Update in-memory state FIRST so the dashboard reflects the freshly
    // computed composite even if the (best-effort) persist below fails.
    // Scoring is cheap, deterministic local arithmetic (no solver/network),
    // so optimistic local state is safe — and decoupling display from the
    // write means a transient/permission/offline error can't blank the whole
    // portfolio view (the prior order discarded the composite when setDoc threw).
    set((s) => ({
      portfolio: {
        ...s.portfolio,
        composites: { ...s.portfolio.composites, [siteId]: composite },
        analysisContexts: { ...s.portfolio.analysisContexts, [siteId]: analysisContext },
        lastComputedAt: composite.lastComputedAt,
        error: null,
      },
    }));

    const { auth, db } = await import('../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const user = auth.currentUser;
    if (user && companyId) {
      try {
        const clean = JSON.parse(JSON.stringify(composite, (_, v) => (v === undefined ? null : v)));
        // Path MUST be an even-segment document ref. The per-site composite
        // lives in the `sites` subcollection under `portfolio/composites` so
        // the doc path is customers/{cid}/portfolio/composites/sites/{siteId}
        // (6 segments). The prior 5-segment path threw "Invalid document
        // reference" before any data moved. Keep in sync with the read below,
        // computePortfolioBatch.ts, and firestore.rules.
        // This is the complete derived result. Merging would retain omitted
        // facts from a previously measured pillar after it becomes missing.
        await setDoc(
          doc(db, 'customers', companyId, 'portfolio', 'composites', 'sites', siteId),
          clean,
        );
      } catch (e) {
        console.warn(
          `[computeCompositeForSite] persist failed for ${siteId} (display unaffected):`,
          (e as Error)?.message,
        );
      }
    }

    return composite;
  },

  computeCompositeForPortfolio: async () => {
    const companyId = get().companyId;
    const localBypass = !companyId && isLocalhostRuntime() && readLocalCompositeBypassFlag();

    // Manual and deferred portfolio recomputes share the same evidence rule as
    // the lazy backstop: an empty report array is authoritative only after the
    // report collection has finished hydrating.
    if (!get().siteRag.isSitesLoaded || !get().siteRag.isReportsLoaded) {
      set((s) => ({ portfolio: { ...s.portfolio, computing: false } }));
      return;
    }

    set((s) => ({ portfolio: { ...s.portfolio, computing: true, error: null } }));

    try {
      const sites = companyId
        ? get().siteRag.sites.filter(dashboardScopePredicate(companyId, {
            isAdmin: get().userRole === 'admin',
            subtreeCompanies: get().subtreeCompanies,
          }))
        : get().siteRag.sites.filter(s => canBypassCompanyForLocalComposite(s.name));
      if (sites.length === 0) {
        const msg = localBypass
          ? 'No local bypass demo sites found (expected Holly Ridge / Southwest Crossing).'
          : 'Not in a company';
        set((s) => ({ portfolio: { ...s.portfolio, computing: false, error: msg } }));
        return;
      }
      const concurrency = 5;
      let idx = 0;
      const workers = Array.from({ length: Math.min(concurrency, sites.length) }, async () => {
        while (idx < sites.length) {
          const i = idx++;
          const site = sites[i];
          if (!site) break;
          try {
            await get().computeCompositeForSite(site.id);
          } catch (e) {
            console.warn(`[computeCompositeForPortfolio] site ${site.id}:`, (e as Error)?.message);
          }
        }
      });
      await Promise.all(workers);

      if (companyId) {
        await get().recomputeClustering(companyId);
      }

      set((s) => ({
        portfolio: {
          ...s.portfolio,
          computing: false,
          lastComputedAt: new Date().toISOString(),
          error: null,
        },
      }));
    } catch (e) {
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          computing: false,
          error: (e as Error)?.message ?? 'Portfolio recompute failed',
        },
      }));
    }
  },

  _staleStampSiteComposite: (siteId) => {
    if (!siteId) return;
    const existing = get().portfolio.composites[siteId];
    // Stamp lastComputedAt to the epoch so isStale() flags it expired on the
    // next recompute pass (lastComputedAt is a required string, so we can't
    // clear it — the epoch is the type-safe "always stale" sentinel). No-op
    // when there's no composite yet (nothing to invalidate — the lazy pass
    // computes it fresh anyway) or it's already epoch-stamped (idempotent).
    const EPOCH_ISO = '1970-01-01T00:00:00.000Z';
    if (existing && existing.lastComputedAt !== EPOCH_ISO) {
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          composites: {
            ...s.portfolio.composites,
            [siteId]: { ...s.portfolio.composites[siteId]!, lastComputedAt: EPOCH_ISO },
          },
        },
      }));
      // X-SCORE-2 (scoring audit 2026-07-02) — PERSIST the epoch stamp. The
      // in-memory-only stamp meant every OTHER session/user of the company
      // kept serving the pre-invalidation composite for up to 24h
      // (isSiteCompositeStale is wall-clock-only and the persisted doc kept
      // its fresh lastComputedAt). Merge-write the epoch onto the persisted
      // doc so any session's next ensurePortfolioComposites pass recomputes.
      // Best-effort: offline/rules failures keep the in-memory stamp (this
      // session still recomputes; others catch up on the 24h window as before).
      // companyId source matches computeCompositeForSite's persist path.
      const companyId = get().companyId;
      if (companyId) {
        void (async () => {
          try {
            const { db } = await import('../firebase');
            const { doc, setDoc } = await import('firebase/firestore');
            await setDoc(
              doc(db, `customers/${companyId}/portfolio/composites/sites/${siteId}`),
              { lastComputedAt: EPOCH_ISO },
              { merge: true },
            );
          } catch (e) {
            console.warn(
              '[staleStampSiteComposite] persisted epoch stamp failed (in-memory stamp still active):',
              (e as Error)?.message,
            );
          }
        })();
      }
    }
  },

  ensurePortfolioComposites: async (customerId, opts) => {
    if (!customerId) return;
    // Sites and reports hydrate in parallel. Never derive or persist a
    // fingerprint from a partial workspace: an empty, not-yet-loaded report
    // array is not evidence that a site has no source reports.
    if (!get().siteRag.isSitesLoaded || !get().siteRag.isReportsLoaded) return;
    // Guard against double-run: if a portfolio recompute is already in flight,
    // it will populate everything — let it finish rather than racing it.
    if (get().portfolio.computing) return;

    const maxAgeMs = opts?.maxAgeMs ?? 24 * 60 * 60 * 1000; // 24h default

    // Pull persisted composites first so we only compute genuinely-missing/stale ones.
    await get().loadPortfolioComposites(customerId);

    // Re-check the guard — loadPortfolioComposites is async and a manual
    // recompute may have started in the meantime.
    if (
      get().portfolio.computing
      || !get().siteRag.isSitesLoaded
      || !get().siteRag.isReportsLoaded
    ) return;

    const composites = get().portfolio.composites;
    // Match the exact authorization-aware scope rendered by the dashboard.
    // The former own-company-only filter skipped descendant-company sites and
    // every cross-company site visible to admins, so Orbit could display their
    // persisted fp1 composites while this refresh pass never selected them.
    const inDashboardScope = dashboardScopePredicate(customerId, {
      isAdmin: get().userRole === 'admin',
      subtreeCompanies: get().subtreeCompanies,
    });
    const scoped = get().siteRag.sites.filter(inDashboardScope);

    // Fingerprint-aware staleness (scoring audit M-tier, 2026-07-02): the
    // shared guard compares the persisted composite's `inputsFingerprint`
    // against the CURRENT inputs (this session's reports + site SED pointer) —
    // unchanged inputs skip the recompute regardless of wall-clock age, and a
    // changed report set / SED version recomputes immediately instead of
    // waiting out the 24h window. Composites persisted before the fingerprint
    // fall back to the previous wall-clock check inside the guard.
    const { isSiteCompositeStale } = await import('../lib/portfolio/compositeScorer');
    // Refresh authoritative pointers even when report IDs/SED version did not
    // move (a failed/queued replacement must invalidate the same saved body).
    set((s) => ({ portfolio: { ...s.portfolio, computing: true } }));
    const { loadCompositeAnalysisContext } = await import('../services/compositeAnalysisContext');
    const contexts: Record<string, import('../lib/portfolio/compositeScorer').CompositeAnalysisContext> = {};
    let contextIndex = 0;
    await Promise.all(Array.from({ length: Math.min(5, scoped.length) }, async () => {
      while (contextIndex < scoped.length) {
        const site = scoped[contextIndex++];
        contexts[site.id] = await loadCompositeAnalysisContext(site);
      }
    }));
    set((s) => ({ portfolio: { ...s.portfolio, analysisContexts: { ...s.portfolio.analysisContexts, ...contexts } } }));
    // The list pass reads the open site's bodies first and warms the rest
    // behind the gate. A site whose current heads are still on their way
    // cannot have its inputs fingerprinted (metadata-only rows fingerprint
    // differently by design, so every such site would look stale and the
    // recompute would re-read the whole portfolio at once). Trust its
    // persisted composite until it settles, unless the composite is missing
    // or was epoch-stamped by a completed job.
    const allReports = get().siteRag.reports;
    const hydratedSiteIds = get().siteRag.hydratedReportSiteIds;
    const listedRows = allReports as unknown as Record<string, unknown>[];
    const needsCompute = scoped.filter((site) => {
      const existing = composites[site.id];
      if (!siteReportBodiesSettled(site, listedRows, hydratedSiteIds)) {
        return isSiteCompositeStale(existing, Number.POSITIVE_INFINITY, null);
      }
      return isSiteCompositeStale(existing, maxAgeMs, { site, reports: allReports, analysisContext: contexts[site.id] });
    });

    if (needsCompute.length === 0) {
      set((s) => ({ portfolio: { ...s.portfolio, computing: false } }));
      return;
    }

    // The staleness helper above is loaded asynchronously. Defend the write
    // boundary too in case auth/workspace hydration reset during that await.
    if (!get().siteRag.isSitesLoaded || !get().siteRag.isReportsLoaded) {
      set((s) => ({ portfolio: { ...s.portfolio, computing: false } }));
      return;
    }

    set((s) => ({ portfolio: { ...s.portfolio, computing: true, error: null } }));
    try {
      const concurrency = 5;
      let idx = 0;
      const workers = Array.from(
        { length: Math.min(concurrency, needsCompute.length) },
        async () => {
          while (idx < needsCompute.length) {
            const i = idx++;
            const site = needsCompute[i];
            if (!site) break;
            try {
              await get().computeCompositeForSite(site.id, contexts[site.id]);
            } catch (e) {
              console.warn(`[ensurePortfolioComposites] site ${site.id}:`, (e as Error)?.message);
            }
          }
        },
      );
      await Promise.all(workers);

      try {
        await get().recomputeClustering(customerId);
      } catch (e) {
        console.warn('[ensurePortfolioComposites] clustering:', (e as Error)?.message);
      }

      set((s) => ({
        portfolio: {
          ...s.portfolio,
          computing: false,
          lastComputedAt: new Date().toISOString(),
          error: null,
        },
      }));
    } catch (e) {
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          computing: false,
          error: (e as Error)?.message ?? 'Portfolio auto-compute failed',
        },
      }));
    }
  },

  clearPortfolioComposites: () => {
    set((s) => ({
      portfolio: {
        ...s.portfolio,
        composites: {},
        analysisContexts: {},
        lastComputedAt: null,
        computing: false,
        error: null,
      },
    }));
  },

  loadPortfolioComposites: async (customerId) => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;

    try {
      const { collection, getDocs } = await import('firebase/firestore');
      // Odd-segment collection ref: customers/{cid}/portfolio/composites/sites
      // (5 segments). Mirrors the write path above (…/composites/sites/{siteId}).
      const snap = await getDocs(collection(db, 'customers', customerId, 'portfolio', 'composites', 'sites'));
      const composites: Record<string, import('../types/portfolioComposite').SiteComposite> = {};
      let latest: string | null = null;
      for (const docSnap of snap.docs) {
        const data = docSnap.data() as import('../types/portfolioComposite').SiteComposite;
        composites[docSnap.id] = data;
        const ts = data.lastComputedAt;
        if (ts && (!latest || Date.parse(ts) > Date.parse(latest))) latest = ts;
      }
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          composites,
          lastComputedAt: latest ?? s.portfolio.lastComputedAt,
          error: null,
        },
      }));
    } catch (e) {
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          error: (e as Error)?.message ?? 'Failed to load portfolio composites',
        },
      }));
    }
  },

  loadDecisionBandConfig: async (customerId) => {
    set((s) => ({ portfolio: { ...s.portfolio, bandConfigLoading: true } }));
    try {
      const { DEFAULT_BAND_CONFIG, normalizeBandConfig } = await import('../lib/portfolio/decisionBands');
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        set((s) => ({
          portfolio: { ...s.portfolio, bandConfig: DEFAULT_BAND_CONFIG, bandConfigLoading: false },
        }));
        return DEFAULT_BAND_CONFIG;
      }
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'companies', customerId, 'config', 'bands'));
      const config = snap.exists()
        ? normalizeBandConfig(snap.data() as import('../lib/portfolio/decisionBands').DecisionBandConfig)
        : DEFAULT_BAND_CONFIG;
      set((s) => ({
        portfolio: { ...s.portfolio, bandConfig: config, bandConfigLoading: false, error: null },
      }));
      return config;
    } catch (e) {
      const { DEFAULT_BAND_CONFIG } = await import('../lib/portfolio/decisionBands');
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          bandConfig: DEFAULT_BAND_CONFIG,
          bandConfigLoading: false,
          error: (e as Error)?.message ?? 'Failed to load band config',
        },
      }));
      return DEFAULT_BAND_CONFIG;
    }
  },

  saveDecisionBandConfig: async (customerId, config) => {
    const { validateBandConfig } = await import('../lib/portfolio/decisionBands');
    const validation = validateBandConfig(config);
    if (!validation.valid) {
      throw new Error(validation.errors.join('; '));
    }

    const { auth, db } = await import('../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');

    const clean = JSON.parse(JSON.stringify(config, (_, v) => (v === undefined ? null : v)));
    await setDoc(doc(db, 'companies', customerId, 'config', 'bands'), clean, { merge: true });

    set((s) => ({
      portfolio: { ...s.portfolio, bandConfig: config, error: null },
    }));

    await get().computeCompositeForPortfolio();
  },

  loadGeoClusterConfig: async (customerId) => {
    try {
      const {
        DEFAULT_GEO_CLUSTER_CONFIG,
        normalizeGeoClusterConfig,
        GEO_CLUSTERS_DOC_ID,
      } = await import('../lib/portfolio/clusterTaxonomy');
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        set((s) => ({ portfolio: { ...s.portfolio, geoClusters: DEFAULT_GEO_CLUSTER_CONFIG } }));
        return DEFAULT_GEO_CLUSTER_CONFIG;
      }
      const { doc, getDoc } = await import('firebase/firestore');
      const snap = await getDoc(doc(db, 'customers', customerId, 'config', GEO_CLUSTERS_DOC_ID));
      const config = snap.exists()
        ? normalizeGeoClusterConfig(snap.data() as import('../lib/portfolio/clusterTaxonomy').GeoClusterConfig)
        : DEFAULT_GEO_CLUSTER_CONFIG;
      set((s) => ({ portfolio: { ...s.portfolio, geoClusters: config, error: null } }));
      return config;
    } catch (e) {
      const { DEFAULT_GEO_CLUSTER_CONFIG } = await import('../lib/portfolio/clusterTaxonomy');
      set((s) => ({
        portfolio: {
          ...s.portfolio,
          geoClusters: DEFAULT_GEO_CLUSTER_CONFIG,
          error: (e as Error)?.message ?? 'Failed to load geo clusters',
        },
      }));
      return DEFAULT_GEO_CLUSTER_CONFIG;
    }
  },

  saveGeoClusterConfig: async (customerId, config) => {
    const { validateGeoClusterConfig, GEO_CLUSTERS_DOC_ID } = await import('../lib/portfolio/clusterTaxonomy');
    const validation = validateGeoClusterConfig(config);
    if (!validation.valid) throw new Error(validation.errors.join('; '));

    const { auth, db } = await import('../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');

    const payload = {
      ...config,
      updatedAt: new Date().toISOString(),
    };
    const clean = JSON.parse(JSON.stringify(payload, (_, v) => (v === undefined ? null : v)));
    await setDoc(doc(db, 'customers', customerId, 'config', GEO_CLUSTERS_DOC_ID), clean, { merge: true });

    set((s) => ({ portfolio: { ...s.portfolio, geoClusters: payload, error: null } }));
    await get().recomputeClustering(customerId);
  },

  recomputeClustering: async (customerId) => {
    const { aggregatePortfolio } = await import('../lib/portfolio/clusterAggregator');
    const { resolveGeoClusters } = await import('../lib/portfolio/clusterTaxonomy');
    const geoConfig = get().portfolio.geoClusters ?? await get().loadGeoClusterConfig(customerId);
    const bandConfig = get().portfolio.bandConfig;
    const sites = get().siteRag.sites.filter(s => !s.companyId || s.companyId === customerId);
    const reports = get().siteRag.reports;
    const clustering = aggregatePortfolio(
      customerId,
      get().portfolio.composites,
      sites,
      reports,
      resolveGeoClusters(geoConfig),
      bandConfig,
    );
    set((s) => ({ portfolio: { ...s.portfolio, clustering, error: null } }));
  },

  loadRecommendationGraphForSite: async (siteId) => {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    try {
      const { doc, getDoc, collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
      const { AUDIT_CONFIG } = await import('../config/auditConfig');
      // #owner-path-sweep — read from the owner's path (shared sites
      // previously read an empty own-path doc and lost the roadmap).
      const snap = await getDoc(doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest`));
      if (!snap.exists()) return;
      const data = snap.data() as {
        graph?: import('../types/recommendationGraph').RecommendationGraph;
        lastBuiltAt?: string;
      };
      if (!data.graph) return;

      const { normalizeRecommendationGraphStatuses } = await import(
        '../lib/audit/recommendationStatus'
      );
      const graph = normalizeRecommendationGraphStatuses(data.graph);

      const snapQ = query(
        collection(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest/snapshots`),
        orderBy('builtAt', 'desc'),
        limit(AUDIT_CONFIG.GRAPH_SNAPSHOT_CAP),
      );
      const snapDocs = await getDocs(snapQ);
      const snapshots = snapDocs.docs.map((d) => d.data() as import('../types/recommendationGraph').RecommendationGraphSnapshot);

      set((state) => ({
        recommendationGraphBySite: {
          ...state.recommendationGraphBySite,
          [siteId]: {
            ...state.recommendationGraphBySite[siteId],
            graph,
            lastBuiltAt: data.lastBuiltAt ?? graph.builtAt,
            building: false,
            snapshots,
          },
        },
      }));
    } catch {
      /* cache load is best-effort */
    }
  },

  rebuildRecommendationGraph: async (siteId) => {
    const entry = get().recommendationGraphBySite[siteId];
    if (entry?.building) return null;

    set((state) => ({
      recommendationGraphBySite: {
        ...state.recommendationGraphBySite,
        [siteId]: {
          ...state.recommendationGraphBySite[siteId],
          building: true,
        },
      },
    }));

    try {
      const { roadmapSourceReports, roadmapSourceIdentity } = await import('../lib/audit/roadmapSourceReports');
      const selectedReports = () => roadmapSourceReports(get().siteRag.reports.filter((r) => r.siteId === siteId));
      const pendingBodies = selectedReports().filter(report => report._metadataOnly === true);
      const hydrated = await Promise.all(pendingBodies.map(report => report.id
        ? get().hydrateReportBody(report.id) : Promise.resolve(false)));
      if (hydrated.some(loaded => !loaded)) throw new Error('Could not load the selected report bodies. Retry roadmap extraction.');
      const reports = selectedReports();
      if (reports.some(report => report._metadataOnly === true)) {
        throw new Error('Selected report sources changed while loading. Retry roadmap extraction.');
      }
      const sourceIdentity = roadmapSourceIdentity(reports);
      const { normalizeRecommendationStatus } = await import('../lib/audit/recommendationStatus');
      const statusByNodeId: Record<string, import('../types/recommendationGraph').RecommendationStatus> = {};
      const statusByStableKey: Record<string, import('../types/recommendationGraph').RecommendationStatus> = {};
      const { buildRecommendationGraphFromReports, computeStableKey } = await import('../lib/audit/recommendationGraphBuilder');
      for (const n of entry?.graph?.nodes ?? []) {
        const normalized = normalizeRecommendationStatus(n.status);
        if (normalized !== 'not-started') statusByNodeId[n.id] = normalized;
        if (normalized !== 'not-started') statusByStableKey[computeStableKey(n.canonicalForm)] = normalized;
      }

      const graph = await buildRecommendationGraphFromReports(siteId, reports, {
        statusByNodeId,
        statusByStableKey,
      });

      const { AUDIT_CONFIG } = await import('../config/auditConfig');
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      const firestore = user ? await import('firebase/firestore') : null;
      if (roadmapSourceIdentity(selectedReports()) !== sourceIdentity) {
        throw new Error('Selected report sources changed during extraction. Retry roadmap extraction.');
      }
      const builtAt = graph.builtAt;
      const snapshot: import('../types/recommendationGraph').RecommendationGraphSnapshot = {
        id: builtAt,
        builtAt,
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        graph,
      };
      const priorSnapshots = entry?.snapshots ?? [];
      const snapshots = [snapshot, ...priorSnapshots].slice(0, AUDIT_CONFIG.GRAPH_SNAPSHOT_CAP);

      set((state) => ({
        recommendationGraphBySite: {
          ...state.recommendationGraphBySite,
          [siteId]: {
            graph,
            lastBuiltAt: builtAt,
            building: false,
            snapshots,
          },
        },
      }));

      if (user && firestore) {
        const { doc, setDoc } = firestore;
        await setDoc(
          doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest`),
          stripUndefined({ graph, lastBuiltAt: builtAt, updatedAt: builtAt }),
          { merge: true },
        );
        await setDoc(
          doc(
            db,
            `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest/snapshots`,
            snapshot.id.replace(/:/g, '-'),
          ),
          stripUndefined(snapshot),
        );
      }

      // R-131-ROADMAP-FIX-D — audit re-extract so Activity tab can measure stale-extractor retries.
      try {
        const site = get().siteRag.sites.find((s) => s.id === siteId);
        const prior = entry?.graph;
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'roadmap.extracted',
          target: { type: 'site', id: siteId, label: site?.name },
          before: {
            nodeCount: prior?.nodes.length ?? 0,
            edgeCount: prior?.edges.length ?? 0,
            reportIds: prior?.reportIds ?? [],
          },
          after: {
            nodeCount: graph.nodes.length,
            edgeCount: graph.edges.length,
            reportIds: graph.reportIds,
          },
          metadata: {
            unscheduledCount: graph.nodes.filter((n) => n.phase === 'unscheduled').length,
          },
        });
      } catch { /* best-effort */ }

      return graph;
    } catch (err) {
      set((state) => ({
        recommendationGraphBySite: {
          ...state.recommendationGraphBySite,
          [siteId]: {
            ...state.recommendationGraphBySite[siteId],
            building: false,
          },
        },
      }));
      throw err;
    }
  },

  incrementalUpdateRecommendationGraph: async (siteId) => {
    const entry = get().recommendationGraphBySite[siteId];
    if (entry?.building) return null;
    const existingGraph = entry?.graph;
    // Nothing to merge into — caller should use rebuild for a first build.
    if (!existingGraph) return null;

    const { roadmapSourceReportDelta, roadmapSourceIdentity } = await import('../lib/audit/roadmapSourceReports');
    const { newReports, retiredReportIds } = roadmapSourceReportDelta(
      existingGraph.reportIds,
      get().siteRag.reports.filter((r) => r.siteId === siteId),
    );
    // Appending cannot retract advice from replaced or deleted report sources.
    // The normal rebuild preserves statuses by action identity and snapshots.
    if (retiredReportIds.length > 0) return get().rebuildRecommendationGraph(siteId);
    // No delta — return the existing graph untouched.
    if (newReports.length === 0) return existingGraph;

    set((state) => ({
      recommendationGraphBySite: {
        ...state.recommendationGraphBySite,
        [siteId]: {
          ...state.recommendationGraphBySite[siteId],
          building: true,
        },
      },
    }));

    try {
      const pendingBodies = roadmapSourceReportDelta(
        existingGraph.reportIds,
        get().siteRag.reports.filter(report => report.siteId === siteId),
      ).reports.filter(report => report._metadataOnly === true);
      const hydrated = await Promise.all(pendingBodies.map(report => report.id
        ? get().hydrateReportBody(report.id) : Promise.resolve(false)));
      if (hydrated.some(loaded => !loaded)) throw new Error('Could not load the selected report bodies. Retry roadmap extraction.');
      const currentDelta = roadmapSourceReportDelta(
        existingGraph.reportIds,
        get().siteRag.reports.filter((report) => report.siteId === siteId),
      );
      if (currentDelta.retiredReportIds.length > 0 || currentDelta.reports.some(report => report._metadataOnly === true)) {
        throw new Error('Selected report sources changed while loading. Retry roadmap extraction.');
      }
      const sourceIdentity = roadmapSourceIdentity(currentDelta.reports);
      const { embedText } = await import('../lib/embeddings/embedText');
      const embedder = (text: string) =>
        embedText(text, { taskType: 'SEMANTIC_SIMILARITY' });
      const { appendRecommendationsFromReports } = await import('../lib/audit/recommendationGraphBuilder');
      const { graph } = await appendRecommendationsFromReports(
        existingGraph,
        currentDelta.newReports,
        embedder,
      );

      const { AUDIT_CONFIG } = await import('../config/auditConfig');
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      const firestore = user ? await import('firebase/firestore') : null;
      if (roadmapSourceIdentity(get().siteRag.reports.filter(report => report.siteId === siteId)) !== sourceIdentity) {
        throw new Error('Selected report sources changed during extraction. Retry roadmap extraction.');
      }
      const builtAt = graph.builtAt;
      const snapshot: import('../types/recommendationGraph').RecommendationGraphSnapshot = {
        id: builtAt,
        builtAt,
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        graph,
      };
      const priorSnapshots = entry?.snapshots ?? [];
      const snapshots = [snapshot, ...priorSnapshots].slice(0, AUDIT_CONFIG.GRAPH_SNAPSHOT_CAP);

      set((state) => ({
        recommendationGraphBySite: {
          ...state.recommendationGraphBySite,
          [siteId]: {
            graph,
            lastBuiltAt: builtAt,
            building: false,
            snapshots,
          },
        },
      }));

      if (user && firestore) {
        try {
          const { doc, setDoc } = firestore;
          await setDoc(
            doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest`),
            stripUndefined({ graph, lastBuiltAt: builtAt, updatedAt: builtAt }),
            { merge: true },
          );
          await setDoc(
            doc(
              db,
              `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest/snapshots`,
              snapshot.id.replace(/:/g, '-'),
            ),
            stripUndefined(snapshot),
          );
        } catch (persistErr) {
          // Non-fatal: the in-memory graph is updated; only persistence failed.
          console.warn('[roadmap] incremental update persist failed (non-fatal)', persistErr);
        }
      }

      // Audit the incremental merge so the Activity tab can track delta extractions.
      try {
        const site = get().siteRag.sites.find((s) => s.id === siteId);
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'roadmap.extracted',
          target: { type: 'site', id: siteId, label: site?.name },
          before: {
            nodeCount: existingGraph.nodes.length,
            edgeCount: existingGraph.edges.length,
            reportIds: existingGraph.reportIds,
          },
          after: {
            nodeCount: graph.nodes.length,
            edgeCount: graph.edges.length,
            reportIds: graph.reportIds,
          },
          metadata: {
            incremental: true,
            newReportIds: currentDelta.newReports.map((r) => r.id).filter(Boolean),
            addedNodeCount: graph.nodes.length - existingGraph.nodes.length,
          },
        });
      } catch { /* best-effort */ }

      return graph;
    } catch (err) {
      set((state) => ({
        recommendationGraphBySite: {
          ...state.recommendationGraphBySite,
          [siteId]: {
            ...state.recommendationGraphBySite[siteId],
            building: false,
          },
        },
      }));
      throw err;
    }
  },

  updateRecommendationStatus: async (siteId, nodeId, status) => {
    const entry = get().recommendationGraphBySite[siteId];
    if (!entry?.graph) return;

    const { normalizeRecommendationStatus } = await import('../lib/audit/recommendationStatus');
    const normalized = normalizeRecommendationStatus(status);

    const graph: import('../types/recommendationGraph').RecommendationGraph = {
      ...entry.graph,
      nodes: entry.graph.nodes.map((n) => (n.id === nodeId ? { ...n, status: normalized } : n)),
    };

    const { reconcileGraph } = await import('../lib/audit/recommendationGraphBuilder');
    const cycleFindings = graph.findings.filter((f) => f.kind === 'CYCLE_BROKEN');
    graph.findings = [...cycleFindings, ...reconcileGraph(graph)];

    set((state) => ({
      recommendationGraphBySite: {
        ...state.recommendationGraphBySite,
        [siteId]: {
          ...state.recommendationGraphBySite[siteId],
          graph,
        },
      },
    }));

    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      await setDoc(
        doc(db, `${ownerSiteDocPath(get(), siteId, user.uid)}/recommendationGraph/latest`),
        stripUndefined({ graph, updatedAt: new Date().toISOString() }),
        { merge: true },
      );
    } catch {
      /* best-effort persist */
    }
  },

  deleteRagReport: async (id, mode = 'report-only') => {
    // Workflow Prompt 7 — cascade modes.
    //
    // PH-A2.1 — capture-and-restore. Previously the optimistic local
    // mutation (filter the report out of state) ran BEFORE the Firestore
    // auth check / delete attempt. A failed delete left a "deleted" report
    // hidden from the UI while still alive in Firestore, so the next reload
    // would re-surface it without warning. Snapshot the pre-delete report
    // list + activeReportId, mutate optimistically, then restore on any
    // failure path. Mirrors the rollback pattern already used by deleteSite.
    const reportToDelete = get().siteRag.reports.find(r => r.id === id);
    if (!reportToDelete) {
      return { sedRemoved: false, docsRemoved: 0, bytesFreed: 0 };
    }
    const allReports = get().siteRag.reports;
    const reportsSnapshot = allReports;
    const activeReportIdSnapshot = get().siteRag.activeReportId;
    // PH-A2.2 — snapshot the parent site so we can walk site.documents via
    // documentRefs without re-reading state mid-cascade.
    const siteSnapshot = reportToDelete.siteId
      ? get().siteRag.sites.find(s => s.id === reportToDelete.siteId)
      : undefined;

    set((state) => ({
      siteRag: {
        ...state.siteRag,
        reports: state.siteRag.reports.filter(r => r.id !== id),
        activeReportId: state.siteRag.activeReportId === id ? null : state.siteRag.activeReportId
      }
    }));

    const rollback = () => {
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          reports: reportsSnapshot,
          activeReportId: activeReportIdSnapshot,
        },
      }));
    };

    let sedRemoved = false;
    let docsRemoved = 0;
    let bytesFreed = 0;

    // Delete from Firestore if logged in
    const { auth, db, handleFirestoreError, OperationType } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) {
      // PH-A2.1 — unauthenticated callers must NOT see a phantom-delete.
      // Restore the snapshot so the UI reflects truth.
      rollback();
      return { sedRemoved: false, docsRemoved: 0, bytesFreed: 0 };
    }
    try {
      const { doc, deleteDoc } = await import('firebase/firestore');
      // #owner-path-sweep-p2 — delete at the OWNER's path (where the doc actually
      // lives). For own reports the owner falls back to user.uid — byte-identical.
      await deleteDoc(doc(db, reportOwnerDocPath(reportToDelete, user.uid, id)));
      // Always delete the report's GCS payload.
      try { await authedFetch('/api/storage/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: reportOwnerGcsPath(reportToDelete, user.uid, id) }) }); } catch { /* best-effort */ }

      // Cascade — SED removal. Refuse if any other report still references this sedId.
      // NOTE: the SED GCS blob lives at the site's sedStoragePath (set by the server
      // bootstrap under users/{ownerUid}/sites/{siteId}/evidence/{sedId}.json) — NOT
      // at users/{currentUid}/evidence/{sedId}.json. The legacy path here is kept for
      // backward-compat with older SEDs that may have been bootstrapped client-side
      // before the server-bootstrap path existed; correctness of the delete depends on
      // sedStoragePath being present on the owning site (which the cascade code above
      // could consult — deferred to a follow-up, tracked as RULES GAP #2).
      if ((mode === 'with-evidence' || mode === 'with-everything') && reportToDelete.sedId) {
        const { isSedStillReferenced } = await import('../lib/reportDeleteCascade');
        const ref = isSedStillReferenced(reportToDelete, allReports);
        if (ref.stillReferenced) {
          console.warn(`[Delete] SED ${reportToDelete.sedId} kept — referenced by ${ref.referencingReportIds.length} other report(s).`);
        } else {
          try {
            // For company-shared sites the owner's uid is in site._ownerUid (which is
            // also stored on the report via report._ownerUid after this sweep). Use it
            // so the GCS delete targets the correct owner bucket path.
            const sedOwnerUid = reportOwnerUid(reportToDelete, user.uid);
            await authedFetch('/api/storage/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: `users/${sedOwnerUid}/evidence/${reportToDelete.sedId}.json` }) });
            sedRemoved = true;
            bytesFreed += 50_000; // rough estimate; we don't HEAD the object
          } catch (e) {
            console.warn(`[Delete] Failed to remove SED ${reportToDelete.sedId}:`, e);
          }
        }
      }

      // Cascade — uploaded documents.
      // PH-A2.2 — walk `site.documents` filtered by `report.documentRefs`
      // (post-IC source of truth) instead of the deprecated inline
      // `report.documents`. Legacy reports without documentRefs fall
      // through to the inline list for back-compat.
      if (mode === 'with-everything') {
        const docsForReport: import('../types/siteRag').SiteDocument[] = [];
        if (reportToDelete.documentRefs?.length && siteSnapshot) {
          const byId = new Map((siteSnapshot.documents ?? []).map(d => [d.id, d]));
          for (const ref of reportToDelete.documentRefs) {
            if (!ref.userIncluded) continue;
            const sd = byId.get(ref.docId);
            if (sd) docsForReport.push(sd);
          }
        } else if (reportToDelete.documents?.length) {
          docsForReport.push(...reportToDelete.documents);
        }
        for (const d of docsForReport) {
          if (!d.storagePath) continue;
          try {
            await authedFetch('/api/storage/delete', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: d.storagePath }) });
            docsRemoved++;
            bytesFreed += d.sizeBytes || 0;
          } catch (e) {
            console.warn(`[Delete] Failed to remove doc ${d.fileName}:`, e);
          }
        }
      }

      // Unpin from parent site if it was pinned
      if (siteSnapshot && siteSnapshot.pinnedReportIds?.includes(id)) {
        await get().updateSite(siteSnapshot.id, {
          pinnedReportIds: siteSnapshot.pinnedReportIds.filter(pid => pid !== id)
        });
      }
    } catch (error) {
      // PH-A2.1 — Firestore delete itself failed. Restore the snapshot
      // so the deleted report reappears in the UI rather than vanishing
      // silently; the user must see the failure before retrying.
      rollback();
      handleFirestoreError(error, OperationType.DELETE, reportOwnerDocPath(reportToDelete, user.uid, id));
    }

    return { sedRemoved, docsRemoved, bytesFreed };
  },

  regenerateNarrativeOnly: async (reportId: string): Promise<boolean> => {
    const { toast } = await import('sonner');
    const r = get().siteRag.reports.find(x => x.id === reportId);
    if (!r) {
      toast.error('Report not found.');
      return false;
    }
    if (r.reportType === 'water-resource') {
      toast.error('Water Resource Assessment is retired. Update the water screen through the Early Screening Assessment.');
      return false;
    }

    // Fire a loading toast immediately so the user sees feedback the
    // instant they click. Stage-2 narrative calls take 30-60s; without
    // this the click looked like a no-op until the result landed.
    // Every downstream success / error toast in this function reuses
    // the same `narrative-refresh` id so it REPLACES this row rather
    // than stacking on top.
    toast.loading('Refreshing narrative…', { id: 'narrative-refresh' });

    try {
      const gemini = await import('../services/geminiService');
      const { createTelemetryCollector } = await import('../services/generationTelemetry');
      const reportType = (r as any).reportType as string | undefined;
      const tp = r.typedPreferences;

      // RN-2 (RTB-FOLLOWUP-AUTO-FILL-NEXT-REGEN, 2026-05-19) — read the
      // one-shot section-regen hints stamped at render-time when a
      // placeholder fired for `reason === 'data-too-thin'`. We prepend
      // the strengthened prompt prefix to the sedText (the evidence
      // blob Gemini reads first) so the next gen produces fuller
      // content for the hinted sections. The hint is cleared on success
      // below so the prefix is one-shot — a follow-up regen without a
      // new placeholder render gets the default prompt.
      const hintsAtStart = r.sectionRegenHints;
      const baseSedText = r.sedText ?? '';
      let sedTextForRegen = baseSedText;
      if (hintsAtStart && Object.keys(hintsAtStart).length > 0) {
        const { applyHintsToPrompt } = await import('../lib/sectionRegenHints');
        sedTextForRegen = applyHintsToPrompt(baseSedText, hintsAtStart);
      }

      const siteData: any = {
        ...(r.datasets ?? {}),
        sedText: sedTextForRegen,
        adamsDocuments: (r as any).adamsDocuments,
      };
      const opts: any = {
        projectIntent: (r as any).projectIntent,
        siteArea: r.siteArea,
        documents: r.documents,
        regulatory: undefined,
        knowledgeRefStartIdx: 0,
        // Was `{ calls: [] }` — a plain object, not a TelemetrySink. The SDK's
        // recordCall() then tried `telemetry.record(...)` and threw
        // "telemetry.record is not a function". Use the real collector.
        telemetry: createTelemetryCollector(),
        throwOnFailure: true,
      };
      if (reportType === 'test-fit'
        && r.generationImpactScenarioAtGen
        && r.generationImpactProjection?.testFitFootprint) {
        // A narrative refresh edits the archived report version; it must use
        // that report's frozen scenario/projection, never mutable live intent
        // or the generic targetFootprintAcres heuristic.
        opts.generationImpactScenario = r.generationImpactScenarioAtGen;
        opts.generationImpactTestFitProjection = r.generationImpactProjection.testFitFootprint;
      }

      let data: any = null;
      try {
        if (reportType === 'environmental') {
          data = await gemini.generateEnvironmentalReportContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.environmental });
        } else if (reportType === 'power-availability') {
          data = await gemini.generatePowerReportContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.['power-availability'] });
        } else if (reportType === 'water-resource') {
          data = await gemini.generateWaterReportContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.['water-resource'] });
        } else if (reportType === 'test-fit') {
          data = await gemini.generateTestFitContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.['test-fit'] });
        } else if (reportType === 'interconnection') {
          data = await gemini.generateInterconnectionContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.interconnection });
        } else {
          data = await gemini.generateReportContent(r.siteName, r.address, siteData, { ...opts, typedPreferences: tp?.feasibility });
        }
      } catch (e) {
        const { classifyNarrativeFailure, narrativeFailureToastMessage } = await import('../lib/classifyNarrativeFailure');
        const { isNarrativeGenerationError } = await import('../lib/narrativeGenerationError');
        const classified = isNarrativeGenerationError(e)
          ? { kind: e.kind, userMessage: e.message }
          : classifyNarrativeFailure({ error: (e as Error)?.message });
        toast.error(narrativeFailureToastMessage(classified), { id: 'narrative-refresh' });
        return false;
      }
      if (!data) {
        const { classifyNarrativeFailure, narrativeFailureToastMessage } = await import('../lib/classifyNarrativeFailure');
        toast.error(narrativeFailureToastMessage(classifyNarrativeFailure({ error: undefined })), { id: 'narrative-refresh' });
        return false;
      }

      // Citation validator coverage (Reader Polish follow-up). The
      // legacy refresh path silently accepted hallucinated [Ref N]
      // markers because validation only ran on the full generation
      // path. Scan the regen response against the report's existing
      // references.length; scrub any out-of-bounds markers to
      // "[Ref ?]" and audit the substitution count. The validator
      // checks n < 1 || n > references.length — i.e. existence, not
      // just an absurd cap.
      try {
        const { scanCitations, replaceOutOfBoundsCitations, buildCitationAudit } = await import(
          '../services/citationValidation'
        );
        const refCount = r.references?.length ?? 0;
        const scan = scanCitations(data, refCount);
        if (scan.outOfBounds.length > 0) {
          // Mutate `data` in place — replaceOutOfBoundsCitations
          // walks the structure and replaces "[Ref N]" → "[Ref ?]"
          // for every N outside [1, refCount]. The narrative shape
          // is unchanged so the merge below copies fields normally.
          replaceOutOfBoundsCitations(data, refCount);
          const { emitAuditEvent } = await import('../lib/auditLogClient');
          void emitAuditEvent({
            action: 'narrative-citations-scrubbed',
            target: { type: 'report', id: r.id, label: r.siteName },
            metadata: {
              reportType,
              referenceCount: refCount,
              outOfBoundsCount: scan.outOfBounds.length,
              outOfBoundsRefs: scan.outOfBounds,
              surface: 'regenerate-narrative',
            },
          });
          console.warn(
            `[regenerateNarrativeOnly] scrubbed ${scan.outOfBounds.length} out-of-bounds citation(s) [Ref ${scan.outOfBounds.join(', ')}] against ${refCount} references`,
          );
        }
        // Persist the audit on the report so the quality appendix
        // reflects the regen-time scan.
        const auditedScan = scanCitations(data, refCount);
        const citationAudit = buildCitationAudit(auditedScan, refCount);
        (data as { __citationAudit?: typeof citationAudit }).__citationAudit = citationAudit;
      } catch (e) {
        console.warn('[regenerateNarrativeOnly] citation validation failed', e);
      }

      // Merge whatever narrative fields the AI returned. Anything missing
      // keeps its prior value — we never blow away SED-derived deterministic
      // content with a partial AI response.
      const updates: Partial<SiteRagReport> = {};
      const copyIfPresent = <K extends keyof SiteRagReport>(key: K): void => {
        if (data[key as string] !== undefined && data[key as string] !== null) {
          (updates as any)[key] = data[key as string];
        }
      };
      // EXEC-SUMMARY-GUARD (2026-06-03) — reject a regenerated
      // executiveSummary/summary that is actually the user's edit
      // INSTRUCTION rather than prose (field incident: a regen stored
      // "Remove fabricated tribal consultation requirements." verbatim
      // as the executiveSummary + summary). The MEL strip targets QA
      // self-directives, not short imperative reviewer commands, so the
      // directive slipped through. When the candidate looks like an
      // instruction we drop it and keep the prior content; the regen of
      // the OTHER (clean) fields still lands. The user's instruction is
      // surfaced to the prompt via `regenerationReason`, so the verbatim
      // echo is checked against it too.
      {
        const { detectEditInstruction } = await import('../lib/editInstructionGuard');
        const candidateExec = data.executiveSummary;
        const verdict = detectEditInstruction(candidateExec, r.regenerationReason);
        if (verdict.isInstruction) {
          console.warn(
            '[regenerateNarrativeOnly] rejected executiveSummary that looked like an edit instruction',
            { reportId, reason: verdict.reason, candidate: String(candidateExec).slice(0, 200) },
          );
          try {
            const { emitAuditEvent } = await import('../lib/auditLogClient');
            void emitAuditEvent({
              action: 'narrative-section-instruction-rejected',
              target: { type: 'report', id: reportId, label: r.siteName },
              metadata: {
                sectionId: 'executiveSummary',
                reason: verdict.reason,
                candidate: String(candidateExec).slice(0, 500),
                surface: 'regenerate-narrative',
              },
            });
          } catch { /* audit best-effort — never mask the regen */ }
          // Null out so copyIfPresent skips it and `summary` is not
          // overwritten with the instruction below. Prior content stays.
          data.executiveSummary = null;
        }
      }
      // Feasibility narrative fields.
      copyIfPresent('executiveSummary');
      if (data.executiveSummary) updates.summary = data.executiveSummary;
      copyIfPresent('sitingNotes');
      copyIfPresent('climateSevereWeather');
      copyIfPresent('infrastructureInterconnection');
      copyIfPresent('btmOptions');
      copyIfPresent('gridStudy');
      copyIfPresent('transportStudy');
      copyIfPresent('connectivityStudy');
      copyIfPresent('developmentPathway');
      copyIfPresent('projectIntentOverview');
      copyIfPresent('constructabilityAssessment');
      copyIfPresent('costRangeEstimate');
      copyIfPresent('bopEconomics');
      copyIfPresent('maxCampusCapacity');
      copyIfPresent('conclusion');
      // Typed sections — copyIfPresent is type-safe via Partial<SiteRagReport>.
      for (const key of [
        'powerAnalysis', 'btmDetailedAnalysis', 'gridVsBtmComparison', 'lcoeDetailedAnalysis',
        'nepaScreening', 'ecologicalAssessment', 'waterResources', 'airQualityEmissions',
        'floodNaturalHazards', 'culturalHistorical', 'landUseZoning', 'noiseVisualImpact',
        'mitigationPlan', 'permittingPathway', 'environmentalRiskMatrix',
        'waterAnalysis', 'testFitAnalysis', 'interconnectionAnalysis',
      ] as const) {
        copyIfPresent(key as keyof SiteRagReport);
      }
      // Risks + recommendations come back in their assembled shape; map them.
      if (Array.isArray(data.developmentRisks)) {
        updates.developmentRisks = data.developmentRisks;
        updates.risks = data.developmentRisks.map((dr: any) => ({
          label: dr.category, category: 'execution', severity: (dr.riskLevel ?? 'medium').toLowerCase(),
          impact: dr.reason ?? '', mitigation: dr.action ?? '',
        }));
      }
      if (Array.isArray(data.recommendations)) updates.recommendations = data.recommendations;

      // Clear the degraded flag, bump minor version, append refresh reason.
      updates.degradedMode = false;
      updates.degradedReason = undefined as unknown as string;
      updates.minorVersion = (r.minorVersion ?? 0) + 1;
      updates.regenerationReason = r.regenerationReason
        ? `${r.regenerationReason}; narrative-refresh`
        : 'narrative-refresh';
      // Persist the regen-time citation audit so the report's
      // quality appendix reflects this refresh.
      const refreshedAudit = (data as { __citationAudit?: import('../types/siteRag').CitationAudit }).__citationAudit;
      if (refreshedAudit) {
        updates.citationAudit = refreshedAudit;
      }

      // RN-2 (RTB-FOLLOWUP-AUTO-FILL-NEXT-REGEN) — one-shot hint
      // consumption. Clear every hint that was active at the START
      // of this regen call (we don't touch hints stamped DURING the
      // regen — those should fire on the next rerun). Persisting an
      // empty object would still be a hint shape; the helper returns
      // `undefined` when the map empties so the field unsets cleanly.
      if (hintsAtStart && Object.keys(hintsAtStart).length > 0) {
        const { clearHintForSection } = await import('../lib/sectionRegenHints');
        let cleared: typeof hintsAtStart | undefined = hintsAtStart;
        for (const sectionId of Object.keys(hintsAtStart)) {
          cleared = clearHintForSection(cleared, sectionId);
        }
        // `cleared` is `undefined` after every key removed → unset on
        // persistence. Cast handles the optional-field-removal shape.
        (updates as Partial<SiteRagReport>).sectionRegenHints = cleared as undefined;
      }

      if (reportType === 'test-fit'
        && r.generationImpactScenarioAtGen
        && r.generationImpactProjection?.testFitFootprint) {
        const candidate = typeof structuredClone === 'function'
          ? structuredClone(r)
          : JSON.parse(JSON.stringify(r)) as SiteRagReport;
        Object.assign(candidate, updates);
        const { reconcileTestFitReportWithGenerationImpact } = await import('../lib/generationImpact/testFitProjection');
        reconcileTestFitReportWithGenerationImpact(
          candidate,
          r.generationImpactScenarioAtGen,
          r.generationImpactProjection.testFitFootprint,
        );
        // Reconciliation sanitizes every Test Fit prose carrier, including
        // claims/editorial copy that exports outside testFitAnalysis. Copy
        // those normalized fields back into this partial update before the
        // existing durable report write.
        for (const key of [
          'testFitAnalysis', 'summary', 'executiveSummary', 'projectIntentOverview',
          'sitingNotes', 'constructabilityAssessment', 'costRangeEstimate',
          'bopEconomics', 'maxCampusCapacity', 'recommendations', 'risks',
          'structuredFindings', 'contextualKeyFindings', 'claims', 'conclusion',
          'editorialNarrative',
        ] as const) {
          if (candidate[key] !== undefined) (updates as Record<string, unknown>)[key] = candidate[key];
        }
      }

      await get().updateRagReport(reportId, updates);
      toast.success('Narrative refreshed.', { id: 'narrative-refresh' });
      return true;
    } catch (e) {
      console.warn('regenerateNarrativeOnly failed', e);
      const { classifyNarrativeFailure, narrativeFailureToastMessage } = await import('../lib/classifyNarrativeFailure');
      const { isNarrativeGenerationError } = await import('../lib/narrativeGenerationError');
      const classified = isNarrativeGenerationError(e)
        ? { kind: e.kind, userMessage: e.message }
        : classifyNarrativeFailure({ error: (e as Error)?.message });
      toast.error(narrativeFailureToastMessage(classified), { id: 'narrative-refresh' });
      return false;
    }
  },

  triggerSectionRegen: async (reportId: string, sectionId: string): Promise<boolean> => {
    // MEL-MR-2 — admin-only single-section regen. Mirrors the MR-1
    // gen-tail wire-up (regenSectionsAfterMetaLeak + the geminiService
    // helper) but takes a single explicit sectionId rather than walking
    // the leak warning. Fires ONE call, patches in place, persists via
    // updateRagReport. Never throws.
    const { toast } = await import('sonner');
    const report = get().siteRag.reports.find(r => r.id === reportId);
    if (!report) {
      toast.error('Report not found.');
      return false;
    }
    const toastId = `section-regen-${reportId}-${sectionId}`;
    toast.loading('Regenerating section…', { id: toastId });
    try {
      const { REGEN_ELIGIBLE_SECTIONS } = await import('../services/sectionRegenAfterMetaLeak');
      const eligible = (REGEN_ELIGIBLE_SECTIONS as ReadonlyArray<string>).includes(sectionId);
      if (!eligible) {
        toast.error('This section cannot be regenerated.', { id: toastId });
        return false;
      }
      const { regenSectionAfterMetaLeak } = await import('../services/geminiService');
      const { stripMetaInstructions } = await import('../services/narrativeHygiene');
      // Read the family that fired for this section (defensive: if the
      // warning is missing, fall back to 'manual-regen' as the family
      // tag so the audit log still tells us what triggered the call).
      const warnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
      const leakWarning = warnings.find(w => w.type === 'meta-instruction-leak') as
        | { samples?: ReadonlyArray<{ sectionId: string; family: string }> }
        | undefined;
      const matchedFamily = leakWarning?.samples?.find(s => s.sectionId === sectionId)?.family
        ?? 'manual-regen';
      const currentBody = (report as unknown as Record<string, unknown>)[sectionId];
      const sectionLabel =
        sectionId === 'executiveSummary' ? 'Executive Summary'
          : sectionId === 'summary' ? 'Cover Summary'
            : sectionId === 'sitingNotes' ? 'Siting Notes'
              : sectionId;
      const newBody = await regenSectionAfterMetaLeak({
        reportId,
        sectionId: sectionId as 'executiveSummary' | 'summary' | 'sitingNotes',
        sectionLabel,
        currentBody: typeof currentBody === 'string' ? currentBody : '',
        family: matchedFamily,
        sedContext: report.sedText,
        reportTypeId: report.reportType,
        projectIntent: report.projectIntentAtGen,
      });
      if (!newBody || newBody.trim().length === 0) {
        try {
          const { emitAuditEvent } = await import('../lib/auditLogClient');
          void emitAuditEvent({
            action: 'narrative-section-regen-failure',
            target: { type: 'report', id: reportId },
            metadata: {
              sectionId,
              family: matchedFamily,
              beforeChars: typeof currentBody === 'string' ? currentBody.length : 0,
              afterChars: typeof currentBody === 'string' ? currentBody.length : 0,
              reason: 'manual regen returned empty',
              trigger: 'manual',
            },
          });
        } catch { /* audit best-effort */ }
        toast.error('Regenerate failed. Try again in a moment.', { id: toastId });
        return false;
      }
      // EXEC-SUMMARY-GUARD (2026-06-03) — reject a regenerated body that
      // is actually an edit INSTRUCTION rather than prose. The MEL retry
      // forbids QA self-directives but a short imperative reviewer
      // command ("Remove …", "Add …") can still echo back. When it does,
      // keep the prior content untouched and audit the rejection.
      {
        const { detectEditInstruction } = await import('../lib/editInstructionGuard');
        const verdict = detectEditInstruction(newBody, report.regenerationReason);
        if (verdict.isInstruction) {
          console.warn(
            '[triggerSectionRegen] rejected regenerated body that looked like an edit instruction',
            { reportId, sectionId, reason: verdict.reason, candidate: newBody.slice(0, 200) },
          );
          try {
            const { emitAuditEvent } = await import('../lib/auditLogClient');
            void emitAuditEvent({
              action: 'narrative-section-instruction-rejected',
              target: { type: 'report', id: reportId, label: report.siteName },
              metadata: {
                sectionId,
                reason: verdict.reason,
                candidate: newBody.slice(0, 500),
                surface: 'trigger-section-regen',
              },
            });
          } catch { /* audit best-effort */ }
          toast.error('Regenerate produced an instruction, not prose. Kept prior content.', { id: toastId });
          return false;
        }
      }
      // Defensive re-strip + persistence. Build a patched report so the
      // stripMetaInstructions pass can clean a re-leaked retry.
      let patched: SiteRagReport = {
        ...report,
        [sectionId]: newBody,
      } as SiteRagReport;
      try {
        patched = stripMetaInstructions(patched);
      } catch {
        // Defensive strip failure isn't fatal — patched body is still
        // shippable (the original leak warning persists, the chip stays).
      }
      const beforeChars = typeof currentBody === 'string' ? currentBody.length : 0;
      const afterChars = ((patched as unknown as Record<string, unknown>)[sectionId] as string | undefined)?.length ?? 0;
      await get().updateRagReport(reportId, {
        [sectionId]: (patched as unknown as Record<string, unknown>)[sectionId],
        qualityWarnings: patched.qualityWarnings,
      } as Partial<SiteRagReport>);
      try {
        const { emitAuditEvent } = await import('../lib/auditLogClient');
        void emitAuditEvent({
          action: 'narrative-section-regen-success',
          target: { type: 'report', id: reportId },
          metadata: {
            sectionId,
            family: matchedFamily,
            beforeChars,
            afterChars,
            trigger: 'manual',
          },
        });
      } catch { /* audit best-effort */ }
      toast.success('Section regenerated.', { id: toastId });
      return true;
    } catch (e) {
      console.warn('triggerSectionRegen failed', e);
      toast.error('Regenerate failed. Try again in a moment.', { id: toastId });
      return false;
    }
  },

  // ── RN-1 (wave-16) ───────────────────────────────────────────────
  // Per-section regen wrapper. Today's narrative pipeline regenerates
  // the FULL set of feasibility/typed narrative fields in one shot —
  // we don't yet split by sectionId. Surfacing this as a separate
  // store action lets the placeholder UI plumb a "Regenerate" button
  // without binding it to the more user-loaded "Refresh narrative"
  // entry point, and stamps the sectionId in regenerationReason so
  // the audit trail records which placeholder triggered the call.
  regenerateSection: async (reportId, sectionId) => {
    const trimmedSection = (sectionId ?? '').trim();
    if (!trimmedSection) return false;
    const ok = await get().regenerateNarrativeOnly(reportId);
    if (!ok) return false;
    // Stamp the sectionId on the regenerationReason audit so the
    // edit-history surface can identify which empty placeholder
    // click drove the regen. Best-effort — the underlying narrative
    // refresh already succeeded.
    try {
      const r = get().siteRag.reports.find((x) => x.id === reportId);
      if (r) {
        const prior = r.regenerationReason ?? '';
        const note = `section-regen:${trimmedSection}`;
        const merged = prior.includes(note) ? prior : (prior ? `${prior}; ${note}` : note);
        await get().updateRagReport(reportId, { regenerationReason: merged });
      }
    } catch (e) {
      // Never let an audit-stamp failure mask a successful regen.
      console.warn('[regenerateSection] audit-stamp failed', (e as Error)?.message);
    }
    return true;
  },

  // ── RN-2 (wave-16) ───────────────────────────────────────────────
  // Apply a single provider-derived value to an empty section. Pulled
  // from the coverage matrix's auto-fill source — the chip is only
  // ever surfaced when the matrix already carries a non-missing
  // fillSource for the relevant requirement, so the value is known
  // at click time. CONSERVATIVE — never called implicitly.
  applyAutoFillToSection: async (reportId, sectionId, providerId) => {
    const { toast } = await import('sonner');
    const trimmedSection = (sectionId ?? '').trim();
    const trimmedProvider = (providerId ?? '').trim();
    if (!trimmedSection || !trimmedProvider) return false;
    const r = get().siteRag.reports.find((x) => x.id === reportId);
    if (!r) {
      toast.error('Report not found.');
      return false;
    }
    // Read the provider value off the report's coverage cache. The
    // CAR-1..5 chains stamp their resolved cell on
    // report.coverageCells[sectionId] = { fillSource, providerId,
    // providerLabel, value }. When that cache is empty we silently
    // refuse — the chip shouldn't have rendered in that case.
    const coverage = (r as unknown as {
      coverageCells?: Record<string, {
        fillSource?: string;
        providerId?: string;
        providerLabel?: string;
        value?: string;
      }>
    }).coverageCells;
    const cell = coverage?.[trimmedSection];
    const cellProvider = cell?.providerId;
    const cellValue = cell?.value;
    if (!cell || cell.fillSource === 'missing' || cellProvider !== trimmedProvider || typeof cellValue !== 'string' || !cellValue.trim()) {
      toast.error('Auto-fill source no longer available.');
      return false;
    }
    // Persist the value as a custom section body so the viewer
    // surfaces it immediately. Conservative — we never overwrite a
    // non-empty `(r as any)[sectionId]` typed narrative field; the
    // RN-2 contract is "empty section + provider knows the answer →
    // user clicks chip → answer lands".
    const liveBody = (r as unknown as Record<string, unknown>)[trimmedSection];
    if (typeof liveBody === 'string' && liveBody.trim()) {
      toast.error('Section already populated; auto-fill skipped.');
      return false;
    }
    const customSections = Array.isArray(r.customSections) ? r.customSections.slice() : [];
    const existingIdx = customSections.findIndex((cs) => cs && cs.id === trimmedSection);
    const stamped = {
      id: trimmedSection,
      title: cell.providerLabel ?? trimmedProvider,
      content: cellValue,
    };
    if (existingIdx >= 0) {
      customSections[existingIdx] = { ...customSections[existingIdx], ...stamped };
    } else {
      customSections.push(stamped);
    }
    const priorReason = r.regenerationReason ?? '';
    const auditNote = `auto-fill:${trimmedSection}:${trimmedProvider}`;
    const mergedReason = priorReason.includes(auditNote)
      ? priorReason
      : (priorReason ? `${priorReason}; ${auditNote}` : auditNote);
    await get().updateRagReport(reportId, {
      customSections,
      regenerationReason: mergedReason,
    });
    toast.success(`Auto-fill applied from ${cell.providerLabel ?? trimmedProvider}.`);
    return true;
  },

  // ── RN-3 (wave-16) ───────────────────────────────────────────────
  // Deep-prefetch a report's body so the viewer renders instantly
  // when the user clicks the dashboard tile they're hovering. Goes
  // through the same `dedupeFetch` map already used by fetchSites /
  // fetchRagReports so repeated hovers on the SAME id collapse into
  // one network call — and the in-flight Promise stays resolved when
  // the user finally clicks. If the report is already fully hydrated
  // (`!_metadataOnly`) we no-op cheaply.
  prefetchReport: async (reportId) => {
    const trimmedId = (reportId ?? '').trim();
    if (!trimmedId) return;
    const sessionSignal = workspaceReadSignal();
    const scopeCompanyId = get().companyId;
    await dedupeFetch(`prefetchReport:${scopeCompanyId ?? ''}:${trimmedId}`, async () => {
      try {
        const r = get().siteRag.reports.find((x) => x.id === trimmedId);
        if (!r) return; // Unknown id — nothing to warm.
        const metadataOnly = (r as unknown as { _metadataOnly?: boolean })._metadataOnly === true;
        const slimBody = (r as unknown as { _portfolioSlimBody?: boolean })._portfolioSlimBody === true;
        const storagePath = (r as unknown as { storagePath?: string }).storagePath;
        // Already hydrated → no work. The dedupe wrapper still
        // collapses parallel hovers into a single promise even when
        // the body is a no-op, so the contract holds.
        if (!metadataOnly && !slimBody && r.datasets) return;
        if (!storagePath || typeof storagePath !== 'string') return;
        const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(storagePath)}`);
        if (!res.ok) return; // Silent — RN-3 is best-effort warming.
        const full = await res.json() as unknown;
        const { auth } = await import('../firebase');
        const uid = auth.currentUser?.uid ?? '';
        const metadata = r as unknown as Record<string, unknown>;
        const hydrated = await hydrateReportBodyWithUserState(
          full,
          metadata,
          uid,
        );
        if (
          !hydrated
          || sessionSignal.aborted
          || (auth.currentUser?.uid ?? '') !== uid
          || get().companyId !== scopeCompanyId
        ) {
          return;
        }
        publishHydratedReportBody(set, metadata, hydrated);
      } catch (e) {
        // Never escalate a prefetch failure to the user. The real
        // click path will retry through the normal load surface.
        console.debug('[prefetchReport] silent fail', (e as Error)?.message);
      }
    });
  },

  resolveSanityFinding: async (reportId, findingIndex, next) => {
    const report = get().siteRag.reports.find(r => r.id === reportId);
    if (!report) return;
    const owningSiteId = report.siteId;
    if (owningSiteId) await get().assertSiteWritable(owningSiteId);
    const findings = [...(report.sanityFindings ?? [])];
    const finding = findings[findingIndex];
    if (!finding) return;
    const now = new Date().toISOString();
    const isVerified = finding.category === 'risk-verified' || finding.category === 'strength-verified';
    const effectiveResolution = next.resolution === 'apply' && isVerified ? 'ignore' : next.resolution;
    const trimmedNote = typeof next.userNote === 'string' && next.userNote.trim().length > 0
      ? next.userNote.trim()
      : undefined;
    const nextFinding = effectiveResolution === 'apply'
      ? { ...finding, resolution: 'apply' as const, userNote: trimmedNote, applyDirective: next.applyDirective as never, resolvedAt: now }
      : effectiveResolution === 'ignore'
        ? {
            ...finding,
            resolution: 'ignore' as const,
            userNote: isVerified ? trimmedNote : undefined,
            applyDirective: undefined,
            resolvedAt: now,
          }
        : {
            ...finding,
            resolution: undefined,
            userNote: undefined,
            applyDirective: undefined,
            resolvedAt: undefined,
            correctionLineage: undefined,
          };
    findings[findingIndex] = nextFinding;
    const findingIdKey = sanityFindingIdentity(finding);
    const {
      upsertApprovedFactualCorrection,
      removeApprovedFactualCorrection,
    } = await import('../lib/preservedUserDecisions');
    const nextApprovedLedger = effectiveResolution === 'apply'
      ? upsertApprovedFactualCorrection(report.approvedFactualCorrections, {
          identity: findingIdKey,
          finding: nextFinding,
          userNote: trimmedNote,
          applyDirective: nextFinding.applyDirective,
          approvedAt: now,
          originReportId: reportId,
        })
      : removeApprovedFactualCorrection(report.approvedFactualCorrections, findingIdKey, now, {
          section: finding.section,
          category: finding.category,
          finding: finding.finding,
          originReportId: reportId,
        });
    // Optimistic in-memory mirror — the reader tray re-renders from this.
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        reports: state.siteRag.reports.map(r =>
          r.id === reportId
            ? {
                ...r,
                sanityFindings: findings,
                ...(nextApprovedLedger !== undefined ? { approvedFactualCorrections: nextApprovedLedger } : {}),
              }
            : r
        ),
      },
    }));
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { doc, setDoc } = await import('firebase/firestore');
      const ownerUid = reportOwnerUid(report as never, user.uid);
      const ref = doc(db, reportUserStateDocPath(ownerUid, reportId));
      const { key, entry } = buildSanityDecisionEntry(
        finding,
        {
          resolution: next.resolution ?? 'withdrawn',
          userNote: next.userNote,
          applyDirective: next.applyDirective,
        },
        user.uid,
        now,
      );
      await setDoc(ref, {
        schemaVersion: REPORT_USER_STATE_SCHEMA_VERSION,
        reportId,
        sanityDecisions: { [key]: entry },
        updatedAt: now,
        updatedByUid: user.uid,
      }, { merge: true });
    } catch (e) {
      console.warn(`[resolveSanityFinding] persist failed for ${reportId}:`, e);
    }
  },

  applySourceSupportedCorrectionToReport: async (reportId, corrections, options) => {
    const report = get().siteRag.reports.find(r => r.id === reportId);
    const owningSiteId = report?.siteId;
    if (owningSiteId) await get().assertSiteWritable(owningSiteId);
    const expectedRevisionId =
      options?.expectedRevisionId ??
      report?.reportRevision?.revisionId ??
      report?.reportRevisionId;
    const reportOwnerUidOpt =
      options?.reportOwnerUid ??
      (report as unknown as { ownerUid?: string } | undefined)?.ownerUid;
    const {
      computeAuditOverridesFingerprint,
      computeAuditOverridesVersion,
      generateAuditOperationId,
    } = await import('../lib/audit/truthAttestations');
    type PendingCorrectionEnvelope = {
      operationId: string;
      expectedRevisionId?: string;
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
    };
    const envelopeCache = ((
      globalThis as { __reportCorrectionEnvelopeCache?: Map<string, PendingCorrectionEnvelope> }
    ).__reportCorrectionEnvelopeCache ??= new Map<string, PendingCorrectionEnvelope>());
    const payloadKey = options?.operationId?.trim()
      ? `op:${options.operationId.trim()}`
      : JSON.stringify({ reportId, corrections });
    let envelope: PendingCorrectionEnvelope;
    if (options?.operationId?.trim()) {
      const cachedExplicit = envelopeCache.get(payloadKey);
      if (cachedExplicit) {
        envelope = cachedExplicit;
      } else {
        const owningSite = owningSiteId
          ? get().siteRag.sites.find((s) => s.id === owningSiteId)
          : undefined;
        const expectedAuditVersion =
          options.expectedAuditVersion ??
          (owningSite ? computeAuditOverridesVersion(owningSite) : undefined);
        const expectedAuditFingerprint =
          options.expectedAuditFingerprint ??
          (owningSite
            ? computeAuditOverridesFingerprint(owningSite.auditOverrides ?? [])
            : undefined);
        if (expectedAuditVersion === undefined && !expectedAuditFingerprint) {
          return {
            applied: false,
            conflict: false,
            code: 'MISSING_SITE_BASELINE',
            error:
              'Authoritative site audit baseline is not loaded; refresh before applying report corrections.',
            reason: 'missing-site-baseline',
          };
        }
        envelope = {
          operationId: options.operationId.trim(),
          expectedRevisionId,
          expectedAuditVersion,
          expectedAuditFingerprint,
        };
        envelopeCache.set(payloadKey, envelope);
      }
    } else {
      const cached = envelopeCache.get(payloadKey);
      if (cached) {
        envelope = cached;
      } else {
        const owningSite = owningSiteId
          ? get().siteRag.sites.find((s) => s.id === owningSiteId)
          : undefined;
        const expectedAuditVersion =
          options?.expectedAuditVersion ??
          (owningSite ? computeAuditOverridesVersion(owningSite) : undefined);
        const expectedAuditFingerprint =
          options?.expectedAuditFingerprint ??
          (owningSite
            ? computeAuditOverridesFingerprint(owningSite.auditOverrides ?? [])
            : undefined);
        if (expectedAuditVersion === undefined && !expectedAuditFingerprint) {
          return {
            applied: false,
            conflict: false,
            code: 'MISSING_SITE_BASELINE',
            error:
              'Authoritative site audit baseline is not loaded; refresh before applying report corrections.',
            reason: 'missing-site-baseline',
          };
        }
        envelope = {
          operationId: generateAuditOperationId(`report-correction:${reportId}`),
          expectedRevisionId,
          expectedAuditVersion,
          expectedAuditFingerprint,
        };
        envelopeCache.set(payloadKey, envelope);
      }
    }
    const res = await authedFetch(
      `/api/reports/${encodeURIComponent(reportId)}/audit-corrections/apply`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          corrections,
          ...(envelope.expectedRevisionId ? { expectedRevisionId: envelope.expectedRevisionId } : {}),
          ...(reportOwnerUidOpt ? { reportOwnerUid: reportOwnerUidOpt } : {}),
          ...(envelope.expectedAuditVersion !== undefined
            ? { expectedAuditVersion: envelope.expectedAuditVersion }
            : {}),
          ...(envelope.expectedAuditFingerprint
            ? { expectedAuditFingerprint: envelope.expectedAuditFingerprint }
            : {}),
          operationId: envelope.operationId,
        }),
      },
    );
    const payload = (await res.json().catch(() => ({}))) as {
      applied?: boolean;
      idempotent?: boolean;
      code?: string;
      report?: SiteRagReport;
      newRevisionId?: string;
      previousRevisionId?: string;
      clearedHoldFindings?: string[];
      remainingHoldFindings?: string[];
      receipt?: import('../lib/reportGeneration/applySourceSupportedAuditCorrection').SourceSupportedAuditCorrectionReceiptV1;
      error?: string;
      reason?: string;
      detail?: string;
    };
    if (!res.ok || !payload.applied || !payload.report) {
      if (res.status >= 400 && res.status < 500) {
        envelopeCache.delete(payloadKey);
      }
      return {
        applied: false,
        conflict: res.status === 409,
        code: payload.code,
        error: payload.detail ?? payload.error ?? `HTTP ${res.status}`,
        reason: payload.reason ?? payload.error,
      };
    }
    envelopeCache.delete(payloadKey);
    set((state) => ({
      siteRag: {
        ...state.siteRag,
        reports: state.siteRag.reports.map((existing) =>
          existing.id === reportId ? payload.report! : existing,
        ),
      },
    }));
    return {
      applied: true,
      ...(payload.idempotent ? { idempotent: true } : {}),
      report: payload.report,
      newRevisionId: payload.newRevisionId,
      previousRevisionId: payload.previousRevisionId,
      clearedHoldFindings: payload.clearedHoldFindings,
      remainingHoldFindings: payload.remainingHoldFindings,
      receipt: payload.receipt,
    };
  },

  updateRagReport: async (id, updates, options) => {
    const strictPersist = options?.strictPersist === true;
    // #82 — report writes against a locked site are gated. Resolve the owning
    // site from the report's `siteId`; an orphaned/legacy report (no siteId, or
    // a site not in state) falls through assertSiteWritable's writable path.
    const priorReport = get().siteRag.reports.find(r => r.id === id);
    const owningSiteId = priorReport?.siteId;
    if (owningSiteId) await get().assertSiteWritable(owningSiteId);
    let updatedReport: SiteRagReport | undefined;
    let latestOptimisticReport: SiteRagReport | undefined;
    set((state) => {
      const reports = state.siteRag.reports.map(r => {
        if (r.id !== id) return r;
        const merged = { ...r, ...updates };
        if (typeof (updates as { templateId?: string }).templateId === 'string') {
          // COVER FIX — snapshot the gen-time cover layout before stripping
          // so an unresolved-template render can fall back to it.
          const prevCover = (merged as { coverLayout?: unknown }).coverLayout;
          if ((merged as { coverLayoutAtGen?: unknown }).coverLayoutAtGen == null && prevCover != null) {
            (merged as { coverLayoutAtGen?: unknown }).coverLayoutAtGen = prevCover;
          }
          delete (merged as { coverLayout?: unknown }).coverLayout;
          delete (merged as { sectionVisibility?: unknown }).sectionVisibility;
          delete (merged as { sectionColumnLayouts?: unknown }).sectionColumnLayouts;
        }
        updatedReport = merged;
        latestOptimisticReport = merged;
        return merged;
      });
      return { siteRag: { ...state.siteRag, reports } };
    });

    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (!user) {
        if (strictPersist) throw new Error('Not signed in');
        return;
      }
      const { doc, setDoc } = await import('firebase/firestore');
      if (!updatedReport) {
        if (strictPersist) throw new Error(`Report ${id} not found`);
        return;
      }

      let toPersist = updatedReport;
      if (!isReportHydratedForGcsSave(toPersist)) {
        await get().prefetchReport(id);
        const hydrated = get().siteRag.reports.find(r => r.id === id);
        if (!hydrated || !isReportHydratedForGcsSave(hydrated)) {
          throw new Error(`Refusing GCS save for metadata-only report ${id}`);
        }
        toPersist = { ...hydrated, ...updates };
        if (typeof (updates as { templateId?: string }).templateId === 'string') {
          // COVER FIX — snapshot the gen-time cover layout before stripping.
          const prevCover = (toPersist as { coverLayout?: unknown }).coverLayout;
          if ((toPersist as { coverLayoutAtGen?: unknown }).coverLayoutAtGen == null && prevCover != null) {
            (toPersist as { coverLayoutAtGen?: unknown }).coverLayoutAtGen = prevCover;
          }
          delete (toPersist as { coverLayout?: unknown }).coverLayout;
          delete (toPersist as { sectionVisibility?: unknown }).sectionVisibility;
          delete (toPersist as { sectionColumnLayouts?: unknown }).sectionColumnLayouts;
        }
        latestOptimisticReport = toPersist;
        set((state) => ({
          siteRag: {
            ...state.siteRag,
            reports: state.siteRag.reports.map(r => (r.id === id ? toPersist! : r)),
          },
        }));
      }

      // #owner-path-sweep-p2 — resolve the doc owner from the in-memory report
      // entity's transient `_ownerUid` stamp (set by fetchRagReports for
      // company-shared reports). For own reports _ownerUid is absent and
      // ownerReportGcsPath/ownerReportDocPath fall back to user.uid — byte-
      // identical to the pre-sweep paths. Strip _ownerUid from EVERY payload
      // so the transient never reaches Firestore or GCS.
      const cleanReport = serializeReportForGcsSave(stripTransientReportAnnotations(toPersist));
      const storagePath = ownerReportGcsPath(get(), id, user.uid);
      const isTemplateLink = typeof (updates as { templateId?: string }).templateId === 'string';
      try {
        const saveResponse = await authedFetch('/api/storage/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: storagePath, data: cleanReport }),
        });
        if (strictPersist && !saveResponse.ok) {
          const body = await saveResponse.text().catch(() => '');
          throw new Error(
            `Report storage save failed (${saveResponse.status})${body ? `: ${body.slice(0, 200)}` : ''}`,
          );
        }
      } catch (e) {
        console.warn(`GCS save failed for ${id}:`, e);
        if (strictPersist || isTemplateLink) throw e;
      }
      try {
        const reportSite = get().siteRag.sites.find(s => (toPersist.siteId && s.id === toPersist.siteId));
        const orgStamp = (await import('../lib/siteCompany')).resolveOrgStamp(
          toPersist.companyId ?? reportSite?.companyId,
          get().companyId,
        );
        await setDoc(doc(db, ownerReportDocPath(get(), id, user.uid)), {
          id, siteName: toPersist.siteName, overallScore: toPersist.overallScore,
          overallStatus: toPersist.overallStatus, reportType: toPersist.reportType || 'feasibility',
          storagePath, updatedAt: new Date().toISOString(),
          ...(toPersist.siteId ? { siteId: toPersist.siteId } : {}),
          ...orgStamp,
        }, { merge: true });
      } catch { /* Firestore metadata failed — non-blocking, GCS has full report */ }
    } catch (e) {
      if (strictPersist && priorReport && latestOptimisticReport) {
        set((state) => ({
          siteRag: {
            ...state.siteRag,
            reports: state.siteRag.reports.map((current) => (
              current.id === id && current === latestOptimisticReport ? priorReport : current
            )),
          },
        }));
      }
      // Metadata-only refusal is expected when best-effort writes (e.g. RN-2
      // hint stamps) race with hydration; downgrade to debug so the console
      // isn't flooded on every thin-intent report open. Real persist failures
      // (network, auth, serialization) keep the warn level.
      const msg = (e as Error)?.message ?? String(e);
      if (typeof msg === 'string' && msg.includes('Refusing GCS save for metadata-only report')) {
        console.debug('Report persist skipped (metadata-only):', msg);
      } else {
        console.warn('Report persist failed:', e);
      }
      if (strictPersist || typeof (updates as { templateId?: string }).templateId === 'string') throw e;
    }
  },

  saveCseConflictResolution: async (reportId, resolutions, review) => {
    const report = get().siteRag.reports.find(r => r.id === reportId);
    if (!report) throw new Error('Report not found');
    const { auth } = await import('../firebase');
    const actorUid = auth.currentUser?.uid;
    if (!actorUid) throw new Error('Sign in before saving a report review.');
    const ownerUid = reportOwnerUid(report, actorUid);
    if (review.basis.reportId !== reportId || review.basis.ownerUid !== ownerUid
      || review.basis.siteId !== (report.siteId ?? null)) throw new Error('The review source no longer matches this report. Reload before retrying.');
    const { saveCseReportReview } = await import('../services/cseReportReview');
    const ack = await saveCseReportReview({ basis: review.basis, operationId: review.operationId,
      choices: Object.entries(resolutions).map(([conflictKey, value]) => ({ conflictKey, value })) }, review.signal);
    if (auth.currentUser?.uid !== actorUid || ack.review.reviewedByUid !== actorUid) {
      throw new Error('The signed-in reviewer changed before the save could be confirmed.');
    }
    // Report claims/warnings, site pointers and SED caches are never changed by a review ACK.
    return ack;
  },

  // Wrapped in dedupeFetch — see comment on fetchSites above.
  fetchRagReports: () => dedupeFetch('fetchRagReports', async () => {
    const sessionSignal = workspaceReadSignal();
    const { auth, db, handleFirestoreError, OperationType } = await import('../firebase');
    const user = auth.currentUser;
    if (sessionSignal.aborted) return;
    if (!user) {
      // No signed-in user → no reports to fetch. Mark loaded with the
      // empty list so any UI gated on isReportsLoaded resolves correctly.
      set((state) => ({ siteRag: { ...state.siteRag, isReportsLoaded: true, isReportMetadataLoaded: true } }));
      return;
    }

    const scopeCompanyId = get().companyId;
    const scopeIsCurrent = () => !sessionSignal.aborted && auth.currentUser?.uid === user.uid && get().companyId === scopeCompanyId;
    try {
      const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
      const ownReportMetadata: Record<string, unknown>[] = [];
      const seenIds = new Set<string>();
      {
        const q = query(collection(db, `users/${user.uid}/reports`), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        for (const d of snapshot.docs) {
          seenIds.add(d.id);
          const raw = d.data();
          if (
            raw.hiddenFromReportLibrary === true
            || raw.artifactSourceKind === 'decision-dossier'
          ) {
            if (typeof raw.id === 'string') seenIds.add(raw.id);
            continue;
          }
          const row = compactReportMetadataRow({ ...raw, id: d.id });
          if (typeof row.id === 'string') seenIds.add(row.id);
          ownReportMetadata.push(row);
        }
      }

      const companyId = scopeCompanyId;
      const companyReportMetadata: Record<string, unknown>[] = [];
      if (!companyId) {
        console.debug('[fetchRagReports] no companyId — skipping company-shared collectionGroup query');
      } else {
        try {
          const { collectionGroup, where } = await import('firebase/firestore');
          const subtreeCompanyIds = await resolveSubtreeCompanyIds(db, companyId);
          const chunks = chunkIds(subtreeCompanyIds, 30);
          for (const chunk of chunks) {
            const compQ = query(
              collectionGroup(db, 'reports'),
              where('isCompanyShared', '==', true),
              where('companyId', 'in', chunk),
            );
            const compSnap = await getDocs(compQ);
            for (const d of compSnap.docs) {
              if (seenIds.has(d.id)) continue;
              const ownerUidFromRef = (d as { ref?: { parent?: { parent?: { id?: string } | null } } })
                .ref?.parent?.parent?.id;
              if (ownerUidFromRef === user.uid) continue;
              const raw = d.data();
              if (
                raw.hiddenFromReportLibrary === true
                || raw.artifactSourceKind === 'decision-dossier'
              ) {
                continue;
              }
              const id = typeof raw.id === 'string' ? raw.id : d.id;
              if (!id || seenIds.has(id)) continue;
              seenIds.add(id);
              seenIds.add(d.id);
              const ownerStamp = ownerUidFromRef ? { _ownerUid: ownerUidFromRef } : {};
              companyReportMetadata.push(
                compactReportMetadataRow({
                  ...raw,
                  id: d.id,
                  isCompanyShared: true,
                  ...ownerStamp,
                }),
              );
            }
          }
        } catch (e) {
          if (isPermissionDenied(e)) {
            console.warn('[fetchRagReports] permission denied on company-shared query despite companyId in store — token may be stale.', e);
          } else {
            console.warn('Failed to load company-shared reports:', e);
          }
        }
      }

      const reportMetadata = [...ownReportMetadata, ...companyReportMetadata];
      if (!scopeIsCurrent()) return;
      const previousMetadataById = new Map(lastReportMetadataById);
      rememberListedReportMetadata(reportMetadata);
      // Let exact report URLs and the library resolve before the open site's
      // current heads finish. Body/decision completeness remains false on
      // initial load.
      set((state) => ({ siteRag: {
        ...state.siteRag,
        ...(!state.siteRag.isReportsLoaded ? {
          reports: reportMetadata.map(metadata => isRenderableReportBody(metadata)
            ? { ...metadata, _metadataOnly: false } as unknown as SiteRagReport
            : retainOpenedReportBody(markReportMetadataOnly(metadata), state.siteRag.reports.find(r => r.id === metadata.id))),
          // Residency is rebuilt per session; a restored list would let a
          // site page settle on metadata-only heads.
          hydratedReportSiteIds: [],
        } : {}),
        isReportMetadataLoaded: true,
      } }));

      // PHASE 1 — what the first screen renders from: every row's metadata
      // plus the current heads of the open site (and of sites whose heads are
      // already resident). One site page used to wait for ~50 bodies / ~34 MB
      // across the whole portfolio before Home or Findings could settle.
      const openSiteId = resolveOpenSiteId(get());
      const portfolioHeadIds = selectEagerReportBodyIds(reportMetadata);
      const firstScreenHeadIds = selectEagerReportBodyIdsForSites(
        reportMetadata,
        residentReportSiteScopes(get(), openSiteId),
      );
      const residentById = new Map(get().siteRag.reports.map((report) => [report.id, report] as const));
      const hydratedReports = await mapWithConcurrency(
        reportMetadata,
        REPORT_LIST_BODY_HYDRATION_CONCURRENCY,
        async (metadata): Promise<SiteRagReport> => {
          if (isRenderableReportBody(metadata)) {
            return { ...metadata, _metadataOnly: false } as unknown as SiteRagReport;
          }

          const id = typeof metadata.id === 'string' ? metadata.id : null;
          const isOpenActiveReport = Boolean(id && id === get().siteRag.activeReportId);
          // The exact artifact is already resident: no second read, same object.
          const reused = id
            ? reuseHydratedReportBody(
                metadata,
                residentById.get(id),
                previousMetadataById.get(id),
                { requireFullBody: isOpenActiveReport },
              )
            : null;
          if (reused) return reused;
          const alreadyOpened = retainOpenedReportBody(markReportMetadataOnly(metadata), get().siteRag.reports.find(r => r.id === id));
          if (alreadyOpened._metadataOnly !== true) return alreadyOpened;
          if (id && (firstScreenHeadIds.has(id) || isOpenActiveReport) && typeof metadata.storagePath === 'string') {
            try {
              const hydrated = await loadReportBodyWithUserState(metadata, user.uid);
              if (hydrated) return hydrated;
            } catch (error) {
              if (error instanceof Error && error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded');
              }
            }
          }

          // Historical revisions intentionally remain metadata-only on list
          // load; ReportReaderRecovery hydrates an exact immutable body if the
          // user opens the revision. Other sites' current heads warm up in
          // phase 2 below, or sooner through ensureSiteReportBodies. A
          // current-head failure takes the same explicit metadata-only path,
          // so settled Findings cannot silently treat a missing body as proof.
          return markReportMetadataOnly(metadata);
        },
      );

      if (!scopeIsCurrent()) return;
      const firstScreenMigrations = createReportMigrationBatch(get, scopeIsCurrent);
      const reports = hydratedReports.map((loaded) => {
        if (loaded._metadataOnly === true) return loaded;
        const prepared = prepareLoadedReportBody(loaded);
        const fullReport = firstScreenMigrations.collect(prepared);
        const keepFull =
          loaded.id === get().siteRag.activeReportId
          || prepared.persistSanityMigration
          || prepared.retirementMarker !== null;
        return keepFull ? fullReport : slimPortfolioReportBody(fullReport);
      });

      if (!scopeIsCurrent()) return;
      set((state) => {
        // Preserve the user's active report selection across data refreshes
        // (William 2026-06-29, "infinite load loop on dashboard"). The
        // reportGenerationJobs subscription re-invokes fetchRagReports once per
        // completing job (+ the de-orphan sweep / on listener re-arm), so the
        // OLD unconditional `activeReportId = reports[0].id` churned the active
        // report on every tick — and SiteRagEngine promotes engineView→'report'
        // whenever activeReport is non-null, so a batch delete+regenerate drove
        // a re-promotion loop. Keep the prior selection when it still exists;
        // clear a missing selection and let navigation choose what opens next.
        const prevActive = state.siteRag.activeReportId;
        const prevStillExists = prevActive != null && reports.some((r) => r.id === prevActive);
        const residentRows = new Map(state.siteRag.reports.map((report) => [report.id, report] as const));
        const nextSiteRag = {
          ...state.siteRag,
          // Rows that did not change keep their object identity, so memoised
          // selectors keyed on `siteRag.reports` survive the list passes that
          // every completing generation job triggers.
          reports: reports.map((report) => {
            const resident = residentRows.get(report.id);
            return reuseUnchangedMetadataRow(retainOpenedReportBody(report, resident), resident);
          }),
          // NEVER auto-select reports[0] here — report selection is owned by
          // navigation (URL hydration / dashboard card click), NOT by the data
          // fetch. Auto-selecting re-populated activeReportId after a deliberate
          // "Back to reports" clear — and because the reportGenerationJobs
          // subscription fires fetchRagReports repeatedly (esp. during a regen),
          // SiteRagEngine then re-promoted engineView→'report' and "Back" bounced
          // straight back to the report (William 2026-06-29). Preserve a STILL-
          // VALID prior selection; otherwise clear to null and let nav re-open.
          activeReportId: prevStillExists ? prevActive : null,
          // Metadata plus the open site's current heads is what the first
          // screen renders from. The remaining heads, the persist loop and the
          // one-shot migrations no longer hold this gate.
          isReportsLoaded: true,
          isReportMetadataLoaded: true,
        };
        return {
          siteRag: {
            ...nextSiteRag,
            ...markResidentReportSites(nextSiteRag, openSiteId ? [openSiteId] : []),
          },
        };
      });
      const activeIdAfterPhase1 = get().siteRag.activeReportId;
      if (
        activeIdAfterPhase1
        && (get().siteRag.reports.find((r) => r.id === activeIdAfterPhase1) as { _portfolioSlimBody?: boolean } | undefined)?._portfolioSlimBody === true
      ) {
        void get().hydrateReportBody(activeIdAfterPhase1);
      }
      firstScreenMigrations.flush();

      // PHASE 2 — the remaining portfolio heads, behind the gate and in a
      // narrower lane. Portfolio surfaces mounted on every page (the sidebar's
      // screening recommendation, the deck's staleness check, the evidence
      // readiness pass that persists site summaries) read one body per site,
      // so these still load; they just no longer hold the first screen. The
      // batch is published in ONE pass so the deck's fingerprint key, and the
      // analysis-context reads it triggers, move once rather than per body.
      const metadataOnlyRowIds = new Set(
        get().siteRag.reports.flatMap((row) => (row._metadataOnly === true ? [row.id] : [])),
      );
      const remainingHeads = reportMetadata.filter((metadata) => {
        const id = typeof metadata.id === 'string' ? metadata.id : null;
        return id !== null
          && portfolioHeadIds.has(id)
          && !firstScreenHeadIds.has(id)
          && metadataOnlyRowIds.has(id)
          && typeof metadata.storagePath === 'string';
      });
      const warmMigrations = createReportMigrationBatch(get, scopeIsCurrent);
      const warmed = new Map<
        string,
        { metadata: Record<string, unknown>; report: SiteRagReport }
      >();
      await mapWithConcurrency(remainingHeads, REPORT_BACKGROUND_BODY_HYDRATION_CONCURRENCY, async (metadata) => {
        if (!scopeIsCurrent()) return;
        const id = metadata.id as string;
        // A navigation or a composite recompute may have read it meanwhile.
        if (get().siteRag.reports.find((row) => row.id === id)?._metadataOnly !== true) return;
        try {
          const hydrated = await loadReportBodyWithUserState(metadata, user.uid);
          if (hydrated) {
            // Re-check after awaiting: if the user opened and fully hydrated
            // this report during the background read, never queue a slim overwrite.
            const currentRow = get().siteRag.reports.find((row) => row.id === id);
            if (currentRow && isFullReaderReadyReportBody(currentRow)) return;
            const prepared = prepareLoadedReportBody(hydrated);
            const fullReport = warmMigrations.collect(prepared);
            const keepFull =
              id === get().siteRag.activeReportId
              || prepared.persistSanityMigration
              || prepared.retirementMarker !== null;
            warmed.set(id, {
              metadata,
              report: keepFull ? fullReport : slimPortfolioReportBody(fullReport),
            });
          }
        } catch {
          // Fail closed: the row stays an explicit metadata-only record and the
          // reader's Retry remains available.
        }
      });

      if (!scopeIsCurrent()) return;
      set((state) => ({
        siteRag: {
          ...state.siteRag,
          reports: publishHydratedReportBodies(
            state.siteRag.reports,
            warmed,
            state.siteRag.activeReportId,
          ),
          ...markResidentReportSites(state.siteRag, listedReportSiteIds(reportMetadata)),
        },
      }));
      const activeIdAfterWarm = get().siteRag.activeReportId;
      if (
        activeIdAfterWarm
        && (warmed.get(activeIdAfterWarm)?.report as { _portfolioSlimBody?: boolean } | undefined)?._portfolioSlimBody === true
      ) {
        void get().hydrateReportBody(activeIdAfterWarm);
      }
      warmMigrations.flush();
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${user.uid}/reports`);
    } finally {
      // Flip the flag in `finally` so a Firestore hiccup doesn't strand
      // surfaces gated on isReportsLoaded — same pattern as fetchSites.
      if (scopeIsCurrent()) {
        set((state) => ({ siteRag: { ...state.siteRag, isReportsLoaded: true, isReportMetadataLoaded: true } }));
      }
    }
  }),

  saveEditCorrections: async (reportId, original, edited) => {
    // Compute diffs between original and edited report
    const corrections: import('../types/siteRag').EditRecord[] = [];
    const now = new Date().toISOString();
    // Track ALL editable fields — string fields
    const stringFields = ['executiveSummary', 'sitingNotes', 'summary', 'address'] as const;
    for (const field of stringFields) {
      const origVal = (original as any)[field];
      const editVal = (edited as any)[field];
      if (origVal !== editVal && editVal != null) {
        corrections.push({
          id: `corr-${safeUUID()}-${field}`,
          reportId, field,
          originalValue: JSON.stringify(origVal ?? '').slice(0, 2000),
          editedValue: JSON.stringify(editVal).slice(0, 2000),
          editedAt: now, address: original.address, useCase: 'standard',
        });
      }
    }

    // Track nested string fields
    const nestedFields = [
      ['infrastructureInterconnection', 'electricGrid'],
      ['infrastructureInterconnection', 'transportFiber'],
      ['climateSevereWeather', 'implications'],
      ['insights', 'powerStrategy'],
      ['insights', 'coolingStrategy'],
      ['insights', 'regulatoryOutlook'],
      ['insights', 'commercialViability'],
    ] as const;
    for (const [parent, child] of nestedFields) {
      const origVal = (original as any)?.[parent]?.[child];
      const editVal = (edited as any)?.[parent]?.[child];
      if (origVal !== editVal && editVal != null) {
        corrections.push({
          id: `corr-${safeUUID()}-${parent}.${child}`,
          reportId, field: `${parent}.${child}`,
          originalValue: JSON.stringify(origVal ?? '').slice(0, 2000),
          editedValue: JSON.stringify(editVal).slice(0, 2000),
          editedAt: now, address: original.address, useCase: 'standard',
        });
      }
    }

    // Track array-field edits
    const arrayFields = ['btmOptions', 'connectionRisks', 'developmentRisks', 'developmentPathway', 'risks', 'recommendations', 'appendix'] as const;
    for (const arrField of arrayFields) {
      const origArr = JSON.stringify((original as any)[arrField] ?? []);
      const editArr = JSON.stringify((edited as any)[arrField] ?? []);
      if (origArr !== editArr) {
        corrections.push({
          id: `corr-${safeUUID()}-${arrField}`,
          reportId, field: arrField,
          originalValue: origArr.slice(0, 2000),
          editedValue: editArr.slice(0, 2000),
          editedAt: now, address: original.address, useCase: 'standard',
        });
      }
    }

    // Track climate severe weather risks array
    const origClimRisks = JSON.stringify((original as any).climateSevereWeather?.risks ?? []);
    const editClimRisks = JSON.stringify((edited as any).climateSevereWeather?.risks ?? []);
    if (origClimRisks !== editClimRisks) {
      corrections.push({
        id: `corr-${safeUUID()}-climateSevereWeather.risks`,
        reportId, field: 'climateSevereWeather.risks',
        originalValue: origClimRisks.slice(0, 2000),
        editedValue: editClimRisks.slice(0, 2000),
        editedAt: now, address: original.address, useCase: 'standard',
      });
    }

    if (corrections.length === 0) return;

    // Update report with edit history + mark edited fields as user-edited confidence
    const report = get().siteRag.reports.find(r => r.id === reportId);
    if (report) {
      const editHistory = [...(report.editHistory || []), ...corrections];
      const fieldConfidence = [...(report.fieldConfidence || [])];
      for (const c of corrections) {
        const idx = fieldConfidence.findIndex(f => f.field === c.field);
        const entry = { field: c.field, confidence: 'user-edited' as const, source: 'User correction' };
        if (idx >= 0) fieldConfidence[idx] = entry;
        else fieldConfidence.push(entry);
      }
      await get().updateRagReport(reportId, { editHistory, fieldConfidence });
    }

    // Save corrections to Firestore
    try {
      const { auth, db } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        const { doc, setDoc } = await import('firebase/firestore');
        for (const c of corrections) {
          await setDoc(doc(db, `users/${user.uid}/corrections`, c.id), c);
        }
      }
    } catch (e) {
      console.error('Failed to save corrections to Firestore', e);
    }
  },


  // Workflow Prompt 13 — `generateSiteRagReport` shim deleted.
  // Every caller now goes through generateTypedReport directly; the
  // legacy positional-args entrypoint had zero remaining call sites
  // before deletion (verified via repo-wide grep).

  enqueueReportGenerationJob: async (
    siteId,
    reportTypeId,
    typedPreferences,
    mode = 'new',
    parentReportId,
    companyArchetypeOverride,
    documentSelection,
    siteContextOverride,
    siteConfigOverrides,
    clientRunId,
  ) => {
    const site = get().siteRag.sites.find(s => s.id === siteId);
    if (!site) {
      set((s) => ({ siteRag: { ...s.siteRag, error: 'Site not found' } }));
      return undefined;
    }
    const runId = clientRunId ?? `gen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const runStartedAt = new Date().toISOString();

    type GenerationRun = NonNullable<SiteRagState['activeGenerations']>[string];
    const resolveLatestRun = (
      runs: NonNullable<SiteRagState['activeGenerations']>,
    ): GenerationRun | null => {
      const values = Object.values(runs);
      if (values.length === 0) return null;
      values.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
      return values[0] ?? null;
    };

    // `background`: an update no user action caused at this moment — the
    // Public Sentiment evidence watcher dispatching a held run, its post-POST
    // job id, or a re-wait after a 409 (up to an hour after the click). It
    // must not move the user to the held run's site (activeSiteId drives
    // SiteRagEngine's site view) or wipe an unrelated error they have not
    // read; only the user-initiated upserts keep the original behaviour.
    const upsertRun = (
      patch: Partial<GenerationRun> & { jobId?: string },
      options: { background?: boolean } = {},
    ) => {
      set((s) => {
        const runs: NonNullable<SiteRagState['activeGenerations']> = {
          ...(s.siteRag.activeGenerations ?? {}),
        };
        runs[runId] = {
          ...(runs[runId] ?? {
            id: runId,
            siteId: site.id,
            reportTypeId,
            startedAt: runStartedAt,
            progress: 0,
            progressStage: 'Queued…',
            estimatedDurationMs: estimatedReportGenerationDurationMs(
              reportTypeId,
              siteConfigOverrides?.generationTier,
            ),
          }),
          ...patch,
        };
        const latest = resolveLatestRun(runs);
        return {
          siteRag: {
            ...s.siteRag,
            activeGenerations: runs,
            isLoading: true,
            progress: normalizeProgressPercent(latest?.progress ?? 0),
            progressStage: latest?.progressStage ?? 'Queued…',
            activeSiteId: options.background ? s.siteRag.activeSiteId : (latest?.siteId ?? site.id),
            error: options.background ? s.siteRag.error : null,
          },
        };
      });
    };

    // PUBLIC SENTIMENT EVIDENCE WAIT. Public Sentiment refreshes its evidence
    // SYNCHRONOUSLY at enqueue, so it cannot run while the site's evidence is
    // mid-build (e.g. a top-up another analysis just started) or needs a
    // rebuild; the server answers 409 `sentiment-evidence-not-ready`. That is
    // an expected condition, never a failure:
    //   - a running build HOLDS the run here (no POST, no job) and dispatches
    //     it automatically once the live site doc reports complete evidence;
    //   - evidence that needs a rebuild records a hold the Analyses row and the
    //     sidebar explain, with the existing Rebuild SED action.
    // Neither path writes a generation outcome or `siteRag.error`, so no
    // surface can render "Failed" / "Generation Failed" for it. See
    // `src/lib/sentiment/sentimentEvidenceReadiness.ts`.
    const isPublicSentiment = reportTypeId === 'public-sentiment';
    // The requester's uid, for runs the watcher dispatches later: a held run
    // must never be sent under another user's token (the auth uid boundary
    // also disposes every wait — `resetSentimentEvidenceWaits`). Captured from
    // the user object of the first (foreground) dispatch, or — when the run
    // waits before any dispatch — right when it starts waiting.
    let requesterUid: Promise<string | null> | undefined;
    const liveSiteNow = () => get().siteRag.sites.find(s => s.id === site.id);
    const clearGeneratingDraft = () => {
      // The wizard persists a sessionStorage "generating" draft before
      // dispatch. With no job ever created nothing else would sweep it, and the
      // landing page would render it as a phantom "Continuing generation…" row.
      void import('../lib/reportDialogDraft')
        .then(({ clearReportDialogDraft }) => clearReportDialogDraft(site.id, reportTypeId))
        .catch(() => { /* best-effort */ });
    };
    const notifySentimentEvidence = (
      message: string,
      action?: { label: string; onClick: () => void },
    ) => {
      void import('sonner')
        .then(({ toast }) => {
          toast.info(message, {
            id: `sentiment-evidence-${site.id}`,
            duration: action ? 12_000 : 8_000,
            ...(action ? { action } : {}),
          });
        })
        .catch(() => { /* the inline notices still explain the hold */ });
    };
    const withoutRun = (s: AppState) => {
      const runs = { ...(s.siteRag.activeGenerations ?? {}) };
      delete runs[runId];
      const latest = resolveLatestRun(runs);
      return {
        activeGenerations: runs,
        isLoading: Object.keys(runs).length > 0,
        progress: normalizeProgressPercent(latest?.progress ?? 0),
        progressStage: latest?.progressStage ?? '',
      };
    };
    const siteHoldsExcept = (s: AppState): NonNullable<SiteRagState['sentimentEvidenceHolds']> => {
      const holds: NonNullable<SiteRagState['sentimentEvidenceHolds']> = {};
      for (const [key, hold] of Object.entries(s.siteRag.sentimentEvidenceHolds ?? {})) {
        if (hold.siteId !== site.id) holds[key] = hold;
      }
      return holds;
    };
    const recordSentimentHold = (
      reason: SentimentEvidenceNotReadyReason,
      exhausted?: SentimentEvidenceExhaustion,
    ) => {
      const live = liveSiteNow();
      // Copy from the observed state: a failed build "did not complete", an
      // exhausted wait/retry budget "did not start — run it again".
      const message = sentimentEvidenceHoldMessage({ reason, exhausted, site: live });
      forgetSentimentHoldRetriesForSite(site.id);
      if (exhausted) {
        // "Run again" re-sends this exact request (same run id: a refused
        // request left no receipt, and every automatic retry reused it too).
        _sentimentEvidenceHoldRetries.set(runId, {
          siteId: site.id,
          run: () => get().enqueueReportGenerationJob(
            siteId,
            reportTypeId,
            typedPreferences,
            mode,
            parentReportId,
            companyArchetypeOverride,
            documentSelection,
            siteContextOverride,
            siteConfigOverrides,
            runId,
          ),
        });
      }
      set((s) => ({
        siteRag: {
          ...s.siteRag,
          ...withoutRun(s),
          sentimentEvidenceHolds: {
            ...siteHoldsExcept(s),
            [runId]: {
              id: runId,
              siteId: site.id,
              reason,
              message,
              heldAt: new Date().toISOString(),
              ...(live?.sedId ? { sedId: live.sedId } : {}),
              ...(typeof live?.sedVersion === 'number' ? { sedVersion: live.sedVersion } : {}),
              ...(exhausted ? { exhausted } : {}),
            },
          },
        },
      }));
      clearGeneratingDraft();
      notifySentimentEvidence(
        message,
        exhausted
          ? { label: 'Run again', onClick: () => { void get().retrySentimentEvidenceHold(runId); } }
          : reason === 'sed-rebuild-required'
            ? { label: 'Rebuild SED', onClick: () => { void get().rebuildSiteSed(site.id); } }
            : undefined,
      );
    };
    // `clearDraft: false` when a NEWER attempt for the site superseded this
    // run: the site's sessionStorage draft may belong to that newer attempt.
    const dropHeldRun = (options: { clearDraft?: boolean } = {}) => {
      set((s) => (s.siteRag.activeGenerations?.[runId]
        ? { siteRag: { ...s.siteRag, ...withoutRun(s) } }
        : {}));
      if (options.clearDraft !== false) clearGeneratingDraft();
    };
    const isNewestSentimentAttempt = () => _latestSentimentAttemptBySite.get(site.id) === runId;

    type SentimentRetryState = { dispatches: number; afterScanRefusals: number; notBeforeMs?: number };

    const watchSentimentEvidence = (retry: SentimentRetryState) => {
      disposeSentimentEvidenceWaiter(runId);
      const waitStartedAtMs = Date.parse(runStartedAt);
      let active = true;
      let unsubscribe: (() => void) | null = null;
      let interval: ReturnType<typeof setInterval> | null = null;
      const evaluate = () => {
        if (!active) return;
        // Cancelled (or replaced) while waiting: stop watching, send nothing.
        if (!get().siteRag.activeGenerations?.[runId]?.waitingForSiteEvidence) {
          disposeSentimentEvidenceWaiter(runId);
          return;
        }
        // A newer attempt for this site (or the auth boundary) superseded it.
        if (!isNewestSentimentAttempt()) {
          disposeSentimentEvidenceWaiter(runId);
          dropHeldRun({ clearDraft: false });
          return;
        }
        const next = nextSentimentWaitStep({
          site: liveSiteNow(),
          nowMs: Date.now(),
          waitStartedAtMs,
          notBeforeMs: retry.notBeforeMs,
        });
        if (next.step === 'wait') return;
        disposeSentimentEvidenceWaiter(runId);
        if (next.step === 'drop') dropHeldRun();
        else if (next.step === 'hold') recordSentimentHold(next.reason, next.exhausted);
        else void dispatch(retry, { background: true });
      };
      _sentimentEvidenceWaiters.set(runId, () => {
        active = false;
        unsubscribe?.();
        if (interval) clearInterval(interval);
      });
      syncSentimentEvidenceLeaveWarning();
      // Same one-shot store-subscription pattern as rebuildSiteSed's AMP-2
      // reapply trigger; skipped when `useStore` is mocked without subscribe.
      const subscribable = useStore as unknown as { subscribe?: (cb: () => void) => () => void };
      if (typeof subscribable.subscribe === 'function') unsubscribe = subscribable.subscribe(evaluate);
      interval = setInterval(evaluate, SENTIMENT_EVIDENCE_WAIT_TICK_MS);
      evaluate();
    };

    const holdForSiteEvidence = (
      reason: SentimentEvidenceNotReadyReason,
      retry: SentimentRetryState,
      options: { background?: boolean } = {},
    ): undefined => {
      if (reason === 'sed-rebuild-required') {
        // Needs a rebuild: stop and explain; never auto-runs.
        disposeSentimentEvidenceWaiter(runId);
        recordSentimentHold(reason);
        return undefined;
      }
      if (
        retry.dispatches >= SENTIMENT_EVIDENCE_MAX_AUTO_DISPATCHES
        || retry.afterScanRefusals > SENTIMENT_EVIDENCE_MAX_AFTER_SCAN_RETRIES
      ) {
        // The evidence kept changing under repeated automatic attempts (a
        // refusal after a completed scan gets one retry: each repeats the
        // scan). Stop and say Public Sentiment did not start; never loop.
        disposeSentimentEvidenceWaiter(runId);
        recordSentimentHold(reason, 'retries');
        return undefined;
      }
      const alreadyWaiting = Boolean(get().siteRag.activeGenerations?.[runId]?.waitingForSiteEvidence);
      upsertRun(
        { progress: 0, progressStage: SENTIMENT_EVIDENCE_WAIT_STAGE, waitingForSiteEvidence: true },
        { background: options.background },
      );
      // The wait is in-memory by design (a closed page drops it, nothing was
      // sent), so the wizard's reload bridge draft must not outlive it.
      clearGeneratingDraft();
      watchSentimentEvidence(retry);
      if (!alreadyWaiting && get().siteRag.activeGenerations?.[runId]?.waitingForSiteEvidence) {
        notifySentimentEvidence(SENTIMENT_EVIDENCE_WAITING_NOTICE);
      }
      return undefined;
    };

    // Hoisted: the evidence watcher above dispatches a held run through here
    // (`background: true` — no user action at that moment).
    async function dispatch(
      retry: SentimentRetryState,
      options: { background?: boolean } = {},
    ): Promise<string | undefined> {
      const background = options.background === true;
      upsertRun({
        progress: 0,
        progressStage: 'Queued…',
        ...(isPublicSentiment ? { waitingForSiteEvidence: false } : {}),
      }, { background });

      try {
        // The watcher's dispatch shares the requester capture's module load.
        const expectedUid = background ? await (requesterUid ?? Promise.resolve(null)) : null;
        const { auth } = background ? await loadFirebaseForSentimentWait() : await import('../firebase');
        const user = auth.currentUser;
        if (background) {
          // A held run belongs to the session that requested it: signed out
          // (idle logout) or a different user now means drop it silently —
          // never a "Sign in required" failure, never another user's token.
          if (!user || (user.uid ?? null) !== expectedUid) {
            dropHeldRun();
            return undefined;
          }
        } else if (isPublicSentiment && !requesterUid) {
          requesterUid = Promise.resolve(user?.uid ?? null);
        }
        if (!user) throw new Error('Sign in required to generate reports');

        const token = await user.getIdToken();
        // Template→report linkage (Phase 1): thread the client's ACTIVE template
        // (the one selected in Template Studio at generation time) through to the
        // job so the persisted report doc can be stamped with templateId +
        // templateVersionUsed. Without this, reports were never linked to the
        // template they ran under — so "Apply to existing reports" found nothing
        // and the per-report templateStale banner never appeared. Purely additive:
        // when no template is active these fields stay absent and behaviour is
        // unchanged. We capture BOTH the id and the active template's monotonic
        // `version` so the report records the EXACT version it was generated under.
        const activeTemplateId = get().activeTemplateId;
        const activeTemplate = activeTemplateId
          ? get().reportTemplates.find(t => t.id === activeTemplateId)
          : undefined;
        const body = {
          schemaVersion: 2 as const,
          clientRunId: runId,
          siteId,
          reportTypeId,
          mode,
          parentReportId,
          typedPreferences,
          companyArchetypeOverride,
          documentSelection,
          siteContextOverride,
          siteConfigOverrides,
          // Only include when an active template exists (additive linkage).
          ...(activeTemplate
            ? { templateId: activeTemplate.id, templateVersion: activeTemplate.version }
            : {}),
        };
        const res = await authedFetch('/api/report-generation/jobs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          // The enqueue endpoint answers a rejected request with BOTH a generic
          // headline and `details: Array<{path, message}>` naming exactly which
          // field failed validation. This branch used to read only `error`, so
          // the panel showed "Invalid report generation request" and the actual
          // cause — the one thing needed to fix it — was discarded on arrival.
          // The server logs the 400 as a bare WARNING with no payload, so once
          // the client dropped `details` the reason existed nowhere at all, and
          // every diagnosis had to start by reproducing the failure by hand.
          const errBody = await res.json().catch(() => ({})) as {
            error?: string;
            code?: string;
            details?: unknown;
          };
          // Public Sentiment race safety: a build that started between the
          // preflight and the server's check (or evidence the client cannot
          // judge — stale intent, documents or storage path) is the server's
          // 409 `sentiment-evidence-not-ready`. Hold, never fail.
          if (isPublicSentiment) {
            const notReady = parseSentimentEvidenceNotReadyResponse(res.status, errBody);
            if (notReady) {
              // Cancelled while the request was in flight (the run left the
              // map; nothing else removes a job-less run): the refusal needs
              // no hold, and a new wait must not resurrect the run.
              if (!get().siteRag.activeGenerations?.[runId]) return undefined;
              // Superseded while in flight by a newer attempt for this site (or
              // the auth boundary): drop it silently so it cannot wait beside
              // the newer run and dispatch a second scan for the same site.
              if (!isNewestSentimentAttempt()) {
                dropHeldRun({ clearDraft: false });
                return undefined;
              }
              return holdForSiteEvidence(notReady.reason, {
                dispatches: retry.dispatches + 1,
                afterScanRefusals: retry.afterScanRefusals + (notReady.afterScan ? 1 : 0),
                notBeforeMs: Date.now() + SENTIMENT_EVIDENCE_RETRY_DELAY_MS,
              }, { background: true });
            }
          }
          // The machine `code` (PARENT_REPORT_NOT_FOUND, PARENT_REPORT_MISMATCH,
          // …) used to be discarded here, so a rejected update reached the user
          // as raw server prose with no next step. The shared formatter keeps
          // the headline + bounded details and appends actionable guidance.
          throw new Error(formatEnqueueRejection(res.status, errBody));
        }
        const data = (await res.json()) as { jobId: string };
        upsertRun({ jobId: data.jobId, progressStage: 'Queued…' }, { background });
        return data.jobId;
      } catch (e) {
        const message = (e as Error)?.message || 'Failed to enqueue report generation';
        set((s) => {
          const runs = { ...(s.siteRag.activeGenerations ?? {}) };
          delete runs[runId];
          return {
            siteRag: {
              ...s.siteRag,
              activeGenerations: runs,
              isLoading: Object.keys(runs).length > 0,
              error: message,
              generationOutcomes: {
                ...(s.siteRag.generationOutcomes ?? {}),
                [runId]: {
                  id: runId,
                  siteId,
                  reportTypeId,
                  finishedAt: new Date().toISOString(),
                  status: 'failed',
                  error: message,
                },
              },
            },
          };
        });
        return undefined;
      }
    }

    const firstAttempt: SentimentRetryState = { dispatches: 0, afterScanRefusals: 0 };
    if (isPublicSentiment) {
      // A new attempt supersedes any earlier hold, and any run still waiting
      // for evidence, for this site: one Public Sentiment request per site.
      // An older request still in flight is superseded too — if the server
      // refuses it, it is dropped instead of waiting again (see the 409 branch).
      _latestSentimentAttemptBySite.set(site.id, runId);
      forgetSentimentHoldRetriesForSite(site.id);
      const supersededWaits = Object.values(get().siteRag.activeGenerations ?? {})
        .filter(run => run.id !== runId && run.siteId === site.id && run.waitingForSiteEvidence)
        .map(run => run.id);
      for (const id of supersededWaits) disposeSentimentEvidenceWaiter(id);
      const hasSiteHold = Object.values(get().siteRag.sentimentEvidenceHolds ?? {})
        .some(hold => hold.siteId === site.id);
      if (supersededWaits.length > 0 || hasSiteHold) {
        set((s) => {
          const runs = { ...(s.siteRag.activeGenerations ?? {}) };
          for (const id of supersededWaits) delete runs[id];
          return { siteRag: { ...s.siteRag, activeGenerations: runs, sentimentEvidenceHolds: siteHoldsExcept(s) } };
        });
      }
      const preflight = classifySentimentSiteEvidence(site);
      if (!preflight.ready) {
        if (preflight.reason === 'sed-rebuilding') {
          // Settle the requester capture now, at enqueue time (the module is
          // already loaded in the app, so this is a microtask). A newer
          // attempt that starts meanwhile still wins: the watcher's first
          // evaluation drops a run that is no longer the newest.
          requesterUid = loadFirebaseForSentimentWait()
            .then(({ auth }) => auth.currentUser?.uid ?? null, () => null);
          await requesterUid;
        }
        return holdForSiteEvidence(preflight.reason, firstAttempt);
      }
    }
    return dispatch(firstAttempt);
  },

  cancelReportGenerationJob: async (runId) => {
    const run = get().siteRag.activeGenerations?.[runId];
    const jobId = run?.jobId;
    // A Public Sentiment run held for site evidence has no job yet: stop its
    // watcher so it can never dispatch after the user cancelled it.
    const wasHeldForEvidence = disposeSentimentEvidenceWaiter(runId)
      || Boolean(run?.waitingForSiteEvidence);

    // DE-PHANTOM/CANCEL — tombstone this run BEFORE the local delete so the
    // very next onSnapshot tick cannot re-hydrate the card while the job doc
    // is still 'queued'/'running' (the server cancel flip lags, and a
    // server-cancel failure would otherwise leave the card permanently
    // re-hydrated). We seed by runKey AND by jobId because the snapshot
    // rebuild keys on `job.clientRunId ?? job.id` — covering both guarantees
    // the suppression matches regardless of whether the job carries a
    // clientRunId. The tombstone is evicted when the terminal reconcile
    // settles this job (applyTerminalJob) or on subscription teardown.
    _cancelledRunKeys.add(runId);
    if (jobId) _cancelledRunKeys.add(jobId);

    // Server-side cancel for runs that reached the backend (have a jobId).
    // Firestore rules make the job doc client-read-only, so this MUST go
    // through the server cancel endpoint. Best-effort — even if it fails
    // (already terminal / offline) we still clear the local card below.
    if (jobId) {
      try {
        const { auth } = await import('../firebase');
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          await authedFetch(`/api/report-generation/jobs/${encodeURIComponent(jobId)}/cancel`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch (e) {
        console.warn('[cancelReportGenerationJob] server cancel failed (clearing card anyway):', (e as Error)?.message);
      }
    }

    // Drop the local run so the progress card disappears immediately. The
    // Firestore subscription also drops it once the doc flips to 'cancelled';
    // we don't wait for that round-trip.
    set((s) => {
      const runs = { ...(s.siteRag.activeGenerations ?? {}) };
      delete runs[runId];
      const remaining = Object.values(runs);
      const latest = remaining.length
        ? [...remaining].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0]
        : null;
      return {
        siteRag: {
          ...s.siteRag,
          activeGenerations: runs,
          isLoading: remaining.length > 0,
          progress: normalizeProgressPercent(latest?.progress ?? 0),
          progressStage: latest?.progressStage ?? '',
        },
      };
    });
    // No job doc will ever settle a held run, so nothing else would sweep the
    // wizard's sessionStorage "generating" draft (a phantom row otherwise).
    if (wasHeldForEvidence && run) {
      void import('../lib/reportDialogDraft')
        .then(({ clearReportDialogDraft }) => clearReportDialogDraft(run.siteId, run.reportTypeId))
        .catch(() => { /* best-effort */ });
    }
  },

  dismissSentimentEvidenceHold: (holdId) => {
    _sentimentEvidenceHoldRetries.delete(holdId);
    set((s) => {
      if (!s.siteRag.sentimentEvidenceHolds?.[holdId]) return {};
      const holds = { ...s.siteRag.sentimentEvidenceHolds };
      delete holds[holdId];
      return { siteRag: { ...s.siteRag, sentimentEvidenceHolds: holds } };
    });
  },

  retrySentimentEvidenceHold: async (holdId) => {
    const retry = _sentimentEvidenceHoldRetries.get(holdId);
    _sentimentEvidenceHoldRetries.delete(holdId);
    // A second click, or a hold already superseded / dismissed: nothing to do.
    if (!retry || !get().siteRag.sentimentEvidenceHolds?.[holdId]) return undefined;
    // The new attempt supersedes this hold (enqueue clears the site's holds).
    return retry.run();
  },

  publicSentimentEvidenceDisposition: (target) => publicSentimentEvidenceDisposition(get().siteRag, target),

  subscribeReportGenerationJobs: (uid) => {
    resetReportGenerationJobsSubscription();
    // Fresh user => fresh re-arm budget and a fresh auth-recovery attempt. Same
    // uid (AuthGate re-firing on token refresh, or our own re-arm) keeps the
    // running budget so a persistent error still terminates.
    if (uid !== _reportGenerationJobsSubUid) {
      _reportGenerationJobsSubUid = uid;
      _reportGenerationJobsResubAttempts = 0;
      _reportGenerationJobsAuthRecoveryTried = false;
    }
    void (async () => {
      const { db } = await import('../firebase');
      const { collection, doc, getDoc, getDocs, onSnapshot, query, where } = await import('firebase/firestore');
      const { REPORT_GENERATION_JOBS_COLLECTION } = await import('../types/reportGenerationJob');
      const { forceIdTokenRefresh, JOBS_LISTENER_AUTH_LOST_MESSAGE } = await import(
        '../lib/reportGeneration/reportGenerationJobsAuthRecovery'
      );
      const collPath = `users/${uid}/${REPORT_GENERATION_JOBS_COLLECTION}`;
      const q = query(
        collection(db, collPath),
        where('status', 'in', ['queued', 'running']),
      );

      type ReportGenerationJob = import('../types/reportGenerationJob').ReportGenerationJob;
      const TERMINAL_STATUSES: ReportGenerationJob['status'][] = ['succeeded', 'failed', 'cancelled'];

      // DE-PHANTOM — reconcile a terminal job doc into the local store from a
      // job OBJECT (not a jobId). This is the authoritative cleanup: it runs
      // for ANY terminal status (succeeded/failed/cancelled) and is
      // wasTracked-INDEPENDENT, so a job that was already terminal at subscribe
      // time (post-reload phantom) or a job that failed while the tab was
      // closed still clears/records its card. Live in-flight progress is
      // untouched because terminal jobs are by definition no longer
      // queued/running.
      const reconcileTerminalJob = (
        job: ReportGenerationJob,
        opts?: { skipReportFetch?: boolean; quiet?: boolean },
      ) => {
        if (!TERMINAL_STATUSES.includes(job.status)) return;
        const terminalStatus = job.status as 'succeeded' | 'failed' | 'cancelled';
        const runKey = job.clientRunId ?? job.id;
        // DE-PHANTOM/CANCEL — this job settled terminal; drop any tombstone so
        // the cancelled-run suppression set never leaks across runs.
        _cancelledRunKeys.delete(runKey);
        _cancelledRunKeys.delete(job.id);

        set((s) => {
          const runs = { ...(s.siteRag.activeGenerations ?? {}) };
          // No-op if absent (untracked / already-removed) — reconcile is
          // unconditional so any terminal doc records an outcome regardless of
          // in-session tracking.
          delete runs[runKey];
          const outcomes = {
            ...(s.siteRag.generationOutcomes ?? {}),
            [runKey]: {
              id: runKey,
              jobId: job.id,
              siteId: job.siteId,
              reportTypeId: job.reportTypeId,
              finishedAt: job.finishedAt ?? new Date().toISOString(),
              status: terminalStatus,
              ...(job.error ? { error: job.error } : {}),
              ...(job.errorCode ? { errorCode: job.errorCode } : {}),
              ...(job.terminalReason ? { terminalReason: job.terminalReason } : {}),
              ...(job.blockedByProviderCoverage ? { blockedByProviderCoverage: job.blockedByProviderCoverage } : {}),
              ...(job.reportId ? { reportId: job.reportId } : {}),
              // P2.1 — the runner persists the citation-density gate payload on
              // the FAILED job doc (`blockedByCitationDensity` + snapshot); the
              // wizard's CitationDensityOverrideBanner reads it from the outcome.
              // Dropping it here made the banner sync-path-only.
              ...(job.blockedByCitationDensity && job.citationDensity
                ? {
                  citationDensity: job.citationDensity,
                  citationDensityRegenUsed: Boolean(job.siteConfigOverrides?.citationDensityRegenerateFocus),
                }
                : {}),
            },
          };
          const latest = Object.values(runs).sort(
            (a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt),
          )[0] ?? null;
          // RED-ERROR-CLEAR FIX (#8) — resolve the GLOBAL `siteRag.error` banner
          // symmetrically and race-proof.
          //
          // (A) ASYNC SUCCESS MUST CLEAR. The sync path clears the global error
          //     via onReportGenerated; the async terminal path previously only
          //     ever ADDED an error (on failure), never nulled it on success, so
          //     a banner left over from an earlier failed job persisted after a
          //     successful regeneration. Null it explicitly on 'succeeded'.
          //
          // (B) NO STALE RE-CLOBBER. The docChanges path awaits getDoc before
          //     this set, so a slow terminal handler for an OLD failed run
          //     can land AFTER a retry's upsertRun already cleared the error. The
          //     `!latest` guard only protects while another run is still active;
          //     if the retry already finished there's no `latest` and the stale
          //     error would be written back. Also suppress the failed-error write
          //     when a NEWER run for the same site is in flight (superseded).
          const supersededByNewerRun = Object.values(runs).some(
            (r) => r.siteId === job.siteId && r.id !== runKey,
          );
          // QUIET SWEEP — the one-shot login reconcile records OUTCOMES for
          // historical terminal jobs but must never raise the global error
          // banner for them: generationOutcomes is in-memory, so without
          // this every login re-surfaced the user's last failed job as a
          // fresh "Generation Failed" banner (e.g. a long-dead 'Site not
          // found' job). Only LIVE terminal transitions banner.
          const nextGlobalError =
            job.status === 'succeeded'
              ? null
              : job.error && !latest && !supersededByNewerRun && !opts?.quiet
                ? job.error
                : s.siteRag.error;
          return {
            siteRag: {
              ...s.siteRag,
              activeGenerations: runs,
              generationOutcomes: outcomes,
              isLoading: Object.keys(runs).length > 0,
              progress: normalizeProgressPercent(latest?.progress ?? 0),
              progressStage: latest?.progressStage ?? '',
              // Background completion updates availability and outcomes; only
              // navigation may change the report the user is currently reading.
              error: nextGlobalError,
            },
          };
        });
        if (job.status === 'succeeded' && job.reportId && !opts?.skipReportFetch) {
          // COMPOSITE STALE-ON-REPORT-WRITE — a server-side async report job
          // that just succeeded ingests a fresh report for job.siteId. That can
          // move the site's overview verdict / band, so invalidate this site's
          // cached composite for the lazy ensurePortfolioComposites refresh on
          // next view. Invalidation only (no scoring change); idempotent.
          //
          // The one-shot subscribe sweep passes skipReportFetch and instead
          // batches a SINGLE fetchRagReports + per-site stale-stamp after the
          // loop, so N historically-succeeded job docs don't trigger N report
          // refetches on every page load.
          //
          // X-SCORE-1 (scoring audit 2026-07-02) — FETCH FIRST, stamp AFTER.
          // The old order (stamp → void fetch) let the mounted recompute
          // effects consume the stale-stamp IMMEDIATELY, while the slow
          // per-report GCS fetch was still in flight — computeCompositeForSite
          // then saw ZERO reports for the site, persisted an all-pillars
          // "No source reports available" composite with a FRESH
          // lastComputedAt, and the 24h staleness window LOCKED the poison in
          // (live-proven: Redhead composite computed 4s after the feasibility
          // report landed, Deep Dive listing no reports). Stamping after the
          // fetch resolves means the recompute reads the store WITH the new
          // report. Fetch failure still stamps (a stale composite that
          // recomputes later is strictly better than a poisoned-fresh one).
          if (job.siteId) {
            const siteIdToStamp = job.siteId;
            void get()
              .fetchRagReports()
              .catch(() => { /* stamp regardless — see above */ })
              .finally(() => get()._staleStampSiteComposite(siteIdToStamp));
          } else {
            void get().fetchRagReports();
          }
        }
        // FIX #94 — a terminal job leaves behind its sessionStorage
        // report-dialog draft (step:'generating'). SiteLandingPage's
        // displayInflightRuns synthesizes a phantom "Continuing generation… 0%"
        // run from any such draft that has no live Firestore run, so a draft
        // that is never swept becomes a permanent ghost. Clear it here for ANY
        // terminal status (succeeded/failed/cancelled). The draft key is
        // (siteId, reportType) — both carried on the job.
        void import('../lib/reportDialogDraft').then(({ clearReportDialogDraft }) => {
          clearReportDialogDraft(job.siteId, job.reportTypeId);
        });
      };

      // DE-PHANTOM — thin wrapper for the docChanges('removed') path: re-read
      // the (now-terminal) job doc and delegate to reconcileTerminalJob. A doc
      // that left the queued/running query is terminal; re-reading picks up its
      // final status/error/reportId.
      const applyTerminalJob = async (jobId: string) => {
        const snap = await getDoc(doc(db, collPath, jobId));
        if (!snap.exists()) return;
        reconcileTerminalJob(snap.data() as ReportGenerationJob);
      };

      // DE-PHANTOM — ONE-SHOT terminal reconciliation on subscribe. The live
      // query only watches queued/running, and applyTerminalJob fires solely
      // from its 'removed' docChange — so a job that was ALREADY terminal when
      // the tab loaded (post-reload, or one that failed/was cancelled while the
      // tab was closed) never enters the result set, 'removed' never fires, and
      // the card would render "generating" forever. Sweep those here: query the
      // terminal jobs once and reconcile any whose runKey is not already a
      // settled outcome. Runs BEFORE onSnapshot is registered so the card is
      // already reconciled by the time the running-jobs snapshot lands.
      try {
        const terminalQ = query(
          collection(db, collPath),
          where('status', 'in', TERMINAL_STATUSES),
        );
        // FROZEN-GHOST FIX (2026-06-26) — also read the LIVE queued/running set
        // here so the sweep can compute the FULL universe of run keys that still
        // have a backing job doc (terminal ∪ live). Any tracked jobId-run NOT in
        // that universe has NO doc at all (deleted / orphaned / 404) and must be
        // reconciled — see the absent-doc branch below. Fetch in parallel; the
        // live query is the same one onSnapshot watches, so a doc that is still
        // queued/running is correctly counted as "present" and left untouched.
        const [terminalSnap, liveSnap] = await Promise.all([
          getDocs(terminalQ),
          getDocs(q),
        ]);
        const succeededSiteIds = new Set<string>();
        // Run keys (clientRunId ?? id) that DO have a backing doc right now.
        const docBackedRunKeys = new Set<string>();
        for (const docSnap of liveSnap.docs) {
          const job = docSnap.data() as ReportGenerationJob;
          docBackedRunKeys.add(job.clientRunId ?? job.id);
          docBackedRunKeys.add(job.id);
        }
        const existingReportIds = new Set((get().siteRag.reports ?? []).map((r) => r.id));
        const recentTerminalCutoffMs = Date.now() - 15 * 60_000;
        for (const docSnap of terminalSnap.docs) {
          const job = docSnap.data() as ReportGenerationJob;
          const runKey = job.clientRunId ?? job.id;
          docBackedRunKeys.add(runKey);
          docBackedRunKeys.add(job.id);
          if (get().siteRag.generationOutcomes?.[runKey]) continue;
          const wasTrackedInSession = Boolean(get().siteRag.activeGenerations?.[runKey]);
          reconcileTerminalJob(job, { skipReportFetch: true, quiet: true });
          const finishedMs = Date.parse(job.finishedAt ?? job.updatedAt ?? '');
          const isRecentUntrackedTerminal =
            !existingReportIds.has(job.reportId ?? '')
            && Number.isFinite(finishedMs)
            && finishedMs >= recentTerminalCutoffMs;
          if (
            job.status === 'succeeded'
            && job.reportId
            && job.siteId
            && (wasTrackedInSession || isRecentUntrackedTerminal)
          ) {
            succeededSiteIds.add(job.siteId);
          }
        }

        // FROZEN-GHOST FIX (2026-06-26) — DE-ORPHAN: a tracked jobId-run whose
        // doc is in NEITHER the live NOR the terminal set has no backing doc at
        // all (deleted by cleanup/TTL, never persisted, or orphaned while its
        // Cloud Run execution kept running — the live evidence: execution ran to
        // completion but the Firestore doc was 404 and both queries returned it
        // nowhere). The healthy onSnapshot rebuild would normally drop such a run
        // (it rebuilds `runs` from docs), but if the listener detached on a
        // transient error and never re-delivered an empty snapshot, the card
        // froze on its last frame — and a frozen heartbeat then false-flips it to
        // "RUN STALLED". The heartbeat stall-fix does NOT cover this. Reconcile
        // every absent-doc tracked run HERE (this sweep re-runs on each listener
        // re-arm too, so a fresh subscription self-heals the ghost):
        //   - If a report for this run's (siteId, reportType) now exists and was
        //     created AT/AFTER the run started, the execution finished without
        //     leaving a sweepable terminal doc → finalize the card as SUCCESS.
        //   - Otherwise we cannot prove success, so CLEAR the ghost (record no
        //     success outcome; never assert a verdict we can't back).
        // Sync-path runs (no jobId) are intentionally skipped — they are never
        // backed by a job doc and are preserved by the onSnapshot merge.
        const trackedRuns = Object.values(get().siteRag.activeGenerations ?? {});
        const reports = get().siteRag.reports ?? [];
        for (const run of trackedRuns) {
          if (!run.jobId) continue; // sync-path run — not doc-backed by design
          if (_cancelledRunKeys.has(run.id) || _cancelledRunKeys.has(run.jobId)) continue;
          if (docBackedRunKeys.has(run.id) || docBackedRunKeys.has(run.jobId)) continue;
          // No backing doc anywhere → this card is a ghost. Try to finalize it
          // as success from the report list, else clear it.
          const producedReport = run.reportTypeId
            ? reports.find(
                (r) =>
                  r.siteId === run.siteId &&
                  r.reportType === run.reportTypeId &&
                  typeof r.createdAt === 'string' &&
                  Date.parse(r.createdAt) >= Date.parse(run.startedAt) - 60_000,
              )
            : undefined;
          set((s) => {
            const runs = { ...(s.siteRag.activeGenerations ?? {}) };
            delete runs[run.id];
            const outcomes = producedReport
              ? {
                  ...(s.siteRag.generationOutcomes ?? {}),
                  [run.id]: {
                    id: run.id,
                    jobId: run.jobId,
                    siteId: run.siteId,
                    reportTypeId: run.reportTypeId,
                    finishedAt: producedReport.createdAt ?? new Date().toISOString(),
                    status: 'succeeded' as const,
                    reportId: producedReport.id,
                  },
                }
              : (s.siteRag.generationOutcomes ?? {});
            const latest = Object.values(runs).sort(
              (a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt),
            )[0] ?? null;
            return {
              siteRag: {
                ...s.siteRag,
                activeGenerations: runs,
                generationOutcomes: outcomes,
                isLoading: Object.keys(runs).length > 0,
                progress: normalizeProgressPercent(latest?.progress ?? 0),
                progressStage: latest?.progressStage ?? '',
                // Recover the run without navigating away from the open report.
              },
            };
          });
          if (producedReport && run.siteId) succeededSiteIds.add(run.siteId);
          console.warn(
            `[subscribeReportGenerationJobs] de-orphaned ghost run ${run.id} (jobId=${run.jobId}) — ${producedReport ? 'finalized as success' : 'cleared (no backing doc)'}`,
          );
        }

        // Batch the heavy side-effects ONCE for the whole sweep so N
        // historically-succeeded jobs don't trigger N report refetches.
        // X-SCORE-1 — FETCH FIRST, stamp AFTER (same race as
        // reconcileTerminalJob: stamping before the report fetch resolved let
        // the recompute effects persist a zero-report "No source reports
        // available" composite with a fresh 24h lock).
        if (succeededSiteIds.size > 0) {
          void get()
            .fetchRagReports()
            .catch(() => { /* stamp regardless */ })
            .finally(() => {
              for (const siteId of succeededSiteIds) get()._staleStampSiteComposite(siteId);
            });
        }
      } catch (e) {
        // Best-effort: if the one-shot sweep fails (offline / rules), the live
        // 'removed' path still reconciles jobs that terminate during the
        // session. Never block the live subscription on this.
        // `permission-denied` is reported once, with a remedy, by the listener's
        // onError below — logging it again here just doubled the console noise
        // (every denial produced a matched pair of warnings).
        const sweepCode = (e as { code?: string } | null)?.code;
        if (sweepCode !== 'permission-denied') {
          console.warn(
            `[subscribeReportGenerationJobs] one-shot terminal sweep failed (${sweepCode ?? 'unknown'}):`,
            (e as Error)?.message,
          );
        }
      }

      _reportGenerationJobsUnsub = onSnapshot(q, (snap) => {
        // A healthy snapshot — clear the re-arm budget so transient blips spread
        // across a long session never accumulate toward the cap, and re-allow a
        // future auth recovery now that the credential is demonstrably good.
        _reportGenerationJobsResubAttempts = 0;
        _reportGenerationJobsAuthRecoveryTried = false;
        set((s) => {
          const prevRuns = s.siteRag.activeGenerations ?? {};
          const prevBeats = s.generationHeartbeats;
          const runs: NonNullable<SiteRagState['activeGenerations']> = {};
          const beats: Record<string, string> = {};
          // HEARTBEAT-IDENTITY (2026-10-07) — the worker re-stamps
          // heartbeatAt / leaseExpiresAt / progressSeq on every job doc every
          // 30s (HEARTBEAT_TICK_MS), so this listener fires every 30s for the
          // whole run. Rebuilding every run object on each tick re-rendered
          // the app shell (bare `useStore()` in App) and re-ran the site page's
          // memo chain down to the Path-to-GO build (≈1.2s long task per tick
          // on a large SED). A run object is now REUSED when its visible
          // fields are unchanged, the beat lives in the root
          // `generationHeartbeats` slice that only the stall indicators read,
          // and a tick that moved nothing visible returns the SAME state.
          let runsChanged = false;
          let beatsChanged = false;
          for (const docSnap of snap.docs) {
            const job = docSnap.data() as import('../types/reportGenerationJob').ReportGenerationJob;
            const runKey = job.clientRunId ?? job.id;
            // DE-PHANTOM/CANCEL — a just-cancelled run is tombstoned locally
            // until its job doc flips terminal. While the doc still reads
            // queued/running, skip re-hydrating its card so Cancel STAYS
            // applied ("Cancel does nothing" was this loop re-adding the card
            // within one snapshot tick). All other running jobs hydrate and
            // update progress normally.
            if (_cancelledRunKeys.has(runKey) || _cancelledRunKeys.has(job.id)) continue;
            // ZOMBIE-RUN GUARD (2026-08-13, Redhead; tightened per Codex
            // review on #130-#132) — a job doc whose runner died keeps
            // status 'running' with a frozen heartbeat; hydrating it
            // re-attached the Rerun modal to a dead "88%" forever and made
            // the audit defer on "generation in-flight". Mirror the server
            // reaper's EXACT eligibility so the UI never declares dead what
            // the reaper would not reap:
            //   - RUNNING jobs only — the reaper scans status==='running';
            //     a queued job has no heartbeat yet and a delayed dispatcher
            //     may still launch it, so it stays visible.
            //   - a live attempt lease (leaseExpiresAt in the future) means
            //     a worker may still complete — keep the card.
            const leaseMs = job.leaseExpiresAt ? Date.parse(job.leaseExpiresAt) : Number.NaN;
            const leaseLive = Number.isFinite(leaseMs) && leaseMs > Date.now();
            const beatIso = job.heartbeatAt ?? job.updatedAt ?? job.startedAt ?? job.createdAt;
            const beatMs = beatIso ? Date.parse(beatIso) : Number.NaN;
            const beatStale = !Number.isFinite(beatMs)
              || Date.now() - beatMs > REPORT_GENERATION_STALE_HEARTBEAT_MS;
            if (job.status === 'running' && beatStale && !leaseLive) {
              console.warn(
                `[report-generation] skipping stalled job ${job.id} (${job.reportTypeId}) — no heartbeat since ${beatIso ?? 'unknown'}; the reaper will fail it and Rerun starts fresh.`,
              );
              continue;
            }
            const next: NonNullable<SiteRagState['activeGenerations']>[string] = {
              id: runKey,
              jobId: job.id,
              siteId: job.siteId,
              reportTypeId: job.reportTypeId,
              // Countdown stays anchored to enqueue acceptance. A worker may
              // stamp/re-stamp `startedAt` when it claims or reclaims the job;
              // using that field would make the displayed ETA jump backwards.
              startedAt: job.createdAt,
              progress: normalizeProgressPercent(job.progress ?? 0),
              progressStage: job.progressStage ?? 'Running…',
              estimatedDurationMs: job.estimatedDurationMs
                ?? estimatedReportGenerationDurationMs(
                  job.reportTypeId,
                  job.siteConfigOverrides?.generationTier,
                ),
            };
            // Same visible fields → keep the previous object (and everything
            // memoised on it). `shallow` also catches a key the previous run
            // carried that this doc does not (e.g. a cleared evidence wait).
            const prev = prevRuns[runKey];
            const run = prev && shallow(prev, next) ? prev : next;
            if (run !== prev) runsChanged = true;
            runs[runKey] = run;
            // Server liveness beat — the stall detectors key off THIS changing
            // (never older than ~30s while alive), not off the % moving, so a
            // long phase with a flat bar is not falsely flagged stalled. Falls
            // back to updatedAt so a job written before the heartbeatAt field
            // still has a beat.
            const beat = job.heartbeatAt ?? job.updatedAt;
            if (beat) {
              beats[runKey] = beat;
              if (prevBeats[runKey] !== beat) beatsChanged = true;
            }
          }
          // Preserve sync-path runs (no jobId) while merging async jobs
          for (const [key, run] of Object.entries(prevRuns)) {
            if (!run.jobId && !runs[key]) runs[key] = run;
          }
          // A run that LEFT the live query (terminal / cancelled) is a change
          // too — the loop above only visits docs still present.
          if (!runsChanged) {
            for (const key of Object.keys(prevRuns)) {
              if (!(key in runs)) { runsChanged = true; break; }
            }
          }
          if (!beatsChanged) {
            for (const key of Object.keys(prevBeats)) {
              if (!(key in beats)) { beatsChanged = true; break; }
            }
          }
          const values = Object.values(runs);
          const latest = values.length
            ? values.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0]
            : null;
          const isLoading = values.length > 0;
          const progress = normalizeProgressPercent(latest?.progress ?? 0);
          const progressStage = latest?.progressStage ?? '';
          const siteRagChanged = runsChanged
            || s.siteRag.isLoading !== isLoading
            || s.siteRag.progress !== progress
            || s.siteRag.progressStage !== progressStage;
          // Heartbeat-only tick (or a no-op echo): hand zustand the SAME state
          // object so it notifies no subscriber at all.
          if (!siteRagChanged && !beatsChanged) return s;
          return {
            ...(siteRagChanged
              ? {
                  siteRag: {
                    ...s.siteRag,
                    activeGenerations: runsChanged ? runs : prevRuns,
                    isLoading,
                    progress,
                    progressStage,
                  },
                }
              : {}),
            ...(beatsChanged ? { generationHeartbeats: beats } : {}),
          };
        });

        for (const change of snap.docChanges()) {
          if (change.type === 'removed') {
            void applyTerminalJob(change.doc.id);
          }
        }
      }, (err) => {
        // FROZEN-CARD FIX (2026-06-17) — the running-jobs listener detached on a
        // transient error (permission re-eval as the job doc gains fields, an
        // index hiccup, a network blip). With NO error handler it froze the
        // progress card on its last frame ("QUEUED… 0%") even as the job ran and
        // completed (it never saw the running/progress/succeeded writes). Re-arm:
        // re-subscribe after a backoff so a fresh listener + the one-shot terminal
        // sweep reconcile the card. The cap prevents a tight loop on a persistent
        // error (the card then recovers on the next navigation / reload).
        const code = (err as { code?: string } | null)?.code;
        if (_reportGenerationJobsResubTimer) {
          clearTimeout(_reportGenerationJobsResubTimer);
          _reportGenerationJobsResubTimer = null;
        }

        // AUTH-DENIAL FIX (2026-08-03) — `permission-denied` here is a MISSING
        // CREDENTIAL, never a rules gap: the deployed rule allows this path for
        // the owner unconditionally, so the only way to be denied is
        // `request.auth == null` (proof + verification method in
        // `reportGenerationJobsAuthRecovery.ts`). Backoff cannot fix that — the
        // old handler retried six times, logged six identical warnings, and
        // left the progress card frozen. Force a token refresh ONCE instead.
        if (code === 'permission-denied') {
          if (_reportGenerationJobsAuthRecoveryTried) {
            console.warn(
              '[subscribeReportGenerationJobs] still denied after a forced token refresh — '
              + 'the Firebase session can no longer mint an ID token. Sign out and back in.',
            );
            set((s) => ({ siteRag: { ...s.siteRag, error: JOBS_LISTENER_AUTH_LOST_MESSAGE } }));
            return;
          }
          _reportGenerationJobsAuthRecoveryTried = true;
          void forceIdTokenRefresh().then((outcome) => {
            if (outcome !== 'refreshed') {
              console.warn(
                `[subscribeReportGenerationJobs] auth recovery failed (${outcome}) — `
                + 'report progress cannot be tracked until you sign in again.',
              );
              set((s) => ({ siteRag: { ...s.siteRag, error: JOBS_LISTENER_AUTH_LOST_MESSAGE } }));
              return;
            }
            // Credential re-minted — re-subscribe once so the sweep + listener
            // reconcile whatever settled while we were denied.
            if (uid) get().subscribeReportGenerationJobs(uid);
          });
          return;
        }

        console.warn(
          `[subscribeReportGenerationJobs] listener error (${code ?? 'unknown'}) — re-arming:`,
          (err as Error)?.message,
        );
        if (_reportGenerationJobsResubAttempts >= REPORT_GEN_JOBS_MAX_RESUBS) {
          console.warn('[subscribeReportGenerationJobs] re-subscribe cap reached; progress recovers on next navigation/reload.');
          return;
        }
        _reportGenerationJobsResubAttempts += 1;
        const backoffMs = Math.min(2000 * _reportGenerationJobsResubAttempts, 15000);
        _reportGenerationJobsResubTimer = setTimeout(() => {
          _reportGenerationJobsResubTimer = null;
          if (uid) void get().subscribeReportGenerationJobs(uid);
        }, backoffMs);
      });
    })();
  },

  unsubscribeReportGenerationJobs: () => {
    resetReportGenerationJobsSubscription();
  },

  // LIVE-SITES — real-time site state for company-scoped fields (the
  // motivating one is `pinnedReportIds`: a pin/unpin by one user must reach
  // every user with access WITHOUT a reload). `fetchSites` only ran a one-shot
  // `getDocs` at login, so cross-user updates landed only on the next reload.
  // This registers `onSnapshot` listeners that MIRROR `fetchSites`'s exact two
  // RBAC queries and funnel every snapshot through the SAME freshest-wins
  // `mergeSitesIntoStore` merge.
  //
  // SECURITY (no sibling-site leak): the listeners use byte-identical query
  // constraints to `fetchSites` —
  //   1. OWN sites:     `collection(users/{uid}/sites)`        — the caller's
  //                     own subtree only.
  //   2. COMPANY sites: `collectionGroup('sites')` filtered by
  //                     `where('isCompanyShared','==',true)` +
  //                     `where('companyId','in', chunk)` where every chunk id
  //                     comes ONLY from `resolveSubtreeCompanyIds` (own company
  //                     + verified descendants; degrades to self-only on
  //                     permission-denied). The per-doc Firestore rule
  //                     re-verifies subtree membership, so a tampered id list
  //                     cannot widen access. This is the SAME scope the proven
  //                     `fetchSites` company-shared sweep uses — it does NOT
  //                     broaden it.
  //
  // `fetchSites` remains the authoritative INITIAL load (it runs the lazy
  // migrations, layer-loading, audit passes); this listener layers LIVE field
  // updates on top of that loaded state. It preserves locally-loaded
  // `siteLayers` + transient annotations (`preserveLocalLayers`) so a live tick
  // never blanks the map or breaks owner-path writes, and it does NOT touch the
  // legacy derive-from-reports fallback (that path has no live site docs).
  subscribeSites: (uid) => {
    const companyId = get().companyId ?? null;
    // Idempotent: same (uid, company) scope already live → no-op (never
    // double-subscribe). Different scope → tear the old listeners down first.
    if (_sitesOwnUnsub && _sitesSubUid === uid && _sitesSubCompanyId === companyId) {
      return;
    }
    resetSitesSubscription();
    _sitesSubUid = uid;
    _sitesSubCompanyId = companyId;

    type SiteEntity = import('../types/site').SiteEntity;

    // Apply an incoming batch of canonical site docs onto the store via the
    // shared freshest-wins merge. The listener delivers a SUBSET of the full
    // site set (own-only, or one company chunk), so we merge that subset into
    // the existing in-store sites BY ID — present sites are field-updated,
    // genuinely-new shared sites are appended; sites not in this batch are left
    // untouched (a peer's chunk / the user's own docs).
    const applyLiveSites = (incoming: SiteEntity[]) => {
      if (incoming.length === 0) return;
      set((state) => {
        const incomingIds = new Set(incoming.map((s) => s.id));
        // Merge the incoming subset (freshest-wins, layers preserved)…
        const mergedSubset = mergeSitesIntoStore({
          current: state.siteRag.sites,
          incoming,
          preserveLocalLayers: true,
        });
        const mergedById = new Map(mergedSubset.map((s) => [s.id, s]));
        // …then splice the merged subset back over the full list in place,
        // preserving order and every site NOT in this batch.
        const next = state.siteRag.sites.map((s) =>
          mergedById.get(s.id) ?? s,
        );
        // Append shared sites that are new to the store (not previously loaded).
        const known = new Set(state.siteRag.sites.map((s) => s.id));
        for (const s of mergedSubset) {
          if (!known.has(s.id) && incomingIds.has(s.id)) next.push(s);
        }
        // IDENTITY — `mergeSitesIntoStore` hands back the in-store object for a
        // site whose data did not change. When every site survived as-is and
        // nothing was appended, return the SAME state so no subscriber (the
        // app shell, the engine, the memoised site page) is notified.
        if (sameArrayElements(next, state.siteRag.sites)) return state;
        return { siteRag: { ...state.siteRag, sites: next } };
      });
    };

    void (async () => {
      const { db } = await import('../firebase');
      const { collection, collectionGroup, query, where, onSnapshot } = await import('firebase/firestore');

      // GUARD: the auth callback may have resolved a DIFFERENT scope (logout /
      // user-swap) between this action firing and the dynamic import settling.
      // Bail if our scope is no longer the live one — never attach a listener
      // for a stale uid/company.
      if (_sitesSubUid !== uid || _sitesSubCompanyId !== companyId) return;

      // ── 1. OWN sites — identical scope to fetchSites' first query. ──
      const ownQ = collection(db, `users/${uid}/sites`);
      _sitesOwnUnsub = onSnapshot(
        ownQ,
        (snap) => {
          const changedDocs = typeof (snap as { docChanges?: () => Array<{ type: string; doc: (typeof snap.docs)[number] }> }).docChanges === 'function'
            ? (snap as { docChanges: () => Array<{ type: string; doc: (typeof snap.docs)[number] }> })
                .docChanges()
                .filter((change) => change.type !== 'removed')
                .map((change) => change.doc)
            : snap.docs;
          const own: SiteEntity[] = changedDocs.flatMap((d) => {
            const data = normalizeSiteEntity(d.data() as SiteEntity);
            const duplicateOf = (data as { duplicateOf?: unknown }).duplicateOf;
            // DUP-1 — the canonical peer-owned doc arrives on the company
            // listener. Never let this user's tombstone overwrite it live.
            if (typeof duplicateOf === 'string' && duplicateOf.trim().length > 0) {
              return [];
            }
            return [{
              ...data,
              id: data.id ?? d.id,
              _sourceScope: 'mine' as const,
            }];
          });
          applyLiveSites(own);
        },
        (err) => {
          // Permission-denied / transient: log, never throw. The one-shot
          // fetchSites already populated the store; losing the live channel
          // degrades to reload-to-update, not data loss.
          console.warn('[subscribeSites] own-sites listener error:', (err as Error)?.message);
        },
      );

      // ── 2. COMPANY-shared sites — identical scope to fetchSites' second
      //       (collectionGroup) query. Skipped entirely when there's no
      //       companyId (own-only), exactly like fetchSites. ──
      if (!companyId) {
        console.debug('[subscribeSites] no companyId — own-sites live listener only');
        return;
      }
      let subtreeCompanyIds: string[];
      try {
        subtreeCompanyIds = await resolveSubtreeCompanyIds(db, companyId);
      } catch (e) {
        console.warn('[subscribeSites] subtree resolve failed — own-sites live listener only:', (e as Error)?.message);
        return;
      }
      // Re-check scope after the async resolve (same guard as above).
      if (_sitesSubUid !== uid || _sitesSubCompanyId !== companyId) return;

      const chunks = chunkIds(subtreeCompanyIds, 30);
      for (const chunk of chunks) {
        const compQ = query(
          collectionGroup(db, 'sites'),
          where('isCompanyShared', '==', true),
          where('companyId', 'in', chunk),
        );
        const unsub = onSnapshot(
          compQ,
          (snap) => {
            const changedDocs = typeof (snap as { docChanges?: () => Array<{ type: string; doc: (typeof snap.docs)[number] }> }).docChanges === 'function'
              ? (snap as { docChanges: () => Array<{ type: string; doc: (typeof snap.docs)[number] }> })
                  .docChanges()
                  .filter((change) => change.type !== 'removed')
                  .map((change) => change.doc)
              : snap.docs;
            const company: SiteEntity[] = [];
            for (const d of changedDocs) {
              // OWN sites win — an own site is ALSO isCompanyShared:true and so
              // matches here; exclude it BEFORE calling d.data() so we neither
              // deserialize own multi-MB site docs twice nor overwrite scope 'mine'.
              const ownerUid = (d as { ref?: { parent?: { parent?: { id?: string } | null } } })
                .ref?.parent?.parent?.id;
              if (ownerUid === uid) {
                continue;
              }
              const data = normalizeSiteEntity(d.data() as SiteEntity);
              const siteId = data.id ?? d.id;
              const duplicateOf = (data as { duplicateOf?: unknown }).duplicateOf;
              // Query ordering is unspecified; discard tombstones before they
              // can replace the canonical same-id site's owner annotation.
              if (typeof duplicateOf === 'string' && duplicateOf.trim().length > 0) {
                continue;
              }
              const scope: 'company' | 'subCompany' =
                data.companyId === companyId ? 'company' : 'subCompany';
              company.push({
                ...data,
                id: siteId,
                _sourceScope: scope,
                ...(ownerUid ? { _ownerUid: ownerUid } : {}),
              });
            }
            applyLiveSites(company);
          },
          (err) => {
            console.warn('[subscribeSites] company-shared listener error:', (err as Error)?.message);
          },
        );
        _sitesCompanyUnsubs.push(unsub);
      }
    })();
  },

  unsubscribeSites: () => {
    resetSitesSubscription();
  },

  clearGenerationError: () => {
    // Only touch `error` — never disturb activeGenerations/progress/etc.
    set((s) => (s.siteRag.error == null ? {} : { siteRag: { ...s.siteRag, error: null } }));
  },

  generateTypedReport: async (siteId, reportTypeId, typedPreferences, mode = 'new', parentReportId, companyArchetypeOverride, documentSelection, siteContextOverride, siteConfigOverrides, runRequestId) => {
    // v1.3.4 (#24) — converge same-type regenerations onto ONE definitive
    // lineage. The wizard's genuine "Add another report alongside" A/B fork
    // ALWAYS passes `parentReportId: undefined` (it seeds prefs from the
    // sibling but wants a fresh root). So a call that is BOTH 'add-another'
    // AND carries a `parentReportId` is necessarily a regeneration that was
    // mislabeled as a fork (the old AutoRerun fast-path / rerun-panel shape).
    // runTypedReportGeneration only stamps `parentReportId` for update/replace,
    // so 'add-another'-with-parent used to mint an orphan root of the same
    // type → the stray "<type> v1.0 ALT #2" card. Normalize it to 'update' so
    // it version-bumps the parent and stays in the single lineage. The genuine
    // fork (no parent) and explicit 'new' generations are untouched.
    if (mode === 'add-another' && parentReportId) {
      mode = 'update';
    }
    // Rerun fallback (Workflow Prompt rerunConfig): when the caller is
    // rerunning an existing report (mode != 'new' + parentReportId set)
    // and any of typedPreferences / companyArchetypeOverride /
    // documentSelection.documentRefs are undefined, inherit them from
    // the parent via getRerunDefaults so a rerun can never silently
    // drop the framing the parent shipped against. Panels that DO
    // forward defaults override this — explicit caller wins.
    // Pulled out of the if-block below so the report-gen call site can
    // forward it into the quality pipeline (mode-agnostic — empty array
    // on a fresh generation, populated only on rerun).
    let prevSanityResolutions: import('../services/reportQualityPipeline').PrevSanityResolution[] = [];
    if (mode !== 'new' && parentReportId) {
      const parent = get().siteRag.reports.find(r => r.id === parentReportId);
      if (parent) {
        const { getRerunDefaults } = await import('../lib/rerunDefaults');
        const defaults = getRerunDefaults(parent);
        typedPreferences = typedPreferences ?? defaults.typedPreferences;
        companyArchetypeOverride = companyArchetypeOverride ?? defaults.archetypeOverride;
        if (!documentSelection && defaults.documentRefs?.length) {
          documentSelection = { documentRefs: defaults.documentRefs, documentSelectionMode: 'mixed' };
        }
        // Feed the parent's resolved sanity findings forward to the
        // rerun: "apply" resolutions carry their applyDirective so the
        // narrative builders incorporate the suggestion (and
        // finalSanityCheck verifies it was applied); "ignore"
        // resolutions are passed so finalSanityCheck suppresses
        // re-flagging. The directive is only forwarded for "apply" —
        // an ignored finding must not drag a stale directive along.
        const { collectDurableSanityResolutionsForRerun } = await import('../lib/preservedUserDecisions');
        prevSanityResolutions = collectDurableSanityResolutionsForRerun(parent, sanityFindingIdentity);
      }
    }

    // Request-shape guard — a prefs map missing the selected type's own key
    // fails request validation, while an absent map lets the server derive
    // defaults. Normalize here so EVERY caller (audit tab, landing page,
    // rerun panels) survives legacy parents whose stored typedPreferences
    // predate per-type persistence. See normalizeTypedPreferencesForRequest.
    {
      const { normalizeTypedPreferencesForRequest } = await import('../lib/rerunDefaults');
      typedPreferences = normalizeTypedPreferencesForRequest(typedPreferences, reportTypeId);
    }

    const { isAsyncReportGenerationEnabled } = await import('../lib/reportGeneration/asyncReportGenerationFlag');
    // Fictional sources must be hydrated and verified in the canonical owner
    // namespace. The browser runner cannot provide that server-only verifier,
    // even when async generation is disabled for local development.
    const requiresServerSourceVerification = Boolean(
      get().siteRag.sites.find(candidate => candidate.id === siteId)?.demonstrationProvenance,
    );
    if (isAsyncReportGenerationEnabled() || requiresServerSourceVerification) {
      await get().enqueueReportGenerationJob(
        siteId,
        reportTypeId,
        typedPreferences,
        mode,
        parentReportId,
        companyArchetypeOverride,
        documentSelection,
        siteContextOverride,
        siteConfigOverrides,
        runRequestId,
      );
      return;
    }

    // Browser/synchronous fallback has no enqueue route to bind the current
    // Test Fit tuple, so resolve it before entering the shared runner. The
    // async path performs the same binding server-side for every caller.
    if (reportTypeId === 'test-fit' && !siteConfigOverrides?.expectedTestFitAuthorityRef) {
      const authoritySite = get().siteRag.sites.find(candidate => candidate.id === siteId);
      if (authoritySite) {
        const { loadSedById } = await import('../services/sedLoader');
        const { evaluatePersistedTestFitWorkflow } = await import('../lib/generationImpact/testFitWorkflow');
        const authoritySed = authoritySite.sedId
          ? await loadSedById(
              authoritySite.sedId,
              authoritySite.sedStoragePath,
              authoritySite.sedVersion,
            )
          : null;
        const readiness = evaluatePersistedTestFitWorkflow(authoritySite, authoritySed);
        if (!readiness.ready) {
          throw new Error(`Test Fit generation blocked — ${readiness.title}: ${readiness.message}`);
        }
        if (readiness.generationImpactRequired) {
          if (!readiness.authority) {
            throw new Error('test-fit-authority-missing: Current Test Fit authority could not be resolved.');
          }
          siteConfigOverrides = {
            ...(siteConfigOverrides ?? {}),
            expectedTestFitAuthorityRef: readiness.authority,
          };
        }
      }
    }

    // Unified report-generation pipeline (all 6 report types):
    //   1. Load site from store.
    //   2. Read or bootstrap the site's SED.
    //   3. Top up any sections required by the report type that are still
    //      'unavailable' on the persisted SED.
    //   4. Generate the report from the SED via service.generateReportFromSed
    //      (which dispatches the right Gemini prompt by reportTypeId).
    //   5. Save report to GCS + Firestore metadata.
    //
    // Replaces the previous dual-path where feasibility flowed through the
    // legacy generateSiteRagReport → service.generateReport (full data
    // sweep) and other types went through generateReportFromSed. The
    // legacy generateSiteRagReport is now a thin shim that forwards here.
    const regenerationReason = mode === 'new' ? undefined : mode;
    const state = get();
    const site = state.siteRag.sites.find(s => s.id === siteId);
    if (!site) {
      set((s) => ({ siteRag: { ...s.siteRag, error: 'Site not found' } }));
      return;
    }
    const runId = runRequestId ?? `gen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const runStartedAt = new Date().toISOString();
    type GenerationRun = NonNullable<SiteRagState['activeGenerations']>[string];
    const resolveLatestRun = (
      runs: NonNullable<SiteRagState['activeGenerations']>,
    ): GenerationRun | null => {
      const values = Object.values(runs);
      if (values.length === 0) return null;
      values.sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
      return values[0] ?? null;
    };

    const upsertRun = (patch: Partial<GenerationRun>) => {
      const hadRun = get().siteRag.activeGenerations?.[runId];
      set((s) => {
        const runs: NonNullable<SiteRagState['activeGenerations']> = {
          ...(s.siteRag.activeGenerations ?? {}),
        };
        runs[runId] = {
          ...(runs[runId] ?? {
            id: runId,
            siteId: site.id,
            reportTypeId,
            startedAt: runStartedAt,
            progress: 0,
            progressStage: 'Initializing…',
            estimatedDurationMs: estimatedReportGenerationDurationMs(
              reportTypeId,
              siteConfigOverrides?.generationTier,
            ),
          }),
          ...patch,
          ...(patch.progress !== undefined
            ? { progress: normalizeProgressPercent(patch.progress) }
            : {}),
        };
        const latest = resolveLatestRun(runs);
        return {
          siteRag: {
            ...s.siteRag,
            activeGenerations: runs,
            isLoading: true,
            progress: normalizeProgressPercent(latest?.progress ?? 0),
            progressStage: latest?.progressStage ?? 'Initializing…',
            activeSiteId: latest?.siteId ?? site.id,
            error: null,
          },
        };
      });
      if (!hadRun) {
        void import('../lib/reportDialogDraft').then(({ saveGeneratingDraft }) => {
          saveGeneratingDraft({ siteId: site.id, reportType: reportTypeId, runId });
        });
      }
    };

    const finishRun = (
      errorMessage?: string,
      providerCoverage?: import('../lib/audit/providerCoverageGate').ProviderCoverageReport,
      siteStateRequired?: boolean,
      citationDensity?: import('../lib/audit/citationDensityGate').CitationDensitySnapshot,
      citationDensityRegenUsed?: boolean,
    ) => {
      set((s) => {
        const runs: NonNullable<SiteRagState['activeGenerations']> = {
          ...(s.siteRag.activeGenerations ?? {}),
        };
        delete runs[runId];
        const outcomes = {
          ...(s.siteRag.generationOutcomes ?? {}),
          [runId]: {
            id: runId,
            siteId: site.id,
            reportTypeId,
            finishedAt: new Date().toISOString(),
            status: errorMessage ? 'failed' as const : 'succeeded' as const,
            ...(errorMessage ? { error: errorMessage } : {}),
            ...(providerCoverage ? { providerCoverage, errorCode: 'PROVIDER_COVERAGE_BLOCKED', blockedByProviderCoverage: true } : {}),
            ...(siteStateRequired ? { siteStateRequired: true } : {}),
            ...(citationDensity ? { citationDensity, citationDensityRegenUsed: Boolean(citationDensityRegenUsed) } : {}),
          },
        };
        const trimmedOutcomeEntries = Object.values(outcomes)
          .sort((a, b) => Date.parse(b.finishedAt) - Date.parse(a.finishedAt))
          .slice(0, 50);
        const generationOutcomes = Object.fromEntries(trimmedOutcomeEntries.map(o => [o.id, o]));
        const latest = resolveLatestRun(runs);
        const lastCoverageGate = providerCoverage
          ? {
            ...(s.siteRag.lastCoverageGate ?? {}),
            [site.id]: {
              ...(s.siteRag.lastCoverageGate?.[site.id] ?? {}),
              [reportTypeId]: providerCoverage,
            },
          }
          : s.siteRag.lastCoverageGate;
        return {
          siteRag: {
            ...s.siteRag,
            activeGenerations: runs,
            generationOutcomes,
            isLoading: latest != null,
            progress: normalizeProgressPercent(latest?.progress ?? 0),
            progressStage: latest?.progressStage ?? '',
            activeSiteId: latest?.siteId ?? s.siteRag.activeSiteId,
            ...(errorMessage && latest == null ? { error: errorMessage } : {}),
            ...(providerCoverage ? { lastProviderCoverageBlock: providerCoverage, lastCoverageGate } : {}),
            ...(citationDensity
              ? {
                lastCitationDensityBlock: citationDensity,
                lastCitationDensityRegenUsed: Boolean(citationDensityRegenUsed),
              }
              : {}),
          },
        };
      });
      // FIX #94 — clear the generating draft on ANY terminal finish, not just
      // success. Previously the draft was only swept when there was no
      // errorMessage, so a FAILED sync generation left a `step:'generating'`
      // draft in sessionStorage. SiteLandingPage.displayInflightRuns then
      // synthesized a phantom "Continuing generation… 0%" run from it that
      // never cleared. The run is over in every terminal case, so the draft
      // (whose only purpose is post-refresh resume of an in-flight wizard) is
      // always stale here.
      void import('../lib/reportDialogDraft').then(({ clearReportDialogDraft }) => {
        clearReportDialogDraft(site.id, reportTypeId);
      });
    };

    upsertRun({
      progress: 0,
      progressStage: 'Initializing…',
    });

    const onProgress = (stage: string, pct: number) => {
      upsertRun({ progress: pct, progressStage: stage });
    };

    try {
      const { runTypedReportGeneration, bootstrapSiteSedForGeneration } =
        await import('../lib/reportGeneration/runTypedReportGeneration');
      const { loadSedById, saveSedById } = await import('../services/sedLoader');
      const { auth, db } = await import('../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      const user = auth.currentUser;

      const result = await runTypedReportGeneration(
        {
          siteId,
          reportTypeId,
          typedPreferences,
          mode,
          parentReportId,
          // Forward the compatibility value to the canonical resolver. Stale
          // `draft` overrides cannot downgrade the full investment-grade path.
          generationTier: siteConfigOverrides?.generationTier,
          companyArchetypeOverride,
          documentSelection,
          siteContextOverride,
          siteConfigOverrides,
        },
        {
          loadDecisionAnalysisContext: async ({ site: frozenSite }) => {
            const { loadCompositeAnalysisContext } = await import('../services/compositeAnalysisContext');
            return loadCompositeAnalysisContext(frozenSite);
          },
          getSite: (id) => get().siteRag.sites.find(s => s.id === id),
          refreshSite: (id) => get().siteRag.sites.find(s => s.id === id),
          getReports: () => get().siteRag.reports,
          getCompanyArchetype: () => get().companyArchetype,
          getCompanyId: () => get().companyId,
          getCompanyName: () => get().companyName,
          getCompanyArchetypeNote: () => get().companyArchetypeNote,
          getCompanyMissingCriticalCap: () => get().companyMissingCriticalCap,
          getReportTemplates: () => get().reportTemplates,
          getActiveTemplateId: () => get().activeTemplateId,
          loadSed: async (sedId, storagePath, version) => {
            const cached = await loadSedById(sedId, storagePath, version);
            if (!cached) return cached;
            const loaded = structuredClone(cached);
            // Test Fit must use the scenario frozen into this SED authority.
            // Overlaying the live promoted scenario here would make an older
            // SED look current and mix scenario/layout revisions.
            if (reportTypeId !== 'test-fit') {
              const { resolvePromotedGenerationScenario } = await import('../lib/generationImpact/promotedScenario');
              const generationScenario = resolvePromotedGenerationScenario(
                site.projectIntent,
                site.promotedGenerationScenarioSnapshot,
                loaded.promotedGenerationScenarioSnapshot,
                site.generationAssessmentSelection,
                site.generationProjectInputBinding,
              );
              if (generationScenario) {
                loaded.promotedGenerationScenarioSnapshot = typeof structuredClone === 'function'
                  ? structuredClone(generationScenario)
                  : JSON.parse(JSON.stringify(generationScenario));
              }
            }
            return loaded;
          },
          saveSed: async (sed, storagePath) => {
            if (!await saveSedById(sed, storagePath)) {
              throw new Error('The report evidence update could not be confirmed as saved. Reload evidence before retrying.');
            }
          },
          saveReport: async (report, sed, rtId) => {
            if (!user) return false;
            try {
              const { stampGenerationImpactOntoReport } = await import('../lib/generationImpact/reportProjection');
              const { resolvePromotedGenerationScenario } = await import('../lib/generationImpact/promotedScenario');
              const liveSite = get().siteRag.sites.find((candidate) => candidate.id === site.id) ?? site;
              const generationScenario = resolvePromotedGenerationScenario(
                liveSite.projectIntent,
                liveSite.promotedGenerationScenarioSnapshot,
                sed.promotedGenerationScenarioSnapshot,
                liveSite.generationAssessmentSelection,
                liveSite.generationProjectInputBinding,
              );
              const { requireGenerationImpactTestFitAuthority } = await import('../lib/generationImpact/testFitProjection');
              const { evaluatePersistedTestFitWorkflow, sameTestFitAuthorityRef } =
                await import('../lib/generationImpact/testFitWorkflow');
              const testFitReadiness = rtId === 'test-fit'
                ? evaluatePersistedTestFitWorkflow(liveSite, sed)
                : null;
              if (testFitReadiness && !testFitReadiness.ready) {
                throw new Error(
                  `test-fit-authority-stale: ${testFitReadiness.title}: ${testFitReadiness.message}`,
                );
              }
              const generationImpactRequired = Boolean(
                testFitReadiness?.generationImpactRequired,
              );
              if (generationImpactRequired) {
                const expected = siteConfigOverrides?.expectedTestFitAuthorityRef;
                if (!expected) {
                  throw new Error(
                    'test-fit-authority-missing: On-site Test Fit has no expected authority tuple.',
                  );
                }
                if (!sameTestFitAuthorityRef(expected, testFitReadiness?.authority)) {
                  throw new Error(
                    'test-fit-authority-stale: Generation & Impact authority changed during generation.',
                  );
                }
              }
              const authority = rtId === 'test-fit' && generationImpactRequired
                ? requireGenerationImpactTestFitAuthority(
                    liveSite,
                    liveSite.promotedGenerationScenarioSnapshot,
                    sed,
                  )
                : null;
              const scenarioForReport = rtId === 'test-fit'
                ? (generationImpactRequired ? authority?.scenarioSnapshot ?? null : null)
                : generationScenario;
              const testFitWithoutGenerationImpact =
                rtId === 'test-fit' && !generationImpactRequired
                  ? (testFitReadiness?.applicability.status === 'grid-only'
                      ? 'grid-only' as const
                      : 'undecided' as const)
                  : undefined;
              stampGenerationImpactOntoReport(
                report,
                rtId,
                scenarioForReport,
                {
                  site: liveSite,
                  sed,
                  authority,
                  ...(testFitWithoutGenerationImpact
                    ? { testFitWithoutGenerationImpact }
                    : {}),
                },
              );
              if (rtId === 'test-fit') {
                if (generationImpactRequired && testFitReadiness?.authority) {
                  report.testFitAuthorityRef = testFitReadiness.authority;
                }
              }
              const { applyMetaInstructionStripToReport } = await import('../lib/metaInstructionStrip');
              const { runSectionDepthGate } = await import('../lib/sectionDepthGate');
              let toSave = applyMetaInstructionStripToReport(report);
              const { applySectionDedupFindingsToReport } = await import('../lib/sectionContentDedup');
              toSave = applySectionDedupFindingsToReport(toSave);
              const { applyCaveatHonestyCheckToReport } = await import('../lib/caveatHonestyGate');
              toSave = applyCaveatHonestyCheckToReport(toSave);
              runSectionDepthGate(toSave);
              const cleanReport = JSON.parse(JSON.stringify(toSave, (_, v) =>
                typeof v === 'number' && !isFinite(v) ? null : v === undefined ? null : v));
              const storagePath = `users/${user.uid}/reports/${report.id}.json`;
              const res = await authedFetch('/api/storage/save', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path: storagePath, data: cleanReport }),
              });
              if (res.ok) {
                const orgStamp = (await import('../lib/siteCompany')).resolveOrgStamp(site.companyId, get().companyId);
                await setDoc(doc(db, `users/${user.uid}/reports`, report.id), {
                  id: report.id, siteName: report.siteName, address: report.address,
                  overallScore: report.overallScore, overallStatus: report.overallStatus,
                  createdAt: report.createdAt, version: report.version, minorVersion: report.minorVersion,
                  reportType: report.reportType || rtId, storagePath,
                  siteId: site.id, sedId: sed.id, sedVersionUsed: sed.version,
                  ...(cleanReport.generationImpactProjection ? {
                    generationImpactScenarioId: cleanReport.generationImpactProjection.source.scenarioId,
                    generationImpactInputHash: cleanReport.generationImpactProjection.source.inputHash,
                    generationImpactResultHash: cleanReport.generationImpactProjection.source.resultHash,
                    ...(cleanReport.generationImpactProjection.source.layoutPublicationId
                      ? { generationImpactLayoutPublicationId: cleanReport.generationImpactProjection.source.layoutPublicationId }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.layoutVersion != null
                      ? { generationImpactLayoutVersion: cleanReport.generationImpactProjection.source.layoutVersion }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.layoutHash
                      ? { generationImpactLayoutHash: cleanReport.generationImpactProjection.source.layoutHash }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.sedAuthorityContractVersion != null
                      ? { generationImpactSedAuthorityContractVersion: cleanReport.generationImpactProjection.source.sedAuthorityContractVersion }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.parcelBoundarySchemaVersion != null
                      ? { generationImpactParcelBoundarySchemaVersion: cleanReport.generationImpactProjection.source.parcelBoundarySchemaVersion }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.parcelBoundarySource
                      ? { generationImpactParcelBoundarySource: cleanReport.generationImpactProjection.source.parcelBoundarySource }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.parcelBoundaryHash
                      ? { generationImpactParcelBoundaryHash: cleanReport.generationImpactProjection.source.parcelBoundaryHash }
                      : {}),
                    ...(cleanReport.generationImpactProjection.source.parcelBoundaryAreaAcres != null
                      ? { generationImpactParcelBoundaryAreaAcres: cleanReport.generationImpactProjection.source.parcelBoundaryAreaAcres }
                      : {}),
                  } : {}),
                  ...orgStamp,
                  // Report P4 (quality-hold propagation) — mirror the publish
                  // bar onto the report metadata doc so a held/draft artifact
                  // never lists as a clean success. Firebase JS SDK setDoc THROWS
                  // on undefined, so conditional spreads are mandatory here.
                  // generationTier rides alongside so consumers distinguish the
                  // COST TIER from the HOLD (never infer the tier from isDraft).
                  ...(report.isDraft ? { isDraft: true } : {}),
                  ...((report as { qualityHold?: 'needs-revision'; qualityHoldFindings?: string[] }).qualityHold
                    ? {
                        qualityHold: (report as { qualityHold?: 'needs-revision' }).qualityHold,
                        qualityHoldFindings: (report as { qualityHoldFindings?: string[] }).qualityHoldFindings,
                      }
                    : {}),
                  ...(report.generationTier ? { generationTier: report.generationTier } : {}),
                });
                return true;
              }
            } catch (e) {
              console.warn('[generateTypedReport] save failed:', (e as Error)?.message);
            }
            return false;
          },
          onReportGenerated: (report) => {
            set((s) => ({
              siteRag: {
                ...s.siteRag,
                reports: [report, ...s.siteRag.reports].slice(0, 50),
                activeReportId: report.id,
                error: null,
              },
            }));
            // COMPOSITE STALE-ON-REPORT-WRITE — a new report can move the site's
            // overview verdict / band; invalidate this site's cached composite
            // so the lazy ensurePortfolioComposites pass refreshes it on next
            // view. Invalidation only (no scoring change); idempotent.
            get()._staleStampSiteComposite((report as { siteId?: string }).siteId ?? siteId);
          },
          updateSiteReportIds: async (sid, reportId) => {
            const s = get().siteRag.sites.find(x => x.id === sid);
            if (s && !(s.reportIds ?? []).includes(reportId)) {
              await get().updateSite(sid, { reportIds: [...(s.reportIds ?? []), reportId] });
            }
          },
          applyReportToSed: async (report, sid, sedId) => {
            if (!user) return;
            const { applyReportToSedFirestore } = await import('../services/sedReportIngestion');
            // #86 — see the sibling applyReportToSed above: stamp the owning
            // company on the report-claim docs for the re-keyed READ rule.
            const companyId =
              get().siteRag.sites.find(s => s.id === sid)?.companyId
              ?? report.companyId
              ?? get().companyId
              ?? undefined;
            await applyReportToSedFirestore({ uid: user.uid, siteId: sid, sedId, report, companyId });
          },
          deleteParentReport: (pid) => get().deleteRagReport(pid).then(() => undefined),
          bootstrapSiteSed: (s) => bootstrapSiteSedForGeneration(s),
          topUpSiteSed: async (sid, rt, signal) => {
            const { topUpSiteSed } = await import('../services/sedBootstrap');
            // Intentional: ADDITIVE top-up, not a full rebuild.
            // R-28 guard scope: rebuildSedWithGuard only (see docs/runbooks/sed-rebuild-safely.md).
            // A 'superseded' result carries its outcome and is surfaced by
            // runTypedReportGeneration before narrative generation or save.
            return topUpSiteSed(sid, { reportType: rt, abortSignal: signal });
          },
          isBootstrapping: (sid) =>
            get().siteRag.sites.find(s => s.id === sid)?.sedStatus === 'bootstrapping',
          onProgress,
          uid: user?.uid,
          // Load the latest annual-yield run (web SDK) so the
          // gen-time Go/No-Go verdict cap (PATH 5) can read the convergence/CF
          // signal on the client gen path. Best-effort: absent / unauthed →
          // null → cap NEUTRAL (verdict byte-identical to before).
          getAnnualYieldEvidence: async () => {
            if (!user) return null;
            try {
              const { collection, getDocs, limit, orderBy, query } = await import('firebase/firestore');
              const { ACTIVE_EO_EVIDENCE_SCAN_LIMIT } = await import(
                '../lib/reportGeneration/evidence/activeEoEvidence'
              );
              const { annualYieldEvidenceFromRow } = await import(
                '../lib/reportGeneration/evidence/loadLatestAnnualYieldEvidence'
              );
              const snap = await getDocs(query(
                collection(db, `users/${user.uid}/sites/${siteId}/annualYieldRuns`),
                orderBy('completedAt', 'desc'),
                limit(ACTIVE_EO_EVIDENCE_SCAN_LIMIT),
              ));
              return snap.docs
                .map(doc => annualYieldEvidenceFromRow(
                  doc.data() as Record<string, unknown>,
                  siteId,
                  doc.id,
                ))
                .find((value): value is NonNullable<typeof value> => value != null) ?? null;
            } catch {
              return null;
            }
          },
        },
      );

      if (result.error) {
        // R-78 provider-precedence is detect-and-record-only and never returns
        // an error result, so any error here is a genuine generation failure —
        // surface it normally via finishRun.
        finishRun(
          result.error,
          result.coverage,
          result.blockedBySiteStateRequired,
          result.citationDensity,
          siteConfigOverrides?.citationDensityRegenerateFocus,
        );
        return;
      }
      finishRun();
    } catch (e) {
      const message = (e as Error)?.message || 'Report generation failed';
      console.error('[generateTypedReport] failed:', message);
      finishRun(message);
    }
  },

  addTab: (
    name,
    nodes = [],
    edges = [],
    simState = initialSimState,
    site = null,
    cloudId,
    version,
    autoSave,
    eoGraphConfig,
    savedEoInputSignature,
    repairDisposition,
    ignoredNodeIds = [],
    eoStudySpec = defaultEoStudySpec(),
    eoTransientConfig = defaultEoTransientConfig(),
    eoKpiUncertaintyDefinitionSet,
    eoCanvasExecutionConfig = defaultEoCanvasExecutionConfig(),
    options = {},
  ) => {
    const state = get();
    state.syncActiveTab(); // Sync current tab before switching
    const newId = 'tab-' + safeUUID();
    // Imported/new canvases never inherit a prior active result incarnation.
    if (!repairDisposition) nodes = nodes.map(newCanvasNodeIncarnation);
    let normalizedEoStudySpec = normalizeEoStudyDefinition(eoStudySpec).spec;
    let normalizedEoTransientConfig = normalizeEoTransientConfig(eoTransientConfig).config;
    let normalizedEoCanvasExecutionConfig = parseEoCanvasExecutionConfigOrDefault(
      eoCanvasExecutionConfig,
    );
    if (!repairDisposition && !options.preserveLoadedContent) {
      // Editable canvas: every value it carries is in use, so it is accepted
      // at the source ("in use => accepted"). Protected sources and immutable
      // project revisions (preserveLoadedContent) stay verbatim.
      const inUse = acceptInUseCanvasAssumptions({
        nodes,
        edges,
        ...(eoGraphConfig ? { eoGraphConfig } : {}),
        eoStudySpec: normalizedEoStudySpec,
        eoTransientConfig: normalizedEoTransientConfig,
        eoCanvasExecutionConfig: normalizedEoCanvasExecutionConfig,
      });
      nodes = inUse.nodes;
      if (inUse.eoGraphConfig) eoGraphConfig = inUse.eoGraphConfig;
      normalizedEoStudySpec = inUse.eoStudySpec;
      normalizedEoTransientConfig = inUse.eoTransientConfig;
      normalizedEoCanvasExecutionConfig = inUse.eoCanvasExecutionConfig;
    }
    const normalizedIgnoredNodeIds = normalizeIgnoredNodeIds(ignoredNodeIds, nodes);
    const normalizedUncertaintyDefinitionSet = eoKpiUncertaintyDefinitionSet === undefined
      ? undefined
      : parseEoKpiUncertaintyDefinitionSetV1(eoKpiUncertaintyDefinitionSet);
    const newTab: SimulationTab = {
      id: newId,
      name,
      nodes,
      edges,
      simState,
      site,
      eoCanvasExecutionConfig: normalizedEoCanvasExecutionConfig,
      ignoredNodeIds: normalizedIgnoredNodeIds,
      eoStudySpec: normalizedEoStudySpec,
      eoTransientConfig: normalizedEoTransientConfig,
      ...(normalizedUncertaintyDefinitionSet
        ? { eoKpiUncertaintyDefinitionSet: normalizedUncertaintyDefinitionSet }
        : {}),
      cloudId,
      version,
      // Protected repair sources must never enter the debounce save path. The
      // explicit false also makes the tab's cloud status truthful immediately.
      autoSave: repairDisposition ? false : autoSave,
      ...(eoGraphConfig ? { eoGraphConfig } : {}),
      ...(savedEoInputSignature !== undefined ? { savedEoInputSignature } : {}),
      ...(repairDisposition ? { repairDisposition } : {}),
    };
    set({
      tabs: [...get().tabs, newTab],
      activeTabId: newId,
      nodes,
      edges,
      // Runtime playback is never resumed merely by opening a protected
      // source. Its canonical tab payload remains byte-identical.
      simState: repairDisposition && simState.isRunning
        ? { ...simState, isRunning: false }
        : simState,
      site,
      ignoredNodeIds: normalizedIgnoredNodeIds,
      // New active canvas — undo history starts empty (Sim Phase 3b).
      canvasHistory: EMPTY_CANVAS_HISTORY,
    });
  },

  closeTab: (id) => {
    const state = get();
    const newTabs = state.tabs.filter(t => t.id !== id);
    if (newTabs.length === 0) {
      // FIX #92 — closing the last tab returns to the empty state (no phantom
      // "Simulation 1"). The tab bar shows just the "+" until the user creates
      // or restores a sim. The live canvas state is cleared so the editor isn't
      // left showing the just-closed tab's nodes.
      set({
        tabs: [],
        activeTabId: '',
        nodes: [],
        edges: [],
        simState: initialSimState,
        ignoredNodeIds: [],
        canvasHistory: EMPTY_CANVAS_HISTORY,
      });
      return;
    }

    if (state.activeTabId === id) {
      // Switching to the previous tab
      const closedIndex = state.tabs.findIndex(t => t.id === id);
      const nextTab = newTabs[Math.max(0, closedIndex - 1)];
      set({
        tabs: newTabs,
        activeTabId: nextTab.id,
        nodes: nextTab.nodes,
        edges: nextTab.edges,
        simState: canvasTabMutationBlocker(nextTab) && nextTab.simState.isRunning
          ? { ...nextTab.simState, isRunning: false }
          : nextTab.simState,
        ignoredNodeIds: normalizeIgnoredNodeIds(nextTab.ignoredNodeIds, nextTab.nodes),
        // Switched to a different canvas — reset undo history (Sim Phase 3b).
        canvasHistory: EMPTY_CANVAS_HISTORY,
      });
    } else {
      set({ tabs: newTabs });
    }
  },

  setActiveTab: (id) => {
    const state = get();
    if (state.activeTabId === id) return;
    state.syncActiveTab(); // Sync current tab before switching
    const nextTab = get().tabs.find(t => t.id === id);
    if (nextTab) {
      set({
        activeTabId: id,
        nodes: nextTab.nodes,
        edges: nextTab.edges,
        simState: canvasTabMutationBlocker(nextTab) && nextTab.simState.isRunning
          ? { ...nextTab.simState, isRunning: false }
          : nextTab.simState,
        site: nextTab.site,
        ignoredNodeIds: normalizeIgnoredNodeIds(nextTab.ignoredNodeIds, nextTab.nodes),
        // S8 — restore the per-tab viewport (null → editor falls back to fitView).
        flowViewport: nextTab.flowViewport ?? null,
        // Switched to a different canvas — reset undo history (Sim Phase 3b).
        canvasHistory: EMPTY_CANVAS_HISTORY,
      });
    }
  },

  updateTabName: (id, name) => {
    set(state => ({
      tabs: state.tabs.map(tab =>
        tab.id === id && !canvasTabMutationBlocker(tab) ? { ...tab, name } : tab
      )
    }));
  },

  updateTab: (id, data) => {
    let normalizedData = data;
    if (Object.prototype.hasOwnProperty.call(data, 'eoCanvasExecutionConfig')) {
      try {
        normalizedData = {
          ...data,
          eoCanvasExecutionConfig: parseEoCanvasExecutionConfig(
            data.eoCanvasExecutionConfig,
          ),
        };
      } catch {
        // A live mutation is not a legacy migration boundary. Reject the
        // entire patch atomically so malformed/partial v3 edits cannot
        // silently switch the tab back to graph-v2 connection/solver rules.
        return;
      }
    }
    set(state => {
      let activatedProtection = false;
      const tabs = state.tabs.map((tab) => {
        if (tab.id !== id) return tab;
        if (simulationRevisionMutationBlocker(tab.revisionBinding)) {
          // Server lifecycle may refresh/clear the binding that created the
          // project read-only state. No other tab/content field may be bundled
          // into that authority update.
          const keys = Object.keys(normalizedData);
          return keys.length === 1 && keys[0] === 'revisionBinding'
            ? { ...tab, revisionBinding: normalizedData.revisionBinding }
            : tab;
        }
        if (isRepairProtectedTab(tab)) {
          if (isProtectedSourceInitializationPatch(tab, normalizedData)) {
            return { ...tab, ...normalizedData };
          }
          if (isProtectedGeneratedCloudInitializationPatch(tab, normalizedData)) {
            return { ...tab, ...normalizedData, autoSave: false };
          }
          return tab;
        }
        const next = { ...tab, ...normalizedData };
        // A caller establishing typed protection must not leave a save path
        // armed in the same atomic patch or smuggle a model mutation alongside
        // the marker.
        if (isRepairProtectedTab(next)) {
          if (!isProtectionEstablishmentPatch(normalizedData)) return tab;
          activatedProtection ||= tab.id === state.activeTabId;
          return { ...next, autoSave: false };
        }
        // A patch that carries the revision binding synchronizes the tab with
        // server-owned project state (opening a head or recovery draft, a
        // publish result, draft bookkeeping). It is applied verbatim so an
        // immutable revision reopens byte-exact.
        if (Object.prototype.hasOwnProperty.call(normalizedData, 'revisionBinding')) return next;
        // A tab bound to a project revision: settle only the fields this patch
        // actually changes (an edit makes a new draft). A write-back of the
        // same content keeps the immutable revision byte-exact. Once the draft
        // differs, the profile in use also completes its model-limitation
        // record for the merged topology.
        if (tab.revisionBinding !== undefined) {
          const changed = changedAcceptanceFields(tab, normalizedData);
          if (Object.keys(changed).length === 0) return next;
          return {
            ...next,
            ...acceptInUseCanvasAssumptions(withExecutionConfigInUse(changed, next), next),
          };
        }
        // Editable tab: in-use engineering inputs are accepted at the source,
        // so an edit or a reconcile write never re-introduces a pending
        // acceptance. Values are never changed. The execution profile's
        // model-limitation record is kept complete for the merged topology.
        return {
          ...next,
          ...acceptInUseCanvasAssumptions(withExecutionConfigInUse(normalizedData, next), next),
        };
      });
      return {
        tabs,
        ...(activatedProtection && state.simState.isRunning
          ? { simState: { ...state.simState, isRunning: false } }
          : {}),
      };
    });
  },

  // ── SIM-MGMT — saved cloud-sim management ────────────────────────────
  // All four+1 actions operate on the SAME doc path AutoSaver / App.tsx use:
  //   users/{uid}/simulations/{cloudId}
  // and never throw — callers branch on `ok` / receive `[]`.

  deleteCloudSim: async (cloudId) => {
    // Destructive — the UI MUST confirm before calling this. Deletes ONLY the
    // one targeted sim: its `backups/*` subcollection docs, then the sim doc,
    // then a best-effort GCS payload cleanup (swallowed). Returns { ok, error? }.
    if (!cloudId) return { ok: false, error: 'Missing cloudId' };
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'Not authenticated' };

    const { doc, deleteDoc, collection, getDocs } = await import('firebase/firestore');
    const simDocPath = `users/${user.uid}/simulations`;
    try {
      // 1. Delete every backup doc in the subcollection first so we don't
      //    strand orphaned backups under a vanished parent. The ENTIRE backups
      //    sweep (read + per-doc delete) is wrapped so a backups read failure
      //    (e.g. rules not yet deployed, transient error) can NEVER block the
      //    primary sim-doc delete below — orphaned backups are harmless.
      try {
        const backupsSnap = await getDocs(
          collection(db, `${simDocPath}/${cloudId}/backups`),
        );
        for (const b of backupsSnap.docs) {
          try {
            await deleteDoc(b.ref);
          } catch (e) {
            console.warn(`[SIM-MGMT] backup ${b.id} delete failed:`, e);
          }
        }
      } catch (e) {
        console.warn('[SIM-MGMT] backups sweep failed (sim delete continues):', e);
      }

      // 2. Delete the sim doc itself.
      await deleteDoc(doc(db, simDocPath, cloudId));

      // 3. Best-effort GCS payload cleanup. Sims today persist entirely in
      //    Firestore, but a future large-payload offload would live here;
      //    deleting a non-existent object is a harmless 404 (server returns
      //    success on 404). Swallow ALL errors — never fail the delete on GCS.
      try {
        await authedFetch('/api/storage/delete', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: `users/${user.uid}/simulations/${cloudId}.json` }),
        });
      } catch (e) {
        console.warn('[SIM-MGMT] best-effort GCS payload cleanup failed (ignored):', e);
      }

      return { ok: true };
    } catch (e) {
      const msg = (e as Error)?.message || 'Delete failed';
      console.warn('[SIM-MGMT] deleteCloudSim failed:', e);
      return { ok: false, error: msg };
    }
  },

  renameCloudSim: async (cloudId, name) => {
    // Trim + reject empty (no write on empty). Updates only the `name` field.
    if (!cloudId) return { ok: false, error: 'Missing cloudId' };
    const trimmed = (name ?? '').trim();
    if (!trimmed) return { ok: false, error: 'Name cannot be empty' };

    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'Not authenticated' };

    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    try {
      await updateDoc(doc(db, `users/${user.uid}/simulations`, cloudId), {
        name: trimmed,
        updatedAt: serverTimestamp(),
      });
      // Mirror the rename onto any open tab bound to this cloudId so the UI
      // reflects it without a re-fetch.
      set((state) => ({
        tabs: state.tabs.map(t => t.cloudId === cloudId ? { ...t, name: trimmed } : t),
      }));
      return { ok: true };
    } catch (e) {
      const msg = (e as Error)?.message || 'Rename failed';
      console.warn('[SIM-MGMT] renameCloudSim failed:', e);
      return { ok: false, error: msg };
    }
  },

  listSimBackups: async (cloudId) => {
    // Newest version first. Returns [] (never throws) when unauthenticated or
    // on any read error so the UI can render an empty list cleanly.
    if (!cloudId) return [];
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return [];

    const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
    try {
      const snap = await getDocs(query(
        collection(db, `users/${user.uid}/simulations/${cloudId}/backups`),
        orderBy('version', 'desc'),
      ));
      return snap.docs.map((d) => {
        const data = d.data() as Partial<SimBackupRow> & { updatedAt?: { toDate?: () => Date } };
        // Doc id is the version; fall back to the stored field for robustness.
        const version = Number.isFinite(data.version)
          ? (data.version as number)
          : Number(d.id);
        // savedAt: prefer the explicit ISO string; else derive from the Firestore
        // serverTimestamp the AutoSaver writes as `updatedAt` (the production
        // backup writer) so the version picker always shows a real time.
        const savedAt = typeof data.savedAt === 'string'
          ? data.savedAt
          : (data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : '');
        return { version, savedAt };
      });
    } catch (e) {
      console.warn('[SIM-MGMT] listSimBackups failed:', e);
      return [];
    }
  },

  restoreSimBackup: async (cloudId, version) => {
    // Load a backup snapshot into the LIVE canvas, mirroring loadCloudSim's
    // parse path exactly (merge siteRag, open a tab, switch to the sim view).
    // ADDITIVE-tolerant: missing nodes/edges/simState/site/siteRag all fall
    // back to empty/defaults rather than crashing.
    if (!cloudId) return { ok: false, error: 'Missing cloudId' };
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'Not authenticated' };

    const { doc, getDoc } = await import('firebase/firestore');
    try {
      const snap = await getDoc(doc(db, `users/${user.uid}/simulations/${cloudId}/backups`, String(version)));
      if (!snap.exists()) return { ok: false, error: 'Backup not found' };
      const data = snap.data() as CloudSimPayload;

      // Reuse the canonical saved-canvas parse path. Missing legacy fields are
      // additive-tolerant; malformed graph arrays fail visibly rather than
      // silently erasing the user's source canvas.
      const {
        parseSavedCanvasArray,
        prepareSavedCanvasForLoad,
      } = await import('../lib/sim/canvas/migrations/prepareSavedCanvasForLoad');
      await ensureCanvasAssumptionBridge();
      let parsedNodes: AppNode[] = [];
      let parsedEdges: Edge[] = [];
      let parsedSimState: SimulationState | undefined;
      let parsedSite: Site | null = null;
      try {
        parsedNodes = parseSavedCanvasArray<AppNode>(data.nodes, 'nodes');
        parsedEdges = parseSavedCanvasArray<Edge>(data.edges, 'edges');
      } catch (e) {
        return { ok: false, error: (e as Error).message || 'Saved canvas graph is malformed' };
      }
      try { if (data.simState) parsedSimState = JSON.parse(data.simState); } catch { /* default simState */ }
      try { if (data.site) parsedSite = JSON.parse(data.site); } catch { /* null site */ }
      if (data.siteRag) {
        try {
          const parsedSiteRag = JSON.parse(data.siteRag);
          // Merge UNDER the live siteRag so a partial/legacy view-state blob
          // doesn't replace fully-shaped state and crash downstream readers.
          set((state) => ({ siteRag: { ...state.siteRag, ...savedWorkspaceUiState(parsedSiteRag) } }));
        } catch { /* ignore unparseable view-state */ }
      }

      // Open a fresh tab bound to the same cloudId (matches loadCloudSim).
      const prepared = prepareSavedCanvasForLoad({
        // The user's own mutable save: settle "in use => accepted" on load.
        acceptInUse: true,
        nodes: parsedNodes,
        edges: parsedEdges,
        eoGraphConfig: data.eoGraphConfig,
        eoCanvasExecutionConfig: data.eoCanvasExecutionConfig,
        eoInputSignature: data.eoInputSignature,
        solveMembership: data.solveMembership,
        eoStudyDefinition: data.eoStudyDefinition,
        eoTransientConfig: data.eoTransientConfig,
        legacySourceLabel: data.sourceBoP,
        persistedRepairMode: data.repairMode === true,
        persistedEoCapability: data.eoCapability,
        explicitContractVersion: data.eoContractVersion,
      });
      get().addTab(
        data.name || 'Restored Simulation',
        prepared.nodes,
        prepared.edges,
        parsedSimState ?? initialSimState,
        parsedSite,
        cloudId,
        version,
        false,
        prepared.eoGraphConfig,
        prepared.savedEoInputSignature,
        undefined,
        prepared.ignoredNodeIds,
        prepared.eoStudySpec,
        prepared.eoTransientConfig,
        undefined,
        prepared.eoCanvasExecutionConfig,
      );
      if (prepared.repairModeReason) {
        get().updateTab(get().activeTabId, { sourceBoP: prepared.repairModeReason });
      }
      get().setViewMode('simulation');
      // Restore the saved editor view-state too (consistent with loadCloudSim).
      // Additive: a legacy backup without `viewState` falls back to each field's
      // default via resolveViewState. Best-effort — never fail the restore on it.
      try {
        const { applyViewState } = await import('../lib/restoreEditorViewState');
        applyViewState((data as { viewState?: unknown }).viewState, {
          setCompactView: get().setCompactView,
          setColorOverlay: get().setColorOverlay,
          setCanvasBackgroundMode: get().setCanvasBackgroundMode,
          setCanvasMetersPerFlowUnit: get().setCanvasMetersPerFlowUnit,
          // #115 — restore the canvas-panel + guide overlay visibility flags.
          setShowMinimap: get().setShowMinimap,
          setShowControls: get().setShowControls,
          setShowSolverStatus: get().setShowSolverStatus,
          setShowInspector: get().setShowInspector,
          setShowGuide: get().setShowGuide,
        });
      } catch { /* view-state restore is best-effort */ }
      return { ok: true };
    } catch (e) {
      const msg = (e as Error)?.message || 'Restore failed';
      console.warn('[SIM-MGMT] restoreSimBackup failed:', e);
      return { ok: false, error: msg };
    }
  },

  createSimBackup: async (cloudId, snapshot) => {
    // Bounded backup write: store this snapshot at backups/{version}, then prune
    // the oldest so at most SIM_BACKUP_RETAIN remain. Version is the snapshot's
    // own version when present, else max(existing)+1, else 1.
    if (!cloudId) return { ok: false, error: 'Missing cloudId' };
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'Not authenticated' };

    const { doc, setDoc, deleteDoc, collection, getDocs, query, orderBy } =
      await import('firebase/firestore');
    const backupsPath = `users/${user.uid}/simulations/${cloudId}/backups`;
    try {
      // Determine the version (subcollection doc id must be an even-segment doc:
      // users/{uid}/simulations/{cloudId}/backups/{version} → 6 segments → valid).
      const existing = await getDocs(query(collection(db, backupsPath), orderBy('version', 'desc')));
      const maxExisting = existing.docs.reduce((m, d) => {
        const v = Number((d.data() as Partial<SimBackupRow>).version ?? d.id);
        return Number.isFinite(v) && v > m ? v : m;
      }, 0);
      const version = Number.isFinite(snapshot.version) && (snapshot.version as number) > 0
        ? (snapshot.version as number)
        : maxExisting + 1;

      await setDoc(doc(db, backupsPath, String(version)), stripUndefined({
        ...snapshot,
        version,
        savedAt: new Date().toISOString(),
      }));

      // Prune oldest beyond the retention cap. Re-read so the just-written doc
      // is counted. `orderBy('version','desc')` → slice off the tail.
      const after = await getDocs(query(collection(db, backupsPath), orderBy('version', 'desc')));
      if (after.docs.length > SIM_BACKUP_RETAIN) {
        const toDrop = after.docs.slice(SIM_BACKUP_RETAIN);
        for (const d of toDrop) {
          try {
            await deleteDoc(d.ref);
          } catch (e) {
            console.warn(`[SIM-MGMT] prune backup ${d.id} failed (ignored):`, e);
          }
        }
      }
      return { ok: true };
    } catch (e) {
      const msg = (e as Error)?.message || 'Backup failed';
      console.warn('[SIM-MGMT] createSimBackup failed:', e);
      return { ok: false, error: msg };
    }
  },

  // ── Sim Phase 3b — canvas undo/redo history ──────────────────────────
  snapshotForUndo: (coalesceKey?: string) => {
    if (isActiveCanvasRepairProtected(get())) return;
    const nowMs = Date.now();
    // Coalesce rapid consecutive keyed edits (e.g. per-keystroke param onChange)
    // into ONE undo entry: if the same key snapshotted within the window, the
    // top of `past` already holds the pre-edit-session state — just refresh the
    // timer and skip pushing. Unkeyed (truly discrete) mutations never coalesce.
    if (
      coalesceKey &&
      coalesceKey === _undoLastCoalesceKey &&
      nowMs - _undoLastSnapshotMs < UNDO_COALESCE_MS
    ) {
      _undoLastSnapshotMs = nowMs;
      return;
    }
    _undoLastCoalesceKey = coalesceKey ?? null;
    _undoLastSnapshotMs = nowMs;
    const { nodes, edges, canvasHistory } = get();
    const snapshot = cloneCanvas(nodes, edges);
    // Push pre-mutation snapshot; drop the oldest if we exceed the cap.
    const past = [...canvasHistory.past, snapshot];
    if (past.length > HISTORY_CAP) past.splice(0, past.length - HISTORY_CAP);
    // Any new discrete mutation invalidates the redo stack.
    set({ canvasHistory: { past, future: [] } });
  },
  undo: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    const { nodes, edges, canvasHistory } = get();
    if (canvasHistory.past.length === 0) return;
    // Break any open coalesce session — a param edit AFTER an undo must start a
    // fresh history entry, not merge with the pre-undo edit.
    _undoLastCoalesceKey = null;
    const past = [...canvasHistory.past];
    const previous = past.pop()!;
    // Current canvas moves onto the redo stack (also capped).
    const future = [cloneCanvas(nodes, edges), ...canvasHistory.future];
    if (future.length > HISTORY_CAP) future.length = HISTORY_CAP;
    set({
      nodes: previous.nodes,
      edges: retireInvalidCanvasResultEdges(previous.edges),
      canvasHistory: { past, future },
    });
    get().syncActiveTab();
  },
  redo: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    const { nodes, edges, canvasHistory } = get();
    if (canvasHistory.future.length === 0) return;
    _undoLastCoalesceKey = null;
    const future = [...canvasHistory.future];
    const next = future.shift()!;
    // Current canvas moves back onto the undo stack (also capped).
    const past = [...canvasHistory.past, cloneCanvas(nodes, edges)];
    if (past.length > HISTORY_CAP) past.splice(0, past.length - HISTORY_CAP);
    set({
      nodes: next.nodes,
      edges: retireInvalidCanvasResultEdges(next.edges),
      canvasHistory: { past, future },
    });
    get().syncActiveTab();
  },
  canUndo: () => !isActiveCanvasRepairProtected(get()) && get().canvasHistory.past.length > 0,
  canRedo: () => !isActiveCanvasRepairProtected(get()) && get().canvasHistory.future.length > 0,

  onNodesChange: (changes: NodeChange[]) => {
    const protectedCanvas = isActiveCanvasRepairProtected(get());
    const admittedChanges = protectedCanvas
      ? changes.filter((change) => change.type === 'select')
      : changes;
    if (admittedChanges.length === 0) return;
    const nextNodes = applyNodeChanges(admittedChanges.map(change =>
      change.type === "add" || change.type === "reset" ? { ...change, item: newCanvasNodeIncarnation(change.item) } : change,
    ), get().nodes);
    set({ nodes: protectedCanvas ? nextNodes : ensureCanvasNodeIncarnations(nextNodes) });
    if (!protectedCanvas) get().syncActiveTab();
  },
  onEdgesChange: (changes: EdgeChange[]) => {
    const protectedCanvas = isActiveCanvasRepairProtected(get());
    const admittedChanges = protectedCanvas
      ? changes.filter((change) => change.type === 'select')
      : changes;
    if (admittedChanges.length === 0) return;
    set({
      edges: applyEdgeChanges(admittedChanges, get().edges),
    });
    if (!protectedCanvas) get().syncActiveTab();
  },
  onConnect: (connection: Connection) => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) return;
    const sourceNode = state.nodes.find(n => n.id === connection.source);
    const targetNode = state.nodes.find(n => n.id === connection.target);

    if (!sourceNode || !targetNode) return;

    const activeCanvasExecutionConfig = state.tabs.find(
      (tab) => tab.id === state.activeTabId,
    )?.eoCanvasExecutionConfig;
    const shaftCoupledProfileActive =
      activeCanvasExecutionConfig?.execution_profile ===
        EO_GRAPH_V3_SHAFT_COUPLED_RANKINE_SOLVER_PROFILE ||
      activeCanvasExecutionConfig?.execution_profile ===
        EO_GRAPH_V3_SHAFT_COUPLED_DRY_COOLED_RANKINE_ACC_SOLVER_PROFILE;
    const declaresShaftMechanicalHandle =
      connection.sourceHandle ===
        EO_CANVAS_SHAFT_COUPLED_RANKINE_TURBINE_SOURCE_HANDLE ||
      connection.sourceHandle ===
        EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_PUMP_HANDLE ||
      connection.targetHandle ===
        EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_TURBINE_HANDLE ||
      connection.targetHandle ===
        EO_CANVAS_SHAFT_COUPLED_RANKINE_PUMP_LOAD_HANDLE;
    const involvesMechanicalShaft =
      sourceNode.type === 'mechanicalShaft' ||
      targetNode.type === 'mechanicalShaft';
    const exactShaftMechanicalConnection = shaftCoupledProfileActive && (
      (
        sourceNode.type === 'turbine' &&
        targetNode.type === 'mechanicalShaft' &&
        connection.sourceHandle ===
          EO_CANVAS_SHAFT_COUPLED_RANKINE_TURBINE_SOURCE_HANDLE &&
        connection.targetHandle ===
          EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_TURBINE_HANDLE
      ) || (
        sourceNode.type === 'mechanicalShaft' &&
        targetNode.type === 'pump' &&
        connection.sourceHandle ===
          EO_CANVAS_SHAFT_COUPLED_RANKINE_SHAFT_PUMP_HANDLE &&
        connection.targetHandle ===
          EO_CANVAS_SHAFT_COUPLED_RANKINE_PUMP_LOAD_HANDLE
      )
    );

    // SimulationEditor normally rejects this through its typed port contract.
    // Keep the store defensive as well: a programmatic/bypassed invalid shaft
    // gesture must never become a visual fluid Pipe or pollute node fluid data.
    if (
      (declaresShaftMechanicalHandle || involvesMechanicalShaft) &&
      !exactShaftMechanicalConnection
    ) return;

    // Sim Phase 3b — discrete mutation: snapshot BEFORE any node/edge change
    // (taken after the guard so a rejected connection records no history).
    state.snapshotForUndo();

    // Legacy flat fluid propagation remains available for older graph-v2
    // equipment, but it must never recreate a second fluid authority on the
    // canonical graph-v3 Boundary/HX tiles. Their fluid identity lives only in
    // Boundary `data.params.fluid_tag` plus the HX port contract; the exact
    // execution adapters validate that identity without mutating either node.
    const hasCanonicalPortFluidAuthority = (node: Node): boolean =>
      node.type === 'boundaryCondition' || node.type === 'heatExchanger';
    const sourceFluid = hasCanonicalPortFluidAuthority(sourceNode)
      ? undefined
      : sourceNode.data.fluidType;
    const targetFluid = hasCanonicalPortFluidAuthority(targetNode)
      ? undefined
      : targetNode.data.fluidType;

    if (
      !exactShaftMechanicalConnection &&
      sourceFluid &&
      !targetFluid &&
      !hasCanonicalPortFluidAuthority(targetNode)
    ) {
      state.updateNodeData(targetNode.id, {
        fluidType: sourceFluid,
      });
    } else if (
      !exactShaftMechanicalConnection &&
      !sourceFluid &&
      targetFluid &&
      !hasCanonicalPortFluidAuthority(sourceNode)
    ) {
      state.updateNodeData(sourceNode.id, {
        fluidType: targetFluid,
      });
    }

    // Sim Phase 2: new connections render via the custom `flow` edge
    // (FlowEdge.tsx) — direction arrow + flow-scaled width + phase colour.
    // Only the edge TYPE changed here; source/target/handle wiring is
    // untouched. Existing persisted edges (legacy `smoothstep`, or no type)
    // keep rendering via ReactFlow's built-in edges. `animated` is left set
    // for back-compat but FlowEdge gates its own motion on data.running +
    // prefers-reduced-motion. The inline `style`/`markerEnd` remain as a
    // graceful fallback for any renderer that does not honour edge.data.
    const derivedStreamComposition =
      (activeCanvasExecutionConfig?.graph_contract_version ?? 2) === 2
        ? deriveEoGraphV2ConnectionComposition(
            connection,
            state.nodes,
            state.edges,
          )
        : null;
    const streamData = derivedStreamComposition
      ? {
          fluid: derivedStreamComposition.fluid,
          ...(derivedStreamComposition.omega !== undefined
            ? { omega: derivedStreamComposition.omega }
            : {}),
          compositionProvenance: derivedStreamComposition.provenance,
        }
      : undefined;
    const edgeStyle = {
      type: 'flow' as const,
      animated: true,
      style: {
        stroke: exactShaftMechanicalConnection ? '#d97706' : '#94a3b8',
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: exactShaftMechanicalConnection ? '#d97706' : '#94a3b8',
      },
      ...(exactShaftMechanicalConnection
        ? { data: { domain: 'mechanical' as const, running: false } }
        : streamData
          ? { data: streamData }
          : {}),
    };

    // Auto-insert a VISUAL routing pipe between ordinary equipment. An explicit lumpedPipe is
    // already a physical 1-in/1-out EO component and must connect directly.
    const noPipeTypes = new Set(['pipe', 'lumpedPipe', 'thermalMolPipe', 'junction', 'sensor', 'flowMeter']);
    const activeExecutionProfile = activeCanvasExecutionConfig?.execution_profile;
    const pressureSeriesDirectTypes = new Set([
      'boundaryCondition', 'lumpedPipe', 'boiler', 'expansionValve',
    ]);
    const pressureSeriesDirectConnection =
      activeExecutionProfile
        === EO_CANVAS_REAL_PROPERTY_PRESSURE_SERIES_EXECUTION_PROFILE
      && pressureSeriesDirectTypes.has(sourceNode.type ?? '')
      && pressureSeriesDirectTypes.has(targetNode.type ?? '');
    const fixedSplitClosedCommonTransitions = new Set([
      'mixer\u0000boiler',
      'boiler\u0000turbine',
      'turbine\u0000condenser',
      'condenser\u0000pump',
      'pump\u0000splitter',
    ]);
    const fixedSplitClosedCommonDirectConnection =
      activeExecutionProfile === EO_CANVAS_FIXED_SPLIT_RECOMBINE_EXECUTION_PROFILE
      && fixedSplitClosedCommonTransitions.has(
        `${sourceNode.type ?? ''}\u0000${targetNode.type ?? ''}`,
      );
    const generalRealWaterHeaterV6DirectConnection =
      activeExecutionProfile ===
        EO_GRAPH_V3_GENERAL_REAL_WATER_HEATER_V6_SOLVER_PROFILE &&
      sourceNode.type === 'boundaryCondition' &&
      targetNode.type === 'heater';
    const needsPipe = !exactShaftMechanicalConnection
      && !pressureSeriesDirectConnection
      && !fixedSplitClosedCommonDirectConnection
      && !generalRealWaterHeaterV6DirectConnection
      && !noPipeTypes.has(sourceNode.type ?? '')
      && !noPipeTypes.has(targetNode.type ?? '');

    if (needsPipe) {
      // Position the pipe midway between source and target
      const midX = (sourceNode.position.x + targetNode.position.x) / 2;
      const midY = (sourceNode.position.y + targetNode.position.y) / 2;
      const pipeId = 'pipe-' + safeUUID();

      // ── Feature #91 — geo-derived pipe length + inclination. ──────────
      // When BOTH endpoints are geo-enabled AND carry lat/lng, derive the
      // pipe length from the great-circle distance between the components
      // and the inclination from their elevations. Otherwise keep the
      // legacy hardcoded defaults (length 10, elevation 0) VERBATIM so
      // manual / non-geo canvases are bit-for-bit unchanged.
      const bothGeo =
        sourceNode.data?.geoEnabled === true &&
        targetNode.data?.geoEnabled === true;
      const srcLat = sourceNode.data?.lat;
      const srcLng = sourceNode.data?.lng;
      const tgtLat = targetNode.data?.lat;
      const tgtLng = targetNode.data?.lng;
      const haveCoords =
        typeof srcLat === 'number' && Number.isFinite(srcLat) &&
        typeof srcLng === 'number' && Number.isFinite(srcLng) &&
        typeof tgtLat === 'number' && Number.isFinite(tgtLat) &&
        typeof tgtLng === 'number' && Number.isFinite(tgtLng);

      let pipeData: Record<string, any>;
      if (bothGeo && haveCoords) {
        const sourceCoord = { lat: srcLat as number, lng: srcLng as number };
        const targetCoord = { lat: tgtLat as number, lng: tgtLng as number };
        const elevUp = sourceNode.data?.elevation;
        const elevDown = targetNode.data?.elevation;
        // computePipeGeometry: length = haversine over the 2-point line;
        // inclination = asin(clamp(Δz/length, ±1)), +uphill along flow.
        const geom = computePipeGeometry(
          [sourceCoord, targetCoord],
          typeof elevUp === 'number' ? elevUp : undefined,
          typeof elevDown === 'number' ? elevDown : undefined,
        );
        const lengthM = geom.lengthMeters > 0
          ? geom.lengthMeters
          : haversineMeters(sourceCoord, targetCoord) || 10;
        pipeData = {
          label: 'Layout Pipe (visual only)',
          length: lengthM,
          inclinationRad: geom.inclinationRad,
          elevationUpstream: geom.elevationUpstream,
          elevationDownstream: geom.elevationDownstream,
          diameter: 0.3,
          roughness: 0.05,
          nCells: 3,
          pressureDrop: 0,
          heatLoss: 0,
          ambientTemp: 25,
          uValue: 5,
          fluidType: sourceFluid || targetFluid,
          elevation: 0,
          width: 2,
          geoEnabled: true,
        };
      } else {
        pipeData = {
          label: 'Layout Pipe (visual only)',
          length: 10,
          diameter: 0.3,
          roughness: 0.05,
          nCells: 3,
          pressureDrop: 0,
          heatLoss: 0,
          ambientTemp: 25,
          uValue: 5,
          fluidType: sourceFluid || targetFluid,
          elevation: 0,
          width: 2,
        };
      }

      const pipeNode: Node = {
        id: pipeId,
        type: 'pipe',
        position: { x: midX, y: midY - 20 },
        data: pipeData,
      };

      // Create edges: source → pipe, pipe → target
      const edge1 = { id: `e-${connection.source}-${pipeId}`, source: connection.source!, sourceHandle: connection.sourceHandle, target: pipeId, targetHandle: 'in', ...edgeStyle };
      const edge2 = { id: `e-${pipeId}-${connection.target}`, source: pipeId, sourceHandle: 'out', target: connection.target!, targetHandle: connection.targetHandle, ...edgeStyle };

      set({
        // Layout pipes are annotations, not equipment. Keep them first in
        // React Flow's node render order so a midpoint card cannot win pointer
        // hit-testing over the source/target equipment it visually routes.
        // Their exposed area remains selectable and persisted exactly as
        // before; only overlap priority changes.
        nodes: [pipeNode, ...get().nodes],
        edges: [...get().edges, edge1, edge2],
      });
    } else {
      // Direct connection (pipe-to-component, junction-to-component, etc.)
      set({
        edges: addEdge({ ...connection, ...edgeStyle }, get().edges),
      });
    }

    get().syncActiveTab();
  },
  setNodes: (nodes: AppNode[]) => {
    const protectedCanvas = isActiveCanvasRepairProtected(get());
    if (
      protectedCanvas
      && !isSelectionOnlyElementReplacement(get().nodes, nodes)
    ) return;
    set({ nodes: protectedCanvas ? nodes : ensureCanvasNodeIncarnations(nodes) });
    if (!protectedCanvas) get().syncActiveTab();
  },
  setEdges: (edges: Edge[]) => {
    const protectedCanvas = isActiveCanvasRepairProtected(get());
    if (
      protectedCanvas
      && !isSelectionOnlyElementReplacement(get().edges, edges)
    ) return;
    set({ edges });
    if (!protectedCanvas) get().syncActiveTab();
  },
  addNode: (node: AppNode) => {
    if (isActiveCanvasRepairProtected(get())) return;
    // Sim Phase 3b — discrete mutation: snapshot BEFORE adding the node.
    get().snapshotForUndo();
    set({ nodes: [...get().nodes, newCanvasNodeIncarnation(node)] });
    get().syncActiveTab();
  },
  removeNode: (id: string) => {
    if (isActiveCanvasRepairProtected(get())) return;
    // Sim Phase 3b — discrete mutation: snapshot BEFORE deleting node + edges.
    get().snapshotForUndo();
    set({
      nodes: get().nodes.filter(n => n.id !== id),
      edges: get().edges.filter(e => e.source !== id && e.target !== id)
    });
    get().syncActiveTab();
  },

  // ── Geo correlation (Feature #91 / #95) ──────────────────────────────
  recomputeGeoForNodes: () => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) return;
    const site = state.site;
    // No anchor → nothing to project. (Geo is opt-in per node anyway.)
    if (!site?.location) return;
    const hasGeoNodes = state.nodes.some(n => n.data?.geoEnabled === true);
    if (!hasGeoNodes) return;

    const projection = captureCanvasGeoProjection({
      anchor: { lat: site.location.lat, lng: site.location.lng },
      zoom: site.zoom,
    });

    // One snapshot around the batch (Sim Phase 3b discrete mutation).
    state.snapshotForUndo();
    set({
      nodes: state.nodes.map(n => {
        // Gate STRICTLY on geoEnabled so manual lat/lng is never clobbered.
        if (n.data?.geoEnabled !== true) return n;
        const { lat, lng } = projection.toLatLng(n.position);
        return { ...n, data: { ...n.data, lat, lng, updatedAt: Date.now() } };
      }),
    });
    get().syncActiveTab();
  },

  applyGeoElevations: (elevations: Map<string, number>) => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) return;
    if (!elevations || elevations.size === 0) return;
    const hasTarget = state.nodes.some(
      n => n.data?.geoEnabled === true && elevations.has(n.id),
    );
    if (!hasTarget) return;
    state.snapshotForUndo();
    set({
      nodes: state.nodes.map(n => {
        if (n.data?.geoEnabled !== true) return n;
        const elev = elevations.get(n.id);
        if (typeof elev !== 'number' || !Number.isFinite(elev)) return n;
        return {
          ...n,
          data: {
            ...n.data,
            elevation: elev,
            elevationSource: 'auto',
            updatedAt: Date.now(),
          },
        };
      }),
    });
    get().syncActiveTab();
  },

  commitGeneratedNodes: (
    generatedNodes: AppNode[],
    generatedEdges: Edge[],
    retirements: GeneratedCanvasRetirements = { nodeIds: [], edgeIds: [] },
  ): GeneratedCanvasCommitResult => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) return emptyGeneratedCanvasCommit();
    if (
      generatedNodes.length === 0
      && generatedEdges.length === 0
      && retirements.nodeIds.length === 0
      && retirements.edgeIds.length === 0
    ) {
      return emptyGeneratedCanvasCommit();
    }

    // Preflight the entire batch before snapshotting or mutating. A
    // deterministic generated id may collide with a user/unrelated node; an
    // id match is not ownership. Only an identical source marker is eligible
    // for in-place refresh.
    const generatedSourceCounts = new Map<string, number>();
    for (const generated of generatedNodes) {
      const source = generated.data?.sourceSiteLayerId;
      if (typeof source === 'string' && source.trim()) {
        generatedSourceCounts.set(source, (generatedSourceCounts.get(source) ?? 0) + 1);
      }
    }
    const hasInvalidOwnership = generatedNodes.some((generated) => {
      const generatedSource = generated.data?.sourceSiteLayerId;
      if (typeof generatedSource !== 'string' || !generatedSource.trim()) return true;
      if ((generatedSourceCounts.get(generatedSource) ?? 0) !== 1) return true;
      if (
        state.nodes.filter(
          (existing) => existing.data?.sourceSiteLayerId === generatedSource,
        ).length > 1
      ) {
        return true;
      }
      return state.nodes.some((existing) => (
        existing.id === generated.id
        && (
          typeof existing.data?.sourceSiteLayerId !== 'string'
          || !existing.data.sourceSiteLayerId.trim()
          || existing.data.sourceSiteLayerId !== generatedSource
        )
      ));
    });
    if (hasInvalidOwnership) return emptyGeneratedCanvasCommit();

    const beforeCanvas = cloneCanvas(state.nodes, state.edges);
    const beforeHistory = state.canvasHistory;
    const preexistingHistorySnapshots = new Set([
      ...beforeHistory.past,
      ...beforeHistory.future,
    ]);
    const initiatingTabId = state.activeTabId;
    const initiatingTabExists = initiatingTabId !== null
      && initiatingTabId !== undefined
      && state.tabs.some((tab) => tab.id === initiatingTabId);
    state.snapshotForUndo();

    const retiredNodeIds = new Set(retirements.nodeIds);
    const retiredEdgeIds = new Set(retirements.edgeIds);

    // Explicit source retirement runs before the upsert. Incident edges are
    // removed even if an older importer emitted a noncanonical edge id.
    const nextNodes = state.nodes.filter((node) => !retiredNodeIds.has(node.id));
    const bySource = new Map<string, number>();
    nextNodes.forEach((node, index) => {
      const sourceId = node.data?.sourceSiteLayerId;
      if (typeof sourceId === 'string') bySource.set(sourceId, index);
    });

    const committedIds: string[] = [];
    for (const generated of generatedNodes) {
      const sourceId = generated.data?.sourceSiteLayerId;
      const existingIndex =
        typeof sourceId === 'string' && bySource.has(sourceId)
          ? (bySource.get(sourceId) as number)
          : nextNodes.findIndex((node) => node.id === generated.id);
      if (existingIndex >= 0) {
        // Preserve a user's canvas placement while refreshing every generated
        // domain field. Explicit undefined values intentionally clear retired
        // solver assumptions.
        const previous = nextNodes[existingIndex];
        nextNodes[existingIndex] = {
          ...previous,
          type: generated.type ?? previous.type,
          data: { ...previous.data, ...generated.data, updatedAt: Date.now() },
        };
        committedIds.push(previous.id);
        if (typeof sourceId === 'string') bySource.set(sourceId, existingIndex);
      } else {
        const nextIndex = nextNodes.length;
        nextNodes.push(generated);
        committedIds.push(generated.id);
        if (typeof sourceId === 'string') bySource.set(sourceId, nextIndex);
      }
    }

    const nodeIds = new Set(nextNodes.map((node) => node.id));
    const regeneratedEdgeIds = new Set(generatedEdges.map((edge) => edge.id));
    // Remove every prior occurrence of a deterministic generated id before
    // adding its fresh definition. This also heals malformed legacy duplicate
    // ids rather than leaving one stale overlay behind.
    const nextEdges = state.edges.filter((edge) => (
      !retiredEdgeIds.has(edge.id)
      && !retiredNodeIds.has(edge.source)
      && !retiredNodeIds.has(edge.target)
      && !regeneratedEdgeIds.has(edge.id)
    ));
    const edgeIndexById = new Map(nextEdges.map((edge, index) => [edge.id, index]));
    for (const generated of generatedEdges) {
      // A generated edge is accepted only when both transaction-final
      // endpoints exist. Re-import replaces the entire deterministic edge so
      // stale fluid/result overlays cannot survive from an older solve.
      if (!nodeIds.has(generated.source) || !nodeIds.has(generated.target)) continue;
      const existingIndex = edgeIndexById.get(generated.id);
      if (existingIndex === undefined) {
        edgeIndexById.set(generated.id, nextEdges.length);
        nextEdges.push(generated);
      } else {
        nextEdges[existingIndex] = generated;
      }
    }

    set({ nodes: nextNodes, edges: nextEdges });
    get().syncActiveTab();

    const committedState = get();
    const committedCanvas = cloneCanvas(committedState.nodes, committedState.edges);
    const committedHistory = committedState.canvasHistory;
    const transactionHistorySnapshots = new Set([
      ...committedHistory.past,
      ...committedHistory.future,
    ].filter((snapshot) => !preexistingHistorySnapshots.has(snapshot)));
    // The transaction owns exactly what it wrote (nextNodes/nextEdges). The
    // syncActiveTab above may also settle in-use acceptance on untouched nodes
    // ("in use => accepted"); that is not part of the import, so it must never
    // turn a later edit of such a node into a rollback conflict.
    const affectedNodeIds = changedCanvasEntityIds(
      beforeCanvas.nodes,
      nextNodes,
    );
    const affectedEdgeIds = changedCanvasEntityIds(
      beforeCanvas.edges,
      nextEdges,
    );
    let cachedRollback: ReturnType<GeneratedCanvasCommitResult['rollback']> | undefined;

    return {
      committedNodeIds: committedIds,
      rollback: () => {
        if (cachedRollback) return cachedRollback;

        const liveState = get();
        const initiatingTab = initiatingTabExists && initiatingTabId
          ? liveState.tabs.find((tab) => tab.id === initiatingTabId)
          : undefined;
        const initiatingCanvasIsActive = liveState.activeTabId === initiatingTabId;

        if (initiatingTabExists && !initiatingTab) {
          cachedRollback = {
            ok: false,
            conflicts: [`canvas:${initiatingTabId}:missing`],
          };
          return cachedRollback;
        }
        if (!initiatingTabExists && !initiatingCanvasIsActive) {
          cachedRollback = {
            ok: false,
            conflicts: ['canvas:unpinned-target-changed'],
          };
          return cachedRollback;
        }

        const currentNodes = initiatingCanvasIsActive
          ? liveState.nodes
          : initiatingTab?.nodes ?? [];
        const currentEdges = initiatingCanvasIsActive
          ? liveState.edges
          : initiatingTab?.edges ?? [];
        const nodeCompensation = compensateCanvasEntities(
          currentNodes,
          beforeCanvas.nodes,
          committedCanvas.nodes,
          affectedNodeIds,
        );
        const edgeCompensation = compensateCanvasEntities(
          currentEdges,
          beforeCanvas.edges,
          committedCanvas.edges,
          affectedEdgeIds,
        );
        const historyCompensation = initiatingCanvasIsActive
          ? liveState.canvasHistory === committedHistory
            ? { history: beforeHistory, conflicts: [] }
            : compensateGeneratedCanvasHistory(
              liveState.canvasHistory,
              preexistingHistorySnapshots,
              transactionHistorySnapshots,
              beforeCanvas,
              committedCanvas,
              affectedNodeIds,
              affectedEdgeIds,
            )
          : undefined;
        const conflicts = [
          ...nodeCompensation.conflicts.map((id) => `node:${id}`),
          ...edgeCompensation.conflicts.map((id) => `edge:${id}`),
          ...(historyCompensation?.conflicts ?? []),
        ];

        if (initiatingCanvasIsActive) {
          const tabs = initiatingTabExists && initiatingTabId
            ? liveState.tabs.map((tab) => (
              tab.id === initiatingTabId
                ? {
                  ...tab,
                  nodes: nodeCompensation.entities,
                  edges: edgeCompensation.entities,
                }
                : tab
            ))
            : liveState.tabs;
          set({
            nodes: nodeCompensation.entities,
            edges: edgeCompensation.entities,
            tabs,
            canvasHistory: historyCompensation?.history ?? beforeHistory,
          });
        } else if (initiatingTabId) {
          // Restore the pinned tab in place without switching away from the
          // user's current canvas or touching its live nodes/history.
          set({
            tabs: liveState.tabs.map((tab) => (
              tab.id === initiatingTabId
                ? {
                  ...tab,
                  nodes: nodeCompensation.entities,
                  edges: edgeCompensation.entities,
                }
                : tab
            )),
          });
        }

        cachedRollback = { ok: conflicts.length === 0, conflicts };
        return cachedRollback;
      },
    };
  },

  simState: initialSimState,
  analysisRequestToken: 0,
  requestShowAnalysis: () => {
    set((state) => ({ analysisRequestToken: state.analysisRequestToken + 1 }));
  },
  runRequestToken: 0,
  requestRun: () => {
    set((state) => ({ runRequestToken: state.runRequestToken + 1 }));
  },
  toggleSimulation: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    set((state) => ({ simState: { ...state.simState, isRunning: !state.simState.isRunning } }));
    get().syncActiveTab();
  },
  resetSimulation: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    // Wholesale canvas replacement — drop undo history so undo cannot bleed a
    // pre-reset canvas back in (Sim Phase 3b).
    set({ simState: initialSimState, nodes: initialNodes, edges: initialEdges, canvasHistory: EMPTY_CANVAS_HISTORY });
    get().syncActiveTab();
  },
  clearSimulation: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    set({ simState: initialSimState, nodes: [], edges: [], canvasHistory: EMPTY_CANVAS_HISTORY });
    get().syncActiveTab();
  },
  loadCycle: (nodes: AppNode[], edges: Edge[], legacySourceLabel?: string, modelName?: string) => {
    const state = get();
    // Store-level invariant: no template/import caller may replace a preserved
    // repair source in place. The user must create an explicit editable repair
    // copy (or a separate new tab) before loading a different graph.
    if (isActiveCanvasRepairProtected(state)) return false;

    // A template or import that opens editable is in use as loaded: its
    // seeded values are accepted at the source ("in use => accepted"). A
    // legacy source opened read-only keeps its payload verbatim.
    const protectedSource = Boolean(legacySourceLabel?.trim());
    const loaded = acceptInUseCanvasAssumptions({
      nodes,
      eoStudySpec: defaultEoStudySpec(),
      eoTransientConfig: defaultEoTransientConfig(),
    });
    if (!protectedSource) nodes = loaded.nodes;
    // New cycle loaded — reset undo history to the freshly loaded canvas.
    const nextSimState = { ...initialSimState, isRunning: false };
    set({
      nodes,
      edges,
      simState: nextSimState,
      ignoredNodeIds: [],
      canvasHistory: EMPTY_CANVAS_HISTORY,
    });
    // Needs-migration/unavailable templates and old imported workspaces are
    // marked read-only. EO-ready templates omit the marker and remain editable.
    // The persisted property name stays `sourceBoP` for backward compatibility.
    // Replacing a legacy source with an EO-ready topology explicitly clears the
    // old marker; otherwise the fresh model would remain incorrectly locked.
    const nextName = modelName?.trim() || legacySourceLabel?.trim();
    set({
      tabs: state.tabs.map(tab =>
        tab.id === state.activeTabId
          ? {
            ...tab,
            sourceBoP: legacySourceLabel?.trim() || undefined,
            ...(nextName ? { name: nextName } : {}),
            nodes,
            edges,
            simState: nextSimState,
            ignoredNodeIds: [],
            eoStudySpec: protectedSource ? defaultEoStudySpec() : loaded.eoStudySpec,
            eoTransientConfig: protectedSource
              ? defaultEoTransientConfig()
              : loaded.eoTransientConfig,
            eoCanvasExecutionConfig: defaultEoCanvasExecutionConfig(),
          }
          : tab
      )
    });
    return true;
  },
  updateSimState: (newState) => {
    if (isActiveCanvasRepairProtected(get())) {
      // Runtime-only safety exception: a protected source may hydrate with a
      // stale playback bit. Allow an exact stop request on the live state, but
      // never sync that normalization into the canonical tab payload.
      if (isStopOnlySimulationPatch(newState) && get().simState.isRunning) {
        set((state) => ({ simState: { ...state.simState, isRunning: false } }));
      }
      return;
    }
    set((state) => ({ simState: { ...state.simState, ...newState } }));
    get().syncActiveTab();
  },
  // ── R2-B4 transient event-schedule actions ────────────────────────────────
  // Each mutates simState.events (the additive, tab-persisted field) then syncs
  // the active tab, mirroring updateSimState. UI selects simState.events + these
  // actions individually (no derived arrays inside a selector — CLAUDE.md).
  addSimEvent: (event) => {
    if (isActiveCanvasRepairProtected(get())) return;
    const withId: SimEvent = { ...event, id: event.id || safeUUID() };
    set((state) => ({
      simState: { ...state.simState, events: [...(state.simState.events ?? []), withId] },
    }));
    get().syncActiveTab();
  },
  updateSimEvent: (id, patch) => {
    if (isActiveCanvasRepairProtected(get())) return;
    set((state) => ({
      simState: {
        ...state.simState,
        events: (state.simState.events ?? []).map((e) =>
          e.id === id ? { ...e, ...patch, id } : e,
        ),
      },
    }));
    get().syncActiveTab();
  },
  removeSimEvent: (id) => {
    if (isActiveCanvasRepairProtected(get())) return;
    set((state) => ({
      simState: {
        ...state.simState,
        events: (state.simState.events ?? []).filter((e) => e.id !== id),
      },
    }));
    get().syncActiveTab();
  },
  clearSimEvents: () => {
    if (isActiveCanvasRepairProtected(get())) return;
    set((state) => ({ simState: { ...state.simState, events: [] } }));
    get().syncActiveTab();
  },
  loadEventPreset: (presetKey) => {
    if (isActiveCanvasRepairProtected(get())) return;
    const events = buildEventPreset(presetKey);
    set((state) => ({ simState: { ...state.simState, events } }));
    get().syncActiveTab();
  },
  updateNodeData: (id, data) => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) return;
    const currentNodes = [...state.nodes];
    const currentEdges = state.edges;
    const editedNode = currentNodes.find((node) => node.id === id);

    // Auto-inserted layout pipes are one visible stream-authority surface, not
    // physical equipment and not a whole connected-fluid group. Keep an
    // explicitly selected Fluid Type local to this one annotation path. The EO
    // graph adapter collapses the path and reads that declaration; propagating
    // it through a multi-fluid ACC/HX would overwrite unrelated air/water
    // streams and recreate the exact hidden group authority this inspector is
    // meant to replace.
    if (
      data.fluidType &&
      editedNode?.type === 'pipe' &&
      editedNode.data?.label === 'Layout Pipe (visual only)'
    ) {
      set({
        nodes: currentNodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, ...data } }
            : node),
      });
      get().syncActiveTab();
      return;
    }

    // If fluidType is being updated, trigger propagation
    if (data.fluidType || data.hotFluidType || data.coldFluidType) {
      const fluidType = data.fluidType || data.hotFluidType || data.coldFluidType;
      const handle = data.hotFluidType ? 'hot' : data.coldFluidType ? 'cold' : undefined;

      const updatedNodesMap = new Map<string, any>();
      const visited = new Set<string>();

      const propagate = (nodeId: string, fType: string, h?: string) => {
        const key = `${nodeId}-${h || 'default'}`;
        if (visited.has(key)) return;
        visited.add(key);

        const node = currentNodes.find(n => n.id === nodeId);
        if (!node) return;

        // Prepare update for current node
        const existingUpdate = updatedNodesMap.get(nodeId) || {};
        if (node.type === 'heatExchanger') {
          if (h?.startsWith('hot')) updatedNodesMap.set(nodeId, { ...existingUpdate, hotFluidType: fType });
          else if (h?.startsWith('cold')) updatedNodesMap.set(nodeId, { ...existingUpdate, coldFluidType: fType });
        } else {
          updatedNodesMap.set(nodeId, { ...existingUpdate, fluidType: fType });
        }

        // Propagate to neighbors
        currentEdges.forEach(edge => {
          if (edge.source === nodeId) {
            const sourceHandle = edge.sourceHandle;
            let shouldPropagate = true;
            if (node.type === 'heatExchanger') {
              const sourceSide = sourceHandle?.startsWith('hot') ? 'hot' : 'cold';
              const currentSide = h?.startsWith('hot') ? 'hot' : 'cold';
              if (h && sourceSide !== currentSide) shouldPropagate = false;
            }
            if (shouldPropagate) propagate(edge.target, fType, edge.targetHandle || undefined);
          } else if (edge.target === nodeId) {
            const targetHandle = edge.targetHandle;
            let shouldPropagate = true;
            if (node.type === 'heatExchanger') {
              const targetSide = targetHandle?.startsWith('hot') ? 'hot' : 'cold';
              const currentSide = h?.startsWith('hot') ? 'hot' : 'cold';
              if (h && targetSide !== currentSide) shouldPropagate = false;
            }
            if (shouldPropagate) propagate(edge.source, fType, edge.sourceHandle || undefined);
          }
        });
      };

      propagate(id, fluidType, handle);

      set({
        nodes: currentNodes.map(node => {
          const updates = updatedNodesMap.get(node.id);
          if (updates) {
            return { ...node, data: { ...node.data, ...updates } };
          }
          return node;
        })
      });
    } else {
      // Normal update
      set({
        nodes: currentNodes.map((node) => {
          if (node.id === id) {
            return { ...node, data: { ...node.data, ...data } };
          }
          return node;
        }),
      });
    }
    get().syncActiveTab();
  },
  updateNodeParams: (id, params) => {
    if (isActiveCanvasRepairProtected(get())) return;
    // Sim Phase 3b — param edit. The schema-backed inputs call this on every
    // keystroke (onChange), so coalesce per-node: a run of edits to the SAME
    // node collapses to ONE undo entry (the pre-edit-session state), instead of
    // one snapshot per character flooding the history.
    get().snapshotForUndo(`param:${id}`);
    set({
      nodes: get().nodes.map((node) => {
        if (node.id !== id) return node;
        const nextParams = { ...(node.data?.params ?? {}), ...params };
        // Undefined is the inspector's explicit delete operation. Keeping an
        // own-property with an undefined value makes provenance/readiness see
        // a ghost input even though JSON persistence drops it.
        for (const [key, value] of Object.entries(nextParams)) {
          if (value === undefined) delete nextParams[key];
        }
        return {
          ...node,
          data: {
            ...node.data,
            params: nextParams,
            updatedAt: Date.now(),
          },
        };
      }),
    });
    get().syncActiveTab();
  },
  stepSimulation: (dt_input) => {
    const state = get();
    if (isActiveCanvasRepairProtected(state)) {
      if (state.simState.isRunning) {
        set({ simState: { ...state.simState, isRunning: false } });
      }
      return;
    }
    // #116 — exclude IGNORED components (and their incident edges) from the
    // solve input. The canonical store arrays are untouched; we feed the solver
    // a pruned copy and merge its updated nodes back by id so ignored nodes
    // remain on the canvas (un-stepped). No ignores ⇒ identity, unchanged path.
    const { nodes: solveNodes, edges: solveEdges } = filterSolveInput(
      state.nodes, state.edges, state.ignoredNodeIds,
    );
    const result = runSimulationStep(
      solveNodes, solveEdges, state.simState,
      state.site, state.customComponents, dt_input
    );
    set({ nodes: mergeSolvedNodes(state.nodes, result.nodes), simState: { ...state.simState, ...result.simState } });
    get().syncActiveTab();
  },
  // stepSimulation logic extracted to src/simulation/stepSimulation.ts

  // ── Worker-solver transient step (feature-flagged, DEFAULT OFF) ───────────
  // Async sibling of stepSimulation. The await yields the await microtask, so
  // we re-read the latest store state after it resolves and bail out cleanly if
  // the run was stopped or topology changed mid-flight (mirrors steadyStateTick
  // cancellation). Bit-identical to stepSimulation on the happy path; falls back
  // to the main thread on any worker failure.
  stepSimulationViaWorker: async (dt_input) => {
    const before = get();
    if (isActiveCanvasRepairProtected(before)) {
      if (before.simState.isRunning) {
        set({ simState: { ...before.simState, isRunning: false } });
      }
      return;
    }
    // #116 — same ignore-filter as the synchronous path.
    const { nodes: solveNodes, edges: solveEdges } = filterSolveInput(
      before.nodes, before.edges, before.ignoredNodeIds,
    );
    const result = await runSimulationStepViaWorker(
      solveNodes, solveEdges, before.simState,
      before.site, before.customComponents, dt_input,
    );
    // The run may have been stopped while the worker step was in flight. If so,
    // do not apply a stale result on top of a stopped state.
    const afterWorker = get();
    if (isActiveCanvasRepairProtected(afterWorker)) {
      if (afterWorker.simState.isRunning) {
        set({ simState: { ...afterWorker.simState, isRunning: false } });
      }
      return;
    }
    if (!afterWorker.simState.isRunning) return;
    set((s) => ({ nodes: mergeSolvedNodes(s.nodes, result.nodes), simState: { ...s.simState, ...result.simState } }));
    get().syncActiveTab();
  },

  // ── Pseudo-transient run-to-convergence (STEADY-STATE mode only) ──────────
  // Orchestration ONLY: one transient NS step (reusing stepSimulation as-is),
  // then a converge-check on the metrics the solver just produced, then
  // auto-stop on convergence or at the STEADY_STATE_MAX_STEPS cap. Transient
  // mode never calls this; its run loop is byte-identical to before.
  steadyStateTick: (dt_input) => {
    const before = get();
    if (isActiveCanvasRepairProtected(before)) {
      if (before.simState.isRunning) {
        set({ simState: { ...before.simState, isRunning: false } });
      }
      return false;
    }

    // Cancellable: if the run was stopped (Stop pressed → isRunning=false) the
    // tick is a no-op. Reset the run-loop counters so the NEXT run starts fresh.
    if (!before.simState.isRunning) {
      _steadyStateStepCount = 0;
      _steadyStatePrevMetrics = null;
      return false;
    }

    // Capture the previous-step metrics BEFORE stepping, for the inter-step
    // "metrics no longer changing" criterion.
    const prevMetrics = _steadyStatePrevMetrics;

    // Reuse the existing NS step exactly as-is — no solver changes. The ONLY
    // orchestration tweak vs. stepSimulation: drive this step with autoTimeStep
    // semantics (CFL-limited adaptive step) so the pseudo-transient march
    // converges in fewer ticks. We override the flag for THIS call only and do
    // NOT persist it, so the user's autoTimeStep preference is untouched. The
    // CFL math and substep loop inside the solver are unchanged — autoTimeStep
    // merely selects dt = min(dt_max, 0.1) instead of clamping to the user dt.
    // #116 — exclude ignored components from the pseudo-transient march too.
    const { nodes: solveNodes, edges: solveEdges } = filterSolveInput(
      before.nodes, before.edges, before.ignoredNodeIds,
    );
    const result = runSimulationStep(
      solveNodes,
      solveEdges,
      { ...before.simState, autoTimeStep: true },
      before.site,
      before.customComponents,
      dt_input,
    );
    set((s) => ({ nodes: mergeSolvedNodes(s.nodes, result.nodes), simState: { ...s.simState, ...result.simState } }));
    get().syncActiveTab();
    _steadyStateStepCount += 1;

    const after = get();
    const metrics = after.simState.metrics;
    const residual = metrics.convergence?.residual ?? Number.POSITIVE_INFINITY;

    // If the step itself stopped the run (empty canvas → result sets
    // isRunning:false) or emitted hard errors, do NOT declare steady state.
    // Just stop the loop cleanly without a misleading "converged" message —
    // the initial residual is 0, which would otherwise read as converged.
    if (!after.simState.isRunning || (after.simState.errors && after.simState.errors.length > 0)) {
      _steadyStateStepCount = 0;
      _steadyStatePrevMetrics = null;
      return false;
    }

    const converged = isSteadyStateConverged(after.simState, { previousMetrics: prevMetrics });

    if (converged) {
      const iters = _steadyStateStepCount;
      _steadyStateStepCount = 0;
      _steadyStatePrevMetrics = null;
      set((s) => ({
        simState: {
          ...s.simState,
          isRunning: false,
          warnings: [...s.simState.warnings, steadyStateReachedMessage(iters, residual)],
        },
      }));
      get().syncActiveTab();
      return false;
    }

    if (_steadyStateStepCount >= STEADY_STATE_MAX_STEPS) {
      const iters = _steadyStateStepCount;
      _steadyStateStepCount = 0;
      _steadyStatePrevMetrics = null;
      set((s) => ({
        simState: {
          ...s.simState,
          isRunning: false,
          warnings: [...s.simState.warnings, steadyStateNotReachedMessage(iters)],
        },
      }));
      get().syncActiveTab();
      return false;
    }

    // Not converged yet — remember this step's metrics for the next inter-step
    // comparison and keep ticking.
    _steadyStatePrevMetrics = metrics;
    return true;
  },
  ...createChatSlice(set, get, api),
  ...createRecorderSlice(set, get, api),
}));

// Register the userApiKeys getter the data-provider fetch wrapper consults
// when stamping the x-da-user-key header. Done here (not in App.tsx) so the
// registration happens before any provider fetch fires — providers run as
// part of generateReport, which is a store action, so the store is always
// loaded by the time a fetch goes out.
import('../lib/userKeyHeader').then(({ setUserApiKeysGetter }) => {
  setUserApiKeysGetter(() => useStore.getState().userApiKeys ?? {});
}).catch(() => { /* leaf module; failure here would also fail at provider call time */ });
